import React, { useState, useEffect, useRef, useMemo, useCallback, Suspense } from "react";
import { evaluateRoomSuccess, resolveCriteria, filterDefs, calcClearanceBSA } from "./scoring";
import { BASE_CASES, CASE_SCENARIOS, DOMAIN_MAP, DOMAIN_LABELS, SUBJECT_TAGS, scenarioFor, tagsForCase } from "./cases";

import { QRCodeSVG } from "qrcode.react";
import { initializeApp, getApps } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  getAuth,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  onAuthStateChanged,
  signOut as fbSignOut,
} from 'firebase/auth';
import {
  getFirestore,
  doc, getDoc, setDoc, updateDoc, deleteDoc,
  collection, query, where, getDocs, onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';

// Hashing helper for research events. Lives here, not in the case library:
// it has nothing to do with case content and was only swept into the
// extraction because it sat between two of the blocks being moved.
const sha256 = async (str) => {
  try {
    const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(String(str)));
    return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2,'0')).join('');
  } catch { return str; }
};


// The CRRT playground is a large three.js bundle. Lazy-loading keeps the
// machine geometry and three.js itself out of the main bundle, so practice
// learners never download it unless they open the playground.
const CRRTPlaygroundLazy = React.lazy(() => import("./CRRTPlayground"));


// Strip undefined values from an object so Firestore doesn't reject it
const stripUndefined = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  return Object.fromEntries(
    Object.entries(obj)
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => [k, v && typeof v === 'object' && !Array.isArray(v) ? stripUndefined(v) : v])
  );
};


/* ── Google Fonts: Inter for UI chrome, Lora for clinical text ── */
(()=>{
  if (!document.getElementById('kirpa-fonts')) {
    const l=document.createElement('link');
    l.id='kirpa-fonts';l.rel='stylesheet';
    l.href='https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Lora:ital,wght@0,600;1,400&display=swap';
    document.head.appendChild(l);
  }
})();

// ─── SHARED DEFINITIONS ───────────────────────────────────────────────────────
// filterDefs: ecv = priming volume (mL), weightHint = recommended weight range
// Returns display label for a filter key given current name mode
const filterLabel = (key, mode) => {
  const f = filterDefs[key]; if(!f) return key;
  return mode==="brand" ? `${f.brandLabel} (${f.weightHint})` : `${f.genericLabel} (${f.weightHint})`;
};
const accessDefs = {
  "5F":"5 French (<3 kg)","7F":"7 French (3–5.9 kg)","8F":"8 French (6–14.9 kg)",
  "9F":"9 French (15–30 kg)","12F":"12 French (>30 kg)"
};
// ─── Case-level option resolvers (fall back to global defaults) ──────────────
const DEFAULT_ANTICOAGS = ["none","heparin","citrate","epoprostenol"];
const DEFAULT_PRIMES    = ["saline","albumin","blood"];
const caseFilters   = (o) => o?.filters?.length   ? o.filters   : Object.keys(filterDefs).map(k=>({key:k,...filterDefs[k]}));
const caseAccess    = (o) => o?.accesses?.length   ? o.accesses  : Object.keys(accessDefs);
const caseAnticoags = (o) => o?.anticoags?.length  ? o.anticoags : DEFAULT_ANTICOAGS;
const casePrimes    = (o) => o?.primes?.length     ? o.primes    : DEFAULT_PRIMES;
const caseSols      = (o) => o?.solutions?.length  ? o.solutions : ALL_SOLUTIONS;
const caseIntvs     = (o) => o?.interventions?.length ? o.interventions : allInterventions.map(i=>({...i,type:"checkbox"}));
// Resolve a filter's ECV — checks case options first, then global filterDefs
const resolveFilterEcv = (key, o) => {
  const f = o?.filters?.find(x=>x.key===key);
  if(f) return f.ecv;
  return filterDefs[key]?.ecv ?? 0;
};
// Label helper that works for both global and custom filters
const caseFilterLabel = (f, mode) => {
  if(typeof f === "string") {
    const g = filterDefs[f]; if(g) return filterLabel(f,mode);
    return f; // custom key with no global entry
  }
  // f is a {key,label,ecv} object
  return f.label || f.key;
};
// Solutions: stored internally by generic key
// Phoxilium brand names: B22K4 = "Phoxilium B22/K4", BK4/2.5 = "Phoxilium K4/2.5"
const solutionLabels = {
  "Prismasol 0":     "Na140 K0 Ca2.5 HCO3-32",
  "Prismasol 4":     "Na140 K4 Ca2.5 HCO3-32",
  "Prism0cal":       "Na140 K0 Ca0 HCO3-32",
  "Prism0cal B22":   "Na140 K0 Ca0 HCO3-22",
  "Phoxilium B22K4": "Na140 K4 Ca0 HCO3-22",
  "Phoxilium K4/2.5":"Na140 K4 Ca2.5 HCO3-32",
};
const solutionByAnticoag = {
  heparin:     ["Prismasol 0","Prismasol 4","Phoxilium K4/2.5"],
  citrate:     ["Prism0cal","Prism0cal B22","Phoxilium B22K4"],
  none:        ["Prismasol 0","Prismasol 4","Phoxilium K4/2.5"],
  epoprostenol:["Prismasol 0","Prismasol 4","Phoxilium K4/2.5"],
};
// Modality definitions
const MODALITIES = ["CVVH","CVVHD","CVVHDF"];
// Returns which rate fields are active for a given modality
const modalityRates = (m) => ({
  pbp:  m==="CVVH"||m==="CVVHDF",   // pre-blood pump replacement
  rep:  m==="CVVH"||m==="CVVHDF",   // post-filter replacement
  dial: m==="CVVHD"||m==="CVVHDF",  // dialysate
});
// ─── BEDSIDE ACTIONS ─────────────────────────────────────────────────────────
// Grouped for legibility at the bedside. Ids are canonical English identifiers
// written to research_events and are never translated.
//
// Potassium and phosphate are binary actions rather than dosed inputs. There is
// no evidence base distinguishing one additive concentration from another, so
// scoring a dose would encode preference as truth. The defensible construct is
// whether the learner RECOGNISED the need, not how much they wrote.
//
// Each electrolyte has two routes — into the solution bag, or supplemented to
// the patient. Either satisfies the requirement, but both are recorded
// separately so route preference stays analysable.
const INTERVENTION_GROUPS = [
  { id:"meds", label:"Medications", items:[
    {id:"mannitol",              label:"Give Mannitol"},
    {id:"bicarb",                label:"Give Bicarbonate"},
    {id:"sedation",              label:"Give Sedation"},
  ]},
  { id:"circuit", label:"Circuit & Access", items:[
    {id:"reverse_lines",         label:"Reverse Lines"},
    {id:"check_connections",     label:"Check Circuit Connections"},
    {id:"change_circuit",        label:"Change Entire Circuit"},
  ]},
  // `dose` marks an action that needs a quantity once selected. Potassium and
  // phosphate stay binary: those cases test whether replacement was prescribed
  // at all, and the concentration used varies by centre. Sodium and free water
  // are different, because the whole point of those cases is the amount.
  { id:"additives", label:"Additives & Supplements", items:[
    {id:"add_k_bag",             label:"Add Potassium to Solution"},
    {id:"supp_k",                label:"Give Supplemental Potassium"},
    {id:"add_po4_bag",           label:"Add Phosphate to Solution"},
    {id:"supp_po4",              label:"Give Supplemental Phosphate"},
    {id:"supp_mg",               label:"Give Supplemental Magnesium"},
    {id:"add_albumin",           label:"Add Albumin to Dialysate (3%)"},
    {id:"add_sodium",            label:"Add Sodium to Solution",
      dose:{ field:"Na_add",    unit:"mmol per bag", step:10, min:0, max:2000,
             hint:"Standard solution is 140 mmol/L in a 5 litre bag." }},
    {id:"add_free_water",        label:"Add Free Water to Solution",
      dose:{ field:"freeWater",  unit:"mL per bag",   step:50, min:0, max:5000,
             hint:"Dilutes the solution, lowering its sodium." }},
  ]},
  { id:"anticoag", label:"Anticoagulation", items:[
    {id:"pause_citrate",         label:"Pause Citrate Infusion"},
    {id:"pause_calcium",         label:"Pause Calcium Infusion"},
    {id:"increase_epoprostenol", label:"Increase Epoprostenol Dose"},
    {id:"lower_epoprostenol",    label:"Lower Epoprostenol Dose"},
  ]},
];

// Flat list retained so existing lookups by id keep working unchanged.
const allInterventions = INTERVENTION_GROUPS.flatMap(g => g.items);

// Either route satisfies the electrolyte requirement.

// Solution display helpers
const solutionBrand = {
  "Prismasol 0":     "PrismaSol BGK 0/3.5/0",
  "Prismasol 4":     "PrismaSol BGK 4/3.5/0",
  "Prism0cal":       "Prism0Cal",
  "Prism0cal B22":   "Prism0Cal B22",
  "Phoxilium B22K4": "Phoxilium B22/K4",
  "Phoxilium K4/2.5":"Phoxilium K4/2.5",
};
// In generic mode a solution is named by its composition, not its trade name.
// The keys happen to be brand names, so returning the key in generic mode
// leaked the brand straight back into the label.
const solName = (key, mode) => mode==="brand"
  ? (solutionBrand[key] || key)
  : (solutionLabels[key] || key);
const ALL_SOLUTIONS = Object.keys(solutionLabels);

// Success criteria grouped by category
// hint: shown as a light italicised guide under the group header
const SUCCESS_GROUPS = [
  { group:"Clearance",
    hint:"Use this if the case requires the trainee to increase or decrease CRRT clearance. Leave blank if clearance is not the focus.",
    items:[
    { value:"clearance_gte", label:"Clearance must reach a minimum", needsParam:"clearance" },
    { value:"clearance_lte", label:"Clearance must stay below a maximum", needsParam:"clearance" },
    // Effluent dose INCLUDES net ultrafiltration; the two clearance options above
    // do not. Kept as separate criteria rather than a unit toggle so an author
    // always knows which convention a case is scored on.
    { value:"effluent_kg_gte", label:"Effluent dose must reach a minimum (ml/kg/hr, includes net UF)", needsParam:"effluent_kg" },
    { value:"effluent_min_of", label:"Effluent dose must reach the LOWER of two targets (ml/kg/hr or L/1.73m²/hr)", needsParam:"effluent_pair" },
    { value:"ff_lte",          label:"Filtration fraction must stay at or below a ceiling", needsParam:"ff" },
  ]},
  { group:"Net Fluid Removal (Net UF) \u00b7 positive = fluid removed",
    hint:"Use this if the case involves adjusting the fluid removal rate — e.g. too aggressive causing hypotension, or insufficient in fluid overload.",
    items:[
    { value:"netuf_kghr_lte", label:"Fluid removal must stay below a maximum (ml/kg/hr)", needsParam:"netuf_lte" },
    { value:"netuf_kghr_gte", label:"Fluid removal must reach a minimum (ml/kg/hr)", needsParam:"netuf_gte" },
  ]},
  { group:"Blood Flow Rate (BFR)",
    hint:"Use this if the trainee needs to adjust BFR — e.g. too high causing access pressure alarms, or too low for adequate clearance.",
    items:[
    { value:"bfr_lte", label:"BFR must stay below a maximum", needsParam:"bfr" },
    { value:"bfr_gte", label:"BFR must reach a minimum",      needsParam:"bfr" },
  ]},
  { group:"Anticoagulation",
    hint:"Tick all anticoagulants that would be acceptable for this case. The trainee must choose one of the ticked options to pass.",
    items:[
    { value:"anticoag_is", label:"Acceptable anticoagulant(s) — tick all that are correct", needsParam:"anticoag_multi" },
  ]},
  { group:"Solution",
    hint:"Tick all solutions that would be acceptable for this case. The trainee must choose one of the ticked options to pass.",
    items:[
    { value:"solution_is", label:"Acceptable solution(s) — tick all that are correct", needsParam:"solution_multi" },
  ]},
  { group:"Access / Catheter",
    hint:"Tick all catheter sizes that would be acceptable for this case. The trainee must choose one of the ticked options to pass.",
    items:[
    { value:"access_is", label:"Acceptable catheter size(s) — tick all that are correct", needsParam:"access_multi" },
  ]},
  { group:"Circuit / Filter / Prime",
    hint:"Use this if the case requires the trainee to select the correct filter (e.g. right size for weight) or priming solution (e.g. blood prime for small patient).",
    items:[
    { value:"filter_is",    label:"Acceptable filter(s) — tick all that are correct", needsParam:"filter_multi" },
    { value:"prime_select", label:"Acceptable priming solution(s) — tick all that are correct", needsParam:"prime_multi" },
  ]},
  { group:"Modality",
    hint:"Use this if the case requires changing to a specific CRRT modality (CVVH, CVVHD, or CVVHDF). Leave blank if any modality is acceptable.",
    items:[
    { value:"modality_change", label:"Select acceptable modality/modalities — tick all correct", needsParam:"modality_multi" },
  ]},
  { group:"Electrolytes & Additives",
    hint:"Use this if the case involves hypo/hyperkalemia, hypophosphatemia, or sodium correction requiring additives to the CRRT fluids.",
    items:[
    { value:"k_add",      label:"Potassium must be replaced (solution or supplement)",               needsParam:"elec_binary" },
    { value:"po4_add",    label:"Phosphate must be replaced (solution or supplement)",                needsParam:"elec_binary" },
    { value:"na_add",     label:"Correct sodium additive for hypernatremia",     needsParam:"na_target" },
    { value:"free_water", label:"Correct free water addition for hyponatremia",  needsParam:"na_target" },
  ]},
  { group:"Clinical Actions (Non-Prescription)",
    hint:"Use this if the correct answer is a bedside action rather than a prescription change — e.g. giving sedation, reversing lines, or changing the circuit.",
    items:
    allInterventions.map(i=>({ value:`intervention_${i.id}`, label:i.label }))
  },
  { group:"Full Prescription from Blank",
    hint:"Use this for onboarding / general prescribing cases where the trainee writes the entire prescription from scratch. This option replaces all other criteria.",
    items:[
    { value:"room1", label:"Trainee fills in entire prescription from blank — set acceptable ranges for every field", needsParam:"room1_full" },
  ]},
];
// Flat lookup
const SUCCESS_FLAT = SUCCESS_GROUPS.flatMap(g=>g.items);

// ─── RESEARCH DOMAIN TAXONOMY ─────────────────────────────────────────────────
// 5 primary domains aligned with research protocol and App.js
// Maps educator-facing facet labels → protocol domain codes
// Reverse map: protocol code → display label for charts
// Ordered for chart display
const PROTOCOL_DOMAINS = [
  "effluent_dose_flow",
  "hemodynamics_fluid",
  "anticoagulation",
  "electrolytes",
  "access_circuit_alarms",
];

// Facet labels used across the app.
// These map to the 5 primary research domains via FACET_TO_DOMAIN above.
const FACETS = [
  "Clearance",              // → effluent_dose_flow
  "Hemodynamics",           // → hemodynamics_fluid
  "Anticoagulation",        // → anticoagulation
  "Electrolytes",           // → electrolytes
  "Access/Circuit/Machine", // → access_circuit_alarms
  "General Prescribing",    // → effluent_dose_flow (full blank-prescription cases)
  "Miscellaneous",          // → unknown
];
const USER_FACETS = [
  "Clearance","Hemodynamics","Anticoagulation","Electrolytes","Access/Circuit/Machine","Miscellaneous",
];

// Subject tags for educator classification (not visible to trainees)
// Topic tags are CROSS-CUTTING, not nested under a domain. A case sits in one
// clinical domain but may carry several topics, and the same topic can appear
// under different domains. They are reported separately in Topic Scores.

// Derive CRRT modality from flow rates
const deriveModality = (pbp=0, dial=0, rep=0) => {
  const hasDial = dial > 0;
  const hasRep  = pbp > 0 || rep > 0;
  if (hasDial && hasRep) return 'CVVHDF';
  if (hasDial) return 'CVVHD';
  if (hasRep)  return 'CVVH';
  return 'unknown';
};

// Access size category for research bucketing
const accessSizeCategory = (access) => {
  if (['5F','7F'].includes(access)) return 'small';
  if (['8F','9F'].includes(access)) return 'medium';
  if (['12F'].includes(access)) return 'large';
  return 'unknown';
};

// Filter category for research bucketing
const filterCategory = (filter) => {
  const map = { HF20:'small_low_ecv', ST60:'medium_mid_ecv', ST150:'large_high_ecv', OXiris:'large_adsorptive' };
  return map[filter] || 'unknown';
};

// Time-of-day block for session analytics
const timeOfDayBlock = (hour) => {
  if (hour >= 6  && hour < 12) return 'morning';
  if (hour >= 12 && hour < 18) return 'afternoon';
  if (hour >= 18 && hour < 24) return 'evening';
  return 'night';
};

// buildResearchEvent removed — research events are now written server-side
// by the submitTraineeAssignmentResult Cloud Function to /research_events/{id}.
// This ensures authoritative, tamper-proof data and consistent schema.

// Estimated total blood volume, ml/kg by age. Conventional paediatric bands:
//   preterm            100
//   term neonate        90   (0–1 month)
//   infant              80   (1–12 months)
//   toddler             75   (1–3 years)
//   child               75   (3–6 years)
//   older child/adult   70   (>6 years)
// CLINICAL CONTENT: these are the commonly cited values, but confirm them
// against your own reference before the pilot — they change the ECV:TBV ratio
// that learners see, and that ratio drives the blood-prime decision.
const TBV_BANDS = [
  { maxMonths: 1,   mlPerKg: 90,  label: "Neonate"     },
  { maxMonths: 12,  mlPerKg: 80,  label: "Infant"      },
  { maxMonths: 36,  mlPerKg: 75,  label: "Toddler"     },
  { maxMonths: 72,  mlPerKg: 75,  label: "Child"       },
  { maxMonths: Infinity, mlPerKg: 70, label: "Older child / adolescent" },
];
const tbvBandFor = (mo) => {
  const m = (mo === null || mo === undefined || isNaN(mo)) ? 999 : Number(mo);
  return TBV_BANDS.find(b => m <= b.maxMonths) || TBV_BANDS[TBV_BANDS.length - 1];
};
const calculateTBV = (wt, mo) => Math.round(tbvBandFor(mo).mlPerKg * (parseFloat(wt) || 0));
const calcClearanceKg = (pbp, dial, rep, wt) =>
  wt>0?(((pbp+dial+rep)/wt)).toFixed(1):"—";
// Legacy alias used in display components
const calcClearance = calcClearanceBSA;


// SI ↔ US conversions (mirroring App.js exactly)
const cvtToUS = (k, v) => {
  const c={
    Urea:{v:(v*2.8).toFixed(1),u:"mg/dL"},
    Cr:{v:(v/88.4).toFixed(2),u:"mg/dL"},
    iCa:{v:(v*4).toFixed(2),u:"mg/dL"},
    TotalCa:{v:(v*4).toFixed(2),u:"mg/dL"},
    Mg:{v:(v*2.43).toFixed(2),u:"mg/dL"},
    PO4:{v:(v*3.1).toFixed(2),u:"mg/dL"},
    Alb:{v:(v/10).toFixed(1),u:"g/dL"},
    Hgb:{v:(v/10).toFixed(1),u:"g/dL"},
    Glucose:{v:(v*18).toFixed(0),u:"mg/dL"},
    Fibrinogen:{v:(v*100).toFixed(0),u:"mg/dL"},
    Ammonia:{v:(v*1.7).toFixed(0),u:"µg/dL"},
    CircuitiCa:{v:(v*4).toFixed(2),u:"mg/dL"},
    Acetaminophen:{v:(v/6.61).toFixed(1),u:"µg/mL"},
    Salicylate:{v:(v*13.8).toFixed(1),u:"mg/dL"},
    AlbCrRatio:{v:(v*8.84).toFixed(0),u:"mg/g"},
  };
  return c[k]||null;
};
const cvtFromUS = (k, v) => {
  const c={Urea:v/2.8,Cr:v*88.4,iCa:v/4,TotalCa:v/4,Mg:v/2.43,PO4:v/3.1,Alb:v*10,Hgb:v*10,Glucose:v/18,Fibrinogen:v/100,Ammonia:v/1.7,CircuitiCa:v/4,Acetaminophen:v*6.61,Salicylate:v/13.8,AlbCrRatio:v/8.84};
  return c[k]!==undefined?parseFloat(c[k].toFixed(3)):v;
};
const siUnits = {Na:"mmol/L",K:"mmol/L",Cl:"mmol/L",HCO3:"mmol/L",Urea:"mmol/L",Cr:"µmol/L",iCa:"mmol/L",TotalCa:"mmol/L",Mg:"mmol/L",PO4:"mmol/L",Alb:"g/L",Hgb:"g/L",pCO2:"mmHg",pO2:"mmHg",ALT:"U/L",AST:"U/L",INR:"",PTT:"s",Fibrinogen:"g/L",WBC:"×10⁹/L",Plt:"×10⁹/L",Neut:"×10⁹/L",Hct:"",BE:"mmol/L",Ammonia:"µmol/L",Lactate:"mmol/L",Glucose:"mmol/L",CircuitiCa:"mmol/L",Acetaminophen:"µmol/L",Salicylate:"mmol/L",AlbCrRatio:"mg/mmol"};

// ─── BASE CASES ───────────────────────────────────────────────────────────────

// ─── STORAGE 

// ─── FIREBASE CONFIG ─────────────────────────────────────────────────────────
const KIRPA_FIREBASE_CONFIG = {
  apiKey: "AIzaSyDuZClbUQocd0R9eJAZtiUUy52AipPCZKU",
  authDomain: "crrt-kirpa.firebaseapp.com",
  projectId: "crrt-kirpa",
  storageBucket: "crrt-kirpa.firebasestorage.app",
  messagingSenderId: "940462884142",
  appId: "1:940462884142:web:809e9319c26438a21d26bf",
  measurementId: "G-G85K90Z4S9",
};

// Prevent duplicate initialization during hot-reload
const firebaseApp = getApps().length
  ? getApps()[0]
  : initializeApp(KIRPA_FIREBASE_CONFIG);

export const auth = getAuth(firebaseApp);
export const db   = getFirestore(firebaseApp);

// ─── FIRESTORE HELPERS ───────────────────────────────────────────────────────
// These replace the old window.storage `store` object.
// Personal data (per-educator) → /educator_data/{email}/{collection}
// Shared data (community cases) → /shared/{key}
// Educator profiles             → /educators/{email}

const store = {
  // Per-user: assignments, assignment groups, case overrides, favourites
  async get(key, fallback = null) {
    try {
      // key format: "edu_email_a", "edu_email_ag", etc.
      const uid = auth.currentUser?.email;
      if (!uid) return fallback;
      const ref = doc(db, 'educator_data', uid, 'store', key);
      const snap = await getDoc(ref);
      return snap.exists() ? snap.data().value : fallback;
    } catch (e) { console.warn('store.get', key, e); return fallback; }
  },
  async set(key, value) {
    try {
      const uid = auth.currentUser?.email;
      if (!uid) return;
      const ref = doc(db, 'educator_data', uid, 'store', key);
      await setDoc(ref, { value, updatedAt: serverTimestamp() }, { merge: true });
    } catch (e) { console.warn('store.set', key, e); }
  },
  // Shared: community cases, research events
  async getShared(key, fallback = null) {
    try {
      const ref = doc(db, 'shared', key);
      const snap = await getDoc(ref);
      return snap.exists() ? snap.data().value : fallback;
    } catch (e) { console.warn('store.getShared', key, e); return fallback; }
  },
  async setShared(key, value) {
    try {
      const ref = doc(db, 'shared', key);
      await setDoc(ref, { value, updatedAt: serverTimestamp() }, { merge: true });
    } catch (e) { console.warn('store.setShared', key, e); }
  },
};

/* ═══════════════════════════════════════════════════════════════════════════
   i18n CORE — locale context, t() helper, and case-translation resolver.
   Inert while locale === "en": every t() returns its English fallback and
   localizeCase() is a passthrough returning the original object reference.
   ═══════════════════════════════════════════════════════════════════════════ */

// ─── LOCALE REGISTRY ─────────────────────────────────────────────────────────
// `dir` drives document direction. `numbering` is pinned to "latn" everywhere:
// clinical values must read the way they read on the machine, so Arabic uses
// Latin digits deliberately.
const LOCALES = {
  en: { label:"English",  native:"English",  dir:"ltr", numbering:"latn", intl:"en-CA" },
  fr: { label:"French",   native:"Français", dir:"ltr", numbering:"latn", intl:"fr-CA" },
  es: { label:"Spanish",  native:"Español",  dir:"ltr", numbering:"latn", intl:"es" },
  // Arabic is deliberately not enabled yet: the RTL layout pass has not been
  // done, so it would render left-to-right and read wrong. Re-add here once
  // the physical-direction CSS properties have been converted to logical ones.
  // ar: { label:"Arabic", native:"العربية", dir:"rtl", numbering:"latn", intl:"ar-u-nu-latn" },
};
const DEFAULT_LOCALE = "en";
const LOCALE_CODES = Object.keys(LOCALES);
const isRTL = (loc) => (LOCALES[loc]?.dir || "ltr") === "rtl";

// Persisted preference (guarded — never throws in private browsing)
const readStoredLocale = () => {
  try {
    const v = window.localStorage.getItem("kirpa_locale");
    return LOCALES[v] ? v : null;
  } catch { return null; }
};
const writeStoredLocale = (loc) => {
  try { window.localStorage.setItem("kirpa_locale", loc); } catch { /* ignore */ }
};

// URL override wins over storage, so magic links can force a language:
//   ...?lang=fr
const localeFromUrl = () => {
  try {
    const v = new URLSearchParams(window.location.search).get("lang");
    return LOCALES[v] ? v : null;
  } catch { return null; }
};

// ═══════════════════════════════════════════════════════════════════════════
//  BUILT-IN UI STRING PACKS
//
//  English lives in code as the t() fallback argument and is never listed here.
//  These packs ship with the app so a translated case reads correctly the
//  moment it exists, without waiting for anyone to populate /ui_locales.
//  A Firestore pack, when present, overrides these key-by-key.
//
//  DELIBERATELY NOT TRANSLATED (identical in every language):
//    · Chemical and physiological symbols — Na, K, Cl, HCO3, iCa, Mg, PO4,
//      Alb, Urea, Cr, WBC, Hgb, Hct, Plt, pH, pCO2, pO2, BE, Lactate
//    · Modality names — CVVH, CVVHD, CVVHDF
//    · Product and device names — ST60, HF20, 9F, Prismocal, Phoxilium
//    · Units — mL/hr, mmol/L, mmHg (kept in the form printed on the machine)
//  Abbreviations that ARE words do get translated: BSA → SC (fr), SC (es).
// ═══════════════════════════════════════════════════════════════════════════
const BUILTIN_STRINGS = {
  fr: {
    "dash.topicScores":"Scores par thème",
    "dash.topicNote":"Les thèmes traversent les domaines, donc un cas peut apparaître sous plusieurs. Les totaux dépassent votre nombre de cas.",
    "prac.bay":"Baie",
    "ui.requestConsult":"Demander un avis",
    "consult.title":"Demander un avis",
    "consult.sub":"Demandez à un collègue d'examiner le cas avec vous",
    "consult.tier1":"Une piste",
    "consult.tier2":"Plus de détails",
    "consult.ask":"Demander",
    "consult.more":"En demander plus",
    "consult.back":"Retour au cas",
    "consult.never":"Annuler",
    "dash.reportTitle":"Rapport de performance",
    "dash.reportSub":"Réussite au premier essai par domaine et par thème",
    "dash.export":"Exporter",
    "dash.expand":"Détails",
    "dash.summarySub":"{n} cas principaux sur {m} terminés",
    "dash.solvedOf":"{p} sur {a} résolus du premier coup",
    "dash.byTopic":"Par thème",
    "prac.seeProgress":"Ouvrez Ma progression pour votre rendement par domaine et par thème.",
    "dash.overall":"Taux de réussite au premier essai",
    "dash.overallSub":"{p} cas sur {a} résolus du premier coup, sur {d} domaines.",
    "dash.history":"Historique des cas",
    "dash.recurringNote":"Comptabilisé sur les premiers essais, et seulement lorsque le même signalement revient plus d'une fois.",
    "dom.effluentdoseflow":"Dose d'effluent et débits",
    "dom.hemodynamicsfluid":"Hémodynamie et volémie",
    "dom.anticoagulation":"Anticoagulation",
    "dom.electrolytes":"Électrolytes",
    "dom.accesscircuitalarms":"Accès, circuit et alarmes",
    "dom.unknown":"Divers",
    "dash.cases":"Cas",
    "dash.commonHere":"Le plus fréquent dans ce domaine",
    "dash.vsOthers":"Comparaison avec les autres apprenants",
    "dash.zLine":"Moyenne du groupe : {m} % sur {n} apprenants. Vous êtes à {z} écart-type {dir}.",
    "dash.above":"au-dessus",
    "dash.below":"en dessous",
    "dash.noCohort":"La comparaison avec les autres apprenants apparaîtra lorsque suffisamment de personnes auront tenté ce domaine.",
    "calc.grpClearance":"Épuration",
    "calc.grpVolume":"Volume sanguin",
    "calc.grpFF":"Fraction de filtration",
    "calc.plasmaFlow":"Débit plasmatique",
    "calc.ff":"Fraction de filtration",
    "calc.ecv":"Volume du circuit (VEC)",
    "calc.tbv":"Volume sanguin du patient",
    "calc.ecvPct":"VEC / volume sanguin",
    "role.Paediatric Nephrology Consultant":"Néphrologue pédiatrique (consultant)",
    "role.Paediatric Critical Care Consultant":"Intensiviste pédiatrique (consultant)",
    "role.Adult Nephrology Consultant":"Néphrologue adulte (consultant)",
    "role.Adult Critical Care Consultant":"Intensiviste adulte (consultant)",
    "role.Nurse":"Infirmier/ère",
    "role.Nurse Educator":"Infirmier/ère enseignant(e)",
    "role.Other":"Autre",
    "shell.required":"obligatoire",
    "shell.traineePortal":"Portail du stagiaire",
    "shell.casesAssigned":"Cas assignés",
    "shell.completed":"Terminés",
    "shell.pending":"En cours",
    "shell.successRate":"Taux de réussite",
    "shell.performanceByDomain":"Rendement par domaine",
    "shell.editProfile":"Modifier votre profil",
    "shell.saveChanges":"Enregistrer",
    "shell.saving":"Enregistrement…",
    "shell.nameOptional":"Nom (facultatif)",
    "shell.roleRequired":"Rôle (obligatoire)",
    "shell.selectRole":"Choisissez votre rôle…",
    "shell.yourName":"Votre nom",
    "shell.institution":"Établissement",
    "shell.name":"Nom",
    "shell.role":"Rôle",
    "shell.language":"Langue",
    "shell.case":"Cas",
    "shell.continue":"Continuer",
    "shell.next":"Suivant",
    "shell.notFound":"Devoir introuvable.",
    "shell.noSubmission":"Aucune donnée de soumission disponible.",
    "shell.saveFailed":"Enregistrement impossible. Veuillez réessayer.",
    "dom.clearance":"Épuration",
    "dom.electrolytes":"Électrolytes",
    "dom.hemodynamics":"Hémodynamie",
    "dom.anticoagulation":"Anticoagulation",
    "dom.accessCircuit":"Accès / Circuit / Machine",
    "dom.generalPrescribing":"Prescription générale",
    "dom.general":"Général",
    "dom.other":"Autre",
    "role.Paediatric Nephrology Fellow":"Fellow en néphrologie pédiatrique",
    "role.Paediatric Critical Care Fellow":"Fellow en soins intensifs pédiatriques",
    "role.Adult Nephrology Fellow":"Fellow en néphrologie adulte",
    "role.Adult Critical Care Fellow":"Fellow en soins intensifs adultes",
    "role.Resident Physician":"Médecin résident",
    "role.Consultant — Paediatric Nephrologist":"Consultant — néphrologue pédiatrique",
    "role.Consultant — Paediatric Critical Care":"Consultant — soins intensifs pédiatriques",
    "role.Consultant — Adult Nephrologist":"Consultant — néphrologue adulte",
    "role.Consultant — Adult Critical Care":"Consultant — soins intensifs adultes",
    "role.Critical Care Nurse":"Infirmier/ère en soins intensifs",
    "role.Nursing Student":"Étudiant(e) en sciences infirmières",
    "role.Other (please specify)":"Autre (veuillez préciser)",
    "ui.manualRelease":"Votre superviseur publiera manuellement la rétroaction pour ce cas.",
    "ui.submitting":"Envoi en cours…",
    "ui.submitRx":"Soumettre la prescription ↗",
    "rx.accessShort":"Accès",
    "rx.primeShort":"Amorçage",
    "calc.clearanceBsa":"Clairance / SC",
    "calc.clearanceWt":"Clairance / poids",
    "calc.netUfWt":"UF nette / poids",
    "ui.noAttempts":"Aucune tentative restante",
    "ui.feedbackPending":"Rétroaction en attente de publication par votre superviseur.",
    "ui.caseUnavailable":"Données du cas non disponibles.",
    "ui.tryAgain":"Revoyez les détails du cas et réessayez.",
    "ui.submitFailed":"Envoi impossible. Veuillez réessayer.",
    "ui.genericError":"Une erreur est survenue. Veuillez réessayer.",
    "ui.allFieldsRequired":"Tous les champs sont obligatoires.",
    "ui.specifyRole":"Veuillez préciser votre rôle.",
    "prof.fullName":"Nom complet",
    "prof.institution":"Établissement / Hôpital",
    "prof.email":"Courriel institutionnel",
    "prof.role":"Rôle",
    "prof.roleOther":"Autre (veuillez préciser)",
    "prof.rolePlaceholder":"Décrivez votre rôle…",
    "role.paedNephFellow":"Fellow en néphrologie pédiatrique",
    "role.paedNeph":"Néphrologue pédiatrique",
    "role.paedPICUFellow":"Fellow en soins intensifs pédiatriques",
    "role.paedPICU":"Intensiviste pédiatrique",
    "role.adultNephFellow":"Fellow en néphrologie adulte",
    "role.adultNeph":"Néphrologue adulte",
    "role.adultICUFellow":"Fellow en soins intensifs adultes",
    "role.adultICU":"Intensiviste adulte",
    "role.resident":"Médecin résident",
    "role.icuNurse":"Infirmier/ère en soins intensifs",
    "role.nursingStudent":"Étudiant(e) en sciences infirmières",
    // Sections
    "sec.patientInfo":"Données du patient",
    "sec.vitals":"Signes vitaux",
    "sec.labs":"Résultats de laboratoire",
    "sec.gas":"Gaz sanguin",
    "sec.cbc":"Formule sanguine complète",
    "sec.circuitSetup":"Montage du circuit",
    "sec.flowRates":"Débits",
    "sec.additives":"Additifs",
    "sec.calculated":"Valeurs calculées",
    "sec.startingRx":"Prescription initiale",
    "sec.clinicalScenario":"Scénario clinique",
    "sec.learningPoint":"Point d'apprentissage",
    "sec.successCriteria":"Critères de réussite",
    // Patient
    "pt.weight":"Poids",
    "pt.age":"Âge",
    "pt.bsa":"SC",
    "pt.tbv":"VST estimé",
    "pt.years":"ans",
    "pt.months":"mois",
    // Prescription
    "rx.modality":"Modalité",
    "rx.access":"Cathéter d'accès",
    "rx.filter":"Filtre",
    "rx.prime":"Amorçage du circuit",
    "rx.anticoag":"Anticoagulation",
    "rx.solution":"Solution",
    "rx.bfr":"Débit sanguin",
    "rx.pbp":"Pré-pompe (PBP)",
    "rx.dialysate":"Débit du dialysat",
    "rx.replacement":"Réinjection post-filtre",
    "rx.netUf":"Ultrafiltration nette (UF) — positif = liquide retiré",
    "rx.kAdd":"Ajout de potassium",
    "rx.po4Add":"Ajout de phosphate",
    "rx.naAdd":"Ajout de sodium",
    "rx.freeWater":"Eau libre",
    "rx.ecv":"Volume du circuit (VEC)",
    "rx.ecvPct":"Volume du circuit en % du volume sanguin",
    "rx.clearance":"Dose d'épuration",
    "rx.blank":"Ce cas commence avec une prescription vierge — le stagiaire rédige toutes les ordonnances initiales.",
    // Controls
    "ui.labUnits":"Unités de laboratoire",
    "ui.filters":"Filtres",
    "ui.solutions":"Solutions",
    "ui.brand":"Marque",
    "ui.generic":"Générique",
    "ui.attempts":"Tentatives",
    "ui.patientMonitor":"Moniteur du patient",
    "ui.crrtPrescription":"Prescription d'EERC",
    "ui.clinicalHandover":"Transmission clinique",
    "ui.bedsideActions":"Interventions au chevet",
    "ui.crrtCalcs":"Calculs EERC",
  },
  es: {
    "dash.topicScores":"Puntuación por tema",
    "dash.topicNote":"Los temas atraviesan los dominios, por lo que un caso puede aparecer en varios. Los totales superan su número de casos.",
    "prac.bay":"Bahía",
    "ui.requestConsult":"Pedir una consulta",
    "consult.title":"Pedir una consulta",
    "consult.sub":"Pida a un colega que revise el caso con usted",
    "consult.tier1":"Una pista",
    "consult.tier2":"Más detalle",
    "consult.ask":"Preguntar",
    "consult.more":"Pedir más",
    "consult.back":"Volver al caso",
    "consult.never":"Cancelar",
    "dash.reportTitle":"Informe de rendimiento",
    "dash.reportSub":"Acierto al primer intento por dominio y tema",
    "dash.export":"Exportar",
    "dash.expand":"Ampliar",
    "dash.summarySub":"{n} de {m} casos principales completados",
    "dash.solvedOf":"{p} de {a} resueltos al primer intento",
    "dash.byTopic":"Por tema",
    "prac.seeProgress":"Abra Mi progreso para ver su rendimiento por dominio y por tema.",
    "dash.overall":"Tasa de acierto al primer intento",
    "dash.overallSub":"{p} de {a} casos resueltos al primer intento, en {d} dominios.",
    "dash.history":"Historial de casos",
    "dash.recurringNote":"Contabilizado en primeros intentos, y solo cuando la misma señal aparece más de una vez.",
    "dom.effluentdoseflow":"Dosis de efluente y flujos",
    "dom.hemodynamicsfluid":"Hemodinámica y volemia",
    "dom.anticoagulation":"Anticoagulación",
    "dom.electrolytes":"Electrolitos",
    "dom.accesscircuitalarms":"Acceso, circuito y alarmas",
    "dom.unknown":"Varios",
    "dash.cases":"Casos",
    "dash.commonHere":"Lo más frecuente en este dominio",
    "dash.vsOthers":"Comparación con otros usuarios",
    "dash.zLine":"Media del grupo: {m} % entre {n} usuarios. Usted está a {z} desviaciones estándar {dir}.",
    "dash.above":"por encima",
    "dash.below":"por debajo",
    "dash.noCohort":"La comparación con otros usuarios aparecerá cuando suficientes personas hayan intentado este dominio.",
    "calc.grpClearance":"Depuración",
    "calc.grpVolume":"Volumen sanguíneo",
    "calc.grpFF":"Fracción de filtración",
    "calc.plasmaFlow":"Flujo plasmático",
    "calc.ff":"Fracción de filtración",
    "calc.ecv":"Volumen del circuito (VEC)",
    "calc.tbv":"Volumen sanguíneo del paciente",
    "calc.ecvPct":"VEC / volumen sanguíneo",
    "role.Paediatric Nephrology Consultant":"Nefrólogo pediátrico (adjunto)",
    "role.Paediatric Critical Care Consultant":"Intensivista pediátrico (adjunto)",
    "role.Adult Nephrology Consultant":"Nefrólogo de adultos (adjunto)",
    "role.Adult Critical Care Consultant":"Intensivista de adultos (adjunto)",
    "role.Nurse":"Enfermero/a",
    "role.Nurse Educator":"Enfermero/a docente",
    "role.Other":"Otro",
    "shell.required":"obligatorio",
    "shell.traineePortal":"Portal del residente",
    "shell.casesAssigned":"Casos asignados",
    "shell.completed":"Completados",
    "shell.pending":"Pendientes",
    "shell.successRate":"Tasa de éxito",
    "shell.performanceByDomain":"Rendimiento por dominio",
    "shell.editProfile":"Editar su perfil",
    "shell.saveChanges":"Guardar",
    "shell.saving":"Guardando…",
    "shell.nameOptional":"Nombre (opcional)",
    "shell.roleRequired":"Rol (obligatorio)",
    "shell.selectRole":"Seleccione su rol…",
    "shell.yourName":"Su nombre",
    "shell.institution":"Institución",
    "shell.name":"Nombre",
    "shell.role":"Rol",
    "shell.language":"Idioma",
    "shell.case":"Caso",
    "shell.continue":"Continuar",
    "shell.next":"Siguiente",
    "shell.notFound":"Tarea no encontrada.",
    "shell.noSubmission":"No hay datos de envío disponibles.",
    "shell.saveFailed":"No se pudo guardar. Inténtelo de nuevo.",
    "dom.clearance":"Depuración",
    "dom.electrolytes":"Electrolitos",
    "dom.hemodynamics":"Hemodinámica",
    "dom.anticoagulation":"Anticoagulación",
    "dom.accessCircuit":"Acceso / Circuito / Máquina",
    "dom.generalPrescribing":"Prescripción general",
    "dom.general":"General",
    "dom.other":"Otro",
    "role.Paediatric Nephrology Fellow":"Residente de nefrología pediátrica",
    "role.Paediatric Critical Care Fellow":"Residente de cuidados intensivos pediátricos",
    "role.Adult Nephrology Fellow":"Residente de nefrología de adultos",
    "role.Adult Critical Care Fellow":"Residente de cuidados intensivos de adultos",
    "role.Resident Physician":"Médico residente",
    "role.Consultant — Paediatric Nephrologist":"Adjunto — nefrólogo pediátrico",
    "role.Consultant — Paediatric Critical Care":"Adjunto — cuidados intensivos pediátricos",
    "role.Consultant — Adult Nephrologist":"Adjunto — nefrólogo de adultos",
    "role.Consultant — Adult Critical Care":"Adjunto — cuidados intensivos de adultos",
    "role.Critical Care Nurse":"Enfermero/a de cuidados intensivos",
    "role.Nursing Student":"Estudiante de enfermería",
    "role.Other (please specify)":"Otro (especifique)",
    "ui.manualRelease":"Su docente publicará manualmente la retroalimentación de este caso.",
    "ui.submitting":"Enviando…",
    "ui.submitRx":"Enviar la prescripción ↗",
    "rx.accessShort":"Acceso",
    "rx.primeShort":"Cebado",
    "calc.clearanceBsa":"Aclaramiento / SC",
    "calc.clearanceWt":"Aclaramiento / peso",
    "calc.netUfWt":"UF neta / peso",
    "ui.noAttempts":"No quedan intentos",
    "ui.feedbackPending":"Retroalimentación pendiente de publicación por su docente.",
    "ui.caseUnavailable":"Datos del caso no disponibles.",
    "ui.tryAgain":"Revise los detalles del caso e inténtelo de nuevo.",
    "ui.submitFailed":"No se pudo enviar. Inténtelo de nuevo.",
    "ui.genericError":"Algo salió mal. Inténtelo de nuevo.",
    "ui.allFieldsRequired":"Todos los campos son obligatorios.",
    "ui.specifyRole":"Por favor especifique su rol.",
    "prof.fullName":"Nombre completo",
    "prof.institution":"Institución / Hospital",
    "prof.email":"Correo institucional",
    "prof.role":"Rol",
    "prof.roleOther":"Otro (especifique)",
    "prof.rolePlaceholder":"Describa su rol…",
    "role.paedNephFellow":"Residente de nefrología pediátrica",
    "role.paedNeph":"Nefrólogo pediátrico",
    "role.paedPICUFellow":"Residente de cuidados intensivos pediátricos",
    "role.paedPICU":"Intensivista pediátrico",
    "role.adultNephFellow":"Residente de nefrología de adultos",
    "role.adultNeph":"Nefrólogo de adultos",
    "role.adultICUFellow":"Residente de cuidados intensivos de adultos",
    "role.adultICU":"Intensivista de adultos",
    "role.resident":"Médico residente",
    "role.icuNurse":"Enfermero/a de cuidados intensivos",
    "role.nursingStudent":"Estudiante de enfermería",
    "sec.patientInfo":"Datos del paciente",
    "sec.vitals":"Signos vitales",
    "sec.labs":"Resultados de laboratorio",
    "sec.gas":"Gasometría",
    "sec.cbc":"Hemograma completo",
    "sec.circuitSetup":"Montaje del circuito",
    "sec.flowRates":"Flujos",
    "sec.additives":"Aditivos",
    "sec.calculated":"Valores calculados",
    "sec.startingRx":"Prescripción inicial",
    "sec.clinicalScenario":"Escenario clínico",
    "sec.learningPoint":"Punto de aprendizaje",
    "sec.successCriteria":"Criterios de éxito",
    "pt.weight":"Peso",
    "pt.age":"Edad",
    "pt.bsa":"SC",
    "pt.tbv":"VST estimado",
    "pt.years":"años",
    "pt.months":"meses",
    "rx.modality":"Modalidad",
    "rx.access":"Catéter de acceso",
    "rx.filter":"Filtro",
    "rx.prime":"Cebado del circuito",
    "rx.anticoag":"Anticoagulación",
    "rx.solution":"Solución",
    "rx.bfr":"Flujo sanguíneo",
    "rx.pbp":"Prebomba (PBP)",
    "rx.dialysate":"Flujo de dializante",
    "rx.replacement":"Reposición posfiltro",
    "rx.netUf":"Ultrafiltración neta (UF) — positivo = líquido retirado",
    "rx.kAdd":"Aporte de potasio",
    "rx.po4Add":"Aporte de fosfato",
    "rx.naAdd":"Aporte de sodio",
    "rx.freeWater":"Agua libre",
    "rx.ecv":"Volumen del circuito (VEC)",
    "rx.ecvPct":"Volumen del circuito como % del volumen sanguíneo",
    "rx.clearance":"Dosis de depuración",
    "rx.blank":"Este caso comienza con una prescripción en blanco — el residente redacta todas las órdenes iniciales.",
    "ui.labUnits":"Unidades de laboratorio",
    "ui.filters":"Filtros",
    "ui.solutions":"Soluciones",
    "ui.brand":"Marca",
    "ui.generic":"Genérico",
    "ui.attempts":"Intentos",
    "ui.patientMonitor":"Monitor del paciente",
    "ui.crrtPrescription":"Prescripción de TCRR",
    "ui.clinicalHandover":"Transferencia clínica",
    "ui.bedsideActions":"Intervenciones a pie de cama",
    "ui.crrtCalcs":"Cálculos TCRR",
  },
};

// ─── UI STRING PACKS ─────────────────────────────────────────────────────────
// /ui_locales/{locale} → { label, dir, enabled, leads:[email], strings:{key:value} }
// English is never fetched: it lives in code as the t() fallback argument.
const _stringCache = new Map();   // locale → strings object

async function loadUiStrings(locale) {
  if (locale === DEFAULT_LOCALE) return {};
  if (_stringCache.has(locale)) return _stringCache.get(locale);
  try {
    const snap = await getDoc(doc(db, "ui_locales", locale));
    const strings = snap.exists() ? (snap.data().strings || {}) : {};
    _stringCache.set(locale, strings);
    return strings;
  } catch (e) {
    console.warn("[KiRPA i18n] loadUiStrings", locale, e);
    return {};
  }
}

// Interpolation: t("assign.count","{n} assignments",{n:4})
const interpolate = (str, vars) =>
  !vars ? str : String(str).replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined ? vars[k] : m));

// ─── LOCALE CONTEXT ──────────────────────────────────────────────────────────
const LocaleContext = React.createContext({
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  dir: "ltr",
  ready: true,
  t: (key, fallback) => fallback ?? key,
  fmtNum: (n) => String(n),
  fmtDate: (d) => String(d),
});

function LocaleProvider({ children, initialLocale }) {
  const [locale, setLocaleState] = useState(
    initialLocale || localeFromUrl() || readStoredLocale() || DEFAULT_LOCALE
  );
  const [strings, setStrings] = useState({});
  const [ready, setReady] = useState(locale === DEFAULT_LOCALE);

  useEffect(() => {
    let cancelled = false;
    setReady(locale === DEFAULT_LOCALE);
    loadUiStrings(locale).then(s => {
      if (cancelled) return;
      setStrings(s);
      setReady(true);
    });
    return () => { cancelled = true; };
  }, [locale]);

  // Document direction + lang attribute. Applied globally so native form
  // controls, scrollbars and text selection behave correctly in Arabic.
  useEffect(() => {
    const el = document.documentElement;
    const prevDir = el.dir, prevLang = el.lang;
    el.dir = LOCALES[locale]?.dir || "ltr";
    el.lang = locale;
    return () => { el.dir = prevDir; el.lang = prevLang; };
  }, [locale]);

  const setLocale = (loc) => {
    if (!LOCALES[loc]) return;
    writeStoredLocale(loc);
    setLocaleState(loc);
  };

  // t(key, englishFallback, vars?)
  // Resolution order: Firestore pack (contributor-editable) → built-in pack
  // (ships with the app) → the English fallback argument. Because the fallback
  // IS the English source, unwrapped strings and missing keys both degrade to
  // English rather than to a raw key name.
  const t = (key, fallback, vars) => {
    const raw = (strings && strings[key])
      || (BUILTIN_STRINGS[locale] && BUILTIN_STRINGS[locale][key])
      || fallback || key;
    return interpolate(raw, vars);
  };

  const intlTag = LOCALES[locale]?.intl || "en-CA";
  const fmtNum = (n, opts) => {
    if (n === null || n === undefined || n === "") return "";
    const num = typeof n === "number" ? n : parseFloat(n);
    if (!isFinite(num)) return String(n);
    try { return new Intl.NumberFormat(intlTag, opts).format(num); }
    catch { return String(n); }
  };
  const fmtDate = (d, opts = { year:"numeric", month:"short", day:"numeric" }) => {
    try { return new Intl.DateTimeFormat(intlTag, opts).format(new Date(d)); }
    catch { return String(d); }
  };

  const value = { locale, setLocale, dir: LOCALES[locale]?.dir || "ltr", ready, t, fmtNum, fmtDate, strings };
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

const useLocale = () => React.useContext(LocaleContext);

// Role and domain values are stored in English as canonical identifiers and are
// only ever translated at render. The stored value never changes, so research
// data and educator-facing exports stay in English regardless of what the
// trainee saw on screen.
const tRole   = (t, role)   => t(`role.${role}`, role);
// research_events stores protocol codes (effluent_dose_flow, access_circuit_alarms).
// DOMAIN_LABELS turns those into readable English; the locale pack can override.
// Falls back through the raw code only if neither exists, so a new domain still
// renders something rather than breaking.
const tDomain = (t, domain) => {
  const code = String(domain || "");
  const english = DOMAIN_LABELS[code] || code.replace(/_/g, " ");
  return t(`dom.${code.replace(/[^A-Za-z]/g, "").toLowerCase()}`, english);
};


// ─── LOCALE OVERRIDE ──────────────────────────────────────────────────────────
// Renders a subtree in a specific language regardless of the global interface
// language. Used so that opening a French case shows French labels even when
// the educator's own interface is English: the case view follows the CASE, not
// the header picker.
function LocaleOverride({locale, children}){
  const parent = useLocale();
  const [strings,setStrings]=useState({});

  useEffect(()=>{let dead=false;
    if(!locale||locale===DEFAULT_LOCALE){setStrings({});return;}
    loadUiStrings(locale).then(s=>{if(!dead)setStrings(s);});
    return()=>{dead=true;};
  },[locale]);

  if(!locale||locale===parent.locale) return children;

  const t=(key,fallback,vars)=>{
    const raw=(strings&&strings[key])
      ||(BUILTIN_STRINGS[locale]&&BUILTIN_STRINGS[locale][key])
      ||fallback||key;
    return interpolate(raw,vars);
  };
  const value={...parent, locale, dir:LOCALES[locale]?.dir||"ltr", t};
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}



/* IMPORTANT — numeric INPUTS stay unformatted.
   fmtNum is for display only. Never pipe a value through fmtNum and back into
   an <input type="number">, or fr/es grouping separators will break parseFloat
   and silently corrupt a prescription. */

// ═══════════════════════════════════════════════════════════════════════════
//  CASE TRANSLATIONS
// ═══════════════════════════════════════════════════════════════════════════

// The only fields a translator may touch. Everything driving evaluateRoomSuccess
// stays structural and language-neutral, so a translation cannot alter scoring.
const TRANSLATABLE_CASE_FIELDS = ["title", "page", "successSummary", "learningPoint"];

// Synchronous non-cryptographic hash (FNV-1a). Used only to detect that the
// English source changed after a translation was written — not for security.
const textHash = (str) => {
  let h = 0x811c9dc5;
  const s = String(str);
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(16).padStart(8, "0");
};

// Hash of the English source fields, in fixed order.
const caseSourceHash = (caseData) =>
  textHash(TRANSLATABLE_CASE_FIELDS.map(f => caseData?.[f] ?? "").join("\u0000"));

const translationDocId = (caseId, locale) => `${caseId}__${locale}`;

// ─── LOAD ────────────────────────────────────────────────────────────────────
// One query per locale, cached for the session. Small docs, so this stays cheap
// even at a few hundred translated cases.
const _translationCache = new Map();  // locale → { [caseId]: translationDoc }

async function loadCaseTranslations(locale, { force = false } = {}) {
  if (locale === DEFAULT_LOCALE) return {};
  // Rules gate this collection to approved educators (fields.successSummary and
  // fields.learningPoint are answer keys). Trainee sessions are unauthenticated
  // and receive translated case text via the CF payload instead, so skip the
  // query rather than provoke a guaranteed permission-denied.
  if (!auth.currentUser) return {};
  if (!force && _translationCache.has(locale)) return _translationCache.get(locale);
  try {
    const qs = await getDocs(
      query(collection(db, "case_translations"), where("locale", "==", locale))
    );
    const map = {};
    qs.forEach(d => {
      const data = { id: d.id, ...d.data() };
      const prev = map[data.caseId];
      // Verified wins over draft if both somehow exist for the same caseId.
      if (!prev || (data.status === "verified" && prev.status !== "verified")) {
        map[data.caseId] = data;
      }
    });
    _translationCache.set(locale, map);
    return map;
  } catch (e) {
    console.warn("[KiRPA i18n] loadCaseTranslations", locale, e);
    return {};
  }
}

const invalidateTranslationCache = (locale) => {
  if (locale) _translationCache.delete(locale); else _translationCache.clear();
};

// ─── APPLY ───────────────────────────────────────────────────────────────────
// Overlay text onto the canonical case. Returns the ORIGINAL object reference
// when nothing applies, so existing memoisation and equality checks are safe.
//
// Resolution: verified translation → any translation (flagged) → English.
// A blank field in the translation falls back to English individually, so a
// partial translation is usable rather than broken.
function localizeCase(caseId, caseData, translations, { allowUnverified = true, requestedLocale = null } = {}) {
  if (!caseData || !translations) return caseData;

  // sourceLocale is the language the case was AUTHORED in. When the requested
  // language equals the source language the text is original, not translated:
  // no overlay, and no "unverified translation" disclaimer.
  const src = caseData.sourceLocale || DEFAULT_LOCALE;
  if (requestedLocale && requestedLocale === src) return caseData;

  const tr = translations[caseId];
  if (!tr) return caseData;
  if (tr.status !== "verified" && !allowUnverified) return caseData;

  const fields = tr.fields || {};
  const out = { ...caseData };
  let applied = 0;
  TRANSLATABLE_CASE_FIELDS.forEach(f => {
    const v = fields[f];
    if (typeof v === "string" && v.trim()) { out[f] = v; applied++; }
  });
  if (!applied) return caseData;

  // Custom option/intervention labels defined on the case itself.
  if (tr.optionLabels && Object.keys(tr.optionLabels).length) {
    out._optionLabels = { ...(caseData._optionLabels || {}), ...tr.optionLabels };
  }

  // Attached for the UI to surface provenance and staleness. Never persisted
  // back to the case and never sent to research_events.
  out._translation = {
    locale: tr.locale,
    sourceLocale: src,
    isTranslated: true,
    status: tr.status || "draft",
    stale: tr.sourceHash ? tr.sourceHash !== caseSourceHash(caseData) : true,
    translatorName: tr.translatorName || "",
    fieldsTranslated: applied,
    fieldsTotal: TRANSLATABLE_CASE_FIELDS.length,
    updatedAt: tr.updatedAt || null,
  };
  return out;
}

// Bulk variant for the case library.
const localizeCaseMap = (caseMap, translations, opts) =>
  Object.fromEntries(
    Object.entries(caseMap || {}).map(([id, c]) => [id, localizeCase(id, c, translations, opts)])
  );

// ─── HOOK ────────────────────────────────────────────────────────────────────
// Usage:
//   const { translations, ready, localize } = useCaseTranslations();
//   const displayCase = localize(caseId, caseData);
function useCaseTranslations(localeOverride) {
  const ctx = useLocale();
  const locale = localeOverride || ctx.locale;
  const [translations, setTranslations] = useState({});
  const [ready, setReady] = useState(locale === DEFAULT_LOCALE);

  useEffect(() => {
    let cancelled = false;
    if (locale === DEFAULT_LOCALE) { setTranslations({}); setReady(true); return; }
    setReady(false);
    loadCaseTranslations(locale).then(m => {
      if (cancelled) return;
      setTranslations(m);
      setReady(true);
    });
    return () => { cancelled = true; };
  }, [locale]);

  const localize = (caseId, caseData, opts) =>
    localizeCase(caseId, caseData, translations, { requestedLocale: locale, ...(opts || {}) });
  return { translations, ready, locale, localize, localizeMap: (m, o) => localizeCaseMap(m, translations, o) };
}


// Loads the ENTIRE translation collection once and groups it by locale, so the
// case library can show accurate per-language counts and switch languages
// without a refetch. The collection is small (one small doc per case per
// language), so a single unfiltered query is cheaper than one query per locale.
let _allTranslationsCache = null;

async function loadAllCaseTranslations({ force = false } = {}) {
  if (!auth.currentUser) return {};
  if (!force && _allTranslationsCache) return _allTranslationsCache;
  try {
    const qs = await getDocs(collection(db, "case_translations"));
    const byLocale = {};
    qs.forEach(d => {
      const data = { id: d.id, ...d.data() };
      const loc = data.locale;
      if (!loc || !LOCALES[loc]) return;
      if (!byLocale[loc]) byLocale[loc] = {};
      const prev = byLocale[loc][data.caseId];
      if (!prev || (data.status === "verified" && prev.status !== "verified")) {
        byLocale[loc][data.caseId] = data;
      }
    });
    _allTranslationsCache = byLocale;
    return byLocale;
  } catch (e) {
    console.warn("[KiRPA i18n] loadAllCaseTranslations", e);
    return {};
  }
}

const invalidateAllTranslations = () => { _allTranslationsCache = null; };

function useAllCaseTranslations() {
  const [byLocale, setByLocale] = useState({});
  const [ready, setReady] = useState(false);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    let dead = false;
    setReady(false);
    loadAllCaseTranslations().then(m => { if (!dead) { setByLocale(m); setReady(true); } });
    return () => { dead = true; };
  }, [nonce]);
  return { byLocale, ready, refresh: () => { invalidateAllTranslations(); setNonce(n => n + 1); } };
}

// ─── WRITE ───────────────────────────────────────────────────────────────────
// Called by the translation editor. sourceHash is captured from the English
// case at save time, which is what makes staleness detectable later.
// `status` is deliberately capped at "submitted" here; promotion to "verified"
// is a privileged write (admin or language lead) enforced in security rules.
async function saveCaseTranslation({ caseId, locale, sourceCase, fields, optionLabels, status = "draft", translator }) {
  if (!caseId || !locale || locale === DEFAULT_LOCALE) throw new Error("bad translation target");
  if (!["draft", "submitted"].includes(status)) throw new Error("status must be draft or submitted");

  const clean = {};
  TRANSLATABLE_CASE_FIELDS.forEach(f => {
    const v = fields?.[f];
    if (typeof v === "string") clean[f] = v.trim();
  });

  const payload = stripUndefined({
    caseId,
    locale,
    fields: clean,
    optionLabels: optionLabels || {},
    sourceHash: caseSourceHash(sourceCase || {}),
    status,
    translatorEmail: (translator?.email || auth.currentUser?.email || "").toLowerCase(),
    translatorName: translator?.name || "",
    translatorInstitution: translator?.institution || "",
    updatedAt: serverTimestamp(),
  });

  await setDoc(doc(db, "case_translations", translationDocId(caseId, locale)), payload, { merge: true });
  invalidateTranslationCache(locale);
  return payload;
}

async function getCaseTranslation(caseId, locale) {
  try {
    const snap = await getDoc(doc(db, "case_translations", translationDocId(caseId, locale)));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch (e) {
    console.warn("[KiRPA i18n] getCaseTranslation", e);
    return null;
  }
}

// Coverage summary for the translation dashboard: how many cases in a given
// pool have a translation, and how many of those have drifted from source.
function translationCoverage(caseMap, translations) {
  const ids = Object.keys(caseMap || {});
  let translated = 0, verified = 0, stale = 0;
  ids.forEach(id => {
    const tr = translations?.[id];
    if (!tr) return;
    translated++;
    if (tr.status === "verified") verified++;
    if (tr.sourceHash && tr.sourceHash !== caseSourceHash(caseMap[id])) stale++;
  });
  return { total: ids.length, translated, verified, stale, pct: ids.length ? Math.round(translated / ids.length * 100) : 0 };
}



// ─── DESIGN TOKENS ───────────────────────────────────────────────────────────
const FONT_UI   = "'Inter','system-ui',sans-serif";
const FONT_CLIN = "'Lora','Georgia',serif";
// Instrument face for the monitor: numerals, units and channel labels. Tabular
// figures keep values from shifting horizontally as they update, which is what
// makes a readout feel like equipment rather than a form.
const FONT_MONO = "ui-monospace,'SF Mono','JetBrains Mono','Cascadia Mono',Menlo,Consolas,monospace";
const C={
  pu:"#4F2683",dark:"#1a0533",mid:"#2d1054",
  card:{background:"white",borderRadius:"12px",
    boxShadow:"0 2px 8px rgba(0,0,0,0.07),0 0 0 1px rgba(0,0,0,0.04)",
    border:"1px solid #e5e7eb"},
  lbl:{display:"block",fontSize:"10px",fontWeight:"700",color:"#6b7280",
    textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:"4px",fontFamily:FONT_UI},
  inp:{width:"100%",padding:"7px 10px",border:"1px solid #d1d5db",borderRadius:"7px",
    fontSize:"13px",boxSizing:"border-box",fontFamily:FONT_UI,outline:"none",
    lineHeight:"1.5"},
  sel:{width:"100%",padding:"7px 10px",border:"1px solid #d1d5db",borderRadius:"7px",
    fontSize:"13px",boxSizing:"border-box",fontFamily:FONT_UI,background:"white"},
  tag:(bg,col)=>({padding:"3px 9px",background:bg,color:col,borderRadius:"12px",
    fontSize:"11px",fontWeight:"600",display:"inline-block",fontFamily:FONT_UI,
    letterSpacing:"0.01em"}),
  btn:(bg,col="#fff",ex={})=>({padding:"8px 16px",background:bg,color:col,border:"none",
    borderRadius:"8px",cursor:"pointer",fontWeight:"600",fontSize:"13px",
    display:"flex",alignItems:"center",gap:"6px",fontFamily:FONT_UI,
    letterSpacing:"0.01em",...ex}),
  secHead:{fontSize:"10px",fontWeight:"700",color:"#4F2683",textTransform:"uppercase",
    letterSpacing:"0.07em",marginBottom:"8px",paddingBottom:"4px",
    borderBottom:"1px solid #ede9fe",fontFamily:FONT_UI},
};

// ─── ICON ─────────────────────────────────────────────────────────────────────
function Ic({n,s="w-5 h-5",style={}}){
  const p={edit:"M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z",
    send:"M12 19l9 2-9-18-9 18 9-2zm0 0v-8",user:"M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z",
    check:"M5 13l4 4L19 7",eye:"M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z",
    x:"M6 18L18 6M6 6l12 12",back:"M10 19l-7-7m0 0l7-7m-7 7h18",plus:"M12 4v16m8-8H4",
    trash:"M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16",
    lock:"M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z",
    chart:"M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
    info:"M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
    save:"M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4",
    globe:"M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  };
  if(n==="spin") return <svg className={s} style={{animation:"spin 1s linear infinite",...style}} fill="none" viewBox="0 0 24 24"><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" style={{opacity:0.25}}/><path fill="currentColor" style={{opacity:0.75}} d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>;
  return <svg className={s} style={style} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d={p[n]||""}/></svg>;
}


// ─── TRAINEE MAGIC LINK VIEW (when coming from email ?trainee_token=XYZ) ──────
function TraineeMagicLink({ token, onExit }) {
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");
  const [session, setSession] = useState(null);

  // Delivery language. Seeded from the LocaleProvider, which already picked up
  // ?lang= from the magic link. Changing it re-fetches, because translations
  // are resolved server-side rather than held on the client.
  const { locale: uiLocale, setLocale } = useLocale();
  const [traineeLocale, setTraineeLocale] = useState(uiLocale);

  // Keep interface chrome and case language in step when the trainee switches.
  const changeTraineeLocale = (loc) => {
    if (!LOCALES[loc]) return;
    setTraineeLocale(loc);
    setLocale(loc);
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setBusy(true);
        setErr("");

        const functions = getFunctions(firebaseApp, "us-central1");
        const validate = httpsCallable(functions, "validateTraineeToken");

        // Expected response (from CF):
        // {
        //   trainee: { email, name?, role?, institution?, institutionId? },
        //   requireProfile: true/false,
        //   isAnonymous: true/false,
        //   assignments: [{ id, caseId, caseTitle, caseFacet, educatorName, feedbackMode, status, attempts?, lastResult? }],
        // }
        // The CF resolves translations at delivery time. Passing the trainee's
        // current locale lets it return localised case text, and it returns
        // availableLocales so we only offer languages that actually exist.
        // Sending the practice device id lets the server bind this browser's
        // anonymous practice history to the same learner. The magic link already
        // proves ownership of the email, so no extra step is needed.
        let _dev = null;
        try { _dev = getDeviceId(); } catch {}
        const res = await validate({ token, locale: traineeLocale, deviceId: _dev });

        if (!alive) return;

        const payload = res?.data || null;

        // Remove token from URL so refresh doesn't "consume" again
        try {
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch {}

        setSession({
          token,
          ...payload,
        });
      } catch (e) {
        console.error(e);
        const msg =
          e?.details?.message ||
          e?.message ||
          "Unable to open link. Please request a new secure link.";
        setErr(msg);
      } finally {
        if (alive) setBusy(false);
      }
    })();
    return () => { alive = false; };
  }, [token, traineeLocale]);

  if (busy) {
    return (
      <div style={{minHeight:"100vh",background:"#0f0a1e",display:"flex",alignItems:"center",justifyContent:"center",padding:"24px"}}>
        <div style={{color:"rgba(255,255,255,0.85)",fontFamily:FONT_UI,fontWeight:"800"}}>Opening secure link…</div>
      </div>
    );
  }

  if (err) {
    return (
      <div style={{minHeight:"100vh",background:"#0f0a1e",display:"flex",alignItems:"center",justifyContent:"center",padding:"24px"}}>
        <div style={{maxWidth:"640px",width:"100%",fontFamily:FONT_UI}}>
          <div style={{color:"white",fontWeight:"900",fontSize:"18px",marginBottom:"10px"}}>KiRPA · Trainee Portal</div>
          <div style={{padding:"14px 16px",background:"rgba(239,68,68,0.12)",border:"1px solid rgba(239,68,68,0.25)",borderRadius:"12px",color:"rgba(255,255,255,0.88)",fontSize:"13px"}}>
            {err}
          </div>
          <div style={{marginTop:"14px"}}>
            <button onClick={onExit} style={{background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.15)",borderRadius:"10px",color:"rgba(255,255,255,0.85)",padding:"8px 12px",cursor:"pointer",fontSize:"12px",fontWeight:"800",fontFamily:FONT_UI}}>
              ← Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If we have a validated session, render the full trainee app experience:
  return <TraineeSessionApp session={session} onExit={onExit}
    traineeLocale={traineeLocale} onChangeLocale={changeTraineeLocale}
    availableLocales={session?.availableLocales || ["en"]} />;
}
// ─────────────────────────────────────────────────────────────────────────────
// TRAINEE FULL EXPERIENCE (Profile → Assignment list → Run case → Submit)
// ─────────────────────────────────────────────────────────────────────────────

// ─── TRAINEE DESIGN TOKENS ───────────────────────────────────────────────────
const T = {
  bg:          "#f0f3fa",
  bgDeep:      "#e8edf7",
  card:        "#ffffff",
  border:      "#d8e0f0",
  borderStrong:"#b8c6e0",
  text:        "#0b1324",
  muted:       "#526080",
  accent:      "#4f2683",
  accentLight: "#ede9fe",
  accentMid:   "#7c3aed",
  accentDark:  "#3b1a6b",
  accentGlow:  "rgba(79,38,131,0.18)",
  success:       "#059669",
  successBg:     "#ecfdf5",
  successBorder: "#6ee7b7",
  danger:        "#dc2626",
  dangerBg:      "#fef2f2",
  dangerBorder:  "#fca5a5",
  warn:          "#b45309",
  warnBg:        "#fef3c7",
  radius:  "14px",
  radiusSm:"9px",
  shadow:  "0 1px 3px rgba(15,23,90,0.07), 0 4px 14px rgba(15,23,90,0.06)",
  shadowMd:"0 8px 24px rgba(15,23,90,0.13)",
  font:    "'Inter', 'DM Sans', system-ui, sans-serif",
  mono:    "'JetBrains Mono', 'Fira Code', monospace",
};

// ── Result Modal Overlay ──────────────────────────────────────────────────────
function ResultModal({ result, maxAttempts, caseData, onRetry, onNext, onBack, hasNext, onDismiss, isFeedbackVisible=true, feedbackPendingNote=null, frame=null, caseId=null }) {
  const attemptsLeft = (maxAttempts||1) - (result.attempts||0);
  const exhausted    = attemptsLeft <= 0;
  // A ceiling this high is a stand-in for "unlimited", not a limit a learner
  // could ever reach, so the attempt budget is hidden entirely.
  const unlimited    = (maxAttempts||1) >= 50;
  const canContinue  = result.success || (exhausted && (maxAttempts||1) < 50) || !isFeedbackVisible;

  return (
    <div style={{position:"fixed",inset:0,zIndex:999,display:"flex",alignItems:"center",justifyContent:"center",
      background:"rgba(15,23,42,0.6)",backdropFilter:"blur(5px)",padding:"24px"}}
      onClick={e=>{ if(e.target===e.currentTarget && onDismiss) onDismiss(); }}>
      <div style={{width:"100%",maxWidth:"500px",background:T.card,borderRadius:"18px",
        boxShadow:"0 32px 64px rgba(0,0,0,0.25)",overflow:"hidden",fontFamily:T.font}}>

        {/* colour bar */}
        <div style={{height:"4px",background: !isFeedbackVisible&&canContinue ? "#a78bfa" : result.success ? T.success : exhausted ? T.danger : T.warn}}/>

        <div style={{padding:"24px 24px 20px"}}>
          {/* header row */}
          <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:"16px"}}>
            <div style={{display:"flex",alignItems:"center",gap:"12px"}}>
              <div style={{width:"42px",height:"42px",borderRadius:"50%",flexShrink:0,
                display:"flex",alignItems:"center",justifyContent:"center",fontSize:"18px",fontWeight:"700",
                background: (!isFeedbackVisible&&canContinue) ? "#ede9fe" : result.success ? T.successBg : exhausted ? T.dangerBg : T.warnBg,
                border:`1.5px solid ${(!isFeedbackVisible&&canContinue)?"#c4b5fd":result.success?T.successBorder:exhausted?T.dangerBorder:"#fde68a"}`}}>
                {(!isFeedbackVisible&&canContinue) ? "✓" : result.success ? "✓" : exhausted ? "✕" : "↩"}
              </div>
              <div>
                <div style={{fontSize:"16px",fontWeight:"700",color:T.text,lineHeight:1.2}}>
                  {!isFeedbackVisible && canContinue
                    ? "Submission received"
                    : result.success ? "Case complete"
                      : (exhausted && !unlimited) ? "Attempts exhausted"
                      : "Not quite"}
                </div>
                <div style={{fontSize:"12px",color:T.muted,marginTop:"2px"}}>
                  {isFeedbackVisible
                    ? (result.success
                        ? (unlimited ? "Solved" : `Passed · attempt ${result.attempts} of ${maxAttempts||1}`)
                        : (unlimited
                            ? "That prescription didn't solve the problem. Review the chart and try again."
                            : `Attempt ${result.attempts} of ${maxAttempts||1}${!exhausted?` · ${attemptsLeft} remaining`:""}`))
                    : (canContinue
                        ? (unlimited ? "Response recorded" : `Attempt ${result.attempts} of ${maxAttempts||1} recorded`)
                        : (unlimited
                            ? "That prescription didn't solve the problem. Review the chart and try again."
                            : `Attempt ${result.attempts} of ${maxAttempts||1}${!exhausted?` · ${attemptsLeft} remaining`:""}`))}
                </div>
              </div>
            </div>
            {/* ✕ close — always visible; dismisses without advancing */}
            <button onClick={onDismiss}
              style={{background:"none",border:"none",cursor:"pointer",color:T.muted,
                fontSize:"20px",lineHeight:1,padding:"2px 4px",fontFamily:T.font}}>×</button>
          </div>

          {/* feedback — gated on isFeedbackVisible */}
          {isFeedbackVisible && (result.feedback || caseData?.learningPoint) && canContinue && (
            <div style={{padding:"13px 15px",borderRadius:T.radiusSm,marginBottom:"16px",
              background: result.success ? T.successBg : T.dangerBg,
              border:`1px solid ${result.success?T.successBorder:T.dangerBorder}`}}>
              <div style={{fontSize:"10px",fontWeight:"700",color:T.muted,textTransform:"uppercase",
                letterSpacing:"0.07em",marginBottom:"5px"}}>
                {result.success ? "Key takeaway" : "Learning point"}
              </div>
              <div style={{fontSize:"13px",color:T.text,lineHeight:"1.6"}}>
                {result.feedback || caseData?.learningPoint}
              </div>
            </div>
          )}
          {/* The learner's own frame, above the verdict. Seeing your reasoning
              beside what happened turns a mark into a comparison. */}
          {isFeedbackVisible && canContinue && <FrameRecall frame={frame} caseData={caseData}/>}

          {/* What the case was actually asking for. Shown whenever feedback is
              visible, on success and on failure alike: a learner who solved it
              still benefits from seeing the target stated explicitly, and one
              who did not needs it to know what to aim at next attempt. */}
          {isFeedbackVisible && canContinue && caseData?.successSummary && (
            <div style={{padding:"13px 15px",borderRadius:T.radiusSm,marginBottom:"16px",
              background:"#f8f7ff",border:"1px solid #e4dcfb"}}>
              <div style={{fontSize:"10px",fontWeight:"700",color:"#6d28d9",textTransform:"uppercase",
                letterSpacing:"0.07em",marginBottom:"5px"}}>
                What this case required
              </div>
              <div style={{fontSize:"13px",color:"#3d2a63",lineHeight:"1.65"}}>
                {caseData.successSummary}
              </div>
            </div>
          )}

          {isFeedbackVisible && canContinue && <GoingFurther caseId={caseId} caseData={caseData}/>}

          {/* Feedback pending notice */}
          {!isFeedbackVisible && canContinue && feedbackPendingNote && (
            <div style={{padding:"13px 15px",borderRadius:T.radiusSm,marginBottom:"16px",
              background:"#faf5ff",border:"1px solid #e9d5ff"}}>
              <div style={{fontSize:"10px",fontWeight:"700",color:"#7c3aed",textTransform:"uppercase",
                letterSpacing:"0.07em",marginBottom:"5px"}}>Feedback Pending</div>
              <div style={{fontSize:"13px",color:"#374151",lineHeight:"1.6"}}>{feedbackPendingNote}</div>
            </div>
          )}

          {/* action buttons */}
          <div style={{display:"flex",gap:"8px",justifyContent:"flex-end",flexWrap:"wrap"}}>
            {/* Still have attempts → try again (only if feedback visible so they know outcome) */}
            {isFeedbackVisible && !result.success && !exhausted && (
              <button onClick={onDismiss}
                style={{padding:"9px 18px",borderRadius:T.radiusSm,background:"white",color:T.muted,
                  border:`1px solid ${T.border}`,cursor:"pointer",fontSize:"13px",fontWeight:"600",fontFamily:T.font}}>
                Review prescription
              </button>
            )}
            {isFeedbackVisible && !result.success && !exhausted && (
              <button onClick={onRetry}
                style={{padding:"9px 18px",borderRadius:T.radiusSm,background:T.accent,color:"white",
                  border:"none",cursor:"pointer",fontSize:"13px",fontWeight:"600",fontFamily:T.font}}>
                Try again ↩
              </button>
            )}
            {/* Done with this case (success or exhausted) */}
            {canContinue && hasNext && (
              <button onClick={onNext}
                style={{padding:"9px 18px",borderRadius:T.radiusSm,background:T.accent,color:"white",
                  border:"none",cursor:"pointer",fontSize:"13px",fontWeight:"600",fontFamily:T.font}}>
                Next case →
              </button>
            )}
            {canContinue && !hasNext && (
              <button onClick={onBack}
                style={{padding:"9px 18px",borderRadius:T.radiusSm,background:T.accent,color:"white",
                  border:"none",cursor:"pointer",fontSize:"13px",fontWeight:"600",fontFamily:T.font}}>
                View my results ↗
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function TraineeSessionApp({ session, onExit, traineeLocale, onChangeLocale, availableLocales }) {
  const {t}=useLocale();
  const [localSession, setLocalSession] = useState(session || {});

  // The parent re-fetches from the CF when the language changes, producing a
  // new session object with re-resolved case text. Adopt it when it arrives.
  useEffect(() => {
    if (!session) return;
    setLocalSession(prev => ({ ...session, token: session.token || prev?.token }));
  }, [session]);

  const [activeId, setActiveId] = useState("");
  const [reviewId, setReviewId] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileErr, setProfileErr] = useState("");
  // Profile form state — hoisted so hooks never called conditionally
  const [profileName, setProfileName] = useState("");
  const [profileRole, setProfileRole] = useState("");
  const [profileInstitution, setProfileInstitution] = useState("");
  // Edit profile modal
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editName, setEditName] = useState("");
  const [editRole, setEditRole] = useState("");
  const [editInstitution, setEditInstitution] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [editErr, setEditErr] = useState("");

  const assignments = Array.isArray(localSession?.assignments) ? localSession.assignments : [];
  const trainee = localSession?.trainee || {};
  const requireProfile = !!localSession?.requireProfile;

  // Open edit modal: pre-populate with current profile
  const openEdit = () => {
    setEditName(trainee?.name || "");
    setEditRole(trainee?.role || "");
    setEditInstitution(trainee?.institution || "");
    setEditErr("");
    setShowEditProfile(true);
  };

  const doEditProfile = async () => {
    setSavingEdit(true);
    setEditErr("");
    try {
      if (!editRole.trim()) { setEditErr(t("ui.specifyRole","Please select your role.")); setSavingEdit(false); return; }
      const functions = getFunctions(firebaseApp, "us-central1");
      const saveProfile = httpsCallable(functions, "saveTraineeProfile");
      const res = await saveProfile({
        token: localSession.token,
        name: editName.trim(),
        role: editRole.trim(),
        institution: editInstitution.trim(),
      });
      const updated = res?.data || {};
      setLocalSession(prev => ({
        ...prev,
        trainee: { ...(prev.trainee || {}), ...(updated.trainee || { name: editName.trim(), role: editRole.trim(), institution: editInstitution.trim() }) },
      }));
      setShowEditProfile(false);
    } catch (e) {
      setEditErr(e?.details?.message || e?.message || t("shell.saveFailed","Unable to save. Please try again."));
    } finally {
      setSavingEdit(false);
    }
  };

  // Stable string of statuses — used as effect dependency
  const assignmentStatuses = (Array.isArray(localSession?.assignments)?localSession.assignments:[])
    .map(a=>a.id+':'+a.status).join(',');

  // Auto-open: fires on mount (and status changes) to open first pending case.
  // Does NOT fire if user is already viewing a case (activeId set).
  useEffect(() => {
    if (requireProfile) return;
    if (activeId) return; // already in a case — don't clobber
    try {
      const payload = localSession?.payload || {};
      const all = Array.isArray(localSession?.assignments) ? localSession.assignments : [];

      // Priority 1: specific assignment IDs from this link
      if (Array.isArray(payload.assignmentIds) && payload.assignmentIds.length) {
        const ordered = payload.assignmentIds
          .map(id => all.find(a => a.id === id))
          .filter(Boolean);
        const next = ordered.find(a => a.status !== "completed");
        if (next) { setActiveId(next.id); return; }
        // All done — stay on list
        return;
      }

      // Priority 2: groupId from this link
      if (payload.groupId) {
        const match = all.find(a=>a.groupId===payload.groupId&&a.status!=="completed");
        if (match) { setActiveId(match.id); return; }
        return;
      }

      // Priority 3: single pending case
      const pending = all.filter(a=>a.status!=="completed");
      if (pending.length===1) setActiveId(pending[0].id);
    } catch{}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requireProfile, assignmentStatuses, activeId]);

  // ── Consent, before anything else on this path ───────────────────────────
  // Ahead of the profile screen on purpose: saveTraineeProfile writes the
  // trainee's role and institution, and the letter names professional role as
  // something the platform records. Asking for it before the letter would be
  // collection before consent.
  //
  // Covers both token paths. A QR join redirects into this same route, and the
  // server tells them apart from the token's own type.
  if (localSession?.phase2Consented !== true) {
    return (
      <Phase2ConsentGate
        entryPath={localSession?.phase2EntryPath || "assignment"}
        identity={{ token: localSession?.token }}
        consented={localSession?.phase2Consented ?? null}
        onConsented={()=>setLocalSession(prev => ({ ...prev, phase2Consented:true }))}/>
    );
  }

  // PROFILE SCREEN (first-time)
  if (requireProfile) {
    const name = profileName; const setName = setProfileName;
    const role = profileRole; const setRole = setProfileRole;
    const institution = profileInstitution; const setInstitution = setProfileInstitution;

    const doSaveProfile = async () => {
      setSavingProfile(true);
      setProfileErr("");
      try {
        if (!role.trim()) {
          setProfileErr(t("ui.specifyRole","Please select your role."));
          setSavingProfile(false);
          return;
        }
        const functions = getFunctions(firebaseApp, "us-central1");
        const saveProfile = httpsCallable(functions, "saveTraineeProfile");

        const res = await saveProfile({
          token: localSession.token,
          name: name.trim(),
          role: role.trim(),
          institution: institution.trim(),
        });

        const updated = res?.data || {};
        setLocalSession(prev => ({
          ...prev,
          requireProfile: false,
          trainee: { ...(prev.trainee || {}), ...(updated.trainee || { name, role, institution }) },
        }));
      } catch (e) {
        console.error(e);
        setProfileErr(e?.details?.message || e?.message || "Unable to save profile. Please try again.");
      } finally {
        setSavingProfile(false);
      }
    };

    return (
      <div style={{minHeight:"100vh",background:"#0f0a1e",display:"flex",alignItems:"flex-start",justifyContent:"center",padding:"28px 16px"}}>
        <div style={{width:"100%",maxWidth:"720px",fontFamily:FONT_UI}}>
          <button
            onClick={onExit}
            style={{background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.15)",borderRadius:"10px",color:"rgba(255,255,255,0.85)",
              padding:"8px 12px",cursor:"pointer",fontSize:"12px",fontWeight:"800",marginBottom:"16px",fontFamily:FONT_UI}}
          >
            ← Back
          </button>

          <div style={{color:"white",fontWeight:"900",fontSize:"18px",marginBottom:"6px"}}>Confirm your details</div>
          <div style={{color:"rgba(255,255,255,0.55)",fontSize:"12px",marginBottom:"14px"}}>
            This is only requested the first time you open a secure link.
          </div>

          {profileErr && (
            <div style={{padding:"12px 14px",background:"rgba(239,68,68,0.12)",border:"1px solid rgba(239,68,68,0.25)",borderRadius:"12px",
              color:"rgba(255,255,255,0.88)",fontSize:"13px",marginBottom:"12px"}}>
              {profileErr}
            </div>
          )}

          <div style={{padding:"14px 16px",border:"1px solid rgba(255,255,255,0.12)",borderRadius:"12px",background:"rgba(255,255,255,0.04)"}}>
            <div style={{display:"grid",gap:"10px"}}>
              <div>
                <div style={{fontSize:"11px",fontWeight:"800",color:"rgba(255,255,255,0.7)",marginBottom:"6px"}}>{t("shell.nameOptional","Name (optional)")}</div>
                <input value={name} onChange={e=>setName(e.target.value)}
                  style={{width:"100%",padding:"10px 12px",borderRadius:"10px",border:"1px solid rgba(255,255,255,0.18)",background:"rgba(0,0,0,0.25)",color:"white",outline:"none"}} />
              </div>
              <div>
                <div style={{fontSize:"11px",fontWeight:"800",color:"rgba(255,255,255,0.7)",marginBottom:"6px"}}>{t("shell.roleRequired","Role (required)")}</div>
                <select value={role} onChange={e=>setRole(e.target.value)}
                  style={{width:"100%",padding:"10px 12px",borderRadius:"10px",border:"1px solid rgba(255,255,255,0.18)",background:"rgba(0,0,0,0.25)",color:role?"white":"rgba(255,255,255,0.5)",outline:"none",fontFamily:FONT_UI}}>
                  <option value="" style={{background:"#1a1030"}}>{t("shell.selectRole","Select your role…")}</option>
                  {["Paediatric Nephrology Fellow","Paediatric Critical Care Fellow",
                    "Adult Nephrology Fellow","Adult Critical Care Fellow",
                    "Paediatric Nephrology Consultant","Paediatric Critical Care Consultant",
                    "Adult Nephrology Consultant","Adult Critical Care Consultant",
                    "Nurse","Nurse Educator","Other"].map(r=>(
                    <option key={r} value={r} style={{background:"#1a1030"}}>{r}</option>
                  ))}
                </select>
              </div>
              <div>
                <div style={{fontSize:"11px",fontWeight:"800",color:"rgba(255,255,255,0.7)",marginBottom:"6px"}}>{t("shell.institution","Institution")} <span style={{fontWeight:"400",opacity:0.6}}>(optional)</span></div>
                <input value={institution} onChange={e=>setInstitution(e.target.value)}
                  placeholder="e.g., London Health Sciences Centre"
                  style={{width:"100%",padding:"10px 12px",borderRadius:"10px",border:"1px solid rgba(255,255,255,0.18)",background:"rgba(0,0,0,0.25)",color:"white",outline:"none"}} />
              </div>

              <button
                disabled={savingProfile}
                onClick={doSaveProfile}
                style={{background:"#4f2683",border:"1px solid rgba(255,255,255,0.14)",borderRadius:"10px",color:"white",
                  padding:"10px 14px",cursor:"pointer",fontSize:"12px",fontWeight:"900",fontFamily:FONT_UI,opacity:savingProfile?0.7:1}}
              >
                {savingProfile ? "Saving…" : "Continue"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ASSIGNMENT RUN VIEW
  if (activeId) {
    const a = assignments.find(x => x.id === activeId) || null;
    if (!a) return (
      <div style={{minHeight:"100vh",background:T.bg,display:"flex",alignItems:"center",justifyContent:"center",padding:"24px",fontFamily:T.font}}>
        <div style={{color:T.muted}}>{t("shell.notFound","Assignment not found.")} <button onClick={()=>setActiveId("")} style={{color:T.accent,background:"none",border:"none",cursor:"pointer",fontWeight:"600"}}>← Back</button></div>
      </div>
    );

    // Find next assignment after the current one in payload order (regardless of current's status)
    const payload = localSession?.payload || {};
    const orderedIds = Array.isArray(payload.assignmentIds) && payload.assignmentIds.length
      ? payload.assignmentIds
      : (payload.groupId ? assignments.filter(x=>x.groupId===payload.groupId).map(x=>x.id) : []);
    const nextAssignment = (() => {
      if (!orderedIds.length) return null;
      const currentIdx = orderedIds.indexOf(activeId);
      if (currentIdx < 0) return null;
      // Walk forward from the position AFTER current
      for (let i = currentIdx + 1; i < orderedIds.length; i++) {
        const cand = assignments.find(x => x.id === orderedIds[i]);
        if (cand) return cand; // return regardless of status — modal will only show "Next" when done
      }
      return null;
    })();

    const caseIndex = orderedIds.length ? Math.max(0, orderedIds.indexOf(activeId)) : 0;
    const totalCases = orderedIds.length || 1;

    // No gate here: consent is settled at the top of this component, before
    // the profile screen, so by the time a case can be opened it is on file.
    return (
      <TraineeAssignmentExperience
        session={localSession}
        assignment={a}
        nextAssignment={nextAssignment}
        caseIndex={caseIndex}
        totalCases={totalCases}
        onBack={()=>setActiveId("")}
        onNext={nextAssignment ? ()=>setActiveId(nextAssignment.id) : null}
        onUpdateAssignment={(updated)=>{
          setLocalSession(prev => ({
            ...prev,
            assignments: (Array.isArray(prev.assignments) ? prev.assignments : []).map(x => x.id === updated.id ? updated : x)
          }));
        }}
      />
    );
  }

  // ASSIGNMENT LIST
  const domainColors = {"Clearance":"#4f2683","Anticoagulation":"#0891b2","Hemodynamics":"#059669","Electrolytes":"#d97706","Access/Circuit/Machine":"#dc2626","General Prescribing":"#7c3aed"};
  const completedAssignments = assignments.filter(a=>a.status==="completed");
  const passedCount = completedAssignments.filter(a=>(a.lastResult||a.result)?.success).length;
  const pendingCount = assignments.filter(a=>a.status!=="completed").length;
  const successPct = completedAssignments.length ? Math.round(passedCount/completedAssignments.length*100) : null;
  const institutions = [...new Set(assignments.map(a=>a.educatorInstitution).filter(Boolean))];
  const domains = {};
  completedAssignments.forEach(a=>{
    const r = a.lastResult||a.result||null; if(!r) return;
    const d = a.caseFacet||"General";
    if(!domains[d]) domains[d]={pass:0,total:0};
    domains[d].total++; if(r.success) domains[d].pass++;
  });

  // Review modal — show what trainee submitted, read-only
  const reviewAssignment = reviewId ? assignments.find(a=>a.id===reviewId) : null;
  const reviewResult = reviewAssignment ? (reviewAssignment.lastResult||reviewAssignment.result||null) : null;

  return (
    <div style={{minHeight:"100vh",background:T.bg,fontFamily:T.font}}>

      {/* Review modal */}
      {reviewAssignment && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:200,
          display:"flex",alignItems:"center",justifyContent:"center",padding:"16px"}}
          onClick={()=>setReviewId(null)}>
          <div style={{background:"white",borderRadius:"16px",width:"100%",maxWidth:"540px",
            maxHeight:"88vh",overflowY:"auto",boxShadow:"0 24px 60px rgba(0,0,0,0.4)"}}
            onClick={e=>e.stopPropagation()}>
            <div style={{padding:"18px 20px",borderBottom:`1px solid ${T.border}`,
              display:"flex",justifyContent:"space-between",alignItems:"center",
              background:"linear-gradient(135deg,#f5f0ff,#ede8ff)"}}>
              <div>
                <div style={{fontWeight:"700",fontSize:"15px",color:T.text}}>{reviewAssignment.caseTitle||"Case"}</div>
                <div style={{fontSize:"11px",color:T.muted,marginTop:"2px"}}>{reviewAssignment.caseFacet||""}</div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:"10px"}}>
                <span style={{fontWeight:"700",fontSize:"13px",
                  color:reviewResult?.success?T.success:T.danger}}>
                  {reviewResult?.success?"✓ Passed":"✗ Did not pass"}
                </span>
                <button onClick={()=>setReviewId(null)}
                  style={{background:"none",border:"none",cursor:"pointer",color:T.muted,fontSize:"18px",lineHeight:1}}>×</button>
              </div>
            </div>
            <div style={{padding:"18px 20px"}}>
              {reviewResult?.submission
                ? <SubmissionCard submission={reviewResult.submission} caseInfo={BASE_CASES[reviewAssignment.caseId]||null}/>
                : <div style={{color:T.muted,fontSize:"13px",textAlign:"center",padding:"24px"}}>{t("shell.noSubmission","No submission data available.")}</div>}
              {reviewResult?.feedback && (
                <div style={{marginTop:"14px",padding:"12px 14px",background:"#f5f3ff",borderRadius:"8px",
                  border:`1px solid ${T.border}`,fontSize:"13px",color:T.text}}>
                  <span style={{fontWeight:"700",color:T.accent}}>Feedback: </span>{reviewResult.feedback}
                </div>
              )}
              {reviewResult?.attempts > 1 && (
                <div style={{marginTop:"10px",fontSize:"12px",color:T.muted,textAlign:"center"}}>
                  {reviewResult.attempts} attempt{reviewResult.attempts!==1?"s":""} used
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit profile modal */}
      {showEditProfile && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:300,
          display:"flex",alignItems:"center",justifyContent:"center",padding:"24px"}}
          onClick={e=>{if(e.target===e.currentTarget)setShowEditProfile(false);}}>
          <div style={{background:"white",borderRadius:"16px",width:"100%",maxWidth:"420px",
            boxShadow:"0 24px 60px rgba(0,0,0,0.25)",overflow:"hidden",fontFamily:T.font}}>
            <div style={{padding:"18px 20px",borderBottom:`1px solid ${T.border}`,
              background:`linear-gradient(135deg,#f5f0ff,#ede8ff)`,
              display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{fontWeight:"700",fontSize:"15px",color:T.text}}>{t("shell.editProfile","Edit Your Profile")}</div>
              <button onClick={()=>setShowEditProfile(false)}
                style={{background:"none",border:"none",cursor:"pointer",color:T.muted,fontSize:"20px",lineHeight:1}}>×</button>
            </div>
            <div style={{padding:"20px 20px 24px"}}>
              {editErr && (
                <div style={{padding:"10px 12px",background:"#fef2f2",border:"1px solid #fca5a5",
                  borderRadius:"8px",fontSize:"13px",color:"#dc2626",marginBottom:"14px"}}>
                  {editErr}
                </div>
              )}
              <div style={{display:"grid",gap:"14px"}}>
                <div>
                  <label style={{...C.lbl}}>{t("shell.name","Name")} <span style={{fontWeight:"400",color:"#9ca3af"}}>(optional)</span></label>
                  <input value={editName} onChange={e=>setEditName(e.target.value)}
                    style={{...C.inp}} placeholder={t("shell.yourName","Your name")}/>
                </div>
                <div>
                  <label style={{...C.lbl}}>{t("shell.role","Role")} <span style={{fontWeight:"700",color:T.danger,fontSize:"10px"}}>{t("shell.required","required")}</span></label>
                  <select value={editRole} onChange={e=>setEditRole(e.target.value)} style={{...C.sel}}>
                    <option value="">{t("shell.selectRole","Select your role…")}</option>
                    {["Paediatric Nephrology Fellow","Paediatric Critical Care Fellow",
                      "Adult Nephrology Fellow","Adult Critical Care Fellow",
                      "Paediatric Nephrology Consultant","Paediatric Critical Care Consultant",
                      "Adult Nephrology Consultant","Adult Critical Care Consultant",
                      "Nurse","Nurse Educator","Other"].map(r=>(
                      <option key={r} value={r}>{tRole(t,r)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{...C.lbl}}>{t("shell.institution","Institution")} <span style={{fontWeight:"400",color:"#9ca3af"}}>(optional)</span></label>
                  <input value={editInstitution} onChange={e=>setEditInstitution(e.target.value)}
                    style={{...C.inp}} placeholder="e.g., London Health Sciences Centre"/>
                </div>
                <div style={{display:"flex",gap:"8px",justifyContent:"flex-end",marginTop:"4px"}}>
                  <button onClick={()=>setShowEditProfile(false)}
                    style={{padding:"8px 16px",borderRadius:"8px",border:"1px solid #d1d5db",
                      background:"white",cursor:"pointer",fontSize:"13px",fontWeight:"600",fontFamily:T.font,color:T.muted}}>
                    Cancel
                  </button>
                  <button onClick={doEditProfile} disabled={savingEdit}
                    style={{padding:"8px 18px",borderRadius:"8px",border:"none",
                      background:T.accent,color:"white",cursor:"pointer",fontSize:"13px",
                      fontWeight:"600",fontFamily:T.font,opacity:savingEdit?0.7:1}}>
                    {savingEdit ? "Saving…" : "Save changes"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Top bar */}
      <div style={{background:"white",borderBottom:`1px solid ${T.border}`,padding:"0 24px",
        boxShadow:"0 2px 8px rgba(79,38,131,0.07)"}}>
        <div style={{maxWidth:"760px",margin:"0 auto",height:"58px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{display:"flex",alignItems:"center",gap:"10px"}}>
            <div style={{width:"32px",height:"32px",borderRadius:"9px",
              background:`linear-gradient(135deg,${T.accent},${T.accentMid})`,
              display:"flex",alignItems:"center",justifyContent:"center",
              boxShadow:"0 2px 6px rgba(79,38,131,0.3)"}}>
              <span style={{color:"white",fontSize:"14px",fontWeight:"800",letterSpacing:"-0.03em"}}>K</span>
            </div>
            <div>
              <span style={{fontSize:"15px",fontWeight:"700",color:T.text,letterSpacing:"-0.02em"}}>KiRPA</span>
              <span style={{fontSize:"11px",color:T.muted,marginLeft:"6px",fontWeight:"400"}}>{t("shell.traineePortal","Trainee Portal")}</span>
            </div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:"10px"}}>
            {Array.isArray(availableLocales)&&availableLocales.length>1&&(
              <select value={traineeLocale||"en"}
                onChange={e=>onChangeLocale&&onChangeLocale(e.target.value)}
                title="Language"
                style={{padding:"5px 9px",borderRadius:"7px",border:`1px solid ${T.border}`,
                  background:"white",cursor:"pointer",fontSize:"11px",fontWeight:"600",
                  color:T.accent,fontFamily:T.font,outline:"none"}}>
                {availableLocales.map(c=><option key={c} value={c}>{LOCALES[c]?.native||c}</option>)}
              </select>
            )}
            <div style={{fontSize:"12px",color:T.muted,fontWeight:"500"}}>
              {trainee?.name ? trainee.name : trainee?.email || ""}
            </div>
            <button onClick={openEdit}
              style={{padding:"5px 12px",borderRadius:"7px",border:`1px solid ${T.border}`,
                background:"white",cursor:"pointer",fontSize:"11px",fontWeight:"600",
                color:T.accent,fontFamily:T.font,display:"flex",alignItems:"center",gap:"4px"}}>
              <Ic n="edit" s="w-3 h-3"/> Edit profile
            </button>
          </div>
        </div>
      </div>

      <div style={{maxWidth:"760px",margin:"0 auto",padding:"clamp(16px,4vw,28px)"}}>

        {/* ── Stats header ─────────────────────────────────────────────── */}
        {assignments.length > 0 && (
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",
            gap:"10px",marginBottom:"20px"}}>
            {[
              {label:t("shell.casesAssigned","Cases Assigned"), value:assignments.length, color:T.accent, bg:"#f5f0ff"},
              {label:t("shell.completed","Completed"),      value:completedAssignments.length, color:"#059669", bg:"#f0fdf4"},
              {label:t("shell.pending","Pending"),        value:pendingCount, color:"#d97706", bg:"#fffbeb"},
              ...(successPct!==null?[{label:t("shell.successRate","Success Rate"), value:`${successPct}%`,
                color:successPct>=70?"#059669":successPct>=40?"#d97706":"#dc2626",
                bg:successPct>=70?"#f0fdf4":successPct>=40?"#fffbeb":"#fff1f2"}]:[]),
              ...(institutions.length>0?[{label:"Institution"+(institutions.length>1?"s":""),
                value:institutions.length, color:"#0369a1", bg:"#f0f9ff"}]:[]),
            ].map(({label,value,color,bg})=>(
              <div key={label} style={{background:bg,borderRadius:T.radius,padding:"14px 16px",
                border:`1px solid ${color}22`}}>
                <div style={{fontSize:"22px",fontWeight:"800",color,lineHeight:1}}>{value}</div>
                <div style={{fontSize:"11px",color:T.muted,marginTop:"4px",fontWeight:"500"}}>{label}</div>
              </div>
            ))}
          </div>
        )}

        {/* ── Domain performance ───────────────────────────────────────── */}
        {Object.keys(domains).length > 0 && (
          <div style={{background:"white",borderRadius:T.radius,border:`1px solid ${T.border}`,
            padding:"18px 20px",boxShadow:T.shadow,marginBottom:"20px"}}>
            <div style={{fontSize:"11px",fontWeight:"700",color:T.muted,textTransform:"uppercase",
              letterSpacing:"0.07em",marginBottom:"14px"}}>{t("shell.performanceByDomain","Performance by Domain")}</div>
            <div style={{display:"flex",flexDirection:"column",gap:"10px"}}>
              {Object.entries(domains).map(([domain,{pass,total}])=>{
                const pct = Math.round((pass/total)*100);
                const col = domainColors[domain]||T.accent;
                return (
                  <div key={domain}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"5px"}}>
                      <span style={{fontSize:"12px",fontWeight:"600",color:T.text}}>{tDomain(t,domain)}</span>
                      <span style={{fontSize:"12px",color:T.muted,fontWeight:"500"}}>
                        {pass}/{total} · <span style={{fontWeight:"700",color:pct>=80?T.success:pct>=50?T.warn:T.danger}}>{pct}%</span>
                      </span>
                    </div>
                    <div style={{height:"6px",borderRadius:"3px",background:T.bg,overflow:"hidden"}}>
                      <div style={{height:"100%",width:`${pct}%`,background:col,borderRadius:"3px",transition:"width 0.6s ease"}}/>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Assignment cards ─────────────────────────────────────────── */}
        <div style={{display:"flex",flexDirection:"column",gap:"10px"}}>
          {assignments.length === 0 && (
            <div style={{padding:"32px",textAlign:"center",background:"white",borderRadius:T.radius,
              border:`1px solid ${T.border}`,color:T.muted,fontSize:"13px"}}>
              No assignments found for this link.
            </div>
          )}
          {assignments.map((a, aidx) => {
            const done = a.status==="completed";
            const r = a.lastResult||a.result||null;
            const canReview = done && r?.submission && (()=>{
              const fm=a.feedbackMode||"immediate"; const fd=a.feedbackDate||null; const fr=a.feedbackReleased||false;
              return fm==="immediate"||fr||(fm==="date"&&fd&&new Date()>=new Date(fd));
            })();
            return (
              <div key={a.id||aidx} style={{background:"white",borderRadius:T.radius,
                border:`1px solid ${done?(()=>{const fm=a.feedbackMode||'immediate',fd=a.feedbackDate||null,fr=a.feedbackReleased||false;const fbVis=fm==='immediate'||fr||(fm==='date'&&fd&&new Date()>=new Date(fd));return fbVis?(r?.success?'#bbf7d0':'#fecaca'):'#e9d5ff';})():T.border}`,
                boxShadow:T.shadow,overflow:"hidden",
                cursor:canReview?"pointer":"default",transition:"box-shadow 0.15s"}}
                onClick={canReview?()=>setReviewId(a.id):undefined}
                onMouseEnter={e=>{if(canReview)e.currentTarget.style.boxShadow="0 4px 16px rgba(0,0,0,0.10)";}}
                onMouseLeave={e=>{e.currentTarget.style.boxShadow=T.shadow;}}>
                <div style={{height:"3px",background:done?(()=>{const fm=a.feedbackMode||'immediate',fd=a.feedbackDate||null,fr=a.feedbackReleased||false;const fbVis=fm==='immediate'||fr||(fm==='date'&&fd&&new Date()>=new Date(fd));return fbVis?(r?.success?T.success:T.danger):'#a78bfa';})():T.border}}/>
                <div style={{padding:"14px 18px",display:"flex",gap:"14px",alignItems:"center"}}>
                  <div style={{width:"38px",height:"38px",borderRadius:"10px",flexShrink:0,
                    background:done?(()=>{const fm=a.feedbackMode||'immediate',fd=a.feedbackDate||null,fr=a.feedbackReleased||false;const fbVis=fm==='immediate'||fr||(fm==='date'&&fd&&new Date()>=new Date(fd));return fbVis?(r?.success?T.successBg:T.dangerBg):'#faf5ff';})():T.accentLight,
                    display:"flex",alignItems:"center",justifyContent:"center",
                    fontSize:"15px",fontWeight:"700",
                    color:done?(()=>{const fm=a.feedbackMode||'immediate',fd=a.feedbackDate||null,fr=a.feedbackReleased||false;const fbVis=fm==='immediate'||fr||(fm==='date'&&fd&&new Date()>=new Date(fd));return fbVis?(r?.success?T.success:T.danger):'#7c3aed';})():T.accent}}>
                    {done ? (()=>{const fm=a.feedbackMode||'immediate',fd=a.feedbackDate||null,fr=a.feedbackReleased||false;const fbVis=fm==='immediate'||fr||(fm==='date'&&fd&&new Date()>=new Date(fd));return fbVis?(r?.success?'✓':'✕'):'⏳';})() : String(aidx+1)}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:"14px",fontWeight:"600",color:T.text,
                      whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                      {a.caseTitle||a.caseId||"Case"}
                    </div>
                    <div style={{fontSize:"12px",color:T.muted,marginTop:"2px",display:"flex",gap:"8px",flexWrap:"wrap",alignItems:"center"}}>
                      {a.caseFacet&&<span>{a.caseFacet}</span>}
                      {a.caseFacet&&a.educatorName&&<span style={{color:T.border}}>·</span>}
                      {a.educatorName&&(
                        <span style={{display:"inline-flex",alignItems:"center",gap:"3px",
                          fontSize:"11px",color:T.accent,fontWeight:"600",
                          background:T.accentLight,padding:"1px 7px",borderRadius:"20px"}}>
                          📋 {a.educatorName}
                        </span>
                      )}
                      {a.attempts!=null&&a.attempts>0&&<span style={{color:T.border}}>·</span>}
                      {a.attempts!=null&&a.attempts>0&&<span>{a.attempts} attempt{a.attempts!==1?"s":""}</span>}
                    </div>
                  </div>
                  <div style={{flexShrink:0,display:"flex",alignItems:"center",gap:"8px"}}>
                    {!done && (
                      <button onClick={e=>{e.stopPropagation();setActiveId(a.id);}}
                        style={{padding:"8px 16px",borderRadius:T.radiusSm,background:T.accent,
                          color:"white",border:"none",cursor:"pointer",fontSize:"13px",
                          fontWeight:"600",fontFamily:T.font}}>
                        Open →
                      </button>
                    )}
                    {done && (()=>{
                      const fm=a.feedbackMode||"immediate",fd=a.feedbackDate||null,fr=a.feedbackReleased||false;
                      const fbVis=fm==="immediate"||fr||(fm==="date"&&fd&&new Date()>=new Date(fd));
                      return <div style={{textAlign:"right"}}>
                        {fbVis
                          ? <><div style={{fontSize:"12px",fontWeight:"700",color:r?.success?T.success:T.danger}}>
                              {r?.success?"Passed":"Not passed"}
                            </div>
                            {canReview&&<div style={{fontSize:"10px",color:T.muted,marginTop:"2px"}}>tap to review</div>}</>
                          : <><div style={{fontSize:"11px",fontWeight:"700",color:"#7c3aed"}}>Feedback Pending</div>
                            <div style={{fontSize:"10px",color:T.muted,marginTop:"2px"}}>
                              {fm==="manual"?"Awaiting release":fd?`Releases ${new Date(fd).toLocaleDateString()}`:"Pending"}
                            </div></>}
                      </div>;
                    })()}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Alternating background colours for multi-case assignments (up to 5)
const CASE_BG_PALETTES = [
  { bg:"linear-gradient(160deg,#f8f5ff 0%,#f1f5f9 60%,#f8f5ff 100%)", accent:"#4f2683", pill:"#ede9ff" }, // purple
  { bg:"linear-gradient(160deg,#f0f9ff 0%,#e0f2fe 60%,#f0f9ff 100%)", accent:"#0369a1", pill:"#e0f2fe" }, // blue
  { bg:"linear-gradient(160deg,#fff1f2 0%,#fde8ea 60%,#fff1f2 100%)", accent:"#be123c",  pill:"#ffe4e6" }, // rose
  { bg:"linear-gradient(160deg,#f0fdf4 0%,#dcfce7 60%,#f0fdf4 100%)", accent:"#15803d", pill:"#dcfce7" }, // green
  { bg:"linear-gradient(160deg,#fffbeb 0%,#fef3c7 60%,#fffbeb 100%)", accent:"#b45309", pill:"#fef3c7" }, // amber
];

function TraineeAssignmentExperience(props) {
  // Render the whole case in the language it was delivered in, so labels match
  // the vignette even if the interface chrome is set to something else.
  const _loc = props.assignment?.locale || props.assignment?.sourceLocale || "en";
  return <LocaleOverride locale={_loc}><TraineeAssignmentExperienceInner {...props}/></LocaleOverride>;
}

// Viewport hook. Inline styles cannot carry media queries, so breakpoints are
// resolved in JS and applied as ordinary style values.
function useViewport(query){
  const [match,setMatch]=useState(()=>{
    try{ return window.matchMedia(query).matches; }catch{ return false; }
  });
  useEffect(()=>{
    let mq;
    try{ mq=window.matchMedia(query); }catch{ return; }
    const on=e=>setMatch(e.matches);
    setMatch(mq.matches);
    mq.addEventListener?.("change",on);
    return ()=>mq.removeEventListener?.("change",on);
  },[query]);
  return match;
}

function TraineeAssignmentExperienceInner({ session, assignment, onBack, onNext, onUpdateAssignment, caseIndex=0, totalCases=1, embedded=false, hideBack=false }) {
  const {t}=useLocale();
  // Which calc groups are expanded. All open by default; absent means open.
  const [calcOpen,setCalcOpen]=useState({});
  const topRef       = useRef(null);
  const caseStartRef = useRef(Date.now()); // records when this attempt started
  const palette      = CASE_BG_PALETTES[caseIndex % CASE_BG_PALETTES.length];

  const [rx, setRx] = useState(() => {
    const base =
      assignment.caseOverride?.initialPrescription ||
      BASE_CASES[assignment.caseId]?.initialPrescription ||
      assignment.initialPrescription || {};
    return JSON.parse(JSON.stringify(base));
  });
  const [actions, setActions]         = useState([]);
  const [hintCount, setHintCount]     = useState(0);    // incremented when trainee opens a hint
  const [consultOpen, setConsultOpen] = useState(false);
  // Frame capture. askFrame opens the reflection card; frameDone lets the next
  // press through, so the prompt gates a submit exactly once per attempt and
  // never becomes a wall.
  const [askFrame, setAskFrame]       = useState(false);
  const [frameDone, setFrameDone]     = useState(false);
  const [frame, setFrame]             = useState({problem:"",why:"",confidence:""});
  const [feedbackViewed, setFeedbackViewed] = useState(false); // set true when result modal shown
  // Reset form state whenever assignment changes
  const assignmentId = assignment.id;
  useEffect(() => {
    caseStartRef.current = Date.now(); // restart timer on new case
    const ip =
      assignment.caseOverride?.initialPrescription ||
      BASE_CASES[assignment.caseId]?.initialPrescription ||
      assignment.initialPrescription || {};
    setRx(JSON.parse(JSON.stringify(ip)));
    setActions([]);
    setResult(assignment.lastResult || null);
    setHintCount(0);
    setFeedbackViewed(false);
    setErr("");
  }, [assignmentId]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult]   = useState(assignment.lastResult || null);
  const [err, setErr]         = useState("");
  const [labSI, setLabSI]     = useState(true); // SI/US toggle for labs panel

  // Merge override on top of BASE_CASES so trainee always gets educator's edits
  const caseData = (() => {
    const base = BASE_CASES[assignment.caseId] || null;
    const ov   = assignment.caseOverride || assignment.caseData || null;
    if (!base && !ov) return null;
    if (!ov)   return base;
    if (!base) return ov;
    // Deep-merge initialState same way as educator view
    const mergedIS = {
      ...(base.initialState||{}),
      ...(ov.initialState||{}),
      vitals: (ov.initialState?.vitals||base.initialState?.vitals) ? {...(base.initialState?.vitals||{}),...(ov.initialState?.vitals||{})} : undefined,
      labs:   (ov.initialState?.labs  ||base.initialState?.labs  ) ? {...(base.initialState?.labs  ||{}),...(ov.initialState?.labs  ||{})} : undefined,
      gas:    (ov.initialState?.gas   ||base.initialState?.gas   ) ? {...(base.initialState?.gas   ||{}),...(ov.initialState?.gas   ||{})} : undefined,
      cbc:    (ov.initialState?.cbc   ||base.initialState?.cbc   ) ? {...(base.initialState?.cbc   ||{}),...(ov.initialState?.cbc   ||{})} : undefined,
    };
    Object.keys(mergedIS).forEach(k=>mergedIS[k]===undefined&&delete mergedIS[k]);
    // Merge caseOptions from override too
    const mergedCaseOptions = ov.caseOptions || base.caseOptions || undefined;
    return {
      ...base, ...ov,
      initialState: mergedIS,
      ...(mergedCaseOptions ? {caseOptions: mergedCaseOptions} : {}),
    };
  })();

  if (!caseData) return (
    <div style={{minHeight:"100vh",background:T.bg,display:"flex",alignItems:"center",
      justifyContent:"center",padding:"24px",fontFamily:T.font}}>
      <div style={{textAlign:"center",color:T.muted}}>
        <div style={{marginBottom:"12px"}}>{t("ui.caseUnavailable","Case data not available.")}</div>
        <button onClick={onBack} style={{padding:"8px 16px",borderRadius:T.radiusSm,
          background:T.accent,color:"white",border:"none",cursor:"pointer",fontFamily:T.font}}>← Back</button>
      </div>
    </div>
  );

  const wt = caseData?.initialState?.weightKg || 0;
  const bsa = caseData?.initialState?.bsa || 0;
  const rates = modalityRates(rx.modality || "");
  const pbp  = parseFloat(rx.pbpRate||0)||0;
  const dial = parseFloat(rx.dialysateRate||0)||0;
  const rep  = parseFloat(rx.repRate||0)||0;
  const clBsa     = parseFloat(calcClearanceBSA(pbp,dial,rep,bsa))||0;
  const clKg      = parseFloat(calcClearanceKg(pbp,dial,rep,wt))||0;
  const netUf     = parseFloat(rx.netUf||0)||0;
  const netUfKgHr = wt > 0 ? (netUf / wt) : 0;
  // Effluent dose in ml/kg/hr, INCLUDING net ultrafiltration. This is the dose
  // convention used for the weight-based targets in Rooms 1, 2 and 6.
  const efflKg    = wt > 0 ? ((pbp + dial + rep + netUf) / wt) : 0;

  // ── Filtration fraction ───────────────────────────────────────────────────
  // FF = fluid crossing the membrane ÷ plasma flow reaching it.
  //   plasma flow  = Qb(ml/min) × 60 × (1 − Hct)
  //   fluid across = post-filter replacement + net UF + pre-blood-pump
  // PBP appears on BOTH sides: it crosses the membrane, but it also dilutes
  // blood before the filter, which is exactly why pre-dilution lowers FF.
  // Dialysate is absent: it is diffusive and removes no plasma water.
  const hct = (()=>{ const h=parseFloat(caseData?.initialState?.cbc?.Hct);
                     return (isFinite(h) && h>0 && h<1) ? h : 0.30; })();
  // Blood flow is not destructured in this scope, unlike pbp/dial/rep/netUf.
  const bfrMlMin   = parseFloat(rx.bfr || 0) || 0;
  const plasmaFlow = bfrMlMin * 60 * (1 - hct);
  const ffPct = (plasmaFlow + pbp) > 0
    ? ((rep + netUf + pbp) / (plasmaFlow + pbp)) * 100
    : 0;
  // Same total expressed per 1.73 m², for the L/1.73m²/hr convention.
  const efflBsa   = bsa > 0 ? (((pbp + dial + rep + netUf) / 1000) / bsa * 1.73) : 0;

  const toggleAction = (id) =>
    setActions(prev => prev.includes(id) ? prev.filter(x=>x!==id) : [...prev,id]);

  const doSubmit = async (frameArg) => {
    // Passed in rather than read from state: the modal calls this in the same
    // tick as setFrame, so state would still hold the previous value.
    const submittedFrame = frameArg || frame;
    setSubmitting(true); setErr("");
    try {
      const attempts = (assignment.attempts||0)+1;
      const evalRes  = evaluateRoomSuccess(caseData,{rx,actions,clBsa,clKg,netUfKgHr,efflKg,efflBsa,ffPct});
      const timeOnCaseSeconds = Math.round((Date.now() - caseStartRef.current) / 1000);
      const finalResult = {
        success:      !!evalRes.success,
        attempts,
        feedback:     evalRes.feedback || "",
        learningPoint: caseData.learningPoint || "",
        submission:   { rx, actions, metrics: { clBsa, clKg, netUfKgHr, efflKg, efflBsa, ffPct } },
        submittedAt:  new Date().toISOString(),
        // ── Research fields ──────────────────────────────────────────────
        errorFlags:          evalRes.errorFlags          || null,
        criteriaLogic:       evalRes.criteriaLogic       || "single",
        chosenSolutionPath:  evalRes.chosenSolutionPath  || null,
        prescribingMode:     Object.keys(caseData?.initialPrescription || {}).length > 0 ? "modify" : "new",
        // ── Engagement fields ────────────────────────────────────────────
        timeOnCaseSeconds,
        hintCount,
        // No letter fields are sent. Which version of the Letter of Information
        // was in force when a row was written is stamped by the Cloud Function
        // from its own constant: under a notice model there is no acceptance
        // event for a client to report, and a client-supplied version would be
        // both a guess and spoofable. This is the field an ethics reviewer is
        // most likely to ask about, so it does not come from here.
        // Frame capture. Separates diagnostic reasoning from prescription
        // accuracy: "knew the problem, botched the numbers" and "never
        // identified the problem" are different failures, and nothing else in
        // the schema tells them apart.
        framedProblem:    submittedFrame?.problem || null,
        framedWhy:        (submittedFrame?.why || "").slice(0,600),
        framedConfidence: submittedFrame?.confidence || null,
        frameSkipped:     !!submittedFrame?.skipped,
        // Recorded so hinted attempts can be analysed as their own group. A
        // case solved after three hints is not the same evidence as one solved
        // cold, and pooling them would overstate performance.
        usedConsult: hintCount > 0,
        consultDepth: hintCount,
        feedbackViewed,
        // Language context for research_events. The CF re-resolves these
        // server-side rather than trusting them; they are sent so the event
        // reflects what this trainee actually read.
        caseLocale:       assignment.locale       || "en",
        uiLocale:         session?.locale         || assignment.locale || "en",
        localeOverridden: (assignment.locale || "en") !== (assignment.sourceLocale || "en"),
      };
      const fns = getFunctions(firebaseApp,"us-central1");
      if (assignment._practice) {
        // Practice track: no token and no assignment document. The facet is
        // sent for server-side domain analytics only and is never rendered.
        await httpsCallable(fns,"submitPracticeResult")({
          deviceId:  assignment._deviceId,
          caseId:    assignment.caseId,
          caseFacet: assignment._practiceFacet || "",
          isCommunity: !!assignment._isCommunity,
          sourceLocale: assignment.sourceLocale || "en",
          sourceFields: assignment._sourceFields || {},
          // Case context for the derived analytic variables. The server cannot
          // look these up for a community case, so they travel with the result.
          initialState:  caseData?.initialState  || null,
          baselineRx:    caseData?.initialPrescription || null,
          successType:   caseData?.successType   || null,
          successParam:  caseData?.successParam,
          scenario:      caseData?.scenario      || null,
          result: { ...finalResult,
            followedDefaultOrder: assignment._followedDefaultOrder !== false,
            sourceLocale: assignment.sourceLocale || "en" },
        });
      } else {
        await httpsCallable(fns,"submitTraineeAssignmentResult")({
          token: session.token, assignmentId: assignment.id,
          result: {
            ...finalResult,
            // Same case context as the practice path. The assignment snapshot
            // usually carries these, but an older assignment may not.
            initialState:  caseData?.initialState  || null,
            baselineRx:    caseData?.initialPrescription || null,
            successType:   caseData?.successType   || null,
            successParam:  caseData?.successParam,
            scenario:      caseData?.scenario      || null,
          },
        });
      }
      const updated = {
        ...assignment, attempts,
        status: (finalResult.success || attempts >= (assignment.maxAttempts||1)) ? "completed" : (assignment.status||"pending"),
        lastResult: finalResult,
      };
      setResult(finalResult);
      setFeedbackViewed(true);
      caseStartRef.current = Date.now();
      onUpdateAssignment(updated);
    } catch(e) {
      console.error(e);
      setErr(e?.details?.message||e?.message||"Unable to submit. Please try again.");
    } finally { setSubmitting(false); }
  };

  const maxAttempts = assignment.maxAttempts || 1;

  // ── Feedback release gating ─────────────────────────────────────────────────
  const feedbackMode = assignment.feedbackMode || "immediate";
  const feedbackDate = assignment.feedbackDate || null;
  const feedbackReleased = assignment.feedbackReleased || false;
  const isFeedbackVisible = (() => {
    if (feedbackMode === "immediate") return true;
    if (feedbackMode === "manual") return feedbackReleased;
    if (feedbackMode === "date" && feedbackDate) return new Date() >= new Date(feedbackDate);
    return feedbackReleased; // fallback
  })();

  // Pending message for trainee
  const feedbackPendingNote = (() => {
    if (isFeedbackVisible) return null;
    if (feedbackMode === "manual") return t("ui.manualRelease","Your educator will manually release feedback for this case.");
    if (feedbackMode === "date" && feedbackDate) {
      const d = new Date(feedbackDate);
      return `Feedback will be released on ${d.toLocaleDateString(undefined,{month:"long",day:"numeric",year:"numeric"})} at ${d.toLocaleTimeString(undefined,{hour:"2-digit",minute:"2-digit"})}.`;
    }
    return t("ui.feedbackPending","Feedback pending release by your educator.");
  })();

  return (
    <div ref={topRef} style={{minHeight:embedded?"auto":"100vh",
      background:embedded?"transparent":palette.bg,
      borderRadius:embedded?"14px":0,overflow:embedded?"hidden":"visible",
      border:embedded?`1px solid ${T.border}`:"none",
      fontFamily:T.font,scrollMarginTop:0}}>
      {/* Result modal overlay */}
      {result && (
        <ResultModal
          result={result}
          maxAttempts={maxAttempts}
          caseData={caseData}
          frame={frame}
          caseId={assignment.caseId}
          hasNext={!!onNext}
          isFeedbackVisible={isFeedbackVisible}
          feedbackPendingNote={feedbackPendingNote}
          onDismiss={()=>setResult(null)}
          onRetry={()=>{
            setResult(null);
            // Per-attempt, not per-case. What the learner believes on the
            // second try, having seen the first fail, is the more interesting
            // frame of the two.
            setFrameDone(false); setAskFrame(false);
            setFrame({problem:"",why:"",confidence:""});
            setHintCount(0);
          }}
          onNext={()=>{ if(onNext) {
            setResult(null);
            // The next case needs its own frame. Without this the prompt is
            // skipped for every case after the first.
            setFrameDone(false); setAskFrame(false);
            setFrame({problem:"",why:"",confidence:""});
            setHintCount(0);
            setTimeout(()=>topRef.current?.scrollIntoView({behavior:"smooth"}),80);
            onNext();
          } }}
          onBack={()=>{ setResult(null); onBack(); }}
        />
      )}

      {/* Top bar */}
      <div style={{background:"linear-gradient(135deg,#ffffff 0%,#faf8ff 100%)",
        borderBottom:`1px solid ${T.border}`,
        position:"sticky",top:0,zIndex:10,
        boxShadow:"0 2px 12px rgba(79,38,131,0.10)"}}>
        <div style={{maxWidth:embedded?"none":"960px",margin:"0 auto",
          padding:embedded?"0 14px":"0 16px",height:embedded?"50px":"58px",
          display:"flex",alignItems:"center",gap:"12px"}}>
          {!embedded && <>
            <div style={{width:"30px",height:"30px",borderRadius:"8px",background:T.accent,
              display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              <span style={{color:"white",fontSize:"13px",fontWeight:"800",letterSpacing:"-0.02em"}}>K</span>
            </div>
            <div style={{width:"1px",height:"22px",background:T.border}}/>
          </>}
          {/* On a wide two-panel layout the case wall is already visible beside
              this panel, so a back control would be redundant. */}
          {!hideBack && <>
            <button onClick={onBack}
              style={{display:"flex",alignItems:"center",gap:"5px",background:"none",border:"none",
                cursor:"pointer",color:T.muted,fontSize:"12px",fontWeight:"600",fontFamily:T.font,
                padding:"4px 0",letterSpacing:"0.01em"}}>
              ← All cases
            </button>
            <div style={{width:"1px",height:"22px",background:T.border}}/>
          </>}
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:"14px",fontWeight:"700",color:T.text,
              whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",letterSpacing:"-0.01em"}}>
              {caseData.title}
            </div>
            {/* The case facet is deliberately NOT shown. Knowing a case is about
                electrolytes before opening it is a cue that changes the problem
                being solved. Domains are revealed only in the progress view. */}
          </div>
          {/* Attempt pips */}
          <div style={{flexShrink:0,display:"flex",alignItems:"center",gap:"5px"}}>
            {Array.from({length:maxAttempts}).map((_,i)=>(
              <div key={i} style={{width:"8px",height:"8px",borderRadius:"50%",
                background: i<(assignment.attempts||0) ? T.accent : T.bgDeep,
                border: `1.5px solid ${i<(assignment.attempts||0) ? T.accentMid : T.borderStrong}`}}/>
            ))}
            <span style={{fontSize:"11px",color:T.muted,marginLeft:"4px",fontWeight:"600"}}>
              {(assignment.attempts||0)}/{maxAttempts}
            </span>
          </div>
        </div>
      </div>

      <div style={{maxWidth:embedded?"none":"900px",margin:"0 auto",
        padding:embedded?"14px":"clamp(12px,4vw,24px)"}}>
        {err && (
          <div style={{padding:"12px 16px",background:T.dangerBg,border:`1px solid ${T.dangerBorder}`,
            borderRadius:T.radiusSm,color:T.danger,fontSize:"13px",marginBottom:"16px"}}>
            {err}
          </div>
        )}

        <div style={{display:"grid",gridTemplateColumns:"minmax(0,1fr)",gap:"14px",alignItems:"start"}}
          className="kirpa-assignment-grid">
          <style>{`
            @media (min-width: 700px) { .kirpa-assignment-grid { grid-template-columns: minmax(0,1fr) 320px !important; } }
            .kirpa-card { transition: box-shadow 0.15s; }
            .kirpa-card:hover { box-shadow: 0 4px 20px rgba(79,38,131,0.10) !important; }
          `}</style>

          {/* LEFT: scenario + prescription + actions */}
          <div style={{display:"flex",flexDirection:"column",gap:"14px"}}>

            {/* Clinical scenario */}
            <div className="kirpa-card" style={{background:"white",borderRadius:T.radius,
              border:`1px solid ${T.border}`,boxShadow:T.shadow,overflow:"hidden"}}>
              <div style={{padding:"12px 18px",borderBottom:`1px solid ${T.border}`,
                background:"#0f172a",
                display:"flex",alignItems:"center",gap:"10px",flexWrap:"wrap"}}>
                <div style={{width:"26px",height:"26px",borderRadius:"7px",flexShrink:0,
                  background:"linear-gradient(145deg,#ede9fe,#ddd6fe)",
                  border:"1px solid #c4b5fd",boxShadow:"inset 0 1px 0 rgba(255,255,255,0.7)",
                  display:"flex",alignItems:"center",justifyContent:"center"}}>
                  <span style={{color:"#5b21b6",fontSize:"10px",fontWeight:"700",
                    fontFamily:FONT_MONO,letterSpacing:"0.02em"}}>Hx</span>
                </div>
                <span style={{fontSize:"10px",fontWeight:"600",color:"#6d28d9",
                  textTransform:"uppercase",letterSpacing:"0.16em",
                  fontFamily:FONT_MONO}}>{t("ui.clinicalHandover","Clinical Handover")}</span>
                {assignment.educatorName && (
                  <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:"8px",flexWrap:"wrap",justifyContent:"flex-end"}}>
                  {totalCases > 1 && (
                    <div style={{display:"flex",alignItems:"center",gap:"5px",background:palette.pill,
                      padding:"3px 10px",borderRadius:"20px",border:`1px solid ${palette.accent}22`}}>
                      {Array.from({length:totalCases}).map((_,ci)=>(
                        <div key={ci} style={{width:"8px",height:"8px",borderRadius:"50%",
                          background: ci < caseIndex ? palette.accent :
                                      ci === caseIndex ? palette.accent :
                                      "#d1d5db",
                          opacity: ci === caseIndex ? 1 : ci < caseIndex ? 0.5 : 0.3,
                          boxShadow: ci === caseIndex ? `0 0 0 2px white, 0 0 0 3px ${palette.accent}` : "none",
                          transition:"all 0.3s"}}/>
                      ))}
                      <span style={{fontSize:"11px",fontWeight:"700",color:palette.accent,marginLeft:"4px"}}>
                        {caseIndex+1}/{totalCases}
                      </span>
                    </div>
                  )}
                  {assignment.educatorName && (
                    <div style={{fontSize:"11px",color:T.muted,fontStyle:"italic",
                      background:"white",padding:"3px 8px",borderRadius:"20px",border:`1px solid ${T.border}`}}>
                      📋 {assignment.educatorName}
                    </div>
                  )}
                </div>
                )}
              </div>
              {/* Bay plate. Brushed metal, the room large and thin, identifiers in
                  small mono beneath. Pure typography and material: arriving at a
                  case should feel like arriving at a bedside, with nothing
                  moving. The top edge carries the tile's state colour, so the
                  wall and the case read as one place. */}
              <div style={{position:"relative",overflow:"hidden",
                background:"linear-gradient(160deg,#20192f 0%,#171124 45%,#1d1630 100%)",
                borderTop:`3px solid ${assignment?.status==="completed" ? "#22e57f" : "#a78bfa"}`,
                borderBottom:"1px solid rgba(255,255,255,0.07)",
                padding:"14px 18px 13px",display:"flex",alignItems:"flex-end",gap:"14px"}}
                className="kirpa-grain">
                {/* Brushed streaks, very low contrast */}
                <span style={{position:"absolute",inset:0,pointerEvents:"none",opacity:0.5,
                  background:"repeating-linear-gradient(97deg,rgba(255,255,255,0.022) 0px,rgba(255,255,255,0.022) 1px,transparent 1px,transparent 5px)"}}/>
                <div style={{position:"relative",minWidth:0,flex:1}}>
                  <div style={{fontSize:"9px",fontWeight:"700",color:"#8b7fb0",
                    fontFamily:FONT_MONO,textTransform:"uppercase",
                    letterSpacing:"0.22em",marginBottom:"3px"}}>
                    {caseData?.facet || t("ui.case","Case")}
                  </div>
                  <div style={{fontSize:"clamp(19px,4.4vw,26px)",fontWeight:"300",
                    color:"#f4f1ff",letterSpacing:"-0.015em",lineHeight:1.1,
                    overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                    {caseData?.title || ""}
                  </div>
                </div>
                <div style={{position:"relative",textAlign:"end",flexShrink:0,
                  fontFamily:FONT_MONO,color:"#9086b5",fontSize:"9.5px",
                  lineHeight:"1.6",letterSpacing:"0.05em"}}>
                  {caseData?.initialState?.weightKg != null && (
                    <div>{caseData.initialState.weightKg} KG</div>
                  )}
                  {caseData?.initialState?.bsa != null && (
                    <div>BSA {caseData.initialState.bsa}</div>
                  )}
                </div>
              </div>

              {/* The handover is a person speaking, not machine output, so it is
                  set in the reading face at a comfortable measure. Monospace
                  here was what made the stem read as terminal text. */}
              <div style={{padding:"20px 22px",background:"#fdfaf6",
                borderBottom:"1px solid #ece3d9",borderInlineStart:"3px solid #a1714a"}}>
                <div style={{fontSize:"15px",color:"#33261f",lineHeight:"1.75",
                  fontFamily:FONT_CLIN,letterSpacing:"0.005em",maxWidth:"62ch",
                  textWrap:"pretty"}}>
                  {caseData.page}
                </div>
              </div>

              {/* What is being asked. Sits under the handover, so the reading
                  order is: which case, what happened, what you are being asked
                  to do. */}
              <div style={{padding:"12px 16px",background:"#faf8ff",
                borderBottom:"1px solid #e4dcfb"}}>
                <ScenarioBanner caseData={caseData}/>
              </div>

              {/* Translation provenance. Renders only when the trainee is
                  reading text translated from another language. A case authored
                  natively in this language shows nothing. */}
              {assignment.isTranslated && (
                <div style={{padding:"10px 14px",background:"#f3efff",
                  borderBottom:"1px solid #e4dcfb"}}>
                  <TranslationNote compact tr={{
                    isTranslated: true,
                    sourceLocale: assignment.sourceLocale,
                    status:       assignment.translationStatus,
                    stale:        assignment.translationStale,
                    translatorName: assignment.translatorName,
                  }}/>
                </div>
              )}

              {/* Patient data + vitals + labs — ICU monitor aesthetic */}
              {(() => {
                const st = caseData.initialState || {};

                // Unit converters
                const toUS = (k, v) => {
                  const us = cvtToUS(k, parseFloat(v));
                  if (us) return {val: us.v, unit: us.u};
                  return {val: v, unit: siUnits[k] || ""};
                };
                const fmtVal = (k, v) => {
                  if (labSI) return String(v);
                  const {val} = toUS(k, v);
                  return String(val);
                };

                // Abnormal flag
                const isAbn = (k, v) => {
                  const n = parseFloat(String(v));
                  if (isNaN(n)) return false;
                  return (k==="K"&&(n<3.5||n>5.5))||(k==="Na"&&(n<135||n>145))||
                    (k==="pH"&&(n<7.35||n>7.45))||(k==="BE"&&(n<-3||n>3))||
                    (k==="iCa"&&(n<1.0||n>1.3))||(k==="TotalCa"&&(n<2.1||n>2.6))||
                    (k==="Lactate"&&n>2)||(k==="Plt"&&n<150)||(k==="PO4"&&n<0.8)||
                    (k==="Mg"&&n<0.7)||(k==="HCO3"&&(n<22||n>28))||
                    (k==="Ammonia"&&n>50)||(k==="pCO2"&&(n<35||n>45))||
                    (k==="Cr"&&n>110)||(k==="Glucose"&&(n<3.9||n>10))||
                    (k==="Fibrinogen"&&n<1.5)||(k==="CircuitiCa"&&(n<0.25||n>0.45))||
                    (k==="Alb"&&n<30)||(k==="ALT"&&n>40)||(k==="AST"&&n>40)||
                    (k==="INR"&&n>1.3)||(k==="hr"&&(n<60||n>100));
                };

                const age = st.ageMonths != null
                  ? (st.ageMonths < 1 ? `${Math.round(st.ageMonths*30)}d`
                     : st.ageMonths < 12 ? `${Math.round(st.ageMonths)}mo`
                     : `${(st.ageMonths/12).toFixed(st.ageMonths%12===0?0:1)}y`)
                  : null;

                // ── Parse vitals for the monitor tiles ───────────────────────
                const v = st.vitals || {};
                const bpStr   = v.newBp || v.bp || null;
                const hrVal   = v.hr    || null;
                const spo2Val = v.spo2  || null;
                const rrVal   = v.rr    || null;
                const tmpVal  = v.tmp   || v.temp || null;

                // Parse systolic from "90/50" → "90"
                const bpSys = bpStr ? bpStr.split("/")[0] : null;
                const bpDia = bpStr ? bpStr.split("/")[1] : null;
                const bpNum = bpSys ? parseFloat(bpSys) : null;
                const hrNum = hrVal ? parseFloat(hrVal) : null;

                // Colour logic: red if alarming, amber if borderline, else channel colour
                const bpColor  = bpNum  ? (bpNum<70?"#ff4444":bpNum<90?"#ffaa00":"#0f766e") : "#0f766e";
                const hrColor  = hrNum  ? (hrNum>150||hrNum<50?"#ff4444":hrNum>120||hrNum<60?"#ffaa00":"#047857") : "#047857";
                const spo2Color = spo2Val ? (parseFloat(spo2Val)<88?"#ff4444":parseFloat(spo2Val)<92?"#ffaa00":"#5b21b6") : "#5b21b6";

                const gasEntries = st.gas  ? Object.entries(st.gas)  : [];
                const labEntries = st.labs ? Object.entries(st.labs)  : [];
                const cbcEntries = st.cbc  ? Object.entries(st.cbc)   : [];

                // ── Monitor tile component ────────────────────────────────────
                // Bedside monitors colour-code by parameter, and every clinician
                // reads them without thinking: green HR, cyan SpO2, red BP,
                // yellow RR, white temp. Adopting that convention makes the
                // display both more authentic and more legible than tinting
                // everything violet. Abnormal values override to amber or red.
                const MON_COLORS = { BP:"#ff5470", HR:"#22e57f", "SpO₂":"#22d3ee",
                                     RR:"#facc15", TEMP:"#e4e4e7" };
                const MonTile = ({label, value, sub, color, unit, wide=false, trace}) => (
                  <div style={{
                    // Recessed dark face. The monitor should read as equipment
                    // sunk into the page, not as another white card at the same
                    // elevation as everything else.
                    background:"linear-gradient(180deg,#141024,#0b0816)",
                    border:`1px solid ${color}33`,
                    borderRadius:"10px",
                    padding: wide ? "11px 15px 12px" : "10px 12px 11px",
                    display:"flex",flexDirection:"column",gap:"1px",
                    position:"relative",overflow:"hidden",
                    // clamp() lets one tile spec serve phone and desktop: it
                    // grows with the viewport but never collapses or overflows.
                    flex: wide ? "1 1 clamp(150px,34vw,230px)" : "1 1 clamp(96px,21vw,150px)",
                    minWidth: wide ? "150px" : "96px",
                    boxShadow:`inset 0 3px 9px rgba(0,0,0,0.72), inset 0 -1px 0 rgba(255,255,255,0.05), 0 0 18px ${color}1a`,
                  }} className="kirpa-scan">
                    {/* Glow behind the numeral, keyed to the parameter colour */}
                    <div style={{position:"absolute",inset:0,
                      background:`radial-gradient(ellipse at 50% 92%, ${color}26 0%, transparent 68%)`,
                      pointerEvents:"none"}}/>
                    {/* Slow sweeping trace. One moving element is the difference
                        between a screenshot and a running machine; more than one
                        becomes irritating within a minute. */}
                    {trace && <svg viewBox="0 0 240 40" preserveAspectRatio="none"
                      style={{position:"absolute",left:0,right:0,bottom:0,width:"100%",
                        height:"58%",opacity:0.28,pointerEvents:"none"}}>
                      <path d={trace} fill="none" stroke={color} strokeWidth="2.4"
                        className="mon-trace"/>
                    </svg>}
                    <div style={{fontSize:"9.5px",fontWeight:"700",color:`${color}cc`,
                      textTransform:"uppercase",letterSpacing:"0.16em",fontFamily:FONT_MONO,
                      position:"relative",zIndex:1}}>
                      {label}
                    </div>
                    <div style={{display:"flex",alignItems:"baseline",gap:"5px",position:"relative",zIndex:1}}>
                      <span style={{
                        fontSize: wide ? "clamp(30px,7.5vw,46px)" : "clamp(26px,6.5vw,40px)",
                        fontWeight:"700",
                        color: color,fontFamily:FONT_MONO,
                        textShadow:`0 0 14px ${color}aa,0 0 30px ${color}55`,
                        letterSpacing:"-0.035em",lineHeight:1,
                        fontVariantNumeric:"tabular-nums"
                      }}>{value || "—"}</span>
                      {unit && <span style={{fontSize:"9px",color:`${color}99`,fontFamily:FONT_MONO}}>{unit}</span>}
                    </div>
                    {sub && <div style={{fontSize:"10px",color:`${color}88`,fontFamily:FONT_MONO,position:"relative",zIndex:1}}>{sub}</div>}
                  </div>
                );

                // ── Lab chip ─────────────────────────────────────────────────
                const LabChip = ({k, v}) => {
                  const abn = isAbn(k, v);
                  return (
                    <div style={{
                      // Abnormal values glow; normal ones recede. On a dark
                      // face this reads far faster than the previous pale
                      // orange on pale violet.
                      background: abn ? "rgba(255,80,60,0.10)" : "rgba(255,255,255,0.04)",
                      border:`1px solid ${abn?"#ff547066":"rgba(255,255,255,0.08)"}`,
                      borderRadius:"6px",padding:"5px 9px",
                      display:"flex",flexDirection:"column",gap:"1px",
                      flex:"1 1 clamp(64px,13vw,104px)",minWidth:"64px"
                    }}>
                      <span style={{fontSize:"9px",fontWeight:"700",
                        color: abn ? "#ff8fa0" : "#8b7fb0",
                        textTransform:"uppercase",letterSpacing:"0.08em",
                        fontFamily:FONT_MONO}}>
                        {k}
                      </span>
                      <span style={{
                        fontSize:"clamp(15px,3.4vw,19px)",fontWeight:"700",
                        color: abn ? "#ff5470" : "#ddd6fe",
                        fontFamily:FONT_MONO,
                        fontVariantNumeric:"tabular-nums",
                        textShadow: abn ? "0 0 12px #ff547099" : "none",
                        lineHeight:1.2
                      }}>
                        {fmtVal(k,v)}
                        {abn && <span style={{fontSize:"9px",marginLeft:"3px",color:"#ff8844"}}>!</span>}
                      </span>
                    </div>
                  );
                };

                const hasLabs = gasEntries.length||labEntries.length||cbcEntries.length;

                // Recessed instrument panel. Three surface treatments across
                // the case view: paper for the handover, this sunken dark face
                // for the monitor, raised white cards for anything interactive.
                // Uniform elevation was what made everything look equally
                // important.
                return (
                  <div className="kirpa-monitor kirpa-grain" style={{background:"#0d0a1a",
                    borderTop:"1px solid #2c2447",position:"relative",
                    boxShadow:"inset 0 4px 14px rgba(0,0,0,0.5)"}}>
                    <style>{`
                      @keyframes monitorPulse {
                        0%,100% { opacity:1; }
                        50% { opacity:0.55; }
                      }
                      .mon-pulse { animation: monitorPulse 2.4s ease-in-out infinite; }

                      /* Tabular figures throughout the monitor so values do not
                         jitter horizontally as they change — the detail that
                         separates an instrument readout from a web form. */
                      .kirpa-monitor, .kirpa-monitor * { font-variant-numeric: tabular-nums; }

                      /* Very faint scanline wash. Two percent opacity: felt as
                         surface texture, never seen as stripes. */

                      @media (prefers-reduced-motion: reduce) {
                        .mon-pulse { animation: none; }
                      }

                      /* One slow sweeping trace across the vital tiles. Kept at
                         28 percent opacity and 4.5 seconds: felt as life in the
                         display, not seen as an animation competing for
                         attention. */
                      /* The browser's own spinner duplicates our stepper buttons. */
      input[type=number]::-webkit-inner-spin-button,
      input[type=number]::-webkit-outer-spin-button {
        -webkit-appearance: none; margin: 0;
      }
      @keyframes monTrace {
                        from { stroke-dashoffset: 520; }
                        to   { stroke-dashoffset: 0; }
                      }
                      .mon-trace {
                        stroke-dasharray: 520;
                        animation: monTrace 4.5s linear infinite;
                      }
                      @media (prefers-reduced-motion: reduce) {
                        .mon-trace { animation: none; stroke-dasharray: none; }
                      }
                    `}</style>

                    {/* ── Monitor header bar ─── */}
                    <div style={{padding:"8px 14px",display:"flex",alignItems:"center",
                      justifyContent:"space-between",flexWrap:"wrap",gap:"6px",
                      borderBottom:"1px solid rgba(255,255,255,0.07)",
                      background:"linear-gradient(180deg,#171229,#100c1f)"}}>
                      <div style={{display:"flex",alignItems:"center",gap:"10px"}}>
                        <div className="mon-pulse" style={{width:"7px",height:"7px",borderRadius:"50%",
                          background:"#047857",boxShadow:"0 0 6px #04785799"}}/>
                        <span style={{fontSize:"10px",fontWeight:"700",color:"#a78bfa",
                          fontFamily:FONT_MONO,letterSpacing:"0.16em",
                          textTransform:"uppercase"}}>{t("ui.patientMonitor","Patient Monitor")}</span>
                        <span style={{fontSize:"10px",color:"#8b7fb0",fontFamily:FONT_MONO,
                          fontVariantNumeric:"tabular-nums",letterSpacing:"0.02em"}}>
                          {age && `${age}`}{st.weightKg && ` · ${st.weightKg}kg`}{st.bsa && ` · BSA ${st.bsa}m²`}
                        </span>
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
                        {v.newBp && v.bp && (
                          <span style={{fontSize:"9px",color:"#ff6644",
                            fontFamily:FONT_MONO,letterSpacing:"0.06em"}}>
                            ▲ HAEMODYNAMIC CHANGE
                          </span>
                        )}
                        {labEntries.length > 0 && (
                          <TToggle
                            options={[["si","SI"],["us","US"]]}
                            value={labSI?"si":"us"}
                            onChange={v2=>setLabSI(v2==="si")}
                          />
                        )}
                      </div>
                    </div>

                    {/* ── Vital sign tiles ─── */}
                    {(bpStr||hrVal||spo2Val||rrVal||tmpVal) && (
                      <div style={{padding:"12px 14px",
                        borderBottom:hasLabs?"1px solid rgba(255,255,255,0.07)":"none",
                        background:"#0d0a1a",
                        display:"flex",flexWrap:"wrap",gap:"8px"}}>
                        {/* Colour follows the monitor convention unless the value
                            is abnormal, in which case the warning colour wins:
                            a deranged parameter should not look normal because
                            its channel happens to be green. */}
                        {bpStr && <MonTile label="BP" value={bpStr} unit="mmHg" wide
                          color={bpColor==="#7c6aa0"?MON_COLORS.BP:bpColor}
                          trace="M0 30 L26 30 L34 12 L42 34 L50 26 L58 30 L120 30 L146 30 L154 12 L162 34 L170 26 L178 30 L240 30"
                          sub={v.newBp && v.bp ? `prev: ${v.bp}` : null}/>}
                        {hrVal  && <MonTile label="HR" value={String(hrVal)} unit="bpm"
                          color={hrColor==="#7c6aa0"?MON_COLORS.HR:hrColor}
                          trace="M0 30 L22 30 L28 8 L34 38 L40 22 L48 30 L110 30 L132 30 L138 8 L144 38 L150 22 L158 30 L240 30"/>}
                        {spo2Val && <MonTile label="SpO₂" value={String(spo2Val)} unit="%"
                          color={spo2Color==="#7c6aa0"?MON_COLORS["SpO₂"]:spo2Color}
                          trace="M0 32 Q18 8 36 32 T72 32 T108 32 T144 32 T180 32 T216 32 T252 32"/>}
                        {rrVal  && <MonTile label="RR" value={String(rrVal)} unit="/min"
                          color={MON_COLORS.RR}
                          trace="M0 32 Q30 10 60 32 T120 32 T180 32 T240 32"/>}
                        {tmpVal && <MonTile label="TEMP" value={String(tmpVal)} unit="°C"
                          color={MON_COLORS.TEMP}/>}
                        {/* Any extra vitals fields not covered above */}
                        {Object.entries(v).filter(([k])=>!["bp","hr","spo2","rr","tmp","temp","newBp","skin","capRefill"].includes(k))
                          .map(([k,val])=><MonTile key={k} label={k.toUpperCase()} value={String(val)} color="#7c6aa0"/>)}
                      </div>
                    )}

                    {/* ── Labs panel ─── */}
                    {hasLabs && (
                      <div style={{padding:"10px 14px 12px"}}>
                        {gasEntries.length>0 && (
                          <div style={{marginBottom:"8px"}}>
                            <div style={{fontSize:"9px",fontWeight:"700",color:"#7c6af0",
                              textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:"5px",
                              fontFamily:FONT_MONO,display:"flex",alignItems:"center",gap:"5px"}}>
                              <span style={{display:"inline-block",width:"4px",height:"4px",borderRadius:"50%",background:"#7c6af0"}}/>
                              Blood Gas
                            </div>
                            <div style={{display:"flex",flexWrap:"wrap",gap:"5px"}}>
                              {gasEntries.map(([k,v2])=><LabChip key={k} k={k} v={v2}/>)}
                            </div>
                          </div>
                        )}
                        {labEntries.length>0 && (
                          <div style={{marginBottom:"8px"}}>
                            <div style={{fontSize:"9px",fontWeight:"700",color:"#6d28d9",
                              textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:"5px",
                              fontFamily:FONT_MONO,display:"flex",alignItems:"center",gap:"5px"}}>
                              <span style={{display:"inline-block",width:"4px",height:"4px",borderRadius:"50%",background:"#6d28d9"}}/>
                              Chemistry
                            </div>
                            <div style={{display:"flex",flexWrap:"wrap",gap:"5px"}}>
                              {labEntries.map(([k,v2])=><LabChip key={k} k={k} v={v2}/>)}
                            </div>
                          </div>
                        )}
                        {cbcEntries.length>0 && (
                          <div>
                            <div style={{fontSize:"9px",fontWeight:"700",color:"#34d399",
                              textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:"5px",
                              fontFamily:FONT_MONO,display:"flex",alignItems:"center",gap:"5px"}}>
                              <span style={{display:"inline-block",width:"4px",height:"4px",borderRadius:"50%",background:"#34d399"}}/>
                              CBC
                            </div>
                            <div style={{display:"flex",flexWrap:"wrap",gap:"5px"}}>
                              {cbcEntries.map(([k,v2])=><LabChip key={k} k={k} v={v2}/>)}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Prescription */}
            <div style={{background:"white",borderRadius:T.radius,border:`1px solid ${T.border}`,
              boxShadow:T.shadow,overflow:"hidden"}}>
              <div style={{padding:"12px 18px",borderBottom:`1px solid ${T.border}`,
                background:"linear-gradient(135deg,#faf8ff,#f4f0ff)",
                display:"flex",alignItems:"center",gap:"9px"}}>
                <div style={{width:"22px",height:"22px",borderRadius:"6px",
                  background:`linear-gradient(135deg,${T.accent},${T.accentMid})`,
                  display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,
                  boxShadow:"0 2px 6px rgba(79,38,131,0.3)"}}>
                  <span style={{color:"white",fontSize:"10px",fontWeight:"800"}}>Rx</span>
                </div>
                <span style={{fontSize:"11px",fontWeight:"700",color:T.accent,textTransform:"uppercase",
                  letterSpacing:"0.08em"}}>{t("ui.crrtPrescription","CRRT Prescription")}</span>
              </div>
              <div style={{padding:"16px 18px"}}>
                <TraineeRxForm rx={rx} setRx={setRx} rates={rates}
                  caseOptions={assignment.caseOptions||caseData?.caseOptions}/>
              </div>
            </div>

            {/* Bedside actions */}
            <div style={{background:"white",borderRadius:T.radius,border:`1px solid ${T.border}`,
              boxShadow:T.shadow,overflow:"hidden"}}>
              <div style={{padding:"13px 18px",borderBottom:`1px solid ${T.border}`,
                display:"flex",alignItems:"center",gap:"8px"}}>
                <div style={{width:"6px",height:"6px",borderRadius:"50%",background:T.accent}}/>
                <span style={{fontSize:"11px",fontWeight:"700",color:T.muted,textTransform:"uppercase",
                  letterSpacing:"0.07em"}}>{t("ui.bedsideActions","Bedside Actions")}</span>
              </div>
              <div style={{padding:"14px 18px",display:"flex",flexDirection:"column",gap:"14px"}}>

                {/* Grouped by category. Case-specific custom interventions, which
                    belong to no group, are collected into a final section so a
                    custom case never silently loses its actions. */}
                {(() => {
                  // The full standard set is ALWAYS shown. Case-specific custom
                  // interventions are appended, never substituted, so a stored
                  // caseOptions list can never remove a standard action.
                  const known  = new Set(allInterventions.map(a=>a.id));
                  const extras = (caseData.caseOptions?.interventions||[])
                    .filter(iv=>iv&&iv.id&&!known.has(iv.id));
                  const sections = [...INTERVENTION_GROUPS,
                    ...(extras.length?[{id:"other",label:"Case-Specific Actions",items:extras}]:[])];

                  return sections.map(g=>(
                    <div key={g.id}>
                      <div style={{display:"flex",alignItems:"center",gap:"9px",marginBottom:"9px"}}>
                        <span style={{fontSize:"9.5px",fontWeight:"700",color:T.muted,
                          textTransform:"uppercase",letterSpacing:"0.13em",whiteSpace:"nowrap"}}>
                          {t(`act.${g.id}`,g.label)}
                        </span>
                        <span style={{flex:1,height:"1px",background:T.border}}/>
                      </div>
                      <div style={{display:"flex",flexWrap:"wrap",gap:"8px"}}>
                        {g.items.map(a=>{
                          const active = actions.includes(a.id);
                          // A dosed action opens its own stepper when chosen,
                          // and clears the value when deselected so a stale
                          // amount cannot be submitted for an action that is
                          // no longer selected.
                          if(a.dose){
                            return (
                              <div key={a.id} style={{flex:"1 1 100%",display:"flex",
                                flexDirection:"column",gap:"8px"}}>
                                <button onClick={()=>{
                                    toggleAction(a.id);
                                    if(active) setRx(p=>({...p,[a.dose.field]:0}));
                                  }}
                                  aria-pressed={active}
                                  style={{alignSelf:"flex-start",padding:"8px 13px",
                                    borderRadius:"9px",cursor:"pointer",
                                    fontSize:"12.5px",fontWeight:active?"650":"500",
                                    fontFamily:T.font,letterSpacing:"-0.005em",
                                    display:"inline-flex",alignItems:"center",gap:"7px",
                                    border:`1px solid ${active?T.accentMid:T.border}`,
                                    background:active?T.accentLight:"white",
                                    color:active?T.accentDark:T.text}}>
                                  <span style={{width:"6px",height:"6px",borderRadius:"50%",
                                    background:active?T.accent:T.border,flexShrink:0}}/>
                                  {a.label}
                                </button>
                                {active && (
                                  <div style={{padding:"11px 13px",borderRadius:"9px",
                                    background:"#faf8ff",border:"1px solid #e4dcfb"}}>
                                    <div style={{fontSize:"11px",fontWeight:"700",
                                      color:T.muted,marginBottom:"7px"}}>
                                      How much? ({a.dose.unit})
                                    </div>
                                    <RateStepper value={rx[a.dose.field]??""}
                                      onChange={v=>setRx(p=>({...p,[a.dose.field]:v}))}
                                      step={a.dose.step} min={a.dose.min} max={a.dose.max}/>
                                    {a.dose.hint && (
                                      <div style={{fontSize:"11px",color:T.muted,
                                        marginTop:"7px",lineHeight:"1.5"}}>
                                        {a.dose.hint}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          }
                          return (
                            <button key={a.id} onClick={()=>toggleAction(a.id)}
                              aria-pressed={active}
                              style={{padding:"8px 13px",borderRadius:"9px",cursor:"pointer",
                                fontSize:"12.5px",fontWeight:active?"650":"500",fontFamily:T.font,
                                transition:"background 0.14s, border-color 0.14s, color 0.14s",
                                letterSpacing:"-0.005em",display:"inline-flex",alignItems:"center",gap:"7px",
                                border:`1px solid ${active?T.accentMid:T.border}`,
                                background: active?T.accentLight:"white",
                                color: active?T.accentDark:T.text,
                                boxShadow: active
                                  ? `inset 0 0 0 1px ${T.accentMid}33`
                                  : "0 1px 2px rgba(15,23,42,0.04)"}}>
                              {/* A filled dot rather than a tick: these are states
                                  being toggled, not tasks being completed. */}
                              <span style={{width:"7px",height:"7px",borderRadius:"50%",flexShrink:0,
                                background:active?T.accentDark:"transparent",
                                border:active?"none":`1px solid ${T.border}`,
                                transition:"background 0.14s"}}/>
                              {t(`act.id.${a.id}`,a.label)}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>

          {/* RIGHT: live metrics + submit */}
          <div style={{display:"flex",flexDirection:"column",gap:"14px",position:"sticky",top:"72px"}}>

            {/* Live CRRT metrics — monitor-style */}
            <div style={{background:"#faf8ff",borderRadius:T.radius,
              border:"1px solid #e4dcfb",boxShadow:"0 2px 10px rgba(79,38,131,0.07)",overflow:"hidden"}}>
              <div style={{padding:"9px 14px",borderBottom:"1px solid #e4dcfb",background:"#f3efff",
                display:"flex",alignItems:"center",gap:"8px"}}>
                <div style={{width:"6px",height:"6px",borderRadius:"50%",background:"#7c3aed",
                  boxShadow:"0 0 6px rgba(124,58,237,0.45)"}}/>
                <span style={{fontSize:"10px",fontWeight:"700",color:"#6d28d9",
                  textTransform:"uppercase",letterSpacing:"0.1em",
                  fontFamily:FONT_MONO}}>{t("ui.crrtCalcs","CRRT Calcs")}</span>
                <span style={{marginLeft:"auto",fontSize:"9px",color:"#a99cc4",
                  fontFamily:FONT_MONO}}>live</span>
              </div>
              <div style={{padding:"9px 10px",display:"flex",flexDirection:"column",gap:"8px"}}>
                {(()=>{
                  // ── Derived values ────────────────────────────────────────
                  // All of these follow the prescription live, so changing a
                  // rate or a filter updates every dependent figure at once.
                  const _ecv = resolveFilterEcv(rx.filter, caseData.options || assignment.caseOptions);
                  const _tbv = calculateTBV(wt, caseData?.initialState?.ageMonths);
                  const _pct = (_ecv && _tbv) ? (_ecv / _tbv) * 100 : null;
                  const _pctColor = _pct === null ? "#5b21b6"
                                  : _pct >= 15 ? "#be123c"
                                  : _pct >= 10 ? "#b45309" : "#047857";

                  // Filtration fraction. Shown always, as a real machine does:
                  // the teaching point is whether the learner ACTS on it, not
                  // whether they can derive it unaided.
                  const _hct = (()=>{ const h=parseFloat(caseData?.initialState?.cbc?.Hct);
                                      return (isFinite(h)&&h>0&&h<1)?h:0.30; })();
                  const _plasma = (parseFloat(rx.bfr)||0) * 60 * (1-_hct);
                  const _pbp  = parseFloat(rx.pbpRate)||0;
                  const _rep  = parseFloat(rx.repRate)||0;
                  const _nuf  = parseFloat(rx.netUf)||0;
                  const _ff = (_plasma+_pbp) > 0 ? ((_rep+_nuf+_pbp)/(_plasma+_pbp))*100 : null;
                  const _ffColor = _ff === null ? "#5b21b6"
                                 : _ff >= 25 ? "#be123c"
                                 : _ff >= 20 ? "#b45309" : "#047857";

                  return [
                    { id:"clearance", label:t("calc.grpClearance","Clearance"), accent:"#5b21b6", rows:[
                      { label:t("calc.clearanceBsa","Clearance / BSA"),
                        val: isFinite(clBsa)?clBsa.toFixed(2):null, unit:"L/1.73m\u00b2/hr", color:"#5b21b6",
                        eq:"(PBP + dialysate + post-filter) \u00f7 BSA \u00d7 1.73" },
                      { label:t("calc.clearanceWt","Clearance / wt"),
                        val: isFinite(clKg)?clKg.toFixed(1):null, unit:"ml/kg/hr", color:"#5b21b6",
                        eq:"(PBP + dialysate + post-filter) \u00f7 weight" },
                      { label:t("calc.netUfWt","Net UF / wt"),
                        val: isFinite(netUfKgHr)?netUfKgHr.toFixed(2):null, unit:"ml/kg/hr", color:"#047857",
                        eq:"net fluid removal \u00f7 weight" },
                    ]},
                    { id:"volume", label:t("calc.grpVolume","Blood Volume"), accent:"#6d28d9", rows:[
                      { label:t("calc.ecv","Circuit volume (ECV)"),
                        val:_ecv?String(_ecv):null, unit:"ml", color:"#6d28d9",
                        eq:"priming volume of the selected filter and lines" },
                      { label:t("calc.tbv","Patient blood volume"),
                        val:_tbv?String(_tbv):null, unit:"ml", color:"#6d28d9",
                        eq:"weight \u00d7 ml/kg for age (90 neonate \u2192 70 adolescent)" },
                      { label:t("calc.ecvPct","ECV / blood volume"),
                        val:_pct!==null?_pct.toFixed(1):null, unit:"%", color:_pctColor,
                        eq:"ECV \u00f7 blood volume \u00d7 100 \u00b7 <10% saline, 10\u201315% albumin, \u226515% blood prime" },
                    ]},
                    { id:"ff", label:t("calc.grpFF","Filtration Fraction"), accent:"#b45309", rows:[
                      { label:t("calc.plasmaFlow","Plasma flow"),
                        val:_plasma?Math.round(_plasma).toString():null, unit:"ml/hr", color:"#b45309",
                        eq:"blood flow \u00d7 60 \u00d7 (1 \u2212 haematocrit)" },
                      { label:t("calc.ff","Filtration fraction"),
                        val:_ff!==null?_ff.toFixed(1):null, unit:"%", color:_ffColor,
                        eq:"(post-filter + net UF + PBP) \u00f7 (plasma flow + PBP) \u00d7 100 \u00b7 keep \u226420\u201325%" },
                    ]},
                  ];
                })().map(group=>{
                  const open = calcOpen[group.id] !== false;
                  return (
                    <div key={group.id} style={{borderRadius:"8px",overflow:"hidden",
                      border:`1px solid ${group.accent}26`,background:`${group.accent}08`}}>

                      {/* Group header — tap to collapse. Sections are open by
                          default; collapsing is for learners who want to focus
                          on one relationship at a time. */}
                      <button onClick={()=>setCalcOpen(p=>({...p,[group.id]:open?false:true}))}
                        style={{width:"100%",display:"flex",alignItems:"center",gap:"7px",
                          padding:"7px 10px",background:`${group.accent}14`,border:"none",
                          cursor:"pointer",fontFamily:"inherit",textAlign:"start"}}>
                        <span style={{fontSize:"9px",fontWeight:"700",color:group.accent,
                          textTransform:"uppercase",letterSpacing:"0.1em",fontFamily:FONT_MONO}}>
                          {group.label}
                        </span>
                        <span style={{marginInlineStart:"auto",fontSize:"10px",
                          color:`${group.accent}99`}}>{open?"\u25be":"\u25b8"}</span>
                      </button>

                      {open && <div style={{padding:"8px",display:"flex",
                        flexDirection:"column",gap:"6px"}}>
                        {group.rows.map(({label,val,unit,color,eq})=>(
                          <div key={label} style={{background:"white",
                            border:`1px solid ${color}26`,borderRadius:"6px",padding:"7px 10px"}}>
                            <div style={{fontSize:"9px",color:`${color}99`,fontFamily:FONT_MONO,
                              textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:"2px"}}>
                              {label}
                            </div>
                            <div style={{display:"flex",alignItems:"baseline",gap:"5px"}}>
                              <span style={{fontSize:"20px",fontWeight:"700",fontFamily:FONT_MONO,
                                color: val ? color : "#e4dcfb",lineHeight:1,letterSpacing:"-0.02em",
                                fontVariantNumeric:"tabular-nums"}}>
                                {val || "\u2014"}
                              </span>
                              {val && <span style={{fontSize:"9px",color:`${color}77`,
                                fontFamily:FONT_MONO}}>{unit}</span>}
                            </div>
                            {/* The equation, so the number is never a black box. */}
                            {eq && <div style={{fontSize:"8.5px",color:"#a99cc4",marginTop:"4px",
                              lineHeight:"1.45",fontFamily:FONT_MONO}}>{eq}</div>}
                          </div>
                        ))}
                      </div>}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Attempt tracker */}
            <div className="kirpa-card" style={{background:"white",borderRadius:T.radius,
              border:`1px solid ${T.border}`,boxShadow:T.shadow,padding:"14px 16px",
              borderTop:`3px solid ${T.accent}`}}>
              <div style={{fontSize:"11px",fontWeight:"700",color:T.accent,textTransform:"uppercase",
                letterSpacing:"0.07em",marginBottom:"10px"}}>{t("ui.attempts","Attempts")}</div>
              <div style={{display:"flex",gap:"6px",marginBottom:"12px"}}>
                {Array.from({length:maxAttempts}).map((_,i)=>(
                  <div key={i} style={{height:"6px",flex:1,borderRadius:"3px",
                    background: i < (assignment.attempts||0) ? T.accent : T.bg,
                    border:`1px solid ${T.border}`}}/>
                ))}
              </div>

              {(() => {
                const used = assignment.attempts || 0;
                const exhausted = used >= maxAttempts;
                const blocked = submitting || exhausted;
                return (
                  <button
                    disabled={blocked}
                    onClick={!blocked ? ()=>{
                      // Opens the reflection modal, which submits from there.
                      // A retry that has already reflected goes straight
                      // through.
                      if(!frameDone){ setAskFrame(true); return; }
                      doSubmit(frame);
                    } : undefined}
                    style={{width:"100%",padding:"11px",borderRadius:T.radiusSm,
                      background: exhausted ? T.bgDeep : submitting ? "#a78bfa" : T.accent,
                      color: exhausted ? T.muted : "white",
                      border: exhausted ? `1px solid ${T.border}` : "none",
                      cursor: blocked ? "not-allowed" : "pointer",
                      fontSize:"14px",fontWeight:"700",
                      fontFamily:T.font,letterSpacing:"-0.01em",transition:"background 0.15s",
                      opacity: exhausted ? 0.6 : 1}}>
                    {exhausted ? t("ui.noAttempts","No attempts remaining") : submitting ? t("ui.submitting","Submitting…") : t("ui.submitRx","Submit Prescription ↗")}
                  </button>
                );
              })()}

              {/* Sits with the submit button because that is the moment a
                  learner decides whether they can answer. Buried further up it
                  was effectively invisible. */}
              {(CASE_CONSULTS[assignment.caseId]||[]).length > 0 && (
                <button onClick={()=>setConsultOpen(true)}
                  style={{width:"100%",marginTop:"9px",padding:"10px",
                    borderRadius:T.radiusSm,background:"white",
                    border:`1px solid ${hintCount>0?T.accentMid:T.border}`,
                    color:hintCount>0?T.accentDark:T.muted,cursor:"pointer",
                    fontSize:"13px",fontWeight:"650",fontFamily:T.font,
                    display:"flex",alignItems:"center",justifyContent:"center",gap:"8px"}}>
                  <span style={{fontSize:"14px"}}>☎</span>
                  {t("ui.requestConsult","Request a consult")}
                  {hintCount>0 && (
                    <span style={{fontSize:"11px",fontWeight:"700",
                      background:T.accentLight,color:T.accentDark,
                      borderRadius:"8px",padding:"1px 7px"}}>{hintCount}</span>
                  )}
                </button>
              )}

              {/* Section 12 of the letter states that it remains available
                  from every case screen, so the link stays. The version 2
                  notice paragraph does not: the letter is now shown in full on
                  the consent screen before the first case, which is where a
                  learner reads it, and repeating a paragraph of it under every
                  prescription form earns nothing. New tab, so a learner who
                  opens it mid-case keeps their part-entered prescription. */}
              <div style={{marginTop:"14px",paddingTop:"12px",
                borderTop:`1px solid ${T.border}`,textAlign:"center"}}>
                <a href={LOI_PATH} target="_blank" rel="noopener noreferrer"
                  style={{fontSize:"10.5px",color:T.muted,
                    textDecoration:"underline"}}>
                  Letter of Information and Consent
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reflection, then straight into the submit. One step, not two. */}
      {askFrame && (
        <FrameCapture caseData={caseData} value={frame} onChange={setFrame}
          onSkip={()=>{
            const skipped = {problem:"",why:"",confidence:"",skipped:true};
            setFrame(skipped); setAskFrame(false); setFrameDone(true);
            doSubmit(skipped);
          }}
          onConfirm={()=>{
            setAskFrame(false); setFrameDone(true);
            doSubmit(frame);
          }}/>
      )}

      {consultOpen && (
        <ConsultModal caseId={assignment.caseId} used={hintCount}
          onUse={(n)=>setHintCount(n)} onClose={()=>setConsultOpen(false)}/>
      )}
    </div>
  );
}

function Metric({ label, value, color }) {
  const c = color || T.accent;
  return (
    <div style={{padding:"10px 14px",borderRadius:T.radiusSm,background:T.bgDeep,
      border:`1px solid ${T.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
      <div style={{fontSize:"11px",fontWeight:"600",color:T.muted}}>{label}</div>
      <div style={{fontSize:"15px",fontWeight:"700",color:c,
        fontVariantNumeric:"tabular-nums",fontFamily:T.mono}}>{value}</div>
    </div>
  );
}

function TToggle({ options, value, onChange }) {
  return (
    <div style={{display:"inline-flex",borderRadius:"8px",padding:"2px",
      background:T.bgDeep,border:`1px solid ${T.border}`}}>
      {options.map(([val,lbl])=>(
        <button key={val} onClick={()=>onChange(val)}
          style={{padding:"4px 12px",fontSize:"11px",fontWeight:"700",cursor:"pointer",
            border:"none",fontFamily:T.font,transition:"all 0.15s",borderRadius:"6px",
            background: value===val
              ? `linear-gradient(135deg,${T.accent},${T.accentMid})`
              : "transparent",
            color: value===val ? "white" : T.muted,
            boxShadow: value===val ? "0 1px 4px rgba(79,38,131,0.3)" : "none"}}>
          {lbl}
        </button>
      ))}
    </div>
  );
}

function TraineeRxForm({ rx, setRx, rates, caseOptions }) {
  const [solMode,   setSolMode]   = useState("generic"); // "generic" | "brand"
  const [useSI,     setUseSI]     = useState(true);      // true = SI, false = US

  const up  = (k, v) => setRx(prev => ({ ...prev, [k]: v }));
  const num = (k, v) => up(k, v === "" ? "" : (parseFloat(v) || 0));

  // Unit conversion helpers (US: ml/hr→oz/hr, kg→lb — simple labels only, values stay SI)
  const rateLabel = (base) => useSI ? base : base.replace("ml/hr","mL/hr");
  const unitLabel = useSI ? "ml/hr" : "mL/hr";

  const opts = caseOptions || {};
  const LI = {width:"100%",padding:"8px 11px",borderRadius:"8px",
    border:`1px solid ${T.border}`,background:"white",color:T.text,
    fontSize:"13px",fontFamily:T.font,outline:"none"};

  const accessOpts   = opts.accesses?.length   ? opts.accesses   : Object.keys(accessDefs);
  const filterOpts   = opts.filters?.length    ? opts.filters.map(f=>typeof f==="string"?f:f.key) : ["HF20","ST60","ST150"];
  const primeOpts    = opts.primes?.length     ? opts.primes     : ["saline","albumin","blood"];
  const anticoagOpts = opts.anticoags?.length  ? opts.anticoags  : ["none","citrate","heparin","epoprostenol"];
  const rawSolOpts   = opts.solutions?.length  ? opts.solutions  : ALL_SOLUTIONS;
  const solutionOpts = rawSolOpts.map(k => ({
    key: k,
    // Brand mode shows the trade name with its composition; generic mode shows
    // the composition only, with no trade name anywhere.
    label: solMode==="brand"
      ? `${solutionBrand[k] || k} (${solutionLabels[k] || ""})`
      : (solutionLabels[k] || k)
  }));

  // Filter display name
  // Local override that dropped the weight hint. The global filterLabel keeps
  // it, so the trainee form was the only place a learner could not see which
  // filter suited the patient in front of them.
  const filterLabel = (key) => {
    const f = filterDefs[key];
    if (!f) return key;
    const name = solMode==="brand" ? f.brandLabel : f.genericLabel;
    return f.weightHint ? `${name} (${f.weightHint})` : name;
  };

  return (
    <div>
      {/* Mode toggles — brand/generic only; SI/US is in the labs panel above */}
      <div style={{display:"flex",gap:"10px",marginBottom:"14px",flexWrap:"wrap",alignItems:"center"}}>
        <TToggle options={[["generic","Generic"],["brand","Brand"]]} value={solMode} onChange={setSolMode}/>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(2, minmax(0,1fr))",gap:"10px"}}>
        <Field label="Modality">
          <select value={rx.modality||""} onChange={e=>up("modality",e.target.value)} style={LI}>
            <option value="">Select…</option>
            {MODALITIES.map(m=><option key={m} value={m}>{m}</option>)}
          </select>
        </Field>

        <Field label="Access">
          <select value={rx.access||""} onChange={e=>up("access",e.target.value)} style={LI}>
            <option value="">Select…</option>
            {/* accessDefs already carries the recommended weight range; the
                trainee form was rendering the bare key, so a learner choosing
                a catheter had no size guidance at the point of decision. */}
            {accessOpts.map(x=><option key={x} value={x}>{accessDefs[x]||x}</option>)}
          </select>
        </Field>

        <Field label="Filter">
          <select value={rx.filter||""} onChange={e=>up("filter",e.target.value)} style={LI}>
            <option value="">Select…</option>
            {filterOpts.map(x=><option key={x} value={x}>{filterLabel(x)}</option>)}
          </select>
        </Field>

        <Field label="Prime">
          <select value={rx.prime||""} onChange={e=>up("prime",e.target.value)} style={LI}>
            <option value="">Select…</option>
            {primeOpts.map(x=><option key={x} value={x}>{x}</option>)}
          </select>
        </Field>

        <Field label="Anticoagulation">
          <select value={rx.anticoag||""} onChange={e=>up("anticoag",e.target.value)} style={LI}>
            <option value="">Select…</option>
            {anticoagOpts.map(x=><option key={x} value={x}>{x}</option>)}
          </select>
        </Field>

        <Field label="Solution">
          <select value={rx.solution||""} onChange={e=>up("solution",e.target.value)} style={LI}>
            <option value="">Select…</option>
            {solutionOpts.map(({key,label})=><option key={key} value={key}>{label}</option>)}
          </select>
        </Field>

        <Field label={`BFR (${useSI?"ml/min":"mL/min"})`}>
          <RateStepper value={rx.bfr??""} onChange={v=>num("bfr",v)} step={5} min={0} max={500}/>
        </Field>
        {/* Sign convention stated at the point of entry. Without it a learner
            reasonably wonders whether removing fluid is positive or negative,
            and the read-only display said so while the form did not. */}
        <Field label={`Net UF (${unitLabel}) \u00b7 positive = fluid removed`}>
          <RateStepper value={rx.netUf??""} onChange={v=>num("netUf",v)} step={5} min={0} max={2000}/>
        </Field>

        {rates?.pbp  && <Field label={`PBP (${unitLabel})`}><RateStepper value={rx.pbpRate??""} onChange={v=>num("pbpRate",v)} step={25} min={0} max={5000}/></Field>}
        {rates?.dial && <Field label={`Dialysate (${unitLabel})`}><RateStepper value={rx.dialysateRate??""} onChange={v=>num("dialysateRate",v)} step={25} min={0} max={5000}/></Field>}
        {rates?.rep  && <Field label={`Replacement (${unitLabel})`}><RateStepper value={rx.repRate??""} onChange={v=>num("repRate",v)} step={25} min={0} max={5000}/></Field>}
      </div>

      {/* Solution additives. Shown only where the case involves one, so the
          form stays short for the twenty cases that do not. Without these the
          sodium cases were literally unanswerable: the criterion checked a
          field the learner had no way to fill. */}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{fontSize:"10px",fontWeight:"700",color:T.muted,marginBottom:"5px",
        textTransform:"uppercase",letterSpacing:"0.06em"}}>{label}</div>
      {children}
    </div>
  );
}

// Minimal evaluator for your existing BASE_CASES success types.
// (You can expand this later as you add more success types.)

// Reverse of resolveCriteria: turn a flat successType/successParam into the
// array form the case builder edits. Built-in cases store the flat form only,
// so without this the builder shows NO criterion selected when one of them is
// opened, and re-saving from that screen would silently drop the rule.
//
// Types with no builder equivalent (composite OR-logic cases such as
// citrate_accum, and multi-part cases such as ecv) return null: the builder
// then shows a read-only note instead of an empty, misleading form.
const BUILDER_EDITABLE_TYPES = {
  clearance_gte:   (p) => [{ type:"clearance_gte",   params:{ threshold: parseFloat(p) || 1.9 } }],
  clearance_lte:   (p) => [{ type:"clearance_lte",   params:{ threshold: parseFloat(p) || 3.0 } }],
  effluent_kg_gte: (p) => [{ type:"effluent_kg_gte", params:{ threshold: parseFloat(p) || 25 } }],
  effluent_min_of: (p) => [{ type:"effluent_min_of", params:{
                              mlKgHr: parseFloat(p?.mlKgHr) || 90,
                              lBsaHr: parseFloat(p?.lBsaHr) || 6 } }],
  ff_lte:          (p) => [{ type:"ff_lte", params:{
                              maxFf:        parseFloat(p?.maxFf) || 20,
                              minNetUfKgHr: parseFloat(p?.minNetUfKgHr) || 0 } }],
  netuf_kghr_lte:  (p) => [{ type:"netuf_kghr_lte",  params:{ threshold: parseFloat(p) || 2.0 } }],
  bfr_lte:         (p) => [{ type:"bfr_lte",         params:{ threshold: parseFloat(p) || 150 } }],
  room1:           ()  => [{ type:"room1",           params:{} }],
  electrolytes:    (p) => {
    const req = (v) => v === true || (typeof v === "number" && v > 0);
    const out = [];
    if (req(p?.k))   out.push({ type:"k_add",   params:{} });
    if (req(p?.po4)) out.push({ type:"po4_add", params:{} });
    return out.length ? out : null;
  },
};

function flatToCriteria(caseData){
  if (!caseData?.successType) return null;
  if (Array.isArray(caseData.successCriteria) && caseData.successCriteria.length) {
    return caseData.successCriteria;
  }
  const fn = BUILDER_EDITABLE_TYPES[caseData.successType];
  return fn ? fn(caseData.successParam) : null;
}

// Human description for criteria the builder cannot represent, so an author
// can at least see what the case scores on.
const UNEDITABLE_TYPE_NOTE = {
  ecv:                      "Prime choice must match the circuit-to-blood-volume ratio.",
  intervention:             "Trainee must select a specific bedside action.",
  anticoag_not:             "Trainee must switch away from a named anticoagulant.",
  access_not:               "Trainee must change away from a named catheter size.",
  citrate_alkalosis:        "Any one of several corrective routes (OR logic).",
  citrate_accum:            "Any one of several corrective routes (OR logic).",
  epoprostenol:             "Trainee must stop or reduce epoprostenol.",
  clearance_lte_and_mannitol:"Clearance below a maximum AND mannitol given.",
  na_add:                   "Calculated sodium additive within tolerance.",
  free_water:               "Calculated free water within tolerance.",
};

// Derive successType/successParam from successCriteria array (custom cases)
// Base cases already have flat successType/successParam; custom cases use criteria array.

// ─── EVALUATION ENGINE ────────────────────────────────────────────────────────
// evaluateRoomSuccess returns:
//   success          — boolean overall pass/fail (unchanged behaviour)
//   feedback         — string shown to trainee (unchanged)
//   errorFlags       — object: per-parameter error type or null
//                      null  = parameter not in scope for this case
//                      false = parameter was in scope and acceptable
//                      string = parameter was in scope and had a typed error:
//                               "too_low" | "too_high" | "wrong_choice" |
//                               "missing" | "unnecessary"
//   criteriaLogic    — "single" | "AND" | "OR"
//   chosenSolutionPath — for OR-logic cases: which branch the trainee satisfied
//                        (or null if none satisfied)
//
// errorFlags schema mirrors research_events error_* fields in index.js.
// null = not evaluated in this case (excluded from parameter-level analysis).
// false = evaluated and acceptable.
// string = evaluated and wrong, with direction where meaningful.
//
// Design principle: only flag errors for parameters the case explicitly tests.
// Pre-filled parameters that were not the focus of the case are left null.
// OR-logic cases leave all error_* null and use chosenSolutionPath instead.

// Wraps the evaluator so an inappropriate solution additive fails the case,
// whichever branch returns.
//
// This is a hard failure rather than a flag. Sodium and free water change the
// tonicity of every bag that runs through the circuit, so adding either where
// it is not indicated means the patient receives a solution that was made up
// wrong. Unlike an unnecessary potassium supplement, there is no dose at which
// it is harmless, and the correction it produces is exactly the thing the
// sodium cases teach you to control.


// ─── REQUEST ACCESS FORM ─────────────────────────────────────────────────────
const TRAINEE_ROLES=[
  "Paediatric Nephrology Fellow",
  "Paediatric Critical Care Fellow",
  "Adult Nephrology Fellow",
  "Adult Critical Care Fellow",
  "Resident Physician",
  "Consultant — Paediatric Nephrologist",
  "Consultant — Paediatric Critical Care",
  "Consultant — Adult Nephrologist",
  "Consultant — Adult Critical Care",
  "Critical Care Nurse",
  "Nursing Student",
  "Other (please specify)",
];
const EDUCATOR_ROLES=[
  "Paediatric Nephrologist","Paediatric Critical Care Physician",
  "Adult Nephrologist","Adult Critical Care Physician",
  "Critical Care Nurse","Other (please specify)",
];
function RequestAccessForm({onBack,onDone}){
  const {t}=useLocale();
  const rName=useRef(""); const rEmail=useRef("");
  const [role,setRole]=useState("");
  const [otherRole,setOtherRole]=useState("");
  const [institution,setInstitution]=useState("");
  const [err,setErr]=useState(""); const [busy,setBusy]=useState(false);

  const doReg=async()=>{
    const name=rName.current.trim(); const email=rEmail.current.trim();
    const finalRole=role==="Other (please specify)"?`Other: ${otherRole.trim()}`:role;
    if(!name||!email||!role||!institution.trim()){setErr("All fields are required.");return;}
    if(role==="Other (please specify)"&&!otherRole.trim()){setErr("Please specify your role.");return;}
    if(!email.includes("@")){setErr("Please enter a valid institutional email address.");return;}
    setBusy(true);setErr("");
    try {
      // Check for duplicate
      const ref = doc(db, 'educators', email);
      const snap = await getDoc(ref);
      if(snap.exists()){setErr("An account with that email already exists.");setBusy(false);return;}
      // Write pending educator record to Firestore
      await setDoc(ref, {
        email, name,
        role: finalRole,
        institution: institution.trim(),
        institutionId: `inst_${institution.trim().toLowerCase().replace(/[^a-z0-9]/g,"_").slice(0,20)}_${Date.now().toString(36)}`,
        approved: false,
        requestedAt: serverTimestamp(),
      });
      // A Firestore onCreate Cloud Function (index.js) will:
      //   1. Email amrit.kirpalani@lhsc.on.ca with name/email/role/institution
      //      and a direct link to approve: https://nephro-kirpa.netlify.app/approve?token=...
      //   2. When you approve, a second CF creates the Firebase Auth account,
      //      sets a temp password, and emails the educator from appnephro@gmail.com.
      onDone();
    } catch(e) {
      console.error(e);
      setErr("Something went wrong. Please try again.");
    }
    setBusy(false);
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:"13px"}}>
      <div style={{padding:"11px 13px",background:"#fffbeb",borderRadius:"9px",
        border:"1px solid #fde68a",fontSize:"11px",color:"#92400e",lineHeight:"1.5"}}>
        ⚠ This form is for <strong>educators only</strong>. Trainees do not need an account.
        You will receive an email when your account is ready — no password needed at this step.
      </div>
      <div>
        <label style={C.lbl}>Full Name</label>
        <input type="text" placeholder="Dr. Jane Smith" style={C.inp}
          onChange={e=>rName.current=e.target.value}/>
      </div>
      <div>
        <label style={C.lbl}>Institutional Email</label>
        <input type="email" placeholder="jane@hospital.edu" style={C.inp}
          onChange={e=>rEmail.current=e.target.value}/>
      </div>
      <div>
        <label style={C.lbl}>Institution / Hospital</label>
        <input type="text" placeholder="e.g. Children's Hospital London" style={C.inp}
          onChange={e=>setInstitution(e.target.value)}/>
      </div>
      <div>
        <label style={C.lbl}>Role</label>
        <select value={role} onChange={e=>setRole(e.target.value)} style={{...C.inp,fontSize:"13px"}}>
          <option value="">{t("shell.selectRole","Select your role…")}</option>
          {EDUCATOR_ROLES.map(r=><option key={r} value={r}>{r}</option>)}
        </select>
        {role==="Other (please specify)"&&<input type="text" placeholder="Please describe your role…"
          style={{...C.inp,marginTop:"6px"}}
          onChange={e=>setOtherRole(e.target.value)}/>}
      </div>
      {err&&<p style={{color:"#dc2626",fontSize:"12px",padding:"8px 12px",background:"#fef2f2",borderRadius:"7px",margin:0}}>{err}</p>}
      <button onClick={doReg} disabled={busy}
        style={{padding:"11px",borderRadius:"10px",background:`linear-gradient(135deg,${C.dark},${C.pu})`,
          border:"none",color:"white",fontWeight:"700",fontSize:"13px",cursor:"pointer",
          fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:"7px",
          boxShadow:"0 4px 16px rgba(79,38,131,0.25)",opacity:busy?0.7:1}}>
        {busy?<Ic n="spin" s="w-4 h-4"/>:null}
        {busy?"Submitting…":"Request Access →"}
      </button>
    </div>
  );
}

// ─── LOGIN SCREEN ─────────────────────────────────────────────────────────────
function LoginScreen({onLogin,onBack}){
  const [panel,setPanel]=useState("educator"); // "educator" | "request" | "trainee"
  const [regDone,setRegDone]=useState(false);
  const [err,setErr]=useState(""); const [busy,setBusy]=useState(false);
  const [resetSent,setResetSent]=useState(false);
  const rEmail=useRef(""); const rPw=useRef("");
  const canvasRef=useRef(null);

  // ── F1 slow tilted-loop circuit animation ─────────────────────────────────
  useEffect(()=>{
    const cv=canvasRef.current;
    if(!cv) return;
    const ct=cv.getContext('2d');
    let W,H,loops=[],particles=[],raf;

    const buildLoops=()=>{
      loops=[];particles=[];
      const cx=W*0.5,cy=H*0.5;
      const defs=[
        {rx:W*0.38,ry:H*0.28,tilt:-0.3,col:'blood', pw:0.9, pc:8,  speed:0.00055},
        {rx:W*0.24,ry:H*0.18,tilt:0.4, col:'dial',  pw:0.7, pc:6,  speed:-0.00045},
        {rx:W*0.14,ry:H*0.10,tilt:-0.1,col:'blood', pw:0.55,pc:4,  speed:0.0007},
      ];
      defs.forEach((d,di)=>{
        const pts=[];
        for(let i=0;i<=120;i++){
          const a=(i/120)*Math.PI*2;
          pts.push({
            x:cx+d.rx*Math.cos(a)*Math.cos(d.tilt)-d.ry*Math.sin(a)*Math.sin(d.tilt),
            y:cy+d.rx*Math.cos(a)*Math.sin(d.tilt)+d.ry*Math.sin(a)*Math.cos(d.tilt),
          });
        }
        loops.push({...d,pts});
        for(let i=0;i<d.pc;i++)
          particles.push({li:di,t:i/d.pc,speed:d.speed,size:d.col==='blood'?2.0:1.5});
      });
    };

    const ptOn=(pts,t)=>{
      const n=pts.length-1;
      const norm=((t%1)+1)%1;
      const i=Math.floor(norm*n);
      const f=norm*n-i;
      const a=pts[i],b=pts[Math.min(i+1,n-1)];
      return {x:a.x+(b.x-a.x)*f,y:a.y+(b.y-a.y)*f};
    };

    const draw=()=>{
      ct.clearRect(0,0,W,H);

      // Loop paths
      loops.forEach(l=>{
        ct.strokeStyle=l.col==='blood'?'rgba(210,55,75,0.09)':'rgba(150,90,230,0.08)';
        ct.lineWidth=l.pw;
        ct.beginPath();
        l.pts.forEach((p,i)=>i===0?ct.moveTo(p.x,p.y):ct.lineTo(p.x,p.y));
        ct.closePath();ct.stroke();
      });

      // Particles
      particles.forEach(p=>{
        p.t+=p.speed;
        const l=loops[p.li];
        const pos=ptOn(l.pts,p.t);
        const isB=l.col==='blood';
        const g=ct.createRadialGradient(pos.x,pos.y,0,pos.x,pos.y,p.size*3.5);
        g.addColorStop(0,isB?'rgba(251,130,145,0.55)':'rgba(192,132,252,0.5)');
        g.addColorStop(1,'rgba(0,0,0,0)');
        ct.beginPath();ct.arc(pos.x,pos.y,p.size*3.5,0,Math.PI*2);ct.fillStyle=g;ct.fill();
        ct.beginPath();ct.arc(pos.x,pos.y,p.size,0,Math.PI*2);
        ct.fillStyle=isB?'rgba(255,168,178,0.85)':'rgba(215,178,255,0.85)';ct.fill();
      });

      raf=requestAnimationFrame(draw);
    };

    const resize=()=>{
      W=cv.offsetWidth||600;H=cv.offsetHeight||700;
      cv.width=W;cv.height=H;
      buildLoops();
    };

    const ro=new ResizeObserver(()=>resize());
    ro.observe(cv.parentElement);
    resize();draw();
    return ()=>{ cancelAnimationFrame(raf); ro.disconnect(); };
  },[]);

  const doLogin=async()=>{
    const email=rEmail.current.trim(); const pw=rPw.current;
    if(!email||!pw){setErr("Please enter your email and password.");return;}
    setBusy(true);setErr("");
    try {
      const cred = await signInWithEmailAndPassword(auth, email, pw);
      const snap = await getDoc(doc(db, 'educators', email));
      if(!snap.exists()){
        await fbSignOut(auth);
        setErr("No educator account found for this email. Please request access.");
        setBusy(false);return;
      }
      const profile = snap.data();
      if(!profile.approved){
        await fbSignOut(auth);
        setErr("Your account is pending approval. You'll receive an email when it's ready.");
        setBusy(false);return;
      }
      onLogin({
        email,
        name: profile.name,
        institution: profile.institution || "",
        institutionId: profile.institutionId || "",
        role: profile.role || "",
        uid: cred.user.uid,
      });
    } catch(e) {
      const msg = {
        'auth/invalid-credential': 'Incorrect email or password.',
        'auth/user-not-found':     'No account found for this email.',
        'auth/wrong-password':     'Incorrect password.',
        'auth/too-many-requests':  'Too many attempts. Please wait a moment and try again.',
        'auth/user-disabled':      'This account has been disabled. Contact the administrator.',
        'auth/network-request-failed': 'Network error. Please check your connection.',
      }[e.code] || 'Sign-in failed. Please check your credentials.';
      setErr(msg);
    }
    setBusy(false);
  };

  const doResetPassword=async()=>{
    const email=rEmail.current.trim();
    if(!email){setErr("Enter your email above, then click Forgot Password.");return;}
    setBusy(true);setErr("");
    try {
      await sendPasswordResetEmail(auth, email);
      setErr(""); setResetSent(true);
    } catch(e) {
      setErr(e.code==='auth/user-not-found'
        ? 'No account found for this email.'
        : 'Could not send reset email. Please try again.');
    }
    setBusy(false);
  };

  // Panel-aware colours: dark theme needs adjusted tab/form colours
  const tabBg      = "rgba(255,255,255,0.08)";
  const tabActive  = "rgba(255,255,255,0.14)";
  const cardBg     = "rgba(10,5,22,0.55)";
  const cardBorder = "rgba(192,132,252,0.2)";
  const inpStyle   = {...C.inp, background:"rgba(255,255,255,0.07)",
    border:"1px solid rgba(255,255,255,0.13)", color:"white",
    "::placeholder":{color:"rgba(255,255,255,0.35)"}};

  return (
    <div style={{minHeight:"100vh",background:"#0e0814",display:"flex",overflow:"hidden",
      fontFamily:FONT_UI,position:"relative"}}>

      {/* Canvas background */}
      <canvas ref={canvasRef} style={{position:"absolute",inset:0,width:"100%",height:"100%",display:"block"}}/>

      {/* Back to the landing fork. Only rendered when a fork exists to go back
          to, so magic-link and direct-URL entries are unaffected. */}
      {onBack&&<button onClick={onBack} style={{position:"absolute",top:"22px",insetInlineStart:"22px",
        zIndex:3,background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.16)",
        borderRadius:"8px",color:"rgba(255,255,255,0.8)",padding:"7px 12px",cursor:"pointer",
        fontSize:"12px",fontWeight:"600",fontFamily:FONT_UI}}>← Back</button>}

      {/* Pill tags — top centre */}
      <div style={{position:"absolute",top:"28px",left:0,right:0,display:"flex",
        justifyContent:"center",gap:"8px",flexWrap:"wrap",zIndex:2,padding:"0 20px"}}>
        {["CRRT Simulation","Nephrology","Critical Care"].map((lbl,i)=>(
          <span key={lbl} style={{
            fontSize:"11px",fontWeight:"600",padding:"5px 14px",borderRadius:"20px",
            letterSpacing:"0.04em",
            border: i===1
              ? "1px solid rgba(192,132,252,0.38)"
              : "1px solid rgba(251,113,133,0.38)",
            color: i===1 ? "#d8b4fe" : "#fda4af",
            background: i===1 ? "rgba(192,132,252,0.1)" : "rgba(251,113,133,0.1)",
          }}>{lbl}</span>
        ))}
      </div>

      {/* Main centered card */}
      <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",
        padding:"80px 20px 20px",position:"relative",zIndex:1}}>
        <div style={{width:"100%",maxWidth:"460px"}}>

          {/* Wordmark */}
          <div style={{textAlign:"center",marginBottom:"28px"}}>
            <h1 style={{fontSize:"36px",fontWeight:"900",margin:"0 0 4px",
              letterSpacing:"-0.03em",fontFamily:FONT_UI,color:"white"}}>
              Ki<span style={{color:"#c084fc"}}>RPA</span>
            </h1>
            <p style={{color:"rgba(192,132,252,0.6)",fontSize:"11px",margin:0,
              textTransform:"uppercase",letterSpacing:"0.14em",fontWeight:"600"}}>
              Kidney Replacement Prescription Analytics
            </p>
          </div>

          {/* Panel selector tabs */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"4px",
            background:"rgba(255,255,255,0.06)",borderRadius:"12px",padding:"4px",
            marginBottom:"20px",border:"1px solid rgba(255,255,255,0.08)"}}>
            {[
              {id:"educator",label:"Educator",icon:"🎓"},
              {id:"request", label:"Request Access",icon:"✉️"},
              {id:"trainee", label:"Trainee",icon:"📋"},
            ].map(({id,label,icon})=>{
              const active=panel===id;
              return <button key={id} onClick={()=>{setPanel(id);setErr("");}}
                style={{padding:"9px 6px",borderRadius:"9px",border:"none",cursor:"pointer",
                  fontFamily:"inherit",fontWeight:active?"700":"500",fontSize:"11px",
                  background:active?"rgba(255,255,255,0.12)":"transparent",
                  color:active?"white":"rgba(255,255,255,0.45)",
                  transition:"all 0.15s",display:"flex",flexDirection:"column",
                  alignItems:"center",gap:"3px",
                  boxShadow:active?"0 1px 4px rgba(0,0,0,0.3)":"none"}}>
                <span style={{fontSize:"14px"}}>{icon}</span>
                {label}
              </button>;
            })}
          </div>

          {/* Panel content card */}
          <div style={{background:cardBg,borderRadius:"16px",padding:"26px",
            border:`1px solid ${cardBorder}`,
            boxShadow:"0 8px 40px rgba(0,0,0,0.4)",backdropFilter:"blur(12px)"}}>

            {/* Dark-theme overrides for child components (RequestAccessForm, TraineePortal) */}
            <style>{`
              .kirpa-dark-panel input,.kirpa-dark-panel select,.kirpa-dark-panel textarea{
                background:rgba(255,255,255,0.07)!important;
                border:1px solid rgba(255,255,255,0.13)!important;
                color:white!important;
              }
              .kirpa-dark-panel label{color:rgba(255,255,255,0.6)!important;}
              .kirpa-dark-panel input::placeholder,.kirpa-dark-panel select::placeholder{color:rgba(255,255,255,0.3)!important;}
              .kirpa-dark-panel select option{background:#1a0e2e;color:white;}
            `}</style>
            <div className="kirpa-dark-panel">

            {/* ── EDUCATOR SIGN IN ── */}
            {panel==="educator"&&<>
              <div style={{marginBottom:"18px"}}>
                <h2 style={{fontSize:"16px",fontWeight:"700",color:"white",margin:"0 0 3px",fontFamily:FONT_UI}}>Educator Sign In</h2>
                <p style={{fontSize:"12px",color:"rgba(255,255,255,0.4)",margin:0}}>
                  For approved educators only. Trainees —{" "}
                </p>
              </div>
              <div style={{marginBottom:"12px"}}>
                <label style={{...C.lbl,color:"rgba(255,255,255,0.6)"}}>Email</label>
                <input type="email" placeholder="educator@hospital.edu"
                  style={{...C.inp,fontSize:"13px",background:"rgba(255,255,255,0.07)",
                    border:"1px solid rgba(255,255,255,0.13)",color:"white"}}
                  onChange={e=>rEmail.current=e.target.value}
                  onKeyDown={e=>e.key==="Enter"&&doLogin()}/>
              </div>
              <div style={{marginBottom:"16px"}}>
                <label style={{...C.lbl,color:"rgba(255,255,255,0.6)"}}>Password</label>
                <input type="password" placeholder="••••••••"
                  style={{...C.inp,fontSize:"13px",background:"rgba(255,255,255,0.07)",
                    border:"1px solid rgba(255,255,255,0.13)",color:"white"}}
                  onChange={e=>rPw.current=e.target.value}
                  onKeyDown={e=>e.key==="Enter"&&doLogin()}/>
              </div>
              {err&&<p style={{color:"#fca5a5",fontSize:"12px",padding:"9px 12px",
                background:"rgba(220,38,38,0.15)",borderRadius:"8px",
                border:"1px solid rgba(220,38,38,0.25)",marginBottom:"12px",margin:"0 0 12px"}}>{err}</p>}
              <button onClick={doLogin} disabled={busy}
                style={{width:"100%",padding:"12px",borderRadius:"10px",border:"none",
                  background:"linear-gradient(135deg,#7e22ce,#4f46e5)",color:"white",
                  fontWeight:"700",fontSize:"13px",cursor:"pointer",fontFamily:"inherit",
                  display:"flex",alignItems:"center",justifyContent:"center",gap:"8px",
                  boxShadow:"0 4px 20px rgba(124,58,237,0.35)",
                  opacity:busy?0.7:1,transition:"opacity 0.15s"}}>
                {busy?<Ic n="spin" s="w-4 h-4"/>:null}
                {busy?"Signing in…":"Sign In"}
              </button>
              {resetSent
                ? <p style={{textAlign:"center",fontSize:"12px",color:"#6ee7b7",marginTop:"12px",marginBottom:0,padding:"8px",background:"rgba(5,150,105,0.15)",borderRadius:"7px",border:"1px solid rgba(5,150,105,0.25)"}}>
                    Password reset email sent! Check your inbox.
                  </p>
                : <button onClick={doResetPassword} disabled={busy}
                    style={{display:"block",width:"100%",marginTop:"10px",background:"none",border:"none",
                      cursor:"pointer",fontSize:"12px",color:"rgba(255,255,255,0.35)",fontFamily:"inherit",
                      textAlign:"center"}}>
                    Forgot password?
                  </button>
              }
            </>}

            {/* ── REQUEST ACCESS ── */}
            {panel==="request"&&(regDone
              ? <div style={{textAlign:"center",padding:"20px 0"}}>
                  <div style={{fontSize:"40px",marginBottom:"12px"}}>✅</div>
                  <h3 style={{color:"white",fontWeight:"700",marginBottom:"8px",fontFamily:FONT_UI}}>Request submitted!</h3>
                  <p style={{fontSize:"12px",color:"rgba(255,255,255,0.5)",lineHeight:"1.6",marginBottom:"18px"}}>
                    Your request has been sent to the KiRPA administrator.<br/>
                    You'll receive an email at your institutional address once your account is ready — this typically takes 1–2 business days.
                  </p>
                  <button onClick={()=>{setPanel("educator");setRegDone(false);}}
                    style={{padding:"9px 22px",borderRadius:"9px",background:"rgba(192,132,252,0.12)",
                      border:"1px solid rgba(192,132,252,0.35)",color:"#d8b4fe",fontWeight:"600",
                      fontSize:"13px",cursor:"pointer",fontFamily:"inherit"}}>
                    ← Back to Sign In
                  </button>
                </div>
              : <>
                  <div style={{marginBottom:"16px"}}>
                    <h2 style={{fontSize:"16px",fontWeight:"700",color:"white",margin:"0 0 3px",fontFamily:FONT_UI}}>Request Educator Access</h2>
                    <p style={{fontSize:"12px",color:"rgba(255,255,255,0.4)",margin:0}}>
                      Accounts are manually approved. Trainees don't need an account —.
                    </p>
                  </div>
                  <RequestAccessForm onBack={()=>setPanel("educator")} onDone={()=>setRegDone(true)}/>
                </>
            )}

            </div>{/* end kirpa-dark-panel */}
          </div>

          {/* Footer */}
          <p style={{textAlign:"center",fontSize:"10px",color:"rgba(255,255,255,0.22)",
            marginTop:"20px",lineHeight:"1.7"}}>
            KiRPA · Kidney Replacement Prescription Analytics<br/>
            Dr. Amrit Kirpalani MD, MEHP, FRCPC<br/>
            For educational purposes only · Not for clinical decision-making
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── PATIENT SNAPSHOT (read-only, SI/US toggle) ───────────────────────────────
function PatientSnapshot({initialState,useSI}){
  const {t}=useLocale();
  const display=(k,v)=>{
    if(typeof v!=="number") return {val:String(v),unit:""};
    if(!useSI){const us=cvtToUS(k,v);if(us)return {val:us.v,unit:us.u};}
    return {val:String(v),unit:siUnits[k]||""};
  };
  // Abnormal flagging drives the red highlight in the labs panel. The list was
  // previously limited to K, Na, pH, HCO3, Plt and Cr, which meant the very
  // derangements several cases turn on (phosphate, ionised calcium, magnesium,
  // ammonia) rendered as ordinary black text and had to be pointed at in the
  // stem instead. Paediatric reference ranges.
  const CRIT = {
    K:       v => v > 5.5  || v < 3.5,
    Na:      v => v > 145  || v < 135,
    Cl:      v => v > 110  || v < 98,
    HCO3:    v => v < 22   || v > 28,
    Urea:    v => v > 8,
    Cr:      v => v > 150,
    PO4:     v => v > 1.8  || v < 1.0,
    Mg:      v => v > 1.05 || v < 0.70,
    iCa:     v => v > 1.35 || v < 1.10,
    TotalCa: v => v > 2.65 || v < 2.10,
    Alb:     v => v < 30,
    pH:      v => v < 7.35 || v > 7.45,
    pCO2:    v => v > 45   || v < 35,
    BE:      v => v < -4   || v > 4,
    Lactate: v => v > 2.0,
    Ammonia: v => v > 50,
    Glucose: v => v > 10   || v < 3.5,
    Hgb:     v => v < 90,
    Plt:     v => v < 100,
    WBC:     v => v > 15   || v < 4,
    Hct:     v => v < 0.28,
    INR:     v => v > 1.5,
    Fibrinogen: v => v < 1.5,
  };
  const isCrit=(k,v)=> typeof v==="number" && !!CRIT[k] && CRIT[k](v);
  const DP=({lbl,k,v})=>{const {val,unit}=display(k||lbl,v);return <div style={{padding:"6px 8px",borderRadius:"6px",background:"#f8f7ff"}}>
    <div style={{fontSize:"10px",color:"#9ca3af",textTransform:"uppercase",letterSpacing:"0.05em"}}>{lbl}</div>
    <div style={{fontSize:"14px",fontWeight:"600",color:isCrit(k||lbl,v)?"#dc2626":"#1a0533"}}>{val}{unit&&<span style={{fontSize:"10px",color:"#9ca3af",fontWeight:"400"}}> {unit}</span>}</div>
  </div>;};
  const Sec=({title,ch})=><div style={{marginBottom:"14px"}}>
    <div style={C.secHead}>{title}</div>
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(110px,1fr))",gap:"5px"}}>{ch}</div>
  </div>;
  const ageD=initialState.ageMonths>24
    ?`${Math.floor(initialState.ageMonths/12)} ${t("pt.years","yr")}`
    :`${initialState.ageMonths} ${t("pt.months","mo")}`;
  return <div style={{fontSize:"13px"}}>
    <Sec title={t("sec.patientInfo","Patient Info")} ch={[
      <DP key="wt" lbl={t("pt.weight","Weight")} k="Weight" v={`${initialState.weightKg} kg`}/>,
      <DP key="ag" lbl={t("pt.age","Age")} k="Age" v={ageD}/>,
      <DP key="bs" lbl={t("pt.bsa","BSA")} k="BSA" v={`${initialState.bsa} m²`}/>,
      <DP key="tb" lbl={t("pt.tbv","Est. TBV")} k="TBV" v={`${calculateTBV(initialState.weightKg,initialState.ageMonths)} mL`}/>,
    ]}/>
    {initialState.vitals&&<Sec title={t("sec.vitals","Vitals")} ch={Object.entries(initialState.vitals).filter(([k])=>k!=="newBp").map(([k,v])=><DP key={k} lbl={k.toUpperCase()} k={k} v={initialState.vitals.newBp&&k==="bp"?`${v} → ${initialState.vitals.newBp}`:v}/>)}/>}
    {initialState.labs&&<Sec title={t("sec.labs","Lab Values")} ch={Object.entries(initialState.labs).map(([k,v])=><DP key={k} lbl={k} k={k} v={v}/>)}/>}
    {initialState.gas&&<Sec title={t("sec.gas","Blood Gas")} ch={Object.entries(initialState.gas).map(([k,v])=><DP key={k} lbl={k} k={k} v={v}/>)}/>}
    {initialState.cbc&&<Sec title={t("sec.cbc","CBC")} ch={Object.entries(initialState.cbc).map(([k,v])=><DP key={k} lbl={k} k={k} v={v}/>)}/>}
  </div>;
}

// ─── PRESCRIPTION DISPLAY ─────────────────────────────────────────────────────
function PrescriptionDisplay({caseId,rx,initialState,solMode="generic",filterMode="brand"}){
  const {t}=useLocale();
  if(!rx||Object.keys(rx).length===0) return <div style={{padding:"14px",background:"#f5f3ff",borderRadius:"8px",fontSize:"13px",color:"#6b7280",textAlign:"center"}}>{t("rx.blank","This case starts with a blank prescription — the trainee writes all initial orders from scratch.")}</div>;
  const ecv=filterDefs[rx.filter]?.ecv||0;
  const tbv=calculateTBV(initialState.weightKg,initialState.ageMonths);
  const ecvPct=tbv>0?((ecv/tbv)*100).toFixed(1):0;
  const cl=calcClearance(rx.pbpRate||0,rx.dialysateRate||0,rx.repRate||0,initialState.bsa);
  const R=({l,v})=>v!=null&&v!==""?<div style={{display:"flex",justifyContent:"space-between",padding:"4px 0",borderBottom:"1px solid #f3f4f6",fontSize:"12px"}}><span style={{color:"#6b7280"}}>{l}</span><span style={{fontWeight:"600",color:"#1a0533"}}>{v}</span></div>:null;
  const modalityColors={"CVVH":"#dbeafe","CVVHD":"#dcfce7","CVVHDF":"#ede9fe"};
  const mc=modalityColors[rx.modality]||"#f3f4f6";
  return <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"8px",fontSize:"13px"}}>
    {rx.modality&&<div style={{background:mc,borderRadius:"8px",padding:"10px 12px",gridColumn:"1/-1",display:"flex",alignItems:"center",gap:"8px"}}>
      <span style={{fontSize:"11px",fontWeight:"700",color:"#374151",textTransform:"uppercase",letterSpacing:"0.06em"}}>{t("rx.modality","Modality")}</span>
      <span style={{fontWeight:"700",fontSize:"15px",color:"#1a0533"}}>{rx.modality}</span>
    </div>}
    <div style={{background:"#f8f7ff",borderRadius:"8px",padding:"12px"}}>
      <div style={C.secHead}>{t("sec.circuitSetup","Circuit Setup")}</div>
      <R l={t("rx.access","Access catheter")} v={rx.access?`${rx.access} — ${accessDefs[rx.access]}`:null}/>
      <R l={t("rx.filter","Filter")} v={rx.filter?filterLabel(rx.filter,filterMode):null}/>
      <R l={t("rx.prime","Circuit prime")} v={rx.prime}/>
      <R l={t("rx.anticoag","Anticoagulation")} v={rx.anticoag}/>
      <R l={t("rx.solution","Solution")} v={rx.solution
        ? (solMode==="brand"
            ? `${solName(rx.solution,"brand")} (${solutionLabels[rx.solution]||""})`
            : solName(rx.solution,"generic"))
        : null}/>
    </div>
    <div style={{background:"#f8f7ff",borderRadius:"8px",padding:"12px"}}>
      <div style={C.secHead}>{t("sec.flowRates","Flow Rates")}</div>
      <R l={t("rx.bfr","Blood flow rate")} v={rx.bfr!=null?`${rx.bfr} ml/min`:null}/>
      <R l={t("rx.pbp","Pre-blood pump (PBP)")} v={rx.pbpRate!=null?`${rx.pbpRate} ml/hr`:null}/>
      <R l={t("rx.dialysate","Dialysate rate")} v={rx.dialysateRate!=null?`${rx.dialysateRate} ml/hr`:null}/>
      <R l={t("rx.replacement","Post-filter replacement")} v={rx.repRate!=null?`${rx.repRate} ml/hr`:null}/>
      <R l={t("rx.netUf","Net fluid removal (UF) — positive = fluid removed")} v={rx.netUf!=null?`${Math.abs(rx.netUf)} ml/hr`:null}/>
    </div>
    {(rx.Na_add||rx.freeWater)&&<div style={{background:"#f0fdf4",borderRadius:"8px",padding:"12px"}}>
      <div style={{...C.secHead,color:"#065f46",borderBottomColor:"#bbf7d0"}}>{t("sec.additives","Additives")}</div>
      <R l={t("rx.naAdd","Sodium additive")} v={rx.Na_add?`${rx.Na_add} mmol`:null}/>
      <R l={t("rx.freeWater","Free water")} v={rx.freeWater?`${rx.freeWater} mL`:null}/>
    </div>}
    <div style={{background:"#eef2ff",borderRadius:"8px",padding:"12px",gridColumn:(rx.Na_add||rx.freeWater)?"span 1":"1/-1"}}>
      <div style={{...C.secHead,color:"#4338ca",borderBottomColor:"#c7d2fe"}}>{t("sec.calculated","Calculated Values")}</div>
      <R l={t("rx.ecv","Filter circuit volume (ECV)")} v={`${ecv} mL`}/>
      <R l={t("rx.ecvPct","Circuit volume as % of blood volume")} v={`${ecvPct}%`}/>
      <R l={t("rx.clearance","Clearance dose")} v={`${cl} L/1.73m²/hr`}/>
    </div>
  </div>;
}

// ─── LAB EDITOR (with SI/US toggle) ──────────────────────────────────────────
// ─── LAB / VITALS DEFINITIONS ────────────────────────────────────────────────
const LAB_SCHEMA = {
  vitals: [
    {k:"hr",    l:"HR",     unit:"bpm",  type:"num"},
    {k:"rr",    l:"RR",     unit:"/min", type:"num"},
    {k:"temp",  l:"Temp",   unit:"°C",   type:"num"},
    {k:"bp",    l:"BP",     unit:"mmHg", type:"text", placeholder:"e.g. 90/50"},
    {k:"spo2",  l:"SpO2",   unit:"%",    type:"num"},
    {k:"capRefill",l:"Cap Refill", unit:"sec", type:"num"},
    {k:"newBp", l:"BP (post-event)", unit:"mmHg", type:"text", placeholder:"e.g. 60/30"},
    {k:"tmp",   l:"TMP",    unit:"mmHg", type:"text", placeholder:"e.g. 350 mmHg"},
    {k:"skin",  l:"Skin",   unit:"",     type:"text", placeholder:"e.g. flushed"},
  ],
  gas: [
    {k:"pH",   l:"pH",   unit:"",      siU:"",      usU:""},
    {k:"pCO2", l:"pCO2", unit:"mmHg",  siU:"mmHg",  usU:"mmHg"},
    {k:"pO2",  l:"pO2",  unit:"mmHg",  siU:"mmHg",  usU:"mmHg"},
    {k:"BE",   l:"BE",   unit:"mmol/L",siU:"mmol/L",usU:"mEq/L"},
    {k:"HCO3", l:"HCO3", unit:"mmol/L",siU:"mmol/L",usU:"mEq/L"},
    {k:"Lactate",l:"Lactate",unit:"mmol/L",siU:"mmol/L",usU:"mmol/L"},
  ],
  chem: [
    {k:"Na",        l:"Na",           siU:"mmol/L",  usU:"mEq/L"},
    {k:"K",         l:"K",            siU:"mmol/L",  usU:"mEq/L"},
    {k:"Cl",        l:"Cl",           siU:"mmol/L",  usU:"mEq/L"},
    {k:"HCO3",      l:"HCO3",         siU:"mmol/L",  usU:"mEq/L"},
    {k:"Urea",      l:"Urea/BUN",     siU:"mmol/L",  usU:"mg/dL",  cvt:{toUS:v=>(v*2.8).toFixed(1),fromUS:v=>v/2.8}},
    {k:"Cr",        l:"Creatinine",   siU:"µmol/L",  usU:"mg/dL",  cvt:{toUS:v=>(v/88.4).toFixed(2),fromUS:v=>v*88.4}},
    {k:"Glucose",   l:"Glucose",      siU:"mmol/L",  usU:"mg/dL",  cvt:{toUS:v=>(v*18).toFixed(0),fromUS:v=>v/18}},
    {k:"iCa",       l:"iCa",          siU:"mmol/L",  usU:"mg/dL",  cvt:{toUS:v=>(v*4).toFixed(2),fromUS:v=>v/4}},
    {k:"TotalCa",   l:"Total Ca",     siU:"mmol/L",  usU:"mg/dL",  cvt:{toUS:v=>(v*4).toFixed(2),fromUS:v=>v/4}},
    {k:"Mg",        l:"Mg",           siU:"mmol/L",  usU:"mg/dL",  cvt:{toUS:v=>(v*2.43).toFixed(2),fromUS:v=>v/2.43}},
    {k:"PO4",       l:"PO4",          siU:"mmol/L",  usU:"mg/dL",  cvt:{toUS:v=>(v*3.1).toFixed(2),fromUS:v=>v/3.1}},
    {k:"Alb",       l:"Albumin",      siU:"g/L",     usU:"g/dL",   cvt:{toUS:v=>(v/10).toFixed(1),fromUS:v=>v*10}},
    {k:"ALT",       l:"ALT",          siU:"U/L",     usU:"U/L"},
    {k:"AST",       l:"AST",          siU:"U/L",     usU:"U/L"},
    {k:"INR",       l:"INR",          siU:"",        usU:""},
    {k:"PTT",       l:"PTT",          siU:"sec",     usU:"sec"},
    {k:"Fibrinogen",l:"Fibrinogen",   siU:"g/L",     usU:"mg/dL",  cvt:{toUS:v=>(v*100).toFixed(0),fromUS:v=>v/100}},
    {k:"Ammonia",   l:"Ammonia",      siU:"µmol/L",  usU:"µg/dL",  cvt:{toUS:v=>(v*1.7).toFixed(0),fromUS:v=>v/1.7}},
    {k:"Lactate",   l:"Lactate",      siU:"mmol/L",  usU:"mmol/L"},
    {k:"CircuitiCa",l:"Circuit iCa",  siU:"mmol/L",  usU:"mg/dL",  cvt:{toUS:v=>(v*4).toFixed(2),fromUS:v=>v/4}},
    {k:"Acetaminophen",l:"Acetaminophen",siU:"µmol/L",usU:"µg/mL", cvt:{toUS:v=>(v/6.61).toFixed(1),fromUS:v=>v*6.61}},
    {k:"Salicylate",l:"Salicylate",   siU:"mmol/L",  usU:"mg/dL",  cvt:{toUS:v=>(v*13.8).toFixed(1),fromUS:v=>v/13.8}},
    {k:"VPA",       l:"Valproate",    siU:"µmol/L",  usU:"µg/mL",  type:"text", placeholder:"e.g. 950 (toxic>700)"},
    {k:"UABlood",   l:"UA Blood",     type:"text",   placeholder:"neg / trace / 1+ / 2+ / 3+"},
    {k:"UAProtein", l:"UA Protein",   type:"text",   placeholder:"neg / 1+ / 2+ / 3+"},
    {k:"AlbCrRatio",l:"Alb:Cr Ratio", siU:"mg/mmol", usU:"mg/g",   cvt:{toUS:v=>(v*8.84).toFixed(0),fromUS:v=>v/8.84}},
  ],
  cbc: [
    {k:"Hgb",  l:"Hgb",   siU:"g/L",      usU:"g/dL", cvt:{toUS:v=>(v/10).toFixed(1),fromUS:v=>v*10}},
    {k:"Hct",  l:"Hct",   siU:"",         usU:""},
    {k:"WBC",  l:"WBC",   siU:"×10⁹/L",   usU:"×10³/µL"},
    {k:"Plt",  l:"Plt",   siU:"×10⁹/L",   usU:"×10³/µL"},
    {k:"Neut", l:"Neut",  siU:"×10⁹/L",   usU:"×10³/µL"},
  ],
};

function LabEditor({initialState,onChange}){
  const [local, setLocal] = useState(()=>JSON.parse(JSON.stringify(initialState||{})));
  const [useSI, setUseSI] = useState(true);

  const getVal = (section, k) => {
    if (section === "base")    return local[k] ?? "";
    if (section === "vitals")  return local.vitals?.[k] ?? "";
    if (section === "gas")     return local.gas?.[k] ?? "";
    if (section === "chem")    return local.labs?.[k] ?? "";
    if (section === "cbc")     return local.cbc?.[k] ?? "";
    return "";
  };

  const dispVal = (def, rawVal) => {
    if (rawVal === "" || rawVal === null || rawVal === undefined) return "";
    if (def.type === "text") return rawVal;
    const n = parseFloat(rawVal);
    if (isNaN(n)) return rawVal;
    if (!useSI && def.cvt) return def.cvt.toUS(n);
    return n;
  };

  const dispUnit = (def) => {
    if (!useSI && def.cvt && def.usU) return def.usU;
    return def.siU || def.unit || "";
  };

  const flush = (section, k, def, rawInput) => {
    let stored = rawInput;
    if (def.type !== "text" && rawInput !== "") {
      const n = parseFloat(rawInput);
      if (!isNaN(n)) stored = (!useSI && def.cvt) ? parseFloat(def.cvt.fromUS(n).toFixed(4)) : n;
    }
    let next = {...local};
    if (section === "base") {
      next[k] = stored === "" ? undefined : stored;
    } else {
      const secKey = {vitals:"vitals",gas:"gas",chem:"labs",cbc:"cbc"}[section];
      if (stored === "" || stored === undefined) {
        const copy = {...(next[secKey]||{})};
        delete copy[k];
        next[secKey] = Object.keys(copy).length ? copy : undefined;
      } else {
        next[secKey] = {...(next[secKey]||{}), [k]: stored};
      }
    }
    setLocal(next);
    onChange(next);
  };

  const Inp = ({section, def}) => {
    const val = getVal(section, def.k);
    const dv  = dispVal(def, val);
    const isText = def.type === "text";
    return (
      <div style={{marginBottom:"6px"}}>
        <div style={{fontSize:"10px",fontWeight:"700",color:"#6b7280",textTransform:"uppercase",
          letterSpacing:"0.05em",marginBottom:"3px",display:"flex",justifyContent:"space-between"}}>
          <span>{def.l}</span>
          {dispUnit(def) && <span style={{fontWeight:"400",color:"#9ca3af",textTransform:"none"}}>{dispUnit(def)}</span>}
        </div>
        <input
          type={isText ? "text" : "number"}
          step="any"
          defaultValue={dv}
          key={`${section}-${def.k}-${useSI}`}
          placeholder={def.placeholder || ""}
          onBlur={e => flush(section, def.k, def, e.target.value)}
          style={{...C.inp, width:"100%", boxSizing:"border-box",
            background: dv !== "" ? "#fafbff" : "white",
            borderColor: dv !== "" ? "#c7d2fe" : "#e5e7eb"}}
        />
      </div>
    );
  };

  const SecGrid = ({title, color="#4f2683", children}) => (
    <div style={{marginBottom:"16px",background:"white",borderRadius:"10px",
      border:"1px solid #e5e7eb",overflow:"hidden"}}>
      <div style={{padding:"8px 14px",background:`linear-gradient(135deg,#f8f7ff,#f1f0ff)`,
        borderBottom:"1px solid #e5e7eb",display:"flex",alignItems:"center",gap:"6px"}}>
        <span style={{width:"7px",height:"7px",borderRadius:"50%",background:color,display:"inline-block"}}/>
        <span style={{fontSize:"11px",fontWeight:"700",color,textTransform:"uppercase",letterSpacing:"0.07em"}}>{title}</span>
      </div>
      <div style={{padding:"12px 14px",display:"grid",
        gridTemplateColumns:"repeat(auto-fill,minmax(130px,1fr))",gap:"8px 12px"}}>
        {children}
      </div>
    </div>
  );

  return (
    <div style={{fontSize:"13px"}}>
      {/* Header controls */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
        marginBottom:"16px",flexWrap:"wrap",gap:"8px"}}>
        <span style={{fontSize:"12px",color:"#6b7280"}}>
          Leave fields blank to exclude them from the case. Filled fields appear in the trainee view.
        </span>
        <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
          <span style={{fontSize:"12px",color:"#6b7280"}}>Units:</span>
          <button onClick={()=>setUseSI(v=>!v)}
            style={{...C.btn(useSI?C.pu:"#374151"),padding:"4px 12px",fontSize:"12px"}}>
            {useSI?"SI":"US"}
          </button>
          {!useSI&&<span style={{fontSize:"10px",color:"#d97706",background:"#fef3c7",
            padding:"2px 7px",borderRadius:"5px"}}>Saved in SI</span>}
        </div>
      </div>

      {/* Patient Info */}
      <SecGrid title="Patient Info" color="#374151">
        <div style={{marginBottom:"6px"}}>
          <div style={{fontSize:"10px",fontWeight:"700",color:"#6b7280",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:"3px",display:"flex",justifyContent:"space-between"}}>
            <span>Weight</span><span style={{color:"#9ca3af",fontWeight:"400"}}>kg</span>
          </div>
          <input type="number" step="0.1" defaultValue={local.weightKg??""} key={`wt-${local.weightKg}`}
            onBlur={e=>{const v=e.target.value;const next={...local,weightKg:v===""?undefined:parseFloat(v)};setLocal(next);onChange(next);}}
            placeholder="" style={{...C.inp,width:"100%",boxSizing:"border-box"}}/>
        </div>
        <div style={{marginBottom:"6px"}}>
          <div style={{fontSize:"10px",fontWeight:"700",color:"#6b7280",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:"3px",display:"flex",justifyContent:"space-between"}}>
            <span>Age</span><span style={{color:"#9ca3af",fontWeight:"400"}}>months</span>
          </div>
          <input type="number" step="1" defaultValue={local.ageMonths??""} key={`ag-${local.ageMonths}`}
            onBlur={e=>{const v=e.target.value;const next={...local,ageMonths:v===""?undefined:parseFloat(v)};setLocal(next);onChange(next);}}
            placeholder="" style={{...C.inp,width:"100%",boxSizing:"border-box"}}/>
        </div>
        <div style={{marginBottom:"6px"}}>
          <div style={{fontSize:"10px",fontWeight:"700",color:"#6b7280",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:"3px",display:"flex",justifyContent:"space-between"}}>
            <span>BSA</span><span style={{color:"#9ca3af",fontWeight:"400"}}>m²</span>
          </div>
          <input type="number" step="0.01" defaultValue={local.bsa??""} key={`bsa-${local.bsa}`}
            onBlur={e=>{const v=e.target.value;const next={...local,bsa:v===""?undefined:parseFloat(v)};setLocal(next);onChange(next);}}
            placeholder="" style={{...C.inp,width:"100%",boxSizing:"border-box"}}/>
        </div>
      </SecGrid>

      {/* Vital Signs */}
      <SecGrid title="Vital Signs" color="#0891b2">
        {LAB_SCHEMA.vitals.map(def => <Inp key={def.k} section="vitals" def={def}/>)}
      </SecGrid>

      {/* Blood Gas */}
      <SecGrid title="Blood Gas" color="#7c3aed">
        {LAB_SCHEMA.gas.map(def => <Inp key={def.k} section="gas" def={{...def,type:def.type||"num"}}/>)}
      </SecGrid>

      {/* Chemistry */}
      <SecGrid title="Chemistry / Labs" color="#0369a1">
        {LAB_SCHEMA.chem.map(def => <Inp key={def.k} section="chem" def={{...def,type:def.type||"num"}}/>)}
      </SecGrid>

      {/* CBC */}
      <SecGrid title="CBC / Haematology" color="#059669">
        {LAB_SCHEMA.cbc.map(def => <Inp key={def.k} section="cbc" def={{...def,type:def.type||"num"}}/>)}
      </SecGrid>
    </div>
  );
}


// ─── OPTIONS EDITOR HELPERS (module-scope to prevent remount on re-render) ────
// These MUST be at module scope — defining them inside OptionsEditor would cause
// React to see new component references on every render, unmounting inputs and
// losing focus after every keystroke.
const optsChipStyle = (color="#ede9fe",text="#6d28d9") => ({
  display:"inline-flex",alignItems:"center",gap:"5px",padding:"3px 10px 3px 10px",
  borderRadius:"20px",background:color,color:text,fontSize:"11px",fontWeight:"600",
  border:`1px solid ${color}`
});
function OptsRemoveBtn({onClick}){
  return <button onClick={onClick} style={{background:"none",border:"none",cursor:"pointer",color:"inherit",
    lineHeight:1,padding:"0 0 0 2px",fontSize:"13px",opacity:0.7,fontFamily:"inherit"}}>×</button>;
}
function OptsAddBtn({onClick,label="+ Add"}){
  return <button onClick={onClick} style={{padding:"3px 10px",borderRadius:"16px",border:"1px dashed #d1d5db",
    background:"white",color:"#6b7280",fontSize:"11px",cursor:"pointer",fontFamily:"inherit"}}>{label}</button>;
}
function OptsSection({title,hint,children}){
  return <div style={{marginBottom:"14px"}}>
    <div style={{fontSize:"10px",fontWeight:"700",color:"#374151",textTransform:"uppercase",
      letterSpacing:"0.07em",marginBottom:"3px"}}>{title}</div>
    {hint&&<div style={{fontSize:"11px",color:"#9ca3af",marginBottom:"6px"}}>{hint}</div>}
    <div style={{display:"flex",gap:"6px",flexWrap:"wrap",alignItems:"center"}}>{children}</div>
  </div>;
}
function OptsInlineForm({children,onSave,onCancel,saveLabel="Add",disabled}){
  return <div style={{display:"flex",gap:"8px",alignItems:"flex-end",flexWrap:"wrap",padding:"8px 12px",
    background:"#f8f7ff",borderRadius:"8px",border:"1px solid #ede9fe",marginTop:"6px",width:"100%"}}>
    {children}
    <div style={{display:"flex",gap:"6px",marginLeft:"auto"}}>
      <button onClick={onCancel} style={{padding:"4px 12px",borderRadius:"6px",border:"1px solid #d1d5db",
        background:"white",color:"#6b7280",fontSize:"12px",cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
      <button onClick={onSave} disabled={disabled} style={{padding:"4px 12px",borderRadius:"6px",border:"none",
        background:disabled?"#e5e7eb":"#4F2683",color:disabled?"#9ca3af":"white",fontSize:"12px",cursor:disabled?"not-allowed":"pointer",fontFamily:"inherit"}}>{saveLabel}</button>
    </div>
  </div>;
}
function OptsFField({label,value,onChange,type="text",placeholder,style={}}){
  return <div style={{display:"flex",flexDirection:"column",gap:"3px",...style}}>
    <label style={{fontSize:"10px",fontWeight:"700",color:"#6b7280",textTransform:"uppercase"}}>{label}</label>
    <input type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
      style={{width:"100%",padding:"5px 8px",border:"1px solid #d1d5db",borderRadius:"7px",fontSize:"12px",boxSizing:"border-box",fontFamily:"inherit",outline:"none"}}/>
  </div>;
}

// ─── OPTIONS EDITOR — per-case customisation of available dropdown choices ───
function OptionsEditor({opts, onChange}){
  // opts shape: { filters, anticoags, solutions, accesses, primes, interventions }
  const o = opts || {};

  // Resolved lists (with fallbacks)
  const myFilters   = o.filters     || Object.keys(filterDefs).map(k=>({key:k,...filterDefs[k]}));
  const myAccess    = o.accesses    || Object.keys(accessDefs);
  const myAnticoags = o.anticoags   || DEFAULT_ANTICOAGS;
  const myPrimes    = o.primes      || DEFAULT_PRIMES;
  const mySols      = o.solutions   || ALL_SOLUTIONS;
  const myIntvs     = o.interventions || allInterventions.map(i=>({...i,type:"checkbox"}));

  const [showAddFilter,setShowAddFilter]     = useState(false);
  const [showAddSol,setShowAddSol]           = useState(false);
  const [showAddAccess,setShowAddAccess]     = useState(false);
  const [showAddIntv,setShowAddIntv]         = useState(false);
  const [showAddAnticoag,setShowAddAnticoag] = useState(false);
  const [newAnticoagLabel,setNewAnticoagLabel] = useState("");

  // Add-filter form state
  const [newFLabel,setNewFLabel] = useState("");
  const [newFEcv,setNewFEcv]     = useState("");
  // Add-solution form state
  const [newSKey,setNewSKey]     = useState("");
  const [newSLabel,setNewSLabel] = useState("");
  // Add-access form state
  const [newAKey,setNewAKey]     = useState("");
  const [newALabel,setNewALabel] = useState("");
  // Add-intervention form state
  const [newILabel,setNewILabel] = useState("");
  const [newIType,setNewIType]   = useState("checkbox");
  const [newIMin,setNewIMin]     = useState("");
  const [newIMax,setNewIMax]     = useState("");
  const [newIUnit,setNewIUnit]   = useState("");

  const up = (patch) => onChange({...o,...patch});

  return <div style={{fontSize:"12px"}}>

    {/* ── Filters ── */}
    <OptsSection title="Filters available to trainee" hint="Remove filters to restrict what the trainee can choose. Add custom filters with ECV.">
      {myFilters.map((f,i)=>{
        const key = typeof f==="string"?f:f.key;
        // Weight hint belongs here too: an educator choosing which filters a
        // case offers needs the same size guidance a learner gets.
        const label = typeof f==="string"
          ? (filterDefs[key] ? `${filterDefs[key].brandLabel} (${filterDefs[key].weightHint})` : key)
          : f.label||f.key;
        const ecv   = typeof f==="string"?(filterDefs[key]?.ecv||"?"):f.ecv;
        return <span key={key} style={optsChipStyle()}>
          {label} <span style={{fontWeight:"400",opacity:0.7}}>({ecv} mL ECV)</span>
          <OptsRemoveBtn onClick={()=>up({filters:myFilters.filter((_,j)=>j!==i)})}/>
        </span>;
      })}
      {!showAddFilter && <OptsAddBtn onClick={()=>setShowAddFilter(true)} label="+ Add filter"/>}
      {showAddFilter&&<OptsInlineForm
        onCancel={()=>{setShowAddFilter(false);setNewFLabel("");setNewFEcv("");}}
        onSave={()=>{
          if(!newFLabel.trim()||!newFEcv) return;
          const key="custom_"+newFLabel.trim().toLowerCase().replace(/\s+/g,"_");
          up({filters:[...myFilters,{key,label:newFLabel.trim(),ecv:parseFloat(newFEcv)}]});
          setNewFLabel("");setNewFEcv("");setShowAddFilter(false);
        }}
        disabled={!newFLabel.trim()||!newFEcv}
      >
        <OptsFField label="Filter label" value={newFLabel} onChange={setNewFLabel} placeholder="e.g. HF20 Small" style={{flex:"2 1 150px"}}/>
        <OptsFField label="ECV (mL) *required*" type="number" value={newFEcv} onChange={setNewFEcv} placeholder="e.g. 85" style={{flex:"1 1 100px"}}/>
      </OptsInlineForm>}
    </OptsSection>

    {/* ── Anticoagulants ── */}
    <OptsSection title="Anticoagulants available to trainee" hint="Remove options or add a custom anticoagulant for this case.">
      {myAnticoags.map(a=><span key={a} style={optsChipStyle("#dcfce7","#166534")}>
        {a}
        <OptsRemoveBtn onClick={()=>up({anticoags:myAnticoags.filter(x=>x!==a)})}/>
      </span>)}
      {DEFAULT_ANTICOAGS.filter(a=>!myAnticoags.includes(a)).map(a=>(
        <button key={a} onClick={()=>up({anticoags:[...myAnticoags,a]})}
          style={{padding:"3px 10px",borderRadius:"16px",border:"1px dashed #d1d5db",background:"#f9fafb",
            color:"#9ca3af",fontSize:"11px",cursor:"pointer",fontFamily:"inherit"}}>+ {a}</button>
      ))}
      {!showAddAnticoag&&<OptsAddBtn onClick={()=>setShowAddAnticoag(true)} label="+ Custom anticoagulant"/>}
      {showAddAnticoag&&<OptsInlineForm
        onCancel={()=>{setShowAddAnticoag(false);setNewAnticoagLabel("");}}
        onSave={()=>{
          if(!newAnticoagLabel.trim()) return;
          up({anticoags:[...myAnticoags,newAnticoagLabel.trim()]});
          setNewAnticoagLabel("");setShowAddAnticoag(false);
        }}
        disabled={!newAnticoagLabel.trim()}
      >
        <OptsFField label="Anticoagulant name" value={newAnticoagLabel} onChange={setNewAnticoagLabel}
          placeholder="e.g. Argatroban, Bivalirudin" style={{flex:"2 1 200px"}}/>
      </OptsInlineForm>}
    </OptsSection>

    {/* ── Solutions ── */}
    <OptsSection title="Solutions available to trainee" hint="Remove solutions or add a custom one.">
      {mySols.map((s,i)=>{
        const label = solutionLabels[s]?`${s} (${solutionLabels[s]})`:s;
        return <span key={s} style={optsChipStyle("#fef9c3","#854d0e")}>
          {label}
          <OptsRemoveBtn onClick={()=>up({solutions:mySols.filter((_,j)=>j!==i)})}/>
        </span>;
      })}
      {!showAddSol && ALL_SOLUTIONS.filter(s=>!mySols.includes(s)).length>0&&<select
        style={{...C.sel,fontSize:"11px",padding:"3px 8px",height:"auto"}}
        value="" onChange={e=>{if(e.target.value)up({solutions:[...mySols,e.target.value]});}}>
        <option value="">+ Add standard solution…</option>
        {ALL_SOLUTIONS.filter(s=>!mySols.includes(s)).map(s=><option key={s} value={s}>{s}</option>)}
      </select>}
      {!showAddSol && <OptsAddBtn onClick={()=>setShowAddSol(true)} label="+ Custom solution"/>}
      {showAddSol&&<OptsInlineForm
        onCancel={()=>{setShowAddSol(false);setNewSKey("");setNewSLabel("");}}
        onSave={()=>{
          if(!newSKey.trim()) return;
          const key=newSKey.trim();
          up({solutions:[...mySols,key]});
          setNewSKey("");setNewSLabel("");setShowAddSol(false);
        }}
        disabled={!newSKey.trim()}
      >
        <OptsFField label="Solution name (generic)" value={newSKey} onChange={setNewSKey} placeholder="e.g. NormoCal K2" style={{flex:"2 1 160px"}}/>
        <OptsFField label="Electrolyte label (optional)" value={newSLabel} onChange={setNewSLabel} placeholder="e.g. Na140 K2 Ca0" style={{flex:"2 1 160px"}}/>
      </OptsInlineForm>}
    </OptsSection>

    {/* ── Access catheters ── */}
    <OptsSection title="Access catheters available to trainee" hint="Remove sizes not relevant for this case.">
      {myAccess.map((a,i)=>{
        const label = accessDefs[a]?`${a} — ${accessDefs[a]}`:a;
        return <span key={a} style={optsChipStyle("#e0f2fe","#0c4a6e")}>
          {label}
          <OptsRemoveBtn onClick={()=>up({accesses:myAccess.filter((_,j)=>j!==i)})}/>
        </span>;
      })}
      {Object.keys(accessDefs).filter(a=>!myAccess.includes(a)).map(a=>(
        <button key={a} onClick={()=>up({accesses:[...myAccess,a]})}
          style={{padding:"3px 10px",borderRadius:"16px",border:"1px dashed #d1d5db",background:"#f9fafb",
            color:"#9ca3af",fontSize:"11px",cursor:"pointer",fontFamily:"inherit"}}>+ {a}</button>
      ))}
      {!showAddAccess && <OptsAddBtn onClick={()=>setShowAddAccess(true)} label="+ Custom size"/>}
      {showAddAccess&&<OptsInlineForm
        onCancel={()=>{setShowAddAccess(false);setNewAKey("");setNewALabel("");}}
        onSave={()=>{
          if(!newAKey.trim()) return;
          up({accesses:[...myAccess,newAKey.trim()]});
          setNewAKey("");setNewALabel("");setShowAddAccess(false);
        }}
        disabled={!newAKey.trim()}
      >
        <OptsFField label="Catheter size key" value={newAKey} onChange={setNewAKey} placeholder="e.g. 6F" style={{flex:"1 1 100px"}}/>
        <OptsFField label="Description (optional)" value={newALabel} onChange={setNewALabel} placeholder="e.g. 6 French special" style={{flex:"2 1 160px"}}/>
      </OptsInlineForm>}
    </OptsSection>

    {/* ── Primes ── */}
    <OptsSection title="Priming solutions available to trainee">
      {myPrimes.map(p=><span key={p} style={optsChipStyle("#fce7f3","#9d174d")}>
        {p}
        <OptsRemoveBtn onClick={()=>up({primes:myPrimes.filter(x=>x!==p)})}/>
      </span>)}
      {DEFAULT_PRIMES.filter(p=>!myPrimes.includes(p)).map(p=>(
        <button key={p} onClick={()=>up({primes:[...myPrimes,p]})}
          style={{padding:"3px 10px",borderRadius:"16px",border:"1px dashed #d1d5db",background:"#f9fafb",
            color:"#9ca3af",fontSize:"11px",cursor:"pointer",fontFamily:"inherit"}}>+ {p}</button>
      ))}
    </OptsSection>

    {/* ── Interventions ── */}
    <OptsSection title="Clinical interventions available to trainee" hint="Checkbox = trainee selects it. Numeric = trainee enters a value within the specified range.">
      {myIntvs.map((iv,i)=>(
        <span key={iv.id} style={optsChipStyle("#f3e8ff","#6b21a8")}>
          {iv.type==="numeric"?<>📊 {iv.label} ({iv.min}–{iv.max}{iv.unit?" "+iv.unit:""})</>:<>☑ {iv.label}</>}
          <OptsRemoveBtn onClick={()=>up({interventions:myIntvs.filter((_,j)=>j!==i)})}/>
        </span>
      ))}
      {!showAddIntv && <OptsAddBtn onClick={()=>setShowAddIntv(true)} label="+ Add intervention"/>}
      {showAddIntv&&<OptsInlineForm
        onCancel={()=>{setShowAddIntv(false);setNewILabel("");setNewIType("checkbox");setNewIMin("");setNewIMax("");setNewIUnit("");}}
        onSave={()=>{
          if(!newILabel.trim()) return;
          const id="custom_"+newILabel.trim().toLowerCase().replace(/\s+/g,"_").slice(0,24);
          const intv={id,label:newILabel.trim(),type:newIType};
          if(newIType==="numeric"){intv.min=parseFloat(newIMin)||0;intv.max=parseFloat(newIMax)||0;intv.unit=newIUnit.trim();}
          up({interventions:[...myIntvs,intv]});
          setNewILabel("");setNewIType("checkbox");setNewIMin("");setNewIMax("");setNewIUnit("");setShowAddIntv(false);
        }}
        disabled={!newILabel.trim()||(newIType==="numeric"&&!newIMax)}
      >
        <OptsFField label="Label" value={newILabel} onChange={setNewILabel} placeholder="e.g. Adjust citrate dose" style={{flex:"3 1 180px"}}/>
        <div style={{display:"flex",flexDirection:"column",gap:"3px",flex:"0 0 auto"}}>
          <label style={{fontSize:"10px",fontWeight:"700",color:"#6b7280",textTransform:"uppercase"}}>Type</label>
          <select value={newIType} onChange={e=>setNewIType(e.target.value)} style={{...C.sel,fontSize:"12px",padding:"5px 8px",height:"auto"}}>
            <option value="checkbox">Checkbox</option>
            <option value="numeric">Numeric</option>
          </select>
        </div>
        {newIType==="numeric"&&<>
          <OptsFField label="Min" type="number" value={newIMin} onChange={setNewIMin} placeholder="0" style={{flex:"0 0 70px"}}/>
          <OptsFField label="Max" type="number" value={newIMax} onChange={setNewIMax} placeholder="10" style={{flex:"0 0 70px"}}/>
          <OptsFField label="Unit" value={newIUnit} onChange={setNewIUnit} placeholder="ml/hr" style={{flex:"0 0 80px"}}/>
        </>}
      </OptsInlineForm>}
    </OptsSection>

  </div>;
}

// ─── PRESCRIPTION + SUCCESS CRITERIA EDITOR ───────────────────────────────────
function PrescriptionEditor({caseData,caseId,onChange}){
  // ── Own ALL data in local state — initialized once from props ──────
  // This prevents parent re-renders from ever touching our inputs.
  const [ld,setLd]=useState(()=>JSON.parse(JSON.stringify(caseData)));
  // Flush local state to parent (called by buttons/selects/checkboxes — not on typing)
  const flush=(next)=>{setLd(next);onChange(next);};
  // Sync caseOptions into ld when parent updates them via Options tab
  useEffect(()=>{
    if(caseData.caseOptions!==undefined){
      setLd(prev=>{
        if(JSON.stringify(prev.caseOptions)===JSON.stringify(caseData.caseOptions)) return prev;
        return {...prev,caseOptions:caseData.caseOptions};
      });
    }
  },[caseData.caseOptions]);

  const rx=ld.initialPrescription||{};
  // Built-in cases store the flat form, so derive the editable array from it.
  // Types the builder cannot represent yield null and are surfaced as a
  // read-only note rather than an empty form.
  const rawCriteria=ld.successCriteria;
  const derived=Array.isArray(rawCriteria)&&rawCriteria.length?rawCriteria:flatToCriteria(ld);
  const criteria=Array.isArray(derived)?derived:[];
  const uneditableNote=(!criteria.length&&ld.successType)
    ? (UNEDITABLE_TYPE_NOTE[ld.successType]||`Scored by the built-in rule "${ld.successType}".`)
    : null;
  const [clUnit,setClUnit]=useState("bsa");
  const [solMode,setSolMode]=useState("generic");
  const [filterMode,setFilterMode]=useState("brand");
  const [criteriaLogic,setCriteriaLogic]=useState(ld.criteriaLogic||"AND");
  const [room1Confirm,setRoom1Confirm]=useState(false);
  const prescMode=ld.prescribingMode||"";
  const setPrescMode=(m)=>flush({...ld,prescribingMode:m});

  const modality=rx.modality||"";
  const rates=modality?modalityRates(modality):{pbp:true,rep:true,dial:true};
  const isOnboarding=ld.facet==="General Prescribing";

  // upRx: for selects/buttons → flush immediately
  // upRxNum: for typed number inputs → only update localData, flush on blur
  const upRx=(k,v)=>{
    const nums=["bfr","pbpRate","dialysateRate","repRate","netUf","Na_add","freeWater"];
    const val=nums.includes(k)&&v!==""?parseFloat(v)||0:v;
    let newRx={...rx,[k]:val};
    if(modality==="CVVHD"){newRx.pbpRate=0;newRx.repRate=0;}
    if(modality==="CVVH"){newRx.dialysateRate=0;}
    flush({...ld,initialPrescription:newRx});
  };
  // Blur handler for typed number fields — parse and flush
  const upRxBlur=(k,raw)=>{
    const nums=["bfr","pbpRate","dialysateRate","repRate","netUf","Na_add","freeWater"];
    const val=nums.includes(k)&&raw!==""?parseFloat(raw)||0:raw;
    let newRx={...rx,[k]:val};
    if(modality==="CVVHD"){newRx.pbpRate=0;newRx.repRate=0;}
    if(modality==="CVVH"){newRx.dialysateRate=0;}
    flush({...ld,initialPrescription:newRx});
  };
  const upModality=(m)=>{
    const r=modalityRates(m);
    const newRx={...rx,modality:m};
    if(!r.dial) newRx.dialysateRate=0;
    if(!r.pbp)  newRx.pbpRate=0;
    if(!r.rep)  newRx.repRate=0;
    flush({...ld,initialPrescription:newRx});
  };

  const toggleCriterion=(type)=>{
    const exists=criteria.find(c=>c.type===type);
    if(type==="room1"&&!exists&&criteria.length>0){setRoom1Confirm(true);return;}
    const next=exists?criteria.filter(c=>c.type!==type):[...criteria,{type,params:{}}];
    const finalNext=type==="room1"&&!exists?[{type:"room1",params:{}}]:next;
    flush({...ld,successCriteria:finalNext});
  };
  const confirmRoom1=()=>{
    setRoom1Confirm(false);
    flush({...ld,successCriteria:[{type:"room1",params:{}}]});
  };
  const isOn=(type)=>!!criteria.find(c=>c.type===type);
  const isRoom1On=isOn("room1");

  // updateParam: for button toggles → flush immediately
  const updateParam=(type,k,v)=>{
    const next=criteria.map(c=>c.type===type?{...c,params:{...c.params,[k]:parseFloat(v)||v}}:c);
    flush({...ld,successCriteria:next});
  };
  // updateParamBlur: for typed number inputs → flush on blur only
  const updateParamBlur=(type,k,raw)=>{
    const v=parseFloat(raw)||raw;
    const next=criteria.map(c=>c.type===type?{...c,params:{...c.params,[k]:v}}:c);
    flush({...ld,successCriteria:next});
  };
  const getParam=(type,k,fallback="")=>{
    const c=criteria.find(c=>c.type===type);
    return c?.params?.[k]??fallback;
  };

  const upLogic=(v)=>{flush({...ld,criteriaLogic:v});setCriteriaLogic(v);};

  // ── Case-level option lists (from caseOptions, fall back to globals) ────────
  const caseOpts = ld.caseOptions || {};
  const myFilters   = caseFilters(caseOpts);      // [{key,label?,ecv,...}]
  const myAccess    = caseAccess(caseOpts);        // ["8F","9F",...]
  const myAnticoags = caseAnticoags(caseOpts);     // ["none","heparin",...]
  const myPrimes    = casePrimes(caseOpts);        // ["saline","albumin","blood"]
  const mySols      = caseSols(caseOpts);          // ["Prismasol 0",...]
  const myIntvs     = caseIntvs(caseOpts);         // [{id,label,type,...}]

  // Prescription dropdown solution list (filter by anticoag if one is chosen)
  const solsForRx   = (solutionByAnticoag[rx.anticoag]
    ? solutionByAnticoag[rx.anticoag].filter(s=>mySols.includes(s))
    : mySols
  ).map(s=>({key:s,label:solName(s,solMode)}));
  const sols=solsForRx; // keep alias for existing code
  const F=({l,hint,ch})=><div style={{marginBottom:"10px"}}>
    <label style={C.lbl}>{l}</label>
    {hint&&<div style={{fontSize:"11px",color:"#9ca3af",marginBottom:"4px"}}>{hint}</div>}
    {ch}
  </div>;
  const isBlankStart=!rx||Object.keys(rx).length===0;

  // Live clearance display
  const bsa=caseData.initialState?.bsa||0.9;
  const wt=caseData.initialState?.weightKg||25;
  const pbp=rx.pbpRate||0; const dial=rx.dialysateRate||0; const rep=rx.repRate||0;
  const netUf=rx.netUf||0;
  const clBSA=calcClearanceBSA(pbp,dial,rep,bsa);
  const clKg=calcClearanceKg(pbp,dial,rep,wt);
  const netUfKg=wt>0?(netUf/wt).toFixed(2):"—";

  // Param input helper for a selected criterion
  const ParamRow=({type,children})=>isOn(type)?<div style={{marginLeft:"28px",marginTop:"5px",marginBottom:"8px",padding:"10px 12px",background:"#fffbeb",borderRadius:"8px",border:"1px solid #fde68a"}}>{children}</div>:null;

  // Clearance threshold displayed in chosen unit
  const clParamLabel=clUnit==="bsa"?"Target clearance (L/1.73m²/hr)":"Target clearance (ml/kg/hr)";
  const clParamHint=clUnit==="bsa"?"Typical range 1.5 – 4.0":"Typical range 15 – 40 ml/kg/hr";

  // Rx completeness check (for modify mode assignment gate)
  const RX_REQUIRED_FIELDS=["modality","access","filter","prime","anticoag","solution","bfr"];
  const rxMissing=RX_REQUIRED_FIELDS.filter(f=>!rx[f]&&rx[f]!==0);
  const rxRatesOk=modality==="CVVHD"
    ?(rx.dialysateRate>0)
    :modality==="CVVH"
    ?((rx.pbpRate>0||rx.repRate>0))
    :((rx.pbpRate>0||rx.dialysateRate>0||rx.repRate>0));
  const rxComplete=rxMissing.length===0&&rxRatesOk;

  const customIntvItems=myIntvs.filter(iv=>!allInterventions.find(gi=>gi.id===iv.id)).map(iv=>{const lbl=iv.type==="numeric"?(iv.label+" - enter value ("+iv.min+"-"+iv.max+(iv.unit?" "+iv.unit:"")+")"):(iv.label);return {value:"intervention_"+iv.id,label:lbl,needsParam:iv.type==="numeric"?"numeric_intv":undefined,intvDef:iv};});
  const augmentedCriteriaGroups=SUCCESS_GROUPS.filter(grp=>prescMode!=="modify"||grp.group!=="Full Prescription from Blank").map(grp=>grp.group==="Clinical Actions (Non-Prescription)"?{...grp,items:[...grp.items,...customIntvItems]}:grp);

  return <div style={{fontSize:"13px"}}>

    {/* ── Prescribing mode selector — TOP of section ── */}
    <div style={{marginBottom:"18px",padding:"14px 16px",background:"#f5f3ff",borderRadius:"12px",border:"2px solid #ede9fe"}}>
      <div style={{fontSize:"11px",fontWeight:"700",color:C.pu,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:"10px"}}>Case Type — What is the trainee doing?</div>
      <div style={{display:"flex",gap:"10px",flexWrap:"wrap"}}>
        {[["modify","Modify an existing prescription","Trainee reviews a running prescription and corrects a problem. You must fill in all starting orders below."],
          ["new","Write a new prescription from scratch","Trainee fills in all orders from a blank slate (onboarding / initial setup)."]
        ].map(([val,title,desc])=>
          <button key={val} onClick={()=>{
            if(val==="new"&&criteria.length>0&&!isRoom1On){setRoom1Confirm(true);return;}
            if(val==="new") flush({...ld,prescribingMode:val,successCriteria:[{type:"room1",params:{}}]});
            else if(val==="modify") flush({...ld,prescribingMode:val,successCriteria:criteria.filter(c=>c.type!=="room1")});
            else flush({...ld,prescribingMode:val});
          }} style={{flex:"1 1 220px",padding:"11px 14px",borderRadius:"10px",border:`2px solid ${prescMode===val?C.pu:"#d1d5db"}`,background:prescMode===val?"#f5f3ff":"white",cursor:"pointer",textAlign:"left",fontFamily:"inherit",transition:"border-color 0.15s"}}>
            <div style={{fontWeight:"700",fontSize:"13px",color:prescMode===val?C.pu:"#374151",marginBottom:"3px"}}>{title}</div>
            <div style={{fontSize:"11px",color:"#9ca3af",lineHeight:"1.4"}}>{desc}</div>
          </button>
        )}
      </div>
      {!prescMode&&<p style={{fontSize:"11px",color:"#a78bfa",margin:"10px 0 0",fontStyle:"italic"}}>Select a case type above to continue.</p>}
    </div>

    {/* ── Starting prescription ── */}
    {prescMode&&<div style={{marginBottom:"20px"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"8px",flexWrap:"wrap",gap:"8px"}}>
        <div style={C.secHead}>
          {prescMode==="modify"?"Starting Prescription (what the trainee begins with — must be complete)":"Starting State (optional context for the trainee)"}
        </div>
        <div style={{display:"flex",alignItems:"center",gap:"8px",flexWrap:"wrap"}}>
          {[["filters","Filter names:","filterMode",setFilterMode,filterMode],
            ["solutions","Solutions:","solMode",setSolMode,solMode]].map(([id,lbl,,setter,val])=>
            <div key={id} style={{display:"flex",alignItems:"center",gap:"4px"}}>
              <span style={{fontSize:"11px",color:"#6b7280"}}>{lbl}</span>
              {[["generic","Generic"],["brand","Brand"]].map(([m,l])=>
                <button key={m} onClick={()=>setter(m)} style={{padding:"3px 9px",borderRadius:"6px",border:"none",cursor:"pointer",fontSize:"11px",fontWeight:"600",background:val===m?C.pu:"#e5e7eb",color:val===m?"white":"#374151",fontFamily:"inherit"}}>{l}</button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modality selector */}
      <div style={{marginBottom:"14px",padding:"12px 14px",background:"#f8f7ff",borderRadius:"10px",border:"1px solid #ede9fe"}}>
        <div style={{fontSize:"10px",fontWeight:"700",color:C.pu,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:"8px"}}>Modality</div>
        {isOnboarding&&<p style={{fontSize:"11px",color:"#9ca3af",marginBottom:"8px",marginTop:0}}>This is an onboarding case — any modality is acceptable. Leave blank or select a starting point for the trainee.</p>}
        <div style={{display:"flex",gap:"8px",flexWrap:"wrap"}}>
          {MODALITIES.map(m=><button key={m} onClick={()=>upModality(m===modality?"":m)} style={{padding:"7px 20px",borderRadius:"8px",border:`2px solid ${modality===m?C.pu:"#d1d5db"}`,background:modality===m?C.pu:"white",color:modality===m?"white":"#374151",fontSize:"13px",cursor:"pointer",fontWeight:"700",fontFamily:"inherit"}}>{m}</button>)}
          {modality&&<button onClick={()=>upModality("")} style={{padding:"7px 12px",borderRadius:"8px",border:"2px solid #fca5a5",background:"#fef2f2",color:"#dc2626",fontSize:"12px",cursor:"pointer",fontFamily:"inherit"}}>✕ Clear</button>}
        </div>
        {modality&&<div style={{marginTop:"8px",fontSize:"11px",color:"#6b7280"}}>
          {modality==="CVVH"&&"CVVH — replacement fluid only. Dialysate is locked to 0."}
          {modality==="CVVHD"&&"CVVHD — dialysate only. Pre- and post-filter replacement locked to 0."}
          {modality==="CVVHDF"&&"CVVHDF — all three rate channels available."}
        </div>}
      </div>

      {isBlankStart&&<div style={{padding:"10px 14px",background:"#f5f3ff",borderRadius:"8px",marginBottom:"12px",fontSize:"12px",color:"#6b7280"}}>
        This case starts blank — the trainee writes all orders from scratch. You can still set a starting prescription below.
      </div>}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px"}}>
        <F l="Access Catheter" ch={<select value={rx.access||""} onChange={e=>upRx("access",e.target.value)} style={C.sel}>
          <option value="">None / leave blank</option>
          {myAccess.map(k=><option key={k} value={k}>{accessDefs[k]?`${k} — ${accessDefs[k]}`:k}</option>)}</select>}/>
        <F l={`Filter ${filterMode==="brand"?"(brand)":"(generic)"}`} ch={<select value={rx.filter||""} onChange={e=>upRx("filter",e.target.value)} style={C.sel}>
          <option value="">None / leave blank</option>
          {myFilters.map(f=>{const fk=typeof f==="string"?f:f.key;return <option key={fk} value={fk}>{caseFilterLabel(f,filterMode)}</option>;})}
          </select>}/>
        <F l="Circuit Prime" ch={<select value={rx.prime||""} onChange={e=>upRx("prime",e.target.value)} style={C.sel}>
          <option value="">None / leave blank</option>
          {myPrimes.map(v=><option key={v} value={v}>{v}</option>)}</select>}/>
        <F l="Anticoagulation" ch={<select value={rx.anticoag||""} onChange={e=>upRx("anticoag",e.target.value)} style={C.sel}>
          <option value="">None / leave blank</option>
          {myAnticoags.map(v=><option key={v} value={v}>{v}</option>)}</select>}/>
        <F l={`Solution ${solMode==="brand"?"(brand name)":"(generic name)"}`} ch={<select value={rx.solution||""} onChange={e=>upRx("solution",e.target.value)} style={C.sel}>
          <option value="">None / leave blank</option>
          {sols.map(({key,label})=><option key={key} value={key}>{label} ({solutionLabels[key]})</option>)}</select>}/>
        <F l="Blood Flow Rate (ml/min)" ch={<input type="number" min="0" defaultValue={rx.bfr??""} onBlur={e=>upRxBlur("bfr",e.target.value)} style={C.inp}/>}/>
        <F l="Pre-blood Pump Rate (ml/hr)" hint={!rates.pbp?"Locked to 0 — not used in CVVHD":""} ch={<input type="number" min="0" defaultValue={rates.pbp?(rx.pbpRate??""):0} onBlur={e=>upRxBlur("pbpRate",e.target.value)} style={{...C.inp,background:rates.pbp?"white":"#f3f4f6",color:rates.pbp?"inherit":"#9ca3af"}}/>}/>
        <F l="Dialysate Rate (ml/hr)" hint={!rates.dial?"Locked to 0 — not used in CVVH":""} ch={<input type="number" min="0" defaultValue={rates.dial?(rx.dialysateRate??""):0} onBlur={e=>upRxBlur("dialysateRate",e.target.value)} style={{...C.inp,background:rates.dial?"white":"#f3f4f6",color:rates.dial?"inherit":"#9ca3af"}}/>}/>
        <F l="Post-Filter Replacement Rate (ml/hr)" hint={!rates.rep?"Locked to 0 — not used in CVVHD":""} ch={<input type="number" min="0" defaultValue={rates.rep?(rx.repRate??""):0} onBlur={e=>upRxBlur("repRate",e.target.value)} style={{...C.inp,background:rates.rep?"white":"#f3f4f6",color:rates.rep?"inherit":"#9ca3af"}}/>}/>
        <F l="Net Fluid Removal Rate — ml/hr (positive = fluid removed from patient)" ch={<input type="number" min="0" defaultValue={rx.netUf!=null?Math.abs(rx.netUf):""} onBlur={e=>upRxBlur("netUf",Math.abs(parseFloat(e.target.value)||0))} style={C.inp}/>}/>
        <F l="Sodium Additive (mmol)" ch={<input type="number" min="0" defaultValue={rx.Na_add??""} onBlur={e=>upRxBlur("Na_add",e.target.value)} style={C.inp}/>}/>
        <F l="Free Water Additive (mL)" ch={<input type="number" min="0" defaultValue={rx.freeWater??""} onBlur={e=>upRxBlur("freeWater",e.target.value)} style={C.inp}/>}/>
      </div>

      {/* Live clearance display with unit toggle */}
      <div style={{padding:"10px 14px",background:"#eef2ff",borderRadius:"8px",marginTop:"6px",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:"8px"}}>
        <div style={{fontSize:"13px",color:"#4338ca",fontWeight:"700"}}>
          📊 Starting prescription clearance: {clUnit==="bsa"?`${clBSA} L/1.73m²/hr`:`${clKg} ml/kg/hr`}
          <span style={{fontWeight:"400",fontSize:"12px",marginLeft:"14px",color:"#6366f1"}}>Net UF (fluid removed): {Math.abs(netUf)} ml/hr ({netUfKg} ml/kg/hr)</span>
        </div>
        <div style={{display:"flex",gap:"4px"}}>
          {[["bsa","L/1.73m²/hr"],["kg","ml/kg/hr"]].map(([u,l])=>
            <button key={u} onClick={()=>setClUnit(u)} style={{padding:"3px 10px",borderRadius:"6px",border:"none",cursor:"pointer",fontSize:"11px",fontWeight:"600",background:clUnit===u?"#4338ca":"#c7d2fe",color:clUnit===u?"white":"#4338ca",fontFamily:"inherit"}}>{l}</button>
          )}
        </div>
      </div>

      {/* Modify mode: completeness warning */}
      {prescMode==="modify"&&!rxComplete&&<div style={{marginTop:"10px",padding:"10px 14px",background:"#fef2f2",borderRadius:"8px",border:"1px solid #fecaca",fontSize:"12px",color:"#b91c1c",display:"flex",gap:"8px",alignItems:"flex-start"}}>
        <span style={{flexShrink:0}}>⚠️</span>
        <span>This case cannot be assigned until the starting prescription is complete. Missing: <strong>{rxMissing.join(", ")}{!rxRatesOk?" + at least one flow rate":""}</strong>.</span>
      </div>}
      {prescMode==="modify"&&rxComplete&&<div style={{marginTop:"10px",padding:"8px 14px",background:"#f0fdf4",borderRadius:"8px",border:"1px solid #bbf7d0",fontSize:"12px",color:"#065f46"}}>
        ✓ Starting prescription is complete and ready for assignment.
      </div>}
    </div>}

    {/* ── Criteria for Success ── */}
    <div style={{background:"#fefce8",border:"1px solid #fde68a",borderRadius:"12px",padding:"16px",position:"relative"}}>

      {/* Some built-in cases are scored by composite rules the checkbox form
          cannot represent (OR-logic corrective routes, calculated additive
          targets). Saying so explicitly is important: an empty form would
          otherwise read as "no criteria set", and re-saving would drop the rule. */}
      {uneditableNote && <div style={{marginBottom:"14px",padding:"11px 13px",borderRadius:"9px",
        background:"white",border:"1px solid #fcd34d"}}>
        <div style={{fontSize:"10px",fontWeight:"700",color:"#92400e",textTransform:"uppercase",
          letterSpacing:"0.08em",marginBottom:"5px"}}>Built-in scoring rule</div>
        <div style={{fontSize:"12.5px",color:"#78350f",lineHeight:"1.6"}}>
          {uneditableNote} This rule is not editable here. Selecting criteria below
          will replace it.
        </div>
      </div>}

      {/* Room1 confirmation popup */}
      {room1Confirm&&<div style={{position:"absolute",inset:0,background:"rgba(255,255,255,0.95)",borderRadius:"12px",zIndex:10,display:"flex",alignItems:"center",justifyContent:"center"}}>
        <div style={{maxWidth:"360px",textAlign:"center",padding:"24px"}}>
          <div style={{fontSize:"24px",marginBottom:"10px"}}>⚠️</div>
          <h3 style={{fontSize:"15px",fontWeight:"700",color:C.dark,marginBottom:"8px"}}>Clear other criteria?</h3>
          <p style={{fontSize:"13px",color:"#6b7280",marginBottom:"18px",lineHeight:"1.5"}}>Switching to "New prescription" mode will clear all previously selected criteria.</p>
          <div style={{display:"flex",gap:"10px",justifyContent:"center"}}>
            <button onClick={()=>setRoom1Confirm(false)} style={{...C.btn("#f3f4f6","#374151")}}>Cancel</button>
            <button onClick={confirmRoom1} style={{...C.btn("#dc2626")}}>Clear & Continue</button>
          </div>
        </div>
      </div>}

      <div style={{fontSize:"12px",fontWeight:"700",color:"#92400e",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:"12px"}}>✓ Success Criteria</div>

      {/* No mode chosen yet */}
      {!prescMode&&<div style={{padding:"14px",background:"white",borderRadius:"8px",border:"1px dashed #d1d5db",textAlign:"center",fontSize:"12px",color:"#9ca3af"}}>
        Select a case type above to configure success criteria.
      </div>}

      {/* ── MODIFY mode: individual criteria ── */}
      {prescMode==="modify"&&<>
        <div style={{display:"flex",justifyContent:"flex-end",alignItems:"center",gap:"10px",marginBottom:"12px",flexWrap:"wrap"}}>
          <div style={{display:"flex",alignItems:"center",gap:"4px"}}>
            <span style={{fontSize:"11px",color:"#92400e"}}>Solutions:</span>
            {[["generic","Generic"],["brand","Brand"]].map(([m,l])=>
              <button key={m} onClick={()=>setSolMode(m)} style={{padding:"3px 8px",borderRadius:"6px",border:"none",cursor:"pointer",fontSize:"11px",fontWeight:"600",background:solMode===m?"#d97706":"#fde68a",color:solMode===m?"white":"#92400e",fontFamily:"inherit"}}>{l}</button>
            )}
          </div>
          <div style={{display:"flex",alignItems:"center",gap:"4px"}}>
            <span style={{fontSize:"11px",color:"#92400e"}}>Clearance:</span>
            {[["bsa","L/1.73m²/hr"],["kg","ml/kg/hr"]].map(([u,l])=>
              <button key={u} onClick={()=>setClUnit(u)} style={{padding:"3px 8px",borderRadius:"6px",border:"none",cursor:"pointer",fontSize:"11px",fontWeight:"600",background:clUnit===u?"#d97706":"#fde68a",color:clUnit===u?"white":"#92400e",fontFamily:"inherit"}}>{l}</button>
            )}
          </div>
        </div>

        {/* Grouped checkboxes — hide "Full Prescription from Blank" in modify mode */}
      {augmentedCriteriaGroups.map(grp=>{
        return <div key={grp.group} style={{marginBottom:"14px"}}>
          <div style={{fontSize:"10px",fontWeight:"700",color:"#b45309",textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:"2px",paddingBottom:"3px",borderBottom:"1px solid #fde68a"}}>{grp.group}</div>
          {grp.hint&&<p style={{fontSize:"11px",color:"#a16207",fontStyle:"italic",margin:"0 0 8px",lineHeight:"1.45"}}>{grp.hint}</p>}
          {grp.items.map(item=>{
            const disabled=isRoom1On&&item.value!=="room1";
            return <div key={item.value}>
              <label style={{display:"flex",alignItems:"flex-start",gap:"9px",cursor:disabled?"not-allowed":"pointer",padding:"6px 8px",borderRadius:"7px",background:isOn(item.value)?"#fffbeb":"transparent",marginBottom:"1px",opacity:disabled?0.4:1}}>
                <input type="checkbox" checked={isOn(item.value)} onChange={()=>!disabled&&toggleCriterion(item.value)} disabled={disabled} style={{marginTop:"2px",flexShrink:0,accentColor:C.pu}}/>
                <span style={{fontSize:"13px",color:C.dark,lineHeight:"1.4"}}>{item.label}</span>
              </label>

              {/* ── CLEARANCE param ── */}
              {item.needsParam==="clearance"&&isOn(item.value)&&<div style={{marginLeft:"28px",marginTop:"4px",marginBottom:"6px",padding:"10px 12px",background:"#fffbeb",borderRadius:"8px",border:"1px solid #fde68a"}}>
                <label style={C.lbl}>{clUnit==="bsa"?"Target clearance (L/1.73m²/hr)":"Target clearance (ml/kg/hr)"}</label>
                <div style={{fontSize:"11px",color:"#9ca3af",marginBottom:"6px"}}>{clUnit==="bsa"?"Typical range 1.5–4.0":"Typical range 15–40 ml/kg/hr"}</div>
                <div style={{display:"flex",gap:"6px",alignItems:"center",flexWrap:"wrap"}}>
                  <div style={{display:"flex",gap:"3px"}}>
                    {[["gte","≥"],["gt",">"],["lte","≤"],["lt","<"]].map(([op,sym])=>{
                      const cur=getParam(item.value,"op")||(item.value==="clearance_gte"?"gte":"lte");
                      return <button key={op} onClick={()=>updateParam(item.value,"op",op)} style={{padding:"4px 10px",borderRadius:"6px",border:`1px solid ${cur===op?C.pu:"#d1d5db"}`,background:cur===op?C.pu:"white",color:cur===op?"white":"#374151",fontSize:"14px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit",minWidth:"34px"}}>{sym}</button>;
                    })}
                  </div>
                  <input type="number" step="0.1" placeholder={clUnit==="bsa"?"e.g. 1.9":"e.g. 20"} defaultValue={getParam(item.value,"threshold")} onBlur={e=>updateParamBlur(item.value,"threshold",e.target.value)} style={{...C.inp,maxWidth:"140px"}}/>
                  <span style={{fontSize:"11px",color:"#9ca3af"}}>{clUnit==="bsa"?"L/1.73m²/hr":"ml/kg/hr"}</span>
                </div>
              </div>}

              {/* ── FILTRATION FRACTION CEILING ── */}
              {item.needsParam==="ff"&&isOn(item.value)&&<div style={{marginLeft:"28px",marginTop:"4px",marginBottom:"6px",padding:"10px 12px",background:"#fffbeb",borderRadius:"8px",border:"1px solid #fde68a"}}>
                <label style={C.lbl}>Maximum filtration fraction</label>
                <div style={{fontSize:"11px",color:"#9ca3af",marginBottom:"7px",lineHeight:"1.5"}}>
                  Fluid crossing the membrane (post-filter replacement + net UF + PBP)
                  divided by plasma flow. Pre-dilution counts on both sides, so moving
                  replacement to the PBP line lowers it. Keep at or below 20–25%.
                </div>
                <div style={{display:"flex",gap:"6px",alignItems:"center",marginBottom:"9px"}}>
                  <span style={{fontSize:"14px",fontWeight:"700",color:"#374151"}}>≤</span>
                  <input type="number" step="1" placeholder="e.g. 20"
                    defaultValue={getParam(item.value,"maxFf")}
                    onBlur={e=>updateParamBlur(item.value,"maxFf",e.target.value)}
                    style={{...C.inp,maxWidth:"120px"}}/>
                  <span style={{fontSize:"11px",color:"#9ca3af"}}>%</span>
                </div>
                <label style={C.lbl}>Minimum net fluid removal (optional)</label>
                <div style={{fontSize:"11px",color:"#9ca3af",marginBottom:"6px",lineHeight:"1.5"}}>
                  Set this above zero when the patient is fluid overloaded, so that
                  simply switching fluid removal off cannot pass the case.
                </div>
                <div style={{display:"flex",gap:"6px",alignItems:"center"}}>
                  <span style={{fontSize:"14px",fontWeight:"700",color:"#374151"}}>≥</span>
                  <input type="number" step="0.1" placeholder="e.g. 0.5"
                    defaultValue={getParam(item.value,"minNetUfKgHr")}
                    onBlur={e=>updateParamBlur(item.value,"minNetUfKgHr",e.target.value)}
                    style={{...C.inp,maxWidth:"120px"}}/>
                  <span style={{fontSize:"11px",color:"#9ca3af"}}>ml/kg/hr</span>
                </div>
              </div>}

              {/* ── EFFLUENT DOSE (ml/kg/hr, includes net UF) ── */}
              {item.needsParam==="effluent_kg"&&isOn(item.value)&&<div style={{marginLeft:"28px",marginTop:"4px",marginBottom:"6px",padding:"10px 12px",background:"#fffbeb",borderRadius:"8px",border:"1px solid #fde68a"}}>
                <label style={C.lbl}>Minimum effluent dose (ml/kg/hr)</label>
                <div style={{fontSize:"11px",color:"#9ca3af",marginBottom:"6px",lineHeight:"1.5"}}>
                  Total of pre-blood pump, dialysate, post-filter replacement and net ultrafiltration,
                  divided by weight. Standard starting doses are around 20–30 ml/kg/hr.
                </div>
                <div style={{display:"flex",gap:"6px",alignItems:"center"}}>
                  <span style={{fontSize:"14px",fontWeight:"700",color:"#374151"}}>≥</span>
                  <input type="number" step="1" placeholder="e.g. 25"
                    defaultValue={getParam(item.value,"threshold")}
                    onBlur={e=>updateParamBlur(item.value,"threshold",e.target.value)}
                    style={{...C.inp,maxWidth:"140px"}}/>
                  <span style={{fontSize:"11px",color:"#9ca3af"}}>ml/kg/hr</span>
                </div>
              </div>}

              {/* ── EFFLUENT DOSE, LOWER OF TWO TARGETS ── */}
              {item.needsParam==="effluent_pair"&&isOn(item.value)&&<div style={{marginLeft:"28px",marginTop:"4px",marginBottom:"6px",padding:"10px 12px",background:"#fffbeb",borderRadius:"8px",border:"1px solid #fde68a"}}>
                <label style={C.lbl}>Targets — the trainee passes on whichever is lower for this patient</label>
                <div style={{fontSize:"11px",color:"#9ca3af",marginBottom:"8px",lineHeight:"1.5"}}>
                  Weight-based and BSA-based dosing diverge sharply at very small sizes.
                  Taking the lower of the two avoids setting a target that is unreachable
                  for a neonate while still being demanding for a larger child.
                </div>
                <div style={{display:"flex",gap:"12px",flexWrap:"wrap"}}>
                  <div style={{display:"flex",gap:"6px",alignItems:"center"}}>
                    <span style={{fontSize:"14px",fontWeight:"700",color:"#374151"}}>≥</span>
                    <input type="number" step="1" placeholder="e.g. 90"
                      defaultValue={getParam(item.value,"mlKgHr")}
                      onBlur={e=>updateParamBlur(item.value,"mlKgHr",e.target.value)}
                      style={{...C.inp,maxWidth:"110px"}}/>
                    <span style={{fontSize:"11px",color:"#9ca3af"}}>ml/kg/hr</span>
                  </div>
                  <div style={{display:"flex",gap:"6px",alignItems:"center"}}>
                    <span style={{fontSize:"12px",color:"#9ca3af"}}>or</span>
                    <span style={{fontSize:"14px",fontWeight:"700",color:"#374151"}}>≥</span>
                    <input type="number" step="0.1" placeholder="e.g. 6"
                      defaultValue={getParam(item.value,"lBsaHr")}
                      onBlur={e=>updateParamBlur(item.value,"lBsaHr",e.target.value)}
                      style={{...C.inp,maxWidth:"110px"}}/>
                    <span style={{fontSize:"11px",color:"#9ca3af"}}>L/1.73m²/hr</span>
                  </div>
                </div>
              </div>}

              {/* ── NET UF LTE param ── */}
              {item.needsParam==="netuf_lte"&&isOn(item.value)&&<div style={{marginLeft:"28px",marginTop:"4px",marginBottom:"6px",padding:"10px 12px",background:"#fffbeb",borderRadius:"8px",border:"1px solid #fde68a"}}>
                <label style={C.lbl}>Fluid removal threshold (ml/kg/hr)</label>
                <div style={{display:"flex",gap:"6px",alignItems:"center",flexWrap:"wrap",marginTop:"4px"}}>
                  <div style={{display:"flex",gap:"3px"}}>
                    {[["lte","≤"],["lt","<"],["gte","≥"],["gt",">"]].map(([op,sym])=>{
                      const cur=getParam(item.value,"op")||"lte";
                      return <button key={op} onClick={()=>updateParam(item.value,"op",op)} style={{padding:"4px 10px",borderRadius:"6px",border:`1px solid ${cur===op?C.pu:"#d1d5db"}`,background:cur===op?C.pu:"white",color:cur===op?"white":"#374151",fontSize:"14px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit",minWidth:"34px"}}>{sym}</button>;
                    })}
                  </div>
                  <input type="number" step="0.1" min="0" defaultValue={getParam(item.value,"threshold")} onBlur={e=>updateParamBlur(item.value,"threshold",e.target.value)} style={{...C.inp,maxWidth:"120px"}}/>
                  <span style={{fontSize:"11px",color:"#9ca3af"}}>ml/kg/hr</span>
                </div>
              </div>}

              {/* ── NET UF GTE param ── */}
              {item.needsParam==="netuf_gte"&&isOn(item.value)&&<div style={{marginLeft:"28px",marginTop:"4px",marginBottom:"6px",padding:"10px 12px",background:"#fffbeb",borderRadius:"8px",border:"1px solid #fde68a"}}>
                <label style={C.lbl}>Fluid removal threshold (ml/kg/hr)</label>
                <div style={{display:"flex",gap:"6px",alignItems:"center",flexWrap:"wrap",marginTop:"4px"}}>
                  <div style={{display:"flex",gap:"3px"}}>
                    {[["gte","≥"],["gt",">"],["lte","≤"],["lt","<"]].map(([op,sym])=>{
                      const cur=getParam(item.value,"op")||"gte";
                      return <button key={op} onClick={()=>updateParam(item.value,"op",op)} style={{padding:"4px 10px",borderRadius:"6px",border:`1px solid ${cur===op?C.pu:"#d1d5db"}`,background:cur===op?C.pu:"white",color:cur===op?"white":"#374151",fontSize:"14px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit",minWidth:"34px"}}>{sym}</button>;
                    })}
                  </div>
                  <input type="number" step="0.1" min="0" defaultValue={getParam(item.value,"threshold")} onBlur={e=>updateParamBlur(item.value,"threshold",e.target.value)} style={{...C.inp,maxWidth:"120px"}}/>
                  <span style={{fontSize:"11px",color:"#9ca3af"}}>ml/kg/hr</span>
                </div>
              </div>}

              {/* ── BFR param ── */}
              {item.needsParam==="bfr"&&isOn(item.value)&&<div style={{marginLeft:"28px",marginTop:"4px",marginBottom:"6px",padding:"10px 12px",background:"#fffbeb",borderRadius:"8px",border:"1px solid #fde68a"}}>
                <label style={C.lbl}>Blood flow rate threshold (ml/min)</label>
                <div style={{display:"flex",gap:"6px",alignItems:"center",flexWrap:"wrap",marginTop:"4px"}}>
                  <div style={{display:"flex",gap:"3px"}}>
                    {(item.value==="bfr_lte"?[["lte","≤"],["lt","<"],["gte","≥"],["gt",">"]]:
                                              [["gte","≥"],["gt",">"],["lte","≤"],["lt","<"]]).map(([op,sym])=>{
                      const cur=getParam(item.value,"op")||(item.value==="bfr_lte"?"lte":"gte");
                      return <button key={op} onClick={()=>updateParam(item.value,"op",op)} style={{padding:"4px 10px",borderRadius:"6px",border:`1px solid ${cur===op?C.pu:"#d1d5db"}`,background:cur===op?C.pu:"white",color:cur===op?"white":"#374151",fontSize:"14px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit",minWidth:"34px"}}>{sym}</button>;
                    })}
                  </div>
                  <input type="number" defaultValue={getParam(item.value,"threshold")} onBlur={e=>updateParamBlur(item.value,"threshold",e.target.value)} style={{...C.inp,maxWidth:"120px"}}/>
                  <span style={{fontSize:"11px",color:"#9ca3af"}}>ml/min</span>
                </div>
              </div>}


              {/* ── ANTICOAG MULTI (acceptable options) ── */}
              {item.needsParam==="anticoag_multi"&&isOn(item.value)&&<div style={{marginLeft:"28px",marginTop:"4px",marginBottom:"6px",padding:"10px 12px",background:"#fffbeb",borderRadius:"8px",border:"1px solid #fde68a"}}>
                <label style={C.lbl}>Tick all acceptable anticoagulants</label>
                <div style={{display:"flex",gap:"6px",flexWrap:"wrap",marginTop:"6px"}}>
                  {myAnticoags.map(v=>{
                    const sel=(getParam(item.value,"values")||"").split(",").filter(Boolean);
                    const on=sel.includes(v);
                    const tog=()=>{const n=on?sel.filter(x=>x!==v):[...sel,v];updateParam(item.value,"values",n.join(","));};
                    return <button key={v} onClick={tog} style={{padding:"5px 14px",borderRadius:"8px",border:`2px solid ${on?C.pu:"#d1d5db"}`,background:on?C.pu:"white",color:on?"white":"#374151",fontSize:"12px",cursor:"pointer",fontWeight:"600",fontFamily:"inherit"}}>{v}</button>;
                  })}
                </div>
              </div>}


              {/* ── SOLUTION MULTI (acceptable options) ── */}
              {item.needsParam==="solution_multi"&&isOn(item.value)&&<div style={{marginLeft:"28px",marginTop:"4px",marginBottom:"6px",padding:"10px 12px",background:"#fffbeb",borderRadius:"8px",border:"1px solid #fde68a"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:"6px",marginBottom:"6px"}}>
                  <label style={C.lbl}>Tick all acceptable solutions ({solMode==="generic"?"generic names":"brand names"})</label>
                  <div style={{display:"flex",gap:"3px"}}>
                    {[["generic","Generic"],["brand","Brand"]].map(([m,l])=>
                      <button key={m} onClick={()=>setSolMode(m)} style={{padding:"2px 8px",borderRadius:"5px",border:"none",cursor:"pointer",fontSize:"10px",fontWeight:"700",background:solMode===m?"#d97706":"#fde68a",color:solMode===m?"white":"#92400e",fontFamily:"inherit"}}>{l}</button>
                    )}
                  </div>
                </div>
                <div style={{display:"flex",gap:"6px",flexWrap:"wrap"}}>
                  {mySols.map(s=>{
                    const sel=(getParam(item.value,"values")||"").split(",").filter(Boolean);
                    const on=sel.includes(s);
                    const tog=()=>{const n=on?sel.filter(x=>x!==s):[...sel,s];updateParam(item.value,"values",n.join(","));};
                    return <button key={s} onClick={tog} style={{padding:"5px 12px",borderRadius:"8px",border:`2px solid ${on?C.pu:"#d1d5db"}`,background:on?C.pu:"white",color:on?"white":"#374151",fontSize:"12px",cursor:"pointer",fontWeight:"600",fontFamily:"inherit"}}>
                      <div style={{fontWeight:"700"}}>{solName(s,solMode)}</div>
                      <div style={{fontSize:"10px",fontWeight:"400",color:on?"rgba(255,255,255,0.8)":"#9ca3af",marginTop:"1px"}}>{solutionLabels[s]}</div>
                    </button>;
                  })}
                </div>
              </div>}


              {/* ── ACCESS MULTI (acceptable options) ── */}
              {item.needsParam==="access_multi"&&isOn(item.value)&&<div style={{marginLeft:"28px",marginTop:"4px",marginBottom:"6px",padding:"10px 12px",background:"#fffbeb",borderRadius:"8px",border:"1px solid #fde68a"}}>
                <label style={C.lbl}>Tick all acceptable catheter sizes</label>
                <div style={{display:"flex",gap:"6px",flexWrap:"wrap",marginTop:"6px"}}>
                  {myAccess.map(k=>{
                    const sel=(getParam(item.value,"values")||"").split(",").filter(Boolean);
                    const on=sel.includes(k);
                    const tog=()=>{const n=on?sel.filter(x=>x!==k):[...sel,k];updateParam(item.value,"values",n.join(","));};
                    return <button key={k} onClick={tog} style={{padding:"5px 14px",borderRadius:"8px",border:`2px solid ${on?C.pu:"#d1d5db"}`,background:on?C.pu:"white",color:on?"white":"#374151",fontSize:"12px",cursor:"pointer",fontWeight:"600",fontFamily:"inherit"}}>{k}</button>;
                  })}
                </div>
              </div>}


              {/* ── FILTER MULTI (acceptable options) ── */}
              {item.needsParam==="filter_multi"&&isOn(item.value)&&<div style={{marginLeft:"28px",marginTop:"4px",marginBottom:"6px",padding:"10px 12px",background:"#fffbeb",borderRadius:"8px",border:"1px solid #fde68a"}}>
                <label style={C.lbl}>Tick all acceptable filters</label>
                <div style={{display:"flex",gap:"6px",flexWrap:"wrap",marginTop:"6px"}}>
                  {myFilters.map(f=>{const fk=typeof f==="string"?f:f.key;
                    const sel=(getParam(item.value,"values")||"").split(",").filter(Boolean);
                    const on=sel.includes(fk);
                    const tog=()=>{const n=on?sel.filter(x=>x!==fk):[...sel,fk];updateParam(item.value,"values",n.join(","));};
                    return <button key={fk} onClick={tog} style={{padding:"5px 12px",borderRadius:"8px",border:`2px solid ${on?C.pu:"#d1d5db"}`,background:on?C.pu:"white",color:on?"white":"#374151",fontSize:"12px",cursor:"pointer",fontWeight:"600",fontFamily:"inherit"}}>{caseFilterLabel(f,filterMode)}</button>;
                  })}
                </div>
              </div>}

              {/* ── PRIME MULTI param ── */}
              {item.needsParam==="prime_multi"&&isOn(item.value)&&<div style={{marginLeft:"28px",marginTop:"4px",marginBottom:"6px",padding:"10px 12px",background:"#fffbeb",borderRadius:"8px",border:"1px solid #fde68a"}}>
                <label style={C.lbl}>Acceptable priming solution(s) — tick all that are correct</label>
                <div style={{display:"flex",gap:"6px",flexWrap:"wrap",marginTop:"6px"}}>
                  {myPrimes.map(p=>{
                    const sel=(getParam(item.value,"values")||"").split(",").filter(Boolean);
                    const on=sel.includes(p);
                    const tog=()=>{const n=on?sel.filter(x=>x!==p):[...sel,p];updateParam(item.value,"values",n.join(","));};
                    return <button key={p} onClick={tog} style={{padding:"5px 16px",borderRadius:"8px",border:`2px solid ${on?C.pu:"#d1d5db"}`,background:on?C.pu:"white",color:on?"white":"#374151",fontSize:"13px",cursor:"pointer",fontWeight:"600",fontFamily:"inherit"}}>{p}</button>;
                  })}
                </div>
              </div>}

              {/* ── MODALITY MULTI param ── */}
              {item.needsParam==="modality_multi"&&isOn(item.value)&&<div style={{marginLeft:"28px",marginTop:"4px",marginBottom:"6px",padding:"10px 12px",background:"#fffbeb",borderRadius:"8px",border:"1px solid #fde68a"}}>
                <label style={C.lbl}>Acceptable modality/modalities — tick all that are correct</label>
                <div style={{display:"flex",gap:"6px",flexWrap:"wrap",marginTop:"6px"}}>
                  {MODALITIES.map(m=>{
                    const sel=(getParam(item.value,"values")||"").split(",").filter(Boolean);
                    const on=sel.includes(m);
                    const tog=()=>{const n=on?sel.filter(x=>x!==m):[...sel,m];updateParam(item.value,"values",n.join(","));};
                    return <button key={m} onClick={tog} style={{padding:"5px 16px",borderRadius:"8px",border:`2px solid ${on?C.pu:"#d1d5db"}`,background:on?C.pu:"white",color:on?"white":"#374151",fontSize:"13px",cursor:"pointer",fontWeight:"700",fontFamily:"inherit"}}>{m}</button>;
                  })}
                </div>
              </div>}

              {/* ── MODALITY CHANGE (old single-select, kept for legacy) ── */}
              {item.needsParam==="modality"&&isOn(item.value)&&<div style={{marginLeft:"28px",marginTop:"4px",marginBottom:"6px",padding:"10px 12px",background:"#fffbeb",borderRadius:"8px",border:"1px solid #fde68a"}}>
                <label style={C.lbl}>Trainee must change modality to:</label>
                <div style={{display:"flex",gap:"6px",marginTop:"4px"}}>
                  {MODALITIES.map(m=>{
                    const sel=getParam(item.value,"value");
                    const on=sel===m;
                    return <button key={m} onClick={()=>updateParam(item.value,"value",m)} style={{padding:"5px 16px",borderRadius:"8px",border:`2px solid ${on?C.pu:"#d1d5db"}`,background:on?C.pu:"white",color:on?"white":"#374151",fontSize:"13px",cursor:"pointer",fontWeight:"700",fontFamily:"inherit"}}>{m}</button>;
                  })}
                </div>
              </div>}

              {/* ── ELECTROLYTE RANGE param ── */}
              {item.needsParam==="elec_range"&&isOn(item.value)&&<div style={{marginLeft:"28px",marginTop:"4px",marginBottom:"6px",padding:"10px 12px",background:"#fffbeb",borderRadius:"8px",border:"1px solid #fde68a"}}>
                <label style={C.lbl}>{item.value==="k_add"?"Acceptable potassium additive range (mmol/L)":"Acceptable phosphate additive range (mmol/L)"}</label>
                <div style={{display:"flex",gap:"8px",alignItems:"center",marginTop:"4px"}}>
                  <input type="number" step="0.5" min="0" defaultValue={getParam(item.value,"min")} onBlur={e=>updateParamBlur(item.value,"min",e.target.value)} style={{...C.inp,flex:1,maxWidth:"120px"}}/>
                  <span style={{color:"#9ca3af",fontSize:"12px"}}>to</span>
                  <input type="number" step="0.5" min="0" defaultValue={getParam(item.value,"max")} onBlur={e=>updateParamBlur(item.value,"max",e.target.value)} style={{...C.inp,flex:1,maxWidth:"120px"}}/>
                  <span style={{fontSize:"11px",color:"#9ca3af"}}>mmol/L</span>
                </div>
              </div>}

              {/* ── NA TARGET param ── */}
              {item.needsParam==="na_target"&&isOn(item.value)&&<div style={{marginLeft:"28px",marginTop:"4px",marginBottom:"6px",padding:"10px 12px",background:"#fffbeb",borderRadius:"8px",border:"1px solid #fde68a"}}>
                <label style={C.lbl}>Target sodium concentration (mmol/L)</label>
                <div style={{fontSize:"11px",color:"#9ca3af",marginBottom:"4px"}}>The trainee must add enough to bring the fluid sodium close to this value.</div>
                <input type="number" defaultValue={getParam(item.value,"target")} onBlur={e=>updateParamBlur(item.value,"target",e.target.value)} style={{...C.inp,maxWidth:"180px"}}/>
              </div>}

              {/* ── NUMERIC INTERVENTION param (custom interventions) ── */}
              {item.needsParam==="numeric_intv"&&isOn(item.value)&&<div style={{marginLeft:"28px",marginTop:"4px",marginBottom:"6px",padding:"10px 12px",background:"#fffbeb",borderRadius:"8px",border:"1px solid #fde68a"}}>
                <label style={C.lbl}>{item.intvDef?.label} — acceptable value</label>
                <div style={{display:"flex",gap:"6px",alignItems:"center",flexWrap:"wrap",marginTop:"4px"}}>
                  <input type="number" step="any" min={item.intvDef?.min} max={item.intvDef?.max}
                    defaultValue={getParam(item.value,"value")}
                    onBlur={e=>updateParamBlur(item.value,"value",e.target.value)}
                    style={{...C.inp,maxWidth:"120px"}}/>
                  {item.intvDef?.unit&&<span style={{fontSize:"11px",color:"#9ca3af"}}>{item.intvDef.unit}</span>}
                  <span style={{fontSize:"11px",color:"#9ca3af"}}>
                    (range: {item.intvDef?.min}–{item.intvDef?.max}{item.intvDef?.unit?" "+item.intvDef.unit:""})
                  </span>
                </div>
              </div>}

              {/* ── ROOM1 FULL param ── */}
              {item.needsParam==="room1_full"&&isOn(item.value)&&<div style={{marginLeft:"28px",marginTop:"6px",marginBottom:"8px",padding:"14px",background:"#fffbeb",borderRadius:"8px",border:"1px solid #fde68a"}}>
                <div style={{fontSize:"11px",fontWeight:"700",color:"#92400e",marginBottom:"12px"}}>Set acceptable ranges for each field — the trainee must land within these to pass.</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px"}}>
                  <div style={{gridColumn:"1/-1"}}>
                    <label style={C.lbl}>Acceptable modalities (tick all that pass)</label>
                    <div style={{display:"flex",gap:"6px",flexWrap:"wrap",marginTop:"4px"}}>
                      {MODALITIES.map(m=>{
                        const sel=(getParam("room1","modality_ok")||"").split(",").filter(Boolean);
                        const on=sel.includes(m);
                        const tog=()=>{const n=on?sel.filter(x=>x!==m):[...sel,m];updateParam("room1","modality_ok",n.join(","));};
                        return <button key={m} onClick={tog} style={{padding:"5px 16px",borderRadius:"8px",border:`2px solid ${on?C.pu:"#d1d5db"}`,background:on?C.pu:"white",color:on?"white":"#374151",fontSize:"13px",cursor:"pointer",fontWeight:"700",fontFamily:"inherit"}}>{m}</button>;
                      })}
                    </div>
                  </div>
                  <div style={{gridColumn:"1/-1"}}>
                    <label style={C.lbl}>Clearance ({clUnit==="bsa"?"L/1.73m²/hr":"ml/kg/hr"})</label>
                    <div style={{display:"flex",gap:"8px",alignItems:"center"}}>
                      <input type="number" step="0.1" placeholder={clUnit==="bsa"?"min e.g. 1.9":"min e.g. 18"} defaultValue={getParam("room1","cl_min")} onBlur={e=>updateParamBlur("room1","cl_min",e.target.value)} style={{...C.inp,flex:1}}/>
                      <span style={{color:"#9ca3af",fontSize:"12px"}}>to</span>
                      <input type="number" step="0.1" placeholder={clUnit==="bsa"?"max e.g. 3.0":"max e.g. 32"} defaultValue={getParam("room1","cl_max")} onBlur={e=>updateParamBlur("room1","cl_max",e.target.value)} style={{...C.inp,flex:1}}/>
                    </div>
                  </div>
                  <div style={{gridColumn:"1/-1"}}>
                    <label style={C.lbl}>Net fluid removal rate — positive = fluid removed (ml/kg/hr)</label>
                    <div style={{display:"flex",gap:"8px",alignItems:"center"}}>
                      <input type="number" step="0.1" min="0" defaultValue={getParam("room1","uf_min")} onBlur={e=>updateParamBlur("room1","uf_min",Math.abs(parseFloat(e.target.value)||0))} style={{...C.inp,flex:1}}/>
                      <span style={{color:"#9ca3af",fontSize:"12px"}}>to</span>
                      <input type="number" step="0.1" min="0" defaultValue={getParam("room1","uf_max")} onBlur={e=>updateParamBlur("room1","uf_max",Math.abs(parseFloat(e.target.value)||0))} style={{...C.inp,flex:1}}/>
                    </div>
                  </div>
                  <div style={{gridColumn:"1/-1"}}>
                    <label style={C.lbl}>Acceptable access catheter sizes (tick all that pass)</label>
                    <div style={{display:"flex",gap:"6px",flexWrap:"wrap",marginTop:"4px"}}>
                      {Object.keys(accessDefs).map(sz=>{
                        const sel=(getParam("room1","access_ok")||"").split(",").filter(Boolean);
                        const on=sel.includes(sz);
                        const tog=()=>{const n=on?sel.filter(x=>x!==sz):[...sel,sz];updateParam("room1","access_ok",n.join(","));};
                        return <button key={sz} onClick={tog} style={{padding:"4px 12px",borderRadius:"16px",border:`1px solid ${on?C.pu:"#d1d5db"}`,background:on?C.pu:"white",color:on?"white":"#374151",fontSize:"12px",cursor:"pointer",fontWeight:"600",fontFamily:"inherit"}}>{sz}</button>;
                      })}
                    </div>
                  </div>
                  <div style={{gridColumn:"1/-1"}}>
                    <label style={C.lbl}>Acceptable filters (tick all that pass)</label>
                    <div style={{display:"flex",gap:"6px",flexWrap:"wrap",marginTop:"4px"}}>
                      {Object.keys(filterDefs).map(f=>{
                        const sel=(getParam("room1","filter_ok")||"").split(",").filter(Boolean);
                        const on=sel.includes(f);
                        const tog=()=>{const n=on?sel.filter(x=>x!==f):[...sel,f];updateParam("room1","filter_ok",n.join(","));};
                        return <button key={f} onClick={tog} style={{padding:"4px 12px",borderRadius:"16px",border:`1px solid ${on?C.pu:"#d1d5db"}`,background:on?C.pu:"white",color:on?"white":"#374151",fontSize:"12px",cursor:"pointer",fontWeight:"600",fontFamily:"inherit"}}>{filterLabel(f,filterMode)}</button>;
                      })}
                    </div>
                  </div>
                  <div>
                    <label style={C.lbl}>Acceptable prime types</label>
                    <div style={{display:"flex",gap:"6px",flexWrap:"wrap",marginTop:"4px"}}>
                      {["saline","albumin","blood"].map(p=>{
                        const sel=(getParam("room1","prime_ok")||"").split(",").filter(Boolean);
                        const on=sel.includes(p);
                        const tog=()=>{const n=on?sel.filter(x=>x!==p):[...sel,p];updateParam("room1","prime_ok",n.join(","));};
                        return <button key={p} onClick={tog} style={{padding:"4px 12px",borderRadius:"16px",border:`1px solid ${on?C.pu:"#d1d5db"}`,background:on?C.pu:"white",color:on?"white":"#374151",fontSize:"12px",cursor:"pointer",fontWeight:"600",fontFamily:"inherit"}}>{p}</button>;
                      })}
                    </div>
                  </div>
                  <div>
                    <label style={C.lbl}>Acceptable anticoagulation</label>
                    <div style={{display:"flex",gap:"6px",flexWrap:"wrap",marginTop:"4px"}}>
                      {["none","heparin","citrate","epoprostenol"].map(a=>{
                        const sel=(getParam("room1","anticoag_ok")||"").split(",").filter(Boolean);
                        const on=sel.includes(a);
                        const tog=()=>{const n=on?sel.filter(x=>x!==a):[...sel,a];updateParam("room1","anticoag_ok",n.join(","));};
                        return <button key={a} onClick={tog} style={{padding:"4px 12px",borderRadius:"16px",border:`1px solid ${on?C.pu:"#d1d5db"}`,background:on?C.pu:"white",color:on?"white":"#374151",fontSize:"12px",cursor:"pointer",fontWeight:"600",fontFamily:"inherit"}}>{a}</button>;
                      })}
                    </div>
                  </div>
                  <div style={{gridColumn:"1/-1"}}>
                    <label style={C.lbl}>Acceptable solutions</label>
                    <div style={{display:"flex",gap:"6px",flexWrap:"wrap",marginTop:"4px"}}>
                      {ALL_SOLUTIONS.map(s=>{
                        const sel=(getParam("room1","solution_ok")||"").split(",").filter(Boolean);
                        const on=sel.includes(s);
                        const tog=()=>{const n=on?sel.filter(x=>x!==s):[...sel,s];updateParam("room1","solution_ok",n.join(","));};
                        return <button key={s} onClick={tog} style={{padding:"4px 12px",borderRadius:"16px",border:`1px solid ${on?C.pu:"#d1d5db"}`,background:on?C.pu:"white",color:on?"white":"#374151",fontSize:"12px",cursor:"pointer",fontWeight:"600",fontFamily:"inherit"}}>{solName(s,solMode)}</button>;
                      })}
                    </div>
                  </div>
                  <div>
                    <label style={C.lbl}>Blood flow rate range (ml/min)</label>
                    <div style={{display:"flex",gap:"8px",alignItems:"center"}}>
                      <input type="number" defaultValue={getParam("room1","bfr_min")} onBlur={e=>updateParamBlur("room1","bfr_min",e.target.value)} style={{...C.inp,flex:1}}/>
                      <span style={{color:"#9ca3af",fontSize:"12px"}}>to</span>
                      <input type="number" defaultValue={getParam("room1","bfr_max")} onBlur={e=>updateParamBlur("room1","bfr_max",e.target.value)} style={{...C.inp,flex:1}}/>
                    </div>
                  </div>
                </div>
              </div>}
            </div>;
          })}
        </div>;
      })}

        {/* CRITERIA SUMMARY — per-criterion AND/OR toggle (modify mode) */}
        {criteria.length>0&&<div style={{marginTop:"16px",padding:"12px 14px",background:"white",borderRadius:"10px",border:"1px solid #fde68a"}}>
          <div style={{fontSize:"10px",fontWeight:"700",color:"#92400e",marginBottom:"10px",textTransform:"uppercase",letterSpacing:"0.05em"}}>Selected criteria — set AND / OR between each pair</div>
          {criteria.map((c,idx)=>{
            const item=SUCCESS_FLAT.find(i=>i.value===c.type);
            const op=getParam(c.type,"op");
            const opSym=op==="gte"?"≥":op==="gt"?">":op==="lte"?"≤":op==="lt"?"<":"";
            const thresh=getParam(c.type,"threshold");
            const detail=thresh?` ${opSym} ${thresh}`:"";
            return <div key={c.type}>
              <div style={{display:"flex",alignItems:"center",gap:"8px",padding:"6px 0"}}>
                <div style={{width:"8px",height:"8px",borderRadius:"50%",background:C.pu,flexShrink:0}}/>
                <span style={{fontSize:"13px",color:"#1a0533",flex:1,fontWeight:"500"}}>{item?.label||c.type}{detail&&<span style={{fontFamily:"monospace",fontWeight:"700",color:C.pu,marginLeft:"4px"}}>{detail}</span>}</span>
              </div>
              {idx<criteria.length-1&&<div style={{display:"flex",alignItems:"center",gap:"8px",margin:"2px 0 2px 16px"}}>
                <div style={{width:"1px",height:"16px",background:"#fde68a"}}/>
                {[["AND","AND"],["OR","OR"]].map(([v,l])=>{
                  const curLogic=c.logicAfter||"AND";
                  return <button key={v} onClick={()=>{const next=criteria.map((cr,i)=>i===idx?{...cr,logicAfter:v}:cr);flush({...ld,successCriteria:next});}} style={{padding:"2px 10px",borderRadius:"6px",border:`1px solid ${curLogic===v?C.pu:"#d1d5db"}`,background:curLogic===v?C.pu:"white",color:curLogic===v?"white":"#374151",fontSize:"11px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit"}}>{l}</button>;
                })}
                <div style={{width:"1px",height:"16px",background:"#fde68a"}}/>
              </div>}
            </div>;
          })}
        </div>}
      </>}

      {/* ── NEW PRESCRIPTION mode: room1 full-blank builder ── */}
      {prescMode==="new"&&<>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"10px",flexWrap:"wrap",gap:"6px"}}>
          <p style={{fontSize:"12px",color:"#78350f",margin:0,lineHeight:"1.5"}}>Set the acceptable ranges for every prescription field. The trainee must land within all ticked ranges to pass.</p>
          <div style={{display:"flex",alignItems:"center",gap:"4px"}}>
            <span style={{fontSize:"11px",color:"#92400e"}}>Names:</span>
            {[["generic","Generic"],["brand","Brand"]].map(([m,l])=>
              <button key={m} onClick={()=>{setSolMode(m);setFilterMode(m);}} style={{padding:"3px 8px",borderRadius:"6px",border:"none",cursor:"pointer",fontSize:"11px",fontWeight:"600",background:solMode===m?"#d97706":"#fde68a",color:solMode===m?"white":"#92400e",fontFamily:"inherit"}}>{l}</button>
            )}
            <span style={{fontSize:"11px",color:"#92400e",marginLeft:"8px"}}>Clearance:</span>
            {[["bsa","L/1.73m²/hr"],["kg","ml/kg/hr"]].map(([u,l])=>
              <button key={u} onClick={()=>setClUnit(u)} style={{padding:"3px 8px",borderRadius:"6px",border:"none",cursor:"pointer",fontSize:"11px",fontWeight:"600",background:clUnit===u?"#d97706":"#fde68a",color:clUnit===u?"white":"#92400e",fontFamily:"inherit"}}>{l}</button>
            )}
          </div>
        </div>

        {/* Helper to toggle a room1 param list item */}
        {(()=>{
          const r1p=(k)=>getParam("room1",k);
          const r1s=(k,v)=>{
            const next=criteria.find(c=>c.type==="room1")
              ? criteria.map(c=>c.type==="room1"?{...c,params:{...c.params,[k]:v}}:c)
              : [{type:"room1",params:{[k]:v}}];
            flush({...ld,successCriteria:next});
          };
          const tog=(k,val)=>{
            const sel=(r1p(k)||"").split(",").filter(Boolean);
            const on=sel.includes(val);
            r1s(k,on?sel.filter(x=>x!==val).join(","):[...sel,val].join(","));
          };
          const isSelOn=(k,val)=>(r1p(k)||"").split(",").filter(Boolean).includes(val);
          const Pill=({k,val,label,size="sm",onToggle})=>{
            const on=isSelOn(k,val);
            return <button onClick={()=>{tog(k,val);onToggle&&onToggle(on);}} style={{padding:size==="sm"?"4px 12px":"6px 18px",borderRadius:"20px",border:`2px solid ${on?C.pu:"#d1d5db"}`,background:on?C.pu:"white",color:on?"white":"#374151",fontSize:size==="sm"?"12px":"13px",cursor:"pointer",fontWeight:on?"700":"500",fontFamily:"inherit"}}>{label}</button>;
          };
          const MinMax=({lk,lv,hk,hv,ph1,ph2,unit})=><div style={{display:"flex",gap:"6px",alignItems:"center",flexWrap:"wrap"}}>
            <input type="number" step="0.1" placeholder={ph1} defaultValue={r1p(lk)||""} onBlur={e=>r1s(lk,e.target.value)} style={{...C.inp,flex:1,minWidth:"80px",maxWidth:"110px"}}/>
            <span style={{fontSize:"11px",color:"#9ca3af"}}>to</span>
            <input type="number" step="0.1" placeholder={ph2} defaultValue={r1p(hk)||""} onBlur={e=>r1s(hk,e.target.value)} style={{...C.inp,flex:1,minWidth:"80px",maxWidth:"110px"}}/>
            {unit&&<span style={{fontSize:"11px",color:"#9ca3af"}}>{unit}</span>}
          </div>;

          // which anticoags are selected
          const selAnticoag=(r1p("anticoag_ok")||"").split(",").filter(Boolean);
          // which solutions are selected
          const selSols=(r1p("solution_ok")||"").split(",").filter(Boolean);
          // All solutions shown for every anticoag — educator decides what is acceptable
          const solsForAnticoag=(_a)=>mySols;

          return <div style={{display:"flex",flexDirection:"column",gap:"16px"}}>

            {/* ── Modality ── */}
            <div style={{background:"white",borderRadius:"10px",padding:"12px 14px",border:"1px solid #e5e7eb"}}>
              <label style={C.lbl}>Acceptable modality/modalities</label>
              <div style={{display:"flex",gap:"6px",flexWrap:"wrap",marginTop:"6px"}}>
                {MODALITIES.map(m=><Pill key={m} k="modality_ok" val={m} label={m} size="md"/>)}
              </div>
            </div>

            {/* ── Clearance ── */}
            <div style={{background:"white",borderRadius:"10px",padding:"12px 14px",border:"1px solid #e5e7eb"}}>
              <label style={C.lbl}>Clearance range ({clUnit==="bsa"?"L/1.73m²/hr":"ml/kg/hr"})</label>
              <MinMax lk="cl_min" hk="cl_max" ph1={clUnit==="bsa"?"min 1.9":"min 18"} ph2={clUnit==="bsa"?"max 3.0":"max 32"} unit={clUnit==="bsa"?"L/1.73m²/hr":"ml/kg/hr"}/>
            </div>

            {/* ── Net UF ── */}
            <div style={{background:"white",borderRadius:"10px",padding:"12px 14px",border:"1px solid #e5e7eb"}}>
              <label style={C.lbl}>Net fluid removal rate (ml/kg/hr) — positive = fluid removed</label>
              <MinMax lk="uf_min" hk="uf_max" ph1="min e.g. 1" ph2="max e.g. 2" unit="ml/kg/hr"/>
            </div>

            {/* ── BFR ── */}
            <div style={{background:"white",borderRadius:"10px",padding:"12px 14px",border:"1px solid #e5e7eb"}}>
              <label style={C.lbl}>Blood flow rate range (ml/min)</label>
              <MinMax lk="bfr_min" hk="bfr_max" ph1="min" ph2="max" unit="ml/min"/>
            </div>

            {/* ── Access ── */}
            <div style={{background:"white",borderRadius:"10px",padding:"12px 14px",border:"1px solid #e5e7eb"}}>
              <label style={C.lbl}>Acceptable catheter sizes</label>
              <div style={{display:"flex",gap:"6px",flexWrap:"wrap",marginTop:"6px"}}>
                {Object.keys(accessDefs).map(sz=><Pill key={sz} k="access_ok" val={sz} label={`${sz} (${accessDefs[sz]})`}/>)}
              </div>
            </div>

            {/* ── Filter ── */}
            <div style={{background:"white",borderRadius:"10px",padding:"12px 14px",border:"1px solid #e5e7eb"}}>
              <label style={C.lbl}>Acceptable filters</label>
              <div style={{display:"flex",gap:"6px",flexWrap:"wrap",marginTop:"6px"}}>
                {myFilters.map(f=>{const fk=typeof f==="string"?f:f.key;return <Pill key={fk} k="filter_ok" val={fk} label={caseFilterLabel(f,filterMode)}/>;})}
              </div>
            </div>

            {/* ── Prime — with ECV/TBV % ranges per prime type ── */}
            {(()=>{
              const selPrimes=(r1p("prime_ok")||"").split(",").filter(Boolean);
              const PRIME_DEFAULTS={saline:{min:0,max:9.9},albumin:{min:10,max:14.9},blood:{min:15,max:100}};
              return <div style={{background:"white",borderRadius:"10px",padding:"12px 14px",border:"1px solid #e5e7eb"}}>
                <label style={C.lbl}>Acceptable priming solutions</label>
                <p style={{fontSize:"11px",color:"#9ca3af",margin:"2px 0 8px",lineHeight:"1.4"}}>Select acceptable primes. For each selected prime, set the ECV/TBV% range where it is appropriate (or tick "Any %" to allow regardless of patient size).</p>
                <div style={{display:"flex",gap:"6px",flexWrap:"wrap",marginBottom:"8px"}}>
                  {myPrimes.map(p=><Pill key={p} k="prime_ok" val={p} label={p} onToggle={(wasOn)=>{
                    // When toggling ON (wasOn=true means it WAS on, now turning off — skip)
                    if(wasOn) return;
                    const d=PRIME_DEFAULTS[p];
                    if(!r1p(`prime_ecv_min_${p}`)) r1s(`prime_ecv_min_${p}`,d.min);
                    if(!r1p(`prime_ecv_max_${p}`)) r1s(`prime_ecv_max_${p}`,d.max);
                  }}/>)}
                </div>
                {selPrimes.length>0&&<div style={{display:"flex",flexDirection:"column",gap:"8px"}}>
                  {selPrimes.map(p=>{
                    const anyPct=r1p(`prime_ecv_any_${p}`)==="1";
                    const minV=r1p(`prime_ecv_min_${p}`)??PRIME_DEFAULTS[p].min;
                    const maxV=r1p(`prime_ecv_max_${p}`)??PRIME_DEFAULTS[p].max;
                    return <div key={p} style={{padding:"10px 12px",background:"#f8f7ff",borderRadius:"8px",border:"1px solid #ede9fe",display:"flex",alignItems:"center",gap:"12px",flexWrap:"wrap"}}>
                      <div style={{fontSize:"12px",fontWeight:"700",color:C.pu,minWidth:"60px",textTransform:"capitalize"}}>{p}</div>
                      <label style={{display:"flex",alignItems:"center",gap:"5px",cursor:"pointer",fontSize:"12px",color:"#374151",flexShrink:0}}>
                        <input type="checkbox" checked={anyPct} onChange={e=>r1s(`prime_ecv_any_${p}`,e.target.checked?"1":"")} style={{accentColor:C.pu}}/>
                        Any %
                      </label>
                      {!anyPct&&<div style={{display:"flex",alignItems:"center",gap:"6px",flexWrap:"wrap"}}>
                        <span style={{fontSize:"11px",color:"#6b7280",flexShrink:0}}>ECV/TBV</span>
                        <input type="number" min="0" max="100" step="0.1"
                          defaultValue={minV} key={`min-${p}`}
                          onBlur={e=>r1s(`prime_ecv_min_${p}`,parseFloat(e.target.value)??0)}
                          style={{...C.inp,width:"70px",textAlign:"center"}}/>
                        <span style={{fontSize:"11px",color:"#9ca3af"}}>%  to</span>
                        <input type="number" min="0" max="100" step="0.1"
                          defaultValue={maxV} key={`max-${p}`}
                          onBlur={e=>r1s(`prime_ecv_max_${p}`,parseFloat(e.target.value)??100)}
                          style={{...C.inp,width:"70px",textAlign:"center"}}/>
                        <span style={{fontSize:"11px",color:"#9ca3af"}}>%</span>
                        <button onClick={()=>{r1s(`prime_ecv_min_${p}`,PRIME_DEFAULTS[p].min);r1s(`prime_ecv_max_${p}`,PRIME_DEFAULTS[p].max);}}
                          style={{fontSize:"10px",padding:"2px 8px",borderRadius:"5px",border:"1px solid #d1d5db",background:"#f9fafb",cursor:"pointer",color:"#6b7280",fontFamily:"inherit"}}>Reset</button>
                      </div>}
                    </div>;
                  })}
                </div>}
              </div>;
            })()}

            {/* ── Anticoagulation — with per-anticoag solution sub-list ── */}
            <div style={{background:"white",borderRadius:"10px",padding:"12px 14px",border:"1px solid #e5e7eb"}}>
              <label style={C.lbl}>Acceptable anticoagulation</label>
              <div style={{display:"flex",gap:"6px",flexWrap:"wrap",marginTop:"6px",marginBottom:"8px"}}>
                {myAnticoags.map(a=><Pill key={a} k="anticoag_ok" val={a} label={a}/>)}
              </div>
              {/* Per-anticoag: show acceptable solutions */}
              {selAnticoag.length>0&&<div style={{marginTop:"10px",display:"flex",flexDirection:"column",gap:"10px"}}>
                <div style={{fontSize:"10px",fontWeight:"700",color:"#6b7280",textTransform:"uppercase",letterSpacing:"0.06em"}}>Acceptable solutions for each anticoagulant</div>
                {selAnticoag.map(a=><div key={a} style={{padding:"10px 12px",background:"#f8f7ff",borderRadius:"8px",border:"1px solid #ede9fe"}}>
                  <div style={{fontSize:"11px",fontWeight:"700",color:C.pu,marginBottom:"6px",textTransform:"capitalize"}}>{a}</div>
                  <div style={{display:"flex",gap:"6px",flexWrap:"wrap"}}>
                    {solsForAnticoag(a).map(s=>{
                      const k=`sol_ok_${a}`;
                      const sel2=(r1p(k)||"").split(",").filter(Boolean);
                      const on2=sel2.includes(s);
                      const tog2=()=>r1s(k,on2?sel2.filter(x=>x!==s).join(","):[...sel2,s].join(","));
                      return <button key={s} onClick={tog2} style={{padding:"4px 12px",borderRadius:"16px",border:`2px solid ${on2?C.pu:"#d1d5db"}`,background:on2?C.pu:"white",color:on2?"white":"#374151",fontSize:"12px",cursor:"pointer",fontWeight:on2?"700":"500",fontFamily:"inherit"}}>{solName(s,solMode)}</button>;
                    })}
                  </div>
                </div>)}
              </div>}
            </div>

            {/* ── Solutions — with per-solution K/PO4 ranges ── */}
            {(()=>{
              // Aggregate all solutions selected across all anticoags
              const allSelSols=[...new Set(selAnticoag.flatMap(a=>(r1p(`sol_ok_${a}`)||"").split(",").filter(Boolean)))];
              return allSelSols.length>0&&<div style={{background:"white",borderRadius:"10px",padding:"12px 14px",border:"1px solid #e5e7eb"}}>
                <label style={C.lbl}>Acceptable K⁺ and PO₄ additives per solution</label>
                <p style={{fontSize:"11px",color:"#9ca3af",margin:"2px 0 10px",lineHeight:"1.4"}}>For each selected solution, set the acceptable additive ranges (leave blank if not required).</p>
                <div style={{display:"flex",flexDirection:"column",gap:"10px"}}>
                  {allSelSols.map(s=><div key={s} style={{padding:"10px 12px",background:"#f0fdf4",borderRadius:"8px",border:"1px solid #bbf7d0"}}>
                    <div style={{fontSize:"11px",fontWeight:"700",color:"#065f46",marginBottom:"8px"}}>
                      {solName(s,solMode)}
                      {solMode==="brand" && <span style={{fontWeight:"400",color:"#6b7280"}}> ({solutionLabels[s]})</span>}
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"8px"}}>
                      <div>
                        <label style={{...C.lbl,color:"#065f46"}}>K⁺ additive range (mmol/L)</label>
                        <MinMax lk={`k_min_${s}`} hk={`k_max_${s}`} ph1="min e.g. 0" ph2="max e.g. 4" unit="mmol/L"/>
                      </div>
                      <div>
                        <label style={{...C.lbl,color:"#065f46"}}>PO₄ additive range (mmol/L)</label>
                        <MinMax lk={`po4_min_${s}`} hk={`po4_max_${s}`} ph1="min e.g. 0" ph2="max e.g. 2" unit="mmol/L"/>
                      </div>
                    </div>
                  </div>)}
                </div>
              </div>;
            })()}

            {/* ── Clinical interventions for blank-start cases ── */}
            <div style={{background:"white",borderRadius:"10px",padding:"12px 14px",border:"1px solid #e5e7eb"}}>
              <label style={C.lbl}>Required clinical actions (if applicable)</label>
              <p style={{fontSize:"11px",color:"#9ca3af",margin:"2px 0 8px",lineHeight:"1.4"}}>Tick any bedside actions also required to pass this case. Numeric interventions show an acceptable value range.</p>
              <div style={{display:"flex",gap:"6px",flexWrap:"wrap",marginBottom:"6px"}}>
                {myIntvs.filter(iv=>iv.type!=="numeric").map(act=><Pill key={act.id} k="actions_ok" val={act.id} label={act.label}/>)}
              </div>
              {myIntvs.filter(iv=>iv.type==="numeric").length>0&&<>
                <div style={{fontSize:"10px",fontWeight:"700",color:"#6b7280",textTransform:"uppercase",letterSpacing:"0.06em",margin:"8px 0 6px"}}>Numeric actions</div>
                {myIntvs.filter(iv=>iv.type==="numeric").map(act=>{
                  const selKey=`action_val_${act.id}`;
                  const isActOn=isSelOn("actions_ok",act.id);
                  return <div key={act.id} style={{display:"flex",alignItems:"center",gap:"8px",flexWrap:"wrap",marginBottom:"4px"}}>
                    <Pill k="actions_ok" val={act.id} label={act.label}/>
                    {isActOn&&<div style={{display:"flex",alignItems:"center",gap:"4px"}}>
                      <span style={{fontSize:"11px",color:"#6b7280"}}>target:</span>
                      <input type="number" min={act.min} max={act.max} step="any"
                        defaultValue={r1p(selKey)||""} key={selKey}
                        onBlur={e=>r1s(selKey,parseFloat(e.target.value)||0)}
                        style={{...C.inp,width:"80px"}}/>
                      {act.unit&&<span style={{fontSize:"11px",color:"#9ca3af"}}>{act.unit}</span>}
                      <span style={{fontSize:"10px",color:"#9ca3af"}}>({act.min}–{act.max})</span>
                    </div>}
                  </div>;
                })}
              </>}
            </div>

          </div>;
        })()}
      </>}

      {/* Free-text summary */}
      {prescMode&&<div style={{marginTop:"16px"}}>
        <label style={C.lbl}>Plain-language summary (Not shown to trainee - for other educators)</label>
        <input type="text" defaultValue={caseData.successSummary||""} onBlur={e=>flush({...ld,successSummary:e.target.value})} style={C.inp}/>
      </div>}
    </div>
  </div>;
}

// ─── CASE TAGS EDITOR (educator-only, reusable) ──────────────────────────────
function CaseTagsEditor({tags,onChange}){
  const sel=Array.isArray(tags)?tags:[];
  const toggle=(tag)=>onChange(sel.includes(tag)?sel.filter(t=>t!==tag):sel.length<3?[...sel,tag]:sel);
  return <div style={{marginBottom:"14px"}}>
    <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"4px"}}>
      <label style={{...C.lbl,marginBottom:0}}>Subject Tags</label>
      <span style={{fontSize:"10px",padding:"1px 7px",background:"#fef3c7",color:"#92400e",borderRadius:"8px",fontWeight:"600"}}>🔒 Educator only — not shown to trainees</span>
      <span style={{fontSize:"10px",color:"#9ca3af",marginLeft:"auto"}}>{sel.length}/3 selected</span>
    </div>
    <div style={{fontSize:"11px",color:"#9ca3af",marginBottom:"8px"}}>Select 1–3 clinical tags for research analytics and curriculum mapping.</div>
    <div style={{display:"flex",gap:"6px",flexWrap:"wrap"}}>
      {SUBJECT_TAGS.map(tag=>{
        const active=sel.includes(tag);
        const atMax=sel.length>=3&&!active;
        return <button key={tag} onClick={()=>!atMax&&toggle(tag)} disabled={atMax}
          style={{padding:"4px 11px",borderRadius:"14px",border:`1.5px solid ${active?"#4F2683":"#e5e7eb"}`,
            background:active?"#4F2683":"white",color:active?"white":atMax?"#d1d5db":"#374151",
            fontSize:"11px",fontWeight:active?"700":"400",cursor:atMax?"not-allowed":"pointer",
            fontFamily:"inherit",transition:"all 0.1s"}}>
          {tag}
        </button>;
      })}
    </div>
  </div>;
}

// ─── CASE MODAL (view + edit) ─────────────────────────────────────────────────
function CaseModal({caseId,caseData,displayData,editorEmail,canEdit,isBaseCase,onSave,onSaveAsCustom,onClose}){
  const {t}=useLocale();
  // `draft` is built from caseData and is ALWAYS canonical source-language text:
  // it feeds the Edit tab and every save path. `view` is the localised copy and
  // is used for READ-ONLY display only, so a translation can never be written
  // back over the source.
  const view = displayData || caseData;
  const viewTr = view?._translation || null;
  // New custom cases (no scenario yet) open directly to Edit
  const [mainTab,setMainTab]=useState(()=>caseId.startsWith("custom_")&&!caseData.page?"edit":"view");
  const [editTab,setEditTab]=useState("vignette");
  const [draft,setDraft]=useState(JSON.parse(JSON.stringify(caseData)));
  const [useSI,setUseSI]=useState(true);
  const [flash,setFlash]=useState(false);
  const [previewSolMode,setPreviewSolMode]=useState("generic");
  const [previewFilterMode,setPreviewFilterMode]=useState("brand");
  const doSave=()=>{onSave(caseId,draft);setFlash(true);setTimeout(()=>setFlash(false),2200);};
  const isCustom=caseId.startsWith("custom_");

  return <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:100,display:"flex",alignItems:"flex-start",justifyContent:"center",padding:"12px",overflowY:"auto"}} onClick={onClose}>
    <div style={{background:"white",borderRadius:"16px",width:"100%",maxWidth:"900px",minHeight:"80vh",display:"flex",flexDirection:"column",boxShadow:"0 32px 80px rgba(0,0,0,0.5)",margin:"8px auto"}} onClick={e=>e.stopPropagation()}>
      <div style={{padding:"18px 26px 0",borderBottom:"1px solid #e5e7eb",flexShrink:0}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:"12px",gap:"10px",flexWrap:"wrap"}}>
          <div>
            <h2 style={{margin:"0 0 6px",fontSize:"16px",fontWeight:"700",color:C.dark}}>{draft.title}</h2>
            <div style={{display:"flex",gap:"6px",flexWrap:"wrap",alignItems:"center"}}>
              <span style={C.tag("#ede9fe","#6d28d9")}>{draft.facet}</span>
              {isCustom&&<span style={C.tag("#dbeafe","#1e40af")}>Community Case</span>}
              {isCustom&&draft.creatorName&&<span style={{fontSize:"11px",color:"#9ca3af"}}>by {draft.creatorName}</span>}
              {draft.tags&&draft.tags.length>0&&<span style={{fontSize:"10px",padding:"2px 0",color:"#6b7280",display:"flex",gap:"4px",alignItems:"center"}}>
                <span style={{color:"#f59e0b"}}>🔒</span>
                {draft.tags.map(t=><span key={t} style={{padding:"1px 7px",background:"#fef3c7",color:"#92400e",borderRadius:"8px",fontWeight:"600",fontSize:"10px"}}>{t}</span>)}
              </span>}
            </div>
          </div>
          <div style={{display:"flex",gap:"8px",alignItems:"center",flexShrink:0}}>
            {mainTab==="edit"&&(
              isBaseCase
                ? <>
                    {/* Admin (amrit) can save directly over the base case as a persistent override */}
                    {editorEmail === "amrit.kirpalani@lhsc.on.ca" && (
                      <button onClick={()=>{doSave();setFlash(true);setTimeout(()=>setFlash(false),2200);}}
                        style={{...C.btn(flash?"#059669":"#0369a1"),marginRight:"4px"}}>
                        {flash?<Ic n="check" s="w-4 h-4"/>:<Ic n="save" s="w-4 h-4"/>}
                        {flash?"Saved!":"Save Override"}
                      </button>
                    )}
                    <button onClick={()=>{onSaveAsCustom&&onSaveAsCustom(draft);}}
                      style={{...C.btn(C.pu)}}>
                      <Ic n="copy" s="w-4 h-4"/>
                      Save as Custom
                    </button>
                  </>
                : canEdit
                    ? <button onClick={doSave} style={{...C.btn(flash?"#059669":C.pu)}}>
                        {flash?<Ic n="check" s="w-4 h-4"/>:<Ic n="save" s="w-4 h-4"/>}
                        {flash?"Saved!":"Save Changes"}
                      </button>
                    : <span style={{fontSize:"12px",color:"#9ca3af",padding:"8px"}}>
                        Only {draft.creatorName} can edit this case.
                      </span>
            )}
            <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",color:"#6b7280"}}><Ic n="x" s="w-5 h-5"/></button>
          </div>
        </div>
        <div style={{display:"flex"}}>
          {[{id:"view",l:"Preview"},{id:"edit",l:"Edit"}].map(t=><button key={t.id} onClick={()=>setMainTab(t.id)} style={{padding:"8px 20px",background:"none",border:"none",cursor:"pointer",fontWeight:"700",fontSize:"13px",color:mainTab===t.id?C.pu:"#9ca3af",borderBottom:`2px solid ${mainTab===t.id?C.pu:"transparent"}`,marginBottom:"-1px",fontFamily:"inherit"}}>{t.l}</button>)}
        </div>
      </div>
      <div style={{padding:"22px 26px",overflowY:"auto",flex:1}}>
        {/* ── PREVIEW ── */}
        {mainTab==="view"&&<>
          {viewTr&&viewTr.isTranslated&&<div style={{marginBottom:"14px"}}>
            <TranslationNote tr={viewTr}/>
          </div>}
          <div style={{padding:"13px 16px",background:"#fffbeb",border:"1px solid #fde68a",borderLeft:"4px solid #f59e0b",borderRadius:"8px",marginBottom:"18px"}}>
            <div style={{fontSize:"10px",fontWeight:"700",color:"#92400e",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:"5px"}}>{t("sec.clinicalScenario","Clinical Scenario")}</div>
            <p style={{margin:0,fontSize:"14px",color:"#78350f",lineHeight:"1.6"}}>{view.page||<em style={{color:"#d97706"}}>No scenario written yet.</em>}</p>
          </div>
          <div style={{display:"flex",justifyContent:"flex-end",alignItems:"center",gap:"8px",marginBottom:"12px",flexWrap:"wrap"}}>
            <span style={{fontSize:"12px",color:"#6b7280"}}>{t("ui.labUnits","Lab units")}:</span>
            <button onClick={()=>setUseSI(v=>!v)} style={{...C.btn(useSI?C.pu:"#374151"),padding:"5px 14px",fontSize:"12px"}}>{useSI?"SI":"US"}</button>
            <span style={{fontSize:"12px",color:"#6b7280",marginLeft:"8px"}}>{t("ui.filters","Filters")}:</span>
            {[["brand",t("ui.brand","Brand")],["generic",t("ui.generic","Generic")]].map(([m,l])=><button key={m} onClick={()=>setPreviewFilterMode(m)} style={{padding:"4px 10px",borderRadius:"6px",border:"none",cursor:"pointer",fontSize:"11px",fontWeight:"600",background:previewFilterMode===m?C.pu:"#e5e7eb",color:previewFilterMode===m?"white":"#374151",fontFamily:"inherit"}}>{l}</button>)}
            <span style={{fontSize:"12px",color:"#6b7280",marginLeft:"4px"}}>{t("ui.solutions","Solutions")}:</span>
            {[["generic",t("ui.generic","Generic")],["brand",t("ui.brand","Brand")]].map(([m,l])=><button key={m} onClick={()=>setPreviewSolMode(m)} style={{padding:"4px 10px",borderRadius:"6px",border:"none",cursor:"pointer",fontSize:"11px",fontWeight:"600",background:previewSolMode===m?C.pu:"#e5e7eb",color:previewSolMode===m?"white":"#374151",fontFamily:"inherit"}}>{l}</button>)}
          </div>
          <PatientSnapshot initialState={draft.initialState} useSI={useSI}/>
          <div style={{marginTop:"18px"}}>
            <div style={C.secHead}>{t("sec.startingRx","Starting Prescription")}</div>
            <PrescriptionDisplay caseId={caseId} rx={draft.initialPrescription||{}} initialState={draft.initialState} solMode={previewSolMode} filterMode={previewFilterMode}/>
          </div>
          {view.learningPoint&&<div style={{marginTop:"16px",padding:"13px 16px",background:"#f0fdf4",border:"1px solid #bbf7d0",borderLeft:"4px solid #22c55e",borderRadius:"8px"}}>
            <div style={{fontSize:"10px",fontWeight:"700",color:"#14532d",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:"5px"}}>{t("sec.learningPoint","Learning Point")}</div>
            <p style={{margin:0,fontSize:"13px",color:"#166534",lineHeight:"1.6"}}>{view.learningPoint}</p>
          </div>}
          {view.successSummary&&<div style={{marginTop:"10px",padding:"11px 14px",background:"#fefce8",border:"1px solid #fde68a",borderRadius:"8px"}}>
            <div style={{fontSize:"10px",fontWeight:"700",color:"#92400e",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:"5px"}}>{t("sec.successCriteria","Criteria for Success")}</div>
            <p style={{margin:0,fontSize:"13px",color:"#78350f"}}>{view.successSummary}</p>
          </div>}
        </>}
        {/* ── EDIT ── */}
        {mainTab==="edit" && (
          <>
            {!canEdit ? (
              <div style={{padding:"32px",textAlign:"center",color:"#9ca3af"}}>
                <Ic n="lock" s="w-10 h-10" style={{margin:"0 auto 12px",display:"block"}}/>
                <p style={{fontSize:"14px"}}>
                  This case was created by <strong>{draft.creatorName}</strong> and can only be edited by them.
                </p>
              </div>
            ) : (
              <>
                {(() => {
                  // Completion signals for each section
                  const done = {
                    vignette:     !!(draft.title && draft.page && draft.learningPoint),
                    labs:         !!(draft.initialState?.weightKg && draft.initialState?.ageMonths),
                    prescription: !!(draft.initialPrescription?.modality && (draft.successCriteria || []).length > 0),
                    options:      !!(draft.caseOptions),
                  };

                  const tabs = [
                    {id:"vignette",     l:"Scenario"},
                    {id:"labs",         l:"Patient Data"},
                    {id:"prescription", l:"Orders & Criteria"},
                    {id:"options",      l:"Prescription Options"},
                  ];

                  return (
                    <>
                      {/* Tab bar */}
                      <div style={{display:"flex",gap:"6px",marginBottom:"18px",flexWrap:"wrap"}}>
                        {tabs.map(t => {
                          const active = editTab === t.id;
                          const complete = done[t.id];
                          return (
                            <button
                              key={t.id}
                              onClick={() => setEditTab(t.id)}
                              style={{
                                padding:"7px 14px",
                                borderRadius:"18px",
                                border:`1.5px solid ${active ? C.pu : complete ? "#bbf7d0" : "#e5e7eb"}`,
                                cursor:"pointer",
                                fontWeight:"600",
                                fontSize:"13px",
                                background: active ? C.pu : complete ? "#f0fdf4" : "#f9fafb",
                                color: active ? "white" : complete ? "#15803d" : "#374151",
                                fontFamily:"inherit",
                                display:"flex",
                                alignItems:"center",
                                gap:"6px",
                                transition:"all 0.12s",
                              }}
                            >
                              {t.l}
                              {complete && !active && (
                                <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                                  <circle cx="6.5" cy="6.5" r="6" fill="#22c55e"/>
                                  <path d="M3.5 6.5l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              )}
                              {!complete && !active && (
                                <span style={{width:"7px",height:"7px",borderRadius:"50%",background:"#e5e7eb",display:"inline-block"}}/>
                              )}
                            </button>
                          );
                        })}
                      </div>

                      {/* Completion progress bar */}
                      {(() => {
                        const n = Object.values(done).filter(Boolean).length;
                        const pct = Math.round((n / 4) * 100);
                        return (
                          <div style={{marginBottom:"18px"}}>
                            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"5px"}}>
                              <span style={{fontSize:"11px",color:"#9ca3af",fontWeight:"600"}}>Case completeness</span>
                              <span style={{fontSize:"11px",fontWeight:"700",color: pct===100 ? "#15803d" : pct>=50 ? "#d97706" : "#9ca3af"}}>{pct}%</span>
                            </div>
                            <div style={{height:"5px",borderRadius:"4px",background:"#e5e7eb",overflow:"hidden"}}>
                              <div style={{height:"100%",borderRadius:"4px",background: pct===100 ? "#22c55e" : pct>=50 ? "#f59e0b" : C.pu, width:`${pct}%`,transition:"width 0.4s ease"}}/>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Tab content */}
                      {editTab === "vignette" && (
                        <>
                          <div style={{marginBottom:"14px"}}>
                            <label style={C.lbl}>Case Title</label>
                            <input
                              defaultValue={draft.title || ""}
                              onBlur={e => setDraft(p => ({...p, title:e.target.value}))}
                              style={C.inp}
                            />
                          </div>
                          <div style={{marginBottom:"14px"}}>
                            <label style={C.lbl}>Clinical Scenario (what the trainee reads)</label>
                            <textarea
                              defaultValue={draft.page || ""}
                              onBlur={e => setDraft(p => ({...p, page:e.target.value}))}
                              rows={6}
                              style={{...C.inp, resize:"vertical"}}
                            />
                          </div>
                          <div style={{marginBottom:"14px"}}>
                            <label style={C.lbl}>Learning Point (revealed after a correct answer)</label>
                            <textarea
                              defaultValue={draft.learningPoint || ""}
                              onBlur={e => setDraft(p => ({...p, learningPoint:e.target.value}))}
                              rows={4}
                              style={{...C.inp, resize:"vertical"}}
                            />
                          </div>
                          <CaseTagsEditor tags={draft.tags || []} onChange={t => setDraft(p => ({...p, tags:t}))}/>
                        </>
                      )}

                      {editTab === "labs" && (
                        <LabEditor key="labs" initialState={draft.initialState} onChange={ns => setDraft(p => ({...p, initialState:ns}))}/>
                      )}

                      {editTab === "prescription" && (
                        <PrescriptionEditor key="presc" caseData={draft} caseId={caseId} onChange={u => setDraft(u)}/>
                      )}

                      {editTab === "options" && (
                        <div>
                          <div style={{marginBottom:"12px",padding:"12px 14px",background:"#f0f9ff",borderRadius:"10px",border:"1px solid #bae6fd",fontSize:"12px",color:"#0c4a6e"}}>
                            <strong>Available Options</strong> — customise which choices appear in the trainee&apos;s prescription form and success criteria for this case.
                          </div>
                          <OptionsEditor opts={draft.caseOptions} onChange={newOpts => setDraft(p => ({...p, caseOptions:newOpts}))}/>
                        </div>
                      )}
                    </>
                  );
                })()}
              </>
            )}
          </>
        )}
      </div>
    </div>
  </div>;
}

// ─── CREATE NEW CASE MODAL ────────────────────────────────────────────────────
function CreateCaseModal({educator,onSave,onClose}){
  const [facet,setFacet]=useState("");
  const [tags,setTags]=useState([]);
  const [keyword,setKeyword]=useState("");
  const [title,setTitle]=useState("");
  const [isShared,setIsShared]=useState(false);
  // Language this case is being AUTHORED in. Defaults to the educator's current
  // interface language, so someone working in French writes a French-native
  // case rather than a "translation" of an English original.
  const {locale:uiLoc}=useLocale();
  const [sourceLocale,setSourceLocale]=useState(uiLoc||"en");
  const [step,setStep]=useState(1); // 1=basics, 2=edit full case
  const [newCase,setNewCase]=useState(null);
  const [saving,setSaving]=useState(false);

  const toggleTag=(tag)=>setTags(prev=>prev.includes(tag)?prev.filter(t=>t!==tag):prev.length<3?[...prev,tag]:prev);

  const BLANK_STATE={ageMonths:36,weightKg:25,bsa:0.9,vitals:{bp:"",hr:""},labs:{Na:140,K:4.0,Cl:104,HCO3:24,Urea:5.0,Cr:88,iCa:1.12,Mg:0.8,PO4:1.3,Alb:35},gas:{pH:7.4,pCO2:40,pO2:95,BE:0},cbc:{WBC:10.5,Hgb:120,Hct:0.36,Plt:250}};

  const doCreate=()=>{
    if(!facet||!title.trim()) return;
    const id=`custom_${Date.now()}_${Math.random().toString(36).slice(2,6)}`;
    const c={
      facet:facet==="Miscellaneous"&&keyword?`Miscellaneous (${keyword})`:facet,
      tags:[...tags],
      title:title.trim(),page:"",
      initialState:JSON.parse(JSON.stringify(BLANK_STATE)),
      initialPrescription:{},
      successType:"",successParam:"",successSummary:"",learningPoint:"",
      creatorEmail:educator.email,creatorName:educator.name,
      sourceLocale:sourceLocale||"en",
      isShared,createdAt:new Date().toISOString(),
    };
    setNewCase({id,data:c});setStep(2);
  };

  const doFinish=async(id,data)=>{
    setSaving(true);
    await onSave(id,data,isShared);
    setSaving(false);onClose();
  };

  if(step===2&&newCase) return <CaseModal caseId={newCase.id} caseData={newCase.data} editorEmail={educator.email} canEdit={true} onSave={doFinish} onClose={onClose}/>;

  return <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:"16px"}} onClick={onClose}>
    <div style={{background:"white",borderRadius:"16px",width:"100%",maxWidth:"480px",boxShadow:"0 24px 60px rgba(0,0,0,0.4)",padding:"32px"}} onClick={e=>e.stopPropagation()}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"22px"}}>
        <h2 style={{margin:0,fontSize:"18px",fontWeight:"700",color:C.dark}}>Create a New Case</h2>
        <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",color:"#6b7280"}}><Ic n="x" s="w-5 h-5"/></button>
      </div>
      <div style={{marginBottom:"16px"}}>
        <label style={C.lbl}>Case Title</label>
        <input defaultValue={title} onBlur={e=>setTitle(e.target.value)} placeholder="e.g. Room 22: Unexpected Phosphate Drop" style={C.inp}/>
      </div>
      <div style={{marginBottom:"16px"}}>
        <label style={C.lbl}>Language you are writing this case in</label>
        <select value={sourceLocale} onChange={e=>setSourceLocale(e.target.value)} style={C.sel}>
          {LOCALE_CODES.map(c=><option key={c} value={c}>{LOCALES[c].native} ({LOCALES[c].label})</option>)}
        </select>
        <div style={{fontSize:"11px",color:"#9ca3af",marginTop:"5px",lineHeight:"1.5"}}>
          Cases read in the language they were written in are shown as original content.
          Only text translated from another language carries an unverified-translation note.
        </div>
      </div>
      <div style={{marginBottom:"16px"}}>
        <label style={C.lbl}>Category</label>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"8px"}}>
          {USER_FACETS.map(f=><button key={f} onClick={()=>setFacet(f)} style={{padding:"10px 8px",borderRadius:"8px",border:`2px solid ${facet===f?C.pu:"#e5e7eb"}`,background:facet===f?"#faf5ff":"white",color:facet===f?C.pu:"#374151",fontWeight:facet===f?"700":"500",fontSize:"12px",cursor:"pointer",fontFamily:"inherit",textAlign:"center"}}>{f}</button>)}
        </div>
      </div>
      {facet==="Miscellaneous"&&<div style={{marginBottom:"16px"}}>
        <label style={C.lbl}>Keyword / Topic (optional)</label>
        <input defaultValue={keyword} onBlur={e=>setKeyword(e.target.value)} placeholder="e.g. Drug removal, Liver failure…" style={C.inp}/>
      </div>}
      {facet&&<div style={{marginBottom:"16px"}}>
        <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"4px"}}>
          <label style={{...C.lbl,marginBottom:0}}>Subject Tags</label>
          <span style={{fontSize:"10px",padding:"1px 7px",background:"#fef3c7",color:"#92400e",borderRadius:"8px",fontWeight:"600"}}>🔒 Educator only — not shown to trainees</span>
          <span style={{fontSize:"10px",color:"#9ca3af",marginLeft:"auto"}}>{tags.length}/3 selected</span>
        </div>
        <div style={{fontSize:"11px",color:"#9ca3af",marginBottom:"8px"}}>Select 1–3 clinical tags to classify this case for research analytics and curriculum mapping.</div>
        <div style={{display:"flex",gap:"6px",flexWrap:"wrap"}}>
          {SUBJECT_TAGS.map(tag=>{
            const sel=tags.includes(tag);
            const atMax=tags.length>=3&&!sel;
            return <button key={tag} onClick={()=>!atMax&&toggleTag(tag)} disabled={atMax}
              style={{padding:"4px 11px",borderRadius:"14px",border:`1.5px solid ${sel?"#4F2683":"#e5e7eb"}`,
                background:sel?"#4F2683":"white",color:sel?"white":atMax?"#d1d5db":"#374151",
                fontSize:"11px",fontWeight:sel?"700":"400",cursor:atMax?"not-allowed":"pointer",
                fontFamily:"inherit",transition:"all 0.1s"}}>
              {tag}
            </button>;
          })}
        </div>
      </div>}
      <div style={{marginBottom:"20px",padding:"14px",background:"#f8f7ff",borderRadius:"10px"}}>
        <label style={{display:"flex",alignItems:"flex-start",gap:"10px",cursor:"pointer"}}>
          <input type="checkbox" checked={isShared} onChange={e=>setIsShared(e.target.checked)} style={{marginTop:"3px",flexShrink:0}}/>
          <div>
            <div style={{fontWeight:"600",fontSize:"13px",color:C.dark,display:"flex",alignItems:"center",gap:"6px"}}>
              <Ic n="globe" s="w-4 h-4"/> Make this case visible to all educators
            </div>
            <div style={{fontSize:"12px",color:"#6b7280",marginTop:"3px"}}>Other educators can view and assign your case (but only you can edit it). Your name will appear as the creator.</div>
          </div>
        </label>
      </div>
      <div style={{display:"flex",gap:"10px",justifyContent:"flex-end"}}>
        <button onClick={onClose} style={{...C.btn("#f3f4f6","#374151")}}>Cancel</button>
        <button onClick={doCreate} disabled={!facet||!title.trim()} style={{...C.btn(C.pu),opacity:(!facet||!title.trim())?0.5:1}}>
          <Ic n="plus" s="w-4 h-4"/> Continue to Case Builder →
        </button>
      </div>
    </div>
  </div>;
}

// ─── SEND ASSIGNMENT MODAL ────────────────────────────────────────────────────
function SendModal({caseId,title,caseOptions,caseData,educator,onSend,onClose}){
  const [trainees,setTrainees]=useState([{name:"",email:""}]);
  const [fm,setFm]=useState("immediate"); const [dl,setDl]=useState(""); const [fd,setFd]=useState(""); const [note,setNote]=useState("");
  const [maxAttempts,setMaxAttempts]=useState(1);
  const [busy,setBusy]=useState(false); const [sent,setSent]=useState(false);

  // ── Delivery language ──────────────────────────────────────────────────────
  // Defaults to the case's own authoring language. Only languages that actually
  // exist for this case are offered, so an educator can't send a trainee to a
  // language with no content.
  const caseSourceLoc = caseData?.sourceLocale || "en";
  const [sendLocale,setSendLocale]=useState(caseSourceLoc);
  const [allowOverride,setAllowOverride]=useState(true);
  const [availLocales,setAvailLocales]=useState([caseSourceLoc]);

  useEffect(()=>{let dead=false;(async()=>{
    const found=new Set([caseSourceLoc]);
    try{
      const qs=await getDocs(query(collection(db,"case_translations"),where("caseId","==",caseId)));
      qs.forEach(d=>{const l=d.data().locale; if(LOCALES[l]) found.add(l);});
    }catch{/* educator-gated read; ignore */}
    if(!dead) setAvailLocales(LOCALE_CODES.filter(c=>found.has(c)));
  })();return()=>{dead=true;};},[caseId,caseSourceLoc]);
  const addT=()=>setTrainees(p=>[...p,{name:"",email:""}]);
  const remT=i=>setTrainees(p=>p.filter((_,j)=>j!==i));
  const updT=(i,f,v)=>setTrainees(p=>p.map((t,j)=>j===i?{...t,[f]:v}:t));
  const doSend = async () => {
  const valid = trainees
    .map(t => ({ name: (t.name || "").trim(), email: (t.email || "").trim().toLowerCase() }))
    .filter(t => t.email && t.email.includes("@"));

  if (!valid.length) return;

  setBusy(true);

  try {
    // 1) Create local assignment objects (your current behavior)
    const newAssignments = valid.map(t => ({
      id: `a_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      caseId,
      caseTitle: title,
      caseOptions: caseOptions || null,
      // Embed overridden case fields so trainee gets educator's version
      caseFacet: caseData?.facet || null,
      caseOverride: caseData ? stripUndefined({
        page:                caseData.page,
        initialState:        caseData.initialState,
        initialPrescription: caseData.initialPrescription,
        successType:         caseData.successType         || null,
        successParam:        caseData.successParam        != null ? caseData.successParam : null,
        successCriteria:     caseData.successCriteria     || null,
        criteriaLogic:       caseData.criteriaLogic       || null,
        prescribingMode:     caseData.prescribingMode     || null,
        caseOptions:         caseData.caseOptions         || null,
        successSummary:      caseData.successSummary      || null,
        learningPoint:       caseData.learningPoint       || null,
      }) : null,
      educatorName: educator.name,
      traineeName: t.name,
      traineeEmail: t.email,
      feedbackMode: fm,
      deadline: dl,
      feedbackDate: fd,
      note,
      maxAttempts,
      // Language. caseOverride above stays in the SOURCE language; the CF
      // applies any translation at delivery time.
      caseSourceLocale: caseData?.sourceLocale || "en",
      locale: sendLocale,
      allowLocaleOverride: allowOverride,
      sentAt: new Date().toISOString(),
      status: "pending",
      result: null
    }));

    await onSend(newAssignments);

// 2) Send ONE secure link email per trainee with assignment context
const functions = getFunctions(firebaseApp, "us-central1");
const _sendAssignmentLink = httpsCallable(functions, "sendAssignmentLink");

// Deduplicate by traineeEmail
const unique = new Map();
for (const a of newAssignments) unique.set(a.traineeEmail, a.traineeName);

const _assignmentIds = newAssignments.map(a => a.id);
for (const [traineeEmail, traineeName] of unique.entries()) {
  await _sendAssignmentLink({
    traineeEmail,
    traineeName,
    caseTitle: title,
    caseId,
    locale: sendLocale,
    educatorName: educator?.name || "",
    payload: { assignmentIds: _assignmentIds, caseId, educatorName: educator?.name || "" },
  });
}

    setSent(true);
  } catch (e) {
    console.error(e);
    alert(e?.message || "Unable to send assignment emails. Please try again.");
  } finally {
    setBusy(false);
  }
};
  const ok=trainees.some(t=>t.email&&t.email.includes("@"));
  if(sent) return <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:"16px"}}>
    <div style={{background:"white",borderRadius:"16px",padding:"44px",textAlign:"center",maxWidth:"380px",boxShadow:"0 24px 60px rgba(0,0,0,0.4)"}}>
      <div style={{width:"56px",height:"56px",background:"#d1fae5",borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 14px"}}><Ic n="check" s="w-7 h-7"/></div>
      <h3 style={{color:"#065f46",fontSize:"18px",fontWeight:"700",marginBottom:"8px"}}>Assignments Sent!</h3>
      <p style={{color:"#6b7280",fontSize:"13px",marginBottom:"10px"}}>Trainees will receive a unique link to <strong>{title}</strong>.</p>
      <p style={{color:"#9ca3af",fontSize:"11px",padding:"8px",background:"#f9fafb",borderRadius:"7px",marginBottom:"18px"}}>In production, emails are sent via Firebase Functions + SendGrid.</p>
      <button onClick={onClose} style={{...C.btn(C.pu),margin:"0 auto"}}>Done</button>
    </div>
  </div>;
  return <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:"16px",overflowY:"auto"}} onClick={onClose}>
    <div style={{background:"white",borderRadius:"16px",width:"100%",maxWidth:"540px",maxHeight:"90vh",display:"flex",flexDirection:"column",boxShadow:"0 24px 60px rgba(0,0,0,0.4)"}} onClick={e=>e.stopPropagation()}>
      <div style={{padding:"20px 24px",borderBottom:"1px solid #e5e7eb",display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0}}>
        <div><h2 style={{margin:"0 0 3px",fontSize:"16px",fontWeight:"700",color:C.dark}}>Assign Case to Trainees</h2><p style={{margin:0,fontSize:"12px",color:"#6b7280"}}>{title}</p></div>
        <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",color:"#6b7280"}}><Ic n="x" s="w-5 h-5"/></button>
      </div>
      <div style={{padding:"20px 24px",overflowY:"auto",flex:1}}>
        <div style={{marginBottom:"18px"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"9px"}}>
            <label style={C.lbl}>Trainees</label>
            <button onClick={addT} style={{...C.btn("transparent",C.pu,{border:`1px solid ${C.pu}`,padding:"4px 10px",fontSize:"12px"})}}><Ic n="plus" s="w-3 h-3"/> Add row</button>
          </div>
          <div style={{marginBottom:"9px"}}>
            <textarea
              placeholder="Paste multiple emails separated by commas or newlines&#10;e.g. jane@hospital.ca, john@uwo.ca"
              rows={2}
              style={{...C.inp,resize:"vertical",fontSize:"12px",color:"#374151"}}
              onBlur={e=>{
                const raw = e.target.value.trim();
                if(!raw) return;
                const emails = raw.split(/[\s,;]+/).map(s=>s.trim().toLowerCase()).filter(s=>s.includes('@'));
                if(!emails.length) return;
                setTrainees(prev=>{
                  const existing = prev.filter(t=>t.email);
                  const newOnes = emails
                    .filter(em=>!existing.find(t=>t.email===em))
                    .map(em=>({name:"",email:em}));
                  const merged = [...existing,...newOnes];
                  return merged.length ? merged : [{name:"",email:""}];
                });
                e.target.value="";
              }}
            />
            <div style={{fontSize:"10px",color:"#9ca3af",marginTop:"3px"}}>Paste emails above, or add rows one by one below</div>
          </div>
          {trainees.map((t,i)=><div key={i} style={{display:"flex",gap:"7px",marginBottom:"7px",alignItems:"center"}}>
            <input value={t.name} onChange={e=>updT(i,"name",e.target.value)} placeholder="Name (optional — trainee can set on sign-in)" style={{...C.inp,flex:1}}/>
            <input value={t.email} onChange={e=>updT(i,"email",e.target.value)} placeholder="email@hospital.edu" type="email" style={{...C.inp,flex:1.5}}/>
            {trainees.length>1&&<button onClick={()=>remT(i)} style={{background:"none",border:"none",cursor:"pointer",color:"#dc2626",padding:"3px",flexShrink:0}}><Ic n="trash" s="w-4 h-4"/></button>}
          </div>)}
        </div>
        <div style={{marginBottom:"16px"}}>
          <label style={{...C.lbl,marginBottom:"9px"}}>Feedback Timing</label>
          {[["immediate","Immediate","Trainee sees feedback right after submitting"],["delayed","After Date/Time","Feedback hidden until a specific date"],["hidden","Hidden — Manual Release","You manually release feedback for each trainee"]].map(([v,lb,hint])=><label key={v} style={{display:"flex",alignItems:"flex-start",gap:"9px",cursor:"pointer",padding:"8px 11px",borderRadius:"7px",border:`1px solid ${fm===v?C.pu:"#e5e7eb"}`,background:fm===v?"#faf5ff":"white",marginBottom:"5px"}}>
            <input type="radio" value={v} checked={fm===v} onChange={()=>setFm(v)} style={{marginTop:"2px"}}/>
            <div><div style={{fontWeight:"600",fontSize:"13px",color:C.dark}}>{lb}</div><div style={{fontSize:"11px",color:"#6b7280"}}>{hint}</div></div>
          </label>)}
          {fm==="delayed"&&<input type="datetime-local" value={fd} onChange={e=>setFd(e.target.value)} style={{...C.inp,marginTop:"7px"}}/>}
        </div>
        <div style={{marginBottom:"14px"}}>
          <label style={C.lbl}>Delivery Language</label>
          <select value={sendLocale} onChange={e=>setSendLocale(e.target.value)} style={C.sel}>
            {availLocales.map(c=><option key={c} value={c}>
              {LOCALES[c].native}{c===caseSourceLoc?" — original":" — translation"}
            </option>)}
          </select>
          <label style={{display:"flex",alignItems:"center",gap:"8px",marginTop:"8px",cursor:"pointer"}}>
            <input type="checkbox" checked={allowOverride} onChange={e=>setAllowOverride(e.target.checked)}/>
            <span style={{fontSize:"12px",color:"#6b7280"}}>Let the trainee switch language themselves</span>
          </label>
          {availLocales.length===1&&<div style={{fontSize:"11px",color:"#9ca3af",marginTop:"6px"}}>
            No translations exist for this case yet. Use the globe button in the case library to add one.
          </div>}
        </div>
        <div style={{marginBottom:"14px"}}><label style={C.lbl}>Deadline (Optional)</label><input type="datetime-local" value={dl} onChange={e=>setDl(e.target.value)} style={C.inp}/></div>
        <div><label style={C.lbl}>Personal Note (Optional)</label><textarea defaultValue={note} onBlur={e=>setNote(e.target.value)} rows={3} placeholder="e.g. This relates to our rounds discussion today…" style={{...C.inp,resize:"vertical"}}/></div>
        <div style={{marginTop:"16px"}}>
          <label style={C.lbl}>Allowed Attempts</label>
          <div style={{display:"flex",gap:"8px"}}>
            {[1,2,3].map(n=>(
              <button key={n} onClick={()=>setMaxAttempts(n)}
                style={{flex:1,padding:"9px",borderRadius:"8px",border:`1.5px solid ${maxAttempts===n?C.pu:"#e5e7eb"}`,
                  background:maxAttempts===n?"#faf5ff":"white",fontWeight:"700",fontSize:"13px",
                  color:maxAttempts===n?C.pu:"#374151",cursor:"pointer",fontFamily:"inherit"}}>
                {n===1?"1 (single)":`${n}`}
              </button>
            ))}
          </div>
          <div style={{fontSize:"11px",color:"#6b7280",marginTop:"5px"}}>
            {maxAttempts===1?"Trainee gets one attempt.":`Trainee may retry up to ${maxAttempts} times.`}
          </div>
        </div>
      </div>
      <div style={{padding:"13px 24px",borderTop:"1px solid #e5e7eb",display:"flex",justifyContent:"flex-end",gap:"9px",flexShrink:0}}>
        <button onClick={onClose} style={{...C.btn("#f3f4f6","#374151")}}>Cancel</button>
        <button onClick={doSend} disabled={busy||!ok} style={{...C.btn(C.pu),opacity:busy||!ok?0.6:1}}>{busy?<Ic n="spin" s="w-4 h-4"/>:<Ic n="send" s="w-4 h-4"/>}{busy?"Sending…":"Send Assignments"}</button>
      </div>
    </div>
  </div>;
}

// ─── FACET COLOUR PALETTE ─────────────────────────────────────────────────────
const FACET_COLORS = {
  "Clearance":              {bg:"#eff6ff",border:"#bfdbfe",accent:"#2563eb",pill:"#dbeafe",pillTx:"#1e40af"},
  "Hemodynamics":           {bg:"#fdf4ff",border:"#e9d5ff",accent:"#9333ea",pill:"#f3e8ff",pillTx:"#6b21a8"},
  "Anticoagulation":        {bg:"#fff7ed",border:"#fed7aa",accent:"#ea580c",pill:"#ffedd5",pillTx:"#9a3412"},
  "Electrolytes":           {bg:"#f0fdf4",border:"#bbf7d0",accent:"#16a34a",pill:"#dcfce7",pillTx:"#166534"},
  "Access/Circuit/Machine": {bg:"#fefce8",border:"#fde68a",accent:"#ca8a04",pill:"#fef9c3",pillTx:"#854d0e"},
  "General Prescribing":    {bg:"#f8fafc",border:"#e2e8f0",accent:"#64748b",pill:"#f1f5f9",pillTx:"#475569"},
};
const getFacetCol = (facet) => {
  if (!facet) return FACET_COLORS["General Prescribing"];
  const k = Object.keys(FACET_COLORS).find(k => facet.startsWith(k));
  return k ? FACET_COLORS[k] : FACET_COLORS["General Prescribing"];
};

// ─── CASES TAB ────────────────────────────────────────────────────────────────
// ─── LOCALE SWITCHER ──────────────────────────────────────────────────────────
// Header control. Reads enabled locales from /ui_locales; falls back to the
// static registry if those documents are missing so the picker never empties.
function LocaleSwitcher({compact=false}){
  const {locale,setLocale}=useLocale();
  const [enabled,setEnabled]=useState(null);

  useEffect(()=>{let dead=false;(async()=>{
    try{
      const qs=await getDocs(collection(db,"ui_locales"));
      const on=["en"];
      qs.forEach(d=>{ if(d.data().enabled!==false) on.push(d.id); });
      if(!dead) setEnabled(on.filter(c=>LOCALES[c]));
    }catch{ if(!dead) setEnabled(null); }
  })();return()=>{dead=true;};},[]);

  const codes=enabled&&enabled.length?enabled:LOCALE_CODES;

  return <select value={locale} onChange={e=>setLocale(e.target.value)}
    title="Interface and case language"
    style={{padding:compact?"3px 7px":"5px 10px",borderRadius:"6px",
      background:"rgba(255,255,255,0.12)",color:"white",
      border:"1px solid rgba(255,255,255,0.22)",cursor:"pointer",
      fontSize:"12px",fontFamily:"inherit",outline:"none"}}>
    {codes.map(c=><option key={c} value={c} style={{color:"#111"}}>
      {LOCALES[c]?.native||c}
    </option>)}
  </select>;
}

// ─── TRANSLATION DISCLAIMER ───────────────────────────────────────────────────
// Shown ONLY when the text being read is a translation, i.e. the display
// language differs from the language the case was authored in. A case written
// natively in French and read in French shows nothing, because it isn't a
// translation. Verified translations show a quiet checkmark instead.
function TranslationNote({tr,compact=false}){
  if(!tr||!tr.isTranslated) return null;
  const from=LOCALES[tr.sourceLocale]?.label||tr.sourceLocale||"English";
  const verified=tr.status==="verified";

  if(verified) return <div style={{display:"inline-flex",alignItems:"center",gap:"5px",
    fontSize:"11px",color:"#166534",background:"#dcfce7",padding:"3px 9px",borderRadius:"9px"}}>
    ✓ Verified translation from {from}
  </div>;

  return <div style={{display:"flex",gap:"8px",alignItems:"flex-start",
    padding:compact?"7px 10px":"10px 13px",background:"#fffbeb",
    border:"1px solid #fde68a",borderRadius:"8px",
    fontSize:compact?"11px":"12px",color:"#92400e",lineHeight:"1.5"}}>
    <span style={{flexShrink:0}}>🌐</span>
    <span>
      Translated from {from}
      {tr.translatorName?` by ${tr.translatorName}`:""}. This translation has not been
      independently verified for clinical accuracy. Refer to the {from} version if anything is unclear.
      {tr.stale?" The source has also been edited since this translation was made.":""}
    </span>
  </div>;
}

// ─── TRANSLATION EDITOR ───────────────────────────────────────────────────────
// Side-by-side English source (locked) against target language (editable).
// Only TRANSLATABLE_CASE_FIELDS are exposed. Structural fields are absent from
// this component entirely, which is what makes it impossible to alter scoring.
function TranslateCaseModal({caseId,caseData,educator,targetLocale,onClose,onSaved}){
  const [loc,setLoc]=useState(targetLocale&&targetLocale!=="en"?targetLocale:"fr");
  const [fields,setFields]=useState({title:"",page:"",successSummary:"",learningPoint:""});
  const [existing,setExisting]=useState(null);
  const [loading,setLoading]=useState(true);
  const [saving,setSaving]=useState(false);
  const [msg,setMsg]=useState("");

  // Load any existing translation whenever the target language changes
  useEffect(()=>{let dead=false;(async()=>{
    setLoading(true);setMsg("");
    const tr=await getCaseTranslation(caseId,loc);
    if(dead) return;
    setExisting(tr);
    setFields({
      title:          tr?.fields?.title          ||"",
      page:           tr?.fields?.page           ||"",
      successSummary: tr?.fields?.successSummary ||"",
      learningPoint:  tr?.fields?.learningPoint  ||"",
    });
    setLoading(false);
  })();return()=>{dead=true;};},[caseId,loc]);

  const currentHash=caseSourceHash(caseData);
  const isStale=existing?.sourceHash && existing.sourceHash!==currentHash;
  const isVerified=existing?.status==="verified";
  const rtl=isRTL(loc);

  const filled=TRANSLATABLE_CASE_FIELDS.filter(f=>(fields[f]||"").trim()).length;
  const sourceFilled=TRANSLATABLE_CASE_FIELDS.filter(f=>(caseData?.[f]||"").trim()).length;

  const doSave=async(status)=>{
    setSaving(true);setMsg("");
    try{
      await saveCaseTranslation({
        caseId, locale:loc, sourceCase:caseData, fields, status,
        translator:{email:educator.email,name:educator.name,institution:educator.institution||""},
      });
      setMsg("Translation saved. It is now visible to anyone browsing in this language.");
      onSaved&&onSaved(loc);
      const tr=await getCaseTranslation(caseId,loc); setExisting(tr);
    }catch(e){
      console.warn("[KiRPA] saveCaseTranslation",e);
      setMsg("Save failed. "+(e?.message||""));
    }
    setSaving(false);
  };

  const LABELS={
    title:"Case title",
    page:"Referral / page text",
    successSummary:"Success summary",
    learningPoint:"Learning point",
  };
  const HINTS={
    title:"Shown in the case library and on the trainee's assignment list.",
    page:"The clinical vignette the trainee reads. Preserve clinical meaning exactly; do not add or remove detail.",
    successSummary:"Answer key. Educator-facing and shown in feedback.",
    learningPoint:"Teaching message shown after the attempt.",
  };
  const MULTILINE={page:true,successSummary:true,learningPoint:true};

  return <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:220,
    display:"flex",alignItems:"center",justifyContent:"center",padding:"16px"}} onClick={onClose}>
    <div onClick={e=>e.stopPropagation()} style={{background:"white",borderRadius:"16px",
      width:"100%",maxWidth:"940px",maxHeight:"92vh",display:"flex",flexDirection:"column",
      boxShadow:"0 24px 60px rgba(0,0,0,0.4)",fontFamily:FONT_UI,overflow:"hidden"}}>

      {/* header */}
      <div style={{padding:"18px 22px",borderBottom:"1px solid #e5e7eb",display:"flex",
        alignItems:"center",gap:"12px",flexWrap:"wrap"}}>
        <div style={{flex:1,minWidth:"200px"}}>
          <h2 style={{margin:0,fontSize:"17px",fontWeight:"700",color:C.dark}}>Translate Case</h2>
          <div style={{fontSize:"12px",color:"#6b7280",marginTop:"2px"}}>{caseData?.title||caseId}</div>
        </div>
        <div>
          <label style={{...C.lbl,marginBottom:"3px"}}>Target language</label>
          <select value={loc} onChange={e=>setLoc(e.target.value)} style={{...C.sel,width:"auto",minWidth:"150px"}}>
            {LOCALE_CODES.filter(c=>c!=="en").map(c=>
              <option key={c} value={c}>{LOCALES[c].native} ({LOCALES[c].label})</option>)}
          </select>
        </div>
        <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",color:"#6b7280"}}>
          <Ic n="x" s="w-5 h-5"/>
        </button>
      </div>

      {/* status strip */}
      <div style={{padding:"10px 22px",background:"#faf7ff",borderBottom:"1px solid #ede9fe",
        display:"flex",gap:"10px",alignItems:"center",flexWrap:"wrap",fontSize:"12px"}}>
        <span style={C.tag(
          existing?.status==="verified"?"#dcfce7":existing?"#e0f2fe":"#f3f4f6",
          existing?.status==="verified"?"#166534":existing?"#075985":"#6b7280")}>
          {existing?(existing.status==="verified"?"verified":"saved"):"not started"}
        </span>
        <span style={{color:"#6b7280"}}>{filled} of {sourceFilled} source fields translated</span>
        {existing?.translatorName&&
          <span style={{color:"#9ca3af"}}>· by {existing.translatorName}</span>}
      </div>

      {isStale&&<div style={{padding:"10px 22px",background:"#fef3c7",borderBottom:"1px solid #fde68a",
        fontSize:"12px",color:"#92400e",display:"flex",gap:"8px",alignItems:"flex-start"}}>
        <span>⚠</span>
        <span>The English source has been edited since this translation was saved. Review the fields below against the source, then re-save to clear this flag.</span>
      </div>}

      {isVerified&&<div style={{padding:"10px 22px",background:"#ecfdf5",borderBottom:"1px solid #a7f3d0",
        fontSize:"12px",color:"#065f46"}}>
        This translation has been marked verified by a language lead. Saving changes will clear that mark.
      </div>}

      {/* body */}
      <div style={{flex:1,overflowY:"auto",padding:"18px 22px"}}>
        {loading
          ? <div style={{display:"flex",justifyContent:"center",padding:"40px"}}><Ic n="spin" s="w-6 h-6"/></div>
          : TRANSLATABLE_CASE_FIELDS.map(f=>{
              const src=caseData?.[f]||"";
              if(!src.trim()) return null;
              return <div key={f} style={{marginBottom:"20px"}}>
                <label style={{...C.lbl,marginBottom:"2px"}}>{LABELS[f]}</label>
                <div style={{fontSize:"11px",color:"#9ca3af",marginBottom:"7px"}}>{HINTS[f]}</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"12px"}}>
                  {/* English source — locked */}
                  <div>
                    <div style={{fontSize:"10px",fontWeight:"700",color:"#9ca3af",marginBottom:"4px",
                      textTransform:"uppercase",letterSpacing:"0.06em"}}>English source</div>
                    <div style={{padding:"9px 11px",background:"#f9fafb",border:"1px solid #e5e7eb",
                      borderRadius:"7px",fontSize:"13px",lineHeight:"1.5",color:"#374151",
                      whiteSpace:"pre-wrap",minHeight:MULTILINE[f]?"92px":"auto",fontFamily:FONT_CLIN}}>
                      {src}
                    </div>
                  </div>
                  {/* Target — editable */}
                  <div>
                    <div style={{fontSize:"10px",fontWeight:"700",color:C.pu,marginBottom:"4px",
                      textTransform:"uppercase",letterSpacing:"0.06em"}}>{LOCALES[loc].native}</div>
                    {MULTILINE[f]
                      ? <textarea dir={rtl?"rtl":"ltr"} value={fields[f]}
                          onChange={e=>setFields(p=>({...p,[f]:e.target.value}))}
                          placeholder={`${LOCALES[loc].label}…`}
                          style={{...C.inp,minHeight:"92px",resize:"vertical",lineHeight:"1.5",
                            fontFamily:FONT_CLIN,textAlign:rtl?"right":"left"}}/>
                      : <input dir={rtl?"rtl":"ltr"} value={fields[f]}
                          onChange={e=>setFields(p=>({...p,[f]:e.target.value}))}
                          placeholder={`${LOCALES[loc].label}…`}
                          style={{...C.inp,fontFamily:FONT_CLIN,textAlign:rtl?"right":"left"}}/>}
                  </div>
                </div>
              </div>;
            })}

        <div style={{padding:"12px 14px",background:"#f8f7ff",borderRadius:"9px",
          fontSize:"11px",color:"#6b7280",lineHeight:"1.6"}}>
          Leave a field blank to keep the English text for that field only. Partial translations are usable.
          Numbers, laboratory values, catheter and filter names (9F, ST60, HF20), solution names, and all
          scoring criteria are language-neutral and are never edited here.
        </div>
      </div>

      {/* footer */}
      <div style={{padding:"14px 22px",borderTop:"1px solid #e5e7eb",display:"flex",
        alignItems:"center",gap:"10px"}}>
        {msg&&<span style={{fontSize:"12px",color:msg.startsWith("Save failed")?"#dc2626":"#059669"}}>{msg}</span>}
        <div style={{marginLeft:"auto",display:"flex",gap:"9px"}}>
          <button onClick={onClose} style={C.btn("#f3f4f6","#374151")}>Close</button>
          <button onClick={()=>doSave("draft")} disabled={saving||loading||!filled}
            style={{...C.btn(C.pu),opacity:(saving||loading||!filled)?0.5:1}}>
            {saving?<Ic n="spin" s="w-4 h-4"/>:<Ic n="save" s="w-4 h-4"/>} Save Translation
          </button>
        </div>
      </div>
    </div>
  </div>;
}

function CasesTab({educator,myOverrides,sharedCases,onSaveMyCase,onSaveSharedCase,onSend,onDelete,favourites,onToggleFav,savedGroups,onAddToGroup}){
  const [libraryTab,setLibraryTab] = useState("all"); // "all"|"default"|"favs"
  const [facetFilter,setFacetFilter] = useState("All");
  const [search,setSearch] = useState("");
  const [viewing,setViewing] = useState(null);
  const [sending,setSending] = useState(null);
  const [creating,setCreating] = useState(false);
  const [expandedFacets,setExpandedFacets] = useState(new Set()); // open domain panels

  const baseMerged = Object.fromEntries(
    Object.entries(BASE_CASES).map(([id,c])=>{
      const ov = myOverrides[id]||{};
      // Deep-merge initialState so partial overrides (just labs, just vitals) don't wipe other fields
      const mergedInitialState = (ov.initialState || c.initialState)
        ? {
            ...(c.initialState||{}),
            ...(ov.initialState||{}),
            vitals: (ov.initialState?.vitals||c.initialState?.vitals)
              ? {...(c.initialState?.vitals||{}), ...(ov.initialState?.vitals||{})}
              : undefined,
            labs: (ov.initialState?.labs||c.initialState?.labs)
              ? {...(c.initialState?.labs||{}), ...(ov.initialState?.labs||{})}
              : undefined,
            gas: (ov.initialState?.gas||c.initialState?.gas)
              ? {...(c.initialState?.gas||{}), ...(ov.initialState?.gas||{})}
              : undefined,
            cbc: (ov.initialState?.cbc||c.initialState?.cbc)
              ? {...(c.initialState?.cbc||{}), ...(ov.initialState?.cbc||{})}
              : undefined,
          }
        : undefined;
      // Remove undefined sub-keys
      if (mergedInitialState) Object.keys(mergedInitialState).forEach(k=>mergedInitialState[k]===undefined&&delete mergedInitialState[k]);
      return [id, {
        ...c, ...ov,
        ...(mergedInitialState ? {initialState: mergedInitialState} : {}),
        _isBase:true
      }];
    })
  );
  const allCases = {...baseMerged,...sharedCases};
  const allIds   = Object.keys(allCases);

  // Facet counts across ALL cases (for filter chips)
  const facetCounts = {};
  allIds.forEach(id => {
    const f = allCases[id].facet || "";
    const k = Object.keys(FACET_COLORS).find(k => f.startsWith(k)) || "Other";
    facetCounts[k] = (facetCounts[k]||0) + 1;
  });

  const {locale:uiLocale}=useLocale();

  // ── Language filter ────────────────────────────────────────────────────────
  // "all" = show every case, displayed in the global interface language.
  // A locale code = show ONLY cases available in that language (either authored
  // in it or translated into it), and display them in that language.
  const [localeFilter,setLocaleFilter]=useState("all");
  const effLocale = localeFilter==="all" ? uiLocale : localeFilter;

  // All locales loaded at once so the language chips show real counts and
  // switching languages needs no refetch.
  const {byLocale,ready:trReady,refresh:refreshTranslations}=useAllCaseTranslations();
  const translations = byLocale[effLocale] || {};
  const [translating,setTranslating]=useState(null); // case id being translated
  // Which language a given case is currently being SHOWN in.
  //   filter = "all"  → the case's own source language, untranslated
  //   filter = a code → that language, if a translation exists
  // viewLocale overrides both, and is set by clicking a language chip on a tile.
  const [viewLocale,setViewLocale] = useState(null);

  const displayLocaleFor = (id) =>
    viewLocale || (localeFilter!=="all" ? localeFilter : (allCases[id]?.sourceLocale || "en"));

  const getDisplay = (id) => {
    const loc = displayLocaleFor(id);
    const src = allCases[id]?.sourceLocale || "en";
    if (loc === src) return allCases[id];          // original text, no overlay
    return localizeCase(id, allCases[id], byLocale[loc] || {}, {requestedLocale: loc});
  };

  // Every language this case can be read in: its source plus any translation.
  const localesForCase = (id) => {
    const src = allCases[id]?.sourceLocale || "en";
    const out = [src];
    LOCALE_CODES.forEach(c=>{
      if(c!==src && (byLocale[c]||{})[id]) out.push(c);
    });
    return out;
  };
  const coverage = effLocale!=="en" ? translationCoverage(allCases,translations) : null;

  // Is this case readable in the filtered language at all?
  const availableIn = (id, loc) => {
    if (loc==="all") return true;
    const src = allCases[id]?.sourceLocale || "en";
    return src===loc || !!(byLocale[loc]||{})[id];
  };

  // Counts per language for the filter chips.
  const localeCounts = (()=>{
    const out={all:allIds.length};
    LOCALE_CODES.forEach(c=>{
      const trs=byLocale[c]||{};
      out[c]=allIds.filter(id=>{
        const src=allCases[id]?.sourceLocale||"en";
        return src===c || !!trs[id];
      }).length;
    });
    return out;
  })();

  const q = search.toLowerCase().trim();

  // Filter by library tab first, then facet, then search
  const preFilter = allIds.filter(id => {
    if (!availableIn(id, localeFilter)) return false;   // language filter
    if (libraryTab==="default")  return !id.startsWith("custom_");
    if (libraryTab==="favs")     return favourites?.has(id);
    return true; // "all"
  });
  const displayed = preFilter.filter(id => {
    const c = allCases[id];
    const d = getDisplay(id);   // search the localised text as well as the source
    const facetOk = facetFilter==="All" || c.facet===facetFilter || c.facet?.startsWith(facetFilter);
    const searchOk = !q
      || c.title?.toLowerCase().includes(q)
      || d.title?.toLowerCase().includes(q)
      || c.facet?.toLowerCase().includes(q)
      || (c.tags||[]).some(t => t.toLowerCase().includes(q))
      || c.page?.toLowerCase().includes(q)
      || d.page?.toLowerCase().includes(q);
    return facetOk && searchOk;
  });

  const builtIn = displayed.filter(id => !id.startsWith("custom_"));
  const custom  = displayed.filter(id =>  id.startsWith("custom_"));

  // Group built-in cases by facet for the Default view
  const defaultGrouped = FACETS.map(facet => ({
    facet,
    ids: builtIn.filter(id => allCases[id].facet === facet),
  })).filter(g => g.ids.length > 0);

  const getEffective = (id) => allCases[id];

  // ── Translation layer ──────────────────────────────────────────────────────
  // getEffective stays CANONICAL ENGLISH and is what feeds the edit modal and
  // the assign flow. getDisplay is the localised view used for tiles and search
  // only. Keeping these separate is what stops a translated string being saved
  // back into an English override or snapshotted onto an assignment.
  const handleSave = (id,data) => {
    // Base case edits become "Save as Custom" — handled in CaseModal
    if (id.startsWith("custom_")) onSaveSharedCase(id,data);
    else onSaveMyCase(id,data);
    setViewing(null);
  };
  const handleSaveAsCustom = (sourceId, data) => {
    // Clone base case as a new custom case owned by this educator
    const newId = `custom_${Date.now()}_${Math.random().toString(36).slice(2,7)}`;
    onSaveSharedCase(newId, {
      ...data,
      creatorEmail: educator.email,
      creatorName:  educator.name,
      isShared:     false,
      createdAt:    new Date().toISOString(),
    });
    setViewing(null);
  };
  const handleCreate = async (id,data,shared) => onSaveSharedCase(id,data,shared);

  const [confirmDelete,setConfirmDelete] = useState(null); // id to delete

  // ── Single case card ──────────────────────────────────────
  const CaseCard = ({id}) => {
    const c = getDisplay(id);
    const tr = c._translation || null;
    const isCustom   = id.startsWith("custom_");
    const myOverride = !isCustom && !!myOverrides[id];
    const isMine     = isCustom && c.creatorEmail===educator.email;
    const col        = getFacetCol(c.facet);

    const pm  = c.prescribingMode||"";
    const rxc = c.initialPrescription||{};
    const ratesOk = rxc.modality==="CVVHD" ? rxc.dialysateRate>0
                  : rxc.modality==="CVVH"  ? (rxc.pbpRate>0||rxc.repRate>0)
                  : (rxc.pbpRate>0||rxc.dialysateRate>0||rxc.repRate>0);
    const canAssign = pm!=="modify" || (
      !!rxc.modality &&
      ["access","filter","prime","anticoag","solution","bfr"].every(f=>rxc[f]&&rxc[f]!=="") &&
      ratesOk
    );

    const isFav = favourites?.has(id);

    // 3-D depth: layered box-shadows + inner highlight + gradient background
    const baseCard = {
      background:`linear-gradient(160deg,${col.bg} 0%,white 100%)`,
      border:`1px solid ${myOverride||isMine?col.accent:col.border}`,
      borderBottom:`2px solid ${myOverride||isMine?col.accent:col.border}`,
      borderRadius:"14px",
      display:"flex",flexDirection:"column",overflow:"hidden",
      transition:"box-shadow 0.18s,transform 0.18s",
      boxShadow:`0 2px 0 rgba(255,255,255,0.9) inset,0 3px 8px rgba(0,0,0,0.09),0 1px 2px rgba(0,0,0,0.06)`,
      fontFamily:FONT_UI,
    };
    return (
      <div
        style={baseCard}
        onMouseEnter={e=>{
          e.currentTarget.style.boxShadow=`0 2px 0 rgba(255,255,255,0.9) inset,0 12px 28px rgba(0,0,0,0.13),0 4px 8px rgba(0,0,0,0.08)`;
          e.currentTarget.style.transform="translateY(-3px) scale(1.005)";
        }}
        onMouseLeave={e=>{
          e.currentTarget.style.boxShadow=baseCard.boxShadow;
          e.currentTarget.style.transform="";
        }}>

        {/* colour accent bar — slightly taller with gradient fade */}
        <div style={{height:"4px",background:`linear-gradient(90deg,${col.accent},${col.accent}99)`}}/>

        <div style={{padding:"13px 15px 10px",flex:1,display:"flex",flexDirection:"column",gap:"7px"}}>
          {/* badges row + heart */}
          <div style={{display:"flex",gap:"4px",flexWrap:"wrap",alignItems:"center"}}>
            <span style={{fontSize:"10px",fontWeight:"700",padding:"2px 7px",borderRadius:"9px",
              background:col.pill,color:col.pillTx,letterSpacing:"0.02em"}}>
              {c.facet||"General"}
            </span>
            {myOverride&&<span style={{fontSize:"10px",fontWeight:"600",padding:"2px 7px",borderRadius:"9px",background:"#ede9fe",color:"#7c3aed"}}>My edits</span>}
            {isMine&&<span style={{fontSize:"10px",fontWeight:"600",padding:"2px 7px",borderRadius:"9px",background:"#dbeafe",color:"#1e40af"}}>My case</span>}
            {isCustom&&!isMine&&<span style={{fontSize:"10px",fontWeight:"600",padding:"2px 7px",borderRadius:"9px",background:"#f3f4f6",color:"#6b7280"}}>Community</span>}
            {/* Languages this case can be read in. The one currently shown is
                filled; click another to switch just this case. */}
            {(()=>{
              const locs = localesForCase(id);
              if(locs.length<2) return null;
              const shown = displayLocaleFor(id);
              return <span style={{display:"inline-flex",gap:"3px",alignItems:"center"}}>
                {locs.map(lc=>{
                  const on = lc===shown;
                  const isSrc = lc===(c.sourceLocale||"en");
                  return <button key={lc}
                    onClick={e=>{e.stopPropagation();setViewLocale(on?null:lc);}}
                    title={isSrc?`Original language: ${LOCALES[lc]?.label||lc}`
                                :`Translated into ${LOCALES[lc]?.label||lc}`}
                    style={{fontSize:"9.5px",fontWeight:on?"700":"600",padding:"2px 6px",
                      borderRadius:"9px",cursor:"pointer",fontFamily:"inherit",
                      border:`1px solid ${on?C.pu:"#e5e7eb"}`,
                      background:on?C.pu:"white",color:on?"white":"#6b7280",
                      textTransform:"uppercase",letterSpacing:"0.04em"}}>
                    {lc}{isSrc?"":"*"}
                  </button>;
                })}
              </span>;
            })()}
            {tr&&tr.stale&&<span title="Source text changed after this translation was saved"
              style={{fontSize:"10px",fontWeight:"600",padding:"2px 7px",borderRadius:"9px",
                background:"#fef3c7",color:"#92400e"}}>⚠</span>}
            {/* Favourite heart — top-right of badge row */}
            <button
              onClick={e=>{e.stopPropagation();onToggleFav&&onToggleFav(id);}}
              title={isFav?"Remove from favourites":"Add to favourites"}
              style={{marginLeft:"auto",background:"none",border:"none",cursor:"pointer",
                padding:"2px",lineHeight:1,flexShrink:0,
                color:isFav?"#e11d48":"#d1d5db",transition:"color 0.15s,transform 0.15s"}}
              onMouseEnter={e=>{e.currentTarget.style.color=isFav?"#be123c":"#f43f5e";e.currentTarget.style.transform="scale(1.2)";}}
              onMouseLeave={e=>{e.currentTarget.style.color=isFav?"#e11d48":"#d1d5db";e.currentTarget.style.transform="";}}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill={isFav?"currentColor":"none"}
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
            </button>
          </div>

          {/* title */}
          <div style={{fontWeight:"700",fontSize:"13px",color:C.dark,lineHeight:"1.35",flex:1}}>
            {c.title}
          </div>
          {/* Author line for custom cases */}
          {isCustom&&c.creatorName&&(
            <div style={{fontSize:"10px",color:"#9ca3af",fontStyle:"italic",marginTop:"-3px"}}>
              by {c.creatorName}
            </div>
          )}

          {/* subject tags */}
          {c.tags&&c.tags.length>0&&(
            <div style={{display:"flex",gap:"4px",flexWrap:"wrap"}}>
              {c.tags.map(t=>(
                <span key={t} style={{fontSize:"10px",padding:"1px 6px",borderRadius:"8px",
                  background:"rgba(0,0,0,0.055)",color:"#4b5563"}}>{t}</span>
              ))}
            </div>
          )}

          {/* success summary — what the trainee must achieve */}
          {c.successSummary&&(
            <div style={{display:"flex",gap:"5px",alignItems:"flex-start"}}>
              <span style={{fontSize:"10px",flexShrink:0,marginTop:"1px"}}>🎯</span>
              <p style={{margin:0,fontSize:"11px",color:"#6b7280",lineHeight:"1.45",
                display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>
                {c.successSummary}
              </p>
            </div>
          )}
        </div>

        {/* action footer */}
        <div style={{padding:"9px 13px",borderTop:`1px solid ${col.border}`,
          display:"flex",gap:"6px",background:"rgba(255,255,255,0.55)"}}>
          <button onClick={()=>setViewing(id)}
            style={{flex:1,padding:"6px 0",borderRadius:"7px",
              border:`1px solid ${col.border}`,background:"white",
              color:"#374151",fontSize:"12px",fontWeight:"600",cursor:"pointer",
              display:"flex",alignItems:"center",justifyContent:"center",gap:"5px",fontFamily:"inherit"}}>
            <Ic n="eye" s="w-3 h-3"/> View / Edit
          </button>
          {/* Assign button */}
          <button
            onClick={()=>canAssign&&setSending(id)}
            title={canAssign?"Assign directly to trainee(s)":"Complete the prescription before assigning"}
            style={{flex:1,padding:"6px 0",borderRadius:"7px",border:"none",
              background:canAssign?col.accent:"#e5e7eb",
              color:canAssign?"white":"#9ca3af",
              fontSize:"12px",fontWeight:"600",
              cursor:canAssign?"pointer":"not-allowed",
              display:"flex",alignItems:"center",justifyContent:"center",gap:"5px",
              fontFamily:"inherit",opacity:canAssign?1:0.65}}>
            <Ic n="send" s="w-3 h-3"/> Assign{!canAssign&&" ⚠"}
          </button>
          {/* Add-to-group button — distinct teal colour, offset from Assign */}
          {canAssign&&Array.isArray(savedGroups)&&savedGroups.filter(g=>g.status==="pending").length>0&&(
            <button
              onClick={e=>{e.stopPropagation();onAddToGroup&&onAddToGroup(id);}}
              title="Add to a saved assignment group"
              style={{flexShrink:0,width:"28px",padding:"6px 0",borderRadius:"7px",
                border:"none",cursor:"pointer",
                background:"#0891b2",color:"white",
                fontSize:"13px",fontWeight:"700",
                display:"flex",alignItems:"center",justifyContent:"center",
                marginLeft:"2px"}}>
              ＋
            </button>
          )}
          <button
            onClick={e=>{e.stopPropagation();setTranslating(id);}}
            title="Translate this case into another language"
            style={{width:"32px",flexShrink:0,padding:"6px 0",borderRadius:"7px",
              border:`1px solid ${col.border}`,background:"white",color:"#6b7280",
              fontSize:"12px",cursor:"pointer",display:"flex",alignItems:"center",
              justifyContent:"center",fontFamily:"inherit",transition:"all 0.12s"}}
            onMouseEnter={e=>{e.currentTarget.style.background="#f8f7ff";e.currentTarget.style.color=C.pu;}}
            onMouseLeave={e=>{e.currentTarget.style.background="white";e.currentTarget.style.color="#6b7280";}}>
            <Ic n="globe" s="w-3 h-3"/>
          </button>
          {isMine&&(
            <button
              onClick={e=>{e.stopPropagation();setConfirmDelete(id);}}
              title="Delete this case"
              style={{width:"32px",flexShrink:0,padding:"6px 0",borderRadius:"7px",
                border:"1px solid #fecaca",background:"#fff5f5",color:"#dc2626",
                fontSize:"12px",cursor:"pointer",display:"flex",alignItems:"center",
                justifyContent:"center",fontFamily:"inherit",transition:"all 0.12s"}}
              onMouseEnter={e=>{e.currentTarget.style.background="#fee2e2";}}
              onMouseLeave={e=>{e.currentTarget.style.background="#fff5f5";}}>
              <Ic n="trash" s="w-3 h-3"/>
            </button>
          )}
        </div>
      </div>
    );
  };


  // ── Domain hero button config ─────────────────────────────
  const DOMAIN_CONFIG = {
    "Clearance":              { icon:"🔵", tagline:"Dose targeting, inborn errors of metabolism, protein-bound toxins",         emoji:"⚗️" },
    "Hemodynamics":           { icon:"🟣", tagline:"Fluid removal, bradykinin release, extracorporeal volume",   emoji:"💧" },
    "Anticoagulation":        { icon:"🟠", tagline:"Heparin, citrate, regional & systemic",       emoji:"🩸" },
    "Electrolytes":           { icon:"🟢", tagline:"Potassium, phosphate, dysnatremia",  emoji:"⚡" },
    "Access/Circuit/Machine": { icon:"🟡", tagline:"Access type, alarms, circuit troubleshooting",emoji:"🔧" },
    "General Prescribing":    { icon:"⚪", tagline:"Genral prescribing principles",             emoji:"📋" },
  };
  // ── Section header ────────────────────────────────────────
  const SectionHead = ({label,count}) => (
    <div style={{fontSize:"11px",fontWeight:"700",color:"#9ca3af",textTransform:"uppercase",
      letterSpacing:"0.08em",marginBottom:"10px",display:"flex",alignItems:"center",gap:"8px"}}>
      {label}
      <span style={{fontWeight:"600",padding:"1px 7px",borderRadius:"8px",
        background:"#f3f4f6",color:"#9ca3af",fontSize:"10px"}}>{count}</span>
    </div>
  );

  const filterActive = facetFilter!=="All" || !!q;

  return (
    <div style={{padding:"22px"}}>
      {creating&&<CreateCaseModal educator={educator} onSave={handleCreate} onClose={()=>setCreating(false)}/>}
      {viewing&&(()=>{
        const c=getEffective(viewing);
        const isBaseCase=!viewing.startsWith("custom_");
        const canEdit=true; // All cases editable; base cases save as custom override
        const _disp = getDisplay(viewing);
        // Labels follow the language of the TEXT being displayed: a translated
        // case shows the translation's language, an originally-French case
        // shows French, otherwise the interface language.
        const _dispLoc = displayLocaleFor(viewing);
        return <LocaleOverride locale={_dispLoc}><CaseModal caseId={viewing} caseData={c} displayData={_disp}
          editorEmail={educator.email}
          canEdit={canEdit} isBaseCase={isBaseCase}
          onSave={handleSave}
          onSaveAsCustom={(data)=>handleSaveAsCustom(viewing,data)}
          onClose={()=>setViewing(null)}/></LocaleOverride>;
      })()}
      {sending&&<SendModal caseId={sending} title={getEffective(sending).title} caseOptions={(getEffective(sending)||{}).caseOptions||null} caseData={getEffective(sending)||null} educator={educator}
        onSend={async a=>{await onSend(a);setSending(null);}} onClose={()=>setSending(null)}/>}

      {translating&&<TranslateCaseModal caseId={translating}
        caseData={getEffective(translating)} educator={educator}
        targetLocale={localeFilter!=="all"&&localeFilter!=="en"?localeFilter:(uiLocale!=="en"?uiLocale:null)}
        onSaved={(loc)=>{invalidateTranslationCache(loc);refreshTranslations();}}
        onClose={()=>setTranslating(null)}/>}

      {/* ── Translation coverage banner (only when viewing a non-English locale) ── */}
      {coverage&&trReady&&<div style={{marginBottom:"14px",padding:"11px 14px",
        background:"#f8f7ff",border:"1px solid #ede9fe",borderRadius:"10px",
        display:"flex",alignItems:"center",gap:"12px",flexWrap:"wrap",
        fontSize:"12px",fontFamily:FONT_UI}}>
        <Ic n="globe" s="w-4 h-4" style={{color:C.pu,flexShrink:0}}/>
        <span style={{color:C.dark,fontWeight:"600"}}>
          {LOCALES[uiLocale]?.native}: {coverage.translated} of {coverage.total} cases translated
        </span>
        <div style={{flex:1,minWidth:"90px",height:"6px",background:"#ede9fe",
          borderRadius:"3px",overflow:"hidden",maxWidth:"180px"}}>
          <div style={{width:`${coverage.pct}%`,height:"100%",background:C.pu}}/>
        </div>
        {coverage.verified>0&&<span style={C.tag("#dcfce7","#166534")}>{coverage.verified} verified</span>}
        {coverage.stale>0&&<span style={C.tag("#fef3c7","#92400e")}>{coverage.stale} need review</span>}
        <span style={{color:"#9ca3af",marginLeft:"auto"}}>
          Untranslated cases display in English.
        </span>
      </div>}

      {/* ── Language filter ── */}
      <div style={{display:"flex",gap:"6px",marginBottom:"12px",alignItems:"center",flexWrap:"wrap"}}>
        <span style={{fontSize:"11px",fontWeight:"700",color:"#9ca3af",textTransform:"uppercase",
          letterSpacing:"0.06em",marginRight:"2px"}}>Language</span>
        {["all",...LOCALE_CODES].map(c=>{
          const active=localeFilter===c;
          const pick=()=>{setLocaleFilter(c);setViewLocale(null);};
          const n=localeCounts[c]??0;
          if(c!=="all"&&c!=="en"&&n===0&&!active) return null;
          return <button key={c} onClick={pick}
            style={{padding:"4px 11px",borderRadius:"14px",
              border:`1.5px solid ${active?C.pu:"#e5e7eb"}`,
              background:active?C.pu:"white",color:active?"white":"#374151",
              fontSize:"11px",fontWeight:active?"700":"500",cursor:"pointer",
              fontFamily:"inherit",display:"flex",alignItems:"center",gap:"5px"}}>
            {c==="all"?"All languages":LOCALES[c].native}
            <span style={{fontSize:"10px",opacity:0.7}}>{n}</span>
          </button>;
        })}
        {localeFilter==="all"
          ? <span style={{fontSize:"11px",color:"#9ca3af",marginLeft:"4px"}}>
              Each case is shown in the language it was written in. Use the language
              tags on a case to read a translation (marked *).
            </span>
          : localeFilter!=="en" && (
            <span style={{fontSize:"11px",color:"#9ca3af",marginLeft:"4px"}}>
              Showing cases available in {LOCALES[localeFilter]?.label}. Open one to read it in {LOCALES[localeFilter]?.native}.
            </span>
          )}
      </div>

      {/* ── Library header with tab menu ── */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
        marginBottom:"14px",flexWrap:"wrap",gap:"10px"}}>
        <h2 style={{fontSize:"17px",fontWeight:"700",color:C.dark,margin:0,fontFamily:FONT_UI}}>
          Case Library
        </h2>
        <button onClick={()=>setCreating(true)} style={{...C.btn(C.pu)}}>
          <Ic n="plus" s="w-4 h-4"/> New Case
        </button>
      </div>

      {/* ── Library menu tabs ── */}
      <div style={{display:"flex",gap:"4px",marginBottom:"14px",
        background:"#f3f4f6",borderRadius:"11px",padding:"4px",width:"fit-content"}}>
        {[
          {id:"all",     label:"All Cases",     icon:"🗂"},
          {id:"default", label:"Default",       icon:"📋"},
          {id:"favs",    label:"My Favourites", icon:"❤️"},
        ].map(({id,label,icon})=>{
          const active = libraryTab===id;
          return (
            <button key={id} onClick={()=>{setLibraryTab(id);setFacetFilter("All");setSearch("");}}
              style={{padding:"6px 14px",borderRadius:"8px",border:"none",cursor:"pointer",
                fontWeight:"600",fontSize:"12px",fontFamily:"inherit",
                background:active?"white":"transparent",
                color:active?C.dark:"#6b7280",
                boxShadow:active?"0 1px 4px rgba(0,0,0,0.10)":"none",
                transition:"all 0.12s",display:"flex",alignItems:"center",gap:"5px"}}>
              <span style={{fontSize:"13px"}}>{icon}</span> {label}
            </button>
          );
        })}
      </div>

      {/* ── Search ── */}
      <div style={{position:"relative",marginBottom:"12px"}}>
        <svg style={{position:"absolute",left:"11px",top:"50%",transform:"translateY(-50%)",
          color:"#9ca3af",pointerEvents:"none",flexShrink:0}}
          width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35" strokeLinecap="round"/>
        </svg>
        <input value={search} onChange={e=>setSearch(e.target.value)}
          placeholder="Search by title, category, or tag…"
          style={{...C.inp,paddingLeft:"34px",paddingRight:search?"34px":"12px",
            borderRadius:"10px",fontSize:"13px"}}/>
        {search&&(
          <button onClick={()=>setSearch("")}
            style={{position:"absolute",right:"9px",top:"50%",transform:"translateY(-50%)",
              background:"none",border:"none",cursor:"pointer",color:"#9ca3af",padding:"2px",lineHeight:1}}>
            <Ic n="x" s="w-4 h-4"/>
          </button>
        )}
      </div>

      {/* ── Facet filter chips (favs view only — grouping handles the other views) ── */}
      {libraryTab==="favs"&&(
        <div style={{display:"flex",gap:"5px",flexWrap:"wrap",marginBottom:"22px"}}>
          {["All",...Object.keys(FACET_COLORS)].map(f=>{
            const count = f==="All" ? preFilter.length : preFilter.filter(id=>{
              const c=allCases[id]; return c.facet===f||c.facet?.startsWith(f);
            }).length;
            if (f!=="All" && count===0) return null;
            const active = facetFilter===f;
            const col    = f==="All" ? null : FACET_COLORS[f];
            return (
              <button key={f}
                onClick={()=>setFacetFilter(active&&f!=="All"?"All":f)}
                style={{padding:"5px 10px",borderRadius:"14px",
                  border:`1.5px solid ${active?(col?.accent||C.pu):"#e5e7eb"}`,
                  background:active?(col?.accent||C.pu):"white",
                  color:active?"white":"#6b7280",
                  fontSize:"12px",cursor:"pointer",fontWeight:"600",fontFamily:"inherit",
                  display:"flex",alignItems:"center",gap:"5px",transition:"all 0.1s"}}>
                {f==="All"?"All":f}
                <span style={{fontSize:"10px",padding:"0 5px",borderRadius:"8px",
                  background:active?"rgba(255,255,255,0.25)":"#f3f4f6",
                  color:active?"white":"#9ca3af",fontWeight:"700"}}>{count}</span>
              </button>
            );
          })}
        </div>
      )}
      {libraryTab!=="favs"&&<div style={{marginBottom:"14px"}}/>}

      {/* ── DEFAULT VIEW: domain hero buttons ── */}
      {libraryTab==="default"&&(
        <div style={{display:"flex",flexDirection:"column",gap:"10px"}}>
          <div style={{fontSize:"12px",color:"#6b7280",marginBottom:"4px",
            padding:"10px 14px",background:"#f8f7ff",borderRadius:"10px",
            border:"1px solid #ede9fe",display:"flex",alignItems:"center",gap:"8px"}}>
            <span style={{fontSize:"14px"}}>📋</span>
            <span>Cases created and refined by the development team.</span>
          </div>
          {defaultGrouped.map(({facet,ids})=>{
            const col  = getFacetCol(facet);
            const cfg  = DOMAIN_CONFIG[facet] || {};
            const open = expandedFacets.has(facet);
            const toggle = () => setExpandedFacets(prev=>{
              const next = new Set(prev);
              open ? next.delete(facet) : next.add(facet);
              return next;
            });
            return (
              <div key={facet} style={{borderRadius:"16px",overflow:"hidden",
                boxShadow: open
                  ? `0 8px 28px rgba(0,0,0,0.13),0 2px 6px rgba(0,0,0,0.07)`
                  : `0 2px 8px rgba(0,0,0,0.07),0 1px 2px rgba(0,0,0,0.04)`,
                border:`1.5px solid ${open?col.accent:col.border}`,
                transition:"box-shadow 0.2s,border-color 0.2s"}}>

                {/* ── Hero button ── */}
                <button onClick={toggle}
                  style={{width:"100%",padding:"0",border:"none",cursor:"pointer",
                    background: open
                      ? `linear-gradient(120deg,${col.accent} 0%,${col.accent}cc 100%)`
                      : `linear-gradient(120deg,${col.bg} 0%,white 100%)`,
                    display:"flex",alignItems:"center",gap:"16px",
                    fontFamily:FONT_UI,textAlign:"left",transition:"background 0.2s"}}>

                  {/* Left colour bar */}
                  <div style={{width:"5px",alignSelf:"stretch",
                    background: open ? "rgba(255,255,255,0.3)" : col.accent,
                    flexShrink:0}}/>

                  {/* Emoji badge */}
                  <div style={{width:"48px",height:"48px",borderRadius:"14px",flexShrink:0,
                    display:"flex",alignItems:"center",justifyContent:"center",fontSize:"22px",
                    background: open ? "rgba(255,255,255,0.18)" : col.pill,
                    boxShadow: open ? "0 2px 8px rgba(0,0,0,0.15)" : "0 1px 4px rgba(0,0,0,0.08)",
                    margin:"14px 0"}}>
                    {cfg.emoji||"📁"}
                  </div>

                  {/* Label only — no tagline */}
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:"15px",fontWeight:"800",letterSpacing:"-0.01em",
                      color: open ? "white" : C.dark,lineHeight:1.2}}>
                      {facet}
                    </div>
                  </div>

                  {/* Count badge + chevron */}
                  <div style={{display:"flex",alignItems:"center",gap:"10px",paddingRight:"18px",flexShrink:0}}>
                    <span style={{fontSize:"12px",fontWeight:"700",padding:"3px 10px",borderRadius:"20px",
                      background: open ? "rgba(255,255,255,0.22)" : col.pill,
                      color: open ? "white" : col.pillTx}}>
                      {ids.length} case{ids.length!==1?"s":""}
                    </span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                      stroke={open?"white":col.accent} strokeWidth="2.5"
                      strokeLinecap="round" strokeLinejoin="round"
                      style={{transition:"transform 0.22s",transform:open?"rotate(180deg)":"rotate(0deg)",flexShrink:0}}>
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                  </div>
                </button>

                {/* ── Expanded case grid ── */}
                {open&&(
                  <div style={{padding:"16px",
                    background:`linear-gradient(180deg,${col.bg} 0%,white 60%)`,
                    borderTop:`1px solid ${col.border}`}}>
                    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(255px,1fr))",gap:"12px"}}>
                      {ids.map(id=><CaseCard key={id} id={id}/>)}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── ALL CASES VIEW: grouped by facet, built-in then community within each ── */}
      {libraryTab==="all"&&(
        <div style={{display:"flex",flexDirection:"column",gap:"10px"}}>
          {FACETS.map(facet=>{
            const col = getFacetCol(facet);
            const cfg = DOMAIN_CONFIG[facet] || {};
            const facetBuiltIn  = displayed.filter(id=>!id.startsWith("custom_")&&allCases[id].facet===facet);
            const facetCustom   = displayed.filter(id=> id.startsWith("custom_")&&allCases[id].facet===facet);
            if(!facetBuiltIn.length && !facetCustom.length) return null;
            const open = expandedFacets.has(facet);
            const toggle = () => setExpandedFacets(prev=>{
              const next = new Set(prev);
              open ? next.delete(facet) : next.add(facet);
              return next;
            });
            const total = facetBuiltIn.length + facetCustom.length;
            return (
              <div key={facet} style={{borderRadius:"16px",overflow:"hidden",
                boxShadow: open
                  ? `0 8px 28px rgba(0,0,0,0.13),0 2px 6px rgba(0,0,0,0.07)`
                  : `0 2px 8px rgba(0,0,0,0.07),0 1px 2px rgba(0,0,0,0.04)`,
                border:`1.5px solid ${open?col.accent:col.border}`,
                transition:"box-shadow 0.2s,border-color 0.2s"}}>

                {/* ── Hero button ── */}
                <button onClick={toggle}
                  style={{width:"100%",padding:"0",border:"none",cursor:"pointer",
                    background: open
                      ? `linear-gradient(120deg,${col.accent} 0%,${col.accent}cc 100%)`
                      : `linear-gradient(120deg,${col.bg} 0%,white 100%)`,
                    display:"flex",alignItems:"center",gap:"16px",
                    fontFamily:FONT_UI,textAlign:"left",transition:"background 0.2s"}}>
                  <div style={{width:"5px",alignSelf:"stretch",
                    background: open ? "rgba(255,255,255,0.3)" : col.accent,flexShrink:0}}/>
                  <div style={{width:"48px",height:"48px",borderRadius:"14px",flexShrink:0,
                    display:"flex",alignItems:"center",justifyContent:"center",fontSize:"22px",
                    background: open ? "rgba(255,255,255,0.18)" : col.pill,
                    boxShadow: open ? "0 2px 8px rgba(0,0,0,0.15)" : "0 1px 4px rgba(0,0,0,0.08)",
                    margin:"14px 0"}}>
                    {cfg.emoji||"📁"}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:"15px",fontWeight:"800",letterSpacing:"-0.01em",
                      color: open ? "white" : C.dark,lineHeight:1.2}}>
                      {facet}
                    </div>
                    {facetCustom.length>0&&(
                      <div style={{fontSize:"11px",fontWeight:"500",marginTop:"3px",
                        color: open ? "rgba(255,255,255,0.7)" : "#9ca3af"}}>
                        {facetBuiltIn.length} built-in · {facetCustom.length} community
                      </div>
                    )}
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:"10px",paddingRight:"18px",flexShrink:0}}>
                    <span style={{fontSize:"12px",fontWeight:"700",padding:"3px 10px",borderRadius:"20px",
                      background: open ? "rgba(255,255,255,0.22)" : col.pill,
                      color: open ? "white" : col.pillTx}}>
                      {total} case{total!==1?"s":""}
                    </span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                      stroke={open?"white":col.accent} strokeWidth="2.5"
                      strokeLinecap="round" strokeLinejoin="round"
                      style={{transition:"transform 0.22s",transform:open?"rotate(180deg)":"rotate(0deg)",flexShrink:0}}>
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                  </div>
                </button>

                {/* ── Expanded: built-in section then community section ── */}
                {open&&(
                  <div style={{padding:"16px",
                    background:`linear-gradient(180deg,${col.bg} 0%,white 60%)`,
                    borderTop:`1px solid ${col.border}`}}>

                    {/* Built-in cases */}
                    {facetBuiltIn.length>0&&(
                      <div style={{marginBottom: facetCustom.length>0 ? "20px" : 0}}>
                        <div style={{display:"flex",alignItems:"baseline",gap:"8px",marginBottom:"10px"}}>
                          <span style={{fontSize:"11px",fontWeight:"700",color:col.accent,
                            textTransform:"uppercase",letterSpacing:"0.07em"}}>
                            Built-in
                          </span>
                          <span style={{fontSize:"11px",color:"#9ca3af",fontStyle:"italic"}}>
                            Cases created and refined by the development team
                          </span>
                          <span style={{marginLeft:"auto",fontSize:"10px",fontWeight:"700",
                            padding:"1px 7px",borderRadius:"8px",
                            background:col.pill,color:col.pillTx}}>
                            {facetBuiltIn.length}
                          </span>
                        </div>
                        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(255px,1fr))",gap:"12px"}}>
                          {facetBuiltIn.map(id=><CaseCard key={id} id={id}/>)}
                        </div>
                      </div>
                    )}

                    {/* Divider between built-in and community */}
                    {facetBuiltIn.length>0&&facetCustom.length>0&&(
                      <div style={{height:"1px",background:`${col.border}`,marginBottom:"20px"}}/>
                    )}

                    {/* Community cases */}
                    {facetCustom.length>0&&(
                      <div>
                        <div style={{display:"flex",alignItems:"baseline",gap:"8px",marginBottom:"10px"}}>
                          <span style={{fontSize:"11px",fontWeight:"700",color:"#6b7280",
                            textTransform:"uppercase",letterSpacing:"0.07em"}}>
                            Community
                          </span>
                          <span style={{marginLeft:"auto",fontSize:"10px",fontWeight:"700",
                            padding:"1px 7px",borderRadius:"8px",
                            background:"#f3f4f6",color:"#6b7280"}}>
                            {facetCustom.length}
                          </span>
                        </div>
                        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(255px,1fr))",gap:"12px"}}>
                          {facetCustom.map(id=><CaseCard key={id} id={id}/>)}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Community cases with no matching built-in facet (e.g. custom facets) */}
          {(()=>{
            const orphanCustom = displayed.filter(id=>
              id.startsWith("custom_") && !FACETS.includes(allCases[id].facet)
            );
            if(!orphanCustom.length) return null;
            return (
              <div style={{marginTop:"10px"}}>
                <SectionHead label="Community — Other" count={orphanCustom.length}/>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(255px,1fr))",gap:"12px"}}>
                  {orphanCustom.map(id=><CaseCard key={id} id={id}/>)}
                </div>
              </div>
            );
          })()}

          {/* Create new case CTA */}
          {!q&&(
            <button onClick={()=>setCreating(true)}
              style={{width:"100%",minHeight:"64px",borderRadius:"12px",border:"2px dashed #d1d5db",
                background:"white",cursor:"pointer",display:"flex",
                alignItems:"center",justifyContent:"center",gap:"10px",
                color:"#9ca3af",fontFamily:"inherit",fontSize:"12px",fontWeight:"600",
                transition:"all 0.15s"}}
              onMouseEnter={e=>{e.currentTarget.style.borderColor=C.pu;e.currentTarget.style.color=C.pu;e.currentTarget.style.background="#faf5ff";}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor="#d1d5db";e.currentTarget.style.color="#9ca3af";e.currentTarget.style.background="white";}}>
              <div style={{width:"28px",height:"28px",borderRadius:"50%",
                border:"2px dashed currentColor",display:"flex",alignItems:"center",justifyContent:"center"}}>
                <Ic n="plus" s="w-4 h-4"/>
              </div>
              Create New Case
            </button>
          )}
        </div>
      )}

      {/* ── FAVOURITES VIEW ── */}
      {libraryTab==="favs"&&(
        displayed.length===0 ? (
          <div style={{textAlign:"center",padding:"60px 24px"}}>
            <div style={{fontSize:"40px",marginBottom:"12px",opacity:0.3}}>❤️</div>
            <div style={{fontWeight:"700",fontSize:"15px",color:"#374151",marginBottom:"6px"}}>No favourites yet</div>
            <div style={{fontSize:"13px",color:"#9ca3af",marginBottom:"16px"}}>
              Click the ♡ on any case card to save it here.
            </div>
          </div>
        ) : (
          <div>
            {[{label:"Built-in",ids:builtIn},{label:"Community",ids:custom}].filter(s=>s.ids.length>0).map(({label,ids})=>(
              <div key={label} style={{marginBottom:"28px"}}>
                <SectionHead label={label} count={ids.length}/>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(255px,1fr))",gap:"12px"}}>
                  {ids.map(id=><CaseCard key={id} id={id}/>)}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* ── Delete confirmation dialog ── */}
      {confirmDelete&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:300,
          display:"flex",alignItems:"center",justifyContent:"center",padding:"16px"}}
          onClick={()=>setConfirmDelete(null)}>
          <div style={{background:"white",borderRadius:"14px",padding:"28px",
            maxWidth:"360px",width:"100%",boxShadow:"0 24px 60px rgba(0,0,0,0.4)"}}
            onClick={e=>e.stopPropagation()}>
            <div style={{width:"44px",height:"44px",background:"#fee2e2",borderRadius:"50%",
              display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 14px"}}>
              <Ic n="trash" s="w-5 h-5" style={{color:"#dc2626"}}/>
            </div>
            <h3 style={{textAlign:"center",fontSize:"15px",fontWeight:"700",color:C.dark,
              margin:"0 0 8px",fontFamily:FONT_UI}}>Delete this case?</h3>
            <p style={{textAlign:"center",fontSize:"13px",color:"#6b7280",margin:"0 0 20px",
              lineHeight:"1.5",fontFamily:FONT_UI}}>
              <strong>{allCases[confirmDelete]?.title}</strong> will be permanently removed.
              This cannot be undone.
            </p>
            <div style={{display:"flex",gap:"10px"}}>
              <button onClick={()=>setConfirmDelete(null)}
                style={{...C.btn("#f3f4f6","#374151"),flex:1,justifyContent:"center"}}>
                Cancel
              </button>
              <button onClick={()=>{onDelete(confirmDelete);setConfirmDelete(null);}}
                style={{...C.btn("#dc2626"),flex:1,justifyContent:"center"}}>
                <Ic n="trash" s="w-4 h-4"/> Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Search empty state (only shown for non-favs when search returns nothing) ── */}
      {displayed.length===0&&(q||facetFilter!=="All")&&libraryTab!=="favs"&&(
        <div style={{textAlign:"center",padding:"60px 24px"}}>
          <svg width="60" height="60" viewBox="0 0 60 60" fill="none"
            style={{margin:"0 auto 16px",display:"block",opacity:0.3}}>
            <circle cx="26" cy="26" r="16" stroke="#4F2683" strokeWidth="3"/>
            <path d="M38 38l9 9" stroke="#4F2683" strokeWidth="3" strokeLinecap="round"/>
            <path d="M20 26h12M26 20v12" stroke="#4F2683" strokeWidth="2.5" strokeLinecap="round"/>
          </svg>
          <div style={{fontWeight:"700",fontSize:"15px",color:"#374151",marginBottom:"6px"}}>No cases found</div>
          <div style={{fontSize:"13px",color:"#9ca3af",marginBottom:"16px"}}>
            {q ? `No cases match "${search}"` : "No cases in this category"}
          </div>
          <button onClick={()=>{setSearch("");setFacetFilter("All");}}
            style={{...C.btn("#f3f4f6","#374151")}}>Clear filters</button>
        </div>
      )}
    </div>
  );
}

// ─── DOMAIN PERFORMANCE CHART (SVG bar chart, no extra library) ──────────────
function DomainChart({assignments, roleFilter}){
  // Use the 5 protocol domains, mapped from caseFacet via DOMAIN_MAP.
  // BASE_CASES is not in scope here — rely entirely on caseFacet stored on the assignment.
  const filtered = roleFilter && roleFilter !== "All"
    ? assignments.filter(a => {
        const role = a.traineeRole || a.lastResult?.traineeRole || "";
        return role === roleFilter;
      })
    : assignments;
  const rows = PROTOCOL_DOMAINS.map(domainCode => {
    const label = DOMAIN_LABELS[domainCode];
    const relevant = filtered.filter(a => {
      const facet = a.caseFacet || '';
      return a.status === "completed" && (DOMAIN_MAP[facet] === domainCode);
    });
    const total = relevant.length;
    const pass  = relevant.filter(a => (a.lastResult||a.result)?.success).length;
    const pct   = total > 0 ? Math.round(pass / total * 100) : null;
    return { domain: label, total, pass, pct };
  }).filter(r => r.total > 0);

  if(rows.length===0) return <div style={{fontSize:"12px",color:"#9ca3af",textAlign:"center",padding:"16px"}}>No completed cases yet to chart.</div>;

  const BAR_H=28;const BAR_GAP=10;const LABEL_W=185;const CHART_W=265;const SVG_H=rows.length*(BAR_H+BAR_GAP)+BAR_GAP;
  return <svg width="100%" viewBox={`0 0 ${LABEL_W+CHART_W+60} ${SVG_H}`} style={{fontFamily:"Georgia,serif",overflow:"visible"}}>
    {rows.map((r,i)=>{
      const y=BAR_GAP+i*(BAR_H+BAR_GAP);
      const barW=r.pct!==null?(r.pct/100)*CHART_W:0;
      const col=r.pct>=70?"#059669":r.pct>=40?"#d97706":"#dc2626";
      return <g key={r.domain}>
        <text x={LABEL_W-8} y={y+BAR_H/2+5} textAnchor="end" fontSize="12" fill="#374151">{r.domain}</text>
        <rect x={LABEL_W} y={y} width={CHART_W} height={BAR_H} rx="5" fill="#f3f4f6"/>
        {barW>0&&<rect x={LABEL_W} y={y} width={barW} height={BAR_H} rx="5" fill={col}/>}
        <text x={LABEL_W+barW+7} y={y+BAR_H/2+5} fontSize="12" fontWeight="700" fill={col}>{r.pct!==null?`${r.pct}%`:"—"}</text>
        <text x={LABEL_W+CHART_W+12} y={y+BAR_H/2+5} fontSize="10" fill="#9ca3af">{r.pass}/{r.total}</text>
      </g>;
    })}
    <text x={LABEL_W} y={SVG_H-2} fontSize="10" fill="#9ca3af">0%</text>
    <text x={LABEL_W+CHART_W/2} y={SVG_H-2} textAnchor="middle" fontSize="10" fill="#9ca3af">50%</text>
    <text x={LABEL_W+CHART_W} y={SVG_H-2} textAnchor="end" fontSize="10" fill="#9ca3af">100%</text>
  </svg>;
}

// ─── SUBMISSION VIEW — shows a trainee's prescription submission ──────────────
function SubmissionCard({submission, caseInfo}){
  if(!submission) return <div style={{fontSize:"12px",color:"#9ca3af",fontStyle:"italic"}}>No submission data recorded.</div>;
  // Support both flat format {modality,bfr,...} and nested {prescription:{...}}
  const rx = submission.rx || submission.prescription || submission || {};

  // Derived metrics for display
  const pbp  = parseFloat(rx.pbpRate)       || 0;
  const dial = parseFloat(rx.dialysateRate) || 0;
  const rep  = parseFloat(rx.repRate)       || 0;
  const bsa  = caseInfo?.initialState?.bsa  || 1;
  const wt   = caseInfo?.initialState?.weightKg || 1;
  const ageM = caseInfo?.initialState?.ageMonths;
  const totalLPH    = (pbp + dial + rep) / 1000;
  const clearanceBSA = bsa > 0 ? ((totalLPH / bsa) * 1.73).toFixed(2) : '—';
  const clearanceKg  = wt  > 0 ? ((totalLPH * 1000) / wt).toFixed(1)  : '—';
  const derivedMod   = rx.modality || deriveModality(pbp, dial, rep);
  const ecv   = resolveFilterEcv(rx.filter, caseInfo?.options);
  const tbv   = (wt && ageM !== null && ageM !== undefined) ? calculateTBV(wt, ageM) : null;
  const ecvPct = (ecv && tbv) ? ((ecv / tbv) * 100).toFixed(1) : null;

  const fields=[
    ["Modality", derivedMod||"—"],
    ["Access", rx.access||"—"],
    ["Filter", rx.filter||"—"],
    ["Prime", rx.prime||"—"],
    ["Anticoagulation", rx.anticoag||"—"],
    ["Solution", rx.solution||"—"],
    ["BFR (ml/min)", rx.bfr!=null?rx.bfr:"—"],
    ["Pre-BP Rate (ml/hr)", rx.pbpRate!=null?rx.pbpRate:"—"],
    ["Dialysate Rate (ml/hr)", rx.dialysateRate!=null?rx.dialysateRate:"—"],
    ["Post-Filter Rate (ml/hr)", rx.repRate!=null?rx.repRate:"—"],
    ["Net UF (ml/hr)", rx.netUf!=null?rx.netUf:"—"],
  ].filter(([,v])=>v!=="—");
  const interventions=submission.actions||submission.interventions||rx.interventions||[];

  // Electrolyte replacement is a binary action rather than a dosed field, and
  // the route taken is worth showing explicitly on review.
  const _kRoute   = interventions.includes("add_k_bag")   ? "Yes — in solution"
                  : interventions.includes("supp_k")     ? "Yes — supplement" : "No";
  const _po4Route = interventions.includes("add_po4_bag") ? "Yes — in solution"
                  : interventions.includes("supp_po4")   ? "Yes — supplement" : "No";
  fields.push(["Potassium replaced", _kRoute], ["Phosphate replaced", _po4Route]);

  return <div>
    {/* Derived metrics banner */}
    {(pbp + dial + rep) > 0 && <div style={{display:"flex",gap:"12px",flexWrap:"wrap",marginBottom:"9px",padding:"8px 12px",background:"#eef2ff",borderRadius:"7px",fontSize:"11px"}}>
      <span style={{color:"#4338ca",fontWeight:"700"}}>{derivedMod}</span>
      <span style={{color:"#374151"}}>Clearance: <strong>{clearanceBSA} L/1.73m²/hr</strong></span>
      <span style={{color:"#374151"}}>({clearanceKg} ml/kg/hr)</span>
      {ecvPct && <span style={{color:"#374151"}}>ECV: <strong>{ecvPct}%</strong> TBV</span>}
    </div>}
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"6px 14px"}}>
      {fields.map(([k,v])=><div key={k} style={{display:"flex",justifyContent:"space-between",padding:"5px 8px",background:"#f9fafb",borderRadius:"5px",fontSize:"12px"}}>
        <span style={{color:"#6b7280"}}>{k}</span>
        <span style={{fontWeight:"600",color:C.dark}}>{String(v)}</span>
      </div>)}
      {interventions.length>0&&<div style={{gridColumn:"1/-1",marginTop:"4px"}}>
        <span style={{fontSize:"11px",fontWeight:"700",color:"#374151"}}>Clinical Actions: </span>
        {interventions.map(i=><span key={i} style={{...C.tag("#ede9fe","#6d28d9"),marginRight:"4px"}}>{i}</span>)}
      </div>}
    </div>
  </div>;
}

// ─── ADD TRAINEE MODAL ────────────────────────────────────────────────────────
function AddTraineeModal({name,email,saving,error,onName,onEmail,onSave,onClose}){
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:9000,
      display:"flex",alignItems:"center",justifyContent:"center",padding:"20px"}}>
      <div style={{background:"white",borderRadius:"14px",width:"100%",maxWidth:"420px",
        boxShadow:"0 20px 60px rgba(0,0,0,0.25)",overflow:"hidden"}}>
        <div style={{background:C.pu,padding:"18px 22px",display:"flex",alignItems:"center",
          justifyContent:"space-between"}}>
          <div style={{color:"white",fontWeight:"700",fontSize:"15px",fontFamily:FONT_UI}}>Add Trainee</div>
          <button onClick={onClose}
            style={{background:"rgba(255,255,255,0.15)",border:"none",color:"white",
              width:"28px",height:"28px",borderRadius:"50%",cursor:"pointer",fontSize:"16px",
              display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"inherit"}}>×</button>
        </div>
        <div style={{padding:"22px",fontFamily:FONT_UI}}>
          <p style={{margin:"0 0 18px",fontSize:"13px",color:"#6b7280",lineHeight:"1.5"}}>
            Add a trainee to your roster by email. They'll appear in your Trainees tab and
            you can assign cases to them at any time.
          </p>
          <div style={{marginBottom:"14px"}}>
            <label style={{display:"block",fontSize:"12px",fontWeight:"600",color:"#374151",marginBottom:"5px"}}>
              Name <span style={{fontWeight:"400",color:"#9ca3af"}}>(optional)</span>
            </label>
            <input value={name} onChange={e=>onName(e.target.value)}
              placeholder="e.g. Dr. Jane Smith"
              style={{...C.inp,width:"100%",boxSizing:"border-box"}}/>
          </div>
          <div style={{marginBottom:"18px"}}>
            <label style={{display:"block",fontSize:"12px",fontWeight:"600",color:"#374151",marginBottom:"5px"}}>
              Email <span style={{color:"#dc2626"}}>*</span>
            </label>
            <input value={email} onChange={e=>onEmail(e.target.value)}
              placeholder="trainee@hospital.edu" type="email"
              style={{...C.inp,width:"100%",boxSizing:"border-box"}}
              onKeyDown={e=>e.key==="Enter"&&onSave()}/>
          </div>
          {error && <div style={{background:"#fef2f2",border:"1px solid #fecaca",borderRadius:"8px",
            padding:"10px 14px",fontSize:"12px",color:"#dc2626",marginBottom:"14px"}}>{error}</div>}
          <div style={{display:"flex",gap:"10px",justifyContent:"flex-end"}}>
            <button onClick={onClose}
              style={{...C.btn("transparent","#6b7280",{border:"1px solid #d1d5db"}),
                padding:"9px 18px",fontSize:"13px"}}>
              Cancel
            </button>
            <button onClick={onSave} disabled={saving}
              style={{...C.btn(C.pu,"white"),padding:"9px 20px",fontSize:"13px",
                opacity:saving?0.6:1,cursor:saving?"not-allowed":"pointer"}}>
              {saving?"Adding…":"Add Trainee"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── TRAINEES TAB ─────────────────────────────────────────────────────────────
function TraineesTab({assignments, onRelease, onRefresh, educator, traineeRoster=[], dismissedTrainees=new Set(), onAddTrainee, onRemoveTrainee}) {
  const [subTab, setSubTab]       = useState("trainees"); // "trainees" | "mycases"
  const [selKey, setSelKey]       = useState(null);       // drilled-in trainee key
  const [chartRoleFilter, setChartRoleFilter] = useState("All"); // chart role filter
  const [search, setSearch]       = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [caseStats, setCaseStats] = useState({});
  const [statsLoading, setStatsLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addName, setAddName]     = useState("");
  const [addEmail, setAddEmail]   = useState("");
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError]   = useState("");

  // ── Derived data ────────────────────────────────────────────────────────────
  const allCompleted  = assignments.filter(a => a.status === "completed");
  const totalSent     = assignments.length;
  const totalDone     = allCompleted.length;
  const totalPassed   = allCompleted.filter(a => (a.lastResult||a.result)?.success).length;
  const completionPct = totalSent ? Math.round(totalDone / totalSent * 100) : 0;
  const successPct    = totalDone ? Math.round(totalPassed / totalDone * 100) : null;

  // ── Build trainee map from assignments ──────────────────────────────────────
  // Display name rule: educator's stored traineeName if set, otherwise trainee's self-reported name.
  const byT = {};
  assignments.forEach(a => {
    const key = a.isAnonymous ? (a.anonymousId || `anon_${a.traineeName}`) : (a.traineeEmail || a.id);
    if (!byT[key]) byT[key] = {
      key,
      name: a.traineeName || a.traineeProfileName || a.traineeEmail || key,
      email: a.isAnonymous ? null : a.traineeEmail,
      role: "",
      isAnonymous: a.isAnonymous || false,
      list: [],
      fromRoster: false,
    };
    byT[key].list.push(a);
    if (!byT[key].role && (a.traineeRole || a.lastResult?.traineeRole)) {
      byT[key].role = a.traineeRole || a.lastResult?.traineeRole || "";
    }
    if (!a.traineeName && (a.traineeProfileName || a.lastResult?.traineeProfileName)) {
      byT[key].name = a.traineeProfileName || a.lastResult?.traineeProfileName || byT[key].name;
    }
  });

  // ── Merge roster entries — roster trainees with no assignments still appear ─
  traineeRoster.forEach(r => {
    const key = (r.email||"").toLowerCase().trim();
    if(!key || !key.includes("@")) return;
    if(!byT[key]){
      byT[key] = {
        key, name: r.name||key, email: key,
        role:"", isAnonymous:false, list:[], fromRoster:true,
      };
    } else {
      byT[key].fromRoster = true;
      // Prefer roster name if no assignment-derived name yet
      if(!byT[key].name || byT[key].name===key) byT[key].name = r.name||key;
    }
  });

  const rosterEmails = new Set(traineeRoster.map(r=>(r.email||"").toLowerCase().trim()));

  // ── Filter out dismissed trainees ───────────────────────────────────────────
  Object.keys(byT).forEach(key => {
    const em = (byT[key].email||"").toLowerCase();
    if(em && dismissedTrainees.has(em)) delete byT[key];
  });

  const allRoles = [...new Set(Object.values(byT).map(t => t.role).filter(Boolean))].sort();
  const chartRoleOptions = ["All", ...allRoles];

  // Assignments for domain chart (all or selected trainee)
  const chartAssignments = selKey ? (byT[selKey]?.list || []) : assignments;

  const hasAnyTrainees = Object.keys(byT).length > 0;

  // ── Load global case stats when switching to My Cases tab ───────────────────
  useEffect(() => {
    if (subTab !== "mycases" || !educator?.email) return;
    setStatsLoading(true);
    const myCaseIds = [...new Set(assignments.map(a => a.caseId).filter(Boolean))];
    Promise.all(myCaseIds.map(async caseId => {
      try {
        const safeId = caseId.replace(/[^a-zA-Z0-9_-]/g, "_");
        const snap = await getDoc(doc(db, "shared", `case_stats_${safeId}`));
        if (snap.exists()) return { caseId, ...snap.data() };
        return null;
      } catch { return null; }
    })).then(results => {
      const m = {};
      results.filter(Boolean).forEach(r => { m[r.caseId] = r; });
      setCaseStats(m);
      setStatsLoading(false);
    });
  }, [subTab, educator?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Add Trainee modal submit ─────────────────────────────────────────────────
  const handleAddTrainee = async () => {
    const em = addEmail.trim().toLowerCase();
    if(!em.match(/^[^@]+@[^@]+\.[^@]+$/)){
      setAddError("Please enter a valid email address."); return;
    }
    setAddSaving(true); setAddError("");
    try {
      await onAddTrainee(em, addName.trim());
      setAddName(""); setAddEmail(""); setShowAddModal(false);
    } catch(e) {
      setAddError(e?.message||"Failed to add trainee. Please try again.");
    }
    setAddSaving(false);
  };

  if (!hasAnyTrainees) return (
    <div style={{padding:"22px",textAlign:"center"}}>
      <svg width="96" height="80" viewBox="0 0 96 80" fill="none" style={{margin:"0 auto 20px",display:"block"}}>
        <rect x="8" y="58" width="80" height="6" rx="3" fill="#e5e7eb"/>
        <rect x="18" y="64" width="6" height="12" rx="2" fill="#d1d5db"/>
        <rect x="72" y="64" width="6" height="12" rx="2" fill="#d1d5db"/>
        <rect x="28" y="28" width="40" height="28" rx="4" fill="#4F2683" opacity="0.15"/>
        <rect x="31" y="31" width="34" height="20" rx="2" fill="white"/>
        <rect x="44" y="56" width="8" height="4" rx="1" fill="#d1d5db"/>
        <circle cx="48" cy="12" r="7" fill="#4F2683" opacity="0.25"/>
        <path d="M36 30c0-6.627 5.373-12 12-12s12 5.373 12 12" fill="#4F2683" opacity="0.15"/>
        <rect x="35" y="35" width="22" height="2" rx="1" fill="#4F2683" opacity="0.3"/>
        <rect x="35" y="40" width="14" height="2" rx="1" fill="#4F2683" opacity="0.2"/>
      </svg>
      <div style={{fontWeight:"700",fontSize:"15px",color:"#374151",marginBottom:"6px"}}>No trainees yet</div>
      <p style={{fontSize:"13px",color:"#9ca3af",margin:"0 0 18px"}}>Assign a case or add a trainee manually to get started.</p>
      <button onClick={()=>setShowAddModal(true)}
        style={{...C.btn(C.pu,"white"),padding:"9px 20px",fontSize:"13px",display:"inline-flex",alignItems:"center",gap:"6px"}}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        Add Trainee
      </button>
      {showAddModal && <AddTraineeModal
        name={addName} email={addEmail} saving={addSaving} error={addError}
        onName={setAddName} onEmail={setAddEmail}
        onSave={handleAddTrainee} onClose={()=>{setShowAddModal(false);setAddName("");setAddEmail("");setAddError("");}}
      />}
    </div>
  );

  // ── CaseRow used in trainee detail view ────────────────────────────────────
  const CaseRow = ({a}) => {
    const r = a.lastResult||a.result||null;
    const isExp = expandedId===a.id;
    return (
      <div key={a.id} style={{...C.card,padding:"13px 17px",marginBottom:"8px",
        border:`1px solid ${a.status==="completed"?(r?.success?"#bbf7d0":"#fecaca"):"#e5e7eb"}`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:"8px",flexWrap:"wrap"}}>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontWeight:"600",fontSize:"13px",color:C.dark}}>{a.caseTitle}</div>
            <div style={{fontSize:"11px",color:"#9ca3af",marginTop:"2px"}}>
              {a.caseFacet&&<span>{a.caseFacet} · </span>}
              Sent {new Date(a.sentAt).toLocaleDateString()}
            </div>
          </div>
          <span style={C.tag(
            a.status==="completed"?(r?.success?"#d1fae5":"#fee2e2"):"#fef3c7",
            a.status==="completed"?(r?.success?"#065f46":"#991b1b"):"#92400e")}>
            {a.status==="completed"?(r?.success?"✓ Passed":"✗ Failed"):"Pending"}
          </span>
        </div>
        {r&&<div style={{display:"flex",gap:"10px",marginTop:"8px",padding:"6px 10px",
          background:"#f9fafb",borderRadius:"6px",fontSize:"12px",alignItems:"center",flexWrap:"wrap"}}>
          <span>Attempts: <strong>{r.attempts||1}</strong></span>
          {r.submission&&<button onClick={()=>setExpandedId(isExp?null:a.id)}
            style={{...C.btn(isExp?"#e5e7eb":C.pu,isExp?"#374151":"white"),fontSize:"11px",padding:"3px 10px",marginLeft:"auto"}}>
            {isExp?"Hide":"View submission"}
          </button>}
          {a.feedbackMode!=="immediate"&&a.status==="completed"&&
            <button onClick={()=>onRelease(a.id)}
              style={{...C.btn(C.pu),fontSize:"11px",padding:"3px 10px"}}>Release Feedback</button>}
        </div>}
        {isExp&&r?.submission&&(
          <div style={{marginTop:"10px",padding:"12px",background:"#f5f3ff",borderRadius:"8px",border:"1px solid #ede9fe"}}>
            <SubmissionCard submission={r.submission} caseInfo={BASE_CASES[a.caseId]||null}/>
            {r.feedback&&<div style={{marginTop:"8px",padding:"9px 12px",background:"white",
              borderRadius:"6px",border:"1px solid #ede9fe",fontSize:"12px",color:"#374151"}}>
              <span style={{fontWeight:"700",color:C.pu}}>Feedback: </span>{r.feedback}
            </div>}
          </div>
        )}
      </div>
    );
  };

  const SectionToggle = ({label, color, bg, items, defaultOpen=false}) => {
    const [open,setOpen] = useState(defaultOpen);
    return (
      <div style={{marginBottom:"14px"}}>
        <button onClick={()=>setOpen(o=>!o)}
          style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",
            padding:"9px 14px",borderRadius:"10px",border:`1px solid ${color}33`,
            background:bg,cursor:"pointer",fontFamily:"inherit",marginBottom:open?"8px":"0",
            transition:"margin 0.15s"}}>
          <div style={{display:"flex",alignItems:"center",gap:"9px"}}>
            <span style={{fontWeight:"700",fontSize:"13px",color}}>{label}</span>
            <span style={{fontSize:"11px",fontWeight:"700",padding:"2px 8px",borderRadius:"20px",
              background:`${color}22`,color}}>{items.length}</span>
          </div>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color}
            strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
            style={{transition:"transform 0.2s",transform:open?"rotate(180deg)":"rotate(0deg)"}}>
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>
        {open && items.map(a=><CaseRow key={a.id} a={a}/>)}
      </div>
    );
  };

  return (
    <div style={{padding:"22px"}}>

      {/* ── Global stats header ─────────────────────────────────────────────── */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(110px,1fr))",gap:"10px",marginBottom:"20px"}}>
        {[
          {label:"Cases Sent",     value:totalSent,         color:C.pu,       bg:"#f5f0ff"},
          {label:"Completed",      value:`${completionPct}%`,color:"#059669",  bg:"#f0fdf4"},
          {label:"Passed",         value:successPct!=null?`${successPct}%`:"—", color:successPct!=null?(successPct>=70?"#059669":successPct>=40?"#d97706":"#dc2626"):"#9ca3af", bg:successPct!=null?(successPct>=70?"#f0fdf4":successPct>=40?"#fffbeb":"#fff1f2"):"#f9fafb"},
          {label:"Trainees",       value:Object.keys(byT).length, color:"#0369a1", bg:"#f0f9ff"},
        ].map(({label,value,color,bg})=>(
          <div key={label} style={{background:bg,borderRadius:"10px",padding:"14px 16px",border:`1px solid ${color}22`}}>
            <div style={{fontSize:"22px",fontWeight:"800",color,lineHeight:1}}>{value}</div>
            <div style={{fontSize:"11px",color:"#6b7280",marginTop:"4px",fontWeight:"500"}}>{label}</div>
          </div>
        ))}
      </div>

      {/* ── Sub-tab bar ─────────────────────────────────────────────────────── */}
      <div style={{display:"flex",gap:"6px",marginBottom:"18px",borderBottom:"1px solid #e5e7eb",paddingBottom:"10px",alignItems:"center",flexWrap:"wrap"}}>
        {[["trainees","My Trainees"],["mycases","My Cases"]].map(([id,lb])=>(
          <button key={id} onClick={()=>{setSubTab(id);setSelKey(null);}}
            style={{padding:"7px 18px",borderRadius:"8px",border:"none",cursor:"pointer",fontWeight:"700",
              fontSize:"13px",background:subTab===id?C.pu:"#f3f4f6",
              color:subTab===id?"white":"#6b7280",fontFamily:"inherit"}}>
            {lb}
          </button>
        ))}
        <div style={{display:"flex",gap:"6px",marginLeft:"auto",alignItems:"center"}}>
          {subTab==="trainees"&&!selKey&&(
            <button onClick={()=>{setShowAddModal(true);setAddError("");}}
              style={{padding:"5px 12px",borderRadius:"8px",border:"none",
                background:C.pu,color:"white",cursor:"pointer",fontSize:"12px",
                fontFamily:"inherit",display:"flex",alignItems:"center",gap:"5px",fontWeight:"600"}}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Add Trainee
            </button>
          )}
          {onRefresh && (
            <button onClick={onRefresh}
              style={{padding:"5px 12px",borderRadius:"8px",border:"1px solid #e5e7eb",
                background:"white",cursor:"pointer",fontSize:"12px",color:"#6b7280",fontFamily:"inherit",
                display:"flex",alignItems:"center",gap:"5px"}}>
              <Ic n="refresh" s="w-3 h-3"/> Refresh
            </button>
          )}
        </div>
      </div>

      {/* Add Trainee modal */}
      {showAddModal && <AddTraineeModal
        name={addName} email={addEmail} saving={addSaving} error={addError}
        onName={setAddName} onEmail={setAddEmail}
        onSave={handleAddTrainee}
        onClose={()=>{setShowAddModal(false);setAddName("");setAddEmail("");setAddError("");}}
      />}

      {/* ════════════════════════════════════════════════════════════════════
          MY TRAINEES sub-tab
      ════════════════════════════════════════════════════════════════════ */}
      {subTab === "trainees" && (<div>

        {/* Domain chart — with role filter */}
        <div style={{...C.card,padding:"18px 20px",marginBottom:"16px"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"4px",flexWrap:"wrap",gap:"8px"}}>
            <div style={{fontWeight:"700",fontSize:"14px",color:C.dark}}>
              {selKey ? `${byT[selKey]?.name || "Trainee"} — Domain Breakdown` : "All Trainees — Domain Breakdown"}
            </div>
            <div style={{display:"flex",gap:"8px",alignItems:"center"}}>
              {!selKey && chartRoleOptions.length > 1 && (
                <select value={chartRoleFilter} onChange={e=>setChartRoleFilter(e.target.value)}
                  style={{...C.sel,width:"auto",fontSize:"12px",cursor:"pointer",padding:"4px 8px"}}>
                  {chartRoleOptions.map(r=><option key={r}>{r}</option>)}
                </select>
              )}
              {selKey && (
                <button onClick={()=>setSelKey(null)}
                  style={{...C.btn("transparent",C.pu,{border:`1px solid ${C.pu}`,padding:"4px 12px",fontSize:"12px"})}}>
                  ← All trainees
                </button>
              )}
            </div>
          </div>
          <div style={{fontSize:"12px",color:"#9ca3af",marginBottom:"14px"}}>
            Pass rate per clinical domain{!selKey && chartRoleFilter!=="All" ? ` · ${chartRoleFilter}` : ""}
          </div>
          <DomainChart assignments={chartAssignments} roleFilter={selKey ? "All" : chartRoleFilter}/>
        </div>

        {/* ── Trainee list (role-grouped) ─────────────────────────────────── */}
        {!selKey ? (
          <div>
            <div style={{display:"flex",gap:"8px",marginBottom:"14px",flexWrap:"wrap"}}>
              <input value={search} onChange={e=>setSearch(e.target.value)}
                placeholder="Search by name or email…"
                style={{...C.inp,flex:1,minWidth:"160px",fontSize:"12px"}}/>
            </div>

            {/* Group by role — unassigned role shown last */}
            {(() => {
              const traineeList = Object.values(byT).filter(tr => {
                const q = search.toLowerCase();
                return !q || (tr.name||"").toLowerCase().includes(q) || (tr.email||"").toLowerCase().includes(q);
              });
              const rolesPresent = [...new Set(traineeList.map(t => t.role || ""))].sort((a,b) => {
                if (!a) return 1; if (!b) return -1; return a.localeCompare(b);
              });

              return rolesPresent.map(role => {
                const group = traineeList.filter(t => (t.role || "") === role);
                if (!group.length) return null;
                const roleLabel = role || "Role Unknown";
                return (
                  <div key={roleLabel} style={{marginBottom:"20px"}}>
                    <div style={{fontSize:"10px",fontWeight:"700",color:C.pu,textTransform:"uppercase",
                      letterSpacing:"0.08em",marginBottom:"8px",paddingBottom:"5px",
                      borderBottom:`2px solid #ede9fe`}}>
                      {roleLabel}
                      <span style={{marginLeft:"8px",fontSize:"10px",fontWeight:"600",
                        background:"#ede9fe",color:C.pu,padding:"1px 7px",borderRadius:"10px"}}>
                        {group.length}
                      </span>
                    </div>
                    <div style={{display:"grid",gap:"8px"}}>
                      {group.map(tr => {
                        const done = tr.list.filter(a=>a.status==="completed");
                        const rate = done.length ? Math.round(done.filter(a=>(a.lastResult||a.result)?.success).length/done.length*100) : null;
                        const inRoster = rosterEmails.has((tr.email||"").toLowerCase());
                        return (
                          <div key={tr.key}
                            style={{...C.card,padding:"13px 17px",display:"flex",alignItems:"center",
                              gap:"13px",transition:"box-shadow 0.15s",position:"relative"}}
                            onMouseEnter={e=>e.currentTarget.style.boxShadow="0 4px 16px rgba(79,38,131,0.12)"}
                            onMouseLeave={e=>e.currentTarget.style.boxShadow=""}>
                            {/* Clickable area */}
                            <div onClick={()=>tr.list.length>0&&setSelKey(tr.key)}
                              style={{display:"flex",alignItems:"center",gap:"13px",flex:1,minWidth:0,
                                cursor:tr.list.length>0?"pointer":"default"}}>
                              <div style={{width:"40px",height:"40px",background:"#ede9fe",borderRadius:"50%",
                                display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                                <Ic n="user" s="w-5 h-5"/>
                              </div>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{fontWeight:"600",fontSize:"13px",color:C.dark,
                                  whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                                  {tr.name}
                                </div>
                                <div style={{fontSize:"11px",color:"#9ca3af",marginTop:"2px"}}>
                                  {tr.email && tr.email!==tr.name &&
                                    <span style={{marginRight:"6px"}}>{tr.email}</span>}
                                  {tr.list.length>0
                                    ? <>{tr.list.length} case{tr.list.length!==1?"s":""} · {done.length} completed</>
                                    : <span style={{color:"#d97706",fontWeight:"500"}}>No cases assigned yet</span>}
                                </div>
                              </div>
                              <div style={{textAlign:"right",flexShrink:0,marginRight:"6px"}}>
                                {rate!==null
                                  ? <><div style={{fontSize:"20px",fontWeight:"800",
                                      color:rate>=70?"#059669":rate>=40?"#d97706":"#dc2626"}}>{rate}%</div>
                                     <div style={{fontSize:"10px",color:"#9ca3af"}}>success</div></>
                                  : tr.list.length>0
                                    ? <div style={{fontSize:"11px",color:"#d97706",fontWeight:"600"}}>Pending</div>
                                    : null}
                              </div>
                            </div>
                            {/* Remove button — shown for all non-anonymous trainees */}
                            {!tr.isAnonymous && tr.email && onRemoveTrainee && (
                              <button
                                onClick={e=>{e.stopPropagation();
                                  if(window.confirm(`Remove ${tr.name||tr.email} from your trainee list?\n\nThis won't delete their assignment history — just hides them from your Trainees tab. You can re-add them at any time.`))
                                    onRemoveTrainee(tr.email);
                                }}
                                title="Remove from list"
                                style={{flexShrink:0,width:"28px",height:"28px",borderRadius:"50%",
                                  border:"1px solid #fecaca",background:"#fef2f2",color:"#dc2626",
                                  cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",
                                  fontSize:"16px",lineHeight:1,fontFamily:"inherit",transition:"background 0.15s"}}
                                onMouseEnter={e=>e.currentTarget.style.background="#fee2e2"}
                                onMouseLeave={e=>e.currentTarget.style.background="#fef2f2"}>
                                ×
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              });
            })()}
          </div>

        /* ── Individual trainee detail ─────────────────────────────────────── */
        ) : (()=>{
          const tr = byT[selKey] || {};
          const tList = tr.list || [];
          const completed = tList.filter(a=>a.status==="completed");
          const pending   = tList.filter(a=>a.status!=="completed");
          const passedCount = completed.filter(a=>(a.lastResult||a.result)?.success).length;
          const trSuccessPct = completed.length ? Math.round(passedCount/completed.length*100) : null;

          return (
            <div>
              {/* Trainee header */}
              <div style={{...C.card,padding:"16px 20px",marginBottom:"16px",
                background:"linear-gradient(135deg,#f8f5ff,#f1eeff)"}}>
                <div style={{display:"flex",alignItems:"center",gap:"14px",flexWrap:"wrap"}}>
                  <div style={{width:"48px",height:"48px",background:"#ede9fe",borderRadius:"50%",
                    display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    <Ic n="user" s="w-6 h-6"/>
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:"700",fontSize:"15px",color:C.dark}}>{tr.name}</div>
                    <div style={{fontSize:"12px",color:"#6b7280",marginTop:"2px"}}>
                      {tr.role && <span>{tr.role}</span>}
                      {tr.email && <span style={{marginLeft:tr.role?"8px":"0",color:"#9ca3af"}}>{tr.email}</span>}
                    </div>
                  </div>
                  <div style={{display:"flex",gap:"10px",alignItems:"center"}}>
                    {trSuccessPct!==null && (
                      <div style={{textAlign:"right"}}>
                        <div style={{fontSize:"22px",fontWeight:"800",
                          color:trSuccessPct>=70?"#059669":trSuccessPct>=40?"#d97706":"#dc2626"}}>
                          {trSuccessPct}%
                        </div>
                        <div style={{fontSize:"10px",color:"#9ca3af"}}>success</div>
                      </div>
                    )}
                    {tr.email && onRemoveTrainee && (
                      <button
                        onClick={()=>{
                          if(window.confirm(`Remove ${tr.name||tr.email} from your trainee list?\n\nThis won't delete their assignment history — just hides them from your Trainees tab. You can re-add them at any time.`)){
                            onRemoveTrainee(tr.email); setSelKey(null);
                          }
                        }}
                        style={{padding:"6px 12px",borderRadius:"8px",border:"1px solid #fecaca",
                          background:"#fef2f2",color:"#dc2626",cursor:"pointer",fontSize:"12px",
                          fontFamily:"inherit",fontWeight:"600"}}>
                        Remove
                      </button>
                    )}
                    <button onClick={()=>setSelKey(null)}
                      style={{...C.btn("transparent",C.pu,{border:`1px solid ${C.pu}`,padding:"6px 14px",fontSize:"12px"})}}>
                      ← Back
                    </button>
                  </div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:"8px",marginTop:"14px"}}>
                  {[
                    {label:"Assigned",  value:tList.length,     color:C.pu},
                    {label:"Completed", value:completed.length, color:"#059669"},
                    {label:"Pending",   value:pending.length,   color:"#d97706"},
                  ].map(({label,value,color})=>(
                    <div key={label} style={{background:"white",borderRadius:"8px",padding:"10px 14px",
                      border:`1px solid ${color}22`}}>
                      <div style={{fontSize:"18px",fontWeight:"800",color}}>{value}</div>
                      <div style={{fontSize:"10px",color:"#6b7280",marginTop:"2px"}}>{label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Domain chart for this trainee */}
              {completed.length > 0 && (
                <div style={{...C.card,padding:"16px 20px",marginBottom:"16px"}}>
                  <div style={{fontSize:"11px",fontWeight:"700",color:"#6b7280",textTransform:"uppercase",
                    letterSpacing:"0.07em",marginBottom:"12px"}}>Domain Breakdown</div>
                  <DomainChart assignments={tList} roleFilter="All"/>
                </div>
              )}

              {/* Case lists */}
              {completed.length>0&&<SectionToggle
                label="Completed" color="#059669" bg="#f0fdf4"
                items={completed} defaultOpen={true}/>}
              {pending.length>0&&<SectionToggle
                label="Pending" color="#d97706" bg="#fffbeb"
                items={pending} defaultOpen={true}/>}
            </div>
          );
        })()}
      </div>)}

      {/* ════════════════════════════════════════════════════════════════════
          MY CASES sub-tab
      ════════════════════════════════════════════════════════════════════ */}
      {subTab === "mycases" && (<div>
        <div style={{fontSize:"12px",color:"#9ca3af",marginBottom:"14px"}}>
          Global anonymized performance on cases you assigned. Updates as trainees submit worldwide.
        </div>
        {statsLoading && <div style={{textAlign:"center",padding:"32px",color:"#9ca3af",fontSize:"13px"}}>Loading global stats…</div>}
        {!statsLoading && (() => {
          // Build per-case rows from local assignments + enrich with global Firestore stats
          const caseMap = {};
          assignments.forEach(a => {
            if (!caseMap[a.caseId]) caseMap[a.caseId] = {
              caseId: a.caseId, title: a.caseTitle||a.caseId, facet: a.caseFacet||"",
              local: {sent:0,done:0,passed:0,roles:{}},
            };
            caseMap[a.caseId].local.sent++;
            if (a.status==="completed") {
              caseMap[a.caseId].local.done++;
              const r = a.lastResult||a.result;
              if (r?.success) caseMap[a.caseId].local.passed++;
              const role = r?.traineeRole||a.traineeRole||"Unknown";
              caseMap[a.caseId].local.roles[role]=(caseMap[a.caseId].local.roles[role]||0)+1;
            }
          });
          const rows = Object.values(caseMap);
          if (!rows.length) return <div style={{textAlign:"center",padding:"32px",color:"#9ca3af",fontSize:"13px"}}>No cases yet.</div>;
          return (
            <div style={{display:"grid",gap:"12px"}}>
              {rows.sort((a,b)=>b.local.sent-a.local.sent).map(row=>{
                const glob = caseStats[row.caseId];
                const gTotal = glob?.totalAttempts||0;
                const gPass  = glob?.totalPasses||0;
                const gRate  = gTotal ? Math.round(gPass/gTotal*100) : null;
                const lRate  = row.local.done ? Math.round(row.local.passed/row.local.done*100) : null;
                // Extract role breakdown from global stats
                const globRoles = glob ? Object.entries(glob)
                  .filter(([k])=>k.startsWith("role_"))
                  .map(([k,v])=>[k.replace("role_","").replace(/_/g," "),v]) : [];
                return (
                  <div key={row.caseId} style={{...C.card,padding:"18px",
                    borderLeft:`4px solid ${lRate===null?"#e5e7eb":lRate>=70?"#059669":lRate>=40?"#d97706":"#dc2626"}`}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:"8px",marginBottom:"12px"}}>
                      <div>
                        <div style={{fontWeight:"700",fontSize:"14px",color:C.dark}}>{row.title}</div>
                        {row.facet&&<span style={{fontSize:"11px",padding:"2px 8px",borderRadius:"8px",
                          background:"#f3f4f6",color:"#6b7280",display:"inline-block",marginTop:"4px"}}>{row.facet}</span>}
                      </div>
                    </div>
                    {/* Two columns: My Trainees vs Global */}
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px"}}>
                      <div style={{background:"#f5f0ff",borderRadius:"8px",padding:"12px"}}>
                        <div style={{fontSize:"10px",fontWeight:"700",color:C.pu,textTransform:"uppercase",
                          letterSpacing:"0.06em",marginBottom:"8px"}}>My Trainees</div>
                        <div style={{display:"flex",gap:"14px",flexWrap:"wrap",fontSize:"12px",color:"#374151"}}>
                          <span>📤 <strong>{row.local.sent}</strong> sent</span>
                          <span>✓ <strong>{row.local.passed}</strong> passed</span>
                          <span>✗ <strong>{row.local.done-row.local.passed}</strong> failed</span>
                          {lRate!==null&&<span style={{fontWeight:"700",color:lRate>=70?"#059669":lRate>=40?"#d97706":"#dc2626"}}>{lRate}%</span>}
                        </div>
                        {Object.keys(row.local.roles).length>0&&(
                          <div style={{marginTop:"6px",fontSize:"11px",color:"#6b7280"}}>
                            👥 {Object.entries(row.local.roles).map(([r,n])=>`${r} (${n})`).join(", ")}
                          </div>
                        )}
                      </div>
                      <div style={{background:"#f0f9ff",borderRadius:"8px",padding:"12px"}}>
                        <div style={{fontSize:"10px",fontWeight:"700",color:"#0369a1",textTransform:"uppercase",
                          letterSpacing:"0.06em",marginBottom:"8px"}}>Global (all educators)</div>
                        {gTotal > 0 ? (<>
                          <div style={{display:"flex",gap:"14px",flexWrap:"wrap",fontSize:"12px",color:"#374151"}}>
                            <span>🌐 <strong>{gTotal}</strong> attempts</span>
                            <span style={{fontWeight:"700",color:gRate>=70?"#059669":gRate>=40?"#d97706":"#dc2626"}}>{gRate}%</span>
                          </div>
                          {globRoles.length>0&&(
                            <div style={{marginTop:"6px",fontSize:"11px",color:"#6b7280"}}>
                              👥 {globRoles.map(([r,n])=>`${r} (${n})`).join(", ")}
                            </div>
                          )}
                        </>) : (
                          <div style={{fontSize:"12px",color:"#9ca3af"}}>No global data yet — accrues as others assign this case</div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>)}

    </div>
  );
}

// ─── ADMIN TAB ────────────────────────────────────────────────────────────────
// Visible ONLY to ADMIN_EMAIL. Tab is hidden from all other educators via
// the TABS array in Dashboard. Update ADMIN_EMAIL to personal address in prod.
const ADMIN_EMAIL = "amrit.kirpalani@lhsc.on.ca";

function AdminTab({educator}){
  const [adminTab,setAdminTab]=useState("analysis");
  // Which of the two retired screens the legacy tab is showing.
  const [legacyView,setLegacyView]=useState("usage");
  const [edus,setEdus]=useState({});
  const [events,setEvents]=useState([]);
  const [usageEvents,setUsageEvents]=useState({});
  const [loading,setLoading]=useState(true);
  const [exportBusy,setExportBusy]=useState(false);
  const [filterRole,setFilterRole]=useState("All");
  const [filterDomain,setFilterDomain]=useState("All");
  const [filterTag,setFilterTag]=useState("All");
  // Wipe test trainee
  const [wipeEmail,setWipeEmail]=useState("");
  const [wipeBusy,setWipeBusy]=useState(false);
  const [wipeResult,setWipeResult]=useState(null);
  const [wipeErr,setWipeErr]=useState("");
  const isAdmin = educator.email === ADMIN_EMAIL;

  useEffect(()=>{(async()=>{
    try {
      const snap = await getDocs(collection(db, 'educators'));
      const d = {};
      snap.forEach(s=>{ d[s.id]=s.data(); });
      setEdus(d);
    } catch(e){ console.error("AdminTab educators load",e); }
    try {
      // Load research events directly from /research_events collection (admin-only).
      // This is the authoritative server-written source — not the old shared store.
      const snap = await getDocs(collection(db, 'research_events'));
      const evs = [];
      snap.forEach(s => evs.push({ _id: s.id, ...s.data() }));
      setEvents(evs);
    } catch(e){ console.error("AdminTab research_events load",e); }
    try {
      const uev = await store.getShared('kirpa_usage_events') || {};
      setUsageEvents(uev);
    } catch(e){ console.error("AdminTab usage load",e); }
    setLoading(false);
  })();},[]);

  const approve=async em=>{
    try {
      await updateDoc(doc(db,'educators',em),{approved:true,approvedAt:serverTimestamp()});
      setEdus(u=>{const n={...u};n[em]={...n[em],approved:true};return n;});
      // Production: Cloud Function watches for approved=true and:
      //   1. Creates Firebase Auth account with temp password
      //   2. Sends welcome email from appnephro@gmail.com with temp password
      //      and a link to reset it: https://nephro-kirpa.netlify.app
    } catch(e){ alert("Approval failed: "+e.message); }
  };
  const revoke=async em=>{
    try {
      await updateDoc(doc(db,'educators',em),{approved:false,revokedAt:serverTimestamp()});
      setEdus(u=>{const n={...u};n[em]={...n[em],approved:false};return n;});
    } catch(e){ alert("Revoke failed: "+e.message); }
  };

  const exportCSV = (which='performance') => {
    setExportBusy(true);
    try {
      const rows = which==='usage' ? Object.values(usageEvents) : events;
      if(!rows.length){alert(`No ${which} data yet.`);setExportBusy(false);return;}
      // Fields to exclude from export:
      //   performance: trainee_email (PII — stored for deletion only, never exported),
      //                written_at (Firestore timestamp object, not useful in CSV),
      //                _id (internal doc ID)
      //   usage: source (internal)
      const SKIP = which==='performance'
        ? ['trainee_email','written_at','_id','source']
        : ['source'];
      const keys=[...new Set(rows.flatMap(r=>Object.keys(r)))].filter(k=>!SKIP.includes(k));
      const csv=[keys.join(','),...rows.map(r=>keys.map(k=>{
        const v=r[k]; if(v==null) return '';
        // Flatten Firestore Timestamp objects to ISO string
        if(v && typeof v === 'object' && typeof v.toDate === 'function') return v.toDate().toISOString();
        const s=String(v); return s.includes(',')? `"${s.replace(/"/g,'""')}"`:s;
      }).join(','))].join('\n');
      const a=Object.assign(document.createElement('a'),{
        href:URL.createObjectURL(new Blob([csv],{type:'text/csv'})),
        download:`KiRPA_${which}_${new Date().toISOString().slice(0,10)}.csv`
      });
      a.click();
    } catch(e){alert('Export failed: '+e.message);}
    setExportBusy(false);
  };

  if(!isAdmin) return (
    <div style={{padding:"56px",textAlign:"center",color:"#9ca3af"}}>
      <div style={{fontSize:"32px",marginBottom:"12px",opacity:0.4}}>🔒</div>
      <div style={{fontWeight:"700",fontSize:"15px",color:"#374151",marginBottom:"6px"}}>Admin Only</div>
      <p style={{fontSize:"13px",margin:0}}>This panel is only accessible to the platform administrator.</p>
    </div>
  );

  // ── Derived analytics ─────────────────────────────────────────────────────
  const allRoles=["All",...new Set(events.map(e=>e.trainee_role).filter(Boolean))];
  const allDomains=["All",...Object.values(DOMAIN_LABELS)];
  const allTags=["All",...new Set(events.flatMap(e=>e.case_tags||[]).filter(Boolean))];

  const filtered=events.filter(e=>{
    if(filterRole!=="All" && e.trainee_role!==filterRole) return false;
    if(filterDomain!=="All" && DOMAIN_LABELS[e.clinical_domain_primary]!==filterDomain) return false;
    if(filterTag!=="All" && !(e.case_tags||[]).includes(filterTag)) return false;
    return true;
  });

  const total=filtered.length;
  const passed=filtered.filter(e=>e.overall_success===true).length;
  const overallPct=total>0?Math.round((passed/total)*100):null;
  const avgAttempts=total>0?(filtered.reduce((s,e)=>s+(e.attempt_number||1),0)/total).toFixed(1):null;

  // Domain success
  const domainStats=Object.entries(DOMAIN_LABELS).map(([code,label])=>{
    const rows=filtered.filter(e=>e.clinical_domain_primary===code);
    const p=rows.filter(e=>e.overall_success===true).length;
    return {code,label,n:rows.length,passed:p,pct:rows.length>0?Math.round((p/rows.length)*100):null};
  }).filter(d=>d.n>0);

  // Case table
  const caseMap={};
  filtered.forEach(e=>{
    const id=e.case_id_readable||e.case_id;
    if(!caseMap[id]) caseMap[id]={id,title:BASE_CASES[id]?.title||id,facet:e.case_facet||"",n:0,passed:0,totalAttempts:0};
    caseMap[id].n++; if(e.overall_success) caseMap[id].passed++;
    caseMap[id].totalAttempts+=(e.attempt_number||1);
  });
  const caseSorted=Object.values(caseMap).sort((a,b)=>b.n-a.n).slice(0,12);

  // Role distribution
  const roleCounts={};
  filtered.forEach(e=>{const r=e.trainee_role||"Not specified"; roleCounts[r]=(roleCounts[r]||0)+1;});

  // Prescription distributions
  const modalityCounts={};
  filtered.forEach(e=>{const m=e.rx_modality||"?"; modalityCounts[m]=(modalityCounts[m]||0)+1;});
  const anticoagCounts={};
  filtered.forEach(e=>{const a=e.rx_anticoag||"?"; anticoagCounts[a]=(anticoagCounts[a]||0)+1;});

  // Dose direction
  const doseDirs={on_target:0,underdose:0,overdose:0,unknown:0};
  filtered.forEach(e=>{const d=e.rx_dose_direction; doseDirs[d||'unknown']++;});

  // Simple horizontal bar
  const Bar=({pct,color,h=8})=>(
    <div style={{flex:1,height:`${h}px`,background:"#e9e4f5",borderRadius:"4px",overflow:"hidden"}}>
      <div style={{width:`${Math.min(pct||0,100)}%`,height:"100%",background:color||C.pu,borderRadius:"4px",transition:"width 0.3s"}}/>
    </div>
  );

  const SectionHead=({title})=>(
    <div style={{fontSize:"13px",fontWeight:"800",color:C.dark,marginBottom:"12px",fontFamily:FONT_UI,
      borderBottom:`2px solid ${C.pu}20`,paddingBottom:"6px"}}>{title}</div>
  );

  return <div style={{padding:"22px"}}>
    {/* Header */}
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"20px",flexWrap:"wrap",gap:"10px"}}>
      <h2 style={{margin:0,fontSize:"18px",fontWeight:"800",color:C.dark,fontFamily:FONT_UI}}>Admin Dashboard</h2>
      <div style={{display:"flex",gap:"6px",flexWrap:"wrap"}}>
        {/* Two destinations, not eight. Everything analytic lives behind one
            filter bar in "Data & analysis"; the rest is administration that has
            nothing to do with the data. The old per-tab exports are gone: the
            analysis section exports exactly the rows it is showing, which is
            the only export that can be cited. */}
        {[{id:"analysis",label:"🔬 Data & analysis"},
          {id:"legacy",label:"📈 Legacy views"},
          {id:"data",label:"🗑️ Data management"},
          {id:"educators",label:"👥 Educator access"}].map(({id,label})=>(
          <button key={id} onClick={()=>setAdminTab(id)}
            style={{padding:"7px 14px",borderRadius:"8px",border:"none",cursor:"pointer",
              fontWeight:"700",fontSize:"12px",fontFamily:"inherit",
              background:adminTab===id?C.pu:"#f3f4f6",
              color:adminTab===id?"white":"#6b7280",
              boxShadow:adminTab===id?`0 2px 8px rgba(79,38,131,0.3)`:"none"}}>
            {label}
          </button>
        ))}
        {/* Whole-collection exports, kept for the raw dump. The filtered
            export inside the analysis section is the one to cite. */}
        {adminTab!=="analysis"&&<>
          <button onClick={()=>exportCSV('performance')} disabled={exportBusy} style={{...C.btn(C.pu)}}>
            {exportBusy?<Ic n="spin" s="w-4 h-4"/>:<Ic n="save" s="w-4 h-4"/>}
            {exportBusy?"Preparing…":"Export all performance"}
          </button>
          <button onClick={()=>exportCSV('usage')} disabled={exportBusy} style={{...C.btn("#0891b2")}}>
            {exportBusy?<Ic n="spin" s="w-4 h-4"/>:<Ic n="save" s="w-4 h-4"/>}
            Export all usage
          </button>
        </>}
      </div>
    </div>

    {/* ══ HARMONISED DATA & ANALYSIS ══
        One filter bar, every outcome underneath it. Case analytics, the error
        taxonomy, the Delphi results and the analysis plan are all panels in
        here now rather than separate tabs with separate filter state. */}
    {adminTab==="analysis"&&
      <DataAnalysis events={events} usageEvents={usageEvents} loading={loading}/>}

    {/* ══ LEGACY VIEWS ══
        The original usage and performance screens, kept because they hold a
        few breakdowns not yet ported (attempt distribution, the per-role
        table, the raw prescription-choice bars) and because a figure already
        drafted from them should stay reproducible. They use the old
        role/domain/tag filters, not the shared bar. */}
    {adminTab==="legacy"&&<div style={{display:"flex",gap:"6px",marginBottom:"16px"}}>
      {[["usage","Usage"],["performance","Performance"]].map(([k,l])=>(
        <button key={k} onClick={()=>setLegacyView(k)}
          style={{padding:"6px 13px",borderRadius:"9px",cursor:"pointer",fontFamily:"inherit",
            fontSize:"12px",fontWeight:legacyView===k?"700":"500",
            border:`1px solid ${legacyView===k?C.pu:"#e5e7eb"}`,
            background:legacyView===k?"#f3effe":"white",
            color:legacyView===k?C.pu:"#374151"}}>{l}</button>
      ))}
    </div>}

    {adminTab==="legacy"&&legacyView==="usage"&&(loading
      ?<p style={{color:"#9ca3af",textAlign:"center",padding:"40px"}}>Loading…</p>
      :(()=>{
        const uevents=Object.values(usageEvents||{});
        const sent=uevents.filter(e=>e.event_type==="assignment_sent");
        const completedEv=events;
        const uniqueEdus=[...new Set(sent.map(e=>e.educator_id).filter(Boolean))].length;
        const uniqueInst=[...new Set(sent.map(e=>e.institution_id).filter(Boolean))].length;
        const uniqueLearners=[...new Set(completedEv.map(e=>e.learner_id).filter(Boolean))].length;
        const totalSent=sent.length;
        const totalCompleted=completedEv.length;
        const completionRate=totalSent>0?Math.round(totalCompleted/totalSent*100):null;

        // Case popularity
        const casePop={};
        sent.forEach(e=>{
          const k=e.case_id_readable||e.case_id||"?";
          casePop[k]=(casePop[k]||{sent:0,completed:0,isCustom:e.case_is_custom||false,facet:e.case_facet||""});
          casePop[k].sent++;
        });
        completedEv.forEach(e=>{
          const k=e.case_id_readable||e.case_id||"?";
          if(casePop[k]) casePop[k].completed++;
          else casePop[k]={sent:0,completed:1,isCustom:e.case_is_custom||false,facet:e.case_facet||""};
        });
        const casePopSorted=Object.entries(casePop).sort((a,b)=>b[1].sent-a[1].sent).slice(0,15);

        // Facet distribution of sent
        const facetSent={};
        sent.forEach(e=>{const f=e.case_facet||"Unknown"; facetSent[f]=(facetSent[f]||0)+1;});
        const maxFacet=Math.max(...Object.values(facetSent),1);

        // Custom vs built-in
        const customSent=sent.filter(e=>e.case_is_custom).length;
        const builtInSent=totalSent-customSent;

        // Day-of-week submissions
        const dowCounts=[0,0,0,0,0,0,0];
        completedEv.forEach(e=>{if(e.day_of_week!=null) dowCounts[e.day_of_week]++;});
        const dowLabels=["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
        const maxDow=Math.max(...dowCounts,1);

        return <>
          {/* Summary row */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(120px,1fr))",gap:"10px",marginBottom:"20px"}}>
            {[
              ["Assignments Sent",totalSent,C.pu],
              ["Completions",totalCompleted,"#059669"],
              ["Completion Rate",completionRate!=null?`${completionRate}%`:"—","#0891b2"],
              ["Unique Learners",uniqueLearners,"#7c3aed"],
              ["Educators",uniqueEdus,"#d97706"],
              ["Institutions",uniqueInst,"#dc2626"],
            ].map(([l,v,c])=>(
              <div key={l} style={{...C.card,padding:"12px 14px",textAlign:"center"}}>
                <div style={{fontSize:"22px",fontWeight:"800",color:c,fontFamily:FONT_UI}}>{v}</div>
                <div style={{fontSize:"10px",color:"#9ca3af",textTransform:"uppercase",letterSpacing:"0.04em",marginTop:"2px"}}>{l}</div>
              </div>
            ))}
          </div>

          <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:"14px",marginBottom:"14px"}}>
            {/* Case popularity table */}
            <div style={{...C.card,padding:"16px 18px"}}>
              <SectionHead title="Cases by Send Volume"/>
              {casePopSorted.length===0
                ?<p style={{fontSize:"12px",color:"#9ca3af"}}>No data yet.</p>
                :<table style={{width:"100%",borderCollapse:"collapse",fontSize:"11px"}}>
                  <thead><tr style={{borderBottom:"2px solid #e5e7eb"}}>
                    {["Case","Type","Sent","Done","Completion"].map(h=>(
                      <th key={h} style={{padding:"5px 7px",textAlign:h==="Case"?"left":"center",
                        color:"#9ca3af",fontWeight:"700",fontSize:"10px",textTransform:"uppercase"}}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {casePopSorted.map(([cid,{sent:s,completed:c2,isCustom,facet}])=>{
                      const col=getFacetCol(facet);
                      const compPct=s>0?Math.round(c2/s*100):0;
                      return <tr key={cid} style={{borderBottom:"1px solid #f3f4f6"}}>
                        <td style={{padding:"5px 7px",maxWidth:"160px"}}>
                          <div style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontWeight:"600",color:C.dark,fontSize:"11px"}}>
                            {BASE_CASES[cid]?.title||cid}
                          </div>
                          {facet&&<span style={{fontSize:"9px",padding:"1px 5px",borderRadius:"5px",background:col.pill,color:col.pillTx}}>{facet}</span>}
                        </td>
                        <td style={{padding:"5px 7px",textAlign:"center"}}>
                          <span style={{fontSize:"10px",padding:"1px 6px",borderRadius:"5px",
                            background:isCustom?"#ecfeff":"#f3f4f6",
                            color:isCustom?"#0891b2":"#6b7280",fontWeight:"600"}}>
                            {isCustom?"Custom":"Built-in"}
                          </span>
                        </td>
                        <td style={{padding:"5px 7px",textAlign:"center",color:"#374151",fontWeight:"600"}}>{s}</td>
                        <td style={{padding:"5px 7px",textAlign:"center",color:"#6b7280"}}>{c2}</td>
                        <td style={{padding:"5px 7px",textAlign:"center"}}>
                          <span style={{padding:"2px 7px",borderRadius:"6px",fontWeight:"700",fontSize:"10px",
                            background:compPct>=70?"#d1fae5":compPct>=40?"#fef3c7":"#f3f4f6",
                            color:compPct>=70?"#065f46":compPct>=40?"#92400e":"#9ca3af"}}>
                            {s>0?`${compPct}%`:"—"}
                          </span>
                        </td>
                      </tr>;
                    })}
                  </tbody>
                </table>
              }
            </div>

            {/* Right column: Facet dist + DOW + custom ratio */}
            <div style={{display:"flex",flexDirection:"column",gap:"14px"}}>
              <div style={{...C.card,padding:"14px 16px"}}>
                <SectionHead title="By Domain (sent)"/>
                {Object.keys(facetSent).length===0
                  ?<p style={{fontSize:"12px",color:"#9ca3af"}}>No data.</p>
                  :Object.entries(facetSent).sort((a,b)=>b[1]-a[1]).map(([f,n])=>(
                    <div key={f} style={{marginBottom:"6px"}}>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:"2px"}}>
                        <span style={{fontSize:"10px",fontWeight:"600",color:"#374151"}}>{f}</span>
                        <span style={{fontSize:"10px",color:"#9ca3af"}}>{n}</span>
                      </div>
                      <Bar pct={Math.round(n/maxFacet*100)} color={getFacetCol(f).accent||C.pu}/>
                    </div>
                  ))
                }
              </div>

              <div style={{...C.card,padding:"14px 16px"}}>
                <SectionHead title="Built-in vs Custom"/>
                {totalSent===0
                  ?<p style={{fontSize:"12px",color:"#9ca3af"}}>No data.</p>
                  :<>
                    {[["Built-in",builtInSent,"#6b7280"],["Custom",customSent,"#0891b2"]].map(([l,n,c])=>(
                      <div key={l} style={{marginBottom:"8px"}}>
                        <div style={{display:"flex",justifyContent:"space-between",marginBottom:"2px"}}>
                          <span style={{fontSize:"11px",fontWeight:"600",color:"#374151"}}>{l}</span>
                          <span style={{fontSize:"11px",color:"#9ca3af"}}>{n} ({Math.round(n/totalSent*100)}%)</span>
                        </div>
                        <Bar pct={Math.round(n/totalSent*100)} color={c}/>
                      </div>
                    ))}
                  </>
                }
              </div>

              <div style={{...C.card,padding:"14px 16px"}}>
                <SectionHead title="Submissions by Day"/>
                <div style={{display:"flex",gap:"4px",alignItems:"flex-end",height:"60px"}}>
                  {dowCounts.map((n,i)=>(
                    <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:"3px"}}>
                      <div style={{width:"100%",background:C.pu,borderRadius:"3px 3px 0 0",
                        height:`${Math.round((n/maxDow)*48)+4}px`,opacity:n>0?1:0.15,
                        minHeight:"4px",transition:"height 0.3s"}}/>
                      <div style={{fontSize:"9px",color:"#9ca3af",fontWeight:"600"}}>{dowLabels[i]}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>;
      })()
    )}

    {/* ══ PERFORMANCE TAB ══ */}
    {adminTab==="legacy"&&legacyView==="performance"&&(loading
      ?<p style={{color:"#9ca3af",textAlign:"center",padding:"40px"}}>Loading…</p>
      :events.length===0
        ?<div style={{textAlign:"center",padding:"60px",color:"#9ca3af"}}>
          <div style={{fontSize:"40px",marginBottom:"12px",opacity:0.3}}>🎯</div>
          <div style={{fontWeight:"700",color:"#374151",marginBottom:"6px"}}>No performance data yet</div>
          <p style={{fontSize:"12px",margin:0}}>Data is captured automatically when trainees complete cases.</p>
        </div>
        :(()=>{
          // ── Filters ──────────────────────────────────────────────────────
          const allRoles2=["All",...new Set(events.map(e=>e.trainee_role).filter(Boolean))];
          const allDomains2=["All",...Object.values(DOMAIN_LABELS)];
          const allTags2=["All",...new Set(events.flatMap(e=>e.case_tags||[]).filter(Boolean))];
          const filtered2=events.filter(e=>{
            if(filterRole!=="All" && e.trainee_role!==filterRole) return false;
            if(filterDomain!=="All" && DOMAIN_LABELS[e.clinical_domain_primary]!==filterDomain) return false;
            if(filterTag!=="All" && !(e.case_tags||[]).includes(filterTag)) return false;
            return true;
          });
          const total2=filtered2.length;
          const passed2=filtered2.filter(e=>e.overall_success===true).length;
          const pct2=total2>0?Math.round(passed2/total2*100):null;
          const avgAttempts2=total2>0?(filtered2.reduce((s,e)=>s+(e.attempt_number||1),0)/total2).toFixed(1):null;
          const firstAttemptRows=filtered2.filter(e=>e.first_attempt_success!=null);
          const firstAttemptPct=firstAttemptRows.length>0
            ?Math.round(firstAttemptRows.filter(e=>e.first_attempt_success).length/firstAttemptRows.length*100):null;

          // Attempts distribution
          const attemptDist={1:0,2:0,3:0,"4+":0};
          filtered2.forEach(e=>{
            const a=e.attempt_number||1;
            if(a<=3) attemptDist[a]++;
            else attemptDist["4+"]++;
          });

          // Domain stats
          const domStats2=Object.entries(DOMAIN_LABELS).map(([code,label])=>{
            const rows=filtered2.filter(e=>e.clinical_domain_primary===code);
            const p=rows.filter(e=>e.overall_success===true).length;
            const fa=rows.filter(e=>e.first_attempt_success!=null);
            const fapct=fa.length>0?Math.round(fa.filter(e=>e.first_attempt_success).length/fa.length*100):null;
            const avgA=rows.length>0?(rows.reduce((s,e)=>s+(e.attempt_number||1),0)/rows.length).toFixed(1):null;
            return {code,label,n:rows.length,passed:p,
              pct:rows.length>0?Math.round(p/rows.length*100):null,
              fapct,avgA};
          }).filter(d=>d.n>0);

          // Case table
          const caseMap2={};
          filtered2.forEach(e=>{
            const id=e.case_id_readable||e.case_id;
            if(!caseMap2[id]) caseMap2[id]={id,title:BASE_CASES[id]?.title||id,facet:e.case_facet||"",n:0,passed:0,totalAttempts:0,firstAttemptPass:0,firstAttemptN:0};
            caseMap2[id].n++;
            if(e.overall_success) caseMap2[id].passed++;
            caseMap2[id].totalAttempts+=(e.attempt_number||1);
            if(e.first_attempt_success!=null){caseMap2[id].firstAttemptN++;if(e.first_attempt_success)caseMap2[id].firstAttemptPass++;}
          });
          const caseSorted2=Object.values(caseMap2).sort((a,b)=>b.n-a.n).slice(0,15);

          // Role performance
          const rolePerfMap={};
          filtered2.forEach(e=>{
            const r=e.trainee_role||"Unspecified";
            if(!rolePerfMap[r]) rolePerfMap[r]={n:0,passed:0};
            rolePerfMap[r].n++;
            if(e.overall_success) rolePerfMap[r].passed++;
          });

          // Rx choice analysis by domain (wrong choices per domain)
          // For each domain, count the distribution of key choice fields among FAILED attempts
          const anticoagByDomain={};
          const modalityByDomain={};
          filtered2.filter(e=>!e.overall_success).forEach(e=>{
            const d=DOMAIN_LABELS[e.clinical_domain_primary]||e.clinical_domain_primary||"?";
            if(e.rx_anticoag){
              anticoagByDomain[d]=anticoagByDomain[d]||{};
              anticoagByDomain[d][e.rx_anticoag]=(anticoagByDomain[d][e.rx_anticoag]||0)+1;
            }
            if(e.rx_modality){
              modalityByDomain[d]=modalityByDomain[d]||{};
              modalityByDomain[d][e.rx_modality]=(modalityByDomain[d][e.rx_modality]||0)+1;
            }
          });

          // For "new prescription" cases: rx variable distributions stratified by domain
          const newRxRows=filtered2.filter(e=>e.prescribing_mode==="new");
          const modalityCounts2={};
          const anticoagCounts2={};
          const doseDirs2={on_target:0,underdose:0,overdose:0};
          newRxRows.forEach(e=>{
            if(e.rx_modality) modalityCounts2[e.rx_modality]=(modalityCounts2[e.rx_modality]||0)+1;
            if(e.rx_anticoag) anticoagCounts2[e.rx_anticoag]=(anticoagCounts2[e.rx_anticoag]||0)+1;
            if(e.rx_dose_direction && e.rx_dose_direction!=='null') doseDirs2[e.rx_dose_direction]=(doseDirs2[e.rx_dose_direction]||0)+1;
          });

          return <>
            {/* Filters */}
            <div style={{display:"flex",gap:"8px",flexWrap:"wrap",marginBottom:"18px",
              padding:"12px 14px",background:"#f8f7ff",borderRadius:"10px",border:"1px solid #ede9fe",alignItems:"center"}}>
              <span style={{fontSize:"11px",fontWeight:"700",color:"#6b7280",textTransform:"uppercase",letterSpacing:"0.06em",flexShrink:0}}>Stratify by:</span>
              {[[filterRole,setFilterRole,allRoles2,"Role"],[filterDomain,setFilterDomain,allDomains2,"Domain"],[filterTag,setFilterTag,allTags2,"Subject Tag"]].map(([val,set,opts,lbl])=>(
                <select key={lbl} value={val} onChange={e=>set(e.target.value)}
                  style={{...C.inp,minWidth:"140px",fontSize:"12px",padding:"5px 8px"}}>
                  {opts.map(o=><option key={o} value={o}>{o==="All"?`All ${lbl}s`:o}</option>)}
                </select>
              ))}
              <span style={{fontSize:"11px",color:"#9ca3af",marginLeft:"auto"}}>{total2} submission{total2!==1?"s":""}</span>
            </div>

            {/* Summary KPIs */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(120px,1fr))",gap:"10px",marginBottom:"20px"}}>
              {[
                ["Overall Pass Rate",pct2!=null?`${pct2}%`:"—",pct2>=70?"#059669":pct2>=40?"#d97706":"#dc2626"],
                ["1st-Attempt Pass",firstAttemptPct!=null?`${firstAttemptPct}%`:"—",firstAttemptPct>=70?"#059669":firstAttemptPct>=40?"#d97706":"#dc2626"],
                ["Avg Attempts",avgAttempts2||"—","#2563eb"],
                ["Submissions",total2,C.pu],
                ["Unique Learners",[...new Set(filtered2.map(e=>e.learner_id))].length,"#0891b2"],
                ["Institutions",[...new Set(filtered2.map(e=>e.institution_id).filter(Boolean))].length,"#059669"],
              ].map(([l,v,c])=>(
                <div key={l} style={{...C.card,padding:"12px 14px",textAlign:"center"}}>
                  <div style={{fontSize:"22px",fontWeight:"800",color:c,fontFamily:FONT_UI}}>{v}</div>
                  <div style={{fontSize:"10px",color:"#9ca3af",textTransform:"uppercase",letterSpacing:"0.04em",marginTop:"2px"}}>{l}</div>
                </div>
              ))}
            </div>

            {/* Attempts distribution + domain performance */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 2fr",gap:"14px",marginBottom:"14px"}}>
              <div style={{...C.card,padding:"14px 16px"}}>
                <SectionHead title="Attempt Distribution"/>
                <div style={{display:"flex",gap:"6px",alignItems:"flex-end",height:"80px",marginBottom:"8px"}}>
                  {Object.entries(attemptDist).map(([k,n])=>{
                    const maxA=Math.max(...Object.values(attemptDist),1);
                    return <div key={k} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:"3px"}}>
                      <div style={{fontSize:"10px",fontWeight:"700",color:C.dark}}>{n||""}</div>
                      <div style={{width:"100%",background:k==="1"?"#059669":k==="2"?"#d97706":"#dc2626",
                        borderRadius:"4px 4px 0 0",height:`${Math.round((n/maxA)*60)+4}px`,
                        minHeight:"4px",opacity:n>0?1:0.2}}/>
                      <div style={{fontSize:"10px",color:"#9ca3af",fontWeight:"600"}}>{k}</div>
                    </div>;
                  })}
                </div>
                <div style={{fontSize:"10px",color:"#9ca3af",textAlign:"center"}}>Number of attempts to pass</div>
              </div>

              <div style={{...C.card,padding:"14px 16px"}}>
                <SectionHead title="Domain Performance (pass rate · 1st-attempt · avg attempts)"/>
                {domStats2.length===0
                  ?<p style={{fontSize:"12px",color:"#9ca3af"}}>No data for selected filters.</p>
                  :<table style={{width:"100%",borderCollapse:"collapse",fontSize:"11px"}}>
                    <thead><tr style={{borderBottom:"1px solid #e5e7eb"}}>
                      {["Domain","n","Pass%","1st Att%","Avg Att"].map(h=>(
                        <th key={h} style={{padding:"5px 7px",textAlign:h==="Domain"?"left":"center",
                          color:"#9ca3af",fontWeight:"700",fontSize:"10px",textTransform:"uppercase"}}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {domStats2.map(({label,n,passed,pct,fapct,avgA})=>(
                        <tr key={label} style={{borderBottom:"1px solid #f3f4f6"}}>
                          <td style={{padding:"6px 7px",fontWeight:"600",color:C.dark,fontSize:"11px"}}>{label}</td>
                          <td style={{padding:"6px 7px",textAlign:"center",color:"#6b7280"}}>{n}</td>
                          <td style={{padding:"6px 7px",textAlign:"center"}}>
                            <span style={{padding:"2px 7px",borderRadius:"6px",fontWeight:"700",fontSize:"10px",
                              background:pct>=70?"#d1fae5":pct>=40?"#fef3c7":"#fee2e2",
                              color:pct>=70?"#065f46":pct>=40?"#92400e":"#991b1b"}}>{pct!=null?`${pct}%`:"—"}</span>
                          </td>
                          <td style={{padding:"6px 7px",textAlign:"center",color:"#6b7280",fontSize:"10px"}}>{fapct!=null?`${fapct}%`:"—"}</td>
                          <td style={{padding:"6px 7px",textAlign:"center",color:"#6b7280",fontSize:"10px"}}>{avgA||"—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                }
              </div>
            </div>

            {/* Role performance + case table */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 2fr",gap:"14px",marginBottom:"14px"}}>
              <div style={{...C.card,padding:"14px 16px"}}>
                <SectionHead title="Performance by Role"/>
                {Object.keys(rolePerfMap).length===0
                  ?<p style={{fontSize:"12px",color:"#9ca3af"}}>No role data yet.</p>
                  :Object.entries(rolePerfMap).sort((a,b)=>b[1].n-a[1].n).map(([role,{n,passed}])=>{
                    const pct=Math.round(passed/n*100);
                    return <div key={role} style={{marginBottom:"9px"}}>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:"2px"}}>
                        <span style={{fontSize:"10px",fontWeight:"600",color:"#374151",lineHeight:"1.3",flex:1}}>{role}</span>
                        <span style={{fontSize:"10px",color:"#9ca3af",flexShrink:0,marginLeft:"6px"}}>{passed}/{n} · <strong style={{color:pct>=70?"#059669":pct>=40?"#d97706":"#dc2626"}}>{pct}%</strong></span>
                      </div>
                      <Bar pct={pct} color={pct>=70?"#059669":pct>=40?"#d97706":"#dc2626"}/>
                    </div>;
                  })
                }
              </div>

              <div style={{...C.card,padding:"14px 16px"}}>
                <SectionHead title="Case Performance Table"/>
                {caseSorted2.length===0
                  ?<p style={{fontSize:"12px",color:"#9ca3af"}}>No data for selected filters.</p>
                  :<table style={{width:"100%",borderCollapse:"collapse",fontSize:"11px"}}>
                    <thead><tr style={{borderBottom:"2px solid #e5e7eb",background:"#f9fafb"}}>
                      {["Case","n","Pass%","1st Att%","Avg Att"].map(h=>(
                        <th key={h} style={{padding:"6px 8px",textAlign:h==="Case"?"left":"center",
                          color:"#6b7280",fontWeight:"700",fontSize:"10px",textTransform:"uppercase"}}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {caseSorted2.map(({id,title,facet,n,passed,totalAttempts,firstAttemptPass,firstAttemptN})=>{
                        const pct=Math.round(passed/n*100);
                        const fapct=firstAttemptN>0?Math.round(firstAttemptPass/firstAttemptN*100):null;
                        const col=getFacetCol(facet);
                        return <tr key={id} style={{borderBottom:"1px solid #f3f4f6"}}>
                          <td style={{padding:"5px 8px",maxWidth:"160px"}}>
                            <div style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontWeight:"600",color:C.dark,fontSize:"11px"}}>{title}</div>
                            {facet&&<span style={{fontSize:"9px",padding:"1px 5px",borderRadius:"5px",background:col.pill,color:col.pillTx}}>{facet}</span>}
                          </td>
                          <td style={{padding:"5px 8px",textAlign:"center",color:"#6b7280"}}>{n}</td>
                          <td style={{padding:"5px 8px",textAlign:"center"}}>
                            <span style={{padding:"2px 7px",borderRadius:"6px",fontWeight:"700",fontSize:"10px",
                              background:pct>=70?"#d1fae5":pct>=40?"#fef3c7":"#fee2e2",
                              color:pct>=70?"#065f46":pct>=40?"#92400e":"#991b1b"}}>{pct}%</span>
                          </td>
                          <td style={{padding:"5px 8px",textAlign:"center",color:"#6b7280",fontSize:"10px"}}>{fapct!=null?`${fapct}%`:"—"}</td>
                          <td style={{padding:"5px 8px",textAlign:"center",color:"#6b7280",fontSize:"10px"}}>{(totalAttempts/n).toFixed(1)}</td>
                        </tr>;
                      })}
                    </tbody>
                  </table>
                }
              </div>
            </div>

            {/* Prescription choice distributions (new Rx mode only) */}
            <div style={{...C.card,padding:"14px 16px",marginBottom:"14px"}}>
              <SectionHead title="Prescription Choices — Write-from-Scratch Cases"/>
              <div style={{fontSize:"11px",color:"#9ca3af",marginBottom:"12px"}}>
                Distribution of key choices across all &quot;new prescription&quot; submissions.
                Stratify by domain/role above to isolate specific patterns.
                {newRxRows.length===0&&" No write-from-scratch submissions yet."}
              </div>
              {newRxRows.length>0&&<div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"14px"}}>
                {[["Modality",modalityCounts2,{"CVVHDF":"#4f2683","CVVHD":"#7c3aed","CVVH":"#a78bfa"}],
                  ["Anticoagulation",anticoagCounts2,{"citrate":"#059669","heparin":"#0891b2","none":"#9ca3af","epoprostenol":"#d97706"}],
                  ["Dose Direction",doseDirs2,{"on_target":"#059669","underdose":"#d97706","overdose":"#dc2626"}],
                ].map(([title,counts,colorMap])=>{
                  const vals=Object.entries(counts).filter(([,v])=>v>0).sort((a,b)=>b[1]-a[1]);
                  const tot=vals.reduce((s,[,v])=>s+v,0);
                  return <div key={title}>
                    <div style={{fontSize:"11px",fontWeight:"700",color:"#374151",marginBottom:"8px"}}>{title}</div>
                    {vals.length===0?<p style={{fontSize:"11px",color:"#9ca3af"}}>No data.</p>
                    :vals.map(([label,count])=>(
                      <div key={label} style={{marginBottom:"6px"}}>
                        <div style={{display:"flex",justifyContent:"space-between",marginBottom:"2px"}}>
                          <span style={{fontSize:"10px",fontWeight:"600",color:"#374151"}}>{label}</span>
                          <span style={{fontSize:"10px",color:"#9ca3af"}}>{count} ({tot>0?Math.round(count/tot*100):0}%)</span>
                        </div>
                        <Bar pct={tot>0?Math.round(count/tot*100):0} color={colorMap[label]||C.pu}/>
                      </div>
                    ))}
                  </div>;
                })}
              </div>}
            </div>

            <div style={{padding:"11px 14px",background:"#f0fdf4",borderRadius:"9px",border:"1px solid #bbf7d0",
              fontSize:"11px",color:"#065f46",lineHeight:"1.6"}}>
              <strong>Data integrity:</strong> All IDs are SHA-256 hashed. First-attempt success rate is the primary
              validity metric. Role stratification requires trainees to set their role on first link access.
              Full dataset available via Export CSV above.
            </div>
          </>;
        })()
    )}

    {/* ══ RESEARCH PLAN TAB ══ */}

    {/* ══ DATA MANAGEMENT ══ */}
    {adminTab==="data"&&(<div>
      <div style={{marginBottom:"20px"}}>
        <div style={{fontWeight:"700",fontSize:"14px",color:C.dark,marginBottom:"4px"}}>Wipe Test Trainee Data</div>
        <p style={{fontSize:"13px",color:"#6b7280",margin:"0 0 16px"}}>
          Removes all research data for a given trainee email without touching educator cases or assignment structure.
          Specifically: deletes their <code>research_events</code>, resets their assignments to pending,
          deletes their <code>trainee_profiles</code> and <code>learner_registry</code> entries.
        </p>
        <div style={{...C.card,padding:"20px",maxWidth:"520px"}}>
          <div style={{fontSize:"10px",fontWeight:"700",color:"#6b7280",textTransform:"uppercase",
            letterSpacing:"0.07em",marginBottom:"6px"}}>Trainee Email</div>
          <div style={{display:"flex",gap:"8px",marginBottom:"12px"}}>
            <input
              value={wipeEmail}
              onChange={e=>{setWipeEmail(e.target.value);setWipeResult(null);setWipeErr("");}}
              placeholder="trainee@hospital.edu"
              style={{...C.inp,flex:1}}
            />
            <button
              disabled={wipeBusy||!wipeEmail.includes("@")}
              onClick={async()=>{
                if(!window.confirm(`Delete ALL research data for ${wipeEmail}?\n\nThis cannot be undone.`)) return;
                setWipeBusy(true);setWipeResult(null);setWipeErr("");
                try {
                  const fns=getFunctions(firebaseApp,"us-central1");
                  const res=await httpsCallable(fns,"wipeTestTrainee")({email:wipeEmail.trim().toLowerCase()});
                  setWipeResult(res?.data?.deleted||{});
                  setWipeEmail("");
                } catch(e){
                  setWipeErr(e?.details?.message||e?.message||"Wipe failed.");
                } finally{setWipeBusy(false);}
              }}
              style={{...C.btn("#dc2626"),flexShrink:0,opacity:(wipeBusy||!wipeEmail.includes("@"))?0.5:1}}>
              {wipeBusy?<Ic n="spin" s="w-4 h-4"/>:<Ic n="trash" s="w-4 h-4"/>}
              {wipeBusy?"Wiping…":"Wipe Data"}
            </button>
          </div>
          {wipeErr&&(
            <div style={{padding:"10px 12px",background:"#fef2f2",border:"1px solid #fca5a5",
              borderRadius:"8px",fontSize:"13px",color:"#dc2626"}}>{wipeErr}</div>
          )}
          {wipeResult&&(
            <div style={{padding:"12px 14px",background:"#f0fdf4",border:"1px solid #6ee7b7",
              borderRadius:"8px",fontSize:"12px",color:"#065f46"}}>
              <div style={{fontWeight:"700",marginBottom:"6px"}}>✓ Wipe complete</div>
              <div>Research events deleted: <strong>{wipeResult.research_events||0}</strong></div>
              <div>Assignments reset: <strong>{wipeResult.assignments_reset||0}</strong></div>
              <div>Profile deleted: <strong>{wipeResult.profile?"Yes":"Not found"}</strong></div>
              <div>Registry deleted: <strong>{wipeResult.registry?"Yes":"Not found"}</strong></div>
            </div>
          )}
        </div>
      </div>

      <div style={{...C.card,padding:"16px 20px",maxWidth:"640px",
        background:"#fffbeb",border:"1px solid #fde68a"}}>
        <div style={{fontWeight:"700",fontSize:"13px",color:"#92400e",marginBottom:"6px"}}>
          ⚠️ Note on case_stats
        </div>
        <p style={{fontSize:"12px",color:"#92400e",margin:0}}>
          The <code>/shared/case_stats_*</code> documents are aggregate counters and cannot be
          decremented per-trainee. After wiping a test trainee, manually delete any
          <code>case_stats_*</code> documents in the Firestore console that may have been
          inflated by test data, then let them rebuild from real submissions.
        </p>
      </div>
    </div>)}

    {/* ══ EDUCATOR ACCESS ══ */}
    {adminTab==="educators"&&(loading
      ?<p>Loading…</p>
      :Object.keys(edus).length===0
        ?<div style={{textAlign:"center",padding:"44px",color:"#9ca3af"}}>
            <p>No educator accounts yet. Requests submitted via the login page will appear here.</p>
          </div>
        :<div style={{display:"grid",gap:"9px"}}>
          {Object.values(edus).sort((a,b)=>a.approved===b.approved?0:a.approved?1:-1).map(edu=>(
            <div key={edu.email} style={{...C.card,padding:"13px 17px",display:"flex",alignItems:"center",gap:"12px",
              border:`1px solid ${edu.approved?"#bbf7d0":"#fde68a"}`}}>
              <div style={{flex:1}}>
                <div style={{fontWeight:"600",color:C.dark,fontSize:"13px"}}>{edu.name}</div>
                <div style={{fontSize:"12px",color:"#9ca3af"}}>{edu.email}</div>
                <div style={{fontSize:"11px",color:"#6b7280",marginTop:"3px",display:"flex",gap:"8px",flexWrap:"wrap"}}>
                  <span style={{fontWeight:"600",color:"#374151"}}>{edu.institution||"Institution not specified"}</span>
                  <span>·</span>
                  <span>{edu.role||"Role not specified"}</span>
                  <span>·</span>
                  <span>Requested {edu.requestedAt?new Date(edu.requestedAt).toLocaleDateString():"N/A"}</span>
                </div>
              </div>
              <span style={C.tag(edu.approved?"#d1fae5":"#fef3c7",edu.approved?"#065f46":"#92400e")}>
                {edu.approved?"Approved":"Pending"}
              </span>
              {!edu.approved&&<button onClick={()=>approve(edu.email)} style={{...C.btn("#059669")}}>Approve</button>}
              <button onClick={()=>revoke(edu.email)} style={{...C.btn("#fee2e2","#dc2626")}}>Remove</button>
            </div>
          ))}
        </div>
    )}
  </div>;
}


// ─── ASSIGNMENTS TAB ─────────────────────────────────────────────────────────
// Data model for a group assignment:
// {
//   id: "ag_TIMESTAMP",
//   name: "Assignment from Dr. X",
//   status: "pending" | "deployed",
//   caseIds: ["Room1", "Room5", ...],      // ordered list, 1-5
//   caseSelection: "individual" | "random",
//   randomSource: "default" | "all" | "favs",
//   startAt: ISO string | "",
//   expiresAt: ISO string | "",
//   timed: false | minutesNumber,
//   trainees: [{name, email}, ...],
//   feedbackMode: "immediate" | "delayed" | "hidden",
//   feedbackDate: "",
//   note: "",
//   createdAt: ISO,
//   deployedAt: ISO | null,
// }
function AssignmentsTab({educator,allCases,myOverrides,favourites,assignmentGroups,onSave,onDeploy,onDelete}){
  const [view,setView]=useState("list");          // "list" | "builder" | "detail"
  const [detailId,setDetailId]=useState(null);
  const [addCaseFromLib,setAddCaseFromLib]=useState(null); // caseId passed from CasesTab
  const [sessions,setSessions]=useState([]);       // live assignment_sessions for this educator
  const [sessionViewing,setSessionViewing]=useState(null); // session being shown in QR modal

  // Live snapshot of this educator's QR sessions
  React.useEffect(()=>{
    if(!educator?.email) return;
    const q = query(collection(db,'assignment_sessions'),
      where('educatorEmail','==',educator.email.toLowerCase()));
    const unsub = onSnapshot(q,(snap)=>{
      const rows = snap.docs.map(d=>({id:d.id,...d.data()}));
      // Sort by createdAt desc
      rows.sort((a,b)=>{
        const ta = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
        const tb = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
        return tb - ta;
      });
      setSessions(rows);
    },(err)=>console.warn('[KiRPA] sessions snapshot:',err));
    return ()=>unsub();
  },[educator?.email]);

  const closeSession = async (sessionId) => {
    if(!window.confirm("Close this session? No new participants will be able to join, but existing ones keep their assignments.")) return;
    try{
      const fns = getFunctions(firebaseApp,"us-central1");
      const closeFn = httpsCallable(fns,"closeAssignmentSession");
      await closeFn({sessionId});
    }catch(e){alert(e?.message||"Could not close session.");}
  };

  // Check if CasesTab pushed a case to add
  React.useEffect(()=>{
    const pending=sessionStorage.getItem("kirpa_addCase");
    if(pending){
      sessionStorage.removeItem("kirpa_addCase");
      setAddCaseFromLib(pending);
      setView("builder");
    }
  },[]);

  const groups = Array.isArray(assignmentGroups) ? assignmentGroups : [];
  const pending  = groups.filter(g=>g.status==="pending");
  const deployed = groups.filter(g=>g.status==="deployed");

  if(view==="builder") return <AssignmentBuilder
    educator={educator}
    allCases={allCases}
    favourites={favourites}
    groups={groups}
    initialCaseId={addCaseFromLib}
    onSave={async(g)=>{ await onSave([...groups.filter(x=>x.id!==g.id),g]); setView("list"); }}
    onDeploy={async (group, deployedAssignments) => {
  // Delegate entirely to Dashboard's onDeploy (saves group + flat assignments + emails)
  await onDeploy(group, deployedAssignments);
  setView("list");
}}
    onClose={()=>setView("list")}
  />;

  if(view==="detail"&&detailId){
    const g=groups.find(x=>x.id===detailId);
    if(g) return <AssignmentDetail
      group={g}
      allCases={allCases}
      onEdit={()=>setView("builder")}
      onDelete={onDelete ? async()=>{ await onDelete(g); setView("list"); } : null}
      onClose={()=>setView("list")}
    />;
  }

  // ── LIST VIEW ──
  const liveSessions = sessions.filter(s=>s.status==="open");
  const closedSessions = sessions.filter(s=>s.status==="closed");

  return <div style={{padding:"22px"}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"20px",flexWrap:"wrap",gap:"10px"}}>
      <h2 style={{margin:0,fontSize:"17px",fontWeight:"700",color:C.dark,fontFamily:FONT_UI}}>Assignments</h2>
      <button onClick={()=>setView("builder")} style={{...C.btn(C.pu)}}>
        <Ic n="plus" s="w-4 h-4"/> Build Assignment
      </button>
    </div>

    {groups.length===0&&liveSessions.length===0&&closedSessions.length===0&&<div style={{textAlign:"center",padding:"60px 24px"}}>
      <div style={{fontSize:"48px",marginBottom:"14px",opacity:0.25}}>📋</div>
      <div style={{fontWeight:"700",fontSize:"15px",color:"#374151",marginBottom:"6px"}}>No assignments yet</div>
      <p style={{fontSize:"13px",color:"#9ca3af",margin:"0 0 18px"}}>Build an assignment to bundle multiple cases and send to one or more trainees at once.</p>
      <button onClick={()=>setView("builder")} style={{...C.btn(C.pu)}}>
        <Ic n="plus" s="w-4 h-4"/> Build First Assignment
      </button>
    </div>}

    {liveSessions.length>0&&<div style={{marginBottom:"26px"}}>
      <div style={{fontSize:"11px",fontWeight:"700",color:"#9ca3af",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:"10px",display:"flex",alignItems:"center",gap:"7px"}}>
        <span style={{display:"inline-block",width:"8px",height:"8px",borderRadius:"50%",background:"#22c55e",
          boxShadow:"0 0 0 3px rgba(34,197,94,0.2)",animation:"spin 2s linear infinite"}}/>
        Live QR Sessions · {liveSessions.length}
      </div>
      <div style={{display:"grid",gap:"9px"}}>
        {liveSessions.map(s=><SessionCard key={s.id} session={s}
          onShowQR={()=>setSessionViewing(s)}
          onClose={()=>closeSession(s.id)}/>)}
      </div>
    </div>}

    {closedSessions.length>0&&<div style={{marginBottom:"26px"}}>
      <div style={{fontSize:"11px",fontWeight:"700",color:"#9ca3af",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:"10px"}}>
        Closed QR Sessions · {closedSessions.length}
      </div>
      <div style={{display:"grid",gap:"9px"}}>
        {closedSessions.map(s=><SessionCard key={s.id} session={s}
          onShowQR={null}
          onClose={null}/>)}
      </div>
    </div>}

    {deployed.length>0&&<div style={{marginBottom:"26px"}}>
      <div style={{fontSize:"11px",fontWeight:"700",color:"#9ca3af",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:"10px"}}>
        Deployed · {deployed.length}
      </div>
      <div style={{display:"grid",gap:"9px"}}>
        {deployed.map(g=><AssignmentCard key={g.id} group={g} allCases={allCases}
          onClick={()=>{setDetailId(g.id);setView("detail");}}
          onDelete={onDelete}/>)}
      </div>
    </div>}

    {pending.length>0&&<div>
      <div style={{fontSize:"11px",fontWeight:"700",color:"#9ca3af",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:"10px"}}>
        Pending (Saved, not deployed) · {pending.length}
      </div>
      <div style={{display:"grid",gap:"9px"}}>
        {pending.map(g=><AssignmentCard key={g.id} group={g} allCases={allCases}
          onClick={()=>{setDetailId(g.id);setView("detail");}}
          onDelete={onDelete}/>)}
      </div>
    </div>}

    {/* Re-display QR for a live session */}
    {sessionViewing && <SessionQRDisplay
      session={{joinCode:sessionViewing.id, joinUrl:`${window.location.origin}/?join=${sessionViewing.id}`}}
      sessionName={sessionViewing.sessionName||sessionViewing.id}
      caseCount={(sessionViewing.caseIds||[]).length}
      maxParticipants={sessionViewing.maxParticipants||50}
      closeHours={Math.max(1,Math.round((new Date(sessionViewing.closeAt).getTime()-Date.now())/3600000))}
      pinEnabled={!!sessionViewing.pinHash}
      pin={null}
      allowAnonymous={sessionViewing.allowAnonymous}
      onClose={()=>setSessionViewing(null)}/>}
  </div>;
}

// ── Session list card (for live + closed QR sessions) ────────────────────────
function SessionCard({session,onShowQR,onClose}){
  const count = session.participantCount || 0;
  const max   = session.maxParticipants || 50;
  const pct   = Math.min(100, Math.round((count/max)*100));
  const isOpen = session.status === "open";
  const closeAt = session.closeAt ? new Date(session.closeAt) : null;
  const expired = closeAt && closeAt < new Date();
  const timeLeft = closeAt && isOpen && !expired
    ? (() => {
        const ms = closeAt.getTime() - Date.now();
        const h = Math.floor(ms/3600000);
        if(h < 1)  return `${Math.floor(ms/60000)} min left`;
        if(h < 48) return `${h}h left`;
        return `${Math.floor(h/24)}d left`;
      })()
    : null;

  return <div style={{...C.card,padding:"14px 18px",display:"flex",alignItems:"center",gap:"14px"}}>
    <div style={{width:"42px",height:"42px",borderRadius:"10px",
      background:isOpen?"linear-gradient(135deg,#dcfce7,#bbf7d0)":"#f3f4f6",
      display:"flex",alignItems:"center",justifyContent:"center",
      fontSize:"20px",flexShrink:0}}>📲</div>
    <div style={{flex:1,minWidth:0}}>
      <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"3px",flexWrap:"wrap"}}>
        <div style={{fontWeight:"700",fontSize:"14px",color:C.dark,fontFamily:FONT_UI}}>{session.sessionName||session.id}</div>
        <span style={{fontFamily:"monospace",fontSize:"11px",padding:"2px 8px",borderRadius:"6px",
          background:"#f3f4f6",color:"#374151",letterSpacing:"0.1em",fontWeight:"700"}}>{session.id}</span>
        {isOpen && !expired && <span style={{fontSize:"10px",padding:"2px 7px",borderRadius:"10px",
          background:"#dcfce7",color:"#166534",fontWeight:"700"}}>OPEN</span>}
        {(!isOpen || expired) && <span style={{fontSize:"10px",padding:"2px 7px",borderRadius:"10px",
          background:"#f3f4f6",color:"#6b7280",fontWeight:"700"}}>CLOSED</span>}
      </div>
      <div style={{display:"flex",alignItems:"center",gap:"10px",fontSize:"11px",color:"#6b7280",flexWrap:"wrap"}}>
        <span>{(session.caseIds||[]).length} case{(session.caseIds||[]).length!==1?"s":""}</span>
        <span>·</span>
        <span><strong style={{color:C.dark}}>{count}</strong> / {max} joined</span>
        {timeLeft && <><span>·</span><span>{timeLeft}</span></>}
        {session.allowAnonymous && <><span>·</span><span>Anon</span></>}
        {session.pinHash && <><span>·</span><span>🔒 PIN</span></>}
      </div>
      {isOpen && <div style={{marginTop:"6px",height:"4px",background:"#f3f4f6",borderRadius:"2px",overflow:"hidden"}}>
        <div style={{width:`${pct}%`,height:"100%",background:pct>=90?"#dc2626":pct>=70?"#f59e0b":"#22c55e",
          transition:"width 0.3s"}}/>
      </div>}
    </div>
    <div style={{display:"flex",gap:"6px",flexShrink:0}}>
      {onShowQR && <button onClick={onShowQR}
        style={{...C.btn(C.pu,"white",{padding:"7px 12px",fontSize:"12px"})}}>
        Show QR
      </button>}
      {onClose && <button onClick={onClose}
        style={{...C.btn("#fee2e2","#dc2626",{padding:"7px 12px",fontSize:"12px"})}}>
        Close
      </button>}
    </div>
  </div>;
}

// ── Assignment list card ──────────────────────────────────────────────────────
function AssignmentCard({group,allCases,onClick,onDelete}){
  const traineeCount=group.trainees?.length||0;
  const caseCount=group.caseIds?.length||0;
  const isDeployed=group.status==="deployed";
  return (
    <div style={{...C.card,padding:"14px 18px",
      display:"flex",alignItems:"center",gap:"14px",
      border:`1px solid ${isDeployed?"#c4b5fd":"#fde68a"}`,
      transition:"box-shadow 0.15s"}}
      onMouseEnter={e=>e.currentTarget.style.boxShadow="0 4px 16px rgba(79,38,131,0.12)"}
      onMouseLeave={e=>e.currentTarget.style.boxShadow=""}>
      {/* clicking the main body opens detail */}
      <div onClick={onClick} style={{display:"flex",alignItems:"center",gap:"14px",flex:1,minWidth:0,cursor:"pointer"}}>
        <div style={{width:"42px",height:"42px",borderRadius:"10px",flexShrink:0,
          background:isDeployed?"#ede9fe":"#fef9c3",
          display:"flex",alignItems:"center",justifyContent:"center",fontSize:"20px"}}>
          {isDeployed?"📤":"📝"}
        </div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontWeight:"700",fontSize:"13px",color:C.dark,marginBottom:"3px",fontFamily:FONT_UI}}>
            {group.name}
          </div>
          <div style={{fontSize:"11px",color:"#9ca3af",fontFamily:FONT_UI}}>
            {caseCount} case{caseCount!==1?"s":""} · {traineeCount} trainee{traineeCount!==1?"s":""}
            {group.expiresAt&&` · Expires ${new Date(group.expiresAt).toLocaleDateString()}`}
            {group.timed&&` · ${group.timed} min timed`}
          </div>
          {caseCount>0&&<div style={{display:"flex",gap:"4px",flexWrap:"wrap",marginTop:"5px"}}>
            {group.caseIds.slice(0,4).map(id=>{
              const c=allCases[id];
              if(!c) return null;
              const col=getFacetCol(c.facet);
              return <span key={id} style={{fontSize:"10px",padding:"1px 7px",borderRadius:"8px",
                background:col.pill,color:col.pillTx,fontWeight:"600"}}>{c.title||id}</span>;
            })}
            {caseCount>4&&<span style={{fontSize:"10px",color:"#9ca3af"}}>+{caseCount-4} more</span>}
          </div>}
        </div>
        <div style={{textAlign:"right",flexShrink:0}}>
          <span style={C.tag(isDeployed?"#ede9fe":"#fef9c3",isDeployed?C.pu:"#92400e")}>
            {isDeployed?"Deployed":"Pending"}
          </span>
          {group.deployedAt&&<div style={{fontSize:"10px",color:"#9ca3af",marginTop:"4px"}}>
            {new Date(group.deployedAt).toLocaleDateString()}
          </div>}
        </div>
      </div>
      {/* Delete / Cancel button */}
      {onDelete&&(
        <button
          onClick={e=>{e.stopPropagation();onDelete(group);}}
          title={isDeployed?"Cancel assignment":"Delete draft"}
          style={{flexShrink:0,width:"30px",height:"30px",borderRadius:"8px",
            border:`1px solid ${isDeployed?"#fecaca":"#e5e7eb"}`,
            background:isDeployed?"#fef2f2":"#f9fafb",
            color:isDeployed?"#dc2626":"#9ca3af",
            cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",
            fontSize:"15px",fontFamily:"inherit",transition:"all 0.15s"}}
          onMouseEnter={e=>{e.currentTarget.style.background=isDeployed?"#fee2e2":"#f3f4f6";e.currentTarget.style.color=isDeployed?"#b91c1c":"#6b7280";}}
          onMouseLeave={e=>{e.currentTarget.style.background=isDeployed?"#fef2f2":"#f9fafb";e.currentTarget.style.color=isDeployed?"#dc2626":"#9ca3af";}}>
          {isDeployed?"✕":"🗑"}
        </button>
      )}
    </div>
  );
}

// ── Assignment Detail View ────────────────────────────────────────────────────
function AssignmentDetail({group,allCases,onEdit,onDelete,onClose}){
  const [selTrainee,setSelTrainee]=useState(null);
  const caseCount=group.caseIds?.length||0;
  return (
    <div style={{padding:"22px"}}>
      {/* header */}
      <div style={{display:"flex",alignItems:"center",gap:"10px",marginBottom:"20px",flexWrap:"wrap"}}>
        <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",
          color:"#6b7280",padding:"4px",display:"flex",alignItems:"center",gap:"4px",
          fontSize:"12px",fontWeight:"600",fontFamily:"inherit"}}>
          <Ic n="x" s="w-4 h-4"/> Back
        </button>
        <h2 style={{margin:0,fontSize:"16px",fontWeight:"700",color:C.dark,flex:1,fontFamily:FONT_UI}}>
          {group.name}
        </h2>
        <span style={C.tag(group.status==="deployed"?"#ede9fe":"#fef9c3",group.status==="deployed"?C.pu:"#92400e")}>
          {group.status==="deployed"?"Deployed":"Pending"}
        </span>
        {group.status==="pending"&&<button onClick={onEdit} style={{...C.btn(C.pu)}}>
          <Ic n="save" s="w-4 h-4"/> Edit
        </button>}
        {onDelete&&(
          <button onClick={onDelete}
            style={{...C.btn(group.status==="deployed"?"#fef2f2":"#f9fafb",group.status==="deployed"?"#dc2626":"#6b7280",
              {border:`1px solid ${group.status==="deployed"?"#fecaca":"#e5e7eb"}`}),
              padding:"7px 14px",fontSize:"12px",fontWeight:"600"}}>
            {group.status==="deployed"?"✕ Cancel Assignment":"🗑 Delete Draft"}
          </button>
        )}
      </div>

      {/* meta strip */}
      <div style={{display:"flex",gap:"16px",flexWrap:"wrap",marginBottom:"20px",
        padding:"13px 16px",background:"#f8f7ff",borderRadius:"10px",border:"1px solid #ede9fe"}}>
        {[
          ["Cases",`${caseCount} selected`],
          ["Trainees",`${group.trainees?.length||0}`],
          ["Start",group.startAt?new Date(group.startAt).toLocaleString():"Any time"],
          ["Expires",group.expiresAt?new Date(group.expiresAt).toLocaleString():"No expiry"],
          ["Timed",group.timed?`${group.timed} minutes`:"No"],
          ["Feedback",group.feedbackMode||"immediate"],
        ].map(([l,v])=><div key={l}>
          <div style={{fontSize:"10px",fontWeight:"700",color:"#9ca3af",textTransform:"uppercase",letterSpacing:"0.06em"}}>{l}</div>
          <div style={{fontSize:"13px",fontWeight:"600",color:C.dark,fontFamily:FONT_UI}}>{v}</div>
        </div>)}
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"18px"}}>
        {/* Cases panel */}
        <div>
          <div style={{fontSize:"12px",fontWeight:"700",color:C.dark,marginBottom:"10px",fontFamily:FONT_UI}}>
            Cases ({caseCount})
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:"7px"}}>
            {group.caseIds?.map((id,i)=>{
              const c=allCases[id];
              const col=getFacetCol(c?.facet);
              return <div key={id} style={{padding:"10px 12px",borderRadius:"9px",
                background:`linear-gradient(160deg,${col.bg},white)`,
                border:`1px solid ${col.border}`,display:"flex",gap:"10px",alignItems:"center"}}>
                <div style={{width:"22px",height:"22px",borderRadius:"50%",background:col.accent,
                  color:"white",fontSize:"10px",fontWeight:"800",display:"flex",
                  alignItems:"center",justifyContent:"center",flexShrink:0}}>{i+1}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:"12px",fontWeight:"600",color:C.dark,fontFamily:FONT_UI}}>{c?.title||id}</div>
                  <div style={{fontSize:"10px",color:"#9ca3af"}}>{c?.facet||""}</div>
                </div>
              </div>;
            })}
          </div>
        </div>

        {/* Trainees panel */}
        <div>
          <div style={{fontSize:"12px",fontWeight:"700",color:C.dark,marginBottom:"10px",fontFamily:FONT_UI}}>
            Trainees ({group.trainees?.length||0})
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:"7px"}}>
            {group.trainees?.map((t,i)=>(
              <div key={i} onClick={()=>setSelTrainee(selTrainee===i?null:i)}
                style={{padding:"10px 12px",borderRadius:"9px",border:`1px solid ${selTrainee===i?"#c4b5fd":"#e5e7eb"}`,
                  background:selTrainee===i?"#faf5ff":"white",cursor:"pointer",
                  display:"flex",gap:"9px",alignItems:"center",transition:"all 0.12s"}}>
                <div style={{width:"30px",height:"30px",borderRadius:"50%",background:"#ede9fe",
                  display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontSize:"12px",fontWeight:"700",color:C.pu}}>
                  {(t.name||t.email||"?")[0].toUpperCase()}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:"12px",fontWeight:"600",color:C.dark,fontFamily:FONT_UI}}>{t.name||"—"}</div>
                  <div style={{fontSize:"10px",color:"#9ca3af",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.email}</div>
                </div>
                <Ic n={selTrainee===i?"x":"eye"} s="w-3 h-3" style={{color:"#9ca3af",flexShrink:0}}/>
              </div>
            ))}
          </div>
          {selTrainee!==null&&group.trainees&&group.trainees[selTrainee]&&(
            <div style={{marginTop:"10px",padding:"12px 14px",background:"#f0f4ff",borderRadius:"9px",border:"1px solid #c7d7fd"}}>
              <div style={{fontSize:"11px",fontWeight:"700",color:"#3b4fc9",marginBottom:"8px",fontFamily:FONT_UI}}>
                {group.trainees[selTrainee].name} — Progress
              </div>
              <div style={{fontSize:"11px",color:"#6b7280",fontStyle:"italic"}}>
                Submission data is written to Firestore in real time when the trainee completes the case.
                Refresh this page to see updated progress.
              </div>
            </div>
          )}
        </div>
      </div>

      {group.note&&<div style={{marginTop:"18px",padding:"12px 14px",background:"#fffbeb",
        borderRadius:"9px",border:"1px solid #fde68a",fontSize:"12px",color:"#78350f"}}>
        <strong>Note: </strong>{group.note}
      </div>}
    </div>
  );
}

// ── Assignment Builder ────────────────────────────────────────────────────────
// ── Session QR display (shown after createAssignmentSession succeeds) ────────
// Educator-facing modal: big QR, joinCode, copyable URL, projector fullscreen.
function SessionQRDisplay({session,sessionName,caseCount,maxParticipants,closeHours,
  pinEnabled,pin,allowAnonymous,onClose}){
  const [copied,setCopied]=useState("");
  const [fullscreen,setFullscreen]=useState(false);
  const {joinCode,joinUrl}=session||{};

  const copy=async(text,label)=>{
    try{await navigator.clipboard.writeText(text);setCopied(label);setTimeout(()=>setCopied(""),1600);}
    catch{prompt(`Copy this ${label}:`,text);}
  };

  // Fullscreen projector mode — same QR/code, oversized for room display
  if(fullscreen){
    return <div style={{position:"fixed",inset:0,background:"#0f0a1e",zIndex:9999,
      display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",
      padding:"24px",fontFamily:FONT_UI}}>
      <div style={{textAlign:"center",marginBottom:"22px"}}>
        <div style={{fontSize:"14px",color:"rgba(255,255,255,0.55)",fontWeight:"700",letterSpacing:"0.15em",
          textTransform:"uppercase",marginBottom:"10px"}}>KiRPA · Scan to Join</div>
        <div style={{fontSize:"28px",fontWeight:"800",color:"white",letterSpacing:"-0.01em"}}>{sessionName}</div>
      </div>
      <div style={{background:"white",padding:"28px",borderRadius:"18px",boxShadow:"0 20px 60px rgba(0,0,0,0.5)"}}>
        <QRCodeSVG value={joinUrl} size={Math.min(window.innerWidth*0.6,520)} level="M"/>
      </div>
      <div style={{marginTop:"24px",textAlign:"center"}}>
        <div style={{fontSize:"14px",color:"rgba(255,255,255,0.5)",marginBottom:"6px"}}>
          Or visit <span style={{color:"white",fontWeight:"700"}}>{new URL(joinUrl).host}</span> and enter join code
        </div>
        <div style={{fontFamily:FONT_MONO,fontSize:"56px",fontWeight:"800",
          color:"white",letterSpacing:"0.2em"}}>{joinCode}</div>
        {pinEnabled&&pin&&<div style={{fontSize:"16px",color:"rgba(255,255,255,0.7)",marginTop:"10px"}}>
          PIN: <strong style={{color:"white",letterSpacing:"0.15em"}}>{pin}</strong>
        </div>}
      </div>
      <button onClick={()=>setFullscreen(false)}
        style={{position:"fixed",top:"22px",right:"22px",background:"rgba(255,255,255,0.08)",
          border:"1px solid rgba(255,255,255,0.2)",color:"white",padding:"8px 14px",borderRadius:"8px",
          cursor:"pointer",fontSize:"12px",fontWeight:"700",fontFamily:FONT_UI}}>
        ✕ Exit fullscreen
      </button>
    </div>;
  }

  return <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:200,
    display:"flex",alignItems:"center",justifyContent:"center",padding:"16px",overflowY:"auto"}}
    onClick={onClose}>
    <div style={{background:"white",borderRadius:"16px",padding:"28px",maxWidth:"520px",
      width:"100%",boxShadow:"0 24px 60px rgba(0,0,0,0.4)",fontFamily:FONT_UI}}
      onClick={e=>e.stopPropagation()}>
      <div style={{textAlign:"center",marginBottom:"18px"}}>
        <div style={{width:"56px",height:"56px",background:"#d1fae5",borderRadius:"50%",
          display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 12px"}}>
          <Ic n="check" s="w-7 h-7"/>
        </div>
        <h3 style={{color:"#065f46",fontSize:"18px",fontWeight:"700",margin:"0 0 4px"}}>Session ready</h3>
        <p style={{color:"#6b7280",fontSize:"12px",margin:0}}>
          {caseCount} case{caseCount!==1?"s":""} · up to {maxParticipants} participants · expires in {closeHours<24?`${closeHours}h`:closeHours<168?`${closeHours/24}d`:`${closeHours/168}wk`}
          {allowAnonymous?" · anonymous join allowed":""}
          {pinEnabled?" · PIN-gated":""}
        </p>
      </div>

      <div style={{display:"flex",justifyContent:"center",marginBottom:"16px",
        padding:"18px",background:"#fafafa",borderRadius:"12px",border:"1px solid #e5e7eb"}}>
        <QRCodeSVG value={joinUrl} size={220} level="M"/>
      </div>

      <div style={{marginBottom:"12px"}}>
        <label style={C.lbl}>Join Code (verbal / typed entry)</label>
        <div style={{display:"flex",gap:"6px"}}>
          <div style={{flex:1,padding:"10px 14px",background:"#f9fafb",border:"1.5px solid #e5e7eb",
            borderRadius:"8px",fontFamily:FONT_MONO,fontSize:"22px",fontWeight:"800",
            color:C.dark,letterSpacing:"0.18em",textAlign:"center"}}>{joinCode}</div>
          <button onClick={()=>copy(joinCode,"code")}
            style={{...C.btn("#f3f4f6","#374151",{padding:"10px 14px",fontSize:"12px"})}}>
            {copied==="code"?"✓":"Copy"}
          </button>
        </div>
      </div>

      <div style={{marginBottom:"16px"}}>
        <label style={C.lbl}>Join URL</label>
        <div style={{display:"flex",gap:"6px"}}>
          <input readOnly value={joinUrl} style={{...C.inp,fontSize:"11px",fontFamily:"monospace"}}/>
          <button onClick={()=>copy(joinUrl,"url")}
            style={{...C.btn("#f3f4f6","#374151",{padding:"8px 14px",fontSize:"12px"})}}>
            {copied==="url"?"✓":"Copy"}
          </button>
        </div>
      </div>

      {pinEnabled&&pin&&<div style={{padding:"10px 13px",background:"#fffbeb",border:"1px solid #fde68a",
        borderRadius:"8px",marginBottom:"16px"}}>
        <div style={{fontSize:"11px",fontWeight:"700",color:"#92400e",marginBottom:"3px"}}>PIN (share separately)</div>
        <div style={{fontFamily:"monospace",fontSize:"18px",fontWeight:"800",letterSpacing:"0.2em",color:"#78350f"}}>{pin}</div>
      </div>}

      <div style={{display:"flex",gap:"8px"}}>
        <button onClick={()=>setFullscreen(true)} style={{...C.btn(C.pu),flex:1,justifyContent:"center"}}>
          📺 Show fullscreen (projector)
        </button>
        <button onClick={onClose} style={{...C.btn("#f3f4f6","#374151")}}>Done</button>
      </div>

      <p style={{fontSize:"11px",color:"#9ca3af",marginTop:"14px",lineHeight:"1.6",textAlign:"center"}}>
        Participants will appear in your <strong>Trainees</strong> tab as they join. You can close the session anytime from <strong>Assignments</strong>.
      </p>
    </div>
  </div>;
}

function AssignmentBuilder({educator,allCases,favourites,groups,initialCaseId,onSave,onDeploy,onClose}){
  const allIds=Object.keys(allCases);
  const baseIds=allIds.filter(id=>!id.startsWith("custom_"));
  const favIds=allIds.filter(id=>favourites?.has(id));

  // ── Draft state ──
  const [name,setName]=useState(`Assignment from ${educator.name}`);
  const [caseCount,setCaseCount]=useState(3);
  const [selMode,setSelMode]=useState("individual"); // "individual" | "random"
  const [randomSrc,setRandomSrc]=useState("default"); // "default" | "all" | "favs"
  const [selectedCaseIds,setSelectedCaseIds]=useState(initialCaseId?[initialCaseId]:[]);
  const [startAt,setStartAt]=useState("");
  const [expiresAt,setExpiresAt]=useState("");
  const [timed,setTimed]=useState(false);
  const [timedMins,setTimedMins]=useState(60);
  const [feedbackMode,setFeedbackMode]=useState("immediate");
  const [feedbackDate,setFeedbackDate]=useState("");
  const [note,setNote]=useState("");
  const [isAnonymous,setIsAnonymous]=useState(false);
  const [trainees,setTrainees]=useState([{name:"",email:""}]);
  const [emailBulk,setEmailBulk]=useState("");
  const [step,setStep]=useState(1);    // 1=config, 2=cases, 3=trainees
  const [busy,setBusy]=useState(false);
  const [caseSearch,setCaseSearch]=useState("");
  const [caseFacet,setCaseFacet]=useState("All");
  const [caseLibTab,setCaseLibTab]=useState("default"); // "default"|"all"|"favs" — for individual mode
  const [previewCase,setPreviewCase]=useState(null);

  // ── Open-session (QR-based) state ──
  // deployMode: "trainees" (existing behavior) or "session" (open QR-based join)
  const [deployMode,setDeployMode]=useState("trainees"); // "trainees" | "session"
  const [sessionMaxParticipants,setSessionMaxParticipants]=useState(50);
  const [sessionCloseHours,setSessionCloseHours]=useState(72); // 1h..720h (30d)
  const [sessionAllowAnonymous,setSessionAllowAnonymous]=useState(false);
  const [sessionPinEnabled,setSessionPinEnabled]=useState(false);
  const [sessionPin,setSessionPin]=useState("");
  const [createdSession,setCreatedSession]=useState(null); // {sessionId, joinCode, joinUrl}

  // Auto-pick random selection when entering step 2 in random mode
  React.useEffect(()=>{
    if(step===2 && selMode==="random" && selectedCaseIds.length===0){
      pickRandom();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[step]);

  // Case pool for random selection
  const poolForRandom = () => {
    if(randomSrc==="default") return baseIds;
    if(randomSrc==="favs")    return favIds.length>0?favIds:baseIds;
    return allIds;
  };

  const pickRandom = () => {
    const pool=poolForRandom();
    const shuffled=[...pool].sort(()=>Math.random()-0.5);
    setSelectedCaseIds(shuffled.slice(0,caseCount));
  };

  // Case selection toggle
  const toggleCase=(id)=>{
    setSelectedCaseIds(prev=>{
      if(prev.includes(id)) return prev.filter(x=>x!==id);
      if(prev.length>=caseCount) return prev; // at cap
      return [...prev,id];
    });
  };

  // Parse bulk emails
  const parseBulkEmails=()=>{
    const parts=emailBulk.split(/[,;\n]+/).map(s=>s.trim()).filter(s=>s.includes("@"));
    if(!parts.length) return;
    const newOnes=parts.map(e=>({name:"",email:e}));
    setTrainees(prev=>{
      const existing=prev.filter(t=>t.email);
      const combined=[...existing];
      newOnes.forEach(n=>{ if(!combined.find(x=>x.email===n.email)) combined.push(n); });
      return combined.length?combined:[{name:"",email:""}];
    });
    setEmailBulk("");
  };

  // Build the group object
  const buildGroup = (status) => ({
    id:`ag_${Date.now()}_${Math.random().toString(36).slice(2,6)}`,
    name:name.trim()||`Assignment from ${educator.name}`,
    status,
    educatorEmail:educator.email,
    educatorName:educator.name,
    educatorInstitution:educator.institution||"",
    educatorInstitutionId:educator.institutionId||"",
    caseIds:selectedCaseIds,
    caseSelection:selMode,
    randomSource:selMode==="random"?randomSrc:null,
    startAt,expiresAt,
    timed:timed?timedMins:false,
    feedbackMode,feedbackDate,note,
    isAnonymous,
    trainees:trainees.filter(t=>t.email),
    createdAt:new Date().toISOString(),
    deployedAt:null,
  });

  // Flat individual assignments for each trainee × each case
    const buildFlatAssignments=(group)=>{
    const flat=[];
    group.trainees.forEach((t, tIndex)=>{
      // IMPORTANT: one anonymousId per trainee (shared across all their cases)
      const anonId = group.isAnonymous
        ? `anon_${Math.random().toString(36).slice(2,10)}_${Date.now().toString(36)}_${tIndex}`
        : null;

      group.caseIds.forEach(cId=>{
        const c=allCases[cId];
        flat.push({
          id:`a_${Date.now()}_${Math.random().toString(36).slice(2,7)}`,
          groupId:group.id,
          caseId:cId,
          caseTitle:c?.title||cId,
          caseFacet:c?.facet||"",
          caseOptions:c?.caseOptions||null,
          // Embed educator's overridden case data so trainee always gets it
          caseOverride: c ? stripUndefined({
            page:                c.page,
            initialState:        c.initialState,
            initialPrescription: c.initialPrescription,
            successType:         c.successType         || null,
            successParam:        c.successParam        != null ? c.successParam : null,
            successCriteria:     c.successCriteria     || null,
            criteriaLogic:       c.criteriaLogic       || null,
            prescribingMode:     c.prescribingMode     || null,
            caseOptions:         c.caseOptions         || null,
            successSummary:      c.successSummary      || null,
            learningPoint:       c.learningPoint       || null,
          }) : null,
          educatorEmail:group.educatorEmail,
          educatorName:group.educatorName,
          educatorInstitution:group.educatorInstitution,
          educatorInstitutionId:group.educatorInstitutionId,
          traineeName: group.isAnonymous ? `Anonymous Trainee ${flat.length+1}` : t.name,
          traineeEmail: group.isAnonymous ? null : t.email,
          // anonymousId: stable per-trainee UUID used when isAnonymous.
          // The magic link contains this token, NOT the email.
          // The educator sees "Anonymous Trainee N" — real email is never stored.
                    anonymousId: anonId,
          isAnonymous: group.isAnonymous || false,
          realEmail: group.isAnonymous ? t.email : null, // kept only for sending the link, never shown
          traineeToken:`tok_${Date.now()}_${Math.random().toString(36).slice(2,10)}`,
          feedbackMode:group.feedbackMode,
          deadline:group.expiresAt,
          feedbackDate:group.feedbackDate,
          note:group.note,
          timed:group.timed,
          startAt:group.startAt,
          sentAt:new Date().toISOString(),
          status:"pending",
          result:null,
        });
      });
    });
    return flat;
  };

  // ── Create open-session (QR-based join) via CF ──
  const createSession = async () => {
    setBusy(true);
    try {
      // Build per-case metadata so the join CF can mint each per-trainee assignment
      // with the same overrides/options as the educator-curated case.
      const caseMetaByCase = {};
      selectedCaseIds.forEach(cId => {
        const c = allCases[cId];
        if (!c) return;
        const caseOverride = stripUndefined({
          page:                c.page,
          initialState:        c.initialState,
          initialPrescription: c.initialPrescription,
          successType:         c.successType         || null,
          successParam:        c.successParam        != null ? c.successParam : null,
          successCriteria:     c.successCriteria     || null,
          criteriaLogic:       c.criteriaLogic       || null,
          prescribingMode:     c.prescribingMode     || null,
          caseOptions:         c.caseOptions         || null,
          successSummary:      c.successSummary      || null,
          learningPoint:       c.learningPoint       || null,
        });
        caseMetaByCase[cId] = {
          caseTitle:               c.title || cId,
          caseFacet:               c.facet || "",
          caseOptions:             c.caseOptions || null,
          caseOverride,
          caseInitialPrescription: c.initialPrescription ? stripUndefined(c.initialPrescription) : null,
          caseInitialState:        c.initialState        ? stripUndefined(c.initialState)        : null,
          caseWeightKg:            c.initialState?.weightKg  || null,
          caseBsa:                 c.initialState?.bsa        || null,
          caseAgeMonths:           c.initialState?.ageMonths != null ? c.initialState.ageMonths : null,
        };
      });

      const closeAt = new Date(Date.now() + Math.max(1, sessionCloseHours) * 3600000).toISOString();

      const fns = getFunctions(firebaseApp, "us-central1");
      const create = httpsCallable(fns, "createAssignmentSession");
      const res = await create({
        sessionName:            name.trim() || `Session from ${educator.name}`,
        educatorName:           educator.name || "",
        educatorInstitution:    educator.institution    || "",
        educatorInstitutionId:  educator.institutionId  || "",
        caseIds:                selectedCaseIds,
        caseMetaByCase,
        feedbackMode,
        feedbackDate:           feedbackDate || null,
        maxAttempts:            1,
        maxParticipants:        sessionMaxParticipants,
        closeAt,
        allowAnonymous:         sessionAllowAnonymous,
        pin:                    sessionPinEnabled && /^\d{4}$/.test(sessionPin) ? sessionPin : null,
        note:                   note || "",
      });
      setCreatedSession(res?.data || null);
    } catch (e) {
      console.error("createAssignmentSession:", e);
      alert(e?.message || "Could not create session. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  // Filtered case list for step 2
  const q=caseSearch.toLowerCase().trim();
  const casesForPicker=allIds.filter(id=>{
    const c=allCases[id];
    // Library tab filter (individual mode only)
    const libOk = caseLibTab==="all" ? true
      : caseLibTab==="favs" ? favourites?.has(id)
      : !id.startsWith("custom_"); // "default" = built-in only
    const facetOk=caseFacet==="All"||c.facet===caseFacet||c.facet?.startsWith(caseFacet);
    const searchOk=!q||c.title?.toLowerCase().includes(q)||c.facet?.toLowerCase().includes(q);
    return libOk&&facetOk&&searchOk;
  });

  // Steps
  const canStep2 = name.trim().length>0 && caseCount>=1 && caseCount<=5;
  const canStep3 = selMode==="individual"
    ? selectedCaseIds.length===caseCount
    : selectedCaseIds.length>0;
  const canDeploy = deployMode==="session"
    ? canStep3
    : (canStep3 && trainees.some(t=>t.email));

  const STEP_LABELS = deployMode==="session"
    ? ["Configuration","Case Selection","Session & QR"]
    : ["Configuration","Case Selection","Trainees & Deploy"];

  return (
    <div style={{padding:"22px"}}>
      {/* top bar */}
      <div style={{display:"flex",alignItems:"center",gap:"10px",marginBottom:"20px",flexWrap:"wrap"}}>
        <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",
          color:"#6b7280",padding:"4px",display:"flex",alignItems:"center",gap:"4px",
          fontSize:"12px",fontWeight:"600",fontFamily:"inherit"}}>
          <Ic n="x" s="w-4 h-4"/> Cancel
        </button>
        <h2 style={{margin:0,fontSize:"16px",fontWeight:"700",color:C.dark,flex:1,fontFamily:FONT_UI}}>
          Build Assignment
        </h2>
        <button onClick={async()=>{setBusy(true);await onSave(buildGroup("pending"));setBusy(false);}}
          disabled={!canStep2||busy}
          style={{...C.btn("#f3f4f6","#374151"),opacity:(!canStep2||busy)?0.5:1}}>
          {busy?<Ic n="spin" s="w-4 h-4"/>:<Ic n="save" s="w-4 h-4"/>} Save (Pending)
        </button>
        <button onClick={async()=>{
          if(!canDeploy) return;
          if(deployMode==="session"){
            await createSession();
            return;
          }
          setBusy(true);
          const g=buildGroup("deployed");
          await onDeploy(g,buildFlatAssignments(g));
          setBusy(false);
        }}
          disabled={!canDeploy||busy}
          style={{...C.btn(canDeploy?C.pu:"#e5e7eb",canDeploy?"white":"#9ca3af"),opacity:busy?0.7:1}}>
          {busy?<Ic n="spin" s="w-4 h-4"/>:<Ic n="send" s="w-4 h-4"/>}
          {deployMode==="session" ? "Generate QR Session" : "Deploy Assignment"}
        </button>
      </div>

      {/* step progress */}
      <div style={{display:"flex",gap:"4px",marginBottom:"22px"}}>
        {STEP_LABELS.map((l,i)=>{
          const n=i+1; const active=step===n; const done=step>n;
          return <button key={n} onClick={()=>setStep(n)}
            style={{flex:1,padding:"9px 8px",borderRadius:"9px",border:`1.5px solid ${active?C.pu:done?"#22c55e":"#e5e7eb"}`,
              background:active?C.pu:done?"#f0fdf4":"#f9fafb",
              color:active?"white":done?"#15803d":"#6b7280",
              fontWeight:"700",fontSize:"12px",cursor:"pointer",fontFamily:"inherit",
              display:"flex",alignItems:"center",justifyContent:"center",gap:"6px",transition:"all 0.12s"}}>
            {done&&<svg width="13" height="13" viewBox="0 0 13 13" fill="none"><circle cx="6.5" cy="6.5" r="6" fill="#22c55e"/><path d="M3.5 6.5l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
            {!done&&<span style={{width:"18px",height:"18px",borderRadius:"50%",background:active?"rgba(255,255,255,0.25)":"#e5e7eb",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"10px",fontWeight:"800"}}>{n}</span>}
            {l}
          </button>;
        })}
      </div>

      {/* ── STEP 1: Configuration ── */}
      {step===1&&<div style={{display:"flex",flexDirection:"column",gap:"16px",maxWidth:"560px"}}>
        <div>
          <label style={C.lbl}>Assignment Name</label>
          <input value={name} onChange={e=>setName(e.target.value)} style={C.inp}
            placeholder={`Assignment from ${educator.name}`}/>
        </div>

        <div>
          <label style={C.lbl}>Number of Cases (1–5)</label>
          <div style={{display:"flex",gap:"8px"}}>
            {[1,2,3,4,5].map(n=>(
              <button key={n} onClick={()=>{setCaseCount(n);setSelectedCaseIds(prev=>prev.slice(0,n));}}
                style={{width:"44px",height:"44px",borderRadius:"9px",border:`2px solid ${caseCount===n?C.pu:"#e5e7eb"}`,
                  background:caseCount===n?C.pu:"white",color:caseCount===n?"white":"#374151",
                  fontWeight:"800",fontSize:"15px",cursor:"pointer",fontFamily:"inherit"}}>
                {n}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label style={C.lbl}>Case Selection Method</label>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"9px"}}>
            {[["individual","🎯","Pick Individual Cases","Choose specific cases from the library"],
              ["random","🎲","Random Selection","System picks cases at random from a pool"]].map(([v,ico,lb,hint])=>(
              <button key={v} onClick={()=>setSelMode(v)}
                style={{padding:"13px 14px",borderRadius:"10px",border:`2px solid ${selMode===v?C.pu:"#e5e7eb"}`,
                  background:selMode===v?"#faf5ff":"white",cursor:"pointer",textAlign:"left",fontFamily:"inherit"}}>
                <div style={{fontSize:"18px",marginBottom:"5px"}}>{ico}</div>
                <div style={{fontWeight:"700",fontSize:"13px",color:selMode===v?C.pu:C.dark}}>{lb}</div>
                <div style={{fontSize:"11px",color:"#9ca3af",marginTop:"2px"}}>{hint}</div>
              </button>
            ))}
          </div>
        </div>

        {selMode==="random"&&<div>
          <label style={C.lbl}>Random Pool</label>
          <div style={{display:"flex",gap:"8px",flexWrap:"wrap"}}>
            {[["default","📋","Built-in cases"],["all","🗂","All cases (+ community)"],["favs","❤️","My favourites"]].map(([v,ico,lb])=>(
              <button key={v} onClick={()=>setRandomSrc(v)}
                style={{padding:"8px 14px",borderRadius:"20px",border:`1.5px solid ${randomSrc===v?C.pu:"#e5e7eb"}`,
                  background:randomSrc===v?C.pu:"white",color:randomSrc===v?"white":"#374151",
                  fontSize:"12px",fontWeight:"600",cursor:"pointer",fontFamily:"inherit",
                  display:"flex",alignItems:"center",gap:"5px"}}>
                <span>{ico}</span>{lb}
              </button>
            ))}
          </div>
        </div>}

        <div style={{borderTop:"1px solid #e5e7eb",paddingTop:"16px"}}>
          <label style={C.lbl}>Timing & Availability</label>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px",marginBottom:"10px"}}>
            <div>
              <div style={{fontSize:"11px",color:"#6b7280",marginBottom:"4px",fontWeight:"600"}}>Start date/time (local)</div>
              <input type="datetime-local" value={startAt} onChange={e=>setStartAt(e.target.value)} style={C.inp}/>
            </div>
            <div>
              <div style={{fontSize:"11px",color:"#6b7280",marginBottom:"4px",fontWeight:"600"}}>Expiry date/time (local)</div>
              <input type="datetime-local" value={expiresAt} onChange={e=>setExpiresAt(e.target.value)} style={C.inp}/>
            </div>
          </div>
          <label style={{display:"flex",alignItems:"center",gap:"9px",cursor:"pointer",
            padding:"11px 14px",borderRadius:"9px",border:`1.5px solid ${timed?C.pu:"#e5e7eb"}`,
            background:timed?"#faf5ff":"white"}}>
            <input type="checkbox" checked={timed} onChange={e=>setTimed(e.target.checked)} style={{accentColor:C.pu}}/>
            <div style={{flex:1}}>
              <div style={{fontWeight:"600",fontSize:"13px",color:C.dark}}>Timed Assignment</div>
              <div style={{fontSize:"11px",color:"#9ca3af"}}>Trainees have a fixed time window once they open the assignment</div>
            </div>
            {timed&&<div style={{display:"flex",alignItems:"center",gap:"5px",flexShrink:0}}>
              <input type="number" min={1} max={480} value={timedMins}
                onChange={e=>setTimedMins(parseInt(e.target.value)||60)}
                style={{...C.inp,width:"70px",textAlign:"center"}}/>
              <span style={{fontSize:"12px",color:"#6b7280"}}>min</span>
            </div>}
          </label>
        </div>

        <div style={{borderTop:"1px solid #e5e7eb",paddingTop:"16px"}}>
          <label style={C.lbl}>Feedback Mode</label>
          {[["immediate","Immediate","Trainee sees feedback right after submitting"],
            ["delayed","After Date/Time","Feedback hidden until a specific date"],
            ["hidden","Hidden — Manual Release","You manually release feedback"]].map(([v,lb,hint])=>(
            <label key={v} style={{display:"flex",alignItems:"flex-start",gap:"9px",cursor:"pointer",
              padding:"8px 11px",borderRadius:"7px",
              border:`1px solid ${feedbackMode===v?C.pu:"#e5e7eb"}`,
              background:feedbackMode===v?"#faf5ff":"white",marginBottom:"5px"}}>
              <input type="radio" value={v} checked={feedbackMode===v} onChange={()=>setFeedbackMode(v)} style={{marginTop:"2px"}}/>
              <div><div style={{fontWeight:"600",fontSize:"13px",color:C.dark}}>{lb}</div>
              <div style={{fontSize:"11px",color:"#6b7280"}}>{hint}</div></div>
            </label>
          ))}
          {feedbackMode==="delayed"&&<input type="datetime-local" value={feedbackDate}
            onChange={e=>setFeedbackDate(e.target.value)} style={{...C.inp,marginTop:"6px"}}/>}
        </div>

        {/* Anonymous responses toggle */}
        <div style={{borderTop:"1px solid #e5e7eb",paddingTop:"16px"}}>
          <label style={C.lbl}>Response Anonymity</label>
          <label style={{display:"flex",alignItems:"flex-start",gap:"9px",cursor:"pointer",
            padding:"11px 14px",borderRadius:"9px",
            border:`1.5px solid ${isAnonymous?"#0891b2":"#e5e7eb"}`,
            background:isAnonymous?"#ecfeff":"white"}}>
            <input type="checkbox" checked={isAnonymous} onChange={e=>setIsAnonymous(e.target.checked)}
              style={{accentColor:"#0891b2",marginTop:"2px"}}/>
            <div>
              <div style={{fontWeight:"600",fontSize:"13px",color:C.dark}}>Anonymous Responses</div>
              <div style={{fontSize:"11px",color:"#6b7280",marginTop:"2px",lineHeight:"1.5"}}>
                Trainees receive a magic link <strong>not tied to their email</strong>.
                You see "Anonymous Trainee 1, 2…" — no names or emails.
                Useful for high-stakes assessments or research where identifiability could bias responses.
                Research data is collected as usual (de-identified by design).
              </div>
            </div>
          </label>
        </div>

        <div>
          <label style={C.lbl}>Note to Trainees (Optional)</label>
          <textarea value={note} onChange={e=>setNote(e.target.value)} rows={3}
            placeholder="e.g. This assignment covers material from last week's lecture on citrate…"
            style={{...C.inp,resize:"vertical"}}/>
        </div>

        <div style={{display:"flex",justifyContent:"flex-end"}}>
          <button onClick={()=>setStep(2)} disabled={!canStep2}
            style={{...C.btn(C.pu),opacity:canStep2?1:0.5}}>
            Next: Select Cases →
          </button>
        </div>
      </div>}

      {/* ── STEP 2: Case Selection ── */}
      {step===2&&<div>

        {/* ── RANDOM MODE ── */}
        {selMode==="random"&&<>
          {/* Header row: count + re-randomize */}
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"14px",gap:"10px",flexWrap:"wrap"}}>
            <div style={{fontSize:"13px",color:"#6b7280",fontFamily:FONT_UI}}>
              <strong style={{color:C.dark}}>{selectedCaseIds.length}</strong> of <strong style={{color:C.dark}}>{caseCount}</strong> cases randomly selected
              {selectedCaseIds.length>0&&<span style={{fontSize:"11px",color:"#9ca3af",marginLeft:"8px"}}>from {randomSrc==="default"?"built-in cases":randomSrc==="favs"?"your favourites":"all cases"}</span>}
            </div>
            <button onClick={pickRandom} style={{...C.btn(C.pu)}}>
              🎲 Re-randomize
            </button>
          </div>

          {/* Show selected cases as full cards so educator can review */}
          {selectedCaseIds.length===0&&(
            <div style={{textAlign:"center",padding:"40px 24px",background:"#f8f7ff",borderRadius:"12px",border:"1px dashed #c4b5fd"}}>
              <div style={{fontSize:"32px",marginBottom:"8px",opacity:0.4}}>🎲</div>
              <div style={{fontSize:"13px",color:"#9ca3af"}}>Click <strong>Re-randomize</strong> above to pick {caseCount} cases at random.</div>
            </div>
          )}

          {selectedCaseIds.length>0&&<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",gap:"10px"}}>
            {selectedCaseIds.map((id,i)=>{
              const c=allCases[id];
              const col=getFacetCol(c?.facet);
              return <div key={id} style={{borderRadius:"12px",overflow:"hidden",
                border:`1.5px solid ${col.accent}`,background:`linear-gradient(160deg,${col.bg},white)`,
                display:"flex",flexDirection:"column"}}>
                {/* accent bar */}
                <div style={{height:"3px",background:`linear-gradient(90deg,${col.accent},${col.accent}88)`}}/>
                <div style={{padding:"11px 13px",flex:1}}>
                  {/* position badge + facet */}
                  <div style={{display:"flex",alignItems:"center",gap:"6px",marginBottom:"6px"}}>
                    <div style={{width:"20px",height:"20px",borderRadius:"50%",background:col.accent,
                      color:"white",fontSize:"10px",fontWeight:"800",display:"flex",
                      alignItems:"center",justifyContent:"center",flexShrink:0}}>{i+1}</div>
                    <span style={{fontSize:"10px",fontWeight:"700",padding:"1px 6px",borderRadius:"7px",
                      background:col.pill,color:col.pillTx}}>{c?.facet||"General"}</span>
                  </div>
                  <div style={{fontSize:"12px",fontWeight:"700",color:C.dark,lineHeight:"1.3",marginBottom:"4px",fontFamily:FONT_UI}}>
                    {c?.title||id}
                  </div>
                  {c?.page&&<div style={{fontSize:"10px",color:"#9ca3af",lineHeight:"1.4",
                    display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>
                    {c.page}
                  </div>}
                </div>
                <div style={{padding:"8px 12px",borderTop:`1px solid ${col.border}`,background:"rgba(255,255,255,0.55)",
                  display:"flex",gap:"5px"}}>
                  <button onClick={()=>setPreviewCase(id)}
                    style={{flex:1,padding:"5px 0",borderRadius:"7px",border:`1px solid ${col.border}`,
                      background:"white",color:"#374151",fontSize:"11px",fontWeight:"600",cursor:"pointer",
                      display:"flex",alignItems:"center",justifyContent:"center",gap:"4px",fontFamily:"inherit"}}>
                    <Ic n="eye" s="w-3 h-3"/> View
                  </button>
                  <button onClick={()=>setSelectedCaseIds(p=>p.filter(x=>x!==id))}
                    style={{padding:"5px 8px",borderRadius:"7px",border:"1px solid #fecaca",
                      background:"#fff5f5",color:"#dc2626",fontSize:"11px",cursor:"pointer",
                      display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"inherit"}}>
                    <Ic n="trash" s="w-3 h-3"/>
                  </button>
                </div>
              </div>;
            })}
          </div>}

          {/* Hint to add extras if under cap after removing */}
          {selectedCaseIds.length<caseCount&&selectedCaseIds.length>0&&(
            <div style={{marginTop:"12px",padding:"10px 14px",background:"#fef9c3",borderRadius:"8px",
              border:"1px solid #fde68a",fontSize:"12px",color:"#92400e",display:"flex",alignItems:"center",gap:"8px"}}>
              <span>⚠</span>
              {caseCount-selectedCaseIds.length} slot{caseCount-selectedCaseIds.length!==1?"s":""} empty — click Re-randomize to refill, or switch to Individual mode to pick manually.
            </div>
          )}
        </>}

        {/* ── INDIVIDUAL MODE ── */}
        {selMode==="individual"&&<>
          {/* Selected strip */}
          {selectedCaseIds.length>0&&(
            <div style={{display:"flex",gap:"6px",flexWrap:"wrap",marginBottom:"14px",
              padding:"10px 12px",background:"#f8f7ff",borderRadius:"10px",border:"1px solid #ede9fe"}}>
              <div style={{width:"100%",fontSize:"10px",fontWeight:"700",color:"#9ca3af",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:"4px"}}>
                Selected ({selectedCaseIds.length}/{caseCount})
              </div>
              {selectedCaseIds.map((id,i)=>{
                const c=allCases[id];
                const col=getFacetCol(c?.facet);
                return <div key={id} style={{display:"flex",alignItems:"center",gap:"5px",
                  padding:"3px 10px",borderRadius:"16px",
                  background:col.pill,border:`1px solid ${col.border}`,fontSize:"11px",fontWeight:"600",color:col.pillTx}}>
                  <span style={{opacity:0.6}}>{i+1}.</span>
                  {c?.title||id}
                  <button onClick={()=>toggleCase(id)}
                    style={{background:"none",border:"none",cursor:"pointer",color:"inherit",
                      padding:"0 0 0 3px",lineHeight:1,fontSize:"13px",opacity:0.7}}>×</button>
                </div>;
              })}
            </div>
          )}

          {/* Library sub-tabs (Default / All / Favourites) */}
          <div style={{display:"flex",gap:"4px",marginBottom:"12px",
            background:"#f3f4f6",borderRadius:"9px",padding:"3px",width:"fit-content"}}>
            {[{id:"default",label:"Default",icon:"📋"},{id:"all",label:"All Cases",icon:"🗂"},{id:"favs",label:"Favourites",icon:"❤️"}].map(({id,label,icon})=>{
              const active=caseLibTab===id;
              return <button key={id} onClick={()=>{setCaseLibTab(id);setCaseFacet("All");setCaseSearch("");}}
                style={{padding:"5px 12px",borderRadius:"7px",border:"none",cursor:"pointer",
                  fontWeight:"600",fontSize:"12px",fontFamily:"inherit",
                  background:active?"white":"transparent",
                  color:active?C.dark:"#6b7280",
                  boxShadow:active?"0 1px 3px rgba(0,0,0,0.09)":"none",
                  transition:"all 0.1s",display:"flex",alignItems:"center",gap:"4px"}}>
                <span style={{fontSize:"12px"}}>{icon}</span>{label}
              </button>;
            })}
          </div>

          {/* Search + facet */}
          <div style={{display:"flex",gap:"8px",marginBottom:"10px",flexWrap:"wrap"}}>
            <div style={{position:"relative",flex:1,minWidth:"180px"}}>
              <svg style={{position:"absolute",left:"10px",top:"50%",transform:"translateY(-50%)",color:"#9ca3af",pointerEvents:"none"}}
                width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35" strokeLinecap="round"/>
              </svg>
              <input value={caseSearch} onChange={e=>setCaseSearch(e.target.value)}
                placeholder="Search cases…"
                style={{...C.inp,paddingLeft:"32px",fontSize:"12px"}}/>
            </div>
            <select value={caseFacet} onChange={e=>setCaseFacet(e.target.value)}
              style={{...C.inp,minWidth:"150px",fontSize:"12px"}}>
              <option value="All">All Categories</option>
              {Object.keys(FACET_COLORS).map(f=><option key={f} value={f}>{f}</option>)}
            </select>
          </div>

          {/* Case grid */}
          {casesForPicker.length===0&&(
            <div style={{textAlign:"center",padding:"36px",background:"#f9fafb",borderRadius:"10px",color:"#9ca3af",fontSize:"13px"}}>
              No cases match your filters.
              <button onClick={()=>{setCaseLibTab("all");setCaseFacet("All");setCaseSearch("");}}
                style={{marginLeft:"8px",background:"none",border:"none",cursor:"pointer",color:C.pu,fontWeight:"600",fontFamily:"inherit",fontSize:"13px"}}>
                Clear filters
              </button>
            </div>
          )}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(230px,1fr))",gap:"8px",maxHeight:"420px",overflowY:"auto",paddingRight:"4px"}}>
            {casesForPicker.map(id=>{
              const c=allCases[id];
              const col=getFacetCol(c.facet);
              const sel=selectedCaseIds.includes(id);
              const atCap=!sel&&selectedCaseIds.length>=caseCount;
              return <div key={id}
                style={{borderRadius:"10px",overflow:"hidden",
                  background:`linear-gradient(160deg,${col.bg},white)`,
                  border:`2px solid ${sel?col.accent:col.border}`,
                  opacity:atCap?0.45:1,transition:"all 0.12s",
                  display:"flex",flexDirection:"column"}}>
                <div style={{padding:"10px 12px",flex:1,cursor:atCap?"not-allowed":"pointer"}}
                  onClick={()=>!atCap&&toggleCase(id)}
                  title={atCap?"Remove a case to add another":""}>
                  <div style={{display:"flex",alignItems:"center",gap:"6px",marginBottom:"5px"}}>
                    {sel&&<div style={{width:"16px",height:"16px",borderRadius:"50%",background:col.accent,
                      display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                      <svg width="9" height="9" viewBox="0 0 13 13" fill="none">
                        <path d="M3 6.5l2 2 5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>}
                    <span style={{fontSize:"10px",fontWeight:"700",padding:"1px 6px",borderRadius:"7px",
                      background:col.pill,color:col.pillTx}}>{c.facet||"General"}</span>
                  </div>
                  <div style={{fontSize:"12px",fontWeight:"700",color:C.dark,lineHeight:"1.3",marginBottom:"3px",fontFamily:FONT_UI}}>{c.title}</div>
                  {c.page&&<div style={{fontSize:"10px",color:"#9ca3af",lineHeight:"1.4",
                    display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{c.page}</div>}
                </div>
                {/* footer: select + view */}
                <div style={{padding:"6px 10px",borderTop:`1px solid ${col.border}`,background:"rgba(255,255,255,0.6)",
                  display:"flex",gap:"5px"}}>
                  <button onClick={()=>!atCap&&toggleCase(id)} disabled={atCap}
                    style={{flex:1,padding:"4px 0",borderRadius:"6px",border:`1px solid ${sel?col.accent:col.border}`,
                      background:sel?col.accent:"white",color:sel?"white":"#374151",
                      fontSize:"11px",fontWeight:"600",cursor:atCap?"not-allowed":"pointer",
                      display:"flex",alignItems:"center",justifyContent:"center",gap:"4px",fontFamily:"inherit"}}>
                    {sel?"✓ Selected":"+ Select"}
                  </button>
                  <button onClick={()=>setPreviewCase(id)}
                    style={{padding:"4px 8px",borderRadius:"6px",border:`1px solid ${col.border}`,
                      background:"white",color:"#374151",fontSize:"11px",fontWeight:"600",cursor:"pointer",
                      display:"flex",alignItems:"center",justifyContent:"center",gap:"4px",fontFamily:"inherit"}}>
                    <Ic n="eye" s="w-3 h-3"/> View
                  </button>
                </div>
              </div>;
            })}
          </div>
        </>}

        <div style={{display:"flex",justifyContent:"space-between",marginTop:"18px"}}>
          <button onClick={()=>setStep(1)} style={{...C.btn("#f3f4f6","#374151")}}>← Back</button>
          <button onClick={()=>setStep(3)} disabled={!canStep3}
            style={{...C.btn(C.pu),opacity:canStep3?1:0.5}}>
            Next: Trainees →
          </button>
        </div>
      </div>}

      {/* ── STEP 3: Trainees & Deploy  OR  Session & QR ── */}
      {step===3&&<div style={{maxWidth:"580px"}}>
        <div style={{marginBottom:"16px",padding:"13px 16px",background:"#f8f7ff",borderRadius:"10px",border:"1px solid #ede9fe"}}>
          <div style={{fontSize:"12px",fontWeight:"700",color:C.pu,marginBottom:"4px"}}>
            Summary: {name}
          </div>
          <div style={{fontSize:"12px",color:"#6b7280",fontFamily:FONT_UI}}>
            {selectedCaseIds.length} case{selectedCaseIds.length!==1?"s":""} ·{" "}
            {timed?`${timedMins} min timed`:"Untimed"} ·{" "}
            {feedbackMode} feedback ·{" "}
            {expiresAt?`Expires ${new Date(expiresAt).toLocaleDateString()}`:"No expiry"}
          </div>
        </div>

        {/* Deploy mode picker — trainee list (with emails) vs. open QR session */}
        <div style={{marginBottom:"14px"}}>
          <label style={{...C.lbl,marginBottom:"8px"}}>How would you like to send this?</label>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"8px"}}>
            {[
              {id:"trainees",title:"Email to specific trainees",hint:"You enter each trainee's email. Each receives a personal link."},
              {id:"session", title:"Open QR session",          hint:"Generate a QR code anyone can scan to join (conferences, rounds, fellowship days)."},
            ].map(opt=>(
              <button key={opt.id} onClick={()=>setDeployMode(opt.id)}
                style={{textAlign:"left",padding:"11px 13px",borderRadius:"10px",cursor:"pointer",
                  border:`1.5px solid ${deployMode===opt.id?C.pu:"#e5e7eb"}`,
                  background:deployMode===opt.id?"#faf5ff":"white",fontFamily:FONT_UI}}>
                <div style={{fontSize:"13px",fontWeight:"700",color:deployMode===opt.id?C.pu:C.dark,marginBottom:"4px"}}>{opt.title}</div>
                <div style={{fontSize:"11px",color:"#6b7280",lineHeight:"1.5"}}>{opt.hint}</div>
              </button>
            ))}
          </div>
        </div>

        {/* ── Trainee-email mode (existing) ── */}
        {deployMode==="trainees" && <>
          <div style={{marginBottom:"14px"}}>
            <label style={C.lbl}>Paste Emails (comma or newline separated)</label>
            <div style={{display:"flex",gap:"8px"}}>
              <textarea value={emailBulk} onChange={e=>setEmailBulk(e.target.value)}
                rows={2} placeholder="trainee1@hospital.edu, trainee2@hospital.edu"
                style={{...C.inp,resize:"vertical",flex:1}}/>
              <button onClick={parseBulkEmails}
                style={{...C.btn(C.pu),alignSelf:"flex-start",flexShrink:0}}>
                Add
              </button>
            </div>
          </div>

          <div style={{marginBottom:"8px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <label style={C.lbl}>Trainee List</label>
            <button onClick={()=>setTrainees(p=>[...p,{name:"",email:""}])}
              style={{...C.btn("transparent",C.pu,{border:`1px solid ${C.pu}`,padding:"4px 10px",fontSize:"12px"})}}>
              <Ic n="plus" s="w-3 h-3"/> Add Row
            </button>
          </div>

          <div style={{display:"flex",flexDirection:"column",gap:"6px",marginBottom:"16px"}}>
            {trainees.map((t,i)=>(
              <div key={i} style={{display:"flex",gap:"7px",alignItems:"center"}}>
                <input value={t.name} onChange={e=>setTrainees(p=>p.map((x,j)=>j===i?{...x,name:e.target.value}:x))}
                  placeholder="Full Name (optional)" style={{...C.inp,flex:1}}/>
                <input value={t.email} type="email"
                  onChange={e=>setTrainees(p=>p.map((x,j)=>j===i?{...x,email:e.target.value}:x))}
                  placeholder="email@hospital.edu" style={{...C.inp,flex:1.5}}/>
                {trainees.length>1&&<button onClick={()=>setTrainees(p=>p.filter((_,j)=>j!==i))}
                  style={{background:"none",border:"none",cursor:"pointer",color:"#dc2626",padding:"4px",flexShrink:0}}>
                  <Ic n="trash" s="w-4 h-4"/>
                </button>}
              </div>
            ))}
          </div>
        </>}

        {/* ── Open-session (QR) mode ── */}
        {deployMode==="session" && <>
          <div style={{padding:"12px 14px",background:"#f0f9ff",borderRadius:"10px",border:"1px solid #bae6fd",marginBottom:"14px"}}>
            <div style={{fontSize:"12px",color:"#0c4a6e",lineHeight:"1.6"}}>
              You'll get a QR code and join code to share. Anyone who scans (or enters the code) can join this session — useful for conferences, fellowship days, in-person rounds, or unit-wide needs assessments. Their assignments will appear in your <strong>Trainees</strong> tab as they join.
            </div>
          </div>

          <div style={{marginBottom:"14px"}}>
            <label style={C.lbl}>Maximum participants</label>
            <input type="number" min="1" max="200" value={sessionMaxParticipants}
              onChange={e=>setSessionMaxParticipants(Math.max(1,Math.min(200,parseInt(e.target.value)||1)))}
              style={{...C.inp,maxWidth:"180px"}}/>
            <div style={{fontSize:"11px",color:"#9ca3af",marginTop:"4px"}}>
              Hard cap is 200. Once reached, the session refuses new joins.
            </div>
          </div>

          <div style={{marginBottom:"14px"}}>
            <label style={C.lbl}>Session expires in</label>
            <div style={{display:"flex",gap:"6px",flexWrap:"wrap"}}>
              {[
                {h:2,   l:"2 hours"},
                {h:24,  l:"1 day"},
                {h:72,  l:"3 days"},
                {h:168, l:"1 week"},
                {h:720, l:"30 days"},
              ].map(opt=>(
                <button key={opt.h} onClick={()=>setSessionCloseHours(opt.h)}
                  style={{padding:"7px 13px",borderRadius:"8px",
                    border:`1.5px solid ${sessionCloseHours===opt.h?C.pu:"#e5e7eb"}`,
                    background:sessionCloseHours===opt.h?"#faf5ff":"white",
                    color:sessionCloseHours===opt.h?C.pu:"#374151",
                    fontWeight:"600",fontSize:"12px",cursor:"pointer",fontFamily:FONT_UI}}>
                  {opt.l}
                </button>
              ))}
            </div>
          </div>

          <div style={{marginBottom:"14px"}}>
            <label style={{display:"flex",alignItems:"flex-start",gap:"9px",cursor:"pointer",
              padding:"10px 13px",borderRadius:"9px",
              border:`1px solid ${sessionAllowAnonymous?C.pu:"#e5e7eb"}`,
              background:sessionAllowAnonymous?"#faf5ff":"white"}}>
              <input type="checkbox" checked={sessionAllowAnonymous}
                onChange={e=>setSessionAllowAnonymous(e.target.checked)}
                style={{marginTop:"2px"}}/>
              <div>
                <div style={{fontWeight:"700",fontSize:"13px",color:C.dark}}>Allow anonymous join</div>
                <div style={{fontSize:"11px",color:"#6b7280",marginTop:"2px",lineHeight:"1.5"}}>
                  Participants can join without entering an email. Best for needs-assessments where you want maximal participation and no PII. You'll see "Anonymous Trainee" entries with role only.
                </div>
              </div>
            </label>
          </div>

          <div style={{marginBottom:"16px"}}>
            <label style={{display:"flex",alignItems:"flex-start",gap:"9px",cursor:"pointer",
              padding:"10px 13px",borderRadius:"9px",
              border:`1px solid ${sessionPinEnabled?C.pu:"#e5e7eb"}`,
              background:sessionPinEnabled?"#faf5ff":"white",marginBottom:"6px"}}>
              <input type="checkbox" checked={sessionPinEnabled}
                onChange={e=>setSessionPinEnabled(e.target.checked)}
                style={{marginTop:"2px"}}/>
              <div>
                <div style={{fontWeight:"700",fontSize:"13px",color:C.dark}}>Require a 4-digit PIN</div>
                <div style={{fontSize:"11px",color:"#6b7280",marginTop:"2px",lineHeight:"1.5"}}>
                  Optional gate for higher-stakes sessions (credentialing, board-prep). You share the PIN verbally or on the slide alongside the QR.
                </div>
              </div>
            </label>
            {sessionPinEnabled && (
              <input value={sessionPin} onChange={e=>setSessionPin(e.target.value.replace(/\D/g,"").slice(0,4))}
                placeholder="1234" inputMode="numeric" maxLength={4}
                style={{...C.inp,maxWidth:"160px",fontFamily:"monospace",letterSpacing:"0.2em",fontSize:"16px"}}/>
            )}
          </div>
        </>}

        <div style={{display:"flex",justifyContent:"space-between",gap:"10px"}}>
          <button onClick={()=>setStep(2)} style={{...C.btn("#f3f4f6","#374151")}}>← Back</button>
          <div style={{display:"flex",gap:"8px"}}>
            {deployMode==="trainees" && <button onClick={async()=>{setBusy(true);await onSave(buildGroup("pending"));setBusy(false);}}
              disabled={busy} style={{...C.btn("#f3f4f6","#374151")}}>
              <Ic n="save" s="w-4 h-4"/> Save (Pending)
            </button>}
            <button onClick={async()=>{
              if(!canDeploy) return;
              if(deployMode==="session"){
                await createSession();
                return;
              }
              setBusy(true);
              const g=buildGroup("deployed");
              await onDeploy(g,buildFlatAssignments(g));
              setBusy(false);
            }}
              disabled={!canDeploy||busy||(deployMode==="session"&&sessionPinEnabled&&!/^\d{4}$/.test(sessionPin))}
              style={{...C.btn(canDeploy?"#059669":"#e5e7eb",canDeploy?"white":"#9ca3af"),opacity:busy?0.7:1}}>
              {busy?<Ic n="spin" s="w-4 h-4"/>:<Ic n="send" s="w-4 h-4"/>}
              {busy
                ? (deployMode==="session" ? "Creating…" : "Deploying…")
                : (deployMode==="session" ? "📲 Generate QR Session" : "🚀 Deploy Assignment")}
            </button>
          </div>
        </div>
      </div>}

      {/* ── Created-session overlay: QR + joinCode + URL ── */}
      {createdSession && <SessionQRDisplay
        session={createdSession}
        sessionName={name}
        caseCount={selectedCaseIds.length}
        maxParticipants={sessionMaxParticipants}
        closeHours={sessionCloseHours}
        pinEnabled={sessionPinEnabled}
        pin={sessionPin}
        allowAnonymous={sessionAllowAnonymous}
        onClose={()=>{setCreatedSession(null);onClose();}}
      />}

      {/* ── Case preview overlay (full detail, step 2) ── */}
      {previewCase&&allCases[previewCase]&&(()=>{
        const pc=allCases[previewCase];
        const col=getFacetCol(pc.facet);
        const st=pc.initialState||{};
        const labs=st.labs||{};
        const gas=st.gas||{};
        const rx=pc.initialPrescription||{};
        const isSelected=selectedCaseIds.includes(previewCase);
        const atCap=!isSelected&&selectedCaseIds.length>=caseCount;
        // Key labs to show
        const keyLabs=[["K",labs.K,"mmol/L"],["Na",labs.Na,"mmol/L"],["HCO₃",labs.HCO3,"mmol/L"],["Cr",labs.Cr,"µmol/L"],["Ammonia",labs.Ammonia,"µmol/L"],["pH",gas.pH,""],["BE",gas.BE,"mmol/L"],["Alb",labs.Alb,"g/L"]].filter(([,v])=>v!=null&&v!==undefined);
        return (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:300,
            display:"flex",alignItems:"flex-start",justifyContent:"center",padding:"16px",overflowY:"auto"}}
            onClick={()=>setPreviewCase(null)}>
            <div style={{background:"white",borderRadius:"16px",width:"100%",maxWidth:"640px",
              boxShadow:"0 32px 80px rgba(0,0,0,0.45)",margin:"auto"}}
              onClick={e=>e.stopPropagation()}>

              {/* Header bar */}
              <div style={{background:`linear-gradient(135deg,${col.bg},white)`,
                borderRadius:"16px 16px 0 0",padding:"18px 22px 14px",
                borderBottom:`1px solid ${col.border}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:"10px"}}>
                  <div>
                    <div style={{display:"flex",gap:"6px",alignItems:"center",marginBottom:"6px",flexWrap:"wrap"}}>
                      <span style={{fontSize:"10px",fontWeight:"700",padding:"2px 8px",borderRadius:"8px",
                        background:col.pill,color:col.pillTx}}>{pc.facet||"General"}</span>
                      {st.weightKg&&<span style={{fontSize:"10px",color:"#9ca3af"}}>{st.weightKg} kg</span>}
                      {st.ageMonths!=null&&<span style={{fontSize:"10px",color:"#9ca3af"}}>
                        {st.ageMonths<24?`${st.ageMonths} mo`:`${Math.round(st.ageMonths/12)} yr`}
                      </span>}
                    </div>
                    <h3 style={{margin:0,fontSize:"16px",fontWeight:"800",color:C.dark,fontFamily:FONT_UI}}>
                      {pc.title}
                    </h3>
                  </div>
                  <button onClick={()=>setPreviewCase(null)}
                    style={{background:"none",border:"none",cursor:"pointer",color:"#6b7280",flexShrink:0,padding:"4px"}}>
                    <Ic n="x" s="w-5 h-5"/>
                  </button>
                </div>
              </div>

              <div style={{padding:"18px 22px",maxHeight:"65vh",overflowY:"auto"}}>

                {/* Clinical scenario */}
                <div style={{padding:"13px 15px",background:"#fffbeb",border:"1px solid #fde68a",
                  borderLeft:`4px solid #f59e0b`,borderRadius:"8px",marginBottom:"14px"}}>
                  <div style={{fontSize:"10px",fontWeight:"700",color:"#92400e",textTransform:"uppercase",
                    letterSpacing:"0.06em",marginBottom:"5px"}}>Clinical Scenario</div>
                  <p style={{margin:0,fontSize:"13px",color:"#78350f",lineHeight:"1.6"}}>
                    {pc.page||<em style={{color:"#d97706"}}>No scenario written yet.</em>}
                  </p>
                </div>

                {/* Key labs */}
                {keyLabs.length>0&&<div style={{marginBottom:"14px"}}>
                  <div style={{fontSize:"10px",fontWeight:"700",color:"#6b7280",textTransform:"uppercase",
                    letterSpacing:"0.06em",marginBottom:"7px"}}>Key Values</div>
                  <div style={{display:"flex",gap:"6px",flexWrap:"wrap"}}>
                    {keyLabs.map(([l,v,u])=>(
                      <div key={l} style={{padding:"5px 10px",borderRadius:"8px",background:"#f3f4f6",
                        border:"1px solid #e5e7eb",textAlign:"center",minWidth:"60px"}}>
                        <div style={{fontSize:"10px",color:"#9ca3af",fontWeight:"600"}}>{l}</div>
                        <div style={{fontSize:"13px",fontWeight:"700",color:C.dark}}>{v}</div>
                        {u&&<div style={{fontSize:"9px",color:"#9ca3af"}}>{u}</div>}
                      </div>
                    ))}
                  </div>
                </div>}

                {/* Initial prescription snapshot */}
                {rx.modality&&<div style={{marginBottom:"14px",padding:"11px 13px",background:"#f0f9ff",
                  borderRadius:"8px",border:"1px solid #bae6fd"}}>
                  <div style={{fontSize:"10px",fontWeight:"700",color:"#0c4a6e",textTransform:"uppercase",
                    letterSpacing:"0.06em",marginBottom:"8px"}}>Initial Prescription</div>
                  <div style={{display:"flex",gap:"10px",flexWrap:"wrap",fontSize:"12px",color:"#374151"}}>
                    {[["Mode",rx.modality],["Access",rx.access],["Filter",rx.filter],["Anticoag",rx.anticoag],
                      ["BFR",rx.bfr?`${rx.bfr} ml/min`:null],
                      ["Dialysate",rx.dialysateRate?`${rx.dialysateRate} ml/h`:null],
                      ["Rep",rx.repRate?`${rx.repRate} ml/h`:null],
                      ["Net UF",rx.netUf!=null?`${rx.netUf} ml/h`:null],
                    ].filter(([,v])=>v).map(([l,v])=>(
                      <div key={l}><span style={{color:"#9ca3af",fontWeight:"600"}}>{l}: </span>{v}</div>
                    ))}
                  </div>
                </div>}

                {/* Success criteria */}
                {pc.successSummary&&<div style={{padding:"11px 13px",background:"#f0fdf4",
                  borderRadius:"8px",border:"1px solid #bbf7d0",marginBottom:"14px"}}>
                  <div style={{fontSize:"10px",fontWeight:"700",color:"#065f46",textTransform:"uppercase",
                    letterSpacing:"0.06em",marginBottom:"5px"}}>Success Criteria</div>
                  <div style={{fontSize:"12px",color:"#065f46",lineHeight:"1.5"}}>{pc.successSummary}</div>
                </div>}

                {/* Learning point */}
                {pc.learningPoint&&<div style={{padding:"11px 13px",background:"#faf5ff",
                  borderRadius:"8px",border:"1px solid #e9d5ff"}}>
                  <div style={{fontSize:"10px",fontWeight:"700",color:C.pu,textTransform:"uppercase",
                    letterSpacing:"0.06em",marginBottom:"5px"}}>Learning Point</div>
                  <div style={{fontSize:"12px",color:"#374151",lineHeight:"1.5"}}>{pc.learningPoint}</div>
                </div>}
              </div>

              {/* Footer action */}
              <div style={{padding:"13px 22px",borderTop:"1px solid #e5e7eb",display:"flex",gap:"9px",justifyContent:"flex-end"}}>
                <button onClick={()=>setPreviewCase(null)}
                  style={{...C.btn("#f3f4f6","#374151")}}>Close</button>
                {selMode==="individual"&&<button
                  onClick={()=>{if(!isSelected)toggleCase(previewCase);setPreviewCase(null);}}
                  disabled={atCap}
                  style={{...C.btn(isSelected?"#f0fdf4":C.pu,isSelected?"#059669":"white"),
                    opacity:atCap?0.5:1}}>
                  {isSelected
                    ? <><svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{display:"inline"}}><circle cx="6.5" cy="6.5" r="6" fill="#22c55e"/><path d="M3.5 6.5l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg> Selected — click to remove</>
                    : <><Ic n="plus" s="w-4 h-4"/> Add to Assignment</>}
                </button>}
                {selMode==="random"&&<div style={{fontSize:"12px",color:"#9ca3af",alignSelf:"center"}}>
                  Switch to Individual mode to manually add/remove cases.
                </div>}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ─── INFO MODAL ──────────────────────────────────────────────────────────────
function InfoModal({onClose}){
  const Section = ({title,children}) => (
    <div style={{marginBottom:"22px"}}>
      <div style={{fontSize:"11px",fontWeight:"700",color:C.pu,textTransform:"uppercase",
        letterSpacing:"0.08em",marginBottom:"8px",paddingBottom:"5px",
        borderBottom:`1px solid #ede9fe`,fontFamily:FONT_UI}}>{title}</div>
      <div style={{fontSize:"13px",color:"#374151",lineHeight:"1.7",fontFamily:FONT_UI}}>{children}</div>
    </div>
  );
  const Row = ({icon,label,text}) => (
    <div style={{display:"flex",gap:"10px",marginBottom:"8px",alignItems:"flex-start"}}>
      <span style={{fontSize:"16px",flexShrink:0,marginTop:"1px"}}>{icon}</span>
      <div><span style={{fontWeight:"600",color:C.dark}}>{label}: </span>{text}</div>
    </div>
  );
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:400,
      display:"flex",alignItems:"flex-start",justifyContent:"center",
      padding:"24px 16px",overflowY:"auto"}} onClick={onClose}>
      <div style={{background:"white",borderRadius:"18px",width:"100%",maxWidth:"660px",
        boxShadow:"0 32px 80px rgba(0,0,0,0.45)",margin:"auto"}} onClick={e=>e.stopPropagation()}>

        {/* ── Header ── */}
        <div style={{background:`linear-gradient(135deg,${C.dark},${C.pu})`,
          borderRadius:"18px 18px 0 0",padding:"28px 32px",color:"white",position:"relative"}}>
          <button onClick={onClose} style={{position:"absolute",top:"16px",right:"18px",
            background:"rgba(255,255,255,0.15)",border:"none",borderRadius:"50%",
            width:"30px",height:"30px",cursor:"pointer",color:"white",
            display:"flex",alignItems:"center",justifyContent:"center"}}>
            <Ic n="x" s="w-4 h-4"/>
          </button>
          <div style={{fontSize:"11px",fontWeight:"700",letterSpacing:"0.12em",
            textTransform:"uppercase",color:"#c4b5fd",marginBottom:"6px"}}>
            KiRPA · Educator Portal
          </div>
          <h2 style={{margin:"0 0 8px",fontSize:"22px",fontWeight:"800",lineHeight:"1.2",
            fontFamily:FONT_UI}}>Kidney Replacement Prescription Analytics</h2>
          <p style={{margin:0,fontSize:"13px",color:"rgba(255,255,255,0.8)",lineHeight:"1.6"}}>
            A simulation-based educational platform for continuous renal replacement therapy
            prescribing, designed for paediatric critical care teams at academic medical centres.
            KiRPA bridges the gap between theoretical knowledge and high-stakes clinical
            decision-making through structured case-based learning and real-time analytics.
          </p>
        </div>

        <div style={{padding:"28px 32px"}}>

          <Section title="Platform Purpose">
            KiRPA provides clinicians with a safe, realistic environment to practise CRRT
            prescribing across five core competency domains — clearance, anticoagulation,
            haemodynamics and fluid management, electrolytes, and access/circuit/machine alarms.
            Each case is anchored in a real-world clinical scenario and evaluates specific
            prescribing decisions against expert-defined success criteria.
          </Section>

          <Section title="What Educators Can Do">
            <Row icon="📋" label="Browse & Assign" text="Access 21 built-in cases across all five clinical domains. Search, filter by category, and assign any case to trainees with a single click."/>
            <Row icon="✏️" label="Customise" text="Adjust case parameters privately — modify available anticoagulants, solutions, filters, and interventions visible to the trainee without affecting the original case for others."/>
            <Row icon="➕" label="Create Cases" text="Build your own cases from scratch using the full case editor. Custom cases can be kept private or shared with your institution's educator community."/>
            <Row icon="❤️" label="Favourites" text="Star cases you use frequently. Cases you create are automatically favourited."/>
            <Row icon="📊" label="Track Performance" text="Monitor trainee progress in real time — view domain-level success rates, attempt counts, and submission details from the Trainees tab."/>

          </Section>

          <Section title="What Trainees Experience">
            <Row icon="📩" label="Assignment Link" text="Trainees receive a unique link to a specific case — no account or login required. They see only the assigned case, not the full library."/>
            <Row icon="🏥" label="Clinical Scenario" text="Each case opens with a realistic nursing handover. Trainees review patient vitals, labs, and an initial CRRT prescription."/>
            <Row icon="💊" label="Prescribing Interface" text="Trainees adjust the prescription — selecting modality, access, filter, flow rates, anticoagulation, and electrolyte additives — and submit when ready."/>
            <Row icon="📝" label="Feedback" text="Success criteria are evaluated instantly. Educators control when feedback is revealed — immediately, after a set date, or held for manual release."/>
          </Section>



          <div style={{fontSize:"11px",color:"#9ca3af",textAlign:"center",
            paddingTop:"16px",borderTop:"1px solid #f3f4f6",fontFamily:FONT_UI}}>
            Created by <strong style={{color:"#6b7280"}}>Dr. Amrit Kirpalani MD, MEHP, FRCPC</strong>
            {" "}· For educational purposes only ·{" "}
            <em>Not for clinical decision-making</em>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN DASHBOARD ───────────────────────────────────────────────────────────
function Dashboard({educator,onLogout}){
  const [tab,setTab]=useState("cases");
  const [showInfo,setShowInfo]=useState(false);
  // Inject Inter globally once
  useEffect(()=>{
    document.body.style.fontFamily=FONT_UI;
    document.body.style.WebkitFontSmoothing="antialiased";
  },[]);
  const [assignments,setAssignments]=useState([]);
  // assignmentGroups: structured multi-case assignment bundles built in the Assignments tab
  const [assignmentGroups,setAssignmentGroups]=useState([]);
  // myOverrides: per-user edits to base cases (private, stored under user's key)
  const [myOverrides,setMyOverrides]=useState({});
  // sharedCases: community custom cases (shared storage, keyed by case id)
  const [sharedCases,setSharedCases]=useState({});
  // favourites: set of case IDs starred by this educator
  const [favourites,setFavourites]=useState(new Set());
  const [loading,setLoading]=useState(true);
  // traineeRoster: [{email, name, addedAt}] — manually added or auto-added from assignments
  const [traineeRoster,setTraineeRoster]=useState([]);
  // dismissedTrainees: Set of emails the educator has explicitly removed from their list
  const [dismissedTrainees,setDismissedTrainees]=useState(new Set());

  // Per-user storage key — never shared
  const myKey=`edu_${educator.email.replace(/[^a-z0-9]/gi,"_")}`;

  // ── Trainee roster: load from Firestore on mount ───────────────────────────
  useEffect(()=>{
    if(!educator?.email) return;
    const rosterRef=collection(db,`educator_trainees/${educator.email}/roster`);
    const unsub=onSnapshot(rosterRef,(snap)=>{
      setTraineeRoster(snap.docs.map(d=>({email:d.id,...d.data()})));
    },(err)=>console.warn('[KiRPA] roster snapshot error',err));
    return ()=>unsub();
  },[educator?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Dismissed trainees: load from Firestore on mount ──────────────────────
  useEffect(()=>{
    if(!educator?.email) return;
    const dismissedRef=collection(db,`educator_trainees/${educator.email}/dismissed`);
    const unsub=onSnapshot(dismissedRef,(snap)=>{
      setDismissedTrainees(new Set(snap.docs.map(d=>d.id)));
    },(err)=>console.warn('[KiRPA] dismissed snapshot error',err));
    return ()=>unsub();
  },[educator?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  // Upsert a trainee into the roster (idempotent, uses email as doc ID)
  // Also clears any prior dismissal so they re-appear
  const upsertRosterTrainee=async(email,name="")=>{
    if(!email||!educator?.email) return;
    const em=email.toLowerCase().trim();
    if(!em.includes("@")) return;
    const ref=doc(db,`educator_trainees/${educator.email}/roster/${em}`);
    await setDoc(ref,{name:name||"",addedAt:serverTimestamp()},{merge:true});
    // Un-dismiss if previously dismissed
    const dismissRef=doc(db,`educator_trainees/${educator.email}/dismissed/${em}`);
    await deleteDoc(dismissRef).catch(()=>{});
  };

  // Remove a trainee — writes to dismissed so they're hidden even if they have assignments
  const removeRosterTrainee=async(email)=>{
    if(!email||!educator?.email) return;
    const em=email.toLowerCase().trim();
    // Remove from roster
    const rosterRef=doc(db,`educator_trainees/${educator.email}/roster/${em}`);
    await deleteDoc(rosterRef).catch(e=>console.warn('[KiRPA] removeRosterTrainee:',e));
    // Add to dismissed so assignment-derived trainees also disappear
    const dismissRef=doc(db,`educator_trainees/${educator.email}/dismissed/${em}`);
    await setDoc(dismissRef,{dismissedAt:serverTimestamp()}).catch(()=>{});
  };

  useEffect(()=>{(async()=>{
    const a=await store.get(`${myKey}_a`); if(a) setAssignments(a);
    const ag=await store.get(`${myKey}_ag`); if(ag) setAssignmentGroups(Array.isArray(ag)?ag:[]);
    const o=await store.get(`${myKey}_overrides`); if(o) setMyOverrides(o);
    // Load admin (global) overrides — these take effect for all educators
    const adminOv=await store.getShared('kirpa_admin_overrides');
    if(adminOv){
      // Merge: personal overrides win over admin, so educators can still override further
      setMyOverrides(prev=>({...adminOv,...prev}));
    }
    const sc=await store.getShared("crrt_community_cases"); if(sc) setSharedCases(sc);
    const fav=await store.get(`${myKey}_favs`); if(fav) setFavourites(new Set(fav));
    setLoading(false);
  })();},[]);

  // ── Auto-refresh from Firestore on mount (after store loads) ───────────────
  // Ensures educator always sees current results even after page reload
  const [didInitRefresh, setDidInitRefresh] = useState(false);
  useEffect(() => {
    if (didInitRefresh || !educator?.email || !assignments.length) return;
    setDidInitRefresh(true);
    // Pull latest from Firestore silently in background
    Promise.all(assignments.map(async a => {
      try {
        const snap = await getDoc(doc(db, 'assignments', a.id));
        if (!snap.exists()) return a;
        const d = snap.data();
        if (!d.result && !d.status) return a;
        return { ...a,
          attempts:   d.attempts   ?? a.attempts,
          status:     d.status     || a.status,
          lastResult: d.result     || a.lastResult,
          traineeRole: d.traineeRole || a.traineeRole || "",
          traineeProfileName: d.traineeProfileName || a.traineeProfileName || "",
          feedbackReleased: d.feedbackReleased ?? a.feedbackReleased,
        };
      } catch { return a; }
    })).then(updated => {
      setAssignments(updated);
      store.set(`${myKey}_a`, updated).catch(()=>{});
    }).catch(()=>{});
  }, [educator?.email, assignments.length, didInitRefresh]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Real-time listener: sync trainee results to educator ────────────────────
  // Attaches one onSnapshot per assignment doc. Re-runs whenever assignments
  // list changes (e.g. after initial load from store). No composite index needed.
  const assignmentIds = assignments.map(a=>a.id).join(',');
  useEffect(() => {
    if (!educator?.email || !assignments.length) return;
    const unsubs = assignments.map(a => {
      if (!a.id) return null;
      return onSnapshot(
        doc(db, 'assignments', a.id),
        (snap) => {
          if (!snap.exists()) return;
          const d = snap.data();
          // Skip only if truly nothing has changed (no result at all)
          if (!d.result) return;
          setAssignments(curr => {
            const i = curr.findIndex(x => x.id === a.id);
            if (i === -1) return curr;
            const existing = curr[i];
            const next = [...curr];
            next[i] = {
              ...existing,
              attempts:        d.attempts        ?? existing.attempts,
              status:          d.status          || existing.status,
              lastResult:      d.result          || existing.lastResult,
              traineeRole:     d.traineeRole     || existing.traineeRole || "",
              traineeProfileName: d.traineeProfileName || existing.traineeProfileName || "",
              feedbackReleased:d.feedbackReleased ?? existing.feedbackReleased,
            };
            // Persist back to store so results survive refresh
            const storeKey = `edu_${educator.email.replace(/[^a-z0-9]/gi,'_')}_a`;
            store.set(storeKey, next).catch(e => console.warn('[KiRPA] store sync:', e));
            return next;
          });
        },
        (err) => console.warn('[KiRPA] snapshot error', a.id, err)
      );
    }).filter(Boolean);
    return () => unsubs.forEach(u => u());
  }, [assignmentIds, educator?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  // Manual refresh: pull latest results from Firestore for all assignments
  const refreshResults = async () => {
    if (!assignments.length) return;
    try {
      const updated = await Promise.all(assignments.map(async a => {
        try {
          const snap = await getDoc(doc(db, 'assignments', a.id));
          if (!snap.exists()) return a;
          const d = snap.data();
          if (!d.result) return a;
          return { ...a, attempts: d.attempts ?? a.attempts, status: d.status || a.status, lastResult: d.result || a.lastResult };
        } catch { return a; }
      }));
      setAssignments(updated);
      await store.set(`${myKey}_a`, updated);
    } catch(e) { console.warn('[KiRPA] refreshResults error:', e); }
  };

  const saveAssignments = async (updated) => {
    setAssignments(updated);
    await store.set(`${myKey}_a`, updated);

    // ── Auto-add new trainees to roster ──────────────────────────────────────
    const newlySentForRoster = updated.filter(ua => {
      const old = assignments.find(a => a.id === ua.id);
      return !old && ua.traineeEmail && ua.traineeEmail.includes("@");
    });
    for(const a of newlySentForRoster){
      upsertRosterTrainee(a.traineeEmail, a.traineeName||"").catch(()=>{});
    }

    // ── Write new assignments to Firestore via CF (bypasses security rules) ──
    const newlySentFirestore = updated.filter(ua => {
      const old = assignments.find(a => a.id === ua.id);
      return !old;
    });
    if (newlySentFirestore.length > 0) {
      try {
        const fns = getFunctions(firebaseApp, "us-central1");
        const writeAssignment = httpsCallable(fns, "writeAssignment");
        await Promise.all(newlySentFirestore.map(a => writeAssignment({
          assignment: {
            id:                   a.id,
            caseId:               a.caseId               || "",
            caseTitle:            a.caseTitle             || "",
            caseFacet:            a.caseFacet             || "",
            caseOptions:          a.caseOptions           || null,
            caseOverride:         a.caseOverride          || null,
            educatorEmail:        educator.email          || "",
            educatorName:         educator.name           || "",
            educatorInstitution:  educator.institution    || "",
            educatorInstitutionId:educator.institutionId  || "",
            traineeEmail:         (a.traineeEmail         || "").toLowerCase().trim(),
            traineeName:          a.traineeName           || "",
            feedbackMode:         a.feedbackMode          || "immediate",
            maxAttempts:          a.maxAttempts           || 1,
            groupId:              a.groupId               || "",
            sentAt:               a.sentAt                || new Date().toISOString(),
            deadline:             a.deadline              || null,
            note:                 a.note                  || "",
            // Language: caseSourceLocale is the authoring language of the case;
            // locale is the educator's chosen delivery language. Translations
            // are applied server-side at delivery, not snapshotted here.
            caseSourceLocale:     a.caseSourceLocale      || a.caseOverride?.sourceLocale || "en",
            locale:               a.locale                || "en",
            allowLocaleOverride:  a.allowLocaleOverride !== false,
          }
        })));
      } catch(e) { console.error('writeAssignment CF failed:', e); }
    }

    // ── Usage events: log newly-created (sent) assignments ────────────────
    // These feed the "assignments sent" / "cases sent" usage metrics dashboard.
    const newlySent = updated.filter(ua => {
      const old = assignments.find(a => a.id === ua.id);
      return !old; // brand new assignment
    });
    for (const a of newlySent) {
      try {
        const [hashedEdu, hashedInst] = await Promise.all([
          sha256(a.educatorEmail || 'unknown'),
          sha256(a.educatorInstitutionId || a.educatorEmail || 'unknown'),
        ]);
        const usageEvent = {
          event_type:        'assignment_sent',
          educator_id:       hashedEdu,
          institution_id:    hashedInst,
          case_id:           await sha256(a.caseId),
          case_id_readable:  a.caseId,
          case_facet:        a.caseFacet || '',
          case_is_custom:    a.caseId?.startsWith('custom_') || false,
          is_anonymous:      a.isAnonymous || false,
          assignment_group_id: a.groupId || null,
          is_timed:          a.timed ? true : false,
          has_expiry:        a.expiresAt ? true : false,
          feedback_mode:     a.feedbackMode || 'immediate',
          sent_date_utc:     new Date().toISOString().split('T')[0],
        };
        const existing = await store.getShared('kirpa_usage_events') || {};
        existing[`sent_${a.id}`] = usageEvent;
        await store.setShared('kirpa_usage_events', existing);
      } catch(e) { console.error('Usage event capture error:', e); }
    }

    // Research events are now written server-side by the submitTraineeAssignmentResult
    // Cloud Function directly to /research_events/{id}. No client-side capture needed.
  };

  // Save an assignment group bundle
  const saveAssignmentGroup = async (updated) => {
    setAssignmentGroups(updated);
    await store.set(`${myKey}_ag`, updated);
  };

  // Delete a pending group (no Firestore cleanup needed — flat assignments not yet written)
  // Cancel a deployed group — marks Firestore assignment docs as cancelled + removes locally
  const deleteAssignmentGroup = async (group) => {
    const isDeployed = group.status === "deployed";
    const label = isDeployed ? "cancel" : "delete";
    const detail = isDeployed
      ? "This will cancel all pending assignments in this group. Trainees who have already completed a case will keep their results, but incomplete cases will be withdrawn."
      : "This will permanently delete this saved assignment. It has not been sent to any trainees.";
    if (!window.confirm(`${label.charAt(0).toUpperCase()+label.slice(1)} "${group.name}"?\n\n${detail}`)) return;

    // 1. Remove from local state + store immediately
    const updatedGroups = assignmentGroups.filter(g => g.id !== group.id);
    setAssignmentGroups(updatedGroups);
    await store.set(`${myKey}_ag`, updatedGroups);

    // 2. Remove associated flat assignments from local state
    const groupAssignmentIds = assignments.filter(a => a.groupId === group.id).map(a => a.id);
    if (groupAssignmentIds.length) {
      const updatedAssignments = assignments.filter(a => a.groupId !== group.id);
      setAssignments(updatedAssignments);
      await store.set(`${myKey}_a`, updatedAssignments);
    }

    // 3. For deployed groups — call CF to mark Firestore docs as cancelled
    if (isDeployed && group.id) {
      try {
        const fns = getFunctions(firebaseApp, "us-central1");
        const cancel = httpsCallable(fns, "cancelAssignmentGroup");
        await cancel({ groupId: group.id, assignmentIds: groupAssignmentIds });
      } catch(e) { console.error("[KiRPA] cancelAssignmentGroup CF failed:", e); }
    }
  };

  // Save a base-case override — private to this user only
  const saveMyOverride=async(id,data)=>{
    const u={...myOverrides,[id]:data};setMyOverrides(u);
    await store.set(`${myKey}_overrides`,u);
    // Admin overrides are also written to the shared store so all educators see them
    if(educator.email===ADMIN_EMAIL){
      const existing=await store.getShared('kirpa_admin_overrides')||{};
      await store.setShared('kirpa_admin_overrides',{...existing,[id]:data});
    }
  };

  // Toggle a case favourite
  const toggleFavourite=async(id)=>{
    setFavourites(prev=>{
      const next=new Set(prev);
      if(next.has(id)) next.delete(id); else next.add(id);
      store.set(`${myKey}_favs`,[...next]);
      return next;
    });
  };

  // Delete a custom case (only allowed for cases the educator created)
  const deleteCase=async(id)=>{
    if(!id.startsWith("custom_")) return;
    const next={...sharedCases};
    delete next[id];
    setSharedCases(next);
    await store.setShared("crrt_community_cases",next);
  };

  // Save a custom case — can be shared or private (stored in shared pool with creatorEmail)
  const saveSharedCase=async(id,data,shared=data.isShared)=>{
    const updated={...data,isShared:shared};
    // Always save to shared storage so the creator can access it across devices
    // Non-shared cases are still stored there but won't show to others (filtered by isShared)
    const u={...sharedCases,[id]:updated};
    setSharedCases(u);
    await store.setShared("crrt_community_cases",u);
    // Auto-favourite cases created by this educator
    if(data.creatorEmail===educator.email){
      setFavourites(prev=>{
        const next=new Set(prev);
        next.add(id);
        store.set(`${myKey}_favs`,[...next]);
        return next;
      });
    }
  };

  const releaseFeedback=async id=>saveAssignments(assignments.map(a=>a.id===id?{...a,feedbackMode:"immediate"}:a));

  const seedDemo=async()=>saveAssignments([...assignments,
    {id:"d1",caseId:"Case1",caseTitle:"Case 1: The First Prescription",caseFacet:"General Prescribing",educatorName:educator.name,traineeName:"Dr. Alex Chen",traineeEmail:"a.chen@hospital.edu",feedbackMode:"immediate",deadline:"",feedbackDate:"",note:"",sentAt:new Date(Date.now()-86400000*3).toISOString(),status:"completed",result:{success:true,attempts:1,submission:{prescription:{modality:"CVVHDF",access:"9F",filter:"ST60",prime:"albumin",anticoag:"citrate",solution:"Prism0cal",bfr:100,pbpRate:400,dialysateRate:500,repRate:200,netUf:25,K_add:2,PO4_add:1}}}},
    {id:"d2",caseId:"Case6",caseTitle:"Case 6: High Ammonia",caseFacet:"Clearance",educatorName:educator.name,traineeName:"Dr. Alex Chen",traineeEmail:"a.chen@hospital.edu",feedbackMode:"immediate",deadline:"",feedbackDate:"",note:"",sentAt:new Date(Date.now()-86400000*2).toISOString(),status:"completed",result:{success:false,attempts:2,feedback:"Clearance was below target. Remember for hyperammonemia clearance must exceed 6 L/1.73m²/hr.",submission:{prescription:{modality:"CVVHDF",access:"7F",filter:"HF20",prime:"blood",anticoag:"heparin",solution:"Prismasol 0",bfr:40,pbpRate:200,dialysateRate:200,repRate:200,netUf:5,K_add:0,PO4_add:0}}}},
    {id:"d3",caseId:"Case5",caseTitle:"Case 5: Clotting Filter",caseFacet:"Anticoagulation",educatorName:educator.name,traineeName:"Dr. Maria Santos",traineeEmail:"m.santos@hospital.edu",feedbackMode:"hidden",deadline:"",feedbackDate:"",note:"",sentAt:new Date(Date.now()-86400000).toISOString(),status:"pending",result:null},
    {id:"d4",caseId:"Case8",caseTitle:"Case 8: Access Pressure Alarms",caseFacet:"Access/Circuit/Machine",educatorName:educator.name,traineeName:"Dr. Alex Chen",traineeEmail:"a.chen@hospital.edu",feedbackMode:"immediate",deadline:"",feedbackDate:"",note:"",sentAt:new Date(Date.now()-86400000*4).toISOString(),status:"completed",result:{success:true,attempts:1,submission:{prescription:{modality:"CVVHDF",access:"12F",filter:"ST150",prime:"saline",anticoag:"citrate",solution:"Prism0cal",bfr:140,pbpRate:400,dialysateRate:650,repRate:100,netUf:20}}}},
    {id:"d5",caseId:"Case14",caseTitle:"Case 14: Abnormal Electrolytes",caseFacet:"Electrolytes",educatorName:educator.name,traineeName:"Dr. Maria Santos",traineeEmail:"m.santos@hospital.edu",feedbackMode:"immediate",deadline:"",feedbackDate:"",note:"",sentAt:new Date(Date.now()-86400000*2).toISOString(),status:"completed",result:{success:false,attempts:3,feedback:"Potassium additive was insufficient. Target K ≥3 mmol/L.",submission:{prescription:{modality:"CVVHDF",access:"8F",filter:"ST60",prime:"saline",anticoag:"none",solution:"Prismasol 0",bfr:100,pbpRate:325,dialysateRate:425,repRate:100,netUf:16,K_add:1,PO4_add:0}}}},
  ]);

  // Only show community cases that are shared OR created by this user
  const visibleShared=Object.fromEntries(
    Object.entries(sharedCases).filter(([,c])=>c.isShared||c.creatorEmail===educator.email)
  );

  if(loading) return <div style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:"100vh"}}><Ic n="spin" s="w-8 h-8"/></div>;

  const TABS=[{id:"cases",label:"Cases",icon:"eye"},{id:"trainees",label:"Trainees",icon:"chart"},{id:"assignments",label:"Assignments",icon:"send"},...(educator.email===ADMIN_EMAIL?[{id:"admin",label:"Admin",icon:"lock"}]:[])];
  const totalCases=Object.keys(BASE_CASES).length+Object.keys(visibleShared).length;

  return <div style={{minHeight:"100vh",background:"#f5f3ff",fontFamily:"Georgia,serif"}}>
    {showInfo&&<InfoModal onClose={()=>setShowInfo(false)}/>}
    <div style={{background:C.dark,color:"white",padding:"0 24px"}}>
      <div style={{maxWidth:"1080px",margin:"0 auto",display:"flex",alignItems:"center",height:"58px"}}>
        <div style={{flex:1,display:"flex",alignItems:"center",gap:"10px"}}>
          <span style={{fontSize:"16px",fontWeight:"700",letterSpacing:"0.02em"}}>KiRPA</span>
          <span style={{fontSize:"12px",color:"#c4b5fd",fontWeight:"400",marginLeft:"2px"}}>Kidney Replacement Prescription Analytics</span>
          {educator.institution&&<span style={{fontSize:"11px",color:"rgba(196,181,253,0.7)",fontWeight:"400",marginLeft:"4px"}}>· {educator.institution}</span>}
        </div>
        <div style={{display:"flex",alignItems:"center",gap:"10px"}}>
          <span style={{fontSize:"12px",color:"#c4b5fd"}}>{educator.name}</span>
          <LocaleSwitcher/>
          <button onClick={()=>setShowInfo(true)}
            title="About KiRPA"
            style={{width:"30px",height:"30px",borderRadius:"50%",
              background:"rgba(255,255,255,0.12)",color:"white",
              border:"1px solid rgba(255,255,255,0.22)",cursor:"pointer",
              display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            <Ic n="info" s="w-4 h-4"/>
          </button>
          <button onClick={onLogout} style={{padding:"5px 12px",background:"rgba(255,255,255,0.1)",color:"white",border:"1px solid rgba(255,255,255,0.2)",borderRadius:"6px",cursor:"pointer",fontSize:"12px",fontFamily:"inherit"}}>Sign Out</button>
        </div>
      </div>
    </div>
    <div style={{background:C.mid,color:"white",padding:"12px 24px"}}>
      <div style={{maxWidth:"1080px",margin:"0 auto",display:"flex",gap:"32px",flexWrap:"wrap",alignItems:"center"}}>
        {[["Cases Available",totalCases,"#c4b5fd"],["Assignments Sent",assignments.length,"#93c5fd"],["Trainees",(()=>{const allEmails=new Set([...[...new Set(assignments.map(a=>a.traineeEmail).filter(Boolean))],...traineeRoster.map(r=>r.email)].map(e=>(e||"").toLowerCase()).filter(e=>e&&!dismissedTrainees.has(e)));return allEmails.size;})(),"#6ee7b7"],["Completed",assignments.filter(a=>a.status==="completed").length,"#fcd34d"]].map(([lb,v,cc])=><div key={lb}><div style={{fontSize:"20px",fontWeight:"800",color:cc}}>{v}</div><div style={{fontSize:"10px",color:"rgba(255,255,255,0.6)"}}>{lb}</div></div>)}
        {assignments.length===0&&<button onClick={seedDemo} style={{marginLeft:"auto",padding:"5px 12px",background:"rgba(255,255,255,0.1)",color:"white",border:"1px solid rgba(255,255,255,0.2)",borderRadius:"6px",cursor:"pointer",fontSize:"12px",fontFamily:"inherit"}}>Load Demo Data</button>}
      </div>
    </div>
    <div style={{maxWidth:"1080px",margin:"0 auto",padding:"20px"}}>
      <div style={{display:"flex",gap:"7px",marginBottom:"18px"}}>
        {TABS.map(t=><button key={t.id} onClick={()=>setTab(t.id)} style={{padding:"8px 18px",borderRadius:"9px",border:"none",cursor:"pointer",fontWeight:"700",fontSize:"13px",display:"flex",alignItems:"center",gap:"6px",background:tab===t.id?C.pu:"white",color:tab===t.id?"white":"#6b7280",boxShadow:tab===t.id?`0 4px 14px rgba(79,38,131,0.35)`:"0 1px 4px rgba(0,0,0,0.06)",fontFamily:"inherit"}}>
          <Ic n={t.icon} s="w-4 h-4"/>{t.label}</button>)}
      </div>
      <div style={{background:"white",borderRadius:"15px",boxShadow:"0 1px 8px rgba(0,0,0,0.07)",minHeight:"400px",overflow:"hidden"}}>
        {tab==="cases"&&<CasesTab educator={educator} myOverrides={myOverrides} sharedCases={visibleShared} onSaveMyCase={saveMyOverride} onSaveSharedCase={saveSharedCase} onSend={async a=>saveAssignments([...assignments,...a])} onDelete={deleteCase} favourites={favourites} onToggleFav={toggleFavourite} savedGroups={assignmentGroups} onAddToGroup={(caseId)=>{setTab("assignments");/* builder will handle the add */sessionStorage.setItem("kirpa_addCase",caseId);}}/>}
        {tab==="assignments"&&<AssignmentsTab
          educator={educator}
          allCases={{
            ...Object.fromEntries(Object.entries(BASE_CASES).map(([id,c])=>{
              const ov=myOverrides[id]||{};
              const mergedIS=(ov.initialState||c.initialState)?{
                ...(c.initialState||{}),...(ov.initialState||{}),
                vitals:(ov.initialState?.vitals||c.initialState?.vitals)?{...(c.initialState?.vitals||{}),...(ov.initialState?.vitals||{})}:undefined,
                labs:(ov.initialState?.labs||c.initialState?.labs)?{...(c.initialState?.labs||{}),...(ov.initialState?.labs||{})}:undefined,
                gas:(ov.initialState?.gas||c.initialState?.gas)?{...(c.initialState?.gas||{}),...(ov.initialState?.gas||{})}:undefined,
                cbc:(ov.initialState?.cbc||c.initialState?.cbc)?{...(c.initialState?.cbc||{}),...(ov.initialState?.cbc||{})}:undefined,
              }:undefined;
              if(mergedIS) Object.keys(mergedIS).forEach(k=>mergedIS[k]===undefined&&delete mergedIS[k]);
              return [id,{...c,...ov,...(mergedIS?{initialState:mergedIS}:{})}];
            })),
            ...Object.fromEntries(Object.entries(sharedCases).filter(([,c])=>c.isShared||c.creatorEmail===educator.email))
          }}
          myOverrides={myOverrides}
          favourites={favourites}
          assignmentGroups={assignmentGroups}
          onSave={saveAssignmentGroup}
          onDelete={deleteAssignmentGroup}
          onDeploy={async(group,deployedAssignments)=>{
            const updatedGroup = { ...group, status:"deployed", deployedAt:new Date().toISOString(),
              deployedAssignments: Array.isArray(deployedAssignments)?deployedAssignments:[] };
            await saveAssignmentGroup([
              ...assignmentGroups.filter(x=>x.id!==updatedGroup.id),
              updatedGroup,
            ]);
            // Persist flat assignments so TraineesTab can see them immediately
            await saveAssignments([...assignments,...deployedAssignments]);
            // Write each flat assignment to Firestore via CF
            if(Array.isArray(deployedAssignments)&&deployedAssignments.length){
              try{
                const _fns=getFunctions(firebaseApp,"us-central1");
                const _wa=httpsCallable(_fns,"writeAssignment");
                await Promise.all(deployedAssignments.map(a=>_wa({assignment:{
                  id:a.id, caseId:a.caseId||"", caseTitle:a.caseTitle||"",
                  caseFacet:a.caseFacet||"", caseOptions:a.caseOptions||null,
                  caseOverride:a.caseOverride||null,
                  educatorEmail:a.educatorEmail||"", educatorName:a.educatorName||"",
                  educatorInstitution:a.educatorInstitution||"",
                  educatorInstitutionId:a.educatorInstitutionId||"",
                  traineeEmail:(a.traineeEmail||"").toLowerCase().trim(),
                  traineeName:a.traineeName||"",
                  feedbackMode:a.feedbackMode||"immediate",
                  feedbackDate:a.feedbackDate||null,
                  maxAttempts:a.maxAttempts||1,
                  groupId:a.groupId||"", sentAt:a.sentAt||new Date().toISOString(),
                  deadline:a.deadline||null, note:a.note||"",
                  caseSourceLocale:a.caseSourceLocale||a.caseOverride?.sourceLocale||"en",
                  locale:a.locale||group?.locale||"en",
                  allowLocaleOverride:a.allowLocaleOverride!==false,
                }})));
              }catch(e){console.error("writeAssignment CF (group):",e);}
            }
            // Email trainees
            try{
              const _fn=getFunctions(firebaseApp,"us-central1");
              const _sal=httpsCallable(_fn,"sendAssignmentLink");
              const tees=Array.isArray(group?.trainees)?group.trainees:[];
              for(const t of tees){
                const em=(t.email||"").trim().toLowerCase();
                if(!em.includes("@")) continue;
                const ids=(deployedAssignments||[]).filter(a=>(a.traineeEmail||"").toLowerCase()===em).map(a=>a.id);
                await _sal({traineeEmail:em, traineeName:t.name||"",
                  caseTitle:group?.name||"KiRPA Assignment",
                  educatorName:group?.educatorName||"",
                  locale:group?.locale||"en",
                  payload:{assignmentIds:ids,groupId:group?.id||"",educatorName:group?.educatorName||""},
                });
              }
            }catch(e){console.error("sendAssignmentLink failed:",e);alert(e?.message||"Deployed, but emails failed.");}
            // Auto-add all deployed trainees to roster
            try{
              const tees=Array.isArray(group?.trainees)?group.trainees:[];
              for(const t of tees){
                const em=(t.email||"").trim().toLowerCase();
                if(em.includes("@")) await upsertRosterTrainee(em,t.name||"").catch(()=>{});
              }
            }catch(e){console.warn("roster upsert (group):",e);}
          }}
        />}
        {tab==="trainees"&&<TraineesTab assignments={assignments} onRelease={releaseFeedback} onRefresh={refreshResults} educator={educator} traineeRoster={traineeRoster} dismissedTrainees={dismissedTrainees} onAddTrainee={upsertRosterTrainee} onRemoveTrainee={removeRosterTrainee}/>}
        {tab==="admin"&&<AdminTab educator={educator}/>}
      </div>
      <div style={{marginTop:"14px"}}>
        <div style={{textAlign:"center",padding:"10px 0 4px",borderTop:"1px solid #e5e7eb"}}>
          <p style={{margin:0,fontSize:"11px",color:"#9ca3af",lineHeight:"1.8"}}>
            <strong style={{color:"#6b7280"}}>KiRPA — Kidney Replacement Prescription Analytics</strong><br/>
            Created by Dr. Amrit Kirpalani MD, MEHP, FRCPC. For educational purposes only.
          </p>
        </div>
      </div>
    </div>
  </div>;
}

// ─────────────────────────────────────────────────────────────────────────────
// JoinSession — trainee-facing screen for `?join=ABC123` QR landings.
// Collects email + name + role (or anonymous), calls joinAssignmentSession,
// then redirects to /?trainee_token=... to enter the existing magic-link flow.
// ─────────────────────────────────────────────────────────────────────────────
function JoinSession({joinCode,onExit}){
  const {t}=useLocale();
  const [name,setName]=useState("");
  const [email,setEmail]=useState("");
  const [role,setRole]=useState("");
  const [pin,setPin]=useState("");
  const [anonymous,setAnonymous]=useState(false);
  const [busy,setBusy]=useState(false);
  const [err,setErr]=useState("");

  const code = (joinCode||"").toUpperCase().trim();
  const canSubmit = role && (anonymous || /^[^@]+@[^@]+\.[^@]+$/.test(email.trim()));

  const doJoin = async () => {
    setBusy(true); setErr("");
    try {
      const fns = getFunctions(firebaseApp,"us-central1");
      const joinFn = httpsCallable(fns,"joinAssignmentSession");
      const res = await joinFn({
        joinCode:     code,
        traineeEmail: anonymous ? "" : email.trim().toLowerCase(),
        traineeName:  name.trim(),
        traineeRole:  role,
        anonymous,
        pin:          pin || undefined,
      });
      const token = res?.data?.token;
      if(!token) throw new Error("Could not start session.");
      // Hand off to the existing magic-link flow
      window.location.replace(`${window.location.pathname}?trainee_token=${token}`);
    } catch(e) {
      console.error(e);
      setErr(e?.message || "Could not join session.");
      setBusy(false);
    }
  };

  return (
    <div style={{minHeight:"100vh",background:"linear-gradient(135deg,#1a0533 0%,#2d1054 40%,#4F2683 100%)",
      display:"flex",alignItems:"center",justifyContent:"center",padding:"24px",fontFamily:FONT_UI}}>
      <div style={{background:"rgba(255,255,255,0.97)",borderRadius:"16px",padding:"36px",width:"100%",
        maxWidth:"440px",boxShadow:"0 32px 80px rgba(0,0,0,0.4)"}}>
        <div style={{textAlign:"center",marginBottom:"22px"}}>
          <div style={{fontSize:"24px",fontWeight:"800",color:"#1a0533",margin:"0 0 4px",letterSpacing:"-0.02em"}}>KiRPA</div>
          <p style={{color:"#6b7280",fontSize:"13px",margin:"0 0 14px"}}>Join CRRT Prescribing Session</p>
          <div style={{display:"inline-block",padding:"6px 14px",background:"#faf5ff",border:`1px solid ${C.pu}`,
            borderRadius:"8px",fontFamily:"monospace",fontSize:"18px",fontWeight:"800",color:C.pu,letterSpacing:"0.18em"}}>
            {code}
          </div>
        </div>

        {err && <div style={{padding:"10px 13px",background:"#fee2e2",border:"1px solid #fecaca",
          borderRadius:"8px",fontSize:"12px",color:"#991b1b",marginBottom:"14px"}}>{err}</div>}

        <div style={{marginBottom:"12px"}}>
          <label style={C.lbl}>Your Role *</label>
          <select value={role} onChange={e=>setRole(e.target.value)} style={C.sel}>
            <option value="">— Select your role —</option>
            <option value="Medical Student">Medical Student</option>
            <option value="Resident">Resident</option>
            <option value="Fellow">Fellow</option>
            <option value="Attending">Attending Physician</option>
            <option value="Nurse">Nurse</option>
            <option value="Nurse Practitioner">Nurse Practitioner</option>
            <option value="Pharmacist">Pharmacist</option>
            <option value="Respiratory Therapist">Respiratory Therapist</option>
            <option value="Other">Other</option>
          </select>
        </div>

        {!anonymous && <>
          <div style={{marginBottom:"12px"}}>
            <label style={C.lbl}>{t("shell.nameOptional","Name (optional)")}</label>
            <input value={name} onChange={e=>setName(e.target.value)}
              placeholder="Full name" style={C.inp}/>
          </div>
          <div style={{marginBottom:"12px"}}>
            <label style={C.lbl}>Email *</label>
            <input type="email" value={email} onChange={e=>setEmail(e.target.value)}
              placeholder="you@hospital.edu" style={C.inp}/>
          </div>
        </>}

        <div style={{marginBottom:"12px"}}>
          <label style={{display:"flex",alignItems:"center",gap:"8px",cursor:"pointer",
            fontSize:"12px",color:"#6b7280"}}>
            <input type="checkbox" checked={anonymous} onChange={e=>setAnonymous(e.target.checked)}/>
            Join anonymously (if allowed by session)
          </label>
        </div>

        <div style={{marginBottom:"16px"}}>
          <label style={C.lbl}>PIN (if required by educator)</label>
          <input value={pin} onChange={e=>setPin(e.target.value.replace(/\D/g,"").slice(0,4))}
            inputMode="numeric" maxLength={4} placeholder="—"
            style={{...C.inp,maxWidth:"140px",fontFamily:"monospace",letterSpacing:"0.2em",fontSize:"16px"}}/>
        </div>

        <button onClick={doJoin} disabled={!canSubmit||busy}
          style={{...C.btn(canSubmit?C.pu:"#e5e7eb",canSubmit?"white":"#9ca3af",
            {width:"100%",justifyContent:"center",padding:"11px",fontSize:"14px"}),
            opacity:busy?0.7:1,cursor:(!canSubmit||busy)?"not-allowed":"pointer"}}>
          {busy ? <Ic n="spin" s="w-4 h-4"/> : null}
          {busy ? "Joining…" : "Start Session →"}
        </button>

        {onExit && <button onClick={onExit}
          style={{display:"block",margin:"14px auto 0",background:"none",border:"none",
            color:"#9ca3af",cursor:"pointer",fontSize:"12px",fontFamily:FONT_UI}}>
          ← Back
        </button>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Trainee link routing helper (supports both ?trainee_token= AND #/?trainee_token=)
// ─────────────────────────────────────────────────────────────────────────────
function getTraineeRoutingParams(){
  try{
    // Normal query string: https://site.com/?trainee_token=...
    const searchParams = new URLSearchParams(window.location.search || "");

    // Hash query string (some deployments / redirects end up here):
    // https://site.com/#/?trainee_token=...
    const hash = window.location.hash || "";
    const hashQuery = hash.includes("?") ? hash.slice(hash.indexOf("?")) : "";
    const hashParams = new URLSearchParams(hashQuery);

    const token =
      searchParams.get("trainee_token") ||
      searchParams.get("token") ||
      searchParams.get("t") ||
      hashParams.get("trainee_token") ||
      hashParams.get("token") ||
      hashParams.get("t") ||
      "";

    const assignmentId =
      searchParams.get("assignment_id") ||
      searchParams.get("assignmentId") ||
      hashParams.get("assignment_id") ||
      hashParams.get("assignmentId") ||
      "";

    const groupId =
      searchParams.get("group_id") ||
      searchParams.get("groupId") ||
      hashParams.get("group_id") ||
      hashParams.get("groupId") ||
      "";

    const joinCode =
      searchParams.get("join") ||
      searchParams.get("joinCode") ||
      hashParams.get("join") ||
      hashParams.get("joinCode") ||
      "";

    return { token, assignmentId, groupId, joinCode };
  }catch{
    return { token:"", assignmentId:"", groupId:"", joinCode:"" };
  }
}



// ─── ROOT ─────────────────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════
//  PRACTICE TRACK
//  Self-directed learners. No login, no educator, no assignment.
//  Facets and tags are NEVER shown per-case; cases are presented as Room 1…N.
//  Domain performance is revealed only in the progress view, and only once
//  enough cases are done that the breakdown is not per-case identifying.
// ═══════════════════════════════════════════════════════════════════════════

const PRACTICE_DEVICE_KEY = "kirpa_device_id";

// Phase 2 records no consent in the browser. Under the notice model there is
// nothing to consent to by clicking, so there is no state to remember: the
// notice is on the landing page and on every case screen whether or not the
// learner has read it, and the letter version that applies to a row is stamped
// server-side in index.js.
//
// The key the implied-consent modal used was "kirpa_loi_consent". It is left
// behind on devices that saw Version 1 and is read by nothing. It is not
// cleared, because clearing it would mean writing storage code whose only
// purpose is to tidy, on a key that is already inert.
const DOMAIN_REVEAL_MIN   = 4;   // completed cases before the domain breakdown unlocks

// Stable per-device identifier. Created silently on first visit and never
// replaced, so linking an email later can stitch earlier attempts rather than
// orphaning them.
function getDeviceId(){
  try{
    let id = window.localStorage.getItem(PRACTICE_DEVICE_KEY);
    if(id && /^[a-zA-Z0-9_-]{16,64}$/.test(id)) return id;
    id = (window.crypto?.randomUUID?.() || `d${Date.now()}${Math.random().toString(36).slice(2)}`)
           .replace(/-/g,"").slice(0,32);
    window.localStorage.setItem(PRACTICE_DEVICE_KEY,id);
    return id;
  }catch{
    // Private browsing with storage disabled: fall back to a per-session id so
    // the learner can still practise. Their attempts simply will not link
    // across visits, which is recorded honestly by identity_tier.
    if(!window.__kirpaEphemeralDevice){
      window.__kirpaEphemeralDevice =
        `e${Date.now()}${Math.random().toString(36).slice(2)}`.slice(0,32);
    }
    return window.__kirpaEphemeralDevice;
  }
}

// Ordered by discipline then seniority, fellow before consultant, paediatric
// before adult. Values are canonical English identifiers written to
// research_events and are never translated; only the labels localise.
// A learner may hold more than one (a fellow who is also a nurse educator, a
// consultant supervising in both nephrology and critical care), so this is
// multi-select and trainee_role is stored as an array.
const PRACTICE_ROLES = [
  "Paediatric Nephrology Fellow",
  "Paediatric Nephrology Consultant",
  "Paediatric Critical Care Fellow",
  "Paediatric Critical Care Consultant",
  "Adult Nephrology Fellow",
  "Adult Nephrology Consultant",
  "Adult Critical Care Fellow",
  "Adult Critical Care Consultant",
  "Nurse",
  "Nursing Student",
  "Resident Physician",
  "Medical Student",
  "Other",
];


// ─── ENTRY GATE ───────────────────────────────────────────────────────────────
// Role is required because training level is the single most important
// stratifying covariate in a competency study. Country is optional. No email,
// no name, no institution: those either come later or come from the educator.
function PracticeGate({onStart,busy}){
  const {t}=useLocale();
  const [roles,setRoles]=useState([]);
  const [country,setCountry]=useState("");
  const [err,setErr]=useState("");

  const toggle=(r)=>{ setErr(""); setRoles(p=>p.includes(r)?p.filter(x=>x!==r):[...p,r]); };

  const go=()=>{
    if(!roles.length){ setErr(t("prac.roleRequired","Please select at least one role to continue.")); return; }
    onStart({role:roles,country:country||null});
  };

  return <div style={{minHeight:"100vh",background:"linear-gradient(160deg,#1a0b2e 0%,#2d1b4e 100%)",
    display:"flex",alignItems:"center",justifyContent:"center",padding:"20px",fontFamily:FONT_UI}}>
    <div style={{background:"white",borderRadius:"18px",padding:"32px",maxWidth:"460px",width:"100%",
      boxShadow:"0 24px 60px rgba(0,0,0,0.4)"}}>
      <div style={{fontSize:"22px",fontWeight:"900",color:"#4f2683",letterSpacing:"-0.02em"}}>KiRPA</div>
      <h2 style={{margin:"6px 0 6px",fontSize:"18px",fontWeight:"700",color:C.dark}}>
        {t("prac.gateTitle","Before you begin")}
      </h2>
      <p style={{margin:"0 0 22px",fontSize:"13px",color:"#6b7280",lineHeight:"1.6"}}>
        {t("prac.gateBlurb","Two quick questions. Your answers are stored without any identifying information and help us understand how CRRT prescribing skill develops across training levels.")}
      </p>

      <div style={{marginBottom:"18px"}}>
        <label style={C.lbl}>
          {t("prac.yourRole","Your role")} <span style={{color:"#dc2626"}}>*</span>
          <span style={{fontWeight:"400",color:"#9ca3af"}}> — {t("prac.selectAll","select all that apply")}</span>
        </label>
        <div style={{display:"flex",flexDirection:"column",gap:"1px",marginTop:"6px",
          maxHeight:"228px",overflowY:"auto",border:`1px solid ${T.border}`,
          borderRadius:"9px",padding:"4px"}}>
          {PRACTICE_ROLES.map(r=>{
            const on=roles.includes(r);
            return <button key={r} onClick={()=>toggle(r)} type="button"
              style={{display:"flex",alignItems:"center",gap:"9px",padding:"8px 10px",
                borderRadius:"7px",border:"none",cursor:"pointer",textAlign:"start",
                background:on?T.accentLight:"transparent",fontFamily:"inherit",
                color:on?T.accentDark:T.text,fontSize:"12.5px",
                fontWeight:on?"650":"450",transition:"background 0.12s"}}>
              <span style={{width:"15px",height:"15px",borderRadius:"4px",flexShrink:0,
                border:`1.5px solid ${on?T.accent:"#cbd5e1"}`,background:on?T.accent:"white",
                display:"flex",alignItems:"center",justifyContent:"center",
                color:"white",fontSize:"10px",fontWeight:"800",lineHeight:1}}>
                {on?"✓":""}
              </span>
              {tRole(t,r)}
            </button>;
          })}
        </div>
      </div>

      <div style={{marginBottom:"20px"}}>
        <label style={C.lbl}>{t("prac.country","Country")} <span style={{color:"#9ca3af",fontWeight:"400"}}>({t("prac.optional","optional")})</span></label>
        {/* Free text rather than a fixed list: a closed dropdown silently
            excludes anyone whose country is missing from it, which for a
            platform aimed at 70+ countries is the wrong default. */}
        <input value={country} onChange={e=>setCountry(e.target.value)}
          placeholder={t("prac.countryPlaceholder","Leave blank if you would rather not say")}
          maxLength={56} style={C.inp}/>
      </div>

      {err&&<div style={{padding:"9px 12px",background:"#fef2f2",border:"1px solid #fecaca",
        borderRadius:"8px",color:"#dc2626",fontSize:"12px",marginBottom:"14px"}}>{err}</div>}

      <button onClick={go} disabled={busy} style={{...C.btn(C.pu),width:"100%",
        justifyContent:"center",padding:"12px",fontSize:"14px",opacity:busy?0.6:1}}>
        {busy?<Ic n="spin" s="w-4 h-4"/>:null} {t("prac.start","Start practising")}
      </button>

      <p style={{margin:"16px 0 0",fontSize:"11px",color:"#9ca3af",lineHeight:"1.6",textAlign:"center"}}>
        {t("prac.consent","De-identified responses are collected for educational research. No email or name is required.")}
      </p>
    </div>
  </div>;
}

// ─── EMAIL OFFER ──────────────────────────────────────────────────────────────
// Shown after a completed case, never before. Dismissible, and the backend
// stops serving it after three declines.
function PracticeEmailOffer({deviceId,onDone,onSkip}){
  const {t}=useLocale();
  const [email,setEmail]=useState("");
  const [busy,setBusy]=useState(false);
  const [err,setErr]=useState("");

  const [sent,setSent]=useState(false);
  const save=async()=>{
    if(!email.includes("@")){ setErr(t("prac.badEmail","Please enter a valid email address.")); return; }
    setBusy(true);setErr("");
    try{
      const fns=getFunctions(firebaseApp,"us-central1");
      // One email flow: sends a single magic link that both shows any
      // assignments waiting for this address AND confirms the device link.
      // Nothing is linked until the link is clicked, which is what stops a
      // mistyped address from merging one learner's attempts into another's.
      await httpsCallable(fns,"sendTraineePortalLink")({deviceId,email:email.trim().toLowerCase()});
      setSent(true);setBusy(false);
      onDone&&onDone(email.trim().toLowerCase());
    }catch(e){
      setErr(e?.message||t("prac.linkFailed","Unable to save. Please try again."));
      setBusy(false);
    }
  };
  // Declining is tracked in local state only — it just quiets the nudge, and
  // is not worth a round trip or a stored counter.
  const skip=()=>{ onSkip&&onSkip(); };

  return <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:240,
    display:"flex",alignItems:"center",justifyContent:"center",padding:"18px"}}>
    <div style={{background:"white",borderRadius:"16px",padding:"26px",maxWidth:"420px",width:"100%",
      fontFamily:FONT_UI,boxShadow:"0 24px 60px rgba(0,0,0,0.4)"}}>
      <h3 style={{margin:"0 0 8px",fontSize:"17px",fontWeight:"700",color:C.dark}}>
        {t("prac.saveTitle","Save your progress?")}
      </h3>
      <p style={{margin:"0 0 18px",fontSize:"13px",color:"#6b7280",lineHeight:"1.6"}}>
        {sent
          ? t("prac.saveSent","Check your inbox and open the link to finish. You can keep working meanwhile; anything you do now is included once you confirm.")
          : t("prac.saveBlurb","Add an email to combine your progress across devices and see any cases an educator has sent you. One link, and your work is already saved on this device either way.")}
      </p>
      <input value={email} onChange={e=>{setEmail(e.target.value);setErr("");}}
        placeholder="you@hospital.org" type="email" style={C.inp}/>
      {err&&<div style={{marginTop:"8px",fontSize:"12px",color:"#dc2626"}}>{err}</div>}
      <div style={{display:"flex",gap:"9px",marginTop:"18px"}}>
        <button onClick={skip} disabled={busy}
          style={{...C.btn("#f3f4f6","#374151"),flex:"0 0 auto"}}>
          {t("prac.notNow","Not now")}
        </button>
        <button onClick={sent?skip:save} disabled={busy}
          style={{...C.btn(C.pu),flex:1,justifyContent:"center",opacity:busy?0.6:1}}>
          {busy?<Ic n="spin" s="w-4 h-4"/>:null}
          {sent?t("prac.done","Done"):t("prac.saveProgress","Send link")}
        </button>
      </div>
      <p style={{margin:"14px 0 0",fontSize:"11px",color:"#9ca3af",textAlign:"center",lineHeight:"1.55"}}>
        {t("prac.emailPrivacy","Your email is stored as an irreversible hash. Educators never see your self-study history.")}
      </p>
    </div>
  </div>;
}




// ─── PATIENT MONITOR WALL ─────────────────────────────────────────────────────
// Practice case selection presented as a bank of bedside monitors rather than a
// list. Each tile is a patient: green trace while untouched, flatline STABLE once
// solved, red CRITICAL after an unsuccessful attempt. Carried over from the
// original CRRT simulator, which learners navigated far more readily than a list.
//
// Rooms are never labelled with their facet or tags — the bezel shows the room
// number only.
function PatientMonitorTile({label,state,attempts,onOpen,isNext,title,isActive}){
  const solved   = state === "solved";

  // The screen stays dark: it is a monitor. Only the bezel changes, which is
  // what a real bank of monitors does.
  //
  // Bezels are now dark metal rather than light plastic, because the wall
  // behind them is dark. Pale bezels on a dark wall read as cut-out cards; a
  // dark bezel with a lit edge reads as a mounted screen. The state colour
  // moves to the edge and the shadow, where it still scans instantly.
  const bezel = solved ? {bg:"linear-gradient(160deg,#16301f,#0d1d14)",border:"#22e57f66",
                          ring:"rgba(34,229,127,0.28)"}
              : isNext ? {bg:"linear-gradient(160deg,#241a3d,#150f26)",border:"#a78bfa88",
                          ring:"rgba(167,139,250,0.34)"}
              :          {bg:"linear-gradient(160deg,#1b1730,#110e21)",border:"rgba(255,255,255,0.10)",
                          ring:"rgba(255,255,255,0.05)"};

  const stableWave   = "M0 50 Q 20 70, 40 50 T 80 50 Q 90 20, 100 50 T 140 50";

  return <button onClick={onOpen} title={title||label}
    style={{display:"flex",flexDirection:"column",padding:"5px",cursor:"pointer",
      position:"relative",
      borderRadius:"12px",background:bezel.bg,border:`1px solid ${bezel.border}`,
      fontFamily:FONT_MONO,
      transition:"transform 0.18s cubic-bezier(0.22,1,0.36,1), box-shadow 0.18s",
      outline:isActive?`2px solid ${C.pu}`:"none",outlineOffset:"2px",
      // Lit top edge plus a coloured halo: mounted equipment rather than a card.
      boxShadow:`inset 0 1px 0 rgba(255,255,255,0.10), 0 0 0 1px rgba(0,0,0,0.4), 0 4px 14px ${bezel.ring}`}}
    onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-3px)";
      e.currentTarget.style.boxShadow=`inset 0 1px 0 rgba(255,255,255,0.14), 0 0 0 1px rgba(0,0,0,0.4), 0 8px 22px ${bezel.ring}`;}}
    onMouseLeave={e=>{e.currentTarget.style.transform="translateY(0)";
      e.currentTarget.style.boxShadow=`inset 0 1px 0 rgba(255,255,255,0.10), 0 0 0 1px rgba(0,0,0,0.4), 0 4px 14px ${bezel.ring}`;}}>

    {/* Status LED on the bezel, not the screen. Real monitors put the state
        indicator in the hardware, and it means the wall can be read at a
        glance even where a screen is busy. */}
    <span style={{position:"absolute",top:"9px",insetInlineStart:"10px",
      width:"5px",height:"5px",borderRadius:"50%",pointerEvents:"none",zIndex:2,
      background: solved ? "#22e57f" : state==="critical" ? "#ff5470" : "#4b4468",
      boxShadow: solved ? "0 0 6px #22e57f, 0 0 12px #22e57f66"
               : state==="critical" ? "0 0 6px #ff5470, 0 0 12px #ff547066"
               : "none"}}/>

    <div className="kirpa-well" style={{background:"#000",borderRadius:"7px",padding:"7px 5px",
      position:"relative",overflow:"hidden",minHeight:"74px",
      display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>

      {/* Faint scanline wash, same treatment as the in-case monitor */}
      <span style={{position:"absolute",inset:0,pointerEvents:"none",
        background:"repeating-linear-gradient(180deg,rgba(255,255,255,0.025) 0px,rgba(255,255,255,0.025) 1px,transparent 1px,transparent 3px)"}}/>

      {solved ? <>
        <svg viewBox="0 0 150 100" style={{width:"100%",height:"34px"}}>
          <path d={stableWave} stroke="#22c55e" strokeWidth="2" fill="none"/>
        </svg>
        <div style={{fontSize:"10px",fontWeight:"700",color:"#4ade80",letterSpacing:"0.12em"}}>
          COMPLETE
        </div>
      </> : <>
        <svg viewBox="0 0 140 100" style={{width:"100%",height:"34px"}}>
          <path d={stableWave} stroke="#f87171" strokeWidth="2" fill="none" className="kirpa-ekg"/>
        </svg>
        <div style={{fontSize:"8.5px",fontWeight:"700",color:"#f87171",
          letterSpacing:"0.09em",whiteSpace:"nowrap"}}>
          NOT ATTEMPTED
        </div>
      </>}

      {attempts>0 && !solved &&
        <div style={{position:"absolute",top:"5px",insetInlineEnd:"7px",fontSize:"9px",
          color:"#71717a",fontVariantNumeric:"tabular-nums"}}>×{attempts}</div>}
    </div>

    <div style={{padding:"6px 2px 2px",textAlign:"center",fontSize:"10.5px",
      fontWeight:"650",letterSpacing:"0.05em",
      color:solved?"#4ade80":isNext?"#c4b5fd":"#9992b8",
      overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
      {label}
    </div>
  </button>;
}

function PracticeMonitorWall({order,cases,completed,attemptsByCase,onOpen,nextId,numbered=true,activeId}){
  const {t}=useLocale();
  if(!order.length) return <div style={{padding:"34px 20px",textAlign:"center",
    color:T.muted,fontSize:"13px",background:"white",borderRadius:"12px",
    border:`1px solid ${T.border}`,fontFamily:FONT_UI}}>
    {t("prac.noCommunity","No community cases have been published yet.")}
  </div>;

  const solvedCount = order.filter(id=>completed.includes(id)).length;
  const pct = order.length ? Math.round(solvedCount/order.length*100) : 0;

  // Split into bays of six. Twenty-one identical tiles in one uniform grid have
  // no landmarks, so finding a particular room means reading every label. Bays
  // give the eye somewhere to anchor, the way a real unit is laid out.
  const BAY = 6;
  const bays = [];
  for(let i=0;i<order.length;i+=BAY) bays.push(order.slice(i,i+BAY));

  return <div style={{position:"relative"}}>
    <style>{`

      /* ── SURFACE TREATMENTS ──────────────────────────────────────────────
         Static, no animation. These change the material quality of the dark
         panels without anything for the learner to wait through.

         Scanlines are what make a dark rectangle read as a SCREEN rather than
         a shape. Grain kills the banding that gradients show on cheap phone
         panels and stops large dark areas looking like flat fill. Both sit at
         a few percent opacity: visible as texture, invisible as pattern. */
      .kirpa-scan::after {
        content:""; position:absolute; inset:0; pointer-events:none;
        background: repeating-linear-gradient(
          180deg,
          rgba(255,255,255,0.030) 0px,
          rgba(255,255,255,0.030) 1px,
          transparent 1px,
          transparent 3px);
        mix-blend-mode: overlay;
      }
      .kirpa-grain::before {
        content:""; position:absolute; inset:0; pointer-events:none; opacity:0.5;
        background-image:
          radial-gradient(circle at 17% 31%, rgba(255,255,255,0.028) 0 1px, transparent 1px),
          radial-gradient(circle at 63% 12%, rgba(255,255,255,0.022) 0 1px, transparent 1px),
          radial-gradient(circle at 39% 77%, rgba(255,255,255,0.026) 0 1px, transparent 1px),
          radial-gradient(circle at 88% 54%, rgba(255,255,255,0.020) 0 1px, transparent 1px);
        background-size: 47px 43px, 61px 59px, 53px 67px, 71px 41px;
      }
      /* Screen set INTO the bezel rather than printed on it. */
      .kirpa-well {
        box-shadow: inset 0 3px 8px rgba(0,0,0,0.75),
                    inset 0 -1px 0 rgba(255,255,255,0.05);
      }

      @keyframes kirpaEkg { from { stroke-dashoffset: 320; } to { stroke-dashoffset: 0; } }
      @keyframes kirpaNudge {
        0%,100% { box-shadow: 0 0 0 0 rgba(124,58,237,0.0); }
        50%     { box-shadow: 0 0 0 4px rgba(124,58,237,0.16); }
      }
      .kirpa-nudge { animation: kirpaNudge 1.6s ease-in-out 3; }
      .kirpa-ekg { stroke-dasharray: 320; animation: kirpaEkg 3.2s linear infinite; }
      .kirpa-ekg-fast { stroke-dasharray: 320; animation: kirpaEkg 1.1s linear infinite; }

      /* Staggered phase and rate per tile. In lockstep the wall reads as one
         animation copied twenty times; offset, it reads as independent
         patients. Nine variants is enough that the repeat is not visible. */
      .kirpa-wall .kirpa-ekg,
      .kirpa-wall .kirpa-ekg-fast { animation-delay: var(--d, 0s); }
      .kirpa-wall .kirpa-ekg { animation-duration: var(--r, 3.2s); }

      /* Full-bleed on a phone. Dark that stops short of the screen edge reads
         as a card; dark that runs to the edge reads as a device. */
      .kirpa-wall {
        background: radial-gradient(ellipse at 50% 0%, #17132b 0%, #0b0817 60%, #080612 100%);
        border-radius: 16px;
        padding: 16px;
      }
      @media (max-width: 700px) {
        .kirpa-wall {
          border-radius: 0;
          margin-left: calc(50% - 50vw);
          margin-right: calc(50% - 50vw);
          padding: 14px 12px 18px;
        }
      }
      @media (prefers-reduced-motion: reduce) {
        .kirpa-nudge, .kirpa-ekg, .kirpa-ekg-fast { animation: none; stroke-dasharray: none; }
      }
    `}</style>

    <div className="kirpa-wall kirpa-grain" style={{position:"relative"}}>

      {/* Header strip. The wall previously just began, with nothing to orient
          against; on a phone this is the first thing seen before scrolling. */}
      <div style={{display:"flex",alignItems:"center",gap:"11px",marginBottom:"15px",
        paddingBottom:"12px",borderBottom:"1px solid rgba(255,255,255,0.07)"}}>
        <span style={{width:"7px",height:"7px",borderRadius:"50%",background:"#22e57f",
          flexShrink:0,boxShadow:"0 0 8px #22e57f"}} className="mon-pulse"/>
        <span style={{fontSize:"10px",fontWeight:"700",color:"#a78bfa",fontFamily:FONT_MONO,
          textTransform:"uppercase",letterSpacing:"0.18em",whiteSpace:"nowrap"}}>
          {t("prac.monitoring","Patient Monitoring")}
        </span>

        <span style={{marginInlineStart:"auto",display:"flex",alignItems:"center",gap:"10px"}}>
          <span style={{fontSize:"11px",color:"#8b7fb0",fontFamily:FONT_MONO,
            fontVariantNumeric:"tabular-nums",whiteSpace:"nowrap"}}>
            {solvedCount}/{order.length}
          </span>
          {/* Thin progress arc, sized to sit beside the count without competing */}
          <svg width="30" height="30" viewBox="0 0 30 30" style={{flexShrink:0}}>
            <circle cx="15" cy="15" r="12" fill="none"
              stroke="rgba(255,255,255,0.09)" strokeWidth="3"/>
            <circle cx="15" cy="15" r="12" fill="none" stroke="#22e57f" strokeWidth="3"
              strokeLinecap="round" strokeDasharray={2*Math.PI*12}
              strokeDashoffset={2*Math.PI*12*(1-pct/100)}
              style={{transform:"rotate(-90deg)",transformOrigin:"50% 50%",
                transition:"stroke-dashoffset 0.8s cubic-bezier(0.22,1,0.36,1)"}}/>
          </svg>
        </span>
      </div>

      {bays.map((bay,bi)=>(
        <div key={bi} style={{marginBottom:bi<bays.length-1?"18px":0}}>
          {bays.length>1 && (
            <div style={{display:"flex",alignItems:"center",gap:"9px",marginBottom:"9px"}}>
              <span style={{fontSize:"8.5px",fontWeight:"700",color:"#6b5f90",
                fontFamily:FONT_MONO,textTransform:"uppercase",letterSpacing:"0.2em",
                whiteSpace:"nowrap"}}>
                {t("prac.bay","Bay")} {bi+1}
              </span>
              <span style={{flex:1,height:"1px",background:"rgba(255,255,255,0.06)"}}/>
            </div>
          )}
          <div style={{display:"grid",gap:"10px",
            gridTemplateColumns:"repeat(auto-fill,minmax(clamp(104px,26vw,132px),1fr))"}}>
            {bay.map((id,j)=>{
              const i = bi*BAY + j;
              const solved = completed.includes(id);
              const att    = attemptsByCase[id]||0;
              const state  = solved?"solved":att>0?"critical":"idle";
              // Glow colour follows state, so the pattern of solved against
              // unattempted is readable before any label is.
              const glow = solved ? "rgba(34,229,127,0.16)"
                         : att>0  ? "rgba(255,84,112,0.16)"
                         : "rgba(255,255,255,0.05)";
              return <div key={id} style={{position:"relative"}}>
                {/* Light bleeding onto the wall behind each screen */}
                <span style={{position:"absolute",inset:"-9px",borderRadius:"16px",
                  background:`radial-gradient(ellipse at 50% 50%, ${glow} 0%, transparent 72%)`,
                  filter:"blur(8px)",pointerEvents:"none"}}/>
                <div style={{position:"relative",
                  ["--d"]:`${(i%9)*0.37}s`, ["--r"]:`${3.0+(i%5)*0.28}s`}}>
                  <PatientMonitorTile
                    label={numbered ? `${t("prac.room","Room")} ${i+1}` : (cases[id]?.title||"Case")}
                    title={numbered ? undefined : cases[id]?.title}
                    state={state}
                    attempts={att} isNext={numbered && id===nextId}
                    isActive={id===activeId}
                    onOpen={()=>onOpen(id)}/>
                </div>
              </div>;
            })}
          </div>
        </div>
      ))}
    </div>
  </div>;
}

// ─── PERFORMANCE REVIEW ───────────────────────────────────────────────────────
function RadialScore({percentage,size=112}){
  const r=(size/2)-11, circ=2*Math.PI*r;
  const off=circ-(Math.max(0,Math.min(100,percentage))/100)*circ;
  const col = percentage<50 ? "#ef4444" : percentage<90 ? "#f59e0b" : "#22c55e";
  return <div style={{position:"relative",width:size,height:size}}>
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle stroke="#e5e7eb" strokeWidth="11" fill="transparent"
        r={r} cx={size/2} cy={size/2}/>
      <circle stroke={col} strokeWidth="11" strokeDasharray={circ} strokeDashoffset={off}
        strokeLinecap="round" fill="transparent" r={r} cx={size/2} cy={size/2}
        style={{transform:"rotate(-90deg)",transformOrigin:"50% 50%",
          transition:"stroke-dashoffset 0.9s ease"}}/>
    </svg>
    <span style={{position:"absolute",inset:0,display:"flex",alignItems:"center",
      justifyContent:"center",fontSize:"21px",fontWeight:"800",color:"#374151",
      fontVariantNumeric:"tabular-nums"}}>{percentage}%</span>
  </div>;
}

// Domain-specific guidance keyed to performance band. Educational content, not
// scoring: it points the learner at what to read next.
const DOMAIN_GUIDANCE = {
  Clearance: {
    low:"Review clearance principles. Focus on how effluent rate and patient BSA combine to meet clearance goals.",
    mid:"Good work. To advance, focus on adapting clearance for specific toxins such as ammonia or protein-bound drugs.",
    high:"Excellent performance. For a challenge, read about clearance in single-pass albumin dialysis (SPAD)." },
  Hemodynamics: {
    low:"Review causes of hypotension, especially during initiation and with high fluid removal rates.",
    mid:"You are managing basic haemodynamic challenges well. Focus on anticipating fluid shifts and anticoagulant effects.",
    high:"Strong handling of instability. Next, consider CRRT in patients on ECMO." },
  "Access, Circuit, Machine": {
    low:"Understand what each pressure alarm (access, return, filter) signifies about the circuit and the patient.",
    mid:"You troubleshoot common alarms effectively. Focus on differentiating patient from circuit problems.",
    high:"You diagnose and resolve common alarms quickly and reliably." },
  Anticoagulation: {
    low:"Review heparin and citrate: mechanisms, monitoring, and complications such as HIT and citrate lock.",
    mid:"You handle standard anticoagulation well. Focus on recognising and managing complications.",
    high:"Strong work. Consider reading on direct thrombin inhibitors in CRRT." },
  Electrolytes: {
    low:"Review management of severe electrolyte abnormalities and how to avoid iatrogenic shifts.",
    mid:"You manage electrolyte corrections well. Focus on the nuances of correcting severe dysnatraemias.",
    high:"Excellent management of complex disorders. Consider CRRT's role in inborn errors of metabolism." },
};
const guidanceFor=(domain,score)=>{
  const g=DOMAIN_GUIDANCE[domain];
  if(!g) return score<50?"Review the cases in this area and try them again."
        : score<90?"Solid work here. Revisit the cases you did not solve first time."
        : "Strong performance in this area.";
  return score<50?g.low:score<90?g.mid:g.high;
};
const overallGuidance=(score)=>
  score<50 ? "You are at the beginning of this journey. Focus on mastering one domain at a time; repetition builds confidence."
: score<90 ? "You have a solid foundation. Identify your lower-scoring domains and revisit those cases specifically."
: "Outstanding performance. Seek out real-world case discussions and advanced literature to keep honing your expertise.";







// ─── CASE SCENARIO FRAMING ────────────────────────────────────────────────────
// What the learner is actually being asked to do. Without this, "adjust the
// prescription" and "write one from scratch" look identical on screen, and a
// learner cannot tell whether the circuit in front of them is running, stopped,
// or does not exist yet. Each case declares one.


// Banner shown above the prescription form so the task is unambiguous.
function ScenarioBanner({caseData, compact}){
  const sc = scenarioFor(caseData);
  // The banner carries the single most important instruction on the screen:
  // whether the circuit is running, stopped, or does not exist yet. A soft
  // halo in the scenario's own colour makes it read as a live status rather
  // than another bordered box, and the icon gets a matching bloom so the
  // state is legible before the words are.
  return <div style={{position:"relative",display:"flex",gap:"10px",alignItems:"flex-start",
    padding:compact?"9px 12px":"11px 14px",borderRadius:"10px",
    background:sc.bg,border:`1px solid ${sc.border}`,
    boxShadow:`0 0 0 3px ${sc.tone}14, 0 2px 12px ${sc.tone}26`}}>
    <span style={{position:"absolute",inset:"-1px",borderRadius:"11px",pointerEvents:"none",
      background:`linear-gradient(180deg, ${sc.tone}0f 0%, transparent 60%)`}}/>
    <span style={{position:"relative",color:sc.tone,fontSize:"13px",flexShrink:0,
      lineHeight:1.4,textShadow:`0 0 10px ${sc.tone}80`}}>{sc.icon}</span>
    <span style={{position:"relative",minWidth:0}}>
      <span style={{display:"block",fontSize:"11px",fontWeight:"800",color:sc.tone,
        textTransform:"uppercase",letterSpacing:"0.09em",marginBottom:"3px",
        textShadow:`0 0 12px ${sc.tone}4d`}}>
        {sc.label}
      </span>
      <span style={{display:"block",fontSize:compact?"12px":"12.5px",color:"#3f3f46",
        lineHeight:"1.55"}}>{sc.line}</span>
    </span>
  </div>;
}




// ─── FRAME CAPTURE ────────────────────────────────────────────────────────────
// Debriefing works by surfacing the FRAME: the mental model that produced the
// action. Outcome feedback alone does not do that, which is why a result screen
// is feedback rather than debriefing.
//
// Asking what the learner thinks the problem is, immediately BEFORE the outcome
// is revealed, captures the frame while it is still theirs. Showing it back
// afterwards is the closest a solo asynchronous tool gets to advocacy-inquiry.
//
// Skippable by design. A required reflection step becomes a box to click
// through, and the data it produces is worse than no data.
const PROBLEM_TYPES = [
  { id:"dose_low",     label:"Not enough clearance" },
  { id:"dose_high",    label:"Too much clearance" },
  { id:"fluid",        label:"Fluid removal rate" },
  { id:"anticoag",     label:"Anticoagulation" },
  { id:"circuit",      label:"Access or circuit" },
  { id:"solution",     label:"Solution composition" },
  { id:"reaction",     label:"A reaction to something in the circuit" },
  { id:"patient",      label:"Patient factors, not the prescription" },
  { id:"unsure",       label:"Not sure" },
];

function FrameCapture({caseData, value, onChange, onSkip, onConfirm}){
  const {t}=useLocale();
  // A blank-prescription case has no presenting problem to identify, so the
  // question would be nonsense. Only the reasoning line is asked.
  const isNew = (caseData?.scenario || "") === "new";
  const chosen = value?.problem || "";

  // A modal rather than a card under the button. Inline, it competed with the
  // prescription form for attention at the moment the learner had just
  // finished with it; as a modal it is unmissable and it is over in one step.
  // Both buttons submit: asking someone to press Submit twice makes the
  // reflection feel like an obstacle rather than part of the task.
  return <div style={{position:"fixed",inset:0,zIndex:240,
    background:"rgba(15,23,42,0.55)",display:"flex",alignItems:"center",
    justifyContent:"center",padding:"18px",fontFamily:T.font,overflowY:"auto"}}>
  <div onClick={e=>e.stopPropagation()} style={{background:"white",borderRadius:"16px",
    border:`1px solid ${T.accentMid}`,padding:"20px 22px",width:"100%",
    maxWidth:"480px",maxHeight:"88vh",overflowY:"auto",
    boxShadow:"0 24px 60px rgba(0,0,0,0.35)"}}>
    <div style={{fontSize:"13.5px",fontWeight:"750",color:T.text,marginBottom:"3px"}}>
      {t("frame.title","Before you submit")}
    </div>
    <div style={{fontSize:"11.5px",color:T.muted,marginBottom:"13px",lineHeight:"1.55"}}>
      {t("frame.sub","Thirty seconds. You will see this again with the result.")}
    </div>

    {!isNew && <>
      <div style={{fontSize:"12px",fontWeight:"650",color:T.text,marginBottom:"8px"}}>
        {t("frame.q1","What do you think the main problem is?")}
      </div>
      <div style={{display:"grid",gap:"5px",marginBottom:"14px",
        gridTemplateColumns:"repeat(auto-fit,minmax(178px,1fr))"}}>
        {PROBLEM_TYPES.map(p=>{
          const on = chosen===p.id;
          return <button key={p.id}
            onClick={()=>onChange({...value, problem:on?"":p.id})}
            style={{padding:"8px 11px",borderRadius:"8px",cursor:"pointer",
              textAlign:"start",fontFamily:"inherit",fontSize:"12px",
              fontWeight:on?"650":"450",lineHeight:"1.35",
              border:`1px solid ${on?T.accent:T.border}`,
              background:on?T.accentLight:"white",
              color:on?T.accentDark:T.text}}>
            {t("problem."+p.id, p.label)}
          </button>;
        })}
      </div>
    </>}

    <div style={{fontSize:"12px",fontWeight:"650",color:T.text,marginBottom:"7px"}}>
      {isNew ? t("frame.q2new","What is your reasoning for this prescription?")
             : t("frame.q2","What are you changing, and why?")}
    </div>
    <textarea value={value?.why||""}
      onChange={e=>onChange({...value, why:e.target.value})}
      placeholder={t("frame.placeholder","One line is plenty. Optional.")}
      style={{...C.inp,minHeight:"62px",resize:"vertical",lineHeight:"1.6",
        marginBottom:"13px"}}/>

    <div style={{fontSize:"12px",fontWeight:"650",color:T.text,marginBottom:"7px"}}>
      {t("frame.q3","How confident are you?")}
    </div>
    {/* Three points, not a slider. Confident-and-wrong is a distinct failure
        mode worth measuring, and a slider invites false precision. */}
    <div style={{display:"flex",gap:"6px",marginBottom:"15px"}}>
      {[["low","Not very"],["mid","Fairly"],["high","Confident"]].map(([k,lbl])=>{
        const on=(value?.confidence||"")===k;
        return <button key={k} onClick={()=>onChange({...value, confidence:on?"":k})}
          style={{flex:1,padding:"8px 10px",borderRadius:"8px",cursor:"pointer",
            fontFamily:"inherit",fontSize:"12px",fontWeight:on?"700":"500",
            border:`1px solid ${on?T.accent:T.border}`,
            background:on?T.accentLight:"white",color:on?T.accentDark:T.text}}>
          {t("conf."+k, lbl)}
        </button>;
      })}
    </div>

    <div style={{display:"flex",gap:"9px",alignItems:"center"}}>
      <button onClick={onConfirm} style={{...C.btn(C.pu),flex:1,
        justifyContent:"center",padding:"12px"}}>
        {t("frame.submit","Submit prescription \u2197")}
      </button>
      <button onClick={onSkip}
        style={{padding:"12px 16px",borderRadius:T.radiusSm,border:"none",
          background:"none",color:T.muted,fontSize:"12.5px",fontWeight:"600",
          cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>
        {t("frame.skip","Skip and submit")}
      </button>
    </div>
  </div>
  </div>;
}

// Shown in the result, beside what actually happened. Seeing your own words
// next to the outcome is what turns a verdict into a comparison.
function FrameRecall({frame, caseData}){
  const {t}=useLocale();
  if(!frame || (!frame.problem && !frame.why)) return null;
  const label = PROBLEM_TYPES.find(p=>p.id===frame.problem)?.label;
  return <div style={{padding:"13px 15px",borderRadius:"10px",marginBottom:"16px",
    background:"#fdfaf6",border:"1px solid #ece3d9"}}>
    <div style={{fontSize:"10px",fontWeight:"700",color:"#8a6d52",
      textTransform:"uppercase",letterSpacing:"0.12em",marginBottom:"7px"}}>
      {t("frame.recall","What you said before you submitted")}
    </div>
    {label && (
      <div style={{fontSize:"13px",color:"#33261f",marginBottom:frame.why?"7px":0,
        lineHeight:"1.6"}}>
        {t("frame.youThought","You identified this as:")}{" "}
        <strong>{t("problem."+frame.problem, label)}</strong>
      </div>
    )}
    {frame.why && (
      <div style={{fontSize:"13px",color:"#33261f",lineHeight:"1.65",
        fontFamily:FONT_CLIN}}>
        &ldquo;{frame.why}&rdquo;
      </div>
    )}
    {frame.confidence && (
      <div style={{fontSize:"11px",color:"#8a6d52",marginTop:"7px"}}>
        {t("frame.conf","Confidence:")}{" "}
        {t("conf."+frame.confidence,
           frame.confidence==="high"?"Confident":frame.confidence==="mid"?"Fairly":"Not very")}
      </div>
    )}
  </div>;
}


// ─── GOING FURTHER ────────────────────────────────────────────────────────────
// Two questions per case, shown after the result. They extend the scenario
// rather than testing it: what if the first change had not worked, what if the
// equipment were different, what changes in a smaller patient.
//
// Deliberately NOT graded and not answered in the app. The point is to leave a
// learner with something to take to a colleague or a reading list, which is
// what the discussion phase of a debrief does. Answering them here would make
// it another quiz.
//
// `resource` is a free field for a link or citation. Most are left blank: they
// should be filled with references you have actually read and endorse, not
// with plausible-looking ones.
const CASE_EXTENSIONS = {
 Room1: [
  {q:"You have no 9F catheter and the only line available is an 8F. What changes in the rest of your prescription, and what do you accept losing?"},
  {q:"The same child is now 20% fluid overloaded rather than 12%. Does your net ultrafiltration change, and what limits how fast you can go?"}],
 Room2: [
  {q:"You raise the dose and the potassium still does not fall. What are the possibilities, and how would you distinguish them?"},
  {q:"What are the potential advantages and drawbacks of the different anticoagulation strategies in tumour lysis syndrome?",
   resource:{label:"KDIGO AKI Guideline 2012, section on RRT dosing", url:""}}],
 Room3: [
  {q:"You prime with blood and the pressure still drops on connection. What else about the initiation could be responsible?"}],
 Room4: [
  {q:"Albumin dialysis is not available at your centre. What else could you offer, and what would you tell the family about the difference?"},
  {q:"How would your answer change for a drug that is dialysable but has a large volume of distribution rather than high protein binding?"}],
 Room5: [
  {q:"You stop the heparin and switch to citrate. Two days later the circuits are clotting again. What are you now considering?"},
  {q:"The platelet count is very low and the patient is bleeding. Does that change which alternative you choose?"}],
 Room6: [
  {q:"The ammonia falls rapidly over four hours and the child becomes more encephalopathic, not less. What has happened?"},
  {q:"You cannot achieve the dose you want with the catheter in place. What are your options, in what order?"}],
 Room7: [
  {q:"You lower the blood flow and the bicarbonate keeps climbing. What else could be contributing?"},
  {q:"The patient has liver dysfunction as well. Does that change which correction you reach for first?"}],
 Room8: [
  {q:"You reduce the blood flow and the alarms continue. What is your next step, and what would make you replace the line?"}],
 Room9: [
  {q:"You stop the citrate. What anticoagulation do you use instead in a patient with liver failure and a raised INR?"},
  {q:"How would you monitor for citrate accumulation prospectively rather than waiting for the calcium requirement to climb?"}],
 Room10: [
  {q:"You slow the fluid removal and the pressure does not recover. What else about starting CRRT could be responsible?"}],
 Room11: [
  {q:"The child seizes again despite the change. What now?"},
  {q:"How would you have set the initial dose to avoid this, and what does that cost in salicylate clearance?"}],
 Room12: [
  {q:"You trace the circuit and find nothing. Where else does a high return pressure come from?"},
  {q:"The alarm recurs only when the patient is turned. What does that suggest, and what would you do?"}],
 Room13: [
  {q:"You stop the epoprostenol and the circuit clots within two hours. What are your options in a patient who is thrombocytopenic?"},
  {q:"How would you distinguish this from an early anaphylactic reaction at the bedside, before any test comes back?"}],
 Room14: [
  {q:"You add potassium and phosphate to the bags. Two days later both are high. What has changed?"},
  {q:"Your centre stocks only potassium-free solutions. How do you run a week of CRRT safely?"}],
 Room15: [
  {q:"You reduce the citrate load and the sodium keeps rising. What else could be doing it?"}],
 Room16: [
  {q:"Twelve hours in, the sodium has fallen faster than you intended. What do you do?"},
  {q:"Your pharmacy is unable to adjust the sodium content of the CRRT solutions. What other strategies can you use to control the outlet sodium?"}],
 Room17: [
  {q:"The sodium rises 9 mmol/L in the first 12 hours despite your adjustment. What are the options now?"},
  {q:"Your pharmacy is unable to adjust the water content of the CRRT solutions. What other strategies can you use to control the outlet sodium?"}],
 Room18: [
  {q:"Sedation is increased and the alarms continue. What does that tell you?"},
  {q:"What are the risks of sedating specifically to keep a circuit running, and how do you weigh them?"}],
 Room19: [
  {q:"What are the site and size trade-offs for vascular access in a 4kg infant?"}],
 Room20: [
  {q:"This is the third circuit in 24 hours. What do you change beyond replacing it again?"},
  {q:"How would you decide between a filter change and stopping CRRT altogether in a patient who is improving?"}],
 Room21: [
  {q:"The acidosis cannot be corrected quickly enough. What else would let you start?"},
  {q:"Which patients are most at risk of this reaction, and how would you plan a circuit for them in advance?"}],
 Room22: [
  {q:"You lower the filtration fraction and the filter still clots in 12 hours. What is left to look at?"},
  {q:"Your machine has no pre-blood pump. How else would you reduce the filtration fraction?"}],
};


// Shown after the result. Collapsed by default: a learner who wants to move on
// should not have to scroll past two paragraphs to reach the next case.
function GoingFurther({caseId, caseData}){
  const {t}=useLocale();
  const [open,setOpen]=useState(false);
  // A case may carry its own questions, which is how a community or educator
  // case gets them: the built-in table only knows the 22 default ids. Author
  // questions win where both exist.
  const own = Array.isArray(caseData?.extensions) ? caseData.extensions : null;
  const qs = (own && own.length) ? own : (CASE_EXTENSIONS[caseId] || []);
  if(!qs.length) return null;

  return <div style={{background:"white",borderRadius:"10px",
    border:`1px solid ${T.border}`,marginBottom:"16px",overflow:"hidden"}}>
    <button onClick={()=>setOpen(v=>!v)}
      style={{width:"100%",padding:"12px 15px",background:"none",border:"none",
        cursor:"pointer",fontFamily:"inherit",textAlign:"start",
        display:"flex",alignItems:"center",gap:"10px"}}>
      <span style={{fontSize:"14px"}}>&#9997;</span>
      <span style={{flex:1,minWidth:0}}>
        <span style={{display:"block",fontSize:"13px",fontWeight:"700",color:T.text}}>
          {t("ext.title","Going further")}
        </span>
        <span style={{display:"block",fontSize:"11.5px",color:T.muted,marginTop:"1px"}}>
          {t("ext.sub","Two questions to take away. Nothing to answer here.")}
        </span>
      </span>
      <span style={{color:T.muted,fontSize:"11px"}}>{open?"\u25be":"\u25b8"}</span>
    </button>

    {open && <div style={{padding:"0 15px 15px",display:"flex",
      flexDirection:"column",gap:"10px"}}>
      {qs.map((x,i)=>(
        <div key={i} style={{padding:"12px 14px",borderRadius:"9px",
          background:"#faf8ff",border:"1px solid #e4dcfb"}}>
          <div style={{fontSize:"13px",color:T.text,lineHeight:"1.65"}}>{x.q}</div>
          {x.resource?.label && (
            <div style={{marginTop:"8px",fontSize:"11.5px",color:T.muted}}>
              {x.resource.url
                ? <a href={x.resource.url} target="_blank" rel="noopener noreferrer"
                    style={{color:T.accent,fontWeight:"600"}}>{x.resource.label}</a>
                : <span>{x.resource.label}</span>}
            </div>
          )}
        </div>
      ))}
    </div>}
  </div>;
}

// ─── CONSULTS ─────────────────────────────────────────────────────────────────
// Graded hints, in the spirit of phoning a colleague. Three tiers per case:
// the first narrows where to look, the second names the mechanism, the third
// points at the action without stating the numbers.
//
// Every consult is recorded. Attempts that used one are analysed as a SEPARATE
// group from unaided attempts rather than being pooled: a case solved after
// three hints is not evidence of the same competence as one solved cold, and
// mixing them would inflate apparent performance.
const CASE_CONSULTS = {
  Room1:  [
           "Consider the main indications here: adequate clearance and fluid removal, using circuit components sized to the patient.",
           "Use an effluent dose of at least 25 ml/kg/hr, or 2 L/1.73m\u00b2/hr, and target fluid removal of 1 to 2 ml/kg/hr."],
  Room2:  ["The modality is not the problem. Look at what the prescription is actually delivering.",
           "The effluent dose is too low for this potassium. Raise it using dialysate or replacement, or both, and do not add potassium to the solution while trying to clear it."],
  Room3:  ["This happened the instant blood reached the filter, which points to volume rather than dose.",
           "The circuit borrows blood from the patient, and in a child this size that is a large share of their total. Check the circuit-to-blood-volume ratio in the calculations panel and choose a prime that replaces what the circuit takes."],
  Room4:  ["The dose is adequate yet the level is not moving. Ask what property of this drug would explain that.",
           "This drug is heavily protein bound, so it stays in the plasma no matter how high the dose. Giving it something to bind to on the far side of the membrane is what clears it."],
  Room5:  ["Look at what has changed in the bloods alongside the repeated clotting.",
           "A falling platelet count with circuit clotting on this anticoagulant suggests heparin-induced thrombocytopenia. Stop the heparin; which alternative you choose matters less."],
  Room6:  ["Compare the dose being delivered with what this particular indication requires.",
           "Ammonia is the one indication where a standard dose is clearly inadequate. Aim several times higher than usual, and note the weight-based and surface-area targets diverge at this size."],
  Room7:  ["The gas picture and the anticoagulant are related.",
           "Citrate is metabolised to bicarbonate and the load rises with blood flow. Reduce the citrate load or remove more bicarbonate: lower the blood flow, raise the clearance, change to a lower-bicarbonate solution, or pause citrate and calcium."],
  Room8:  ["Read the access pressure and ask what the pump is trying to do.",
           "The pump is asking for more blood than the catheter can deliver. Bring the blood flow down to what this access can sustain."],
  Room9:  ["The rising calcium requirement and the liver enzymes are connected.",
           "A widening total-to-ionised calcium gap with impaired liver function indicates citrate accumulation. Reduce the citrate load or clear more of it: stop citrate, pause citrate and calcium, raise the clearance, or lower the blood flow."],
  Room10:  ["The timing relative to starting CRRT matters.",
           "Fluid is being removed faster than the interstitium can refill the intravascular space. Slow the net removal to a rate this patient tolerates continuously, rather than stopping it."],
  Room11:  ["Look at how far the urea has fallen and how quickly.",
           "Clearing urea faster than the brain equilibrates causes dialysis disequilibrium. Slow the clearance and give an osmotic agent; slowing alone will not reverse the gradient already present."],
  Room12:  ["Not every alarm is a prescription problem.",
           "A return pressure alarm with no kink at the pump points to something mechanical downstream. Trace the circuit physically and check its connections before touching any rate."],
  Room13:  ["Look at what is running for anticoagulation and what its side effects are.",
           "Epoprostenol is a potent vasodilator with dose-dependent effects, which fits flushing, tachycardia and hypotension in the absence of rash, wheeze or fever. Reduce the dose or stop it."],
  Room14:  ["Check which electrolytes have moved since yesterday and what the solution contains.",
           "CRRT removes potassium and phosphate continuously, and this solution replaces neither. Both need replacing, either into the bags or as a supplement to the patient."],
  Room15:  ["The sodium and the magnesium have the same explanation.",
           "Citrate solutions are sodium-rich and citrate chelates magnesium, so both findings follow from the citrate load. Reduce it or clear more of it rather than chasing the magnesium."],
  Room16:  ["Compare the patient's sodium with what the solution contains.",
           "Running standard solution against a much higher patient sodium corrects far faster than is safe. Add sodium to the solution so its concentration sits close to the patient's: the amount is the difference multiplied by the bag volume in litres."],
  Room17:  ["Compare the patient's sodium with what the standard solution contains.",
           "Standard solution will raise this sodium faster than the usual daily limit. Dilute it with free water so its sodium sits closer to the patient's: the volume is the bag volume times the sodium difference, divided by the target."],
  Room18:  ["Note when the alarms happen and when they settle.",
           "The catheter is positioned correctly and pressures are fine when the patient is still, so this is movement rather than prescription. Sedation is the effective answer."],
  Room19:  ["The machine cannot sustain the flow that has been set. Ask why.",
           "Catheter size sets the ceiling on achievable blood flow, and this line is too small for the patient. Reducing the flow to match makes the dose inadequate, so change the catheter."],
  Room20:  ["Look at how fast the transmembrane pressure has risen and what flushing achieved.",
           "The filter has clotted and will not recover. Change the entire circuit; adjusting anticoagulation now addresses the next one, not this one."],
  Room21:  ["The reaction happened within a minute of connection, which narrows the cause considerably.",
           "This membrane can release bradykinin and the reaction is potentiated by acidaemia. Correct the acidosis before reconnecting."],
  Room22:  ["Anticoagulation has been changed three times without success. Look elsewhere.",
           "The filtration fraction is too high, so blood is being concentrated inside the fibres and no anticoagulant compensates. Bring it down while still removing fluid: raise the blood flow, move replacement pre-filter, or shift dose to dialysate."],
};

// Consult modal. Opened from beside the submit button, because that is where a
// learner decides whether they can answer. Hints unlock one at a time so the
// second cannot be taken without reading the first.
//
// The panel says nothing about how consults are recorded or how they affect
// scoring. Telling a learner mid-case that asking for help will be counted
// changes the behaviour being measured, and makes the honest choice feel
// penalised.
function ConsultModal({caseId, used, onUse, onClose}){
  const {t}=useLocale();
  const hints = CASE_CONSULTS[caseId] || [];
  const TIER = [
    t("consult.tier1","A nudge"),
    t("consult.tier2","More detail"),
  ];
  if(!hints.length) return null;

  return <div style={{position:"fixed",inset:0,zIndex:250,background:"rgba(15,23,42,0.6)",
    display:"flex",alignItems:"center",justifyContent:"center",padding:"18px",
    fontFamily:T.font}} onClick={onClose}>
    <div onClick={e=>e.stopPropagation()} style={{background:"white",borderRadius:"16px",
      width:"100%",maxWidth:"440px",maxHeight:"86vh",overflowY:"auto",
      boxShadow:"0 24px 60px rgba(0,0,0,0.35)"}}>

      <div style={{padding:"18px 20px 14px",borderBottom:`1px solid ${T.border}`,
        display:"flex",alignItems:"center",gap:"11px"}}>
        <span style={{fontSize:"19px"}}>☎</span>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:"15.5px",fontWeight:"750",color:T.text}}>
            {t("consult.title","Request a consult")}
          </div>
          <div style={{fontSize:"11.5px",color:T.muted,marginTop:"2px"}}>
            {t("consult.sub","Ask a colleague to look at this case with you")}
          </div>
        </div>
        <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",
          color:T.muted,fontSize:"20px",lineHeight:1,padding:"0 2px"}}>×</button>
      </div>

      <div style={{padding:"15px 20px 20px",display:"flex",flexDirection:"column",gap:"10px"}}>
        {hints.map((h,i)=>{
          const revealed = i < used;
          const nextUp   = i === used;
          if(!revealed && !nextUp) return (
            <div key={i} style={{padding:"13px 15px",borderRadius:"10px",
              background:"#fafafa",border:`1px dashed ${T.border}`,
              fontSize:"12.5px",color:"#c4c4c8"}}>
              {TIER[i]||`Hint ${i+1}`}
            </div>
          );
          return <div key={i} style={{padding:"13px 15px",borderRadius:"10px",
            background:revealed?"#f8f7ff":"white",
            border:`1px solid ${revealed?"#e4dcfb":T.border}`}}>
            <div style={{fontSize:"9.5px",fontWeight:"700",color:T.muted,
              textTransform:"uppercase",letterSpacing:"0.13em",marginBottom:"6px"}}>
              {TIER[i]||`Hint ${i+1}`}
            </div>
            {revealed
              ? <div style={{fontSize:"13px",color:T.text,lineHeight:"1.65"}}>{h}</div>
              : <button onClick={()=>onUse(i+1)}
                  style={{padding:"9px 16px",borderRadius:"9px",cursor:"pointer",
                    border:"none",background:T.accent,color:"white",
                    fontSize:"12.5px",fontWeight:"700",fontFamily:"inherit"}}>
                  {i===0 ? t("consult.ask","Ask") : t("consult.more","Ask for more")}
                </button>}
          </div>;
        })}

        <button onClick={onClose}
          style={{marginTop:"4px",padding:"10px",borderRadius:"9px",
            border:`1px solid ${T.border}`,background:"white",color:T.muted,
            fontSize:"12.5px",fontWeight:"650",cursor:"pointer",fontFamily:"inherit"}}>
          {used>0 ? t("consult.back","Back to the case") : t("consult.never","Never mind")}
        </button>
      </div>
    </div>
  </div>;
}

// ─── RATE STEPPER ─────────────────────────────────────────────────────────────
// Replaces <input type="number"> for the prescription rates. A CRRT machine has
// chunky steppers with a large readout, not a browser spinner, and this is
// where a learner spends most of their time. Also far better on touch, which
// matters for the tablet-at-the-bedside case.
//
// The text input is kept behind the readout so a value can still be typed
// directly; the steppers are an addition, not a replacement.
function RateStepper({value, onChange, step=10, min=0, max=9999, unit, disabled}){
  const v = value === "" || value == null ? "" : value;
  const bump = (d)=>{
    const cur = parseFloat(v);
    const base = isFinite(cur) ? cur : 0;
    const next = Math.max(min, Math.min(max, +(base + d*step).toFixed(3)));
    onChange(String(next));
  };
  const Btn = ({d,label}) => (
    <button type="button" onClick={()=>bump(d)} disabled={disabled}
      aria-label={d>0?"increase":"decrease"}
      style={{width:"38px",minHeight:"38px",flexShrink:0,borderRadius:"9px",
        border:"1px solid #d9cdf5",background:"white",cursor:disabled?"default":"pointer",
        color:"#5b21b6",fontSize:"18px",fontWeight:"700",lineHeight:1,
        fontFamily:"inherit",opacity:disabled?0.4:1,
        display:"flex",alignItems:"center",justifyContent:"center",
        boxShadow:"0 1px 2px rgba(15,23,42,0.06)",
        touchAction:"manipulation"}}>
      {label}
    </button>
  );
  return <div style={{display:"flex",alignItems:"stretch",gap:"7px"}}>
    <Btn d={-1} label="−"/>
    <div style={{flex:1,minWidth:0,position:"relative"}}>
      <input type="number" inputMode="decimal" value={v} disabled={disabled}
        min={min} max={max}
        onChange={e=>{
          const raw = e.target.value;
          if(raw === ""){ onChange(""); return; }
          const n = parseFloat(raw);
          if(!isFinite(n)){ onChange(""); return; }
          // Clamp on entry rather than on blur, so an out-of-range value never
          // sits in the field looking accepted.
          onChange(String(Math.max(min, Math.min(max, n))));
        }}
        style={{width:"100%",height:"100%",minHeight:"38px",textAlign:"center",
          borderRadius:"9px",border:"1px solid #e4dcfb",background:"#faf8ff",
          fontFamily:FONT_MONO,fontSize:"17px",fontWeight:"700",color:"#3d2a63",
          fontVariantNumeric:"tabular-nums",padding:unit?"0 30px 0 8px":"0 8px",
          outline:"none",MozAppearance:"textfield"}}/>
      {unit && <span style={{position:"absolute",insetInlineEnd:"9px",top:"50%",
        transform:"translateY(-50%)",fontSize:"9.5px",color:"#a99cc4",
        fontFamily:FONT_MONO,pointerEvents:"none"}}>{unit}</span>}
    </div>
    <Btn d={1} label="+"/>
  </div>;
}

// ─── PRESSURE GAUGE ───────────────────────────────────────────────────────────
// Domain scores drawn as a CRRT circuit pressure gauge: vertical column,
// 100% at the top, 0% at the bottom, scale ticks down the side and a needle at
// the reading. Borrowing the machine's own visual language means a nephrology
// learner reads these without being taught what they are.
function PressureGauge({pct, color, label, height=150, width=58}){
  const p = Math.max(0, Math.min(100, pct));
  const pad = 22;                         // reading sits in its own band on top
  const track = height - pad - 8;
  const fillH = (p/100) * track;

  // Brighter fills than the band colours used elsewhere. On a dark instrument
  // face the muted print colours read as grey, so each band gets a luminous
  // equivalent plus a glow behind the whole gauge.
  const bright = p>=85 ? "#22e57f" : p>=50 ? "#ffb020" : "#ff4d6a";

  return <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:"7px"}}>
    <div style={{position:"relative"}}>
      {/* Ambient glow behind the gauge, coloured by band. Sits outside the
          clipped face so it spills softly rather than stopping at the edge. */}
      <span style={{position:"absolute",inset:"-10px",borderRadius:"16px",
        background:`radial-gradient(ellipse at 50% ${100-p}%, ${bright}3d 0%, transparent 70%)`,
        filter:"blur(7px)",pointerEvents:"none",
        transition:"background 0.9s ease"}}/>

      <div style={{position:"relative",width:`${width}px`,height:`${height}px`,
        borderRadius:"9px",background:"linear-gradient(180deg,#1c1636,#0a0716)",
        border:`1px solid ${bright}33`,overflow:"hidden",
        boxShadow:`inset 0 2px 8px rgba(0,0,0,0.7), 0 0 16px ${bright}22`}}>

        {/* Reading, in its own band at the top so the column never covers it */}
        <span style={{position:"absolute",top:0,left:0,right:0,height:`${pad}px`,
          display:"flex",alignItems:"center",justifyContent:"center",
          background:"rgba(0,0,0,0.45)",borderBottom:`1px solid ${bright}26`,
          fontSize:"14px",fontWeight:"800",fontFamily:FONT_MONO,color:bright,
          textShadow:`0 0 10px ${bright}99`,letterSpacing:"-0.02em",
          fontVariantNumeric:"tabular-nums"}}>
          {p}%
        </span>

        {/* Scale ticks, 0 at the bottom like a real pressure column */}
        {[0,25,50,75,100].map(v=>(
          <span key={v} style={{position:"absolute",left:0,right:0,
            bottom:`${(v/100)*track+4}px`,height:"1px",
            background:v===50?"rgba(255,255,255,0.22)":"rgba(255,255,255,0.10)"}}/>
        ))}

        {/* Column fill */}
        <span style={{position:"absolute",left:"6px",right:"6px",bottom:"4px",
          height:`${fillH}px`,borderRadius:"5px",
          background:`linear-gradient(180deg, ${bright} 0%, ${bright}d9 60%, ${bright}99 100%)`,
          boxShadow:`0 0 16px ${bright}aa, inset 0 1px 0 rgba(255,255,255,0.45)`,
          transition:"height 0.9s cubic-bezier(0.22,1,0.36,1)"}}/>

        {/* Needle at the reading */}
        <span style={{position:"absolute",left:"2px",right:"2px",
          bottom:`${fillH+3}px`,height:"2px",background:"#fff",
          boxShadow:`0 0 8px #fff, 0 0 14px ${bright}`,
          transition:"bottom 0.9s cubic-bezier(0.22,1,0.36,1)"}}/>
      </div>
    </div>

    {label && <div style={{fontSize:"9px",color:T.muted,fontFamily:FONT_MONO,
      textTransform:"uppercase",letterSpacing:"0.08em",textAlign:"center",
      maxWidth:`${width+30}px`,lineHeight:"1.3"}}>{label}</div>}
  </div>;
}

// ─── SVG EXPORT ───────────────────────────────────────────────────────────────
// Builds a standalone SVG from the DATA rather than scraping the DOM, so the
// exported file is self-contained, has no external fonts or styles, and stays
// legible when opened years later or dropped into a slide deck.
function buildReportSVG({overallPct, overallPassed, overallAttempted, domains,
                         domainKeys, cohort, tagsByDomain, topics, labelFor, dateStr}){
  const esc = (x)=>String(x??"").replace(/[<>&]/g, c=>({"<":"&lt;",">":"&gt;","&":"&amp;"}[c]));
  const band = (p)=> p>=85 ? "#047857" : p>=50 ? "#b45309" : "#be123c";

  const W = 780;
  const rowH = 30, headH = 150, blockGap = 18;
  let bodyH = 0;
  domainKeys.forEach(k=>{ bodyH += 54 + (tagsByDomain[k]?.length||0)*rowH + blockGap; });
  const topicList = topics || [];
  const topicH = topicList.length ? 34 + topicList.length*rowH + blockGap : 0;
  const H = headH + bodyH + topicH + 46;

  let y = headH;
  const parts = [];

  domainKeys.forEach(k=>{
    const d = domains[k];
    const p = d.attempted ? Math.round(d.passed/d.attempted*100) : 0;
    const co = cohort?.[k];
    const z  = (co && co.sd>0) ? ((p - co.mean)/co.sd) : null;

    parts.push(`<text x="40" y="${y}" font-size="14" font-weight="700" fill="#2e1f4d">${esc(labelFor(k))}</text>`);
    parts.push(`<text x="${W-40}" y="${y}" font-size="13" text-anchor="end" fill="#6b7280">${d.passed}/${d.attempted}</text>`);
    parts.push(`<text x="${W-90}" y="${y}" font-size="14" font-weight="800" text-anchor="end" fill="${band(p)}">${p}%</text>`);
    y += 12;
    parts.push(`<rect x="40" y="${y}" width="${W-80}" height="11" rx="5.5" fill="#efe9fb"/>`);
    parts.push(`<rect x="40" y="${y}" width="${(W-80)*p/100}" height="11" rx="5.5" fill="${band(p)}"/>`);
    y += 24;

    if(z!==null){
      parts.push(`<text x="40" y="${y}" font-size="11" fill="#6b7280">Cohort average ${co.mean}% across ${co.n} learners. ${Math.abs(z).toFixed(1)} SD ${z>=0?"above":"below"}.</text>`);
      y += 18;
    }

    (tagsByDomain[k]||[]).forEach(tg=>{
      parts.push(`<text x="58" y="${y+9}" font-size="11.5" fill="#4c3a70">${esc(tg.tag)}</text>`);
      parts.push(`<text x="${W-40}" y="${y+9}" font-size="11" text-anchor="end" fill="#6b7280">${tg.pass}/${tg.n}</text>`);
      parts.push(`<rect x="${W-260}" y="${y+1}" width="150" height="8" rx="4" fill="#efe9fb"/>`);
      parts.push(`<rect x="${W-260}" y="${y+1}" width="${150*tg.pct/100}" height="8" rx="4" fill="${band(tg.pct)}"/>`);
      parts.push(`<text x="${W-100}" y="${y+9}" font-size="11" font-weight="700" text-anchor="end" fill="${band(tg.pct)}">${tg.pct}%</text>`);
      y += rowH;
    });
    y += blockGap;
  });

  // Topic scores: the cross-cutting view, after the domain breakdown.
  if(topicList.length){
    parts.push(`<text x="40" y="${y+4}" font-size="15" font-weight="800" fill="#4F2683">Topic scores</text>`);
    y += 26;
    topicList.forEach(tg=>{
      parts.push(`<text x="58" y="${y+9}" font-size="11.5" fill="#4c3a70">${esc(tg.tag)}</text>`);
      parts.push(`<text x="${W-40}" y="${y+9}" font-size="11" text-anchor="end" fill="#6b7280">${tg.pass}/${tg.n}</text>`);
      parts.push(`<rect x="${W-260}" y="${y+1}" width="150" height="8" rx="4" fill="#efe9fb"/>`);
      parts.push(`<rect x="${W-260}" y="${y+1}" width="${150*tg.pct/100}" height="8" rx="4" fill="${band(tg.pct)}"/>`);
      parts.push(`<text x="${W-100}" y="${y+9}" font-size="11" font-weight="700" text-anchor="end" fill="${band(tg.pct)}">${tg.pct}%</text>`);
      y += rowH;
    });
  }

  // Overall gauge, drawn as the same vertical column used on screen.
  const gH = 84, gX = W-150, gY = 40;
  const gFill = (overallPct/100)*gH;

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}" font-family="Helvetica, Arial, sans-serif">
  <rect width="${W}" height="${H}" fill="#ffffff"/>
  <text x="40" y="52" font-size="24" font-weight="800" fill="#4F2683">KiRPA Performance Report</text>
  <text x="40" y="76" font-size="12" fill="#6b7280">First-attempt performance by clinical domain and topic</text>
  <text x="40" y="98" font-size="12" fill="#9ca3af">${esc(dateStr)}</text>
  <text x="40" y="126" font-size="15" font-weight="700" fill="#2e1f4d">${overallPassed} of ${overallAttempted} cases solved first time (${overallPct}%)</text>
  <rect x="${gX}" y="${gY}" width="44" height="${gH}" rx="6" fill="#1a1430"/>
  <rect x="${gX+5}" y="${gY+gH-gFill}" width="34" height="${gFill}" rx="4" fill="${band(overallPct)}"/>
  <text x="${gX+22}" y="${gY+16}" font-size="12" font-weight="800" text-anchor="middle" fill="#ffffff">${overallPct}%</text>
  <line x1="40" y1="138" x2="${W-40}" y2="138" stroke="#e4dcfb" stroke-width="1"/>
  ${parts.join("\n  ")}
  <text x="40" y="${H-16}" font-size="10" fill="#9ca3af">Based on first attempts only. Generated by KiRPA for educational use.</text>
</svg>`;
}

function downloadSVG(svg, filename){
  try{
    const blob = new Blob([svg], {type:"image/svg+xml;charset=utf-8"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    setTimeout(()=>URL.revokeObjectURL(url), 1000);
  }catch(e){ console.warn("[KiRPA] SVG export failed", e); }
}


// Collapsible section inside the progress card. One visual language for every
// subsection, so the panel reads as a single object rather than a stack of
// unrelated boxes.
function Section({id,title,count,open,onToggle,children,last}){
  return <div style={{borderBottom:last?"none":`1px solid ${T.border}`}}>
    <button onClick={()=>onToggle(id)}
      style={{width:"100%",padding:"13px 18px",background:"none",border:"none",
        cursor:"pointer",fontFamily:"inherit",textAlign:"start",
        display:"flex",alignItems:"center",gap:"9px"}}>
      <span style={{fontSize:"10px",fontWeight:"700",color:T.muted,
        textTransform:"uppercase",letterSpacing:"0.15em",fontFamily:FONT_MONO}}>
        {title}
      </span>
      {count!==undefined && count!==null && (
        <span style={{fontSize:"10px",fontWeight:"700",color:T.accent,
          background:T.accentLight,borderRadius:"9px",padding:"1px 7px",
          fontVariantNumeric:"tabular-nums"}}>{count}</span>
      )}
      <span style={{marginInlineStart:"auto",color:T.muted,fontSize:"11px"}}>
        {open?"\u25be":"\u25b8"}
      </span>
    </button>
    {open && <div style={{padding:"0 18px 16px"}}>{children}</div>}
  </div>;
}

// Performance bar with band markers, so a percentage is read against a
// standard rather than in isolation.
function PerfBar({pct,color,height=14}){
  return <div style={{position:"relative",height:`${height}px`,borderRadius:`${height/2}px`,
    background:"#efe9fb",overflow:"hidden",
    // Inset shadow gives the track real depth, so the fill sits IN the bar
    // rather than floating on a flat strip.
    boxShadow:"inset 0 1px 2px rgba(15,23,42,0.10)"}}>
    <div style={{position:"absolute",inset:0,width:`${pct}%`,borderRadius:`${height/2}px`,
      background:`linear-gradient(180deg, ${color}f2 0%, ${color} 55%, ${color}d9 100%)`,
      boxShadow:`inset 0 1px 0 rgba(255,255,255,0.35), 0 1px 3px ${color}44`,
      transition:"width 0.8s cubic-bezier(0.22,1,0.36,1)"}}/>
    {/* Band edges at 50 and 85 percent, so a number is read against a
        standard rather than in isolation. */}
    {[50,85].map(m=>(
      <span key={m} style={{position:"absolute",top:0,bottom:0,left:`${m}%`,width:"1px",
        background:"rgba(255,255,255,0.55)",mixBlendMode:"overlay"}}/>
    ))}
  </div>;
}

// Small labelled figure used inside the expanded domain detail.
function Stat({label,value}){
  return <div style={{background:"white",border:`1px solid ${T.border}`,
    borderRadius:"8px",padding:"7px 10px"}}>
    <div style={{fontSize:"9px",fontWeight:"700",color:T.muted,textTransform:"uppercase",
      letterSpacing:"0.1em",marginBottom:"2px"}}>{label}</div>
    <div style={{fontSize:"16px",fontWeight:"700",color:T.text,
      fontVariantNumeric:"tabular-nums",lineHeight:1}}>{value}</div>
  </div>;
}

// ─── LEARNER DASHBOARD ────────────────────────────────────────────────────────

// Short, specific feedback per domain and performance band. Deliberately two
// sentences: what the pattern suggests, and what to do next. Anything longer
// stops being read.
const DOMAIN_FEEDBACK = {
  effluent_dose_flow: {
    low:"Your effluent dose is landing short of target more often than not. Work through the dose calculation before adjusting individual rates, and check whether net ultrafiltration is being counted.",
    mid:"You are setting workable doses. The next step is matching dose to indication rather than defaulting to a standard rate, particularly for small solutes like ammonia.",
    high:"Dosing is consistently on target. Look at cases where the standard dose is deliberately wrong, such as hyperammonaemia or intoxications.",
  },
  hemodynamics_fluid: {
    low:"Instability is being missed or answered late. Before prescribing, check fluid removal against weight and the circuit volume against blood volume.",
    mid:"You handle straightforward instability well. Focus on anticipating it, especially at initiation and with large circuits in small patients.",
    high:"Strong handling of unstable patients. Consider how these decisions change on ECMO or with vasoactive support.",
  },
  access_circuit_alarms: {
    low:"Circuit problems are being treated as machine faults. Work out what each pressure is measuring and where in the circuit the resistance sits.",
    mid:"You resolve common alarms reliably. The harder skill is separating a patient problem from a circuit problem when both could explain the reading.",
    high:"You diagnose circuit problems quickly. Push into filtration fraction and circuit life, where the cause is upstream of the alarm.",
  },
  anticoagulation: {
    low:"Anticoagulation choices are not yet matching the clinical picture. Review the monitoring target for each agent and what a failing circuit looks like.",
    mid:"Standard anticoagulation is well handled. Focus on recognising complications early, particularly citrate accumulation and HIT.",
    high:"Confident anticoagulation management. Worth reading on alternatives when heparin and citrate are both unsuitable.",
  },
  electrolytes: {
    low:"Corrections are being made without accounting for what CRRT itself removes or delivers. Check solution composition before adding to the bag.",
    mid:"You manage routine corrections well. The nuance is rate of correction in severe dysnatraemia, where being right too quickly is still wrong.",
    high:"Excellent electrolyte management. Consider CRRT in inborn errors of metabolism, where clearance targets are entirely different.",
  },
  unknown: {
    low:"Whole prescriptions are missing components. Build them in a fixed order every time: access, filter, prime, modality, rates, anticoagulation.",
    mid:"Your prescriptions are sound. Work on sizing choices, where catheter and filter selection drive everything downstream.",
    high:"Complete and appropriate prescriptions. Try the cases where the obvious prescription is the wrong one.",
  },
};
const domainFeedback = (domain, pct) => {
  const f = DOMAIN_FEEDBACK[domain];
  const band = pct < 50 ? "low" : pct < 85 ? "mid" : "high";
  if (f) return f[band];
  return band === "low"
    ? "Revisit the cases in this area and work through the reasoning before prescribing."
    : band === "mid"
    ? "Solid work here. Return to the cases you did not solve first time."
    : "Consistently strong in this area.";
};

// Human labels for the error-flag schema. The keys are canonical identifiers
// written to research_events and are never translated in the data; only these
// display strings change.
const ERR_LABEL = {
  error_clearance:"Clearance", error_netuf:"Net UF", error_bfr:"Blood flow",
  error_anticoag:"Anticoagulation", error_solution:"Solution", error_access:"Access",
  error_filter:"Filter", error_prime:"Prime", error_modality:"Modality",
  error_k_add:"Potassium", error_po4_add:"Phosphate", error_na_add:"Sodium",
  error_free_water:"Free water", error_intervention:"Bedside actions",
};

// One view over both tracks. Defaults to everything, filterable to self-study or
// assigned. Assigned attempts whose feedback an educator has held back appear in
// the list but withhold their outcome, so the dashboard cannot be used to
// sidestep a deliberate feedback hold.
function LearnerDashboard({deviceId,onLink,hasEmail,linkPending,alwaysOpen,onExport}){
  const {t}=useLocale();
  const [data,setData]=useState(null);
  const [busy,setBusy]=useState(true);
  const [filter,setFilter]=useState("all");
  const [expanded,setExpanded]=useState(null);   // case_id currently open
  // In the report everything is open at once: it is meant to be read whole
  // and exported, not browsed.
  const [expandedDomain,setExpandedDomain]=useState(null);
  const domainIsOpen=(k)=>alwaysOpen||expandedDomain===k;
  // Which sections are open. Domains and recurring issues default open;
  // history defaults closed because it is the longest and least glanceable.
  // Open by default, except history, which starts closed because it is the
  // longest section and the least useful at a glance.
  const SEC_DEFAULT = { domains:true, topics:true, errors:true, history:false };
  // The report opens everything EXCEPT case history: that section is a long
  // per-case list, and forcing it open buries the analysis the report exists
  // to show. It stays one tap away.
  const secOpen=(id)=>(alwaysOpen && id!=="history") ? true : (sec[id]??true);
  const [sec,setSec]=useState(SEC_DEFAULT);
  const toggleSec=(id)=>setSec(p=>({...p,[id]:!(p[id]??true)}));

  useEffect(()=>{let dead=false;(async()=>{
    setBusy(true);
    try{
      const fns=getFunctions(firebaseApp,"us-central1");
      const res=await httpsCallable(fns,"getLearnerHistory")({deviceId});
      if(!dead) setData(res?.data||null);
    }catch(e){ console.warn("[KiRPA dashboard]",e); }
    if(!dead) setBusy(false);
  })();return()=>{dead=true;};},[deviceId]);

  if(busy) return <div style={{padding:"40px",textAlign:"center",color:T.muted,fontSize:"13px"}}>
    <Ic n="spin" s="w-5 h-5"/>
  </div>;

  const events=(data?.events||[]).filter(e=>
    filter==="all" ? true : filter==="assigned" ? e.track==="assigned" : e.track!=="assigned");
  const domains=data?.domains||{};
  const domainKeys=Object.keys(domains);

  // ── Group attempts by case ────────────────────────────────────────────────
  // A learner reasons per case, not per event. Attempts are ordered newest
  // first within a case; `solved` is true if ANY attempt succeeded, since the
  // question "did I ever get this" is the one being asked here (the strict
  // first-attempt figure drives the domain scores instead).
  const byCase = {};
  events.forEach(e=>{
    const id = e.case_id || "(unknown)";
    if(!byCase[id]) byCase[id] = { case_id:id, track:e.track, attempts:[],
                                   solved:false, locked:false, lastAt:null, firstErrors:[] };
    const c = byCase[id];
    c.attempts.push(e);
    if(e.locked) c.locked = true;
    if(e.success) c.solved = true;
    if(!c.lastAt || String(e.submitted_at||"") > String(c.lastAt)) c.lastAt = e.submitted_at;
    if(e.is_first_attempt && e.errors) c.firstErrors = Object.entries(e.errors);
  });
  const cases = Object.values(byCase).map(c=>({
    ...c,
    attempts: c.attempts.sort((a,b)=>
      (b.attempt_number||0)-(a.attempt_number||0) ||
      String(b.submitted_at||"").localeCompare(String(a.submitted_at||""))),
  })).sort((a,b)=>{
    // Unsolved first: those are the ones worth acting on.
    if(a.locked!==b.locked) return a.locked?1:-1;
    if(a.solved!==b.solved) return a.solved?1:-1;
    return String(b.lastAt||"").localeCompare(String(a.lastAt||""));
  });

  // ── Recurring issues ──────────────────────────────────────────────────────
  // Counted on FIRST attempts only, so retrying a case does not turn a one-off
  // slip into an apparent pattern.
  const errTally = {};
  events.forEach(e=>{
    if(e.locked || !e.is_first_attempt || !e.errors) return;
    Object.keys(e.errors).forEach(k=>{ errTally[k]=(errTally[k]||0)+1; });
  });
  const topErrors = Object.entries(errTally)
    .filter(([,n])=>n>=2)               // a single occurrence is not a pattern
    .sort((a,b)=>b[1]-a[1]).slice(0,6);

  // Tag breakdown per domain, also used by the SVG export.
  const tagsByDomain = {};
  domainKeys.forEach(k=>{
    const acc={};
    (data?.events||[]).forEach(e=>{
      if(e.locked || !e.is_first_attempt || e.domain!==k) return;
      tagsForCase(e.case_id).forEach(tag=>{
        if(!acc[tag]) acc[tag]={n:0,pass:0};
        acc[tag].n++; if(e.success) acc[tag].pass++;
      });
    });
    tagsByDomain[k]=Object.entries(acc)
      .map(([tag,v])=>({tag,...v,pct:Math.round(v.pass/v.n*100)}))
      .sort((a,b)=>a.pct-b.pct||b.n-a.n);
  });

  // ── Topic scores ────────────────────────────────────────────────────────
  // Cross-cutting, independent of domain. A case sits in exactly one clinical
  // domain but carries several topics, and the same topic appears under
  // different domains: citrate problems show up under Anticoagulation and
  // Electrolytes, neonatal cases under Clearance and Access. Counting topics
  // separately is the only way to see that a learner is weak on, say, every
  // neonatal case regardless of which domain it was filed under.
  //
  // Topic counts therefore sum to MORE than the number of cases, by design.
  const topicTally = {};
  (data?.events||[]).forEach(e=>{
    if(e.locked || !e.is_first_attempt) return;
    tagsForCase(e.case_id).forEach(tag=>{
      if(!topicTally[tag]) topicTally[tag] = {n:0, pass:0};
      topicTally[tag].n++;
      if(e.success) topicTally[tag].pass++;
    });
  });
  const topics = Object.entries(topicTally)
    .map(([tag,v])=>({tag, ...v, pct: Math.round(v.pass/v.n*100)}))
    .sort((a,b)=>a.pct-b.pct || b.n-a.n);

  // Headline figure, first attempts only, matching the domain bars.
  const overallAttempted = domainKeys.reduce((a,k)=>a+domains[k].attempted,0);
  const overallPassed    = domainKeys.reduce((a,k)=>a+domains[k].passed,0);
  const overallPct       = overallAttempted ? Math.round(overallPassed/overallAttempted*100) : 0;

  // Hand the export builder up to the report header.
  //
  // Deliberately NOT a useEffect: everything above this point sits after the
  // loading early-return, so a hook here would run conditionally and break the
  // rules of hooks. A plain assignment during render is correct anyway — the
  // parent only ever calls it from a click handler, by which time render has
  // finished and the closure holds current data.
  if (onExport) {
    onExport(()=>buildReportSVG({
      overallPct, overallPassed, overallAttempted, domains, domainKeys,
      cohort: data?.cohort||{}, tagsByDomain, topics,
      labelFor:(k)=>tDomain(t,k),
      dateStr:new Date().toLocaleDateString(undefined,{year:"numeric",month:"long",day:"numeric"}),
    }));
  }

  return <div>

    {/* Cross-device state. An unconfirmed device sees only its own history. */}
    {!data?.confirmed && <div style={{padding:"11px 14px",marginBottom:"14px",borderRadius:"10px",
      background:"#faf8ff",border:`1px solid ${T.border}`,fontSize:"12px",
      color:T.muted,lineHeight:"1.6"}}>
      {linkPending
        ? t("dash.pending","Check your email to finish linking this device. Everything you do meanwhile is still saved and will be included once you confirm.")
        : t("dash.deviceOnly","This shows work from this device. Add an email to combine your history across devices.")}
      {!linkPending && !hasEmail &&
        <button onClick={onLink} style={{marginInlineStart:"6px",background:"none",border:"none",
          padding:0,color:T.accent,fontWeight:"700",fontSize:"12px",cursor:"pointer",
          fontFamily:"inherit",textDecoration:"underline"}}>
          {t("dash.addEmail","Add email")}
        </button>}
    </div>}

    {/* ── One card, four collapsible sections ─────────────────────────────── */}
    <div style={{background:"white",borderRadius:"16px",border:`1px solid ${T.border}`,
      overflow:"hidden",boxShadow:"0 1px 3px rgba(15,23,42,0.05)"}}>

      {/* Headline: overall first-attempt rate. Always visible — it is the one
          number worth seeing without opening anything. */}
      <div style={{padding:"18px 18px 16px",borderBottom:`1px solid ${T.border}`,
        display:"flex",alignItems:"center",gap:"16px"}}>
        <div style={{flexShrink:0}}><RadialScore percentage={overallPct} size={68}/></div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:"13.5px",fontWeight:"750",color:T.text,marginBottom:"3px"}}>
            {t("dash.overall","Overall first-attempt rate")}
          </div>
          <div style={{fontSize:"12px",color:T.muted,lineHeight:"1.55"}}>
            {t("dash.overallSub","{p} of {a} cases solved on the first try across {d} domains.",
              {p:overallPassed,a:overallAttempted,d:domainKeys.length})}
          </div>
        </div>

      </div>

      {/* ── By domain ── */}
      {domainKeys.length>0 && (
        <Section id="domains" title={t("shell.performanceByDomain","By domain")}
          count={domainKeys.length} open={secOpen("domains")} onToggle={toggleSec}>
          <div style={{display:"flex",flexDirection:"column",gap:"3px"}}>
            {domainKeys.map(k=>{
              const d = domains[k];
              const p = d.attempted ? Math.round(d.passed/d.attempted*100) : 0;
              const open = domainIsOpen(k);
              const col = p>=85?T.success:p>=50?T.warn:T.danger;
              const co = (data?.cohort||{})[k];
              const z  = (co && co.sd>0) ? (p - co.mean)/co.sd : null;
              const errs = Object.entries(d.errorCounts||{}).sort((a,b)=>b[1]-a[1]).slice(0,3);

              // Topic-tag performance within this domain. A learner can be
              // strong on clearance generally but weak on the metabolic cases
              // specifically, which the domain figure alone would hide.
              const tagStats = tagsByDomain[k] || [];

              return <div key={k} style={{borderRadius:"10px",
                background:open?"#faf8ff":"transparent",
                border:`1px solid ${open?"#e4dcfb":"transparent"}`,
                transition:"background 0.15s"}}>

                <button onClick={()=>setExpandedDomain(open?null:k)}
                  style={{width:"100%",padding:"13px 12px",background:"none",border:"none",
                    cursor:"pointer",fontFamily:"inherit",textAlign:"start",
                    display:"flex",alignItems:"center",gap:"15px"}}>
                  {/* Score drawn as a circuit pressure gauge: 100% top, 0%
                      bottom, the same column a nephrology learner already
                      reads on the machine. */}
                  <PressureGauge pct={p} color={col} height={80} width={46}/>
                  <span style={{flex:1,minWidth:0}}>
                    <span style={{display:"block",fontSize:"13px",fontWeight:"700",
                      color:T.text,marginBottom:"3px"}}>
                      {tDomain(t,k)}
                    </span>
                    <span style={{display:"block",fontSize:"11.5px",color:T.muted,
                      fontVariantNumeric:"tabular-nums"}}>
                      {t("dash.solvedOf","{p} of {a} solved first time",{p:d.passed,a:d.attempted})}
                    </span>
                  </span>
                  <span style={{color:T.muted,fontSize:"11px",flexShrink:0}}>
                    {open?"\u25be":"\u25b8"}
                  </span>
                </button>

                {open && <div style={{padding:"4px 12px 13px",display:"flex",
                  flexDirection:"column",gap:"11px"}}>
                  <div style={{fontSize:"12.5px",color:T.text,lineHeight:"1.65"}}>
                    {domainFeedback(k,p)}
                  </div>

                  <div style={{display:"grid",gap:"7px",
                    gridTemplateColumns:"repeat(auto-fit,minmax(88px,1fr))"}}>
                    <Stat label={t("dash.cases","Cases")} value={String(d.cases?.length||d.attempted)}/>
                    <Stat label={t("dash.selfStudy","Self-study")} value={String(d.practice||0)}/>
                    <Stat label={t("dash.assigned","Assigned")} value={String(d.assigned||0)}/>
                  </div>

                  {tagStats.length>0 && <div>
                    <div style={{fontSize:"9.5px",fontWeight:"700",color:T.muted,
                      textTransform:"uppercase",letterSpacing:"0.13em",marginBottom:"8px"}}>
                      {t("dash.byTopic","By topic")}
                    </div>
                    <div style={{display:"flex",flexDirection:"column",gap:"8px"}}>
                      {tagStats.map(({tag,n,pass,pct})=>{
                        const tc = pct>=85?T.success:pct>=50?T.warn:T.danger;
                        return <div key={tag}>
                          <div style={{display:"flex",justifyContent:"space-between",
                            alignItems:"baseline",marginBottom:"4px",gap:"8px"}}>
                            <span style={{fontSize:"11.5px",color:T.text,fontWeight:"600",
                              overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                              {tag}
                            </span>
                            <span style={{fontSize:"11px",color:T.muted,flexShrink:0,
                              fontVariantNumeric:"tabular-nums"}}>
                              {pass}/{n}
                              <span style={{fontWeight:"700",color:tc,
                                marginInlineStart:"6px"}}>{pct}%</span>
                            </span>
                          </div>
                          <PerfBar pct={pct} color={tc} height={9}/>
                        </div>;
                      })}
                    </div>
                  </div>}

                  {errs.length>0 && <div>
                    <div style={{fontSize:"9.5px",fontWeight:"700",color:T.muted,
                      textTransform:"uppercase",letterSpacing:"0.13em",marginBottom:"6px"}}>
                      {t("dash.commonHere","Most common in this domain")}
                    </div>
                    <div style={{display:"flex",flexWrap:"wrap",gap:"6px"}}>
                      {errs.map(([ek,n])=>(
                        <span key={ek} style={{fontSize:"11px",padding:"3px 9px",borderRadius:"8px",
                          background:"#fef2f2",border:"1px solid #fecaca",color:"#991b1b"}}>
                          {ERR_LABEL[ek]||ek.replace("error_","").replace(/_/g," ")}
                          <span style={{marginInlineStart:"5px",opacity:0.7,
                            fontVariantNumeric:"tabular-nums"}}>\u00d7{n}</span>
                        </span>
                      ))}
                    </div>
                  </div>}

                  {z!==null ? (
                    <div style={{padding:"10px 13px",borderRadius:"9px",background:"white",
                      border:`1px solid ${T.border}`}}>
                      <div style={{fontSize:"9.5px",fontWeight:"700",color:T.muted,
                        textTransform:"uppercase",letterSpacing:"0.13em",marginBottom:"5px"}}>
                        {t("dash.vsOthers","Compared with other learners")}
                      </div>
                      <div style={{fontSize:"12.5px",color:T.text,lineHeight:"1.6"}}>
                        {t("dash.zLine","Cohort average {m}% across {n} learners. You are {z} standard deviations {dir}.",
                          { m:co.mean, n:co.n, z:Math.abs(z).toFixed(1),
                            dir: z>=0 ? t("dash.above","above") : t("dash.below","below") })}
                      </div>
                      <div style={{marginTop:"9px",position:"relative",height:"5px",
                        borderRadius:"3px",background:T.bgDeep}}>
                        <span style={{position:"absolute",top:"-3px",width:"2px",height:"11px",
                          background:T.muted,left:"50%",borderRadius:"1px"}}/>
                        <span style={{position:"absolute",top:"-4px",width:"9px",height:"13px",
                          borderRadius:"3px",background:col,
                          left:`calc(${Math.max(2,Math.min(98,50+z*16))}% - 4px)`,
                          transition:"left 0.5s ease"}}/>
                      </div>
                    </div>
                  ) : (
                    <div style={{fontSize:"11.5px",color:"#9ca3af",lineHeight:"1.55"}}>
                      {t("dash.noCohort","Comparison with other learners appears once enough people have attempted this domain.")}
                    </div>
                  )}
                </div>}
              </div>;
            })}
          </div>
          <div style={{marginTop:"10px",fontSize:"11px",color:"#9ca3af"}}>
            {t("dash.firstAttemptNote","Based on first attempts only.")}
          </div>
        </Section>
      )}

      {/* ── Topic scores ── */}
      {topics.length>0 && (
        <Section id="topics" title={t("dash.topicScores","Topic scores")}
          count={topics.length} open={secOpen("topics")} onToggle={toggleSec}>
          <div style={{fontSize:"11.5px",color:T.muted,marginBottom:"12px",
            lineHeight:"1.55"}}>
            {t("dash.topicNote","Topics cut across domains, so a case can appear under more than one. Counts here add up to more than your number of cases.")}
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:"9px"}}>
            {topics.map(({tag,n,pass,pct})=>{
              const tc = pct>=85?T.success:pct>=50?T.warn:T.danger;
              return <div key={tag}>
                <div style={{display:"flex",justifyContent:"space-between",
                  alignItems:"baseline",marginBottom:"4px",gap:"9px"}}>
                  <span style={{fontSize:"12px",color:T.text,fontWeight:"600",
                    overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                    {tag}
                  </span>
                  <span style={{fontSize:"11px",color:T.muted,flexShrink:0,
                    fontVariantNumeric:"tabular-nums"}}>
                    {pass}/{n}
                    <span style={{fontWeight:"700",color:tc,
                      marginInlineStart:"7px"}}>{pct}%</span>
                  </span>
                </div>
                <PerfBar pct={pct} color={tc} height={9}/>
              </div>;
            })}
          </div>
          <div style={{marginTop:"11px",fontSize:"11px",color:"#9ca3af"}}>
            {t("dash.firstAttemptNote","Based on first attempts only.")}
          </div>
        </Section>
      )}

      {/* ── Recurring issues ── */}
      {topErrors.length>0 && (
        <Section id="errors" title={t("dash.recurring","Recurring issues")}
          count={topErrors.length} open={secOpen("errors")} onToggle={toggleSec}>
          <div style={{display:"flex",flexWrap:"wrap",gap:"7px"}}>
            {topErrors.map(([k,n])=>(
              <span key={k} style={{fontSize:"11.5px",padding:"4px 10px",borderRadius:"9px",
                background:"#fffbeb",border:"1px solid #fde68a",color:"#92400e",fontWeight:"600"}}>
                {ERR_LABEL[k]||k.replace("error_","").replace(/_/g," ")}
                <span style={{marginInlineStart:"6px",opacity:0.7,
                  fontVariantNumeric:"tabular-nums"}}>\u00d7{n}</span>
              </span>
            ))}
          </div>
          <div style={{marginTop:"9px",fontSize:"11px",color:"#9ca3af",lineHeight:"1.55"}}>
            {t("dash.recurringNote","Counted on first attempts, and only where the same flag has come up more than once.")}
          </div>
        </Section>
      )}

      {/* ── Case history ── */}
      <Section id="history" title={t("dash.history","Case history")}
        count={cases.length} open={secOpen("history")} onToggle={toggleSec} last>
        <div style={{display:"flex",gap:"6px",marginBottom:"12px"}}>
          {[["all",t("dash.all","All")],
            ["practice",t("dash.selfStudy","Self-study")],
            ["assigned",t("dash.assigned","Assigned")]].map(([k,label])=>{
            const on=filter===k;
            return <button key={k} onClick={()=>setFilter(k)}
              style={{flex:1,padding:"6px 10px",borderRadius:"8px",cursor:"pointer",
                fontFamily:"inherit",fontSize:"11.5px",fontWeight:on?"700":"500",
                border:`1px solid ${on?T.accent:T.border}`,
                background:on?T.accentLight:"white",color:on?T.accentDark:T.text}}>
              {label}
            </button>;
          })}
        </div>

        {cases.length===0
          ? <div style={{padding:"22px",textAlign:"center",color:T.muted,fontSize:"12.5px"}}>
              {t("dash.noneYet","Nothing here yet.")}
            </div>
          : <div style={{display:"flex",flexDirection:"column",gap:"7px"}}>
              {cases.map(c=>{
                const open = expanded===c.case_id;
                return <div key={c.case_id} style={{borderRadius:"10px",
                  border:`1px solid ${open?T.accent:T.border}`,overflow:"hidden"}}>
                  <button onClick={()=>setExpanded(open?null:c.case_id)}
                    disabled={c.locked}
                    style={{width:"100%",textAlign:"start",padding:"11px 13px",background:"none",
                      border:"none",cursor:c.locked?"default":"pointer",fontFamily:"inherit",
                      display:"flex",alignItems:"center",gap:"11px",opacity:c.locked?0.7:1}}>
                    <span style={{width:"9px",height:"9px",borderRadius:"50%",flexShrink:0,
                      background:c.locked?"#d4d4d8":c.solved?T.success:T.danger}}/>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:"13px",fontWeight:"700",color:T.text}}>{c.case_id}</div>
                      <div style={{fontSize:"11px",color:T.muted,marginTop:"2px"}}>
                        {c.track==="assigned"?t("dash.assigned","Assigned"):t("dash.selfStudy","Self-study")}
                        {" \u00b7 "}{t("dash.nAttempts","{n} attempt(s)",{n:c.attempts.length})}
                        {c.lastAt?` \u00b7 ${new Date(c.lastAt).toLocaleDateString()}`:""}
                      </div>
                      {!c.locked && c.firstErrors.length>0 && (
                        <div style={{display:"flex",flexWrap:"wrap",gap:"5px",marginTop:"7px"}}>
                          {c.firstErrors.slice(0,4).map(([k,v])=>(
                            <span key={k} style={{fontSize:"10.5px",padding:"2px 7px",borderRadius:"7px",
                              background:"#fef2f2",border:"1px solid #fecaca",color:"#991b1b"}}>
                              {ERR_LABEL[k]||k.replace("error_","").replace(/_/g," ")}
                              {typeof v==="string"?` ${v.replace(/_/g," ")}`:""}
                            </span>
                          ))}
                          {c.firstErrors.length>4 && (
                            <span style={{fontSize:"10.5px",color:T.muted,alignSelf:"center"}}>
                              +{c.firstErrors.length-4}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <span style={{fontSize:"11px",fontWeight:"700",flexShrink:0,
                      color:c.locked?T.muted:c.solved?T.success:T.danger}}>
                      {c.locked?t("dash.locked","Feedback pending")
                        :c.solved?t("dash.passed","Solved"):t("dash.notPassed","Not solved")}
                    </span>
                    {!c.locked && <span style={{color:T.muted,fontSize:"11px",flexShrink:0}}>
                      {open?"\u25be":"\u25b8"}
                    </span>}
                  </button>

                  {open && !c.locked && (
                    <div style={{borderTop:`1px solid ${T.border}`,padding:"13px",
                      background:"#faf8ff"}}>
                      {c.attempts.map((a,idx)=>(
                        <div key={a.id} style={{marginBottom:idx<c.attempts.length-1?"15px":0}}>
                          <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"7px"}}>
                            <span style={{fontSize:"9.5px",fontWeight:"700",color:T.muted,
                              textTransform:"uppercase",letterSpacing:"0.12em"}}>
                              {t("dash.attemptN","attempt {n}",{n:a.attempt_number})}
                            </span>
                            <span style={{fontSize:"10.5px",fontWeight:"700",
                              color:a.success?T.success:T.danger}}>
                              {a.success?t("dash.solved","Solved"):t("dash.notSolved","Not solved")}
                            </span>
                            <span style={{flex:1,height:"1px",background:T.border}}/>
                          </div>
                          <RxSummary rx={a.rx||{}}/>
                        </div>
                      ))}
                    </div>
                  )}
                </div>;
              })}
            </div>}
      </Section>
    </div>
  </div>;
}

// Compact two-column readout of what was prescribed. Shown inline in the
// history rather than behind a modal: the prescription IS the thing being
// reviewed, so hiding it behind a click was the wrong default.
function RxSummary({rx}){
  const F=[
    ["Modality",rx.modality],["Access",rx.access],["Filter",rx.filter],
    ["Prime",rx.prime],["Anticoag",rx.anticoag],["Solution",rx.solution],
    ["Blood flow",rx.bfr!=null?`${rx.bfr} ml/min`:null],
    ["Pre-blood pump",rx.pbp!=null?`${rx.pbp} ml/hr`:null],
    ["Dialysate",rx.dialysate!=null?`${rx.dialysate} ml/hr`:null],
    ["Post-filter",rx.rep!=null?`${rx.rep} ml/hr`:null],
    ["Net UF",rx.netUf!=null?`${rx.netUf} ml/hr`:null],
    ["Clearance",rx.clearanceBsa!=null?`${rx.clearanceBsa} L/1.73m\u00b2/hr`:null],
    ["K replaced",rx.kReplaced==null?null:(rx.kReplaced?"Yes":"No")],
    ["PO\u2084 replaced",rx.po4Replaced==null?null:(rx.po4Replaced?"Yes":"No")],
  ].filter(([,v])=>v!==null&&v!==undefined&&v!=="");

  if(!F.length) return <div style={{fontSize:"12px",color:T.muted}}>
    No prescription recorded for this attempt.
  </div>;

  return <div style={{display:"grid",gap:"2px 16px",
    gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))"}}>
    {F.map(([l,v])=>(
      <div key={l} style={{display:"flex",justifyContent:"space-between",gap:"10px",
        padding:"4px 0",borderBottom:"1px solid #efe9fb",fontSize:"12px"}}>
        <span style={{color:T.muted}}>{l}</span>
        <span style={{color:T.text,fontWeight:"600",textAlign:"end",
          fontVariantNumeric:"tabular-nums"}}>{v}</span>
      </div>
    ))}
  </div>;
}



// ─── PERFORMANCE REPORT ───────────────────────────────────────────────────────
// The whole analysis in one place, every domain expanded, exportable as a
// self-contained SVG. Reached from the summary gauge rather than a tab, so a
// learner is not asked to go looking for it.
function PerformanceReport({deviceId,hasEmail,linkPending,onLink,onClose}){
  const {t}=useLocale();
  const exportRef = useRef(null);

  return <div style={{position:"fixed",inset:0,zIndex:260,background:"rgba(15,23,42,0.55)",
    overflowY:"auto",padding:"16px",fontFamily:FONT_UI}} onClick={onClose}>
    <div onClick={e=>e.stopPropagation()} style={{background:T.bg,borderRadius:"18px",
      maxWidth:"780px",margin:"14px auto",overflow:"hidden",
      boxShadow:"0 28px 70px rgba(0,0,0,0.35)"}}>

      <div style={{background:"white",borderBottom:`1px solid ${T.border}`,
        padding:"18px 20px",display:"flex",alignItems:"center",gap:"12px",
        position:"sticky",top:0,zIndex:2}}>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:"17px",fontWeight:"800",color:T.text,letterSpacing:"-0.01em"}}>
            {t("dash.reportTitle","Performance Report")}
          </div>
          <div style={{fontSize:"11.5px",color:T.muted,marginTop:"2px"}}>
            {t("dash.reportSub","First-attempt performance by domain and topic")}
          </div>
        </div>
        <button onClick={()=>{
            const svg = exportRef.current && exportRef.current();
            if(svg) downloadSVG(svg, `kirpa-performance-${new Date().toISOString().slice(0,10)}.svg`);
          }}
          style={{padding:"7px 13px",borderRadius:"9px",border:`1px solid ${T.accent}`,
            background:T.accentLight,color:T.accentDark,fontSize:"12px",fontWeight:"700",
            cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>
          {t("dash.export","Export")}
        </button>
        <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",
          color:T.muted,fontSize:"20px",lineHeight:1,flexShrink:0,padding:"0 2px"}}>×</button>
      </div>

      <div style={{padding:"16px"}}>
        <LearnerDashboard deviceId={deviceId} hasEmail={hasEmail}
          linkPending={linkPending} onLink={onLink}
          alwaysOpen onExport={fn=>{ exportRef.current = fn; }}/>
      </div>
    </div>
  </div>;
}

// ─── PRACTICE SESSION CONTAINER ───────────────────────────────────────────────
// Wraps built-in cases into the same assignment shape TraineeAssignmentExperience
// already consumes, so the case-running UI is shared verbatim between tracks and
// cannot drift apart.

// ─── LETTER OF INFORMATION AND CONSENT (Phase 2) ─────────────────────────────
// Reproduced verbatim from "Letter of Information and Consent - Phase 2
// Platform Users - KiRPA, 08102026", Version 3, 8 October 2026. Section
// numbering and wording follow the approved document exactly; nothing here is
// a paraphrase, because a letter that differs from the approved text is an
// unapproved letter.
//
// Version 3 takes EXPRESS consent. The letter is shown in full before the
// learner's first case, and selecting Continue is the consent event, recorded
// server-side per subject. Version 2's persistent notice is gone: the letter
// is shown once, at the first case, not on every screen.
//
// LOI_VERSION here is for display and for the localStorage copy only. The
// version written onto a consent record, and stamped onto every research
// event, is the server's own constant in index.js.
const LOI_VERSION = "2026-10-08";
const LOI_PATH = "/loi";

const LOI_TITLE = {
  project:
    "Kidney Replacement Prescription Analytics (KiRPA): Learning Analytics for " +
    "Paediatric Continuous Renal Replacement Therapy Prescribing",
  pi:
    "Dr. Amrit Kirpalani, MD, MEHP, FRCPC, Paediatric Nephrologist, Department of " +
    "Paediatrics, Schulich School of Medicine & Dentistry, Western University. " +
    "amrit.kirpalani@lhsc.on.ca",
};

// Each entry is [heading, [paragraph, ...]], reproduced verbatim from the
// approved document. Ten sections: the duration section was removed and the
// rest renumbered.
const LOI_PHASE2 = [
  ["1. Invitation to participate", [
    "KiRPA is a free, open-access tool for learning to prescribe continuous renal replacement therapy (CRRT, a form of dialysis for critically ill children), and also a research platform: information about how it is used supports ongoing research in medical education. Everyone who uses KiRPA takes part; there is no version that collects nothing.",
  ]],
  ["2. Why is this study being done?", [
    "CRRT is high-risk, used infrequently, and needs many decisions at once, and there is no standard way to find where prescribing knowledge gaps lie. This study looks at which parts of a prescription are most often got wrong, whether that differs by discipline, and whether practice helps, so that CRRT teaching can be designed better.",
  ]],
  ["3. What are the study procedures?", [
    "Nothing beyond using KiRPA normally: you read a simulated case, enter a prescription, and get feedback. Hints are optional.",
    "The platform records your prescription, which parts met the expert-defined criteria and in which direction any error went, whether you used a hint, which case you did and in what order, your language setting, the date and time, and your professional role. It records no name and no patient information. You appear only as a randomly generated identifier.",
  ]],
  ["4. What are the risks and harms of participating in this study?", [
    "Minimal. With no names or patient information collected, the risk of a privacy breach affecting you is very low. Data are stored by Google Cloud Firestore (policies.google.com/privacy) on servers in the United States and may be subject to access by United States authorities under the laws of that country; no information sent over the internet is ever completely secure.",
    "Your results are never used for your evaluation or any decision about your training or employment, and your educator sees only the feedback the platform already shows you.",
  ]],
  ["5. What are the benefits?", [
    "You may find the feedback useful. There is no direct benefit to you from the research itself. The findings may improve CRRT teaching and the safety of CRRT for critically ill children.",
  ]],
  ["6. Can participants choose to leave the study?", [
    "You may stop at any time and need not finish a case. The information collected has no identifiers, so individual records cannot be found and information already collected cannot be removed. Once the study has been published we will not be able to withdraw your information.",
  ]],
  ["7. How will participants’ information be kept confidential?", [
    "Your information is identified only by a randomly generated identifier, data are encrypted in transit and at rest, and you see only your own results while your educator sees only their own program.",
    "While we will do our best to protect your information there is no guarantee that we will be able to do so.",
    "Representatives of the Western University and its Health Sciences Research Ethics Board that oversees the ethical conduct of this study may require access to study-related records.",
    "Results are reported as group results only and your name will not be used. De-identified data may be shared with journals or other researchers so findings can be checked or re-analysed, and will contain nothing that can identify you.",
    "De-identified data will be kept for 7 years after the study is complete and then destroyed.",
  ]],
  ["8. What are the rights of participants?", [
    "Your participation in this study is voluntary. You may decide not to be in this study. Even if you consent to participate you have the right to not answer individual questions or to withdraw from the study at any time. If you choose not to participate or to leave the study at any time it will have no effect on your employment status or your academic standing.",
    "You do not waive any legal right by consenting to this study.",
    "You will not be compensated for your participation in this research.",
  ]],
  ["9. Whom do participants contact for questions?", [
    "If you have questions about this research study please contact Dr. Amrit Kirpalani (amrit.kirpalani@lhsc.on.ca).",
    "If you have any questions about your rights as a research participant or the conduct of this study, you may contact The Office of Human Research Ethics (519) 661-3036, 1-844-720-9816, email: ethics@uwo.ca. The REB is a group of people who oversee the ethical conduct of research studies. The HSREB is not part of the study team. Everything that you discuss will be kept confidential.",
    "This letter is yours to keep for future reference.",
  ]],
  ["10. Consent", [
    "Selecting Continue indicates your consent to take part in this study: that you have read this letter, that you understand what information is collected and how it is used, and that you agree to take part. Dr. Kirpalani developed KiRPA and has no financial interest in it.",
    "If you do not wish to take part, close this page and do not use the platform. Nothing is recorded.",
  ]],
];

// The letter as plain text, for the download in both the consent screen and the
// read-only route. Section 12 tells the reader to keep or print a copy, so this
// is a requirement of the approved text rather than a convenience.
function loiPlainText(){
  return "Letter of Information and Consent\n\n" +
    "Project: " + LOI_TITLE.project + "\n\n" +
    "Principal Investigator: " + LOI_TITLE.pi + "\n\n" +
    LOI_PHASE2.map(([h, ps]) => h + "\n\n" + ps.join("\n\n")).join("\n\n") + "\n";
}

function loiDownload(){
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([loiPlainText()], { type:"text/plain" }));
  a.download = "KiRPA-Letter-of-Information-and-Consent-" + LOI_VERSION + ".txt";
  a.click();
  URL.revokeObjectURL(a.href);
}

// ─── THE LETTER, AS A BODY OF TEXT ───────────────────────────────────────────
// Shared by the consent screen and the read-only route, so the text a learner
// consents to and the text they can go back and reread are the same component
// and cannot drift apart.
function LoiBody(){
  return (
    <>
      {/* id is what the modal's aria-labelledby points at, so a screen reader
          announces the dialog by the letter's own title. */}
      <h1 id="kirpa-loi-heading" style={{ fontSize:"20px", fontWeight:"750",
        color:T.text, margin:"0 0 20px", lineHeight:"1.35" }}>
        Letter of Information and Consent
      </h1>

      {[["Project", LOI_TITLE.project],
        ["Principal Investigator", LOI_TITLE.pi]].map(([k, v]) => (
        <p key={k} style={{ fontSize:"13px", color:T.text, lineHeight:"1.7",
          margin:"0 0 10px" }}>
          <strong>{k}:</strong> {v}
        </p>
      ))}
      <div style={{ marginBottom:"26px", paddingBottom:"18px",
        borderBottom:`1px solid ${T.border}` }}/>

      {LOI_PHASE2.map(([heading, paras]) => (
        <section key={heading} style={{ marginBottom:"26px" }}>
          <h2 style={{ fontSize:"14.5px", fontWeight:"750", color:T.text,
            margin:"0 0 10px", lineHeight:"1.4" }}>{heading}</h2>
          {paras.map((p, i) => (
            <p key={i} style={{ fontSize:"13.5px", color:T.text,
              lineHeight:"1.8", margin:"0 0 12px" }}>{p}</p>
          ))}
        </section>
      ))}
    </>
  );
}

// ─── THE CONSENT SCREEN ──────────────────────────────────────────────────────
// Shown once per subject, immediately before their first case, on every entry
// path. The letter in full, scrollable, with one control at the foot.
//
// No decline button, by instruction and by the letter: section 7 and section 12
// both tell a learner who does not wish to take part to close the page. A
// Decline button would have to do something, and every option is worse than the
// letter's own instruction. Letting them through without consent would collect
// data they refused; sending them to a dead end screen is a worse experience
// than closing a tab; and returning them to the landing page invites a loop in
// which Continue is the only way out, which makes it coercive rather than
// voluntary.
//
// The button is NOT disabled until the learner scrolls to the bottom. A
// scroll-gate is theatre: it measures scrolling, not reading, and it blocks the
// reader who already knows the letter while doing nothing about the one who
// drags the scrollbar. Section 12 states plainly what Continue means, and it
// sits directly above the button where it cannot be missed.
// The app frame the consent modal sits on. Deliberately inert: a header and a
// background, nothing to click. It exists so the letter reads as a dialog
// inside KiRPA rather than as a different site, and it is NOT the real shell,
// because the real shell is the profile form and the case list, neither of
// which should render before consent.
function ConsentBackdropFrame(){
  return (
    <div aria-hidden="true" style={{ position:"fixed", inset:0,
      background:T.bg, fontFamily:T.font, overflow:"hidden" }}>
      <div style={{ background:"white", borderBottom:`1px solid ${T.border}`,
        padding:"13px 20px", display:"flex", alignItems:"center", gap:"12px" }}>
        <div style={{ fontSize:"17px", fontWeight:"900", color:T.accent,
          letterSpacing:"-0.02em" }}>KiRPA</div>
        <div style={{ fontSize:"11px", color:T.muted, fontFamily:T.mono,
          letterSpacing:"0.12em", textTransform:"uppercase" }}>
          Kidney Replacement Prescription Analytics
        </div>
      </div>
    </div>
  );
}

// The letter, as a modal. Scrolls inside its own card so the page behind does
// not move, and so the card keeps its shape on a phone as well as a desktop.
function ConsentScreen({ busy, err, onContinue }){
  const cardRef   = useRef(null);
  const scrollRef = useRef(null);
  // Has the letter been scrolled to the end? Starts false and is corrected on
  // mount, because on a tall screen the letter may not scroll at all, and a
  // reader who cannot scroll must not be told to.
  const [atEnd, setAtEnd]   = useState(false);
  const [nudge, setNudge]   = useState(false);

  // Move focus into the dialog on mount, so a keyboard or screen-reader user
  // lands on the letter rather than somewhere behind it. preventScroll keeps
  // the card at the top: focusing it must not skip the reader past the first
  // paragraph.
  useEffect(() => {
    try { cardRef.current?.focus({ preventScroll:true }); } catch {}
  }, []);

  // One check, used on mount, on scroll and on resize. The 24px tolerance
  // covers sub-pixel rounding and the elastic overscroll some browsers apply,
  // either of which can leave scrollTop a fraction short of the true bottom
  // and strand a reader who has genuinely finished.
  const checkEnd = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const done = el.scrollHeight - el.scrollTop - el.clientHeight <= 24;
    if (done) { setAtEnd(true); setNudge(false); }
  }, []);

  // On mount, and whenever the window resizes: a letter shorter than the card
  // never fires a scroll event, so without this the reader would be told to
  // scroll through content that is already fully visible.
  useEffect(() => {
    checkEnd();
    window.addEventListener("resize", checkEnd);
    return () => window.removeEventListener("resize", checkEnd);
  }, [checkEnd]);

  // The page behind must not scroll while the letter is open, or a phone will
  // rubber-band the frame instead of the card.
  useEffect(() => {
    let prev;
    try { prev = document.body.style.overflow; document.body.style.overflow = "hidden"; } catch {}
    return () => { try { document.body.style.overflow = prev || ""; } catch {} };
  }, []);

  return (
    <>
      <ConsentBackdropFrame/>

      {/* No onClick on the backdrop: there is nothing to dismiss to. */}
      <div style={{ position:"fixed", inset:0, zIndex:300,
        background:"rgba(15,23,42,0.55)", display:"flex",
        alignItems:"center", justifyContent:"center",
        padding:"18px", fontFamily:T.font }}>

        <div ref={cardRef} tabIndex={-1} role="dialog" aria-modal="true"
          aria-labelledby="kirpa-loi-heading"
          style={{ background:"white", borderRadius:"16px",
            width:"100%", maxWidth:"660px", maxHeight:"90vh",
            display:"flex", flexDirection:"column", outline:"none",
            boxShadow:"0 24px 60px rgba(0,0,0,0.35)" }}>

          {/* Header stays put while the letter scrolls under it, so Download
              and Print remain reachable without scrolling back up. */}
          <div style={{ display:"flex", alignItems:"center", gap:"10px",
            padding:"15px 20px", borderBottom:`1px solid ${T.border}`,
            flexWrap:"wrap", flexShrink:0 }}>
            <div style={{ fontSize:"10px", fontWeight:"700", color:T.accent,
              letterSpacing:"0.14em", textTransform:"uppercase" }}>
              Letter of Information and Consent
            </div>
            {/* Secondary actions, so they are sized as such. They matter for
                the one reader who wants a copy and should not compete with the
                letter for the rest. */}
            <div style={{ marginInlineStart:"auto", display:"flex", gap:"5px" }}>
              {[["Download", loiDownload], ["Print", () => window.print()]].map(([label, fn]) => (
                <button key={label} onClick={fn}
                  style={{ background:"none", border:`1px solid ${T.border}`,
                    borderRadius:"6px", padding:"3px 8px", cursor:"pointer",
                    color:T.muted, fontSize:"10px", fontWeight:"600",
                    fontFamily:"inherit", whiteSpace:"nowrap", lineHeight:"1.5" }}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* The scroll container. The letter is long by nature, so it scrolls
              here rather than moving the whole page. */}
          <div ref={scrollRef} onScroll={checkEnd}
            style={{ overflowY:"auto", padding:"20px 24px 18px",
              WebkitOverflowScrolling:"touch" }}>
            <LoiBody/>
          </div>

          {/* Pinned footer, outside the scroll container, so Continue is on
              screen from the first moment. */}
          <div style={{ flexShrink:0, padding:"13px 24px 16px",
            borderTop:`1px solid ${T.border}` }}>

            {err && (
              <div style={{ marginBottom:"11px", padding:"10px 13px",
                borderRadius:"9px", background:"#fef2f2",
                border:"1px solid #fecaca", fontSize:"12.5px", color:"#b91c1c",
                lineHeight:"1.65" }}>
                {err}
              </div>
            )}

            {/* Shown only after Continue is pressed early, and it clears itself
                the moment the reader reaches the end. role="status" rather than
                "alert": it is a correction, not an emergency, and an assertive
                announcement would cut across whatever the reader is on. */}
            {nudge && !atEnd && (
              <div role="status" style={{ marginBottom:"11px",
                padding:"10px 13px", borderRadius:"9px", background:"#fffbeb",
                border:"1px solid #fde68a", fontSize:"12.5px", color:"#92400e",
                lineHeight:"1.6" }}>
                Please read the entire Letter of Information before continuing.
              </div>
            )}

            <button
              onClick={() => {
                if (busy) return;
                // Not disabled when unread: a dead button explains nothing, and
                // a keyboard user could not reach it to find out why.
                if (!atEnd) {
                  setNudge(true);
                  // Nudge the view too, so the instruction and the thing it
                  // asks for arrive together.
                  try {
                    scrollRef.current?.scrollBy({ top: 240, behavior:"smooth" });
                  } catch { try { scrollRef.current.scrollTop += 240; } catch {} }
                  return;
                }
                onContinue();
              }}
              aria-disabled={busy || !atEnd}
              style={{ ...C.btn(C.pu), width:"100%", justifyContent:"center",
                padding:"14px", fontSize:"15px",
                // Visibly not ready yet, but still live and still focusable.
                opacity: busy ? 0.6 : (atEnd ? 1 : 0.55),
                cursor: busy ? "wait" : "pointer" }}>
              {busy ? <Ic n="spin" s="w-4 h-4"/> : null}
              {busy ? "Recording\u2026" : "Continue to KiRPA"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}


// ─── PHASE 2 CONSENT GATE ────────────────────────────────────────────────────
// Stands in front of the case runner. Renders the letter if this subject has no
// consent record, records consent on Continue, then lets the case through.
//
// Two sources of truth, in order:
//
//   1. The server flag from the session-start callable. Authoritative, and the
//      only thing that works across devices for an account-based learner.
//   2. A localStorage copy, consulted only for the anonymous and device
//      subjects. It exists because joinAssignmentSession mints a FRESH
//      anonymousId on every join, so an anonymous learner rejoining a session
//      is a new subject to the server and would meet the letter again every
//      time without it.
//
// The localStorage copy has one known consequence worth stating: it is scoped
// to the device, not to the person. On a device shared between learners, for
// example one tablet passed around a workshop running a QR session, the second
// learner skips the letter. The short-circuit below still records consent for
// their own subject, so their work submits normally, but they will not have
// read the letter. Narrowing this means either asking every returning
// anonymous learner again, or keying the copy to the join code; both are small
// changes, and which is right is a question for the REB rather than for me.
const PHASE2_CONSENT_KEY = "kirpa_phase2_consent";

function readLocalConsent(){
  try {
    const c = JSON.parse(window.localStorage.getItem(PHASE2_CONSENT_KEY) || "null");
    return (c && c.version === LOI_VERSION) ? c : null;
  } catch { return null; }
}

function writeLocalConsent(){
  try {
    window.localStorage.setItem(PHASE2_CONSENT_KEY,
      JSON.stringify({ version: LOI_VERSION, at: new Date().toISOString() }));
  } catch {
    // Private browsing with storage blocked. The server record still stands,
    // so nothing breaks; the learner may just meet the letter again next visit.
  }
}

// Rendered as an EARLY RETURN at the top of each entry path, before the profile
// form and before any case. Not as a wrapper: a wrapper can only sit where the
// element it wraps already sits, which put it after the profile screen and
// inside a layout column.
//
// The caller's test is simply `consented !== true`. Everything else is decided
// here, so there is one place to read:
//
//   local copy says yes, server says no  -> record for THIS subject, then open
//   server has not answered yet          -> wait, do not flash the letter
//   otherwise                            -> the letter
//
// entryPath: "assignment" | "qr_session" | "practice"
// identity:  { token } for the two token paths, { deviceId } for practice.
// consented: the server flag, or null when the caller has no answer yet.
// onConsented: called once consent is on file; the caller flips its own flag,
//   this unmounts, and the path renders normally.
function Phase2ConsentGate({ entryPath, identity, consented, onConsented }){
  // The device-scoped copy is only trusted for the subjects it can identify.
  // An account subject spans devices and is settled by the server flag alone,
  // so a stale local copy must never speak for one.
  const localOk = (entryPath === "practice" || entryPath === "qr_session")
    ? !!readLocalConsent() : false;

  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState("");

  // Short-circuited by the local copy, but the server has no record for THIS
  // subject: a fresh anonymous join, most often. Record it, so the writer does
  // not refuse the submission at the end of the case. Fire and forget, because
  // a learner who already consented on this device should not be made to wait.
  const recordedRef = useRef(false);
  useEffect(() => {
    if (!(localOk && consented === false) || recordedRef.current) return;
    recordedRef.current = true;
    (async () => {
      try {
        const fns = getFunctions(firebaseApp, "us-central1");
        await httpsCallable(fns, "recordPhase2Consent")({ entryPath, ...identity });
        onConsented?.();
      } catch (e) { console.warn("[KiRPA consent] backfill", e); }
    })();
    // identity and onConsented are deliberately absent: both are fresh
    // references on every render of the caller, and recordedRef is what makes
    // this fire once. Listing them would re-run the effect on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localOk, consented, entryPath]);

  // consented === null means the session-start callable has not answered yet.
  // Rendering the letter in that window would flash it at a learner who has
  // already consented and then snatch it away when the flag arrives, which
  // reads as a glitch and invites a reflexive click on a screen that is
  // supposed to be read. So wait briefly, then fall through to the letter:
  // showing it is always the safe direction, because recordPhase2Consent is
  // idempotent and a second Continue cannot move the recorded date.
  const [waited, setWaited] = useState(false);
  useEffect(() => {
    if (consented !== null && consented !== undefined) return;
    const h = setTimeout(() => setWaited(true), 2500);
    return () => clearTimeout(h);
  }, [consented]);

  // Defensive: callers test `consented !== true` before rendering this, so
  // true should never reach here. If it does, show nothing rather than the
  // letter. A learner who has consented being asked again is the one failure
  // mode of this screen that looks like a bug to them and like a second
  // consent event in the data.
  //
  // Then: waiting on the backfill triggered by the local copy, or on the
  // server's answer. In neither case is the letter shown yet, because in the
  // first this subject has already consented on this device and in the second
  // we do not yet know.
  if (consented === true || localOk ||
      ((consented === null || consented === undefined) && !waited)) {
    // The same frame the modal sits on, so the moment the letter appears
    // nothing jumps: the header is already in place and only the card and its
    // backdrop fade in over it.
    return (
      <>
        <ConsentBackdropFrame/>
        <div style={{ position:"fixed", inset:0, zIndex:300, display:"flex",
          alignItems:"center", justifyContent:"center", fontFamily:T.font }}>
          <div style={{ fontSize:"11.5px", color:T.muted }}>Loading…</div>
        </div>
      </>
    );
  }

  return (
    <ConsentScreen busy={busy} err={err} onContinue={async () => {
      setBusy(true); setErr("");
      try {
        const fns = getFunctions(firebaseApp, "us-central1");
        const res = await httpsCallable(fns, "recordPhase2Consent")({
          entryPath, ...identity });
        if (!res?.data?.ok) throw new Error("Consent was not recorded.");
        writeLocalConsent();
        // The caller flips its own flag, which unmounts this and renders the
        // path. No local `done` state: two sources of truth for the same
        // question is how the first version ended up able to disagree with
        // itself.
        onConsented?.();
      } catch (e) {
        // The case is NOT opened on a failed write. Letting it through would
        // collect data with no consent record behind it, and the server would
        // refuse the submission at the end of the case anyway, losing the
        // learner's work instead of three seconds of their time.
        setErr((e?.message || "Could not record your consent.") +
          " Nothing has been recorded. Please check your connection and try again.");
        setBusy(false);
      }
    }}/>
  );
}

// ─── THE LETTER, AS A ROUTE ──────────────────────────────────────────────────
// Static, read-only, no interaction. Reached at /loi from the notice, and also
// linked from the expert validation module header as reference.
//
// Printable on purpose: section 11 of the letter tells the reader to keep or
// print a copy, so the page has to actually print well. A plain document
// layout with no app chrome does that without a print stylesheet.
function LetterOfInformation({ onExit }){
  const download = loiDownload;

  return (
    <div style={{ minHeight:"100vh", background:"white", fontFamily:T.font }}>
      <div style={{ maxWidth:"72ch", margin:"0 auto", padding:"36px 22px 80px" }}>

        <div style={{ display:"flex", alignItems:"center", gap:"12px",
          marginBottom:"26px", flexWrap:"wrap" }}>
          <div style={{ fontSize:"17px", fontWeight:"900", color:"#4F2683",
            letterSpacing:"-0.02em" }}>KiRPA</div>
          <div style={{ marginInlineStart:"auto", display:"flex", gap:"8px" }}>
            <button onClick={download}
              style={{ background:"none", border:`1px solid ${T.border}`,
                borderRadius:"8px", padding:"7px 13px", cursor:"pointer",
                color:T.accent, fontSize:"12px", fontWeight:"650",
                fontFamily:"inherit" }}>
              Download a copy
            </button>
            <button onClick={() => window.print()}
              style={{ background:"none", border:`1px solid ${T.border}`,
                borderRadius:"8px", padding:"7px 13px", cursor:"pointer",
                color:T.accent, fontSize:"12px", fontWeight:"650",
                fontFamily:"inherit" }}>
              Print
            </button>
            {onExit && (
              <button onClick={onExit}
                style={{ background:"none", border:"none", padding:"7px 6px",
                  cursor:"pointer", color:T.muted, fontSize:"12px",
                  fontWeight:"600", fontFamily:"inherit" }}>
                Close
              </button>
            )}
          </div>
        </div>

        <LoiBody/>
      </div>
    </div>
  );
}

function PracticeSession({onExit}){
  const {t,locale,setLocale}=useLocale();
  const [deviceId]        = useState(()=>getDeviceId());

  useEffect(()=>{
    let urlLang=null;
    try{
      const p=new URLSearchParams(window.location.search);
      urlLang=p.get("lang");
    }catch{}
    if(!urlLang && locale!==DEFAULT_LOCALE) setLocale(DEFAULT_LOCALE);
    // Runs once on entry: an explicit switch inside the session is respected.
  },[]);
  const [profile,setProfile]   = useState(null);   // null until gate passed
  const [busy,setBusy]         = useState(false);
  const [completed,setCompleted]           = useState([]);
  const [attemptsByCase,setAttemptsByCase] = useState({});
  // Admin-published edits to the built-in cases, served by startPracticeSession
  // through the Admin SDK. Practice learners cannot read /shared themselves.
  const [caseOverrides,setCaseOverrides]   = useState({});
  // Published community cases, kept in a separate collection from the numbered
  // core set so the Room sequence stays stable for every learner.
  const [communityCases,setCommunityCases] = useState({});
  // Named caseSet, not collection, so it cannot shadow the Firestore
  // collection() import if a query is ever added to this component.
  const [caseSet,setCaseSet]               = useState("core"); // "core" | "community"
  // Two-panel above 1024px; below that the wall and the chart are separate views.
  const wide = useViewport("(min-width: 1024px)");
  // Translations for every locale, served by startPracticeSession through the
  // Admin SDK because case_translations is educator-gated in Firestore.
  const [caseTranslations,setCaseTranslations] = useState({});
  const [availLocales,setAvailLocales]         = useState(["en"]);
  const [activeId,setActiveId] = useState("");
  const [showEmail,setShowEmail]=useState(false);
  const [nudge,setNudge]        = useState(false);   // brief pulse on the save button
  const [showReport,setShowReport]=useState(false);
  const [resumeChoice,setResumeChoice]=useState(null); // null | "asked" | "resume" | "restart"
  // A claimed but unconfirmed email. The learner is never blocked by it.
  const [linkPending,setLinkPending]  = useState(false);
  const [hasEmail,setHasEmail] = useState(false);
  // null until startPracticeSession answers, so the gate does not flash the
  // letter at a learner who has already consented. null means "not known yet",
  // which the gate treats as not-consented only once it has nothing better.
  const [phase2Consented,setPhase2Consented] = useState(null);
  const [declines,setDeclines] = useState(0);
  const [err,setErr]           = useState("");

  // Built-in cases, with admin overrides applied so practice shows exactly what
  // the master admin has published. Nested initialState is merged rather than
  // replaced, matching the educator-side merge, so an override that touches only
  // labs does not wipe vitals.
  const cases = (() => {
    const out = {};
    Object.entries(BASE_CASES).forEach(([id,c])=>{
      const ov = caseOverrides[id];
      if(!ov){ out[id]=c; return; }
      const mergedIS = (ov.initialState||c.initialState) ? {
        ...(c.initialState||{}), ...(ov.initialState||{}),
        vitals:(ov.initialState?.vitals||c.initialState?.vitals)?{...(c.initialState?.vitals||{}),...(ov.initialState?.vitals||{})}:undefined,
        labs:(ov.initialState?.labs||c.initialState?.labs)?{...(c.initialState?.labs||{}),...(ov.initialState?.labs||{})}:undefined,
        gas:(ov.initialState?.gas||c.initialState?.gas)?{...(c.initialState?.gas||{}),...(ov.initialState?.gas||{})}:undefined,
        cbc:(ov.initialState?.cbc||c.initialState?.cbc)?{...(c.initialState?.cbc||{}),...(ov.initialState?.cbc||{})}:undefined,
      } : undefined;
      if(mergedIS) Object.keys(mergedIS).forEach(k=>mergedIS[k]===undefined&&delete mergedIS[k]);
      out[id] = {...c, ...ov, ...(mergedIS?{initialState:mergedIS}:{})};
    });
    return out;
  })();
  const coreOrder = Object.keys(BASE_CASES);

  // The active collection. Community cases are browsable in any order — they are
  // not a curriculum, so numbering them would imply a sequence that is not there.
  const isCommunity = caseSet === "community";
  const communityOrder = Object.keys(communityCases);
  const activeCases = isCommunity ? communityCases : cases;
  const order = isCommunity ? communityOrder : coreOrder;

  // Resume: try to restore an existing device session before showing the gate.
  useEffect(()=>{let dead=false;(async()=>{
    try{
      const fns=getFunctions(firebaseApp,"us-central1");
      const res=await httpsCallable(fns,"startPracticeSession")({deviceId,locale});
      if(dead) return;
      const d=res?.data||{};
      if(d.role && resumeChoice!=="restart"){
        setProfile({role:d.role,country:d.country});
        setCompleted(d.completed||[]);
        setAttemptsByCase(d.attemptsByCase||{});
        setHasEmail(!!d.hasEmail);
        setDeclines(d.emailDeclined||0);
      }
      if(d.caseOverrides) setCaseOverrides(d.caseOverrides);
      if(d.communityCases) setCommunityCases(d.communityCases);
      if(d.caseTranslations) setCaseTranslations(d.caseTranslations);
      if(d.availableLocales?.length) setAvailLocales(d.availableLocales);
      if(d.linkPending) setLinkPending(true);
      // Has this device, or the account it has since linked, already consented?
      if(typeof d.phase2Consented === "boolean") setPhase2Consented(d.phase2Consented);
    }catch(e){ console.warn("[KiRPA practice] resume:",e); }
  })();return()=>{dead=true;};},[deviceId]);

  const start=async({role,country})=>{
    setBusy(true);setErr("");
    try{
      const fns=getFunctions(firebaseApp,"us-central1");
      const res=await httpsCallable(fns,"startPracticeSession")({deviceId,role,country,locale});
      const d=res?.data||{};
      setProfile({role:d.role||role,country:d.country||country});
      setCompleted(d.completed||[]);
      setAttemptsByCase(d.attemptsByCase||{});
      setHasEmail(!!d.hasEmail);
      setDeclines(d.emailDeclined||0);
      if(d.caseOverrides) setCaseOverrides(d.caseOverrides);
      if(d.communityCases) setCommunityCases(d.communityCases);
      if(d.caseTranslations) setCaseTranslations(d.caseTranslations);
      if(d.availableLocales?.length) setAvailLocales(d.availableLocales);
      if(d.linkPending) setLinkPending(true);
      if(typeof d.phase2Consented === "boolean") setPhase2Consented(d.phase2Consented);
      // Coming through the gate is an explicit choice, so the resume prompt is
      // already answered and must not reappear.
      setResumeChoice("resume");
    }catch(e){
      setErr(e?.message||"Unable to start. Please try again.");
    }
    setBusy(false);
  };

  // Default order: first uncompleted room.
  const nextId = coreOrder.find(id=>!completed.includes(id)) || "";

  // First-attempt rate across core cases, computed locally so the summary gauge
  // renders immediately rather than waiting on the history call.
  // MUST sit below coreOrder: this evaluates during render, so referencing it
  // any earlier hits the temporal dead zone and blanks the screen.
  const summaryPct = (()=>{
    const done  = completed.filter(id=>coreOrder.includes(id)).length;
    const tried = coreOrder.filter(id=>(attemptsByCase[id]||0)>0).length;
    return tried ? Math.round(done/tried*100) : 0;
  })();

  // Shape a built-in case as an assignment so the shared runner accepts it.
  // maxAttempts is very high rather than infinite so the existing attempt
  // plumbing keeps working unchanged.
  const asAssignment = (id) => {
    const c = activeCases[id];
    if (!c) return null;   // id belongs to the other collection; nothing to show

    // Apply the translation for the current language. Source-language cases are
    // left untouched: a case authored in French and read in French is original
    // text, not a translation, so it carries no disclaimer.
    const src = c.sourceLocale || "en";
    const tr  = (locale !== src) ? (caseTranslations[locale]||{})[id] : null;
    const f   = tr?.fields || {};
    const localised = tr ? {
      ...c,
      ...(f.title          ? {title:          f.title}          : {}),
      ...(f.page           ? {page:           f.page}           : {}),
      ...(f.successSummary ? {successSummary: f.successSummary} : {}),
      ...(f.learningPoint  ? {learningPoint:  f.learningPoint}  : {}),
    } : c;

    return {
      id: `practice_${id}`,
      caseId: id,
      // Core cases are numbered Rooms; community cases keep their own titles,
      // since they are not part of the sequence.
      caseTitle: isCommunity
        ? (localised.title || "Case")
        : `${t("prac.room","Room")} ${order.indexOf(id)+1}`,
      caseFacet: "",                 // never surfaced to the learner
      educatorName: "",
      feedbackMode: "immediate",
      feedbackReleased: true,
      status: completed.includes(id) ? "completed" : "pending",
      attempts: attemptsByCase[id]||0,
      maxAttempts: 999,
      caseOptions: c.caseOptions||null,
      caseOverride: localised,
      locale, sourceLocale: src,
      isTranslated: !!tr,
      translationStatus: tr?.status || null,
      translatorName: tr?.translatorName || null,
      translationStale: false,
      allowLocaleOverride: true,
      _practice: true,
      _deviceId: deviceId,
      _practiceFacet: c.facet||"",   // server-side only, for domain analytics
      _followedDefaultOrder: isCommunity ? false : id===nextId,
      _isCommunity: isCommunity,
      // Untranslated source text, so the server can hash it and detect a
      // translation that has drifted from the case it was made from.
      _sourceFields: {
        title:          c.title          || "",
        page:           c.page           || "",
        successSummary: c.successSummary || "",
        learningPoint:  c.learningPoint  || "",
      },
    };
  };

  // Local progress only. The research event is written by the shared runner.
  const noteResult = (id,result) => {
    setAttemptsByCase(p=>({...p,[id]:(p[id]||0)+1}));
    if(result?.success && !completed.includes(id)) setCompleted(p=>[...p,id]);

    // Completing a case draws attention to the save button rather than
    // interrupting with a dialog.
    if(result?.success && !hasEmail && !linkPending){
      setNudge(true);
      setTimeout(()=>setNudge(false), 5200);
    }
  };

  // A returning learner is asked once, before anything else, whether to pick up
  // where they left off or wipe the slate. Restarting clears local progress and
  // returns them to the role/country gate; it does NOT delete research events,
  // which are an immutable record of what actually happened.
  // ── Consent, before anything else on this path ───────────────────────────
  // Ahead of the role and country form, which startPracticeSession writes to
  // practice_devices, and ahead of the resume prompt. Rendered here it is a
  // full-screen page; wrapped around the case runner it appeared inside the
  // right-hand column of the practice shell, beside the case list.
  if(phase2Consented !== true){
    return <Phase2ConsentGate
      entryPath="practice"
      identity={{ deviceId }}
      consented={phase2Consented}
      onConsented={()=>setPhase2Consented(true)}/>;
  }

  if(profile && resumeChoice===null && (completed.length>0 || Object.keys(attemptsByCase).length>0)){
    return <div style={{minHeight:"100vh",background:T.bg,display:"flex",
      alignItems:"center",justifyContent:"center",padding:"20px",fontFamily:FONT_UI}}>
      <div style={{background:"white",borderRadius:"16px",padding:"28px",maxWidth:"420px",
        width:"100%",border:`1px solid ${T.border}`,boxShadow:"0 12px 40px rgba(79,38,131,0.12)"}}>
        <h2 style={{margin:"0 0 8px",fontSize:"18px",fontWeight:"700",color:T.text}}>
          {t("prac.welcomeBack","Welcome back")}
        </h2>
        <p style={{margin:"0 0 20px",fontSize:"13px",color:T.muted,lineHeight:"1.6"}}>
          {t("prac.resumeBlurb","You have {n} of {total} cases completed. Continue where you left off, or start over?",
             {n:completed.length,total:coreOrder.length})}
        </p>
        <button onClick={()=>setResumeChoice("resume")}
          style={{...C.btn(C.pu),width:"100%",justifyContent:"center",padding:"12px",
            fontSize:"14px",marginBottom:"9px"}}>
          {t("prac.resume","Resume")}
        </button>
        <button onClick={()=>{
            setCompleted([]); setAttemptsByCase({}); setActiveId("");
            setProfile(null); setResumeChoice("restart");
          }}
          style={{...C.btn("#f3f4f6","#374151"),width:"100%",justifyContent:"center",padding:"11px",
            fontSize:"13px"}}>
          {t("prac.startOver","Start over")}
        </button>
        <p style={{margin:"14px 0 0",fontSize:"11px",color:"#9ca3af",lineHeight:"1.55",textAlign:"center"}}>
          {t("prac.restartNote","Starting over clears your progress display and asks for your role again. Previously recorded responses are not removed.")}
        </p>
      </div>
    </div>;
  }

  if(!profile) return <>
    <PracticeGate onStart={start} busy={busy}/>
    {err&&<div style={{position:"fixed",bottom:"20px",left:"50%",transform:"translateX(-50%)",
      padding:"10px 16px",background:"#fef2f2",border:"1px solid #fecaca",borderRadius:"9px",
      color:"#dc2626",fontSize:"12px",fontFamily:FONT_UI}}>{err}</div>}
  </>;

  // Dark console shell. The monitors need to sit on a ward wall, not float on
  // white — the surrounding value is what makes the traces read as instruments.
  return <div style={{minHeight:"100vh",background:T.bg,fontFamily:FONT_UI}}>
    <div style={{background:"white",borderBottom:`1px solid ${T.border}`,padding:"13px 20px",
      display:"flex",alignItems:"center",gap:"12px"}}>
      <div style={{fontSize:"17px",fontWeight:"900",color:T.accent,letterSpacing:"-0.02em"}}>KiRPA</div>
      <div style={{fontSize:"11px",color:T.muted,fontFamily:FONT_MONO,
        letterSpacing:"0.12em",textTransform:"uppercase"}}>{t("prac.practiceMode","Practice")}</div>
      <div style={{marginInlineStart:"auto",display:"flex",alignItems:"center",gap:"10px"}}>
        <LocaleSwitcherLight codes={availLocales}/>
        <button onClick={onExit} style={{background:"white",
          border:`1px solid ${T.border}`,borderRadius:"8px",padding:"6px 11px",cursor:"pointer",
          fontSize:"12px",color:T.muted,fontFamily:"inherit"}}>{t("prac.exit","Exit")}</button>
      </div>
    </div>

    <div style={{maxWidth:wide?"1400px":"820px",margin:"0 auto",
      padding:wide?"20px 20px 50px":"18px 14px 50px",
      display:wide?"grid":"block",
      gridTemplateColumns:wide?"minmax(300px,380px) 1fr":undefined,
      gap:wide?"20px":undefined,alignItems:wide?"start":undefined}}>

    {/* ── Left rail: monitors and progress. On a narrow screen this is the whole
        page, and opening a case replaces it. ───────────────────────────────── */}
    <div style={{display:(!wide&&activeId)?"none":"block",
      position:wide?"sticky":"static",top:wide?"18px":undefined,
      maxHeight:wide?"calc(100vh - 40px)":undefined,
      overflowY:wide?"auto":undefined,paddingInlineEnd:wide?"4px":undefined}}>

      {/* First-attempt rate at a glance, with the full report one tap away.
          Replaces the old My progress tab: the number is the thing worth
          seeing, and hiding it behind a tab meant most learners never did. */}
      {completed.length>0 && <div style={{background:"white",borderRadius:"14px",
        border:`1px solid ${T.border}`,padding:"14px 16px",marginBottom:"12px",
        display:"flex",alignItems:"center",gap:"14px",
        boxShadow:"0 1px 3px rgba(15,23,42,0.05)"}}>
        <div style={{flexShrink:0}}><RadialScore percentage={summaryPct} size={58}/></div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:"12.5px",fontWeight:"750",color:T.text,marginBottom:"2px"}}>
            {t("dash.overall","Overall first-attempt rate")}
          </div>
          <div style={{fontSize:"11.5px",color:T.muted,lineHeight:"1.5"}}>
            {t("dash.summarySub","{n} of {m} core cases completed",
              {n:completed.filter(id=>coreOrder.includes(id)).length,m:coreOrder.length})}
          </div>
        </div>
        <button onClick={()=>setShowReport(true)}
          style={{flexShrink:0,padding:"7px 13px",borderRadius:"9px",
            border:`1px solid ${T.accent}`,background:T.accentLight,color:T.accentDark,
            fontSize:"11.5px",fontWeight:"700",cursor:"pointer",fontFamily:"inherit"}}>
          {t("dash.expand","Expand")}
        </button>
      </div>}

      {!hasEmail&&completed.length>0&&
        <button onClick={()=>setShowEmail(true)}
          className={nudge?"kirpa-nudge":undefined}
          style={{width:"100%",padding:"10px",marginBottom:"14px",borderRadius:"9px",
            border:`1px ${nudge?"solid":"dashed"} ${nudge?T.accentMid:T.borderStrong}`,
            background:nudge?T.accentLight:"white",
            color:nudge?T.accentDark:T.muted,
            fontSize:"12px",cursor:"pointer",fontFamily:"inherit",
            transition:"background 0.5s, border-color 0.5s, color 0.5s"}}>
          {t("prac.saveLater","Save your progress with an email →")}
        </button>}

      {/* Collection switch. Community cases sit alongside the core set rather
          than inside it, so the numbered sequence stays identical for everyone. */}
      <div style={{display:"flex",gap:"6px",marginBottom:"14px"}}>
        {[["core",t("prac.coreCases","Core cases"),coreOrder.length],
          ["community",t("prac.communityCases","Community cases"),communityOrder.length]
        ].map(([key,label,count])=>{
          const on = caseSet===key;
          return <button key={key} onClick={()=>{setCaseSet(key);setActiveId("");}}
            style={{flex:1,padding:"9px 12px",borderRadius:"10px",cursor:"pointer",
              fontFamily:"inherit",fontSize:"12.5px",fontWeight:on?"700":"500",
              border:`1px solid ${on?T.accent:T.border}`,
              background:on?T.accentLight:"white",
              color:on?T.accentDark:T.text,
              display:"flex",alignItems:"center",justifyContent:"center",gap:"7px"}}>
            {label}
            {count!==null&&<span style={{fontSize:"11px",opacity:0.65,
              fontVariantNumeric:"tabular-nums"}}>{count}</span>}
          </button>;
        })}
      </div>

      {isCommunity && communityOrder.length > 0 && (
        <div style={{padding:"11px 14px",marginBottom:"14px",borderRadius:"10px",
          background:"#fffbeb",border:"1px solid #fde68a",fontSize:"12px",
          color:"#92400e",lineHeight:"1.6"}}>
          {t("prac.communityNote","These cases were written by other educators using KiRPA. They have not been through the same review as the core set, so the scenarios and answer criteria may vary in style and rigour.")}
        </div>
      )}

      <PracticeMonitorWall order={order} cases={activeCases} completed={completed}
        attemptsByCase={attemptsByCase} nextId={nextId} numbered={!isCommunity}
        onOpen={setActiveId} activeId={activeId}/>
    </div>

    {/* ── Right panel: the patient chart ─────────────────────────────────────
        On mobile this takes over the screen and the back control returns to the
        wall. On desktop the wall stays visible beside it, so no back control is
        needed and switching patients is a single click. */}
    <div style={{display:(wide||activeId)?"block":"none",minWidth:0}}>
      {activeId && activeCases[activeId]
        ? <>
            {!wide && <button onClick={()=>setActiveId("")}
              style={{marginBottom:"12px",padding:"8px 13px",borderRadius:"9px",
                border:`1px solid ${T.border}`,background:"white",
                color:T.muted,fontSize:"12.5px",fontWeight:"600",cursor:"pointer",
                fontFamily:"inherit"}}>
              ← {t("prac.allMonitors","All monitors")}
            </button>}
            <TraineeAssignmentExperience
              key={activeId}
              embedded hideBack={wide}
              session={{trainee:{role:profile.role},payload:{},locale}}
              assignment={asAssignment(activeId)}
              caseIndex={order.indexOf(activeId)}
              totalCases={order.length}
              onBack={()=>setActiveId("")}
              onNext={()=>{
                const i=order.indexOf(activeId);
                setActiveId(i+1<order.length?order[i+1]:"");
              }}
              onUpdateAssignment={(updated)=>{
                // The runner has already written the research event; this only
                // refreshes local progress so the wall and bar update at once.
                if(updated?.lastResult) noteResult(activeId,updated.lastResult);
              }}
            />
          </>
        : <div style={{padding:"56px 28px",textAlign:"center",background:"white",
            border:`1px dashed ${T.borderStrong}`,borderRadius:"14px"}}>
            <div style={{fontSize:"30px",marginBottom:"12px",opacity:0.3}}>🖥️</div>
            <div style={{fontSize:"14px",fontWeight:"700",color:T.text,marginBottom:"6px"}}>
              {t("prac.selectCase","Select a patient to begin")}
            </div>
            <div style={{fontSize:"12.5px",color:T.muted,lineHeight:"1.6",maxWidth:"320px",
              margin:"0 auto"}}>
              {t("prac.selectCaseBlurb","Choose a monitor to read the handover and write a prescription.")}
            </div>
          </div>}
    </div>
    </div>

    {showReport&&<PerformanceReport deviceId={deviceId} hasEmail={hasEmail}
      linkPending={linkPending} onLink={()=>{setShowReport(false);setShowEmail(true);}}
      onClose={()=>setShowReport(false)}/>}

    {showEmail&&<PracticeEmailOffer deviceId={deviceId}
      onDone={()=>{setHasEmail(true);setShowEmail(false);}}
      onSkip={()=>{setDeclines(d=>d+1);setShowEmail(false);}}/>}
  </div>;
}

// Compact locale switcher for light backgrounds.
function LocaleSwitcherLight({codes,dark}){
  const {locale,setLocale}=useLocale();
  // Only languages that actually have translated cases are offered, so the
  // picker never changes the buttons while leaving the vignette in English.
  // Always include English, and always render: a hidden picker makes the
  // language actually in use impossible to confirm.
  const list=[...new Set(["en",...(codes&&codes.length?codes:LOCALE_CODES)])]
    .filter(c=>LOCALES[c]);
  return <select value={locale} onChange={e=>setLocale(e.target.value)}
    style={{padding:"5px 9px",borderRadius:"7px",
      border:`1px solid ${dark?"#3f3f46":T.border}`,
      background:dark?"rgba(255,255,255,0.05)":"white",cursor:"pointer",
      fontSize:"11px",fontWeight:"600",
      color:dark?"#c4b5fd":T.accent,fontFamily:"inherit",outline:"none"}}>
    {list.map(c=><option key={c} value={c} style={{color:"#111"}}>{LOCALES[c].native}</option>)}
  </select>;
}






// ─── CASE ANALYTICS ───────────────────────────────────────────────────────────
// Per-case item analysis. Answers the questions a case bank actually needs:
// which cases are too easy or too hard, which parameter people get wrong, how
// far off the target they land, and whether difficulty tracks patient size.
//
// Reads research_events directly rather than a new callable, since the admin
// dashboard already loads them.

// ─── ERROR TAXONOMY ───────────────────────────────────────────────────────────
// The analysis behind the error-taxonomy paper. Three things the per-case view
// cannot show: how often each error occurs across the whole corpus, whether
// errors travel together, and whether nephrology and critical care fail
// differently.
//
// THE DENOMINATOR IS THE WHOLE METHOD. Each error flag is one of:
//   null    the case never tested this parameter   -> excluded entirely
//   false   tested, acceptable                     -> denominator only
//   string  tested, wrong, with a direction        -> numerator and denominator
// Dividing by total attempts instead of in-scope attempts would understate
// every rate by a different amount per error, which is the usual way this
// analysis goes wrong.

const ERR_FIELDS = [
  ["error_clearance",    "Effluent dose"],
  ["error_netuf",        "Net ultrafiltration"],
  ["error_bfr",          "Blood flow"],
  ["error_anticoag",     "Anticoagulant choice"],
  ["error_solution",     "Solution choice"],
  ["error_access",       "Catheter"],
  ["error_filter",       "Filter"],
  ["error_prime",        "Prime"],
  ["error_modality",     "Modality"],
  ["error_k_add",        "Potassium replacement"],
  ["error_po4_add",      "Phosphate replacement"],
  ["error_na_add",       "Sodium additive"],
  ["error_free_water",   "Free water additive"],
  ["error_intervention", "Bedside action"],
];

const DIR_LABEL = {
  too_high:     "too high",
  too_low:      "too low",
  wrong_choice: "wrong choice",
  missing:      "omitted",
  unnecessary:  "not indicated",
};

// Validated categorical slots (blue, orange): CVD ΔE 24.7 protan, 33.6 normal.
const VIZ = {
  s1:      "#2a78d6",   // nephrology
  s2:      "#eb6834",   // critical care
  seq:     "#2a78d6",   // single-series magnitude
  divPos:  "#d03b3b",   // phi > 0, errors co-occur
  divNeg:  "#2a78d6",   // phi < 0, errors exclude each other
  divMid:  "#f0efec",   // neutral midpoint, never a hue
  grid:    "#e8e8e6",
};

// Wilson score interval. Chosen over the normal approximation because several
// errors will be rare and several cases will have small in-scope denominators,
// where the normal interval misbehaves and can cross zero.
function wilson(k, n, z = 1.96) {
  if (!n) return [0, 0];
  const p = k / n, z2 = z * z;
  const d = 1 + z2 / n;
  const c = p + z2 / (2 * n);
  const h = z * Math.sqrt(p * (1 - p) / n + z2 / (4 * n * n));
  return [Math.max(0, (c - h) / d), Math.min(1, (c + h) / d)];
}

// Phi (mean square contingency) on the subset where BOTH flags were in scope.
// Restricting to that subset is what stops a correlation being manufactured by
// two errors simply appearing on the same cases.
function phi(a, b) {
  let n11 = 0, n10 = 0, n01 = 0, n00 = 0;
  for (let i = 0; i < a.length; i++) {
    if (a[i] === null || b[i] === null) continue;
    if (a[i] && b[i]) n11++;
    else if (a[i] && !b[i]) n10++;
    else if (!a[i] && b[i]) n01++;
    else n00++;
  }
  const n = n11 + n10 + n01 + n00;
  const den = Math.sqrt((n11 + n10) * (n01 + n00) * (n11 + n01) * (n10 + n00));
  if (!den || n < 10) return { phi: null, n };
  return { phi: (n11 * n00 - n10 * n01) / den, n };
}

function ErrorTaxonomy({ events, embedded }) {
  const [track, setTrack]       = useState("all");
  const [firstOnly, setFirst]   = useState(true);
  const [unaidedOnly, setUn]    = useState(true);
  const [prescribersOnly, setPx]= useState(false);
  const [mode, setMode]         = useState("all");   // prescribe from scratch vs edit
  const [view, setView]         = useState("freq");

  // The default analysis set is first attempts, unaided: a case solved after
  // three hints is not evidence of the same competence, and a retry is not an
  // independent observation.
  //
  // When embedded in the harmonised section those decisions have already been
  // made once, by the shared filter bar, so the rows arrive filtered and this
  // local pass is skipped. Applying both would silently intersect two filter
  // sets and make the denominator impossible to state.
  const rows = embedded ? (events || []) : (events || []).filter(e => {
    if (track !== "all" && (e.track || "practice") !== track) return false;
    if (firstOnly   && e.is_first_attempt === false) return false;
    if (unaidedOnly && e.used_consult) return false;
    if (prescribersOnly && e.is_prescriber !== true) return false;
    if (mode === "new"    && (e.case_scenario || "") !== "new") return false;
    if (mode === "modify" && (e.case_scenario || "") === "new") return false;
    return true;
  });

  // A flag is in scope when it is not null. false is in scope and acceptable.
  const inScope = (v) => v !== null && v !== undefined;
  const isErr   = (v) => inScope(v) && v !== false;

  const freq = ERR_FIELDS.map(([f, label]) => {
    const scoped = rows.filter(e => inScope(e[f]));
    const errs   = scoped.filter(e => e[f] !== false);
    const n = scoped.length, k = errs.length;
    const rate = n ? k / n : 0;
    const [lo, hi] = wilson(k, n);
    const dirs = {};
    errs.forEach(e => { const d = String(e[f]); dirs[d] = (dirs[d] || 0) + 1; });
    return { f, label, n, k, rate, lo, hi, dirs };
  }).filter(r => r.n > 0).sort((a, b) => b.rate - a.rate);

  // Nephrology vs critical care, among prescribers only: dose and
  // anticoagulation decisions are not meaningful for roles that do not write
  // the prescription.
  const bySpecialty = ERR_FIELDS.map(([f, label]) => {
    const mk = (sp) => {
      const sc = rows.filter(e => e.specialty === sp && e.is_prescriber === true && inScope(e[f]));
      const k  = sc.filter(e => e[f] !== false).length;
      const [lo, hi] = wilson(k, sc.length);
      return { n: sc.length, k, rate: sc.length ? k / sc.length : 0, lo, hi };
    };
    return { f, label, neph: mk("nephrology"), icu: mk("critical_care") };
  }).filter(r => r.neph.n >= 10 && r.icu.n >= 10)
    .sort((a, b) => Math.abs(b.neph.rate - b.icu.rate) - Math.abs(a.neph.rate - a.icu.rate));

  // Co-occurrence, restricted to fields with enough in-scope observations.
  const coFields = freq.filter(r => r.n >= 20).slice(0, 10);
  const vectors  = {};
  coFields.forEach(({ f }) => {
    vectors[f] = rows.map(e => (inScope(e[f]) ? (e[f] !== false ? 1 : 0) : null));
  });
  const matrix = coFields.map(a => coFields.map(b =>
    a.f === b.f ? { phi: 1, n: null } : phi(vectors[a.f], vectors[b.f])));

  const pct = (x) => (x * 100).toFixed(1);

  const exportCSV = () => {
    const lines = [["error","label","in_scope_n","errors_k","rate","ci_low","ci_high",
                    ...Object.keys(DIR_LABEL)].join(",")];
    freq.forEach(r => lines.push([
      r.f, JSON.stringify(r.label), r.n, r.k, r.rate.toFixed(4),
      r.lo.toFixed(4), r.hi.toFixed(4),
      ...Object.keys(DIR_LABEL).map(d => r.dirs[d] || 0),
    ].join(",")));
    lines.push("");
    lines.push(["error","neph_n","neph_k","neph_rate","icu_n","icu_k","icu_rate","diff"].join(","));
    bySpecialty.forEach(r => lines.push([
      r.f, r.neph.n, r.neph.k, r.neph.rate.toFixed(4),
      r.icu.n, r.icu.k, r.icu.rate.toFixed(4),
      (r.neph.rate - r.icu.rate).toFixed(4),
    ].join(",")));
    lines.push("");
    lines.push(["error_a","error_b","phi","n_both_in_scope"].join(","));
    coFields.forEach((a, i) => coFields.forEach((b, j) => {
      if (j <= i) return;
      const c = matrix[i][j];
      if (c.phi === null) return;
      lines.push([a.f, b.f, c.phi.toFixed(4), c.n].join(","));
    }));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `kirpa-error-taxonomy-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const Toggle = ({ on, set, children }) => (
    <button onClick={() => set(v => !v)}
      style={{ padding:"6px 11px", borderRadius:"7px", cursor:"pointer",
        fontSize:"11.5px", fontWeight:"650", fontFamily:"inherit",
        border:`1px solid ${on ? T.accent : T.border}`,
        background: on ? T.accentLight : "white",
        color: on ? T.accentDark : T.muted }}>
      {on ? "✓ " : ""}{children}
    </button>
  );

  const Pick = ({ value, set, opts }) => (
    <div style={{ display:"flex", gap:"4px" }}>
      {opts.map(([v, l]) => (
        <button key={v} onClick={() => set(v)}
          style={{ padding:"6px 11px", borderRadius:"7px", cursor:"pointer",
            fontSize:"11.5px", fontWeight: value === v ? "700" : "500",
            fontFamily:"inherit",
            border:`1px solid ${value === v ? T.accent : T.border}`,
            background: value === v ? T.accentLight : "white",
            color: value === v ? T.accentDark : T.muted }}>{l}</button>
      ))}
    </div>
  );

  if (!rows.length) return (
    <div style={{ padding:"44px", textAlign:"center", color:T.muted, fontSize:"13px" }}>
      No attempts match these filters yet.
    </div>
  );

  return (
    <div style={{ fontFamily:T.font }}>
      {/* Filters in one row above the charts, unless the shared bar above
          already owns them. */}
      <div style={{ display:"flex", gap:"8px", flexWrap:"wrap", alignItems:"center",
        marginBottom:"18px", paddingBottom:"14px",
        borderBottom:embedded ? "none" : `1px solid ${T.border}` }}>
        {!embedded && <>
          <Pick value={track} set={setTrack}
            opts={[["all","Both tracks"],["practice","Open access"],["assigned","Assigned"]]}/>
          <Pick value={mode} set={setMode}
            opts={[["all","All cases"],["new","Prescribe from scratch"],["modify","Edit existing"]]}/>
          <Toggle on={firstOnly} set={setFirst}>First attempts</Toggle>
          <Toggle on={unaidedOnly} set={setUn}>Unaided</Toggle>
          <Toggle on={prescribersOnly} set={setPx}>Prescribers only</Toggle>
        </>}
        <div style={{ marginInlineStart:"auto", display:"flex", gap:"8px", alignItems:"center" }}>
          <span style={{ fontSize:"11.5px", color:T.muted, fontVariantNumeric:"tabular-nums" }}>
            n = {rows.length} attempts
          </span>
          <button onClick={exportCSV} style={{ ...C.btn(C.pu), padding:"7px 13px", fontSize:"11.5px" }}>
            Export CSV
          </button>
        </div>
      </div>

      <div style={{ display:"flex", gap:"4px", marginBottom:"16px" }}>
        <Pick value={view} set={setView} opts={[
          ["freq","Frequency"],["dirs","Direction"],
          ["spec","Nephrology vs ICU"],["co","Co-occurrence"]]}/>
      </div>

      {/* ── Frequency: one series, so no legend; the heading names it. ── */}
      {view === "freq" && (
        <div>
          <div style={{ fontSize:"13.5px", fontWeight:"700", color:T.text, marginBottom:"3px" }}>
            Error rate by prescription parameter
          </div>
          <div style={{ fontSize:"11.5px", color:T.muted, marginBottom:"16px", lineHeight:"1.6" }}>
            Denominator is attempts where the case actually tested that parameter,
            not all attempts. Bars show 95% Wilson intervals.
          </div>
          {freq.map(r => (
            <div key={r.f} style={{ marginBottom:"13px" }}>
              <div style={{ display:"flex", justifyContent:"space-between",
                alignItems:"baseline", marginBottom:"4px" }}>
                <span style={{ fontSize:"12.5px", color:T.text, fontWeight:"600" }}>{r.label}</span>
                <span style={{ fontSize:"11.5px", color:T.muted, fontVariantNumeric:"tabular-nums" }}>
                  {r.k}/{r.n}
                  <strong style={{ color:T.text, marginInlineStart:"9px" }}>{pct(r.rate)}%</strong>
                  <span style={{ marginInlineStart:"7px", color:"#9ca3af" }}>
                    [{pct(r.lo)}–{pct(r.hi)}]
                  </span>
                </span>
              </div>
              <div style={{ position:"relative", height:"14px", background:VIZ.grid,
                borderRadius:"4px", overflow:"visible" }}>
                <div style={{ position:"absolute", insetInlineStart:0, top:0, height:"100%",
                  width:`${r.rate * 100}%`, background:VIZ.seq,
                  borderStartEndRadius:"4px", borderEndEndRadius:"4px" }}/>
                {/* CI whisker in muted ink, never the series colour. */}
                <div style={{ position:"absolute", top:"50%", height:"2px",
                  insetInlineStart:`${r.lo * 100}%`, width:`${(r.hi - r.lo) * 100}%`,
                  background:"rgba(11,11,11,0.45)", transform:"translateY(-50%)" }}/>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Direction: where the typed error schema pays off. ── */}
      {view === "dirs" && (
        <div>
          <div style={{ fontSize:"13.5px", fontWeight:"700", color:T.text, marginBottom:"3px" }}>
            Direction of error
          </div>
          <div style={{ fontSize:"11.5px", color:T.muted, marginBottom:"16px", lineHeight:"1.6" }}>
            Dosing too high and dosing too low are different errors with different
            causes. Counts, not rates.
          </div>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:"12px" }}>
            <thead>
              <tr style={{ borderBottom:`2px solid ${T.border}` }}>
                <th style={{ textAlign:"start", padding:"7px 8px", color:T.muted, fontWeight:"650" }}>Parameter</th>
                {Object.entries(DIR_LABEL).map(([k, l]) => (
                  <th key={k} style={{ textAlign:"end", padding:"7px 8px", color:T.muted, fontWeight:"650" }}>{l}</th>
                ))}
                <th style={{ textAlign:"end", padding:"7px 8px", color:T.muted, fontWeight:"650" }}>in scope</th>
              </tr>
            </thead>
            <tbody>
              {freq.map(r => (
                <tr key={r.f} style={{ borderBottom:`1px solid ${T.border}` }}>
                  <td style={{ padding:"7px 8px", color:T.text, fontWeight:"600" }}>{r.label}</td>
                  {Object.keys(DIR_LABEL).map(d => (
                    <td key={d} style={{ padding:"7px 8px", textAlign:"end",
                      color: r.dirs[d] ? T.text : "#d1d5db", fontVariantNumeric:"tabular-nums" }}>
                      {r.dirs[d] || "–"}
                    </td>
                  ))}
                  <td style={{ padding:"7px 8px", textAlign:"end", color:T.muted,
                    fontVariantNumeric:"tabular-nums" }}>{r.n}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Nephrology vs ICU: two series, so a legend is required. ── */}
      {view === "spec" && (
        <div>
          <div style={{ fontSize:"13.5px", fontWeight:"700", color:T.text, marginBottom:"3px" }}>
            Nephrology compared with critical care
          </div>
          <div style={{ fontSize:"11.5px", color:T.muted, marginBottom:"12px", lineHeight:"1.6" }}>
            Prescribers only, since dose and anticoagulation decisions are not
            meaningful for roles that do not write the prescription. Parameters
            with fewer than 10 in-scope attempts in either group are omitted.
            Ordered by size of difference.
          </div>
          <div style={{ display:"flex", gap:"16px", marginBottom:"16px" }}>
            {[["Nephrology", VIZ.s1], ["Critical care", VIZ.s2]].map(([l, c]) => (
              <span key={l} style={{ display:"flex", alignItems:"center", gap:"6px",
                fontSize:"11.5px", color:T.muted }}>
                <span style={{ width:"11px", height:"11px", borderRadius:"3px", background:c }}/>
                {l}
              </span>
            ))}
          </div>
          {bySpecialty.length === 0 && (
            <div style={{ padding:"28px", textAlign:"center", color:T.muted, fontSize:"12.5px" }}>
              Not enough prescriber attempts in both groups yet.
            </div>
          )}
          {bySpecialty.map(r => (
            <div key={r.f} style={{ marginBottom:"15px" }}>
              <div style={{ display:"flex", justifyContent:"space-between",
                alignItems:"baseline", marginBottom:"5px" }}>
                <span style={{ fontSize:"12.5px", color:T.text, fontWeight:"600" }}>{r.label}</span>
                <span style={{ fontSize:"11px", color:T.muted, fontVariantNumeric:"tabular-nums" }}>
                  difference {((r.neph.rate - r.icu.rate) * 100).toFixed(1)} points
                </span>
              </div>
              {[[r.neph, VIZ.s1, "Neph"], [r.icu, VIZ.s2, "ICU"]].map(([g, col, lab]) => (
                <div key={lab} style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"3px" }}>
                  <span style={{ width:"32px", fontSize:"10px", color:T.muted }}>{lab}</span>
                  <div style={{ position:"relative", flex:1, height:"11px",
                    background:VIZ.grid, borderRadius:"4px" }}>
                    <div style={{ position:"absolute", insetInlineStart:0, top:0, height:"100%",
                      width:`${g.rate * 100}%`, background:col,
                      borderStartEndRadius:"4px", borderEndEndRadius:"4px" }}/>
                  </div>
                  <span style={{ width:"96px", textAlign:"end", fontSize:"11px",
                    color:T.muted, fontVariantNumeric:"tabular-nums" }}>
                    {pct(g.rate)}% ({g.k}/{g.n})
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* ── Co-occurrence: phi runs -1..+1, so the ramp diverges around a
          neutral grey. A sequential ramp here would hide the sign. ── */}
      {view === "co" && (
        <div>
          <div style={{ fontSize:"13.5px", fontWeight:"700", color:T.text, marginBottom:"3px" }}>
            Do errors travel together?
          </div>
          <div style={{ fontSize:"11.5px", color:T.muted, marginBottom:"14px", lineHeight:"1.6" }}>
            Phi coefficient, computed only on attempts where both parameters were
            in scope, so a correlation cannot be manufactured by two errors
            appearing on the same cases. Cells with fewer than 10 shared
            observations are blank.
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:"10px", marginBottom:"14px",
            fontSize:"10.5px", color:T.muted }}>
            <span>exclusive &minus;1</span>
            <span style={{ width:"130px", height:"10px", borderRadius:"3px",
              background:`linear-gradient(90deg, ${VIZ.divNeg}, ${VIZ.divMid}, ${VIZ.divPos})` }}/>
            <span>+1 together</span>
          </div>
          <div style={{ overflowX:"auto" }}>
            <table style={{ borderCollapse:"separate", borderSpacing:"2px", fontSize:"10.5px" }}>
              <tbody>
                {coFields.map((a, i) => (
                  <tr key={a.f}>
                    <td style={{ padding:"0 9px 0 0", textAlign:"end", color:T.text,
                      fontWeight:"600", whiteSpace:"nowrap", fontSize:"11px" }}>{a.label}</td>
                    {coFields.map((b, j) => {
                      const c = matrix[i][j];
                      const v = c.phi;
                      const bg = v === null ? "#f8f8f7"
                        : v >= 0 ? `color-mix(in oklab, ${VIZ.divPos} ${Math.round(Math.abs(v) * 100)}%, ${VIZ.divMid})`
                                 : `color-mix(in oklab, ${VIZ.divNeg} ${Math.round(Math.abs(v) * 100)}%, ${VIZ.divMid})`;
                      return (
                        <td key={b.f} title={`${a.label} × ${b.label}${v === null ? " (too few)" : `: phi ${v.toFixed(2)}, n ${c.n}`}`}
                          style={{ width:"42px", height:"30px", textAlign:"center",
                            background:bg, borderRadius:"4px",
                            color: v !== null && Math.abs(v) > 0.45 ? "#fff" : T.text,
                            fontVariantNumeric:"tabular-nums" }}>
                          {v === null ? "" : i === j ? "—" : v.toFixed(2)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                <tr>
                  <td/>
                  {coFields.map(b => (
                    <td key={b.f} style={{ fontSize:"9px", color:T.muted, textAlign:"center",
                      paddingTop:"5px", lineHeight:"1.25" }}>
                      {b.label.split(" ")[0]}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function CaseAnalytics({events, embedded}){
  const [openCase,setOpenCase] = useState(null);
  const [track,setTrack] = useState("all");

  // Embedded: the shared filter bar has already selected the rows, including
  // the track. Filtering again here would intersect two selections.
  const rows = embedded ? (events || []) : events.filter(e=>
    track==="all" ? true : track==="assigned" ? e.track==="assigned" : e.track!=="assigned");

  // First attempts only: repeated tries at one case would otherwise make a
  // hard case look easy once people grind it out.
  const firsts = rows.filter(e=>e.is_first_attempt !== false);

  const byCase = {};
  firsts.forEach(e=>{
    const id = e.case_id_readable || "(unknown)";
    if(!byCase[id]) byCase[id] = {
      id, n:0, pass:0, errs:{}, deltas:[], secs:[], attemptsTotal:0,
      changedNothing:0, changedCounts:[], byWeight:{}, byRole:{},
      targetMetric:null, targetValue:null, scenario:null, facet:null,
    };
    const c = byCase[id];
    c.n++;
    if(e.overall_success) c.pass++;
    c.facet = c.facet || e.case_facet;
    c.scenario = c.scenario || e.case_scenario;
    c.targetMetric = c.targetMetric || e.case_target_metric;
    c.targetValue  = c.targetValue  ?? e.case_target_value;
    if(typeof e.case_target_delta_prop === "number") c.deltas.push(e.case_target_delta_prop);
    if(typeof e.time_on_case_seconds === "number") c.secs.push(e.time_on_case_seconds);
    if(e.changed_nothing === true) c.changedNothing++;
    // Unaided and hinted attempts are counted separately. Pooling them would
    // make a hard case look easier than it is.
    const hinted = !!e.used_consult;
    if(!c.unaided) { c.unaided = {n:0,pass:0}; c.hinted = {n:0,pass:0}; c.depths = []; }
    const bucket = hinted ? c.hinted : c.unaided;
    bucket.n++; if(e.overall_success) bucket.pass++;
    if(hinted && typeof e.consult_depth === "number") c.depths.push(e.consult_depth);
    if(typeof e.params_changed_count === "number") c.changedCounts.push(e.params_changed_count);
    Object.entries(e).forEach(([k,v])=>{
      if(k.startsWith("error_") && v !== null && v !== false){
        c.errs[k] = (c.errs[k]||0) + 1;
      }
    });
    const wb = e.pt_weight_band || "unknown";
    if(!c.byWeight[wb]) c.byWeight[wb] = {n:0,pass:0};
    c.byWeight[wb].n++; if(e.overall_success) c.byWeight[wb].pass++;
    const rl = e.trainee_role_primary || e.trainee_role || "unspecified";
    const rk = Array.isArray(rl) ? (rl[0]||"unspecified") : rl;
    if(!c.byRole[rk]) c.byRole[rk] = {n:0,pass:0};
    c.byRole[rk].n++; if(e.overall_success) c.byRole[rk].pass++;
  });

  // Total attempts per case, across all attempts not just firsts.
  rows.forEach(e=>{ const c = byCase[e.case_id_readable]; if(c) c.attemptsTotal++; });

  const cases = Object.values(byCase)
    // Headline difficulty uses UNAIDED attempts where there are any: a case
    // that only passes with hints is a hard case, and the band should say so.
    .map(c=>({...c,
      pct: (c.unaided?.n ? Math.round(c.unaided.pass/c.unaided.n*100)
                         : (c.n ? Math.round(c.pass/c.n*100) : 0)),
      medDelta: median(c.deltas), medSecs: median(c.secs),
      meanChanged: c.changedCounts.length
        ? +(c.changedCounts.reduce((a,b)=>a+b,0)/c.changedCounts.length).toFixed(1) : null,
      attemptsPer: c.n ? +(c.attemptsTotal/c.n).toFixed(2) : null}))
    .sort((a,b)=>a.pct-b.pct);

  if(!cases.length) return <div style={{textAlign:"center",padding:"50px",color:"#9ca3af"}}>
    <div style={{fontSize:"36px",marginBottom:"10px",opacity:0.3}}>🧪</div>
    <div style={{fontWeight:"700",color:"#374151",marginBottom:"5px"}}>No case data yet</div>
    <p style={{fontSize:"12px",margin:0}}>Populates as soon as anyone completes a case.</p>
  </div>;

  const Band = ({pct}) => {
    // Classical item-analysis bands. Below 30 percent and above 90 percent an
    // item discriminates poorly: nearly everyone fails, or nearly everyone
    // passes, so it separates no one.
    const lab = pct<30 ? "too hard" : pct>90 ? "too easy" : "discriminating";
    const col = pct<30 ? "#be123c" : pct>90 ? "#b45309" : "#047857";
    return <span style={{fontSize:"10px",fontWeight:"700",color:col,
      background:`${col}14`,border:`1px solid ${col}33`,borderRadius:"8px",
      padding:"2px 8px",whiteSpace:"nowrap"}}>{lab}</span>;
  };

  return <div>
    <div style={{display:"flex",gap:"6px",marginBottom:"14px"}}>
      {!embedded && [["all","All"],["practice","Self-study"],["assigned","Assigned"]].map(([k,l])=>{
        const on=track===k;
        return <button key={k} onClick={()=>setTrack(k)}
          style={{padding:"6px 13px",borderRadius:"9px",cursor:"pointer",fontFamily:"inherit",
            fontSize:"12px",fontWeight:on?"700":"500",
            border:`1px solid ${on?C.pu:"#e5e7eb"}`,
            background:on?"#f3effe":"white",color:on?C.pu:"#374151"}}>{l}</button>;
      })}
      <span style={{marginInlineStart:"auto",fontSize:"11.5px",color:"#9ca3af",
        alignSelf:"center"}}>
        {firsts.filter(e=>!e.used_consult).length} unaided first attempts
        {" "}({firsts.length} total) across {cases.length} cases
      </span>
    </div>

    {cases.map(c=>{
      const open = openCase===c.id;
      const errs = Object.entries(c.errs).sort((a,b)=>b[1]-a[1]).slice(0,5);
      return <div key={c.id} style={{background:"white",borderRadius:"12px",
        border:`1px solid ${open?C.pu:"#e5e7eb"}`,marginBottom:"8px",overflow:"hidden"}}>
        <button onClick={()=>setOpenCase(open?null:c.id)}
          style={{width:"100%",padding:"13px 16px",background:"none",border:"none",
            cursor:"pointer",fontFamily:"inherit",textAlign:"start",
            display:"flex",alignItems:"center",gap:"12px"}}>
          <span style={{fontSize:"13.5px",fontWeight:"750",color:C.dark,minWidth:"72px"}}>
            {c.id}
          </span>
          <span style={{fontSize:"11.5px",color:"#9ca3af",flex:1,minWidth:0,
            overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
            {BASE_CASES[c.id]?.title||c.facet||""}
          </span>
          <Band pct={c.pct}/>
          <span style={{fontSize:"12px",color:"#6b7280",fontVariantNumeric:"tabular-nums",
            flexShrink:0}}>
            {c.pass}/{c.n}
            <strong style={{marginInlineStart:"7px",fontSize:"13.5px",
              color:c.pct>=85?"#047857":c.pct>=50?"#b45309":"#be123c"}}>{c.pct}%</strong>
          </span>
          <span style={{color:"#9ca3af",fontSize:"11px",flexShrink:0}}>{open?"▾":"▸"}</span>
        </button>

        {open && <div style={{borderTop:"1px solid #e5e7eb",padding:"14px 16px",
          background:"#faf8ff"}}>
          <div style={{display:"grid",gap:"8px",marginBottom:"14px",
            gridTemplateColumns:"repeat(auto-fit,minmax(118px,1fr))"}}>
            <Stat label="Attempts / learner" value={c.attemptsPer ?? "—"}/>
            <Stat label="Median time" value={c.medSecs!=null?`${Math.round(c.medSecs/60)}m`:"—"}/>
            <Stat label="Params changed" value={c.meanChanged ?? "—"}/>
            <Stat label="Changed nothing" value={`${c.changedNothing}`}/>
          </div>

          {c.targetMetric && c.medDelta!=null && (
            <div style={{padding:"11px 13px",borderRadius:"9px",background:"white",
              border:"1px solid #e5e7eb",marginBottom:"12px"}}>
              <div style={{fontSize:"9.5px",fontWeight:"700",color:"#9ca3af",
                textTransform:"uppercase",letterSpacing:"0.12em",marginBottom:"4px"}}>
                Distance from target
              </div>
              <div style={{fontSize:"12.5px",color:C.dark,lineHeight:"1.6"}}>
                Target {c.targetValue} ({c.targetMetric.replace(/_/g," ")}).
                Median submission was{" "}
                <strong style={{color:c.medDelta<0?"#be123c":"#b45309"}}>
                  {c.medDelta>0?"+":""}{Math.round(c.medDelta*100)}%
                </strong>{" "}
                {c.medDelta<0?"below":"above"} it.
              </div>
            </div>
          )}

          {/* Unaided versus hinted. The gap between the two is the most direct
              read on how much scaffolding a case needs. */}
          {(c.hinted?.n > 0 || c.unaided?.n > 0) && (
            <div style={{padding:"11px 13px",borderRadius:"9px",background:"white",
              border:"1px solid #e5e7eb",marginBottom:"12px"}}>
              <div style={{fontSize:"9.5px",fontWeight:"700",color:"#9ca3af",
                textTransform:"uppercase",letterSpacing:"0.12em",marginBottom:"8px"}}>
                With and without a consult
              </div>
              {/* Unaided is the primary analysis set. Hinted attempts are kept
                  and reported, but never pooled with unaided ones: the second
                  tier gives away most of the answer, so a hinted pass is not
                  evidence of the same competence. */}
              {[["Unaided (primary)",c.unaided],["Used a consult",c.hinted]].map(([lbl,b])=>{
                if(!b || !b.n) return null;
                const p = Math.round(b.pass/b.n*100);
                return <div key={lbl} style={{marginBottom:"7px"}}>
                  <div style={{display:"flex",justifyContent:"space-between",
                    fontSize:"11.5px",marginBottom:"3px"}}>
                    <span style={{color:"#374151"}}>{lbl}</span>
                    <span style={{color:"#9ca3af",fontVariantNumeric:"tabular-nums"}}>
                      {b.pass}/{b.n} · {p}%
                    </span>
                  </div>
                  <div style={{height:"5px",borderRadius:"3px",background:"#efe9fb",
                    overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${p}%`,borderRadius:"3px",
                      background:p>=85?"#047857":p>=50?"#b45309":"#be123c"}}/>
                  </div>
                </div>;
              })}
              {c.depths?.length>0 && (
                <div style={{fontSize:"11px",color:"#9ca3af",marginTop:"6px"}}>
                  Median hints taken: {median(c.depths)} of 2
                </div>
              )}
            </div>
          )}

          {errs.length>0 && <div style={{marginBottom:"12px"}}>
            <div style={{fontSize:"9.5px",fontWeight:"700",color:"#9ca3af",
              textTransform:"uppercase",letterSpacing:"0.12em",marginBottom:"7px"}}>
              Where it goes wrong
            </div>
            <div style={{display:"flex",flexWrap:"wrap",gap:"6px"}}>
              {errs.map(([k,n])=>(
                <span key={k} style={{fontSize:"11px",padding:"3px 9px",borderRadius:"8px",
                  background:"#fef2f2",border:"1px solid #fecaca",color:"#991b1b"}}>
                  {(ERR_LABEL[k]||k.replace("error_","").replace(/_/g," "))}
                  <span style={{marginInlineStart:"5px",opacity:0.7}}>
                    {n} ({Math.round(n/c.n*100)}%)
                  </span>
                </span>
              ))}
            </div>
          </div>}

          <div style={{display:"grid",gap:"12px",
            gridTemplateColumns:"repeat(auto-fit,minmax(210px,1fr))"}}>
            <Breakdown title="By patient size" data={c.byWeight}/>
            <Breakdown title="By role" data={c.byRole}/>
          </div>
        </div>}
      </div>;
    })}
  </div>;
}

function Breakdown({title,data}){
  const rows = Object.entries(data).filter(([,v])=>v.n>0)
    .sort((a,b)=>b[1].n-a[1].n);
  if(!rows.length) return null;
  return <div>
    <div style={{fontSize:"9.5px",fontWeight:"700",color:"#9ca3af",
      textTransform:"uppercase",letterSpacing:"0.12em",marginBottom:"7px"}}>{title}</div>
    {rows.map(([k,v])=>{
      const p = Math.round(v.pass/v.n*100);
      return <div key={k} style={{marginBottom:"6px"}}>
        <div style={{display:"flex",justifyContent:"space-between",fontSize:"11px",
          marginBottom:"3px"}}>
          <span style={{color:"#374151"}}>{String(k).replace(/_/g," ")}</span>
          <span style={{color:"#9ca3af",fontVariantNumeric:"tabular-nums"}}>
            {v.pass}/{v.n} · {p}%
          </span>
        </div>
        <div style={{height:"5px",borderRadius:"3px",background:"#efe9fb",overflow:"hidden"}}>
          <div style={{height:"100%",width:`${p}%`,borderRadius:"3px",
            background:p>=85?"#047857":p>=50?"#b45309":"#be123c"}}/>
        </div>
      </div>;
    })}
  </div>;
}

const median = (arr)=>{
  const a = arr.filter(v=>typeof v==="number" && isFinite(v)).sort((x,y)=>x-y);
  if(!a.length) return null;
  return a.length%2 ? a[(a.length-1)/2] : (a[a.length/2-1]+a[a.length/2])/2;
};

// ─── VALIDATION ADMIN TAB ─────────────────────────────────────────────────────
// Invite experts, open round 2, and read the results. Assignment is typed in
// rather than generated: stratifying so every case sees both a nephrologist
// and an intensivist is a judgement call, and random allocation will not do it.

// ─── ANALYSIS PLAN ───────────────────────────────────────────────────────────
// Static reference: which model each outcome is analysed with and which fields
// it needs. Lifted out of the admin tab strip so it can sit inside the
// harmonised section as a panel — it is read when writing a methods section,
// not while looking at data, so it does not deserve a tab of its own.
function ResearchPlan(){
  return <div style={{fontFamily:FONT_UI}}>
      {[
        {
          title:"Primary Outcomes",
          color:"#4f2683",bg:"#faf5ff",border:"#c4b5fd",
          items:[
            {outcome:"Overall pass rate",stat:"Mixed-effects logistic regression (GLMM). Fixed: domain, role, attempt#, prescribing_mode, case_is_custom. Random: learner_id, institution_id.",vars:"overall_success, clinical_domain_primary, trainee_role, attempt_number, prescribing_mode, case_is_custom, learner_id, institution_id"},
            {outcome:"First-attempt success rate",stat:"Same GLMM as above, filtered to attempt_number = 1. Key validity metric: reflects immediate competence without feedback.",vars:"first_attempt_success, all above"},
            {outcome:"Learning curve across attempts",stat:"Bayesian Knowledge Tracing (BKT) per learner × domain. Estimates prior knowledge, learn rate, guess, slip. Requires ≥3 attempts per learner per domain.",vars:"learner_id, clinical_domain_primary, attempt_number, overall_success, submitted_date_utc"},
          ]
        },
        {
          title:"Usage & Reach",
          color:"#0891b2",bg:"#ecfeff",border:"#a5f3fc",
          items:[
            {outcome:"Platform growth over time",stat:"Descriptive time-series. Count unique learner_id, institution_id, educator_id by month. No inferential test required initially.",vars:"learner_id, institution_id, educator_id (hashed), submitted_date_utc"},
            {outcome:"Case utilization",stat:"Frequency counts of case_id_readable, case_facet, case_is_custom. Rank by send volume and completion rate.",vars:"case_id_readable, case_facet, case_is_custom, assignment_sent events"},
            {outcome:"Assignment type patterns",stat:"Descriptive. Proportion of timed vs untimed, anonymous vs identified, feedback mode distribution, case selection mode.",vars:"assignment_is_timed, assignment_is_anon, feedback_mode, selMode"},
          ]
        },
        {
          title:"Domain-Specific Performance",
          color:"#059669",bg:"#f0fdf4",border:"#bbf7d0",
          items:[
            {outcome:"Pass rate by domain",stat:"One-way ANOVA or Kruskal-Wallis across 5 domains. Post-hoc pairwise comparisons with Bonferroni correction.",vars:"overall_success, clinical_domain_primary"},
            {outcome:"Wrong choices leading to failure",stat:"Frequency tables of rx_anticoag, rx_modality, rx_access, rx_dose_direction among failed submissions, stratified by domain. Chi-square for association.",vars:"overall_success=false, rx_anticoag, rx_modality, rx_access, rx_dose_direction, clinical_domain_primary"},
            {outcome:"Dose accuracy (effluent domain)",stat:"Continuous: mean ± SD of rx_dose_vs_target_pct. Distribution of dose_direction. Paired t-test comparing first vs last attempt.",vars:"rx_effluent_dose_l_bsa_hr, rx_dose_vs_target_pct, rx_dose_direction, attempt_number"},
          ]
        },
        {
          title:"Prescriber Role Stratification",
          color:"#d97706",bg:"#fffbeb",border:"#fde68a",
          items:[
            {outcome:"Pass rate by trainee role",stat:"GLMM with trainee_role as fixed effect (reference: Paediatric Nephrology Fellow). Adjust for domain and institution.",vars:"overall_success, trainee_role, clinical_domain_primary, institution_id"},
            {outcome:"Role × Domain interaction",stat:"Two-way interaction term in GLMM. Tests whether domain difficulty differs by role.",vars:"overall_success, trainee_role × clinical_domain_primary interaction"},
            {outcome:"Prescription variable distributions by role",stat:"For new-prescription cases: compare rx_modality, rx_anticoag, rx_effluent_dose_l_bsa_hr across roles. ANOVA for continuous, chi-square for categorical.",vars:"trainee_role, rx_*, prescribing_mode=new"},
          ]
        },
        {
          title:"Case Validity (Construct Validity)",
          color:"#dc2626",bg:"#fff5f5",border:"#fecaca",
          items:[
            {outcome:"Known-groups validity",stat:"Compare pass rates between roles expected to differ (e.g. consultant vs student). Significant difference supports construct validity. Mann-Whitney U or t-test.",vars:"overall_success, trainee_role, case_id"},
            {outcome:"Item difficulty estimation",stat:"p-value (proportion passing) per case. Cases with p<0.30 or p>0.90 reviewed for revision. Rasch model if n>200 per case.",vars:"overall_success, case_id_readable, attempt_number=1 (first only)"},
            {outcome:"Domain clustering",stat:"Tetrachoric correlation matrix of domain_*_tested flags × overall_success. Exploratory factor analysis if n>300. Tests whether domains tap distinct competencies.",vars:"domain_*_tested, overall_success, case_id, learner_id"},
            {outcome:"Educator agreement (content validity — future)",stat:"Inter-rater reliability of educator case ratings (difficulty, domain classification). Krippendorff's α or ICC.",vars:"Requires future educator rating data collection"},
          ]
        },
        {
          title:"Data Quality & Collection Notes",
          color:"#374151",bg:"#f9fafb",border:"#e5e7eb",
          items:[
            {outcome:"Missing trainee_role",stat:"Inspect % of events with trainee_role = null. If >20%, role capture mechanism needs improvement. Exclude null-role records from role-stratified analyses.",vars:"trainee_role"},
            {outcome:"Minimal data cleaning required",stat:"All fields pre-typed at write time. Categorical fields use controlled vocabularies. Hashed IDs prevent merging of PII. The only join key is SHA-256(traineeEmail) = learner_id.",vars:"All fields"},
            {outcome:"Recommended analysis software",stat:"R (lme4 for GLMM, BayesKT package for BKT, ggplot2 for visualization). Python (lifelines for survival, scikit-learn for clustering). SPSS for descriptive tables.",vars:"N/A"},
          ]
        },
      ].map(({title,color,bg,border,items})=>(
        <div key={title} style={{...C.card,padding:"0",marginBottom:"14px",border:`1px solid ${border}`,overflow:"hidden"}}>
          <div style={{padding:"10px 16px",background:bg,borderBottom:`1px solid ${border}`}}>
            <div style={{fontWeight:"800",fontSize:"13px",color}}>{title}</div>
          </div>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:"11px"}}>
            <thead><tr style={{background:"#f9fafb",borderBottom:"1px solid #e5e7eb"}}>
              <th style={{padding:"7px 14px",textAlign:"left",color:"#6b7280",fontWeight:"700",fontSize:"10px",textTransform:"uppercase",width:"22%"}}>Outcome</th>
              <th style={{padding:"7px 14px",textAlign:"left",color:"#6b7280",fontWeight:"700",fontSize:"10px",textTransform:"uppercase",width:"45%"}}>Statistical Analysis</th>
              <th style={{padding:"7px 14px",textAlign:"left",color:"#6b7280",fontWeight:"700",fontSize:"10px",textTransform:"uppercase"}}>Key Variables</th>
            </tr></thead>
            <tbody>
              {items.map(({outcome,stat,vars},i)=>(
                <tr key={i} style={{borderBottom:"1px solid #f3f4f6"}}>
                  <td style={{padding:"8px 14px",fontWeight:"700",color:C.dark,verticalAlign:"top"}}>{outcome}</td>
                  <td style={{padding:"8px 14px",color:"#374151",verticalAlign:"top",lineHeight:"1.5"}}>{stat}</td>
                  <td style={{padding:"8px 14px",color:"#6b7280",fontFamily:"monospace",fontSize:"10px",verticalAlign:"top",lineHeight:"1.6"}}>{vars}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>;
}

// ═══════════════════════════════════════════════════════════════════════════
//  HARMONISED DATA & ANALYSIS
//
//  One filter bar, one dataset, every analysis below it. The eight separate
//  admin tabs each held their own filter state, which meant a question like
//  "how do nephrology fellows do on anticoagulation cases in AR" had to be
//  re-specified in four places and could not be answered consistently in any
//  of them.
//
//  The filters are the study's design variables, so they are declared once
//  here and every panel reads the same filtered rows:
//
//    track          who sent the case:  self-directed practice vs educator-assigned
//    surface        where it was run:   2D runner, 3D screen, AR passthrough
//    specialty      nephrology vs critical care (the cross-sectional contrast)
//    prescriber     whether the role writes the prescription at all
//    scenario       prescribe from scratch vs edit a running circuit
//    first/unaided  the attempts that count as independent observations
//
//  Group-by splits every rate in the outcome panels along one of those
//  dimensions, which is what makes "grouped and divided" a single control
//  rather than a different screen per comparison.
// ═══════════════════════════════════════════════════════════════════════════

// Shannon entropy of a count distribution, normalised to its own maximum.
//
//   H  = -Σ p log2 p                 bits
//   H* = H / log2(k)                 0 when everyone chose the same option,
//                                    1 when the choices are spread evenly
//
// Normalising by log2(k) is what makes a 3-option parameter comparable with an
// 8-option one. k is the number of options actually USED, not the number
// available: an unused option contributes nothing to observed variation and
// padding the denominator with it would make genuine consensus look like
// variation simply because the parameter had a long menu.
function entropyNorm(counts) {
  const vals = Object.values(counts).filter(v => v > 0);
  const n = vals.reduce((a, b) => a + b, 0);
  const k = vals.length;
  if (!n || k < 2) return { h: 0, hmax: 0, norm: 0, k, n };
  const h = -vals.reduce((a, v) => { const p = v / n; return a + p * Math.log2(p); }, 0);
  const hmax = Math.log2(k);
  return { h, hmax, norm: hmax > 0 ? h / hmax : 0, k, n };
}

// Continuous prescription values are binned before entropy is taken, because
// 1,980 and 2,000 ml/hr are the same clinical decision and scoring them as
// distinct categories would report near-total disagreement on a parameter
// everyone actually agrees about.
//
// Bin width is the granularity at which the parameter is genuinely decided. For
// the flow rates that is the increment the interface offers. For effluent dose,
// which is derived rather than typed (pre-blood pump + dialysate + replacement
// + net UF, over weight), it is the granularity clinicians discuss the number
// at — 5 ml/kg/hr. Width matters more than it looks: binning counts to the
// nearest multiple puts bin CENTRES on the multiples and BOUNDARIES halfway
// between, so at a width of 10 the boundary fell on 25 ml/kg/hr, precisely the
// dose threshold these cases teach, and split 24 from 26. At 5 it does not.
const VAR_PARAMS = [
  { f:"rx_modality",               label:"Modality",            cat:true },
  { f:"rx_anticoag",               label:"Anticoagulant",       cat:true },
  { f:"rx_solution",               label:"Solution",            cat:true },
  { f:"rx_access",                 label:"Catheter",            cat:true },
  { f:"rx_filter",                 label:"Filter",              cat:true },
  { f:"rx_prime",                  label:"Prime",               cat:true },
  { f:"rx_bfr_ml_min",             label:"Blood flow",          bin:20,  unit:"ml/min" },
  { f:"rx_effluent_dose_ml_kg_hr", label:"Effluent dose",       bin:5,   unit:"ml/kg/hr" },
  { f:"rx_net_ufr_ml_hr",          label:"Net ultrafiltration", bin:25,  unit:"ml/hr" },
  { f:"rx_dialysate_ml_hr",        label:"Dialysate",           bin:200, unit:"ml/hr" },
  { f:"rx_rep_ml_hr",              label:"Replacement",         bin:200, unit:"ml/hr" },
  { f:"rx_pbp_ml_hr",              label:"Pre-blood pump",      bin:100, unit:"ml/hr" },
];

// Which clinical domain each self-reported problem frame points at. This is
// what turns the frame-capture answer into something scorable: a learner who
// named the right domain and still failed made a numerical error, and one who
// named the wrong domain never had the right target. Those are different
// teaching problems and nothing else in the schema separates them.
const FRAME_DOMAIN = {
  dose_low:  "effluent_dose_flow",
  dose_high: "effluent_dose_flow",
  fluid:     "hemodynamics_fluid",
  anticoag:  "anticoagulation",
  circuit:   "access_circuit_alarms",
  solution:  "electrolytes",
  reaction:  "access_circuit_alarms",
  patient:   null,   // not a prescription frame
  unsure:    null,
};
const FRAME_LABEL = {
  dose_low:"Not enough clearance", dose_high:"Too much clearance",
  fluid:"Fluid removal rate", anticoag:"Anticoagulation",
  circuit:"Access or circuit", solution:"Solution composition",
  reaction:"Reaction to the circuit", patient:"Patient factors",
  unsure:"Not sure",
};

const SURFACE_LABEL = { "2d":"2D runner", "3d":"3D screen", "ar":"AR passthrough" };
const TRACK_LABEL   = { practice:"Self-directed", assigned:"Educator-assigned" };
const SPEC_LABEL    = { nephrology:"Nephrology", critical_care:"Critical care",
                        nursing:"Nursing", pharmacy:"Pharmacy",
                        allied_health:"Allied health", other:"Other" };
const BAND_LABEL    = { student:"Medical student", resident:"Resident", fellow:"Fellow",
                        np:"Nurse practitioner", nurse:"Nurse", pharmacist:"Pharmacist",
                        consultant:"Consultant", other:"Other" };

// Rows written before delivery_mode existed are 2D attempts: the playground
// recorded nothing at all until it was wired up, so an absent value cannot
// mean anything else. Reading it through one function keeps that assumption in
// a single place rather than scattered through every panel.
const surfaceOf = (e) => e.delivery_mode || "2d";
const trackOf   = (e) => e.track || "practice";

// The dimensions group-by can split on. Each returns a key and a label, so one
// splitter serves every outcome panel.
const GROUPERS = {
  none:       { label:"Don't split",     key:() => "all",                      name:() => "All attempts" },
  track:      { label:"Track",           key:trackOf,                          name:(k) => TRACK_LABEL[k] || k },
  surface:    { label:"Surface",         key:surfaceOf,                        name:(k) => SURFACE_LABEL[k] || k },
  specialty:  { label:"Specialty",       key:(e) => e.specialty || "unknown",  name:(k) => SPEC_LABEL[k] || "Not recorded" },
  band:       { label:"Training stage",  key:(e) => e.role_band || "unknown",  name:(k) => BAND_LABEL[k] || "Not recorded" },
  scenario:   { label:"Case type",       key:(e) => (e.case_scenario === "new" ? "new" : "modify"),
                name:(k) => (k === "new" ? "Prescribe from scratch" : "Edit a running circuit") },
  custom:     { label:"Case origin",     key:(e) => (e.case_is_custom ? "custom" : "builtin"),
                name:(k) => (k === "custom" ? "Community-authored" : "Built-in (Delphi-validated)") },
  country:    { label:"Country",         key:(e) => e.country || "unknown",    name:(k) => k === "unknown" ? "Not recorded" : k },
  institution:{ label:"Institution",     key:(e) => e.institution_id || "none",
                name:(k) => k === "none" ? "No cohort (self-directed)" : k.slice(0, 10) + "…" },
};

function DataAnalysis({ events, usageEvents, loading }) {
  // ── Shared filter state ───────────────────────────────────────────────────
  const [track,  setTrack]   = useState("all");
  const [surface,setSurface] = useState("all");
  const [spec,   setSpec]    = useState("all");
  const [band,   setBand]    = useState("all");
  const [scen,   setScen]    = useState("all");
  const [origin, setOrigin]  = useState("all");
  const [domain, setDomain]  = useState("all");
  const [country,setCountry] = useState("all");
  const [inst,   setInst]    = useState("all");
  const [since,  setSince]   = useState("all");
  const [pxOnly, setPxOnly]  = useState(false);
  // Defaults that match the analysis plan rather than the largest number. A
  // retry is not an independent observation and a case solved after three
  // hints is not evidence of the same competence, so the dashboard opens on
  // unaided first attempts and says so.
  const [firstOnly, setFirstOnly]   = useState(true);
  const [unaidedOnly, setUnaided]   = useState(true);
  const [group, setGroup]           = useState("none");
  // Lives up here with the other state rather than beside the panel it belongs
  // to: declaring it lower would put a hook below the loading guard, and the
  // hook count has to be identical on every render.
  const [varSuccessOnly, setVarSuccessOnly] = useState(false);

  // Panels. Delphi and the analysis plan start closed: the first is finished
  // work that will not need looking at again, the second is reference.
  const [open, setOpen] = useState({
    cohort:true, success:true, errors:false, variation:false,
    psychometrics:false, framing:false, reach:false, delphi:false, plan:false,
  });
  const toggle = (k) => setOpen(o => ({ ...o, [k]:!o[k] }));

  const all = events || [];

  // Option lists come from the data, so a filter can never offer a value that
  // selects nothing.
  const countries = useMemo(
    () => [...new Set(all.map(e => e.country).filter(Boolean))].sort(), [all]);
  const insts = useMemo(
    () => [...new Set(all.map(e => e.institution_id).filter(Boolean))].sort(), [all]);

  const rows = useMemo(() => {
    const cutoff = since === "all" ? null
      : Date.now() - ({ "30":30, "90":90, "365":365 }[since] || 0) * 86400000;
    return all.filter(e => {
      if (track   !== "all" && trackOf(e)   !== track)   return false;
      if (surface !== "all" && surfaceOf(e) !== surface) return false;
      if (spec    !== "all" && (e.specialty || "unknown") !== spec) return false;
      if (band    !== "all" && (e.role_band || "unknown") !== band) return false;
      if (domain  !== "all" && e.clinical_domain_primary !== domain) return false;
      if (country !== "all" && (e.country || "unknown") !== country) return false;
      if (inst    !== "all" && (e.institution_id || "none") !== inst) return false;
      if (origin  === "builtin" && e.case_is_custom)  return false;
      if (origin  === "custom"  && !e.case_is_custom) return false;
      if (scen === "new"    && (e.case_scenario || "") !== "new") return false;
      if (scen === "modify" && (e.case_scenario || "") === "new") return false;
      if (pxOnly  && e.is_prescriber !== true) return false;
      if (firstOnly   && e.is_first_attempt === false) return false;
      if (unaidedOnly && e.used_consult) return false;
      if (cutoff) {
        const t = Date.parse(e.submitted_at_iso || e.submitted_date_utc || "");
        if (!isFinite(t) || t < cutoff) return false;
      }
      return true;
    });
  }, [all, track, surface, spec, band, scen, origin, domain, country, inst,
      since, pxOnly, firstOnly, unaidedOnly]);

  // ── Splitter, used by every outcome panel ─────────────────────────────────
  const G = GROUPERS[group] || GROUPERS.none;
  const groups = useMemo(() => {
    const m = new Map();
    rows.forEach(e => {
      const k = G.key(e);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(e);
    });
    // Largest first, so the comparison the data can actually support leads.
    return [...m.entries()]
      .sort((a, b) => b[1].length - a[1].length)
      .map(([k, v]) => ({ key:k, label:G.name(k), rows:v }));
  }, [rows, group]);

  // ── Local UI atoms ────────────────────────────────────────────────────────
  const Sel = ({ value, set, opts, label }) => (
    <label style={{ display:"flex", flexDirection:"column", gap:"3px" }}>
      <span style={{ fontSize:"9.5px", fontWeight:"700", color:T.muted,
        letterSpacing:"0.08em", textTransform:"uppercase" }}>{label}</span>
      <select value={value} onChange={e => set(e.target.value)}
        style={{ padding:"6px 9px", borderRadius:"7px", fontSize:"11.5px",
          fontFamily:"inherit", border:`1px solid ${T.border}`, background:"white",
          color:T.text, fontWeight:"600", minWidth:"118px", cursor:"pointer" }}>
        {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );

  const Chip = ({ on, set, children }) => (
    <button onClick={() => set(v => !v)}
      style={{ padding:"6px 11px", borderRadius:"20px", cursor:"pointer",
        fontSize:"11.5px", fontWeight:"650", fontFamily:"inherit",
        border:`1px solid ${on ? T.accent : T.border}`,
        background: on ? T.accentLight : "white",
        color: on ? T.accentDark : T.muted }}>
      {on ? "✓ " : ""}{children}
    </button>
  );

  const Panel = ({ id, title, sub, children, badge }) => (
    <section style={{ background:"white", borderRadius:"13px",
      border:`1px solid ${open[id] ? T.accentMid : T.border}`, marginBottom:"11px",
      overflow:"hidden" }}>
      <button onClick={() => toggle(id)}
        style={{ width:"100%", display:"flex", alignItems:"center", gap:"11px",
          padding:"14px 17px", background:"none", border:"none", cursor:"pointer",
          textAlign:"start", fontFamily:"inherit" }}>
        <span style={{ fontSize:"11px", color:T.muted, width:"11px",
          transform:open[id] ? "rotate(90deg)" : "none", transition:"transform 0.15s" }}>▸</span>
        <span style={{ flex:1 }}>
          <span style={{ display:"block", fontSize:"13.5px", fontWeight:"750", color:T.text }}>
            {title}
          </span>
          {sub && <span style={{ display:"block", fontSize:"11px", color:T.muted,
            marginTop:"2px", lineHeight:"1.5" }}>{sub}</span>}
        </span>
        {badge && <span style={{ fontSize:"10.5px", fontWeight:"700", color:T.muted,
          background:T.bg, border:`1px solid ${T.border}`, borderRadius:"20px",
          padding:"3px 10px", whiteSpace:"nowrap", fontVariantNumeric:"tabular-nums" }}>
          {badge}</span>}
      </button>
      {open[id] && <div style={{ padding:"2px 17px 19px" }}>{children}</div>}
    </section>
  );

  // A rate with its Wilson interval, drawn as a bar with a whisker. Used
  // everywhere a proportion appears, so a reader never has to work out whether
  // 3 of 4 and 300 of 400 are being shown with the same confidence.
  const Rate = ({ k, n, label, note, colour }) => {
    const p = n ? k / n : 0;
    const [lo, hi] = wilson(k, n);
    return (
      <div style={{ marginBottom:"12px" }}>
        <div style={{ display:"flex", justifyContent:"space-between",
          alignItems:"baseline", marginBottom:"4px", gap:"10px" }}>
          <span style={{ fontSize:"12.5px", color:T.text, fontWeight:"600" }}>{label}</span>
          <span style={{ fontSize:"11.5px", color:T.muted, fontVariantNumeric:"tabular-nums",
            whiteSpace:"nowrap" }}>
            {k}/{n}
            <strong style={{ color:T.text, marginInlineStart:"9px" }}>{(p*100).toFixed(1)}%</strong>
            <span style={{ marginInlineStart:"7px", color:"#9ca3af" }}>
              [{(lo*100).toFixed(1)}–{(hi*100).toFixed(1)}]
            </span>
          </span>
        </div>
        <div style={{ position:"relative", height:"14px", background:VIZ.grid,
          borderRadius:"4px" }}>
          <div style={{ position:"absolute", insetInlineStart:0, top:0, height:"100%",
            width:`${p*100}%`, background:colour || VIZ.seq, borderRadius:"4px" }}/>
          <div style={{ position:"absolute", top:"50%", height:"2px",
            insetInlineStart:`${lo*100}%`, width:`${(hi-lo)*100}%`,
            background:"rgba(11,11,11,0.45)", transform:"translateY(-50%)" }}/>
        </div>
        {note && <div style={{ fontSize:"10.5px", color:T.muted, marginTop:"3px" }}>{note}</div>}
      </div>
    );
  };

  const Stat = ({ v, label, sub }) => (
    <div style={{ background:T.bg, border:`1px solid ${T.border}`, borderRadius:"10px",
      padding:"12px 14px", minWidth:"118px", flex:"1 1 118px" }}>
      <div style={{ fontSize:"21px", fontWeight:"800", color:T.text,
        fontVariantNumeric:"tabular-nums", lineHeight:"1.15" }}>{v}</div>
      <div style={{ fontSize:"10.5px", fontWeight:"700", color:T.muted,
        letterSpacing:"0.05em", textTransform:"uppercase", marginTop:"3px" }}>{label}</div>
      {sub && <div style={{ fontSize:"10.5px", color:T.muted, marginTop:"3px",
        lineHeight:"1.45" }}>{sub}</div>}
    </div>
  );

  const Thin = ({ children }) => (
    <p style={{ fontSize:"11.5px", color:T.muted, lineHeight:"1.65", margin:"0 0 14px" }}>
      {children}
    </p>
  );

  const Empty = ({ what }) => (
    <div style={{ padding:"26px", textAlign:"center", color:T.muted, fontSize:"12px" }}>
      No {what} in the current selection.
    </div>
  );

  // ── Export: exactly the filtered rows, nothing recomputed ────────────────
  // One export rather than one per tab. What lands in the file is what is on
  // screen, which is the only way a figure in a paper can be traced back.
  const exportRows = () => {
    if (!rows.length) { alert("Nothing selected to export."); return; }
    const SKIP = new Set(["trainee_email", "written_at", "_id", "source", "framed_why"]);
    const keys = [...new Set(rows.flatMap(r => Object.keys(r)))].filter(k => !SKIP.has(k)).sort();
    const cell = (v) => {
      if (v == null) return "";
      if (v && typeof v === "object" && typeof v.toDate === "function")
        return v.toDate().toISOString();
      const s = Array.isArray(v) ? v.join("; ") : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [keys.join(","), ...rows.map(r => keys.map(k => cell(r[k])).join(","))].join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type:"text/csv" }));
    a.download = `kirpa-filtered-${rows.length}rows-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // ── PANEL: practice variation ────────────────────────────────────────────
  // Variation is only meaningful within one case: different cases have
  // different right answers, so pooling them would measure the case mix rather
  // than disagreement. Each case is scored separately and the table is sorted
  // by how much the panel of learners disagreed.
  const variation = useMemo(() => {
    const base = varSuccessOnly ? rows.filter(e => e.overall_success === true) : rows;
    const byCase = new Map();
    base.forEach(e => {
      const id = e.case_id_readable || "(unknown)";
      if (!byCase.has(id)) byCase.set(id, []);
      byCase.get(id).push(e);
    });
    return [...byCase.entries()].map(([id, set]) => {
      const params = VAR_PARAMS.map(p => {
        const counts = {};
        let seen = 0;
        set.forEach(e => {
          const raw = e[p.f];
          if (raw == null || raw === "") return;
          let key;
          if (p.cat) key = String(raw);
          else {
            const num = parseFloat(raw);
            if (!isFinite(num)) return;
            key = String(Math.round(num / p.bin) * p.bin);
          }
          counts[key] = (counts[key] || 0) + 1;
          seen++;
        });
        // Below 5 observations entropy is dominated by sampling, not by
        // disagreement, so the cell is left blank rather than reported.
        const ent = seen >= 5 ? entropyNorm(counts) : null;
        return { ...p, n:seen, ent, counts };
      });
      const scored = params.filter(p => p.ent);
      const mean = scored.length
        ? scored.reduce((a, p) => a + p.ent.norm, 0) / scored.length : null;
      return { id, title:BASE_CASES[id]?.title || id, n:set.length, params, mean };
    }).filter(c => c.mean !== null).sort((a, b) => b.mean - a.mean);
  }, [rows, varSuccessOnly]);

  if (loading) return (
    <div style={{ padding:"56px", textAlign:"center", color:T.muted, fontSize:"13px" }}>
      Loading research events…
    </div>
  );

  // ── PANEL: cohort and delivery ───────────────────────────────────────────
  const learners = new Set(rows.map(e => e.learner_id || e.device_id).filter(Boolean)).size;
  const instN    = new Set(rows.map(e => e.institution_id).filter(Boolean)).size;
  const ctryN    = new Set(rows.map(e => e.country).filter(Boolean)).size;

  // The cross-tab the whole request turns on: who sent the case, by which
  // surface it was run on. Educator-assigned in AR and self-directed in 2D are
  // different experiences and this is where they stop being pooled.
  const crossTab = (() => {
    const tracks   = ["practice", "assigned"];
    const surfaces = ["2d", "3d", "ar"];
    const cell = (t, s) => rows.filter(e => trackOf(e) === t && surfaceOf(e) === s);
    return { tracks, surfaces, cell };
  })();

  // ── PANEL: prescribing success ───────────────────────────────────────────
  const successOf = (set) => ({
    n: set.length,
    k: set.filter(e => e.overall_success === true).length,
  });

  const domainRows = Object.entries(DOMAIN_LABELS).map(([code, label]) => {
    const set = rows.filter(e => e.clinical_domain_primary === code);
    return { code, label, ...successOf(set) };
  }).filter(d => d.n > 0).sort((a, b) => (a.k / a.n) - (b.k / b.n));


  // ── PANEL: diagnostic framing ────────────────────────────────────────────
  // Only cases that presented a problem to identify. A blank-prescription case
  // has no presenting problem, so the question was never asked and including
  // those rows would deflate the denominator.
  const framed = rows.filter(e =>
    (e.case_scenario || "") !== "new" && e.framed_problem);
  const frameTab = (() => {
    const right = framed.filter(e =>
      FRAME_DOMAIN[e.framed_problem] === e.clinical_domain_primary);
    const wrong = framed.filter(e =>
      FRAME_DOMAIN[e.framed_problem] && FRAME_DOMAIN[e.framed_problem] !== e.clinical_domain_primary);
    const none  = framed.filter(e => !FRAME_DOMAIN[e.framed_problem]);
    const solved = (set) => set.filter(e => e.overall_success === true).length;
    return {
      right:{ n:right.length, k:solved(right) },
      wrong:{ n:wrong.length, k:solved(wrong) },
      none: { n:none.length,  k:solved(none)  },
      // Engagement is only countable where the step was actually put in front
      // of someone. The playground has no frame-capture modal, and the Cloud
      // Function coerces a missing value to false, so an AR row would otherwise
      // read as a learner who was asked and chose to answer. Restricted to the
      // 2D runner, where the question is real.
      skipped: rows.filter(e =>
        surfaceOf(e) === "2d" && e.frame_skipped === true).length,
      offered: rows.filter(e =>
        surfaceOf(e) === "2d" && (e.case_scenario || "") !== "new").length,
    };
  })();
  const frameChoices = (() => {
    const c = {};
    framed.forEach(e => { c[e.framed_problem] = (c[e.framed_problem] || 0) + 1; });
    return Object.entries(c).sort((a, b) => b[1] - a[1]);
  })();

  // ── PANEL: reach and delivery ────────────────────────────────────────────
  // Usage events are not research events and are deliberately not filtered by
  // the bar above: an assignment that was sent and never opened has no
  // specialty, no surface and no outcome, and dropping it would hide exactly
  // the attrition the funnel exists to show.
  const uev   = Object.values(usageEvents || {});
  const sent  = uev.filter(e => e.event_type === "assignment_sent");
  const funnel = {
    sent:      sent.length,
    educators: new Set(sent.map(e => e.educator_id).filter(Boolean)).size,
    insts:     new Set(sent.map(e => e.institution_id).filter(Boolean)).size,
    completed: all.filter(e => trackOf(e) === "assigned").length,
    selfServe: all.filter(e => trackOf(e) === "practice").length,
    arAttempts: all.filter(e => surfaceOf(e) !== "2d").length,
  };

  return (
    <div style={{ fontFamily:T.font }}>

      {/* ══ ONE FILTER BAR ══════════════════════════════════════════════════
          Sticky, because the panels below are long and a filter you have to
          scroll back up to find gets used once and then worked around. */}
      <div style={{ position:"sticky", top:0, zIndex:30, background:"white",
        border:`1px solid ${T.border}`, borderRadius:"13px", padding:"14px 16px",
        marginBottom:"14px", boxShadow:"0 2px 14px rgba(15,23,42,0.06)" }}>

        <div style={{ display:"flex", gap:"10px", flexWrap:"wrap", marginBottom:"11px" }}>
          <Sel label="Track" value={track} set={setTrack} opts={[
            ["all","Both tracks"],["practice","Self-directed"],["assigned","Educator-assigned"]]}/>
          <Sel label="Surface" value={surface} set={setSurface} opts={[
            ["all","All surfaces"],["2d","2D runner"],["3d","3D screen"],["ar","AR passthrough"]]}/>
          <Sel label="Specialty" value={spec} set={setSpec} opts={[
            ["all","All"],...Object.entries(SPEC_LABEL),["unknown","Not recorded"]]}/>
          <Sel label="Training stage" value={band} set={setBand} opts={[
            ["all","All"],...Object.entries(BAND_LABEL),["unknown","Not recorded"]]}/>
          <Sel label="Case type" value={scen} set={setScen} opts={[
            ["all","All cases"],["new","Prescribe from scratch"],["modify","Edit running circuit"]]}/>
          <Sel label="Case origin" value={origin} set={setOrigin} opts={[
            ["all","All"],["builtin","Built-in"],["custom","Community"]]}/>
          <Sel label="Domain" value={domain} set={setDomain} opts={[
            ["all","All domains"],...Object.entries(DOMAIN_LABELS)]}/>
          {countries.length > 1 && <Sel label="Country" value={country} set={setCountry}
            opts={[["all","All countries"],...countries.map(c => [c, c])]}/>}
          {insts.length > 0 && <Sel label="Institution" value={inst} set={setInst}
            opts={[["all","All"],["none","No cohort"],...insts.map(i => [i, i.slice(0,10)+"…"])]}/>}
          <Sel label="Window" value={since} set={setSince} opts={[
            ["all","All time"],["30","Last 30 days"],["90","Last 90 days"],["365","Last 12 months"]]}/>
          <Sel label="Split every rate by" value={group} set={setGroup}
            opts={Object.entries(GROUPERS).map(([k, g]) => [k, g.label])}/>
        </div>

        <div style={{ display:"flex", gap:"7px", flexWrap:"wrap", alignItems:"center",
          paddingTop:"11px", borderTop:`1px solid ${T.border}` }}>
          <Chip on={firstOnly}   set={setFirstOnly}>First attempts only</Chip>
          <Chip on={unaidedOnly} set={setUnaided}>Unaided only</Chip>
          <Chip on={pxOnly}      set={setPxOnly}>Prescribing roles only</Chip>
          <div style={{ marginInlineStart:"auto", display:"flex", gap:"10px",
            alignItems:"center" }}>
            <span style={{ fontSize:"11.5px", color:T.muted,
              fontVariantNumeric:"tabular-nums" }}>
              <strong style={{ color:T.text }}>{rows.length.toLocaleString()}</strong> of{" "}
              {all.length.toLocaleString()} attempts
            </span>
            <button onClick={exportRows}
              style={{ ...C.btn(C.pu), padding:"7px 13px", fontSize:"11.5px" }}>
              Export selection
            </button>
          </div>
        </div>
      </div>

      {/* ══ COHORT AND DELIVERY ═══════════════════════════════════════════ */}
      <Panel id="cohort" title="Cohort and delivery"
        badge={`${rows.length.toLocaleString()} attempts`}
        sub="Who is in the current selection, and by which route the cases reached them.">
        <div style={{ display:"flex", gap:"9px", flexWrap:"wrap", marginBottom:"18px" }}>
          <Stat v={rows.length.toLocaleString()} label="Attempts"/>
          <Stat v={learners.toLocaleString()} label="Learners"
            sub="distinct device or linked account"/>
          <Stat v={instN} label="Institutions"/>
          <Stat v={ctryN} label="Countries"/>
          <Stat v={rows.filter(e => e.is_prescriber === true).length.toLocaleString()}
            label="Prescriber rows" sub="roles that write the prescription"/>
        </div>

        <div style={{ fontSize:"12.5px", fontWeight:"700", color:T.text, marginBottom:"3px" }}>
          Track × surface
        </div>
        <Thin>
          An educator-assigned case run in AR and a self-directed case typed into the
          2D form are different tasks. Every cell is separately selectable from the bar
          above.
        </Thin>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:"12px",
          marginBottom:"6px" }}>
          <thead>
            <tr style={{ borderBottom:`2px solid ${T.border}` }}>
              <th style={{ textAlign:"start", padding:"7px 8px", color:T.muted, fontWeight:"650" }}>Track</th>
              {crossTab.surfaces.map(s => (
                <th key={s} style={{ textAlign:"end", padding:"7px 8px", color:T.muted,
                  fontWeight:"650" }}>{SURFACE_LABEL[s]}</th>
              ))}
              <th style={{ textAlign:"end", padding:"7px 8px", color:T.text, fontWeight:"750" }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {crossTab.tracks.map(t => {
              const rowTotal = crossTab.cell(t, "2d").length + crossTab.cell(t, "3d").length
                             + crossTab.cell(t, "ar").length;
              return (
                <tr key={t} style={{ borderBottom:`1px solid ${T.border}` }}>
                  <td style={{ padding:"8px", color:T.text, fontWeight:"600" }}>{TRACK_LABEL[t]}</td>
                  {crossTab.surfaces.map(s => {
                    const set = crossTab.cell(t, s);
                    const k = set.filter(e => e.overall_success === true).length;
                    return (
                      <td key={s} style={{ padding:"8px", textAlign:"end", color:T.text,
                        fontVariantNumeric:"tabular-nums" }}>
                        {set.length
                          ? <>{set.length}<span style={{ color:T.muted, marginInlineStart:"6px",
                              fontSize:"11px" }}>{Math.round(k/set.length*100)}% pass</span></>
                          : <span style={{ color:"#d1d5db" }}>—</span>}
                      </td>
                    );
                  })}
                  <td style={{ padding:"8px", textAlign:"end", fontWeight:"750", color:T.text,
                    fontVariantNumeric:"tabular-nums" }}>{rowTotal}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {funnel.arAttempts === 0 && (
          <div style={{ marginTop:"12px", padding:"11px 13px", borderRadius:"9px",
            background:"#fffbeb", border:"1px solid #fde68a", fontSize:"11.5px",
            color:"#92400e", lineHeight:"1.65" }}>
            No 3D or AR attempts recorded yet. The playground now writes through the same
            Cloud Function as the 2D runner, but only once a learner has accepted the
            letter of information — attempts made before that are not retrospectively
            recoverable.
          </div>
        )}
      </Panel>

      {/* ══ OUTCOME: PRESCRIBING SUCCESS ══════════════════════════════════ */}
      <Panel id="success" title="Prescribing success"
        badge={group === "none" ? null : `split by ${G.label.toLowerCase()}`}
        sub="The primary outcome. Proportions carry 95% Wilson intervals, which do not
             collapse to a point when a cell is small.">
        {!rows.length ? <Empty what="attempts"/> : <>
          {groups.map(g => {
            const s = successOf(g.rows);
            return <Rate key={g.key} k={s.k} n={s.n} label={g.label}
              note={group === "none"
                ? `${firstOnly ? "First attempts" : "All attempts"}${unaidedOnly ? ", unaided" : ""}`
                : null}/>;
          })}

          <div style={{ fontSize:"12.5px", fontWeight:"700", color:T.text,
            margin:"20px 0 3px" }}>By clinical domain</div>
          <Thin>
            Hardest first. A domain where the interval still spans a wide range needs
            more attempts before it is worth reporting.
          </Thin>
          {domainRows.length
            ? domainRows.map(d => <Rate key={d.code} k={d.k} n={d.n} label={d.label}/>)
            : <Empty what="domain data"/>}
        </>}
      </Panel>

      {/* ══ OUTCOME: ERROR TAXONOMY ═══════════════════════════════════════ */}
      <Panel id="errors" title="Error taxonomy"
        sub="Which parameter was got wrong, and in which direction. Denominators are
             attempts where the case actually tested that parameter.">
        <ErrorTaxonomy events={rows} embedded/>
      </Panel>

      {/* ══ OUTCOME: PRACTICE VARIATION ═══════════════════════════════════ */}
      <Panel id="variation" title="Practice variation"
        badge={variation.length ? `${variation.length} cases` : null}
        sub="How much learners disagreed with each other on the same case, by normalised
             Shannon entropy.">
        <Thin>
          H* runs from 0, where everyone made the same choice, to 1, where the choices
          were spread evenly across every option anyone used. Continuous parameters are
          binned to the increment the interface offers, so 1,980 and 2,000 ml/hr count as
          one decision rather than two. Cells with fewer than 5 observations are left
          blank: at that size entropy measures the sample, not the disagreement.
        </Thin>
        <div style={{ marginBottom:"14px" }}>
          <Chip on={varSuccessOnly} set={setVarSuccessOnly}>
            Successful attempts only
          </Chip>
          {varSuccessOnly && (
            <div style={{ fontSize:"11px", color:T.muted, marginTop:"7px",
              lineHeight:"1.6", maxWidth:"620px" }}>
              Restricted to attempts that passed, this stops measuring error and starts
              measuring legitimate equivalence: high entropy here means several different
              prescriptions all solved the case, which is a property of the case rather
              than of the learners.
            </div>
          )}
        </div>
        {!variation.length ? <Empty what="case with enough attempts to score"/> : (
          <div style={{ overflowX:"auto" }}>
            <table style={{ borderCollapse:"collapse", fontSize:"11.5px", minWidth:"100%" }}>
              <thead>
                <tr style={{ borderBottom:`2px solid ${T.border}` }}>
                  <th style={{ textAlign:"start", padding:"7px 8px", color:T.muted,
                    fontWeight:"650", position:"sticky", insetInlineStart:0,
                    background:"white" }}>Case</th>
                  <th style={{ textAlign:"end", padding:"7px 6px", color:T.muted,
                    fontWeight:"650" }}>n</th>
                  <th style={{ textAlign:"end", padding:"7px 6px", color:T.text,
                    fontWeight:"750" }}>mean H*</th>
                  {VAR_PARAMS.map(p => (
                    <th key={p.f} style={{ padding:"7px 5px", color:T.muted,
                      fontWeight:"650", writingMode:"vertical-rl",
                      transform:"rotate(180deg)", whiteSpace:"nowrap",
                      height:"96px", fontSize:"10.5px" }}>{p.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {variation.map(c => (
                  <tr key={c.id} style={{ borderBottom:`1px solid ${T.border}` }}>
                    <td style={{ padding:"7px 8px", color:T.text, fontWeight:"600",
                      position:"sticky", insetInlineStart:0, background:"white",
                      maxWidth:"190px", overflow:"hidden", textOverflow:"ellipsis",
                      whiteSpace:"nowrap" }} title={c.title}>{c.title}</td>
                    <td style={{ padding:"7px 6px", textAlign:"end", color:T.muted,
                      fontVariantNumeric:"tabular-nums" }}>{c.n}</td>
                    <td style={{ padding:"7px 6px", textAlign:"end", color:T.text,
                      fontWeight:"750", fontVariantNumeric:"tabular-nums" }}>
                      {c.mean.toFixed(2)}
                    </td>
                    {c.params.map(p => {
                      if (!p.ent) return <td key={p.f} style={{ padding:"7px 5px",
                        textAlign:"center", color:"#e5e7eb" }}>·</td>;
                      // Single-hue sequential ramp: lightness carries the value,
                      // so it survives greyscale printing and colour-vision
                      // deficiency without needing a second channel.
                      const a = 0.08 + p.ent.norm * 0.84;
                      return (
                        <td key={p.f} title={`${p.label}: H*=${p.ent.norm.toFixed(2)} across ${p.ent.k} distinct choices, n=${p.n}`
                              + (p.bin ? ` (binned to ${p.bin} ${p.unit})` : " (categorical)")}
                          style={{ padding:"7px 5px", textAlign:"center",
                            background:`rgba(42,120,214,${a.toFixed(3)})`,
                            color: p.ent.norm > 0.55 ? "white" : T.text,
                            fontVariantNumeric:"tabular-nums", fontSize:"10.5px",
                            fontWeight:"650" }}>
                          {p.ent.norm.toFixed(2)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* ══ OUTCOME: CASE PSYCHOMETRICS ═══════════════════════════════════ */}
      <Panel id="psychometrics" title="Case psychometrics"
        sub="Per-case difficulty and discrimination, with the error profile and the
             sub-group breakdowns for each case.">
        <CaseAnalytics events={rows} embedded/>
      </Panel>

      {/* ══ OUTCOME: DIAGNOSTIC FRAMING ═══════════════════════════════════ */}
      <Panel id="framing" title="Diagnostic framing"
        badge={framed.length ? `${framed.length} framed` : null}
        sub="What the learner thought the problem was, taken before the result was
             revealed, against whether they then solved it.">
        {!framed.length ? <Empty what="frame-capture response"/> : <>
          <Thin>
            This is the split the frame-capture step exists for. Naming the right domain
            and still failing is an execution error — the target was right, the numbers
            were not. Naming the wrong domain is a different failure entirely, and no
            amount of feedback on the numbers addresses it.
          </Thin>
          <Rate k={frameTab.right.k} n={frameTab.right.n}
            label="Named the right domain" colour={VIZ.s1}
            note="Failures here are execution errors, not diagnostic ones."/>
          <Rate k={frameTab.wrong.k} n={frameTab.wrong.n}
            label="Named a different domain" colour={VIZ.s2}
            note="A pass here was reached without the right target in mind."/>
          <Rate k={frameTab.none.k} n={frameTab.none.n}
            label="Unsure, or blamed patient factors" colour="#9ca3af"/>

          <div style={{ display:"flex", gap:"9px", flexWrap:"wrap", margin:"18px 0" }}>
            <Stat v={frameTab.offered
                      ? `${Math.round((1 - frameTab.skipped / frameTab.offered) * 100)}%`
                      : "—"}
              label="Engagement" sub={`${frameTab.skipped} of ${frameTab.offered} skipped it`}/>
          </div>

          <div style={{ fontSize:"12.5px", fontWeight:"700", color:T.text,
            marginBottom:"3px" }}>Which frames get reached for</div>
          <Thin>
            A frame chosen far more often than the cases warrant is an anchoring habit
            worth teaching against.
          </Thin>
          {frameChoices.map(([id, n]) => (
            <div key={id} style={{ marginBottom:"7px" }}>
              <div style={{ display:"flex", justifyContent:"space-between",
                marginBottom:"3px" }}>
                <span style={{ fontSize:"11.5px", color:T.text, fontWeight:"600" }}>
                  {FRAME_LABEL[id] || id}
                </span>
                <span style={{ fontSize:"11px", color:T.muted,
                  fontVariantNumeric:"tabular-nums" }}>
                  {n} ({Math.round(n / framed.length * 100)}%)
                </span>
              </div>
              <div style={{ height:"8px", background:VIZ.grid, borderRadius:"4px" }}>
                <div style={{ width:`${n / framed.length * 100}%`, height:"100%",
                  background:VIZ.seq, borderRadius:"4px" }}/>
              </div>
            </div>
          ))}
        </>}
      </Panel>

      {/* ══ REACH AND DELIVERY ════════════════════════════════════════════ */}
      <Panel id="reach" title="Reach and delivery"
        sub="How cases are getting to people. Counts the whole dataset, not the current
             selection — an assignment that was never opened has no outcome to filter on.">
        <div style={{ display:"flex", gap:"9px", flexWrap:"wrap", marginBottom:"16px" }}>
          <Stat v={funnel.educators} label="Educators" sub="have sent at least one case"/>
          <Stat v={funnel.insts} label="Institutions"/>
          <Stat v={funnel.sent.toLocaleString()} label="Cases sent"/>
          <Stat v={funnel.completed.toLocaleString()} label="Assigned completions"
            sub={funnel.sent
              ? `${Math.round(funnel.completed / funnel.sent * 100)}% of cases sent`
              : null}/>
          <Stat v={funnel.selfServe.toLocaleString()} label="Self-directed attempts"/>
          <Stat v={funnel.arAttempts.toLocaleString()} label="3D / AR attempts"/>
        </div>
        <Thin>
          Completion against cases sent is a ceiling, not a rate: one learner can be sent
          several cases and a case can be re-attempted, so read it as reach rather than
          as per-person adherence.
        </Thin>
      </Panel>

      {/* ══ DELPHI — closed by default ════════════════════════════════════ */}
      <Panel id="delphi" title="Content validation (Delphi)"
        sub="Finished work. Expert agreement on the built-in cases, kept for the methods
             section rather than for monitoring.">
        <ValidationAdminTab/>
      </Panel>

      {/* ══ ANALYSIS PLAN — closed by default ═════════════════════════════ */}
      <Panel id="plan" title="Analysis plan"
        sub="Which model each outcome is analysed with, and which fields it needs.">
        <ResearchPlan/>
      </Panel>
    </div>
  );
}

function ValidationAdminTab(){
  // No name, no email. Phase 1 consent is taken in Qualtrics before a link is
  // issued, so this screen never sees an identifier and the platform stores
  // none. The investigator matches expertId to person from the Qualtrics
  // export and sends the link from their own institutional address.
  //
  // Discipline is still here, because the assignment has to be stratified by it
  // and that happens before the reviewer ever opens the link. It is a provisional
  // value: the reviewer confirms their own discipline on first open and that
  // answer replaces this one.
  const [discipline,setDiscipline] = useState("paediatric_nephrology");
  const [caseIds,setCaseIds]       = useState([]);
  const [busy,setBusy]             = useState(false);
  const [msg,setMsg]               = useState("");
  const [showStats,setShowStats]   = useState(false);

  const [link,setLink]             = useState("");
  const [copied,setCopied]         = useState(false);
  const [reissueId,setReissueId]   = useState("");
  // Shown beside the link. With no name or email stored, the random expertId is
  // the ONLY way to match a link to the person who consented in Qualtrics, so
  // it has to be copyable rather than buried in a console log.
  const [expertIdOut,setExpertIdOut] = useState("");

  const allCases = Object.keys(BASE_CASES);

  // Selection order IS the review order: the reviewer works through caseIds as
  // listed. Clicking adds to the end, so you can build the sequence you want
  // rather than getting whatever order the case bank happens to be in.
  const toggle = (id)=> setCaseIds(p=>p.includes(id)?p.filter(x=>x!==id):[...p,id]);
  const move = (i,d)=> setCaseIds(p=>{
    const j = i+d; if(j<0||j>=p.length) return p;
    const n = [...p]; [n[i],n[j]] = [n[j],n[i]]; return n;
  });

  const invite = async ()=>{
    if(!caseIds.length){ setMsg("Assign at least one case."); return; }
    setBusy(true); setMsg("");
    try{
      const fns=getFunctions(firebaseApp,"us-central1");
      // No address is sent and none is stored. The callable mints a random
      // expertId and a link, and that is all that comes back.
      const res=await httpsCallable(fns,"inviteValidationExpert")({
        discipline, caseIds});
      const d = res?.data||{};
      setMsg(`Reviewer created — ${d.assigned||caseIds.length} cases assigned. `+
             `Copy the link below and send it yourself.`);
      setLink(d.link||"");
      setExpertIdOut(d.expertId||"");
      setCaseIds([]);
    }catch(e){ setMsg(e?.message||"Failed to create reviewer."); }
    setBusy(false);
  };

  // Round 2 links are returned rather than emailed: there is no address to
  // send to. They are held in state so they can be copied out in one go,
  // because losing them means reissuing every reviewer individually.
  const [round2Links,setRound2Links] = useState([]);
  const openRound2 = async ()=>{
    if(!window.confirm(
      "Move every expert who finished round 1 into round 2?\n\n"+
      "Their round-1 answers are archived first, then reopened for revision. "+
      "Fresh links are returned here for you to send; the platform does not "+
      "email reviewers."
    )) return;
    setBusy(true); setMsg("");
    try{
      const fns=getFunctions(firebaseApp,"us-central1");
      const res=await httpsCallable(fns,"openValidationRound2")({});
      const d = res?.data||{};
      setRound2Links(Array.isArray(d.links)?d.links:[]);
      setMsg(`Round 2 opened for ${d.moved||0} experts (${d.skipped||0} skipped). `+
             `Copy the links below.`);
    }catch(e){ setMsg(e?.message||"Failed."); }
    setBusy(false);
  };

  return <div>
    {showStats && <ValidationAnalytics onClose={()=>setShowStats(false)}/>}

    <div style={{display:"flex",gap:"9px",marginBottom:"18px",flexWrap:"wrap"}}>
      <button onClick={()=>setShowStats(true)} style={C.btn(C.pu)}>
        View results
      </button>
      <button onClick={openRound2} disabled={busy} style={C.btn("#f3f4f6","#374151")}>
        Open round 2
      </button>
    </div>

    {round2Links.length > 0 && (
      <div style={{marginBottom:"18px",padding:"14px 16px",borderRadius:"11px",
        background:"#fffbeb",border:"1px solid #fde68a",maxWidth:"760px"}}>
        <div style={{fontSize:"12.5px",fontWeight:"750",color:"#92400e",
          marginBottom:"5px"}}>
          Round 2 links — send these yourself
        </div>
        <div style={{fontSize:"11.5px",color:"#92400e",lineHeight:"1.65",
          marginBottom:"11px"}}>
          These are shown once. The platform holds no addresses, so it cannot
          resend them; if you lose them you will have to reissue each reviewer
          individually from the panel list below.
        </div>
        <button onClick={()=>{
            const txt = round2Links
              .map(l=>`${l.expertId}\t${l.discipline||""}\t${l.link}`).join("\n");
            try{ navigator.clipboard.writeText(txt); setMsg("Round 2 links copied."); }catch{}
          }}
          style={{...C.btn("#92400e"),marginBottom:"11px"}}>
          Copy all as TSV
        </button>
        <div style={{maxHeight:"210px",overflowY:"auto",background:"white",
          borderRadius:"8px",border:"1px solid #fde68a",padding:"9px 11px"}}>
          {round2Links.map(l=>(
            <div key={l.expertId} style={{fontSize:"10.5px",fontFamily:FONT_MONO,
              color:"#78350f",wordBreak:"break-all",padding:"4px 0",
              borderBottom:"1px solid #fef3c7",lineHeight:"1.6"}}>
              <strong>{l.expertId}</strong>
              {l.discipline?` · ${l.discipline}`:""}<br/>{l.link}
            </div>
          ))}
        </div>
      </div>
    )}

    <div style={{background:"white",borderRadius:"12px",border:"1px solid #e5e7eb",
      padding:"18px 20px",maxWidth:"620px"}}>
      <div style={{fontSize:"14px",fontWeight:"800",color:C.dark,marginBottom:"4px"}}>
        Invite a reviewer
      </div>
      <div style={{fontSize:"12px",color:"#6b7280",lineHeight:"1.6",marginBottom:"16px"}}>
        Aim for five independent ratings per case, and make sure each case is seen
        by at least two nephrologists and two intensivists. Six to eight cases per
        person keeps the task under about an hour.
      </div>

      <div style={{marginBottom:"13px",padding:"11px 13px",borderRadius:"9px",
        background:"#f0fdf4",border:"1px solid #bbf7d0",fontSize:"11.5px",
        color:"#14532d",lineHeight:"1.65"}}>
        <strong>No identifiers are collected here.</strong> Consent is taken in
        Qualtrics before a link is issued, so this creates a reviewer identified
        only by a random token. Match the token to the person using your
        Qualtrics export, and send the link from your own address.
      </div>

      <div style={{marginBottom:"13px"}}>
        <label style={C.lbl}>
          Discipline <span style={{fontWeight:"400",color:"#9ca3af"}}>
            (provisional, for stratifying the assignment)
          </span>
        </label>
        <select value={discipline} onChange={e=>setDiscipline(e.target.value)} style={C.sel}>
          <option value="paediatric_nephrology">Paediatric nephrology</option>
          <option value="paediatric_critical_care">Paediatric critical care</option>
          <option value="crrt_nursing">CRRT nursing or nursing education</option>
          <option value="other">Other</option>
        </select>
        <div style={{fontSize:"11px",color:"#9ca3af",marginTop:"5px",lineHeight:"1.55"}}>
          The reviewer confirms their own discipline when they open the link, and
          that answer replaces this one. Every agreement statistic is stratified
          by it, so it is theirs to state rather than yours to guess.
        </div>
      </div>

      <label style={C.lbl}>
        Assigned cases <span style={{fontWeight:"400",color:"#9ca3af"}}>({caseIds.length} selected)</span>
      </label>
      <div style={{display:"grid",gap:"5px",marginTop:"6px",marginBottom:"14px",
        gridTemplateColumns:"repeat(auto-fill,minmax(112px,1fr))",
        maxHeight:"220px",overflowY:"auto",border:"1px solid #e5e7eb",
        borderRadius:"9px",padding:"8px"}}>
        {allCases.map(id=>{
          const on = caseIds.includes(id);
          return <button key={id} onClick={()=>toggle(id)} type="button"
            style={{padding:"6px 8px",borderRadius:"7px",cursor:"pointer",
              fontFamily:"inherit",fontSize:"11.5px",fontWeight:on?"700":"500",
              border:`1px solid ${on?C.pu:"#e5e7eb"}`,
              background:on?"#f3effe":"white",color:on?C.pu:"#374151",
              textAlign:"start",overflow:"hidden",textOverflow:"ellipsis",
              whiteSpace:"nowrap"}}>
            {on?"✓ ":""}{id}
          </button>;
        })}
      </div>

      {caseIds.length>0 && (
        <div style={{marginBottom:"14px",padding:"11px 13px",borderRadius:"9px",
          background:"#faf8ff",border:"1px solid #e4dcfb"}}>
          <div style={{fontSize:"10px",fontWeight:"700",color:"#6d28d9",
            textTransform:"uppercase",letterSpacing:"0.12em",marginBottom:"8px"}}>
            Review order
          </div>
          {caseIds.map((id,i)=>(
            <div key={id} style={{display:"flex",alignItems:"center",gap:"8px",
              padding:"4px 0",fontSize:"12.5px",color:"#374151"}}>
              <span style={{width:"20px",color:"#9ca3af",fontVariantNumeric:"tabular-nums"}}>
                {i+1}.
              </span>
              <span style={{flex:1,minWidth:0}}>
                {id}
                <span style={{color:"#9ca3af",marginInlineStart:"8px",fontSize:"11px"}}>
                  {BASE_CASES[id]?.title||""}
                </span>
              </span>
              <button onClick={()=>move(i,-1)} disabled={i===0}
                style={{border:"none",background:"none",cursor:i===0?"default":"pointer",
                  color:i===0?"#d4d4d8":"#6d28d9",fontSize:"13px",padding:"0 3px"}}>↑</button>
              <button onClick={()=>move(i,1)} disabled={i===caseIds.length-1}
                style={{border:"none",background:"none",
                  cursor:i===caseIds.length-1?"default":"pointer",
                  color:i===caseIds.length-1?"#d4d4d8":"#6d28d9",fontSize:"13px",padding:"0 3px"}}>↓</button>
            </div>
          ))}
        </div>
      )}

      {/* The platform-email option is gone: there is no address to send to.
          This is also the better route in practice, since an invitation from a
          known institutional address survives hospital spam filters in a way one
          from an unfamiliar sender does not. */}

      {link && (
        <div style={{marginBottom:"14px",padding:"12px 14px",borderRadius:"10px",
          background:"#f0fdf4",border:"1px solid #bbf7d0"}}>
          <div style={{fontSize:"10px",fontWeight:"700",color:"#14532d",
            textTransform:"uppercase",letterSpacing:"0.12em",marginBottom:"7px"}}>
            Personal review link
          </div>
          {expertIdOut && (
            <div style={{fontSize:"11px",color:"#166534",marginBottom:"8px",
              lineHeight:"1.6"}}>
              Reviewer token <code style={{fontFamily:FONT_MONO,background:"white",
                padding:"1px 6px",borderRadius:"5px",
                border:"1px solid #bbf7d0"}}>{expertIdOut}</code>
              {" "}— record this beside the Qualtrics response for this person.
              It is the only link between the two, and it is not recoverable
              from here.
            </div>
          )}
          <div style={{fontSize:"11.5px",color:"#166534",wordBreak:"break-all",
            fontFamily:FONT_MONO,background:"white",padding:"9px 11px",
            borderRadius:"7px",border:"1px solid #bbf7d0",marginBottom:"9px"}}>
            {link}
          </div>
          <div style={{display:"flex",gap:"8px",alignItems:"center"}}>
            <button onClick={()=>{
                try{ navigator.clipboard.writeText(link);
                     setCopied(true); setTimeout(()=>setCopied(false),1800); }catch{}
              }}
              style={C.btn("#166534")}>
              {copied?"Copied":"Copy link"}
            </button>
            <span style={{fontSize:"11px",color:"#4b7a5c",lineHeight:"1.5"}}>
              Valid for 60 days. Personal to this reviewer, so it should not be forwarded.
            </span>
          </div>
        </div>
      )}

      <div style={{display:"flex",gap:"9px",alignItems:"center"}}>
        <button onClick={invite} disabled={busy} style={{...C.btn(C.pu),opacity:busy?0.6:1}}>
          {busy?<Ic n="spin" s="w-4 h-4"/>:null}
          Create reviewer and generate link
        </button>
        {msg && <span style={{fontSize:"12px",
          color:msg.startsWith("Reviewer")||msg.startsWith("Round")?"#059669":"#dc2626"}}>
          {msg}
        </span>}
      </div>
    </div>
  </div>;
}

// ═══════════════════════════════════════════════════════════════════════════
//  VALIDATION ANALYTICS (admin)
//
//  Renders what getValidationAnalytics returns: panel progress, headline
//  indices, and a per-case breakdown showing which options cleared consensus.
//  Figures are the ones a methods reviewer will ask for, and each is labelled
//  with what it means rather than left as an acronym.
// ═══════════════════════════════════════════════════════════════════════════
function ValidationAnalytics({onClose}){
  const [data,setData]   = useState(null);
  const [busy,setBusy]   = useState(true);
  const [err,setErr]     = useState("");
  const [thresh,setThresh]= useState(0.25);
  const [openCase,setOpenCase] = useState(null);
  const [reissued,setReissued] = useState(null);

  const load = async (th)=>{
    setBusy(true); setErr("");
    try{
      const fns=getFunctions(firebaseApp,"us-central1");
      const res=await httpsCallable(fns,"getValidationAnalytics")({includeAt:th});
      setData(res?.data||null);
    }catch(e){ setErr(e?.message||"Unable to load."); }
    setBusy(false);
  };
  useEffect(()=>{ load(thresh); },[thresh]);

  const cases = data?.cases || {};
  const caseIds = Object.keys(cases).sort();
  const panel = data?.panel || [];

  const Chip=({label,value,tone})=> (
    <div style={{flex:"1 1 130px",minWidth:"126px",background:"white",
      border:`1px solid ${T.border}`,borderRadius:"11px",padding:"11px 13px"}}>
      <div style={{fontSize:"9px",fontWeight:"700",color:T.muted,textTransform:"uppercase",
        letterSpacing:"0.12em",marginBottom:"4px"}}>{label}</div>
      <div style={{fontSize:"19px",fontWeight:"800",lineHeight:1,
        color:tone||C.dark,fontVariantNumeric:"tabular-nums"}}>{value}</div>
    </div>
  );

  return <div style={{position:"fixed",inset:0,zIndex:270,background:"rgba(15,23,42,0.6)",
    overflowY:"auto",padding:"14px",fontFamily:FONT_UI}} onClick={onClose}>
    <div onClick={e=>e.stopPropagation()} style={{background:T.bg,borderRadius:"18px",
      maxWidth:"900px",margin:"12px auto",overflow:"hidden",
      boxShadow:"0 28px 70px rgba(0,0,0,0.35)"}}>

      <div style={{background:"white",borderBottom:`1px solid ${T.border}`,padding:"17px 20px",
        display:"flex",alignItems:"center",gap:"12px",position:"sticky",top:0,zIndex:2}}>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:"17px",fontWeight:"800",color:C.dark}}>Content validation</div>
          <div style={{fontSize:"11.5px",color:T.muted,marginTop:"2px"}}>
            Expert panel agreement on the case answer key
          </div>
        </div>
        <label style={{fontSize:"11.5px",color:T.muted,display:"flex",alignItems:"center",gap:"7px"}}>
          Include in key at
          <select value={thresh} onChange={e=>setThresh(parseFloat(e.target.value))}
            style={{...C.sel,width:"auto",padding:"4px 8px",fontSize:"12px"}}>
            {[0.125,0.25,0.375,0.50].map(v=>
              <option key={v} value={v}>{Math.round(v*100)}%</option>)}
          </select>
        </label>
        <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",
          color:T.muted,fontSize:"20px",lineHeight:1}}>×</button>
      </div>

      <div style={{padding:"16px"}}>
        {busy && <div style={{padding:"40px",textAlign:"center"}}><Ic n="spin" s="w-5 h-5"/></div>}
        {err && <div style={{padding:"14px",background:"#fef2f2",border:"1px solid #fecaca",
          borderRadius:"10px",color:"#991b1b",fontSize:"13px"}}>{err}</div>}

        {!busy && !err && <>
          {/* Headline indices */}
          <div style={{display:"flex",gap:"9px",flexWrap:"wrap",marginBottom:"14px"}}>
            <Chip label="Cases rated" value={caseIds.length}/>
            <Chip label="Total ratings" value={data?.totalRatings ?? 0}/>
            <Chip label="S-CVI / Ave" value={data?.sCviAve ?? "—"}
              tone={(data?.sCviAve ?? 0) >= 0.9 ? T.success
                  : (data?.sCviAve ?? 0) >= 0.8 ? T.warn : T.danger}/>
            {(()=>{
              const cs = caseIds.map(c=>cases[c].meanCoverage).filter(v=>v!=null);
              const mc = cs.length ? +(cs.reduce((a,b)=>a+b,0)/cs.length).toFixed(2) : null;
              return <Chip label="Mean coverage" value={mc ?? "—"}
                tone={(mc ?? 0) >= 0.95 ? T.success : (mc ?? 0) >= 0.85 ? T.warn : T.danger}/>;
            })()}
            <Chip label="Panel complete"
              value={`${panel.filter(p=>p.done).length}/${panel.length}`}/>
          </div>

          <div style={{fontSize:"11.5px",color:T.muted,lineHeight:"1.6",marginBottom:"18px",
            padding:"10px 13px",background:"white",borderRadius:"10px",
            border:`1px solid ${T.border}`}}>
            Experts mark every option they would accept, not a single best choice, so
            two thresholds apply. An option enters the acceptable key at a LOW rate,
            because a choice two of eight experts would make should not fail a learner.
            An option is treated as an error only at a HIGH rate of unsafe flags.
            Coverage is the figure that matters most: the proportion of experts whose
            own selection the key admits. A parameter can have no single option reaching
            0.78 and still cover every expert, which is what multi-select produces.
            Gwet's AC1 is used instead of Cohen's kappa because kappa collapses toward
            zero when agreement is high and one answer dominates.
          </div>

          {/* Panel progress */}
          <div style={{background:"white",borderRadius:"12px",border:`1px solid ${T.border}`,
            padding:"14px 16px",marginBottom:"14px"}}>
            <div style={{fontSize:"10px",fontWeight:"700",color:T.muted,textTransform:"uppercase",
              letterSpacing:"0.13em",marginBottom:"10px",fontFamily:FONT_MONO}}>Panel</div>
            {panel.length===0
              ? <div style={{fontSize:"12.5px",color:T.muted}}>No experts invited yet.</div>
              : <div style={{display:"flex",flexDirection:"column",gap:"7px"}}>
                  {panel.map(p=>(
                    <div key={p.expertId} style={{display:"flex",alignItems:"center",gap:"10px"}}>
                      <span style={{width:"8px",height:"8px",borderRadius:"50%",flexShrink:0,
                        background:p.done?T.success:p.completed>0?T.warn:"#d4d4d8"}}/>
                      {/* The token, not a name: none is stored. Shown in full
                          and monospaced because it is the only handle that
                          matches this reviewer to their Qualtrics consent, and
                          a truncated one cannot be matched on. */}
                      <span style={{fontSize:"12.5px",color:C.dark,flex:1,minWidth:0,
                        overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                        <code style={{fontFamily:FONT_MONO,fontSize:"11px"}}>
                          {p.expertId}
                        </code>
                        <span style={{color:T.muted,marginInlineStart:"7px",fontSize:"11px"}}>
                          {p.discipline||"unspecified"}
                          {p.discipline && !p.disciplineConfirmed && (
                            <span title="Assigned by you; the reviewer has not confirmed it yet"
                              style={{marginInlineStart:"5px",color:T.warn,
                                fontWeight:"700"}}>unconfirmed</span>
                          )}
                        </span>
                      </span>
                      <span style={{fontSize:"11.5px",color:T.muted,fontVariantNumeric:"tabular-nums"}}>
                        {p.completed}/{p.assigned}
                      </span>
                      {/* A reviewer whose link expired needs a fresh one
                          without losing the work they have saved. The new link
                          is copied to the clipboard for you to send; the
                          platform holds no address to send it to. */}
                      <button onClick={async()=>{
                          try{
                            const fns=getFunctions(firebaseApp,"us-central1");
                            const r=await httpsCallable(fns,"reissueValidationLink")({expertId:p.expertId});
                            const lk=r?.data?.link;
                            if(lk){ try{ await navigator.clipboard.writeText(lk); }catch{}
                              setReissued(p.expertId); setTimeout(()=>setReissued(null),2500); }
                          }catch(e){ console.warn("[KiRPA] reissue",e); }
                        }}
                        style={{flexShrink:0,padding:"3px 9px",borderRadius:"7px",
                          border:`1px solid ${T.border}`,background:"white",
                          color:reissued===p.expertId?T.success:T.muted,
                          fontSize:"10.5px",fontWeight:"700",cursor:"pointer",
                          fontFamily:"inherit"}}>
                        {reissued===p.expertId ? "Copied" : "New link"}
                      </button>
                    </div>
                  ))}
                </div>}
          </div>

          {/* Per case */}
          {caseIds.length===0
            ? <div style={{padding:"30px",textAlign:"center",color:T.muted,fontSize:"13px",
                background:"white",borderRadius:"12px",border:`1px solid ${T.border}`}}>
                No completed ratings yet.
              </div>
            : caseIds.map(cid=>{
                const c = cases[cid];
                const open = openCase===cid;
                const cvi = c.iCvi;
                const tone = cvi==null ? T.muted : cvi>=0.9 ? T.success : cvi>=0.78 ? T.warn : T.danger;
                return <div key={cid} style={{background:"white",borderRadius:"12px",
                  border:`1px solid ${open?T.accent:T.border}`,marginBottom:"8px",
                  overflow:"hidden"}}>
                  <button onClick={()=>setOpenCase(open?null:cid)}
                    style={{width:"100%",padding:"13px 16px",background:"none",border:"none",
                      cursor:"pointer",fontFamily:"inherit",textAlign:"start",
                      display:"flex",alignItems:"center",gap:"12px"}}>
                    <span style={{fontSize:"13.5px",fontWeight:"750",color:C.dark,flex:1}}>
                      {cid}
                      <span style={{fontWeight:"400",color:T.muted,marginInlineStart:"9px",
                        fontSize:"11.5px"}}>
                        {BASE_CASES[cid]?.title||""}
                      </span>
                    </span>
                    <span style={{fontSize:"11px",color:T.muted,flexShrink:0}}>
                      {Object.entries(c.disciplines||{}).map(([d,n])=>`${n} ${d}`).join(" · ")}
                    </span>
                    <span style={{fontSize:"12px",fontWeight:"800",color:tone,flexShrink:0,
                      fontVariantNumeric:"tabular-nums"}}>
                      CVI {cvi ?? "—"}
                    </span>
                    <span style={{color:T.muted,fontSize:"11px",flexShrink:0}}>{open?"▾":"▸"}</span>
                  </button>

                  {open && <div style={{borderTop:`1px solid ${T.border}`,padding:"14px 16px",
                    background:"#faf8ff"}}>
                    {Object.entries(c.params).map(([param,p])=>(
                      <div key={param} style={{marginBottom:"16px"}}>
                        <div style={{display:"flex",alignItems:"baseline",gap:"9px",
                          marginBottom:"7px",flexWrap:"wrap"}}>
                          <span style={{fontSize:"12px",fontWeight:"700",color:C.dark}}>{param}</span>
                          {p.ac1!=null && <span style={{fontSize:"10.5px",color:T.muted,
                            fontFamily:FONT_MONO}}>AC1 {p.ac1}</span>}
                          {p.meanICvi!=null && <span style={{fontSize:"10.5px",color:T.muted,
                            fontFamily:FONT_MONO}}>mean I-CVI {p.meanICvi}</span>}
                          {p.coverage!=null && (
                            <span style={{fontSize:"10.5px",fontFamily:FONT_MONO,fontWeight:"700",
                              color:p.coverage>=0.99?T.success:p.coverage>=0.85?T.warn:T.danger}}>
                              covers {p.coveredExperts}/{p.expertsWhoAnswered}
                            </span>
                          )}
                        </div>
                        {p.keyOptions?.length>0 && (
                          <div style={{fontSize:"11px",color:T.muted,marginBottom:"7px",
                            lineHeight:"1.5"}}>
                            Key: <strong style={{color:C.dark}}>{p.keyOptions.join(", ")}</strong>
                          </div>
                        )}

                        {p.options.length>0 && <div style={{display:"flex",
                          flexDirection:"column",gap:"5px",marginBottom:"7px"}}>
                          {p.options.map(o=>(
                            <div key={o.option} style={{display:"flex",alignItems:"center",
                              gap:"9px",padding:"5px 9px",borderRadius:"7px",
                              background:o.inKey?"#f0fdf4":o.excluded?"#fafafa":"white",
                              border:`1px solid ${o.inKey?"#bbf7d0":T.border}`,
                              opacity:o.excluded?0.55:1}}>
                              <span style={{fontSize:"11.5px",color:C.dark,flex:1,minWidth:0}}>
                                {o.option}
                              </span>
                              <span style={{width:"70px",height:"5px",borderRadius:"3px",
                                background:T.bgDeep,overflow:"hidden",flexShrink:0}}>
                                <span style={{display:"block",height:"100%",
                                  width:`${o.agreement*100}%`,
                                  background:o.inKey?T.success:T.muted}}/>
                              </span>
                              <span style={{fontSize:"11px",color:T.muted,flexShrink:0,
                                fontFamily:FONT_MONO,fontVariantNumeric:"tabular-nums",
                                minWidth:"96px",textAlign:"end"}}>
                                {o.count}/{o.n} · CI {o.ci[0]}–{o.ci[1]}
                              </span>
                            </div>
                          ))}
                        </div>}

                        {p.range && <div style={{fontSize:"11.5px",color:T.muted,
                          padding:"7px 10px",background:"white",borderRadius:"7px",
                          border:`1px solid ${T.border}`,marginBottom:"7px"}}>
                          Median accepted range{" "}
                          <strong style={{color:C.dark}}>
                            {p.range.minMedian ?? "—"} to {p.range.maxMedian ?? "—"}
                          </strong>
                          {p.range.minRange && <> · lower bounds spanned {p.range.minRange[0]} to {p.range.minRange[1]}</>}
                          {" "}({p.range.n} reviewers)
                        </div>}

                        {p.autofail.filter(a=>a.count>0).length>0 && (
                          <div style={{display:"flex",flexWrap:"wrap",gap:"6px"}}>
                            {p.autofail.filter(a=>a.count>0).map(a=>(
                              <span key={a.option} style={{fontSize:"11px",padding:"3px 9px",
                                borderRadius:"8px",background:a.flagged?"#dc2626":"#fef2f2",
                                color:a.flagged?"white":"#991b1b",
                                border:"1px solid #fecaca",fontWeight:a.flagged?"700":"500"}}>
                                ⚑ {a.option} {a.count}/{a.n}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}

                    {c.actionLogic && Object.keys(c.actionLogic).length>0 && (
                      <div style={{marginBottom:"14px",padding:"11px 13px",
                        borderRadius:"9px",background:"white",border:`1px solid ${T.border}`}}>
                        <div style={{fontSize:"9.5px",fontWeight:"700",color:T.muted,
                          textTransform:"uppercase",letterSpacing:"0.12em",marginBottom:"6px"}}>
                          Multiple accepted actions
                        </div>
                        <div style={{fontSize:"12px",color:C.dark,lineHeight:"1.6"}}>
                          {Object.entries(c.actionLogic).map(([k,v])=>(
                            <span key={k} style={{marginInlineEnd:"12px"}}>
                              {k==="any"?"any one suffices":k==="all"?"all required":"a specific combination"}
                              <strong style={{marginInlineStart:"5px"}}>{v}</strong>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {c.comments.length>0 && <div style={{marginTop:"6px"}}>
                      <div style={{fontSize:"10px",fontWeight:"700",color:T.muted,
                        textTransform:"uppercase",letterSpacing:"0.13em",marginBottom:"7px"}}>
                        Reviewer comments
                      </div>
                      {c.comments.map((cm,i)=>(
                        <div key={i} style={{padding:"9px 12px",background:"white",
                          borderRadius:"8px",border:`1px solid ${T.border}`,marginBottom:"6px",
                          fontSize:"12px",color:C.dark,lineHeight:"1.6"}}>
                          <span style={{fontSize:"10px",color:T.muted,fontFamily:FONT_MONO,
                            textTransform:"uppercase",letterSpacing:"0.08em"}}>
                            {cm.discipline}
                          </span>
                          <div style={{marginTop:"3px"}}>{cm.text}</div>
                        </div>
                      ))}
                    </div>}
                  </div>}
                </div>;
              })}
        </>}
      </div>
    </div>
  </div>;
}

// ═══════════════════════════════════════════════════════════════════════════
//  EXPERT VALIDATION MODE
//
//  Reached only by an emailed link (?validate=<token>). Experts define the
//  answer key rather than attempt the case: for each prescription parameter
//  they mark every option they would accept, and flag anything unsafe enough
//  to fail the case outright.
//
//  Two design choices that carry the validity claim:
//
//   · The expert sees the REAL case view, not a text description of it. The
//     claim is that the instrument is valid, so the instrument is what gets
//     rated.
//
//   · Parameters carrying a case's teaching point are rated BLIND, with the
//     current key hidden. Showing it would measure deference rather than
//     judgement: most reviewers tick what is already there and add nothing.
//     Everything else is pre-filled, which keeps the task inside two hours.
// ═══════════════════════════════════════════════════════════════════════════

// Which parameters are blind for a given case. Derived from what the case is
// actually testing, so the teaching point is never pre-answered.
function blindParamsFor(caseData){
  const st = caseData?.successType || "";
  const map = {
    room1:            ["bfr","dialysateRate","repRate","netUf","access","filter"],
    clearance_gte:    ["bfr","dialysateRate","repRate","pbpRate"],
    clearance_lte:    ["bfr","dialysateRate","repRate","pbpRate"],
    effluent_kg_gte:  ["bfr","dialysateRate","repRate","pbpRate","netUf"],
    effluent_min_of:  ["bfr","dialysateRate","repRate","pbpRate","netUf"],
    ff_lte:           ["bfr","pbpRate","repRate","dialysateRate","netUf"],
    ecv:              ["prime","filter","access"],
    netuf_kghr_lte:   ["netUf"],
    bfr_lte:          ["bfr"],
    electrolytes:     ["interventions"],
    anticoag_not:     ["anticoag"],
    citrate_alkalosis:["anticoag","bfr","dialysateRate","repRate"],
    citrate_accum:    ["anticoag","bfr","dialysateRate","repRate"],
    epoprostenol:     ["anticoag"],
    access_not:       ["access"],
    na_add:           ["solution"],
    free_water:       ["solution"],
    intervention:     ["interventions"],
    clearance_lte_and_mannitol: ["bfr","dialysateRate","repRate","interventions"],
  };
  return new Set(map[st] || []);
}

// Parameters an expert rates, with their option sets pulled from the same
// definitions the trainee form uses, so answers map straight onto the key.
function validationParams(caseData){
  const o = caseData?.options || caseData?.caseOptions || null;
  const num = (label,unit,step=10) => ({kind:"range",label,unit,step});
  return [
    {id:"modality",      kind:"options", label:"Modality",
      opts: MODALITIES.map(m=>({value:m,label:m}))},
    {id:"access",        kind:"options", label:"Access catheter",
      opts: caseAccess(o).map(a=>({value:a,label:accessDefs[a]||a}))},
    {id:"filter",        kind:"options", label:"Filter",
      opts: caseFilters(o).map(f=>({value:f.key||f,
        label:`${f.brandLabel||f.key||f}${f.weightHint?` (${f.weightHint})`:""}`}))},
    {id:"prime",         kind:"options", label:"Circuit prime",
      opts: casePrimes(o).map(p=>({value:p,label:p.charAt(0).toUpperCase()+p.slice(1)}))},
    {id:"anticoag",      kind:"options", label:"Anticoagulation",
      opts: caseAnticoags(o).map(a=>({value:a,label:a.charAt(0).toUpperCase()+a.slice(1)}))},
    {id:"solution",      kind:"options", label:"Solution",
      opts: ALL_SOLUTIONS.map(s=>({value:s,label:solutionLabels[s]||s}))},
    {id:"bfr",           ...num("Blood flow rate","ml/min",5)},
    {id:"pbpRate",       ...num("Pre-blood pump","ml/hr",25)},
    {id:"dialysateRate", ...num("Dialysate rate","ml/hr",25)},
    {id:"repRate",       ...num("Post-filter replacement","ml/hr",25)},
    {id:"netUf",         ...num("Net fluid removal","ml/hr",5)},
    {id:"interventions", kind:"options", label:"Bedside actions",
      opts: allInterventions.map(i=>({value:i.id,label:i.label}))},
  ];
}


// Derived figures for the case as written, shown to reviewers alongside the
// prescription. Uses the same formulas as the trainee calcs panel and the
// scorer, so a reviewer and a learner are reading the same numbers.
function ValidationCalcs({caseData}){
  const rx = caseData?.initialPrescription || {};
  const st = caseData?.initialState || {};
  const wt = parseFloat(st.weightKg) || 0;
  const bsa = parseFloat(st.bsa) || 0;
  const pbp = parseFloat(rx.pbpRate) || 0;
  const dial = parseFloat(rx.dialysateRate) || 0;
  const rep = parseFloat(rx.repRate) || 0;
  const nuf = parseFloat(rx.netUf) || 0;
  const bfr = parseFloat(rx.bfr) || 0;

  const efflKg  = wt  ? (pbp + dial + rep + nuf) / wt : null;
  const efflBsa = bsa ? ((pbp + dial + rep + nuf) / 1000) / bsa * 1.73 : null;
  const hct = (()=>{ const h = parseFloat(st.cbc?.Hct);
                     return (isFinite(h) && h > 0 && h < 1) ? h : 0.30; })();
  const plasma = bfr * 60 * (1 - hct);
  const ff = (plasma + pbp) > 0 ? ((rep + nuf + pbp) / (plasma + pbp)) * 100 : null;
  const ecv = resolveFilterEcv(rx.filter, caseData?.options || caseData?.caseOptions);
  const tbv = calculateTBV(wt, st.ageMonths);
  const ecvPct = (ecv && tbv) ? (ecv / tbv) * 100 : null;

  const Row = ({label, val, unit, tone, eq}) => (
    <div style={{padding:"7px 10px",background:"white",borderRadius:"7px",
      border:`1px solid ${tone||"#e4dcfb"}26`,marginBottom:"5px"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",gap:"9px"}}>
        <span style={{fontSize:"10.5px",color:"#7c6aa0",fontFamily:FONT_MONO,
          textTransform:"uppercase",letterSpacing:"0.07em"}}>{label}</span>
        <span style={{fontSize:"15px",fontWeight:"750",fontFamily:FONT_MONO,
          color:tone||"#5b21b6",fontVariantNumeric:"tabular-nums"}}>
          {val==null ? "—" : val}
          <span style={{fontSize:"9px",fontWeight:"500",color:"#a99cc4",
            marginInlineStart:"4px"}}>{unit}</span>
        </span>
      </div>
      {eq && <div style={{fontSize:"8.5px",color:"#a99cc4",marginTop:"3px",
        fontFamily:FONT_MONO,lineHeight:"1.45"}}>{eq}</div>}
    </div>
  );

  const ffTone = ff==null ? null : ff>=25 ? "#be123c" : ff>=20 ? "#b45309" : "#047857";

  return <div style={{background:"#faf8ff",borderRadius:"10px",
    border:"1px solid #e4dcfb",padding:"11px"}}>
    <div style={{fontSize:"9.5px",fontWeight:"700",color:"#6d28d9",
      textTransform:"uppercase",letterSpacing:"0.13em",marginBottom:"9px",
      fontFamily:FONT_MONO}}>As prescribed</div>
    <Row label="Effluent / wt" val={efflKg!=null?efflKg.toFixed(1):null} unit="ml/kg/hr"
      eq="(PBP + dialysate + post-filter + net UF) ÷ weight"/>
    <Row label="Effluent / BSA" val={efflBsa!=null?efflBsa.toFixed(2):null} unit="L/1.73m²/hr"
      eq="same total ÷ BSA × 1.73"/>
    <Row label="Filtration fraction" val={ff!=null?ff.toFixed(1):null} unit="%" tone={ffTone}
      eq="(post-filter + net UF + PBP) ÷ (plasma flow + PBP) × 100"/>
    <Row label="Net UF / wt" val={wt?(nuf/wt).toFixed(2):null} unit="ml/kg/hr"/>
    <Row label="Circuit : blood volume" val={ecvPct!=null?ecvPct.toFixed(1):null} unit="%"
      eq={`ECV ${ecv||"—"} ml ÷ blood volume ${tbv||"—"} ml`}/>
  </div>;
}


// ─── REVIEWER DISCIPLINE ─────────────────────────────────────────────────────
// The only thing the expert panel tells us about themselves, and the only
// attribute the platform stores about a reviewer. It is confirmed by the
// reviewer rather than taken from the admin's assignment, because every
// agreement statistic is stratified by it: an I-CVI reported "by discipline"
// where the discipline came from a spreadsheet guess is reporting the guess.
//
// Values must match VALID_DISCIPLINES in index.js. Four options, no free text:
// a free-text specialty cannot be stratified on, and "other" is an honest
// answer that a text box would turn into forty spellings of three things.
const REVIEWER_DISCIPLINES = [
  ["paediatric_nephrology",     "Paediatric nephrology"],
  ["paediatric_critical_care",  "Paediatric critical care"],
  ["crrt_nursing",              "CRRT nursing or nursing education"],
  ["other",                     "Other"],
];

// One screen, one question, before the first case. Not a profile form: nothing
// else is asked, because nothing else is stored.
function DisciplineConfirm({ initial, busy, err, onConfirm }){
  const [pick,setPick] = useState(initial || "");
  return <Centered>
    <div style={{background:"white",borderRadius:"16px",padding:"28px 30px",
      maxWidth:"500px",border:`1px solid ${T.border}`,
      boxShadow:"0 12px 40px rgba(79,38,131,0.12)"}}>
      <div style={{fontSize:"20px",fontWeight:"900",color:"#4F2683",
        marginBottom:"4px"}}>KiRPA</div>
      <h2 style={{margin:"0 0 10px",fontSize:"17px",fontWeight:"700",color:C.dark}}>
        Before you begin
      </h2>
      <p style={{margin:"0 0 16px",fontSize:"13.5px",color:"#4b5563",
        lineHeight:"1.7"}}>
        Please confirm your discipline. Agreement across the panel is reported
        by discipline, so this needs to come from you rather than from our
        assignment list. It is the only thing we record about you.
      </p>

      <div style={{display:"grid",gap:"7px",marginBottom:"18px"}}>
        {REVIEWER_DISCIPLINES.map(([v,l])=>{
          const on = pick===v;
          return <button key={v} onClick={()=>setPick(v)} type="button"
            style={{padding:"11px 14px",borderRadius:"10px",cursor:"pointer",
              textAlign:"start",fontFamily:"inherit",fontSize:"13.5px",
              fontWeight:on?"700":"450",lineHeight:"1.4",
              border:`1px solid ${on?T.accent:T.border}`,
              background:on?T.accentLight:"white",
              color:on?T.accentDark:C.dark}}>
            {on?"\u2713 ":""}{l}
          </button>;
        })}
      </div>

      {err && <div style={{fontSize:"12px",color:"#dc2626",marginBottom:"12px",
        lineHeight:"1.6"}}>{err}</div>}

      <button onClick={()=>onConfirm(pick)} disabled={!pick||busy}
        style={{...C.btn(C.pu),width:"100%",justifyContent:"center",
          padding:"12px",fontSize:"14px",opacity:(!pick||busy)?0.55:1,
          cursor:(!pick||busy)?"not-allowed":"pointer"}}>
        {busy?<Ic n="spin" s="w-4 h-4"/>:null}
        Continue
      </button>

      {/* Reference only. Phase 1 consent was taken in Qualtrics before this
          link was issued, so this is not a consent step and does not gate
          anything. */}
      <div style={{marginTop:"15px",paddingTop:"14px",
        borderTop:`1px solid ${T.border}`,fontSize:"11.5px",color:T.muted,
        lineHeight:"1.65"}}>
        <a href={LOI_PATH} target="_blank" rel="noopener noreferrer"
          style={{color:T.accent,textDecoration:"underline",fontWeight:"600"}}>
          Letter of Information
        </a>
        {" "}— for your reference. You consented before this link was sent to you.
      </div>
    </div>
  </Centered>;
}

function ValidationMode({token,onExit}){
  const [busy,setBusy]       = useState(true);
  const [err,setErr]         = useState("");
  const [expert,setExpert]   = useState(null);
  const [ratings,setRatings] = useState({});
  const [idx,setIdx]         = useState(0);
  const [saving,setSaving]   = useState(false);
  const [intro,setIntro]     = useState(true);
  const [feedback,setFeedback] = useState({});   // round 2 only
  const [showIntent,setShowIntent] = useState(false);
  // Discipline confirmation, shown once before the first case. Held locally so
  // the screen clears the moment the write succeeds rather than after a refetch.
  const [discConfirmed,setDiscConfirmed] = useState(false);
  const [discBusy,setDiscBusy]   = useState(false);
  const [discErr,setDiscErr]     = useState("");
  // Two columns above 1100px; below that the case sits above the form.
  const wideVal = useViewport("(min-width: 1100px)");
  const startedRef = useRef(Date.now());

  useEffect(()=>{let dead=false;(async()=>{
    try{
      const fns=getFunctions(firebaseApp,"us-central1");
      const res=await httpsCallable(fns,"validateExpertToken")({token});
      if(dead) return;
      const ex = res?.data?.expert||null;
      setExpert(ex);
      // A reviewer who already confirmed is not asked again, including on a
      // reissued link or in round 2.
      setDiscConfirmed(ex?.disciplineConfirmed === true);
      setRatings(res?.data?.ratings||{});
      // Round 2 shows the anonymised group result alongside each answer.
      if(ex && (ex.round||1) >= 2){
        try{
          const fb = await httpsCallable(fns,"getValidationFeedback")({expertId:ex.expertId});
          if(!dead) setFeedback(fb?.data?.feedback||{});
        }catch(e){ console.warn("[KiRPA validation] feedback",e); }
      }
    }catch(e){
      if(!dead) setErr(e?.message||"This link is not valid.");
    }
    if(!dead) setBusy(false);
    try{ window.history.replaceState({},document.title,window.location.pathname); }catch{}
  })();return()=>{dead=true;};},[token]);

  const caseIds  = expert?.caseIds || [];
  const caseId   = caseIds[idx] || "";
  const caseData = BASE_CASES[caseId] || null;
  const current  = ratings[caseId] || {acceptable:{},ranges:{},autofail:{},
    dose:{}, doseUnit:"mlkg", componentsFree:true, ffMax:null,
    actionLogic:"", actionLogicNote:"",
    comment:"", intentComment:""};
  // Blinding applies to round 1 only. By round 2 the expert has already given
  // an independent answer, so hiding the key would serve no purpose.
  const blind    = (caseData && (expert?.round||1)<2) ? blindParamsFor(caseData) : new Set();
  const params   = caseData ? validationParams(caseData) : [];

  // Autosave. Debounced so a burst of checkbox clicks is one write.
  const saveRef = useRef(null);
  const persist = (next, complete)=>{
    setRatings(p=>({...p,[caseId]:next}));
    clearTimeout(saveRef.current);
    saveRef.current = setTimeout(async()=>{
      setSaving(true);
      try{
        const fns=getFunctions(firebaseApp,"us-central1");
        await httpsCallable(fns,"saveValidationRating")({
          expertId: expert.expertId, caseId,
          rating: {...next, complete: complete ?? isComplete(next),
            secondsSpent: Math.round((Date.now()-startedRef.current)/1000)},
        });
      }catch(e){ console.warn("[KiRPA validation] save",e); }
      setSaving(false);
    }, 700);
  };

  // A case counts as done when every parameter has either an accepted option
  // or a stated range. Partial work still saves; it just is not counted.
  // Complete when every option parameter has at least one accepted value and a
  // total dose has been given. Individual pump rates are deliberately NOT
  // required: a reviewer who says the split can be anything has answered fully.
  const isComplete = (r)=>{
    if(!params.length) return false;
    const doseSet = r.dose?.min!=null || r.dose?.max!=null;
    const optsOk = params.filter(p=>p.kind==="options")
      .every(p=>(r.acceptable?.[p.id]||[]).length>0);
    return doseSet && optsOk;
  };

  const toggleOpt = (pid,val)=>{
    const cur = current.acceptable?.[pid]||[];
    const nxt = cur.includes(val)?cur.filter(v=>v!==val):[...cur,val];
    persist({...current, acceptable:{...current.acceptable,[pid]:nxt}});
  };
  const toggleFail = (pid,val)=>{
    const cur = current.autofail?.[pid]||[];
    const nxt = cur.includes(val)?cur.filter(v=>v!==val):[...cur,val];
    persist({...current, autofail:{...current.autofail,[pid]:nxt}});
  };
  const setRange = (pid,key,val)=>{
    const v = val==="" ? null : parseFloat(val);
    persist({...current, ranges:{...current.ranges,
      [pid]:{...(current.ranges?.[pid]||{}),[key]:isFinite(v)?v:null}}});
  };

  if(busy) return <Centered><Ic n="spin" s="w-6 h-6"/></Centered>;
  if(err)  return <Centered>
    <div style={{maxWidth:"360px",textAlign:"center"}}>
      <div style={{fontSize:"30px",marginBottom:"10px"}}>⚠</div>
      <div style={{fontSize:"15px",fontWeight:"700",color:C.dark,marginBottom:"8px"}}>Link not valid</div>
      <div style={{fontSize:"13px",color:"#6b7280",lineHeight:"1.6"}}>{err}</div>
    </div>
  </Centered>;

  // ── Discipline confirmation, before anything else ──
  // Ahead of the briefing, which is itself ahead of the first case. The
  // briefing explains the rating task; there is no point explaining it before
  // we know who is doing the rating, and the stratifying variable has to be in
  // hand before the first rating is saved rather than patched in afterwards.
  if(expert && !discConfirmed) return (
    <DisciplineConfirm
      initial={expert.discipline||""}
      busy={discBusy}
      err={discErr}
      onConfirm={async(d)=>{
        setDiscBusy(true); setDiscErr("");
        try{
          const fns=getFunctions(firebaseApp,"us-central1");
          await httpsCallable(fns,"setValidationDiscipline")({
            expertId: expert.expertId, discipline: d});
          setExpert(e=>({...e, discipline:d, disciplineConfirmed:true}));
          setDiscConfirmed(true);
        }catch(e){
          // Not skippable. Ratings saved without it would carry the admin's
          // guess, and a by-discipline agreement figure built on that is worse
          // than one the reviewer was asked to wait three seconds for.
          setDiscErr(e?.message||"Could not save that. Please try again.");
        }
        setDiscBusy(false);
      }}/>
  );

  // ── Briefing, shown once ──
  if(intro) return <Centered>
    <div style={{background:"white",borderRadius:"16px",padding:"30px",maxWidth:"560px",
      border:`1px solid ${T.border}`,boxShadow:"0 12px 40px rgba(79,38,131,0.12)"}}>
      <div style={{fontSize:"20px",fontWeight:"900",color:"#4F2683",marginBottom:"4px"}}>KiRPA</div>
      <h2 style={{margin:"0 0 14px",fontSize:"18px",fontWeight:"700",color:C.dark}}>
        CRRT case review{(expert?.round||1)>=2 ? " \u2014 round 2" : ""}
      </h2>
      <p style={{margin:"0 0 12px",fontSize:"13.5px",color:"#4b5563",lineHeight:"1.7"}}>
        {/* No name: none is stored. Phase 1 consent is in Qualtrics and this
            platform holds only a random token and a discipline. */}
        Thank you for reviewing these cases. You have <strong>{caseIds.length}</strong>{" "}
        to work through, around 15 minutes each.
      </p>
      <p style={{margin:"0 0 12px",fontSize:"13.5px",color:"#4b5563",lineHeight:"1.7"}}>
        For each case you will see exactly what a trainee sees. Below it, mark
        <strong> every option you would consider acceptable</strong> for that patient,
        not only the one you would choose yourself. Where a value is numeric, give
        the range you would accept.
      </p>
      <p style={{margin:"0 0 12px",fontSize:"13.5px",color:"#4b5563",lineHeight:"1.7"}}>
        If an option is not merely suboptimal but unsafe, tick the
        <strong> flag</strong> beside it. Those become automatic failures.
      </p>
      <div style={{padding:"12px 14px",background:"#f8f7ff",borderRadius:"10px",
        border:`1px solid ${T.border}`,fontSize:"12.5px",color:"#6b7280",
        lineHeight:"1.6",marginBottom:"20px"}}>
        {(expert?.round||1)>=2
          ? <>You will now see how the rest of the panel answered, anonymously,
              beside your own responses. Change anything you would like to
              reconsider. Where you still disagree with the majority, leaving
              your answer unchanged is a perfectly good outcome; genuine
              disagreement is worth recording. Most people change little, so this
              round is quicker.</>
          : <>Some parameters are left blank deliberately. Those are the ones each
              case is built to test, and we want your independent judgement rather
              than agreement with what is already there. Your progress saves
              automatically, so you can stop and return to the same link at any
              time.</>}
      </div>
      <button onClick={()=>{setIntro(false);startedRef.current=Date.now();}}
        style={{...C.btn(C.pu),width:"100%",justifyContent:"center",padding:"12px",fontSize:"14px"}}>
        Begin review
      </button>
    </div>
  </Centered>;

  if(!caseData) return <Centered>
    <div style={{textAlign:"center",color:"#6b7280",fontSize:"13px"}}>
      Case {caseId} not found.
    </div>
  </Centered>;

  const doneCount = caseIds.filter(id=>ratings[id]?.complete).length;

  return <div style={{minHeight:"100vh",background:T.bg,fontFamily:FONT_UI}}>
    {/* Sticky progress bar: reviewers abandon long tasks far less often when
        they can see how much is left. */}
    <div style={{position:"sticky",top:0,zIndex:20,background:"white",
      borderBottom:`1px solid ${T.border}`,padding:"11px 18px",
      display:"flex",alignItems:"center",gap:"12px"}}>
      <div style={{fontSize:"15px",fontWeight:"900",color:"#4F2683"}}>KiRPA</div>
      <div style={{fontSize:"12px",color:T.muted,fontFamily:FONT_MONO}}>
        Case {idx+1} of {caseIds.length}
      </div>
      {(expert?.round||1)>=2 && (
        <span style={{fontSize:"10px",fontWeight:"700",color:T.accentDark,
          background:T.accentLight,borderRadius:"8px",padding:"2px 8px",
          fontFamily:FONT_MONO,letterSpacing:"0.06em"}}>ROUND 2</span>
      )}
      <div style={{flex:1,maxWidth:"220px",height:"6px",borderRadius:"3px",
        background:T.bgDeep,overflow:"hidden"}}>
        <div style={{height:"100%",width:`${(doneCount/Math.max(1,caseIds.length))*100}%`,
          background:T.accent,borderRadius:"3px",transition:"width 0.5s ease"}}/>
      </div>
      <span style={{fontSize:"11px",color:saving?T.accent:T.muted,marginInlineStart:"auto",
        fontFamily:FONT_MONO}}>
        {saving ? "Saving…" : `${doneCount} complete`}
      </span>
      {/* Reference only, not a consent step: Phase 1 consent was taken in
          Qualtrics before this link was issued. New tab, so a reviewer who
          opens it mid-case does not lose a part-finished rating. */}
      <a href={LOI_PATH} target="_blank" rel="noopener noreferrer"
        style={{fontSize:"11px",color:T.accent,textDecoration:"underline",
          fontWeight:"600",whiteSpace:"nowrap"}}>
        Letter of Information
      </a>
    </div>

    <div style={{maxWidth:wideVal?"1320px":"760px",margin:"0 auto",
      padding:wideVal?"18px 20px 90px":"18px 16px 90px",
      display:wideVal?"grid":"block",
      gridTemplateColumns:wideVal?"minmax(360px,470px) 1fr":undefined,
      gap:wideVal?"20px":undefined,alignItems:wideVal?"start":undefined}}>

      {/* Left: the case, sticky on a wide screen so it stays in view while the
          reviewer works down a long form. Scrolling back and forth between the
          vignette and the questions is what made the old layout tiring. */}
      <div style={{position:wideVal?"sticky":"static",top:wideVal?"66px":undefined,
        maxHeight:wideVal?"calc(100vh - 86px)":undefined,
        overflowY:wideVal?"auto":undefined,paddingInlineEnd:wideVal?"4px":undefined}}>
      <div style={{background:"white",borderRadius:"14px",border:`1px solid ${T.border}`,
        padding:"18px 20px",marginBottom:"14px"}}>
        <div style={{fontSize:"10px",fontWeight:"700",color:T.muted,textTransform:"uppercase",
          letterSpacing:"0.14em",marginBottom:"7px",fontFamily:FONT_MONO}}>
          The case as a trainee sees it
        </div>
        <div style={{fontSize:"15px",fontFamily:FONT_CLIN,color:"#33261f",
          lineHeight:"1.75",maxWidth:"62ch",marginBottom:"14px"}}>
          {caseData.page}
        </div>
        <div style={{marginBottom:"14px"}}>
          <ScenarioBanner caseData={caseData}/>
        </div>
        <PatientSnapshot initialState={caseData.initialState} useSI={true}/>
        <div style={{marginTop:"14px"}}>
          <PrescriptionDisplay caseId={caseId} rx={caseData.initialPrescription}
            initialState={caseData.initialState}/>
        </div>
        {/* The same derived figures a trainee sees, for the prescription as it
            currently stands. A reviewer judging whether a dose is acceptable
            needs to know what the current one actually works out to. */}
        <div style={{marginTop:"14px"}}>
          <ValidationCalcs caseData={caseData}/>
        </div>
      </div>
      </div>

      {/* Right: the rating form */}
      <div style={{minWidth:0}}>

      {/* ── Total dose and filtration fraction ────────────────────────────
          Reviewers think in total effluent dose, not in four separate pump
          rates. This asks for the target first, then lets them constrain an
          individual component only if they actually care about it. Anything
          left blank means "distribute it however you like". */}
      <div style={{background:"white",borderRadius:"12px",border:`1px solid ${T.border}`,
        padding:"15px 17px",marginBottom:"10px"}}>
        <div style={{fontSize:"13.5px",fontWeight:"750",color:C.dark,marginBottom:"3px"}}>
          Total effluent dose
        </div>
        <div style={{fontSize:"11.5px",color:T.muted,marginBottom:"11px",lineHeight:"1.55"}}>
          The total you would accept for this patient, counting pre-blood pump,
          dialysate, post-filter replacement and net ultrafiltration together.
        </div>

        <div style={{display:"flex",gap:"5px",marginBottom:"11px"}}>
          {[["mlkg","ml/kg/hr"],["bsa","L/1.73m²/hr"]].map(([u,lbl])=>{
            const on=(current.doseUnit||"mlkg")===u;
            return <button key={u} onClick={()=>persist({...current,doseUnit:u})}
              style={{padding:"5px 12px",borderRadius:"8px",cursor:"pointer",
                fontFamily:"inherit",fontSize:"11.5px",fontWeight:on?"700":"500",
                border:`1px solid ${on?T.accent:T.border}`,
                background:on?T.accentLight:"white",color:on?T.accentDark:T.text}}>
              {lbl}
            </button>;
          })}
        </div>

        <div style={{display:"flex",alignItems:"center",gap:"9px",flexWrap:"wrap"}}>
          <span style={{fontSize:"12px",color:T.muted}}>from</span>
          <input type="number" step={(current.doseUnit||"mlkg")==="bsa"?0.1:1}
            value={current.dose?.min??""}
            onChange={e=>persist({...current,dose:{...(current.dose||{}),
              min:e.target.value===""?null:parseFloat(e.target.value)}})}
            style={{...C.inp,maxWidth:"108px"}}/>
          <span style={{fontSize:"12px",color:T.muted}}>to</span>
          <input type="number" step={(current.doseUnit||"mlkg")==="bsa"?0.1:1}
            value={current.dose?.max??""}
            onChange={e=>persist({...current,dose:{...(current.dose||{}),
              max:e.target.value===""?null:parseFloat(e.target.value)}})}
            style={{...C.inp,maxWidth:"108px"}}/>
          <span style={{fontSize:"11.5px",color:T.muted}}>
            {(current.doseUnit||"mlkg")==="bsa"?"L/1.73m²/hr":"ml/kg/hr"}
          </span>
        </div>

        <label style={{display:"flex",alignItems:"center",gap:"8px",marginTop:"12px",
          fontSize:"12.5px",color:C.dark,cursor:"pointer"}}>
          <input type="checkbox" checked={!!current.componentsFree}
            onChange={e=>persist({...current,componentsFree:e.target.checked})}/>
          The split between pre-filter, dialysate and post-filter can be anything,
          as long as the total is met
        </label>

        {!current.componentsFree && (
          <div style={{marginTop:"11px",fontSize:"11.5px",color:T.muted,lineHeight:"1.55"}}>
            If you would constrain an individual component, set it in the
            parameter sections below. Leave any you do not care about blank.
          </div>
        )}

        <div style={{marginTop:"15px",paddingTop:"13px",borderTop:`1px solid ${T.border}`}}>
          <div style={{fontSize:"13.5px",fontWeight:"750",color:C.dark,marginBottom:"3px"}}>
            Filtration fraction ceiling
          </div>
          <div style={{fontSize:"11.5px",color:T.muted,marginBottom:"9px",lineHeight:"1.55"}}>
            Optional. Leave blank if you would not set one for this case.
          </div>
          <div style={{display:"flex",alignItems:"center",gap:"9px"}}>
            <span style={{fontSize:"14px",fontWeight:"700",color:C.dark}}>≤</span>
            <input type="number" step="1" placeholder="e.g. 20"
              value={current.ffMax??""}
              onChange={e=>persist({...current,
                ffMax:e.target.value===""?null:parseFloat(e.target.value)})}
              style={{...C.inp,maxWidth:"108px"}}/>
            <span style={{fontSize:"11.5px",color:T.muted}}>%</span>
          </div>
        </div>
      </div>

      {params.map(p=>{
        const isBlind = blind.has(p.id);
        const accepted = current.acceptable?.[p.id]||[];
        const failed   = current.autofail?.[p.id]||[];
        const rg       = current.ranges?.[p.id]||{};
        const keyVal   = caseData.initialPrescription?.[p.id];
        // Round 2: anonymised counts from everyone who has rated this case.
        const fb       = feedback[caseId];
        const fbOpts   = fb?.options?.[p.id] || null;
        const fbFail   = fb?.autofail?.[p.id] || null;
        const fbRange  = fb?.ranges?.[p.id] || null;
        const fbN      = fb?.n || 0;

        return <div key={p.id} style={{background:"white",borderRadius:"12px",
          border:`1px solid ${isBlind?"#fde68a":T.border}`,padding:"15px 17px",
          marginBottom:"10px"}}>

          <div style={{display:"flex",alignItems:"baseline",gap:"9px",marginBottom:"10px",
            flexWrap:"wrap"}}>
            <span style={{fontSize:"13.5px",fontWeight:"750",color:C.dark}}>{p.label}</span>
            {isBlind
              ? <span style={{fontSize:"10px",fontWeight:"700",color:"#92400e",
                  background:"#fffbeb",border:"1px solid #fde68a",borderRadius:"8px",
                  padding:"2px 8px"}}>independent judgement</span>
              : keyVal!==undefined && keyVal!=="" && (
                <span style={{fontSize:"11px",color:T.muted}}>
                  currently set to <strong style={{color:C.dark}}>{String(keyVal)}</strong>
                </span>
              )}
          </div>


          {p.kind==="options" ? (
            <div style={{display:"flex",flexDirection:"column",gap:"5px"}}>
              {p.opts.map(o=>{
                const on = accepted.includes(o.value);
                const bad = failed.includes(o.value);
                return <div key={o.value} style={{display:"flex",alignItems:"center",gap:"9px",
                  padding:"7px 10px",borderRadius:"8px",
                  background:bad?"#fef2f2":on?T.accentLight:"transparent",
                  border:`1px solid ${bad?"#fecaca":on?T.accentMid:"transparent"}`}}>
                  <button onClick={()=>toggleOpt(p.id,o.value)}
                    style={{flex:1,display:"flex",alignItems:"center",gap:"9px",
                      background:"none",border:"none",cursor:"pointer",textAlign:"start",
                      fontFamily:"inherit",padding:0}}>
                    <span style={{width:"16px",height:"16px",borderRadius:"4px",flexShrink:0,
                      border:`1.5px solid ${on?T.accent:"#cbd5e1"}`,
                      background:on?T.accent:"white",color:"white",fontSize:"11px",
                      display:"flex",alignItems:"center",justifyContent:"center",
                      fontWeight:"800",lineHeight:1}}>{on?"✓":""}</span>
                    <span style={{fontSize:"12.5px",color:bad?"#991b1b":C.dark,
                      fontWeight:on?"650":"450"}}>{o.label}</span>
                  </button>
                  {/* Panel result. Shown only in round 2, and only as counts:
                      naming who said what would turn a Delphi into a debate. */}
                  {fbOpts && fbN>0 && (()=>{
                    const cnt = fbOpts[o.value]||0;
                    const pctv = Math.round(cnt/fbN*100);
                    const failCnt = fbFail?.[o.value]||0;
                    return <span style={{flexShrink:0,display:"flex",alignItems:"center",
                      gap:"6px",marginInlineEnd:"4px"}}>
                      <span style={{width:"46px",height:"5px",borderRadius:"3px",
                        background:T.bgDeep,overflow:"hidden",display:"block"}}>
                        <span style={{display:"block",height:"100%",width:`${pctv}%`,
                          background:pctv>=75?T.success:pctv>=40?T.warn:T.danger}}/>
                      </span>
                      <span style={{fontSize:"10.5px",color:T.muted,fontFamily:FONT_MONO,
                        minWidth:"34px",fontVariantNumeric:"tabular-nums"}}>
                        {cnt}/{fbN}
                      </span>
                      {failCnt>0 && <span title={`${failCnt} flagged this unsafe`}
                        style={{fontSize:"10px",color:"#dc2626"}}>⚑{failCnt}</span>}
                    </span>;
                  })()}
                  <button onClick={()=>toggleFail(p.id,o.value)}
                    title="Unsafe — should fail the case outright"
                    style={{flexShrink:0,padding:"2px 8px",borderRadius:"7px",cursor:"pointer",
                      fontFamily:"inherit",fontSize:"10.5px",fontWeight:"700",
                      border:`1px solid ${bad?"#dc2626":"#e5e7eb"}`,
                      background:bad?"#dc2626":"white",color:bad?"white":"#9ca3af"}}>
                    ⚑ unsafe
                  </button>
                </div>;
              })}
            </div>
          ) : (
            <div>
              <div style={{fontSize:"11.5px",color:T.muted,marginBottom:"8px",lineHeight:"1.55"}}>
                The range you would accept for this patient. Leave a field blank if
                you would not set a bound on that side.
              </div>
              <div style={{display:"flex",alignItems:"center",gap:"9px",flexWrap:"wrap"}}>
                <span style={{fontSize:"12px",color:T.muted}}>from</span>
                <input type="number" step={p.step} value={rg.min??""}
                  onChange={e=>setRange(p.id,"min",e.target.value)}
                  style={{...C.inp,maxWidth:"110px"}}/>
                <span style={{fontSize:"12px",color:T.muted}}>to</span>
                <input type="number" step={p.step} value={rg.max??""}
                  onChange={e=>setRange(p.id,"max",e.target.value)}
                  style={{...C.inp,maxWidth:"110px"}}/>
                <span style={{fontSize:"11.5px",color:T.muted}}>{p.unit}</span>
              </div>
              {fbRange && fbRange.n>0 && (
                <div style={{marginTop:"9px",padding:"8px 11px",borderRadius:"8px",
                  background:"#f8f7ff",border:`1px solid ${T.border}`,
                  fontSize:"11.5px",color:T.muted,lineHeight:"1.55"}}>
                  The panel's median was{" "}
                  <strong style={{color:C.dark}}>
                    {fbRange.minMedian ?? "—"} to {fbRange.maxMedian ?? "—"} {p.unit}
                  </strong>
                  {" "}across {fbRange.n} {fbRange.n===1?"reviewer":"reviewers"}.
                </div>
              )}
            </div>
          )}
        </div>;
      })}

      <div style={{background:"white",borderRadius:"12px",border:`1px solid ${T.border}`,
        padding:"15px 17px",marginBottom:"14px"}}>
        <div style={{fontSize:"13.5px",fontWeight:"750",color:C.dark,marginBottom:"4px"}}>
          Anything else about this case
        </div>
        <div style={{fontSize:"11.5px",color:T.muted,marginBottom:"9px",lineHeight:"1.55"}}>
          Decisions the options above do not capture, concerns about the scenario
          itself, or anything you would change.
        </div>
        <textarea value={current.comment||""}
          onChange={e=>persist({...current,comment:e.target.value})}
          placeholder="Optional"
          style={{...C.inp,minHeight:"90px",resize:"vertical",lineHeight:"1.6"}}/>
      </div>

      {/* Multi-select expresses OR but cannot express AND. An expert ticking
          two actions on Room 9 means either is enough; the same two ticks on
          Room 11 mean both are required, and the data is identical in shape.
          Asked once per case, and only when more than one action is selected,
          rather than as a checkbox on every parameter. */}
      {(current.acceptable?.interventions||[]).length > 1 && (
        <div style={{background:"white",borderRadius:"12px",
          border:`1px solid ${T.border}`,padding:"15px 17px",marginBottom:"10px"}}>
          <div style={{fontSize:"13.5px",fontWeight:"750",color:C.dark,marginBottom:"4px"}}>
            You marked more than one action acceptable
          </div>
          <div style={{fontSize:"11.5px",color:T.muted,marginBottom:"11px",lineHeight:"1.55"}}>
            Is any one of them enough on its own, or do they need to be done together?
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:"6px"}}>
            {[["any","Any one of them is enough"],
              ["all","They all need to be done together"],
              ["some","Some combination, described below"]].map(([k,label])=>{
              const on=(current.actionLogic||"")===k;
              return <button key={k} onClick={()=>persist({...current,actionLogic:k})}
                style={{display:"flex",alignItems:"center",gap:"9px",padding:"9px 12px",
                  borderRadius:"9px",cursor:"pointer",textAlign:"start",fontFamily:"inherit",
                  border:`1px solid ${on?T.accent:T.border}`,
                  background:on?T.accentLight:"white",
                  color:on?T.accentDark:T.text,fontSize:"12.5px",
                  fontWeight:on?"650":"450"}}>
                <span style={{width:"15px",height:"15px",borderRadius:"50%",flexShrink:0,
                  border:`1.5px solid ${on?T.accent:"#cbd5e1"}`,
                  background:on?T.accent:"white",display:"flex",alignItems:"center",
                  justifyContent:"center",color:"white",fontSize:"9px",
                  fontWeight:"800",lineHeight:1}}>{on?"\u25cf":""}</span>
                {label}
              </button>;
            })}
          </div>
          {current.actionLogic==="some" && (
            <textarea value={current.actionLogicNote||""}
              onChange={e=>persist({...current,actionLogicNote:e.target.value})}
              placeholder="For example: pausing citrate and calcium must go together, but raising the clearance would do on its own."
              style={{...C.inp,minHeight:"68px",resize:"vertical",lineHeight:"1.6",
                marginTop:"10px"}}/>
          )}
        </div>
      )}

      {/* What the case is designed to teach. Hidden until asked for, so the
          reviewer's own ratings are not anchored to the intended answer. They
          can then comment on the intent itself. */}
      <div style={{background:"white",borderRadius:"12px",border:`1px solid ${T.border}`,
        padding:"15px 17px",marginBottom:"14px"}}>
        <button onClick={()=>setShowIntent(v=>!v)}
          style={{width:"100%",background:"none",border:"none",cursor:"pointer",
            fontFamily:"inherit",textAlign:"start",display:"flex",alignItems:"center",
            gap:"9px",padding:0}}>
          <span style={{fontSize:"13.5px",fontWeight:"750",color:C.dark}}>
            What this case is intended to teach
          </span>
          <span style={{marginInlineStart:"auto",color:T.muted,fontSize:"11px"}}>
            {showIntent?"\u25be":"\u25b8"}
          </span>
        </button>
        {!showIntent && <div style={{fontSize:"11.5px",color:T.muted,marginTop:"5px",
          lineHeight:"1.55"}}>
          Hidden until you ask for it, so it does not steer the ratings above.
        </div>}
        {showIntent && <div style={{marginTop:"11px"}}>
          <div style={{padding:"11px 13px",borderRadius:"9px",background:"#fefce8",
            border:"1px solid #fde68a",marginBottom:"9px"}}>
            <div style={{fontSize:"9.5px",fontWeight:"700",color:"#92400e",
              textTransform:"uppercase",letterSpacing:"0.12em",marginBottom:"4px"}}>
              Current pass criterion
            </div>
            <div style={{fontSize:"12.5px",color:"#78350f",lineHeight:"1.6"}}>
              {caseData.successSummary || "Not stated."}
            </div>
          </div>
          <div style={{padding:"11px 13px",borderRadius:"9px",background:"#f0fdf4",
            border:"1px solid #bbf7d0",marginBottom:"11px"}}>
            <div style={{fontSize:"9.5px",fontWeight:"700",color:"#14532d",
              textTransform:"uppercase",letterSpacing:"0.12em",marginBottom:"4px"}}>
              Learning point shown after the attempt
            </div>
            <div style={{fontSize:"12.5px",color:"#166534",lineHeight:"1.6"}}>
              {caseData.learningPoint || "Not stated."}
            </div>
          </div>
          <div style={{fontSize:"12px",fontWeight:"700",color:C.dark,marginBottom:"5px"}}>
            Is that the right thing for this case to teach?
          </div>
          <textarea value={current.intentComment||""}
            onChange={e=>persist({...current,intentComment:e.target.value})}
            placeholder="Optional. Disagreement with the intended lesson is especially useful."
            style={{...C.inp,minHeight:"74px",resize:"vertical",lineHeight:"1.6"}}/>
        </div>}
      </div>

      <div style={{display:"flex",gap:"9px",alignItems:"center"}}>
        <button onClick={()=>setIdx(i=>Math.max(0,i-1))} disabled={idx===0}
          style={{...C.btn("#f3f4f6","#374151"),opacity:idx===0?0.4:1}}>
          ← Previous
        </button>
        <span style={{fontSize:"12px",color:isComplete(current)?T.success:T.muted,
          marginInlineStart:"auto",fontWeight:"600"}}>
          {isComplete(current) ? "✓ This case is complete" : "Some parameters still blank"}
        </span>
        {idx < caseIds.length-1
          ? <button onClick={()=>{persist(current,isComplete(current));setIdx(i=>i+1);setShowIntent(false);
              startedRef.current=Date.now();window.scrollTo(0,0);}}
              style={C.btn(C.pu)}>Next case →</button>
          : <button onClick={()=>{persist(current,isComplete(current));onExit&&onExit();}}
              style={C.btn(C.pu)}>Finish</button>}
      </div>
      </div>
    </div>

  </div>;
}

// Small full-screen centring wrapper used by the validation screens.
function Centered({children}){
  return <div style={{minHeight:"100vh",background:T.bg,display:"flex",
    alignItems:"center",justifyContent:"center",padding:"22px",fontFamily:FONT_UI}}>
    {children}
  </div>;
}

// ─── LANDING ──────────────────────────────────────────────────────────────────
// The circuit-fill opening and the entry choices are ONE screen. The animation
// plays, then the options fade in beneath it — no "Begin" step in between,
// which was friction for no gain.
//
// Magic links, QR joins and ?practice=1 bypass this entirely.
function LandingScreen({ onPractice, onEducator, onPlayground }){
  const {t}=useLocale();
  const [step,setStep]=useState(0);

  useEffect(()=>{
    const timers=[
      setTimeout(()=>setStep(1),100),   // access line draws in
      setTimeout(()=>setStep(2),500),   // filter fills
      setTimeout(()=>setStep(3),900),   // return line draws out
      setTimeout(()=>setStep(4),1250),  // title
      setTimeout(()=>setStep(5),1600),  // choices
    ];
    return ()=>timers.forEach(clearTimeout);
  },[]);

  const Choice=({icon,title,blurb,onClick,primary,muted})=>(
    <button onClick={onClick} style={{width:"100%",textAlign:"start",
      padding:"17px 20px",borderRadius:"14px",cursor:"pointer",fontFamily:FONT_UI,
      display:"flex",alignItems:"center",gap:"15px",
      border:`1.5px solid ${primary?"#4F2683":T.border}`,
      background:primary?"#4F2683":"white",
      color:primary?"white":T.text,
      boxShadow:primary?"0 6px 20px rgba(79,38,131,0.22)":"0 1px 3px rgba(15,23,42,0.06)",
      transition:"transform 0.15s, box-shadow 0.15s",opacity:muted?0.82:1}}
      onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-2px)";}}
      onMouseLeave={e=>{e.currentTarget.style.transform="translateY(0)";}}>
      <span style={{fontSize:"23px",flexShrink:0}}>{icon}</span>
      <span style={{flex:1,minWidth:0}}>
        <span style={{display:"block",fontSize:"15px",fontWeight:"800",
          marginBottom:"3px",letterSpacing:"-0.01em"}}>{title}</span>
        <span style={{display:"block",fontSize:"12px",lineHeight:"1.5",
          color:primary?"rgba(255,255,255,0.72)":T.muted}}>{blurb}</span>
      </span>
      <span style={{fontSize:"14px",fontWeight:"700",flexShrink:0,
        color:primary?"rgba(255,255,255,0.8)":T.muted}}>→</span>
    </button>
  );

  return <div style={{minHeight:"100vh",background:T.bg,display:"flex",
    flexDirection:"column",alignItems:"center",justifyContent:"center",
    padding:"24px 20px 70px",fontFamily:FONT_UI,position:"relative"}}>
    <style>{`
      .kirpa-line-wrap { width:30%; height:10px; background:#e5e7eb; position:relative; overflow:hidden; }
      .kirpa-line { position:absolute; top:0; height:100%; width:100%; }
      .kirpa-line-1 { left:-100%; background:#ef4444; }
      .kirpa-line-1.go { animation: kirpaFlowIn 0.4s ease-in-out forwards; }
      .kirpa-line-2 { right:-100%; background:#3b82f6; }
      .kirpa-line-2.go { animation: kirpaFlowOut 0.4s ease-in-out forwards; }
      @keyframes kirpaFlowIn  { from { left:-100%; }  to { left:0; } }
      @keyframes kirpaFlowOut { from { right:-100%; } to { right:0; } }
      .kirpa-filter { width:84px; height:84px; border:9px solid #e5e7eb; border-radius:50%;
        position:relative; overflow:hidden; flex-shrink:0; }
      .kirpa-filter-fill { position:absolute; bottom:0; left:0; width:100%; height:0;
        background:linear-gradient(to top,#ef4444,#f87171); transition:height 0.4s ease-in-out; }
      .kirpa-filter.go .kirpa-filter-fill { height:100%; }
      .kirpa-fade { opacity:0; transform:translateY(6px);
        transition:opacity 0.7s ease, transform 0.7s ease; }
      .kirpa-fade.go { opacity:1; transform:none; }
      @media (prefers-reduced-motion: reduce) {
        .kirpa-line-1, .kirpa-line-2 { animation:none !important; left:0 !important; right:0 !important; }
        .kirpa-filter-fill { transition:none; height:100%; }
        .kirpa-fade { transition:none; opacity:1; transform:none; }
      }
    `}</style>

    <div style={{width:"100%",maxWidth:"380px",height:"88px",display:"flex",
      alignItems:"center",justifyContent:"center",marginBottom:"22px"}}>
      <div className="kirpa-line-wrap"><div className={`kirpa-line kirpa-line-1 ${step>=1?"go":""}`}/></div>
      <div className={`kirpa-filter ${step>=2?"go":""}`}><div className="kirpa-filter-fill"/></div>
      <div className="kirpa-line-wrap"><div className={`kirpa-line kirpa-line-2 ${step>=3?"go":""}`}/></div>
    </div>

    <div className={`kirpa-fade ${step>=4?"go":""}`} style={{textAlign:"center",marginBottom:"26px"}}>
      <h1 style={{margin:"0 0 5px",fontSize:"clamp(32px,7vw,46px)",fontWeight:"900",
        color:"#4F2683",letterSpacing:"-0.035em",lineHeight:1}}>KiRPA</h1>
      <p style={{margin:0,fontSize:"12.5px",color:T.muted,letterSpacing:"0.02em"}}>
        {t("land.tagline","Kidney Replacement Prescription Analytics")}
      </p>
    </div>

    <div className={`kirpa-fade ${step>=5?"go":""}`}
      style={{width:"100%",maxWidth:"420px",display:"flex",flexDirection:"column",gap:"11px"}}>
      <Choice primary icon="🩺"
        title={t("land.practiceTitle","Practice Cases")}
        blurb={t("land.practiceBlurb","Self-study cases and your performance report")}
        onClick={onPractice}/>
      <Choice icon="📋" muted
        title={t("land.educatorTitle","Educator Login")}
        blurb={t("land.educatorBlurb","Under construction")}
        onClick={onEducator}/>
      <Choice icon="🧊"
        title={t("land.pgTitle","CRRT 3D/AR Playground")}
        blurb={t("land.pgBlurb","On iPhone or Android, try a demo augmented reality CRRT workspace")}
        onClick={onPlayground}/>
    </div>

    <div className={`kirpa-fade ${step>=5?"go":""}`} style={{marginTop:"22px"}}>
      <LocaleSwitcherLight/>
    </div>

    <footer style={{position:"absolute",bottom:"14px",textAlign:"center",fontSize:"10.5px",
      color:"#9ca3af",padding:"0 20px",lineHeight:"1.6"}}>
      Created by Dr. Amrit Kirpalani, MD, MEHP, FRCPC.<br/>
      For educational purposes only. Not to be used for clinical decision-making.
      Licensed under CC BY-NC-SA 4.0.
    </footer>
  </div>;
}

function AppInner(){
    // If a trainee magic link is present, ALWAYS route to trainee flow
  const { token: traineeTokenFromUrl, assignmentId, groupId, joinCode: joinCodeFromUrl } = getTraineeRoutingParams();
  if (traineeTokenFromUrl) {
    // will return below after hooks
  }
  const [edu,setEdu]=useState(undefined); // undefined = "still checking auth"
  const [magicTraineeToken,setMagicTraineeToken]=useState(null);

  // Landing fork: null = show the choice, "practice" | "educator" once chosen.
  // ?practice=1 lets a QR code or a shared link drop straight into practice,
  // which is what conference and workshop dissemination actually needs.

  // ?validate=… from the expert panel invitation.
  // Whether this page load is the Letter of Information route. Read once from
  // the pathname; the letter has no internal navigation, so it does not need to
  // react to history changes.
  const [loiRoute,setLoiRoute]=useState(()=>{
    try{
      if(/^\/loi\/?$/.test(window.location.pathname)) return true;
      // Second spelling, served from the app root. /loi depends on the Netlify
      // SPA fallback in public/_redirects; this one does not depend on
      // anything, so if that file is ever missing from a deploy there is still
      // a working URL for the letter.
      return new URLSearchParams(window.location.search).get("loi") === "1";
    }catch{ return false; }
  });
  const [validateToken,setValidateToken]=useState(()=>{
    try{ return new URLSearchParams(window.location.search).get("validate"); }catch{ return null; }
  });

  const [entry,setEntry]=useState(()=>{
    try{
      const p=new URLSearchParams(window.location.search);
      if(p.get("practice")==="1"||p.get("mode")==="practice") return "practice";
      if(p.get("mode")==="3d"||p.get("mode")==="playground") return "playground";
    }catch{}
    return null;
  });
  useEffect(()=>{
    // Firebase Auth persistence — automatically restores session on page reload.
    // onAuthStateChanged fires once on mount with the current user (or null).
    const unsub = onAuthStateChanged(auth, async (user)=>{
      if(user){
        try {
          const snap = await getDoc(doc(db, 'educators', user.email));
          if(snap.exists() && snap.data().approved){
            const p = snap.data();
            setEdu({ email:user.email, name:p.name, institution:p.institution||"",
                      institutionId:p.institutionId||"", role:p.role||"", uid:user.uid });
          } else {
            // Logged-in but not approved / profile missing — sign out silently
            await fbSignOut(auth);
            setEdu(null);
          }
        } catch(e){ console.error("Auth restore failed",e); setEdu(null); }
      } else {
        setEdu(null);
      }
    });
    return ()=>unsub();
  },[]);

    useEffect(()=>{
    // Accept token from multiple possible link formats:
    //   ?trainee_token=...
    //   ?token=...
    //   ?t=...
    // Also support hash-based routes (sometimes Netlify links end up like /#/?trainee_token=...)
    const pickToken = () => {
      const searchParams = new URLSearchParams(window.location.search);
      const fromSearch =
        searchParams.get("trainee_token") ||
        searchParams.get("token") ||
        searchParams.get("t");

      if (fromSearch) return fromSearch;

      const h = window.location.hash || "";
      // If hash contains a "?" then parse its querystring
      const qIndex = h.indexOf("?");
      if (qIndex !== -1) {
        const hashQuery = h.slice(qIndex + 1);
        const hashParams = new URLSearchParams(hashQuery);
        const fromHash =
          hashParams.get("trainee_token") ||
          hashParams.get("token") ||
          hashParams.get("t");
        if (fromHash) return fromHash;
      }
      return null;
    };

    const tok = pickToken();
    if(tok){
      setMagicTraineeToken(tok);
    }
  },[]);
  const login  = (e)=>setEdu(e);
  const logout = async()=>{ await fbSignOut(auth); setEdu(null); };

  // Now safe to do early return — all hooks have been called above

  // ── The Letter of Information, as its own route ──────────────────────────
  // First, before any token route. The letter is a static document, and a
  // learner who opens it mid-case in a second tab must get the letter rather
  // than be pulled into a token flow by a query string still on the URL.
  //
  // A real path rather than a query flag, so it can be bookmarked, printed and
  // forwarded; section 11 of the letter tells the reader to keep a copy.
  // Netlify's SPA fallback already serves index.html for unknown paths, so this
  // needs no redirect rule. The trailing-slash variant is accepted because
  // some mail clients add one.
  if (loiRoute) {
    return <LetterOfInformation
      onExit={()=>{
        // A tab opened from the notice has no history to go back to, so
        // closing returns to the app root rather than dead-ending.
        try {
          window.history.pushState({}, document.title, "/");
          setLoiRoute(false);
        } catch { window.location.href = "/"; }
      }}/>;
  }

  if (traineeTokenFromUrl) {
    return (
      <TraineeMagicLink
        token={traineeTokenFromUrl}
        assignmentIdFromUrl={assignmentId}
        groupIdFromUrl={groupId}
      />
    );
  }

  // QR-session landing (?join=ABC123) — takes precedence over educator login.
  if (joinCodeFromUrl) {
    return (
      <JoinSession
        joinCode={joinCodeFromUrl}
        onExit={()=>{
          window.history.replaceState({}, document.title, window.location.pathname);
          window.location.reload();
        }}
      />
    );
  }

   if(magicTraineeToken){
    return (
      <TraineeMagicLink
        token={magicTraineeToken}
        onExit={()=>{
          setMagicTraineeToken(null);
          window.history.replaceState({}, document.title, window.location.pathname);
        }}
      />
    );
  }

 // Still checking Firebase auth state — show a brief spinner
  if(edu===undefined) return (
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",
      minHeight:"100vh",background:"#f5f3ff"}}>
      <div style={{textAlign:"center"}}>
        <div style={{fontSize:"24px",fontWeight:"800",color:"#4f2683",marginBottom:"8px",
          fontFamily:"Inter,system-ui,sans-serif",letterSpacing:"-0.02em"}}>KiRPA</div>
        <div style={{fontSize:"11px",color:"#9ca3af",animation:"spin 1s linear infinite",
          display:"inline-block",marginTop:"8px"}}>⟳</div>
      </div>
    </div>
  );



  // Signed-in educators go straight to the dashboard, skipping the landing.
  if (edu) return <Dashboard educator={edu} onLogout={logout}/>;

  // Expert content validation. Reached only by an emailed link, and handled
  // before every other route so the token is consumed wherever the reviewer
  // was beforehand.
  if (validateToken) {
    return <ValidationMode token={validateToken}
      onExit={()=>{ setValidateToken(null); setEntry(null); }}/>;
  }

  // 3D and AR. Attempts here are recorded through the same Cloud Function as
  // the 2D runner, separated only by delivery_mode. Under the Phase 2 notice
  // model nothing gates entry: the research notice sits on the landing page the
  // learner arrived from, and on the playground's own HUD.
  if (entry === "playground") {
    return (
      <Suspense fallback={
        <div style={{minHeight:"100vh",background:"#080e1a",display:"flex",
          alignItems:"center",justifyContent:"center",flexDirection:"column",gap:"14px",
          fontFamily:FONT_UI}}>
          <div style={{width:"38px",height:"38px",borderRadius:"50%",
            border:"3px solid rgba(0,170,255,0.2)",borderTop:"3px solid #00aaff",
            animation:"kirpaSpin 0.9s linear infinite"}}/>
          <div style={{fontSize:"11px",color:"#4a6a8a",letterSpacing:"2px",
            fontFamily:FONT_MONO}}>LOADING 3D ENGINE…</div>
          <style>{`@keyframes kirpaSpin{to{transform:rotate(360deg)}}`}</style>
        </div>
      }>
        <CRRTPlaygroundLazy onExit={()=>setEntry(null)}/>
      </Suspense>
    );
  }

  // Practice track: no account, no educator.
  if (entry === "practice") {
    return <PracticeSession onExit={()=>setEntry(null)}/>;
  }

  // Educator sign-in, reached from the landing fork.
  if (entry === "educator") {
    return <LoginScreen onLogin={login} onBack={()=>setEntry(null)}/>;
  }

  return <LandingScreen
    onPractice={()=>setEntry("practice")}
    onEducator={()=>setEntry("educator")}
    onPlayground={()=>setEntry("playground")}/>;
}
// ─── ROOT ─────────────────────────────────────────────────────────────────────
// LocaleProvider sets document dir/lang and supplies t() to the whole tree.
// With locale === "en" (the default) this is a passthrough.
export default function App(){
  return (
    <LocaleProvider>
      <AppInner/>
    </LocaleProvider>
  );
}